/* eslint-disable */
// Polyfill window.webkit for social media in-app browsers (Instagram, TikTok, Facebook IAB)
// Capacitor's bridge detection throws if window.webkit is undefined in non-Safari web contexts.
// Some IABs (e.g. Instagram) define window.webkit themselves without a messageHandlers
// property, so patch that independently rather than only when window.webkit is fully absent.
if (typeof window !== "undefined") {
  if (!window.webkit) window.webkit = {};
  if (!window.webkit.messageHandlers) window.webkit.messageHandlers = {};
}
import React, { useState, useEffect, useRef, useCallback, useMemo, createContext, useContext, startTransition } from "react";
import { initCapacitorPush, isPushReady } from "./capacitorPush";
import { toUploadableImage } from "./imageUpload";
import { STRIPE_AVAILABLE, mountCardElement, confirmCardBooking, destroyCardElement, mountSetupCardElement, confirmSetupIntent, destroySetupCardElement, mountPaymentRequestButton, confirmPaymentRequestBooking, destroyPaymentRequestButton } from "./stripePayment";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import L from 'leaflet';
import { createClient } from "@supabase/supabase-js";
import * as Sentry from "@sentry/capacitor";
import * as SentryReact from "@sentry/react";
import posthog from "posthog-js";
import { Analytics, track } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { motion, AnimatePresence, useMotionValue, useSpring } from "framer-motion";
import { Capacitor } from "@capacitor/core";
import { Haptics, ImpactStyle, NotificationType } from "@capacitor/haptics";
import { Browser } from "@capacitor/browser";
import { App as CapApp } from "@capacitor/app";
import { Geolocation } from "@capacitor/geolocation";
import { fadeUp, fadeIn, scaleIn, staggerContainer, slideInRight, cardHover } from "./lib/animations";

if (process.env.REACT_APP_POSTHOG_KEY) {
  posthog.init(process.env.REACT_APP_POSTHOG_KEY, {
    api_host: "https://us.i.posthog.com",
    person_profiles: "identified_only",
    capture_pageview: true,
    capture_pageleave: true,
    autocapture: true,
  });
}

if (process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.NODE_ENV,
    // Release must match what sentry-upload.js uploads source maps under (full commit SHA)
    release: process.env.REACT_APP_SENTRY_RELEASE || undefined,
    tracesSampleRate: 0.2,
    replaysOnErrorSampleRate: 1.0,
    replaysSessionSampleRate: 0.05,
    // Supabase auth internals probe WebAuthn/passkey support on every sign-in/
    // sign-up; unsupported environments (native WebView, older browsers) throw
    // this, but it's non-blocking — auth still completes. The unhandledrejection
    // listener in public/init.js only silences the browser console; Sentry's own
    // GlobalHandlers integration installs a separate listener that still fires
    // regardless, so it has to be filtered here too.
    ignoreErrors: [/public key credentials/i],
    // Function form appends to Sentry's defaults instead of replacing them — the
    // array form used previously silently disabled GlobalHandlers (window.onerror /
    // unhandledrejection auto-capture), meaning only explicit Sentry.captureException()
    // calls were ever reaching Sentry. Any raw uncaught crash went uncaptured.
    integrations: (defaultIntegrations) => [...defaultIntegrations, Sentry.browserTracingIntegration(), SentryReact.replayIntegration()],
  }, SentryReact.init);
  // Tag every event with the actual platform — without this, a native iOS/Android
  // crash and a web bug are indistinguishable in Sentry (NODE_ENV is "production"
  // either way). This is what makes "native-only" crashes findable/filterable.
  try { Sentry.setTag("platform", Capacitor.isNativePlatform() ? Capacitor.getPlatform() : "web"); } catch { Sentry.setTag("platform", "web"); }
}

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || "";
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || "https://qyjwfyboyijqiovtagby.supabase.co";
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_KEY || "";
// ── Central Supabase failure reporting ───────────────────────────────────────
// Every failed REST / RPC / storage / edge-function request is reported to Sentry, so a
// failure like "permission denied for table barbers" can never go unnoticed again just
// because a screen forgot to call Sentry.captureException by hand.
// Privacy: only method, table/function name, status and error code/message are sent —
// never the query string, request body, or the error's details/hint (those can echo row data).
// Never throws and never alters the response: the original Response is always returned.
const _sbReportedAt = new Map();
const _SB_SKIP_STATUS = new Set([401, 404, 406]); // expired JWT, not-found, .single() with no row — expected noise
function reportSupabaseFailure(method, url, res) {
  try {
    if (!res || res.ok || _SB_SKIP_STATUS.has(res.status)) return;
    const u = new URL(url);
    const m = u.pathname.match(/^\/(rest|storage|functions)\/v1\/(.+)$/);
    if (!m) return; // auth errors (wrong password etc.) are expected and handled in the UI
    const target = `${m[1]}/${m[2].split("/").slice(0, 2).join("/")}`;
    res.clone().json().catch(() => null).then((body) => {
      try {
        const code = body?.code || "";
        if (code === "23505") return; // unique violation (e.g. handle already taken) — handled in the UI
        const message = String(body?.message || body?.error || res.statusText || "request failed").slice(0, 300);
        const fp = `${method} ${target} ${res.status} ${code}`;
        const now = Date.now();
        if (now - (_sbReportedAt.get(fp) || 0) < 60000) return; // same failure at most once a minute per session
        _sbReportedAt.set(fp, now);
        const err = new Error(`Supabase ${method} ${target} → ${res.status}${code ? ` ${code}` : ""}: ${message}`);
        err.name = "SupabaseRequestError";
        Sentry.captureException(err, {
          level: (res.status >= 500 || res.status === 403) ? "error" : "warning", // 403 = permission/RLS breakage, as serious as a 5xx
          fingerprint: ["supabase-request", method, target, String(res.status), code],
          tags: { flow: "supabase_request", http_status: String(res.status), pg_code: code || "none", target },
        });
      } catch {}
    });
  } catch {}
}
const reportingFetch = async (input, init) => {
  const res = await fetch(input, init); // network errors / aborts propagate untouched
  try {
    const url = typeof input === "string" ? input : (input?.url || String(input));
    const method = (init?.method || (typeof input !== "string" && input?.method) || "GET").toUpperCase();
    if (url.startsWith(SUPABASE_URL)) reportSupabaseFailure(method, url, res);
  } catch {}
  return res;
};

const supabase = createClient(SUPABASE_URL, SUPABASE_KEY, {
  global: { fetch: reportingFetch },
  auth: {
    flowType: 'pkce',
    detectSessionInUrl: true,
    autoRefreshToken: true,
    persistSession: true,
  },
});

// OAuth/PKCE return that can't be completed (verifier/state lost: different webview, reload
// mid-flow, stale link). Previously an unhandled AuthApiError left the user on a blank sign-in.
// Now: warn-level Sentry event, strip the dead ?code= from the URL, show a retry message.
try {
  supabase.auth.initialize().then(({ error }) => {
    if (!error) return;
    const m = String(error.message || "");
    if (!/flow state|code verifier|state not found|expired/i.test(m)) return;
    try { sessionStorage.setItem("faded_auth_notice", "Sign-in expired — please try again."); } catch {}
    try { Sentry.captureMessage("OAuth sign-in could not complete: " + m.slice(0, 120), { level: "warning", tags: { flow: "oauth_return" } }); } catch {}
    try {
      const u = new URL(window.location.href);
      ["code","error","error_code","error_description"].forEach(k => u.searchParams.delete(k));
      window.history.replaceState({}, "", u.pathname + u.search + u.hash);
    } catch {}
  }).catch(() => {});
} catch {}

// detectSessionInUrl processes any ?code= in the URL (password recovery, OAuth,
// magic link) asynchronously as soon as the client is created — which is at
// module load, before React has mounted anything. A React component's own
// useEffect-based onAuthStateChange subscription only attaches after first
// render, so if that processing (and its PASSWORD_RECOVERY event) finishes
// before mount, the event fires with nobody listening and is silently lost —
// the recovery session still gets persisted, so the user ends up just looking
// signed in instead of seeing the "set new password" screen. Registering here,
// at module scope, guarantees nothing is missed regardless of mount timing.
let _earlyAuthEvent = null;
let _earlyAuthSession = null;
supabase.auth.onAuthStateChange((event, s) => {
  // DIAGNOSTIC: log every auth event that fires, on both web and native, so a
  // failed password-reset attempt leaves a trail in Sentry instead of just
  // silently landing the user somewhere unexpected with no explanation.
  console.log("[FADED][auth]", event, "hasSession:", !!s, "userId:", s?.user?.id || null);
  try { Sentry.addBreadcrumb({ category: "auth", message: `onAuthStateChange: ${event}`, level: "info", data: { hasSession: !!s, userId: s?.user?.id || null } }); } catch {}
  // Establishing the recovery session can also fire a follow-up event (e.g.
  // INITIAL_SESSION/TOKEN_REFRESHED) shortly after PASSWORD_RECOVERY — once
  // we've seen the recovery event, latch it so a later event can't overwrite
  // it and erase the signal before the component mounts to read it.
  if (_earlyAuthEvent !== "PASSWORD_RECOVERY") { _earlyAuthEvent = event; }
  _earlyAuthSession = s;
});

// ─── THEME SYSTEM ─────────────────────────────────────────────────────────────
const ThemeCtx = createContext(null);
const useT = () => useContext(ThemeCtx);

const ResponsiveCtx = createContext({ isMd: false, isDesktop: false });
const useR = () => useContext(ResponsiveCtx);

function createTheme(dark) {
  return {
    bg:            dark ? "#0A0A0A" : "#F7F8FA",
    card:          dark ? "#111"    : "#fff",
    input:         dark ? "#0d0d0d" : "#fff",
    section:       dark ? "#0d0d0d" : "#F9FAFB",
    chip:          dark ? "#1a1a1a" : "#F0F0F0",
    surface2:      dark ? "#2a2a2a" : "#F3F4F6",
    border:        dark ? "#1a1a1a" : "#E5E7EB",
    border2:       dark ? "#222"    : "#E5E7EB",
    divider:       dark ? "#1a1a1a" : "#F3F4F6",
    t1:            dark ? "#F0F0F0" : "#111",
    t2:            dark ? "#C4C4C4" : "#374151",
    t3:            dark ? "#888"    : "#6B7280",
    t4:            dark ? "#858585" : "#6B7280",   // light: #9CA3AF was 2.39:1 ❌ → #6B7280 = 4.55:1 ✅ WCAG AA
    inputText:     dark ? "#E8E8E8" : "#111",
    headerBg:      dark ? "rgba(10,10,10,0.95)"  : "rgba(247,248,250,0.96)",
    navBg:         dark ? "rgba(10,10,10,0.97)"  : "rgba(255,255,255,0.97)",
    tabBar:        dark ? "#1a1a1a"               : "#EBEBEB",
    tabActive:     dark ? "#2a2a2a"               : "#fff",
    tabActiveText: dark ? "#fff"                  : "#111",
    tabInactive:   dark ? "#858585"               : "#6B7280",  // light: #9CA3AF was 2.54:1 ❌ → #6B7280 = 4.83:1 ✅ WCAG AA
    blueText:      dark ? "#60A5FA"               : "#2563EB",
    blueBg:        dark ? "rgba(37,99,235,0.12)"  : "#EFF6FF",
    blueBorder:    dark ? "rgba(37,99,235,0.25)"  : "#DBEAFE",
    starEmpty:     dark ? "#2a2a2a"               : "#E5E7EB",
    bodyBg:        dark ? "#0A0A0A"               : "#F7F8FA",
    STATUS: {
      available: { label:"Available Now",  color:dark?"#22C55E":"#16A34A", bg:dark?"rgba(34,197,94,0.12)":"#DCFCE7",   dot:"#22C55E" },
      busy:      { label:"Busy",           color:dark?"#EF4444":"#DC2626", bg:dark?"rgba(239,68,68,0.12)":"#FEE2E2",   dot:"#EF4444" },
      soon:      { label:"Available Soon", color:dark?"#F59E0B":"#D97706", bg:dark?"rgba(245,158,11,0.12)":"#FEF9C3",  dot:"#F59E0B" },
      offline:   { label:"Offline",        color:dark?"#9CA3AF":"#6B7280", bg:dark?"rgba(156,163,175,0.12)":"#F3F4F6", dot:"#9CA3AF" },
    },
    BG_FOR: dark
      ? {"#2563EB":"rgba(37,99,235,0.12)","#059669":"rgba(5,150,105,0.12)","#D97706":"rgba(217,119,6,0.12)","#7C3AED":"rgba(124,58,237,0.12)","#DC2626":"rgba(220,38,38,0.12)","#0891B2":"rgba(8,145,178,0.12)","#BE185D":"rgba(190,24,93,0.12)","#B45309":"rgba(180,83,9,0.12)"}
      : {"#2563EB":"#EFF6FF","#059669":"#ECFDF5","#D97706":"#FFFBEB","#7C3AED":"#F5F3FF","#DC2626":"#FEF2F2","#0891B2":"#F0FDFE","#BE185D":"#FDF2F8","#B45309":"#FFFBEB"},
    statusColor: (s) => dark
      ? ({completed:"#22C55E",confirmed:"#60A5FA",accepted:"#60A5FA",pending:"#F59E0B",cancelled:"#EF4444"}[s]||"#9CA3AF")
      : ({completed:"#16A34A",confirmed:"#2563EB",accepted:"#2563EB",pending:"#D97706",cancelled:"#DC2626"}[s]||"#6B7280"),
    statusBg: (s) => dark
      ? ({completed:"rgba(34,197,94,0.12)",confirmed:"rgba(37,99,235,0.12)",accepted:"rgba(37,99,235,0.12)",pending:"rgba(245,158,11,0.12)",cancelled:"rgba(220,38,38,0.12)"}[s]||"rgba(156,163,175,0.12)")
      : ({completed:"#DCFCE7",confirmed:"#EFF6FF",accepted:"#EFF6FF",pending:"#FEF9C3",cancelled:"#FEE2E2"}[s]||"#F3F4F6"),
  };
}

// ─── GPS DISTANCE ─────────────────────────────────────────────────────────────
function haversineKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180) * Math.cos(lat2*Math.PI/180) * Math.sin(dLng/2)**2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
}


// Distance unit detection — timezone based (bounding boxes incorrectly flag Canada as USA)
const CANADA_TIMEZONES = new Set([
  "America/Toronto","America/Vancouver","America/Edmonton","America/Winnipeg",
  "America/Halifax","America/St_Johns","America/Regina","America/Whitehorse",
  "America/Yellowknife","America/Iqaluit","America/Moncton","America/Glace_Bay",
  "America/Goose_Bay","America/Blanc-Sablon","America/Nipigon","America/Rainy_River",
  "America/Thunder_Bay","America/Atikokan","America/Creston","America/Dawson",
  "America/Dawson_Creek","America/Fort_Nelson","America/Cambridge_Bay",
  "America/Inuvik","America/Rankin_Inlet","America/Resolute","America/Swift_Current",
]);
function usesMiles() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz.startsWith("America/") && !CANADA_TIMEZONES.has(tz);
  } catch(e) { return false; }
}
function formatDist(km) {
  if (km === null || km === undefined) return "nearby";
  if (usesMiles()) return `${(km * 0.621371).toFixed(1)} mi`;
  return `${km.toFixed(1)} km`;
}


// ─── DEMO DATA ────────────────────────────────────────────────────────────────
const DEMO_BARBERS = [
  { id:"demo_1", full_name:"Marcus Cole",   handle:"@marcusfades",  rating:4.9, review_count:214, total_cuts:1240, status:"available", specialty:["Skin Fades","Lineups","Dreads"],    price_min:30, price_max:55, accent_color:"#2563EB", since_year:2019, bio:"Award-winning fade specialist. Walk-ins welcome today — mobile and in-shop.",         walkin:true,  styles:["fades","lineups","dreads"],   avatar_url:null, portfolio_urls:[], shareable_slug:"marcusfades",   instagram:"@marcusfades",   city:"Toronto, ON",    lat:43.6532, lng:-79.3832, is_mobile:false, _distKm:1.2,  _isDemo:true },
  { id:"demo_3", full_name:"Isaiah Thomas", handle:"@isaiahcutz",   rating:4.9, review_count:143, total_cuts:710,  status:"available", specialty:["High Fades","Braids","Cornrows"],  price_min:35, price_max:60, accent_color:"#0891B2", since_year:2020, bio:"Braids and high fades done right. Box braids, cornrows, skin fades. Book today.",        walkin:true,  styles:["fades","braids","cornrows"], avatar_url:null, portfolio_urls:[], shareable_slug:"isaiahcutz",   instagram:"@isaiahcutz",   city:"North York, ON", lat:43.7615, lng:-79.4111, is_mobile:false, _distKm:11.5, _isDemo:true },
  { id:"demo_6", full_name:"Jahleel Knox",  handle:"@jahleelknox",  rating:4.7, review_count:95,  total_cuts:560,  status:"busy",      specialty:["Afro Fades","Designs","Locs"],    price_min:35, price_max:65, accent_color:"#D97706", since_year:2022, bio:"Creative cut artist. Intricate designs and natural hair textures. DM to join waitlist.", walkin:false, styles:["fades","locs","designs"],    avatar_url:null, portfolio_urls:[], shareable_slug:"jahleelknox",  instagram:"@jahleelknox",  city:"Mississauga, ON",lat:43.5890, lng:-79.6441, is_mobile:false, _distKm:16.8, _isDemo:true },
  { id:"demo_8", full_name:"Remy Okafor",   handle:"@remysharp",    rating:4.7, review_count:82,  total_cuts:410,  status:"soon",      specialty:["Tapers","Shape-Ups","Beard"],     price_min:28, price_max:52, accent_color:"#B45309", since_year:2022, bio:"Precision edge specialist. Sharp lines, clean tapers, sculpted beards. Available in ~20 min.", walkin:false, styles:["tapers","shape-ups","beard"], avatar_url:null, portfolio_urls:[], shareable_slug:"remysharp",    instagram:"@remysharp",    city:"Etobicoke, ON",  lat:43.6680, lng:-79.5310, is_mobile:false, _distKm:22.1, _isDemo:true },
];

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const STATUS = {
  available:{ label:"Available Now",  color:"#22C55E", bg:"rgba(34,197,94,0.12)",   dot:"#22C55E" },
  busy:     { label:"Busy",           color:"#EF4444", bg:"rgba(239,68,68,0.12)",   dot:"#EF4444" },
  soon:     { label:"Available Soon", color:"#F59E0B", bg:"rgba(245,158,11,0.12)",  dot:"#F59E0B" },
  offline:  { label:"Offline",        color:"#9CA3AF", bg:"rgba(156,163,175,0.12)", dot:"#9CA3AF" },
};
const BG_FOR = { "#2563EB":"rgba(37,99,235,0.12)","#059669":"rgba(5,150,105,0.12)","#D97706":"rgba(217,119,6,0.12)","#7C3AED":"rgba(124,58,237,0.12)","#DC2626":"rgba(220,38,38,0.12)" };



const STYLES = ["All","Fades","Braids","Locs","Beard","Tapers","Shape-Ups","Twists","Dreads","Curls"];
const PRICES = ["Any Price","Under $30","$30-$50","$50+"];
const ACCENT_OPTIONS = ["#2563EB","#059669","#D97706","#7C3AED","#DC2626","#0891B2","#BE185D","#B45309"];

const DEFAULT_SERVICES = [
  { name:"Adult Fade",            category:"Haircuts",    price:40, duration_minutes:45 },
  { name:"Skin Fade",             category:"Haircuts",    price:45, duration_minutes:45 },
  { name:"Taper",                 category:"Haircuts",    price:38, duration_minutes:40 },
  { name:"Scissor Cut",           category:"Haircuts",    price:40, duration_minutes:40 },
  { name:"Shape-Up / Line-Up",    category:"Haircuts",    price:20, duration_minutes:20 },
  { name:"Kids Cut (under 12)",   category:"Haircuts",    price:28, duration_minutes:30 },
  { name:"Senior Cut",            category:"Haircuts",    price:28, duration_minutes:30 },
  { name:"Design / Pattern",      category:"Haircuts",    price:55, duration_minutes:60 },
  { name:"Beard Trim",            category:"Beard & Face",price:20, duration_minutes:20 },
  { name:"Beard Outline & Shape", category:"Beard & Face",price:25, duration_minutes:25 },
  { name:"Hot Towel Shave",       category:"Beard & Face",price:40, duration_minutes:35 },
  { name:"Straight Razor Shave",  category:"Beard & Face",price:45, duration_minutes:35 },
  { name:"Mustache Trim",         category:"Beard & Face",price:15, duration_minutes:15 },
  { name:"Eyebrow Trim",          category:"Beard & Face",price:15, duration_minutes:15 },
  { name:"Haircut + Beard Trim",  category:"Combos",      price:55, duration_minutes:60 },
  { name:"Haircut + Beard Outline",category:"Combos",     price:60, duration_minutes:65 },
  { name:"Full Grooming Package", category:"Combos",      price:85, duration_minutes:90 },
  { name:"Braids / Cornrows",     category:"Specialty",   price:65, duration_minutes:90 },
  { name:"Dread Maintenance",     category:"Specialty",   price:70, duration_minutes:90 },
  { name:"Hair Colour",           category:"Specialty",   price:55, duration_minutes:60 },
  { name:"Grey Blending",         category:"Specialty",   price:50, duration_minutes:50 },
  { name:"Scalp Treatment",       category:"Specialty",   price:30, duration_minutes:30 },
];

const DEFAULT_ADDONS = [
  { name:"Hot Towel Finish",      price:10, duration_minutes:10 },
  { name:"Eyebrow Trim",          price:10, duration_minutes:10 },
  { name:"Face Mask",             price:15, duration_minutes:15 },
  { name:"Scalp Massage",         price:15, duration_minutes:15 },
  { name:"Tea Tree Treatment",    price:12, duration_minutes:10 },
  { name:"Beard Moisturiser",     price:8,  duration_minutes:5  },
  { name:"Razor Edge Touch-Up",   price:10, duration_minutes:10 },
  { name:"Ear & Nose Trim",       price:8,  duration_minutes:5  },
];

// ─── HELPERS ──────────────────────────────────────────────────────────────────
// Strip HTML tags and dangerous protocols from any user-supplied text before writing to DB
const sanitize = (s, maxLen = 2000) =>
  String(s || "").replace(/<[^>]*>/g, "").replace(/javascript:/gi, "").trim().slice(0, maxLen);

// Always shows time in the viewer's local timezone with explicit timezone abbreviation
// e.g. "Mon, Jun 24 at 3:00 PM EDT"
const formatLocalTime = (isoString) => {
  if (!isoString) return "";
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return new Date(isoString).toLocaleString("en-CA", {
      timeZone: tz,
      weekday: "short", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit", timeZoneName: "short",
    });
  } catch { return new Date(isoString).toLocaleString(); }
};

// Drops localStorage-cached "local-*" booking shadow copies once a matching real DB
// booking exists (same barber + scheduled time). These shadows were written by the
// booking-confirm flow as a fallback for guests, but a bug once wrote them for signed-in
// bookings too, where they never got cleared and sat forever as a frozen-"pending" phantom
// entry duplicating the real (possibly since-accepted) booking. Also persists the cleanup
// back to localStorage so it doesn't have to re-filter every load.
const dropStaleLocalBookings = (localBks, realBookings) => {
  if (!Array.isArray(localBks) || localBks.length === 0) return localBks || [];
  const real = realBookings || [];
  const isStale = (lb) => lb._local && real.some(rb =>
    rb.barber_id === lb.barber_id &&
    new Date(rb.scheduled_at).getTime() === new Date(lb.scheduled_at).getTime()
  );
  const fresh = localBks.filter(lb => !isStale(lb));
  if (fresh.length !== localBks.length) {
    try { localStorage.setItem("faded_local_bookings", JSON.stringify(fresh)); } catch {}
  }
  return fresh;
};

const initials  = (n="") => n.split(" ").map(w=>w[0]).join("").toUpperCase().slice(0,2);
// ─── CURRENCY — CAD only (Canada + US launch, no global currencies) ──────────
const fmt = (amount) => {
  if (amount === null || amount === undefined || amount === "" || amount === "—") return "—";
  const n = parseFloat(String(amount).replace(/[^0-9.-]/g, ""));
  if (isNaN(n)) return "—";
  return `$${n.toFixed(2)}`;
};

const fmtRange = (min, max) => {
  if (!min && !max) return "";
  return `$${parseFloat(min).toFixed(0)}-$${parseFloat(max).toFixed(0)}`;
};

const priceFmt  = b => fmtRange(b.price_min, b.price_max);
const etaFmt    = b => b.status==="available"?"Ready now":b.status==="soon"?"~20 min":"Busy";
// Client-visible freshness signal for "available" status -- a barber can go available and
// then not touch the app for hours, so show how long the status has actually been set.
const lastActiveFmt = b => {
  if (b.status !== "available" || !b.updated_at) return null;
  const mins = Math.round((Date.now() - new Date(b.updated_at).getTime()) / 60000);
  if (mins < 15) return null; // fresh enough to not need a caveat
  if (mins < 60) return `set ${mins}m ago`;
  const hrs = Math.round(mins / 60);
  return `set ${hrs}h ago`;
};
const slugify   = (s="") => s.toLowerCase().replace(/[^a-z0-9]/g,"-").replace(/-+/g,"-").replace(/^-|-$/g,"");
const priceMatch = (b,f) => { if(f==="Any Price")return true; if(f==="Under $30")return b.price_min<30; if(f==="$30-$50")return b.price_min>=30&&b.price_max<=50; if(f==="$50+")return b.price_max>50; return true; };
const styleMatch = (b,s) => { if(s==="All")return true; return(b.styles||[]).some(x=>x.toLowerCase().includes(s.toLowerCase())); };
const distFmt   = (b, userLat, userLng) => {
  if (userLat && userLng && b.lat && b.lng) return formatDist(haversineKm(userLat, userLng, b.lat, b.lng));
  if (b._distKm !== undefined) return formatDist(b._distKm);
  return "nearby";
};
const distKm = (b, userLat, userLng) => {
  if (userLat && userLng && b.lat && b.lng) return haversineKm(userLat, userLng, b.lat, b.lng);
  return b._distKm || b._distance || 99;
};
// Beyond this, a barber is realistically not "book right now nearby" for the client --
// label it honestly instead of presenting it identically to a local result.
const FAR_AWAY_KM = 75;
const isFarAway = km => typeof km === "number" && km > FAR_AWAY_KM;
// Below this many real barbers, Discover shows honest founding-cohort framing instead of
// a mature-marketplace layout that makes thin supply look like a dead marketplace.
const LOW_DENSITY_BARBER_THRESHOLD = 20;

// ─── MICRO COMPONENTS ─────────────────────────────────────────────────────────
function Dot({ status }) {
  const ctx = useT();
  const STATUS_T = ctx ? ctx.t.STATUS : STATUS;
  const s = STATUS_T[status]||STATUS_T.offline;
  return (
    <span style={{ position:"relative", display:"inline-flex", width:10, height:10, alignItems:"center", justifyContent:"center" }}>
      {status==="available"&&<span style={{ position:"absolute", inset:0, borderRadius:"50%", background:s.dot, opacity:0.35, animation:"ripple 1.6s ease-out infinite" }}/>}
      <span style={{ width:8, height:8, borderRadius:"50%", background:s.dot, display:"block", position:"relative", zIndex:1 }}/>
    </span>
  );
}
function Stars({ n, interactive=false, onRate }) {
  const ctx = useT();
  const emptyColor = ctx ? ctx.t.starEmpty : "#2a2a2a";
  const filled = Math.round(n);
  if (interactive) {
    return (
      <span role="group" aria-label={`Star rating, ${filled} out of 5`}>
        {[1,2,3,4,5].map(i=>(
          <button key={i} type="button" onClick={()=>onRate&&onRate(i)}
            aria-label={`${i} star${i>1?"s":""}`} aria-pressed={i<=filled}
            style={{ background:"none", border:"none", color:i<=filled?"#FBBF24":emptyColor, fontSize:20, cursor:"pointer", padding:"2px", lineHeight:1 }}>
            <span aria-hidden="true">★</span>
          </button>
        ))}
      </span>
    );
  }
  return (
    <span aria-label={`${filled} out of 5 stars`} aria-hidden={n===undefined}>
      {[1,2,3,4,5].map(i=>(
        <span key={i} aria-hidden="true"
          style={{ color:i<=filled?"#FBBF24":emptyColor, fontSize:11, padding:0 }}>★</span>
      ))}
    </span>
  );
}
function Tag({ label, accent }) { return <span style={{ padding:"5px 10px", borderRadius:20, background:accent+"18", color:accent, fontSize:11, fontWeight:600, border:`1px solid ${accent}28` }}>{label}</span>; }

let _fbIdCounter = 0;
function FoundingBadge({ size = 64 }) {
  const [uid] = React.useState(() => `fb${++_fbIdCounter}`);
  const small = size <= 32;
  const h = Math.round(size * 1.13);
  return (
    <svg width={size} height={h} viewBox="0 0 100 113" fill="none" style={{ display:"block", flexShrink:0 }}>
      <defs>
        <linearGradient id={`${uid}g`} x1="0" y1="0" x2="100" y2="113" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#FBBF24"/>
          <stop offset="50%" stopColor="#F59E0B"/>
          <stop offset="100%" stopColor="#B45309"/>
        </linearGradient>
        <filter id={`${uid}gl`} x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur in="SourceAlpha" stdDeviation="3.5" result="blur"/>
          <feFlood floodColor="#F59E0B" floodOpacity="0.65" result="col"/>
          <feComposite in="col" in2="blur" operator="in" result="glow"/>
          <feMerge><feMergeNode in="glow"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
      </defs>

      {/* Outer shield — gold with glow */}
      <path d="M50 2 L87 13 L87 57 Q87 89 50 111 Q13 89 13 57 L13 13 Z"
            fill={`url(#${uid}g)`} filter={`url(#${uid}gl)`}/>
      {/* Inner shield — dark */}
      <path d="M50 8 L82 18 L82 57 Q82 86 50 106 Q18 86 18 57 L18 18 Z"
            fill="#0A0A0A"/>
      {/* Blue inner ring */}
      <path d="M50 11 L80 20 L80 57 Q80 84 50 103 Q20 84 20 57 L20 20 Z"
            fill="none" stroke="#2563EB" strokeWidth="1" strokeOpacity="0.25"/>

      {/* Scissors — left blade */}
      <line x1="34" y1="25" x2="51" y2="52" stroke="#2563EB" strokeWidth="3.8" strokeLinecap="round"/>
      {/* Scissors — right blade */}
      <line x1="66" y1="25" x2="49" y2="52" stroke="#2563EB" strokeWidth="3.8" strokeLinecap="round"/>
      {/* Left handle ring */}
      <circle cx="30" cy="21" r="7.5" fill="none" stroke="#2563EB" strokeWidth="3"/>
      <circle cx="30" cy="21" r="3.5" fill="#2563EB" fillOpacity="0.15"/>
      {/* Right handle ring */}
      <circle cx="70" cy="21" r="7.5" fill="none" stroke="#2563EB" strokeWidth="3"/>
      <circle cx="70" cy="21" r="3.5" fill="#2563EB" fillOpacity="0.15"/>
      {/* Center pivot — gold */}
      <circle cx="50" cy="51" r="3.8" fill={`url(#${uid}g)`} stroke="#92400E" strokeWidth="0.8"/>

      {!small && (
        <>
          {/* Barber pole shaft */}
          <rect x="46.5" y="56" width="7" height="23" rx="2" fill="#1A1A1A" stroke="#2A2A2A" strokeWidth="0.5"/>
          {/* Red spiral stripes */}
          <line x1="46.5" y1="59.5" x2="53.5" y2="56.5" stroke="#EF4444" strokeWidth="2.2" strokeLinecap="round"/>
          <line x1="46.5" y1="64.5" x2="53.5" y2="61.5" stroke="#EF4444" strokeWidth="2.2" strokeLinecap="round"/>
          <line x1="46.5" y1="69.5" x2="53.5" y2="66.5" stroke="#EF4444" strokeWidth="2.2" strokeLinecap="round"/>
          <line x1="46.5" y1="74.5" x2="53.5" y2="71.5" stroke="#EF4444" strokeWidth="2.2" strokeLinecap="round"/>
          {/* Pole top ball */}
          <circle cx="50" cy="56" r="4.5" fill={`url(#${uid}g)`} stroke="#92400E" strokeWidth="0.5"/>
          {/* Pole base ball */}
          <circle cx="50" cy="79" r="3.2" fill={`url(#${uid}g)`} stroke="#92400E" strokeWidth="0.5"/>

          {/* Blue stars */}
          <text x="25" y="88" fontSize="12" fill="#2563EB" textAnchor="middle" dominantBaseline="middle">★</text>
          <text x="75" y="88" fontSize="12" fill="#2563EB" textAnchor="middle" dominantBaseline="middle">★</text>

          {/* Gold banner */}
          <path d="M17 84 L83 84 L83 97 L50 102 L17 97 Z" fill={`url(#${uid}g)`}/>
          <text x="50" y="93" textAnchor="middle" dominantBaseline="middle"
                fill="#0A0A0A" fontSize="8.5" fontWeight="900"
                fontFamily="'DM Sans',Arial,sans-serif" letterSpacing="2">FOUNDING</text>
        </>
      )}
    </svg>
  );
}

// ─── Platform helpers ─────────────────────────────────────────────────────────
// Capacitor's own bridge-detection reads window.webkit.messageHandlers internally.
// The polyfill at the top of this file covers the first check, but some social
// in-app browsers (Instagram) replace window.webkit with their own object later
// in the page lifecycle, without a messageHandlers property -- so any *later*
// isNativePlatform()/getPlatform() call can still throw. Wrap every call so a
// stale IAB bridge object can't crash whatever the user is doing at that moment.
const isNative = () => { try { return Capacitor.isNativePlatform(); } catch { return false; } };
const getPlatform = () => { try { return Capacitor.getPlatform(); } catch { return "web"; } };

// Compares dotted version strings ("1.0.21" vs "1.0.9") numerically per segment.
// Returns negative if a<b, 0 if equal, positive if a>b.
const compareVersions = (a, b) => {
  const pa = String(a).split(".").map(n => parseInt(n, 10) || 0);
  const pb = String(b).split(".").map(n => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const diff = (pa[i] || 0) - (pb[i] || 0);
    if (diff !== 0) return diff;
  }
  return 0;
};

const IS_NATIVE_IOS = getPlatform() === "ios";
const IS_APPLE = IS_NATIVE_IOS ||
  /iPad|iPhone|iPod|Macintosh/.test(navigator.userAgent);

// ─── Haptic helper — safe no-op on web ───────────────────────────────────────
const haptic = {
  light:   () => { if (isNative()) Haptics.impact({ style: ImpactStyle.Light   }).catch(()=>{}); },
  medium:  () => { if (isNative()) Haptics.impact({ style: ImpactStyle.Medium  }).catch(()=>{}); },
  success: () => { if (isNative()) Haptics.notification({ type: NotificationType.Success }).catch(()=>{}); },
};

// ─── LiveBadge — pulsing green beacon ────────────────────────────────────────
function LiveBadge({ label = "LIVE" }) {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
      <div style={{ position:"relative", width:8, height:8 }}>
        <span style={{ position:"absolute", inset:0, borderRadius:"50%", background:"#22C55E", opacity:0.75, animation:"ping 1.4s cubic-bezier(0,0,0.2,1) infinite" }}/>
        <span style={{ position:"relative", display:"block", width:8, height:8, borderRadius:"50%", background:"#22C55E" }}/>
      </div>
      <span style={{ fontSize:11, fontWeight:700, letterSpacing:"0.14em", color:"#22C55E", textTransform:"uppercase" }}>{label}</span>
    </div>
  );
}

// ─── GlassCard — frosted luxury card ─────────────────────────────────────────
function GlassCard({ children, style={}, glowColor="blue", onClick, className="" }) {
  const glowMap = { blue:"rgba(59,108,247,0.12)", green:"rgba(34,197,94,0.12)", gold:"rgba(245,158,11,0.12)" };
  return (
    <motion.div
      variants={cardHover}
      initial="rest"
      whileHover="hover"
      whileTap={{ scale:0.99 }}
      onClick={onClick}
      className={className}
      style={{
        position:"relative", overflow:"hidden",
        background:"rgba(26,31,46,0.72)",
        backdropFilter:"blur(20px)", WebkitBackdropFilter:"blur(20px)",
        border:"1px solid rgba(255,255,255,0.06)",
        boxShadow:"0 4px 24px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.04)",
        borderRadius:20,
        ...style
      }}
    >
      <div style={{ position:"absolute", top:0, left:0, right:0, height:1,
        background:`linear-gradient(90deg, transparent 0%, ${glowMap[glowColor]} 50%, transparent 100%)` }}/>
      {children}
    </motion.div>
  );
}

// ─── Skeleton — shimmer placeholder ──────────────────────────────────────────
function Skeleton({ style={}, className="" }) {
  return <div className={`skeleton ${className}`} style={{ minHeight:14, ...style }}/>;
}

function BarberCardSkeleton() {
  return (
    <div style={{ background:"#1A1F2E", borderRadius:16, padding:16, border:"1px solid #252B3B", marginBottom:10 }}>
      <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:12 }}>
        <Skeleton style={{ width:44, height:44, borderRadius:"50%" }}/>
        <div style={{ flex:1 }}>
          <Skeleton style={{ width:"60%", height:14, marginBottom:6 }}/>
          <Skeleton style={{ width:"40%", height:11 }}/>
        </div>
      </div>
      <div style={{ display:"flex", gap:8 }}>
        <Skeleton style={{ flex:1, height:11 }}/>
        <Skeleton style={{ flex:1, height:11 }}/>
        <Skeleton style={{ flex:1, height:11 }}/>
      </div>
    </div>
  );
}

function BookingCardSkeleton() {
  return (
    <div style={{ background:"#1A1F2E", borderRadius:14, overflow:"hidden", marginBottom:10, border:"1px solid #252B3B" }}>
      <Skeleton style={{ height:36, borderRadius:0 }}/>
      <div style={{ padding:"12px 16px" }}>
        <Skeleton style={{ width:"70%", height:13, marginBottom:8 }}/>
        <Skeleton style={{ width:"50%", height:11, marginBottom:8 }}/>
        <Skeleton style={{ width:"40%", height:11, marginBottom:12 }}/>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
          <Skeleton style={{ height:38, borderRadius:10 }}/>
          <Skeleton style={{ height:38, borderRadius:10 }}/>
        </div>
      </div>
    </div>
  );
}

// ─── CountUp — animated number ────────────────────────────────────────────────
function CountUp({ value, prefix="$", suffix="", duration=1200, decimals=0 }) {
  const [display, setDisplay] = React.useState(0);
  const startTime = useRef(null);
  const target = parseFloat(value) || 0;
  const animRef = useRef(null);
  useEffect(() => {
    startTime.current = null;
    if (animRef.current) cancelAnimationFrame(animRef.current);
    const animate = (ts) => {
      if (!startTime.current) startTime.current = ts;
      const elapsed = ts - startTime.current;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(target * eased);
      if (progress < 1) animRef.current = requestAnimationFrame(animate);
    };
    animRef.current = requestAnimationFrame(animate);
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [target, duration]); // eslint-disable-line react-hooks/exhaustive-deps
  return <span>{prefix}{display.toFixed(decimals)}{suffix}</span>;
}

// ping keyframe injected once
if (typeof document !== "undefined" && !document.getElementById("faded-ping-style")) {
  const s = document.createElement("style");
  s.id = "faded-ping-style";
  s.textContent = `@keyframes ping { 75%,100% { transform:scale(2); opacity:0; } }`;
  document.head.appendChild(s);
}

function Avatar({ barber, size=52 }) {
  const a = barber.accent_color||"#2563EB";
  // The status dot sits on its own sibling layer, outside the overflow:hidden
  // circle that clips the photo -- otherwise it gets trapped over the photo
  // instead of sitting at the edge like a normal presence indicator.
  if (barber.avatar_url) return (
    <div style={{ width:size, height:size, flexShrink:0, position:"relative" }}>
      <div style={{ width:"100%", height:"100%", borderRadius:"50%", border:`2.5px solid ${a}55`, overflow:"hidden" }}>
        <img src={barber.avatar_url} alt={barber.full_name} loading="lazy" style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
      </div>
      <span style={{ position:"absolute", bottom:-1, right:-1 }}><Dot status={barber.status}/></span>
    </div>
  );
  return (
    <div style={{ width:size, height:size, flexShrink:0, position:"relative" }}>
      <div style={{ width:"100%", height:"100%", borderRadius:"50%", background:`linear-gradient(135deg,${a}22,${a}44)`, border:`2.5px solid ${a}55`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:size*0.3, fontWeight:800, color:a, fontFamily:"'DM Sans',sans-serif" }}>
        {initials(barber.full_name)}
      </div>
      <span style={{ position:"absolute", bottom:-1, right:-1 }}><Dot status={barber.status}/></span>
    </div>
  );
}
function StatusPill({ status }) {
  const ctx = useT();
  const STATUS_T = ctx ? ctx.t.STATUS : STATUS;
  const s = STATUS_T[status]||STATUS_T.offline;
  if (status === "available") return <LiveBadge label="Available Now"/>;
  return <div style={{ display:"flex", alignItems:"center", gap:5, background:s.bg, padding:"4px 10px", borderRadius:20, width:"fit-content" }}><Dot status={status}/><span style={{ fontSize:11, fontWeight:700, color:s.color }}>{s.label}</span></div>;
}

// ─── TOAST NOTIFICATIONS ─────────────────────────────────────────────────────
function Toast({ toast }) {
  const colors = { info:"#2563EB", success:"#16A34A", error:"#DC2626", warn:"#D97706" };
  if (!toast) return null;
  const isUrgent = toast.type === "error";
  return (
    <div
      role={isUrgent ? "alert" : "status"}
      aria-live={isUrgent ? "assertive" : "polite"}
      aria-atomic="true"
      style={{ position:"fixed", bottom:"calc(max(4px, env(safe-area-inset-bottom, 0px)) + 96px)", left:"50%", transform:"translateX(-50%)", zIndex:500, background:colors[toast.type]||colors.info, color:"#fff", padding:"12px 20px", borderRadius:14, fontSize:13, fontWeight:600, fontFamily:"'DM Sans',sans-serif", boxShadow:"0 8px 24px rgba(0,0,0,0.3)", animation:"popIn 0.2s ease", whiteSpace:"nowrap", maxWidth:"90vw", textAlign:"center" }}>
      {toast.msg}
    </div>
  );
}

// ─── CONFIRM DIALOG (replaces window.confirm — forbidden on iOS) ─────────────
let _confirmResolve = null;
function fakedConfirm(msg) {
  return new Promise(resolve => {
    _confirmResolve = resolve;
    window.dispatchEvent(new CustomEvent("faded-confirm", { detail: { msg } }));
  });
}
function ConfirmDialog() {
  const [dialog, setDialog] = React.useState(null);
  React.useEffect(() => {
    const h = e => setDialog(e.detail);
    window.addEventListener("faded-confirm", h);
    return () => window.removeEventListener("faded-confirm", h);
  }, []);
  if (!dialog) return null;
  const resolve = (val) => { _confirmResolve?.(val); _confirmResolve = null; setDialog(null); };
  return (
    <div role="alertdialog" aria-modal="true" aria-label={dialog.msg}
      style={{ position:"fixed", inset:0, zIndex:9999, background:"rgba(0,0,0,0.72)", backdropFilter:"blur(8px)", display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
      <div style={{ background:"#1a1a1a", border:"1px solid rgba(245,245,240,0.12)", borderRadius:20, padding:"28px 24px", maxWidth:320, width:"100%", textAlign:"center", boxShadow:"0 24px 64px rgba(0,0,0,0.6)" }}>
        <div style={{ fontSize:15, fontWeight:700, color:"#F5F5F0", fontFamily:"'DM Sans',sans-serif", lineHeight:1.45, marginBottom:24 }}>{dialog.msg}</div>
        <div style={{ display:"flex", gap:10 }}>
          <button onClick={()=>resolve(false)} style={{ flex:1, padding:"13px", background:"rgba(245,245,240,0.08)", border:"none", borderRadius:12, color:"rgba(245,245,240,0.6)", fontSize:14, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>Cancel</button>
          <button onClick={()=>resolve(true)} style={{ flex:1, padding:"13px", background:"#EF4444", border:"none", borderRadius:12, color:"#fff", fontSize:14, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>Confirm</button>
        </div>
      </div>
    </div>
  );
}

// ─── REALTIME HEALTH HOOK (FIX 10) ────────────────────────────────────────────
function useRealtimeHealth() {
  const [status, setStatus] = useState("connected"); // "connected" | "reconnecting" | "back"
  const statusRef = useRef("connected"); // avoid stale closure in subscribe callback
  useEffect(() => {
    let backTimer = null;
    let reconnectTimeout = null;
    const ch = supabase.channel("__health__")
      .subscribe((s) => {
        if (s === "SUBSCRIBED") {
          clearTimeout(reconnectTimeout);
          if (statusRef.current === "reconnecting") {
            statusRef.current = "back";
            setStatus("back");
            clearTimeout(backTimer);
            backTimer = setTimeout(() => { statusRef.current = "connected"; setStatus("connected"); }, 2500);
          }
        } else if (s === "CHANNEL_ERROR" || s === "TIMED_OUT" || s === "CLOSED") {
          statusRef.current = "reconnecting";
          setStatus("reconnecting");
          // Auto-clear banner after 8 s — Supabase client retries internally
          reconnectTimeout = setTimeout(() => { statusRef.current = "connected"; setStatus("connected"); }, 8000);
        }
      });
    return () => { supabase.removeChannel(ch); clearTimeout(backTimer); clearTimeout(reconnectTimeout); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return status;
}

// ─── GPS HELPER ───────────────────────────────────────────────────────────────
// Uses Capacitor Geolocation on native (shows app name in permission dialog, not "localhost")
async function getPosition(opts = {}) {
  const defaults = { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 };
  const o = { ...defaults, ...opts };
  if (isNative()) {
    const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: o.enableHighAccuracy, timeout: o.timeout, maximumAge: o.maximumAge });
    return pos;
  }
  return new Promise((resolve, reject) => navigator.geolocation.getCurrentPosition(resolve, reject, o));
}

// ─── GPS HOOK ─────────────────────────────────────────────────────────────────
function useGPS() {
  const [location, setLocation] = useState({ lat:null, lng:null, error:null, loading:true });
  const fetch = useCallback(() => {
    if (!isNative() && !navigator.geolocation) { setLocation({ lat:null, lng:null, error:"GPS not supported", loading:false }); return; }
    setLocation(prev => ({ ...prev, loading:true }));
    getPosition({ enableHighAccuracy:true, timeout:12000, maximumAge:0 })
      .then(pos => setLocation({ lat:pos.coords.latitude, lng:pos.coords.longitude, error:null, loading:false }))
      .catch(err => setLocation({ lat:null, lng:null, error:err.message, loading:false }));
  }, []);
  useEffect(() => { fetch(); }, [fetch]);
  return { ...location, refresh: fetch };
}

// ─── WINDOW WIDTH HOOK ────────────────────────────────────────────────────────
function useWindowWidth() {
  const [width, setWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 1280);
  useEffect(() => {
    const h = () => setWidth(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return width;
}

// ─── SMS HELPER ───────────────────────────────────────────────────────────────
const sendSMS = async (to, message) => {
  if (!to) return;
  try { await supabase.functions.invoke("send-sms", { body: { to, message } }); } catch(e) {}
};

const normalizePhone = (phone) => {
  if (!phone) return phone;
  const trimmed = phone.trim().replace(/\s/g, "");
  if (trimmed.startsWith("+")) return trimmed;
  const digits = trimmed.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  if (digits.length === 10) return `+1${digits}`;
  return trimmed;
};

// ─── PUSH NOTIFICATIONS ───────────────────────────────────────────────────────
async function requestNotificationPermission() {
  if (!("Notification" in window)) return false;
  if (Notification.permission === "granted") return true;
  if (Notification.permission === "denied") return false;
  const result = await Notification.requestPermission();
  return result === "granted";
}

async function sendNotification(title, body, icon="✂️") {
  if (!("Notification" in window) || Notification.permission !== "granted") return;
  const opts = { body, icon: "/favicon.ico", badge: "/favicon.ico", tag: "faded-notification" };
  // Mobile requires service worker context — new Notification() is desktop-only
  if ("serviceWorker" in navigator) {
    try {
      const reg = await navigator.serviceWorker.ready;
      await reg.showNotification(title, opts);
      return;
    } catch(e) {}
  }
  new Notification(title, opts);
}

// ─── WEB PUSH SUBSCRIPTION ────────────────────────────────────────────────────
const VAPID_PUBLIC_KEY = "BMtUqPYAM-fRnE-URct_kQl6kKSyIkqYqBV_j-A6swu0VbBg8Qjw-opcPw4PZTmCulea7RvJbvCxfOs5fupHULk";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

async function subscribeToPush(userId) {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
  const perm = await Notification.requestPermission();
  if (perm !== "granted") return;
  try {
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
    }
    await supabase.from("push_subscriptions").upsert(
      { barber_id: userId, user_id: userId, endpoint: sub.endpoint, subscription: sub.toJSON() },
      { onConflict: "endpoint" }
    );
  } catch (e) {
    console.warn("Push subscription failed:", e);
  }
}

// Schedule a repeating "time for a fresh cut" reminder
function scheduleCutReminder(intervalWeeks=2) {
  try {
    const key = "faded_reminder_set";
    if (localStorage.getItem(key)) return;
    const intervalMs = intervalWeeks * 7 * 24 * 60 * 60 * 1000;
    // First reminder in 2 weeks
    const nextReminder = Date.now() + intervalMs;
    localStorage.setItem(key, nextReminder.toString());
    localStorage.setItem("faded_reminder_interval", intervalMs.toString());
  } catch(e) {}
}

function checkCutReminder() {
  try {
    const next = localStorage.getItem("faded_reminder_set");
    const interval = localStorage.getItem("faded_reminder_interval");
    if (!next || !interval) return;
    if (Date.now() >= parseInt(next)) {
      sendNotification("Time for a Fresh Cut! ✂️", "It has been 2 weeks — FADED has barbers available near you right now.", "✂️");
      // Reset for next reminder
      localStorage.setItem("faded_reminder_set", (Date.now() + parseInt(interval)).toString());
    }
  } catch(e) {}
}

function scheduleApptReminder(scheduledAt, barberName, serviceName, bookingId) {
  try {
    if (!scheduledAt || !bookingId) return;
    const apptMs = new Date(scheduledAt).getTime();
    if (isNaN(apptMs)) return;
    const reminderMs = apptMs - 2 * 60 * 60 * 1000; // T-2hr
    if (reminderMs <= Date.now()) return; // already past
    const stored = JSON.parse(localStorage.getItem("faded_appt_reminders") || "{}");
    stored[bookingId] = { at: reminderMs, barberName, serviceName, fired: false };
    localStorage.setItem("faded_appt_reminders", JSON.stringify(stored));
  } catch(e) {}
}

function checkApptReminders() {
  try {
    const stored = JSON.parse(localStorage.getItem("faded_appt_reminders") || "{}");
    let changed = false;
    for (const id of Object.keys(stored)) {
      const r = stored[id];
      if (!r.fired && Date.now() >= r.at) {
        const firstName = (r.barberName || "your barber").split(" ")[0];
        sendNotification(
          `✂️ Your cut is in 2 hours`,
          `${r.serviceName || "Haircut"} with ${firstName} — head out soon and arrive looking fresh.`,
          "✂️"
        );
        stored[id].fired = true;
        changed = true;
      }
    }
    if (changed) localStorage.setItem("faded_appt_reminders", JSON.stringify(stored));
  } catch(e) {}
}

// Holiday/occasion notifications
const UPCOMING_OCCASIONS = [
  { name:"Prom Season", date:"2025-05-15", msg:"Prom is around the corner! Book your barber now before slots fill up. 💈" },
  { name:"Father's Day", date:"2025-06-15", msg:"Father's Day is coming up — treat dad to a fresh cut on FADED! 🎁" },
  { name:"Canada Day", date:"2025-07-01", msg:"Canada Day weekend! Get fresh before the celebrations. 💳✂️" },
  { name:"Back to School", date:"2025-08-25", msg:"Back to school season — fresh cut, fresh start. Book now on FADED! 📚✂️" },
  { name:"Labour Day", date:"2025-08-30", msg:"Labour Day weekend is here — end the summer looking sharp! 💈" },
  { name:"Halloween", date:"2025-10-28", msg:"Halloween is 3 days away — get that fresh cut for your costume! 🎃✂️" },
  { name:"Christmas", date:"2025-12-20", msg:"Christmas is coming — book your cut before the holidays! 🎄✂️" },
  { name:"New Years", date:"2025-12-28", msg:"New Year, fresh cut. Book before January 1st fills up! 🎆✂️" },
  { name:"Valentines", date:"2026-02-10", msg:"Valentine's Day is coming — look your best! Book now. ❤️✂️" },
  { name:"Easter", date:"2026-04-01", msg:"Easter weekend — get a fresh cut for the family gatherings! 🐣✂️" },
];

function checkUpcomingOccasions() {
  try {
    const now = new Date();
    const seenKey = "faded_seen_occasions";
    let seen = [];
    try { seen = JSON.parse(localStorage.getItem(seenKey)||"[]"); } catch(e) { seen = []; }
    for (const occasion of UPCOMING_OCCASIONS) {
      const eventDate = new Date(occasion.date);
      const daysUntil = Math.ceil((eventDate - now) / (1000*60*60*24));
      if (daysUntil >= 0 && daysUntil <= 7 && !seen.includes(occasion.name)) {
        sendNotification(`${occasion.name} is Coming Up!`, occasion.msg);
        seen.push(occasion.name);
        try { localStorage.setItem(seenKey, JSON.stringify(seen)); } catch(e) {}
        break; // Only one notification at a time
      }
    }
  } catch(e) {}
}

// ─── CLIENT WELCOME SPLASH ────────────────────────────────────────────────────
const CLIENT_TOP = ["Y","O","U","R"," ","S","E","A","T"];
const CLIENT_BOT = ["I","S"," ","R","E","A","D","Y"];

function ClientWelcomeSplash({ onDone }) {
  const [phase, setPhase] = useState("hidden");

  useEffect(() => {
    // Mark immediately so any re-render or remount of App skips this splash
    try { sessionStorage.setItem("faded_client_welcomed","1"); } catch {}
    const t0 = setTimeout(() => setPhase("line"),  200);
    const t1 = setTimeout(() => setPhase("words"), 660);
    const t2 = setTimeout(() => setPhase("sub"),  1280);
    const t3 = setTimeout(() => setPhase("out"),  2000);
    const t4 = setTimeout(() => onDone?.(),       2700);
    return () => [t0,t1,t2,t3,t4].forEach(clearTimeout);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const visible = phase !== "hidden" && phase !== "line";

  return (
    <div style={{
      position:"fixed", inset:0, background:"#0A0A0A",
      display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
      zIndex:9999, overflow:"hidden",
      opacity: phase === "out" ? 0 : 1,
      transition: phase === "out" ? "opacity 0.62s cubic-bezier(0.4,0,0.2,1)" : "opacity 0.2s ease",
    }}>
      <style>{`
        @keyframes lineGrow {
          from { width: 0; }
          to   { width: 164px; }
        }
      `}</style>

      {/* TOP — YOUR SEAT */}
      <div style={{ display:"flex", alignItems:"baseline", justifyContent:"center", marginBottom:20 }}>
        {CLIENT_TOP.map((l, i) => (
          <span key={i} style={{
            fontFamily:"'Bebas Neue', sans-serif",
            fontSize:"clamp(44px, 10vw, 68px)",
            fontWeight:900,
            letterSpacing:"0.1em",
            color:"#fff",
            display:"inline-block",
            opacity:  visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(-22px)",
            transition: `opacity 0.38s ease ${i * 0.07}s, transform 0.44s cubic-bezier(0.22,1,0.36,1) ${i * 0.07}s`,
          }}>
            {l === " " ? " " : l}
          </span>
        ))}
      </div>

      {/* Gold divider line */}
      <div style={{
        height:1,
        background:"linear-gradient(90deg, transparent, #C9A84C 20%, #E8C97A 50%, #C9A84C 80%, transparent)",
        borderRadius:1,
        animation: phase !== "hidden" ? "lineGrow 0.42s cubic-bezier(0.22,1,0.36,1) forwards" : "none",
        width: phase === "hidden" ? 0 : undefined,
        boxShadow:"0 0 12px rgba(201,168,76,0.4)",
        marginBottom:20,
      }}/>

      {/* BOTTOM — IS READY */}
      <div style={{ display:"flex", alignItems:"baseline", justifyContent:"center" }}>
        {CLIENT_BOT.map((l, i) => (
          <span key={i} style={{
            fontFamily:"'Bebas Neue', sans-serif",
            fontSize:"clamp(44px, 10vw, 68px)",
            fontWeight:900,
            letterSpacing:"0.1em",
            color:"#fff",
            display:"inline-block",
            opacity:  visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(22px)",
            transition: `opacity 0.38s ease ${(i + CLIENT_TOP.length) * 0.06}s, transform 0.44s cubic-bezier(0.22,1,0.36,1) ${(i + CLIENT_TOP.length) * 0.06}s`,
          }}>
            {l === " " ? " " : l}
          </span>
        ))}
      </div>

      {/* Subtext */}
      <div style={{
        marginTop:28, fontSize:11, fontWeight:400, color:"#C9A84C",
        letterSpacing:"0.42em", textTransform:"uppercase",
        fontFamily:"'DM Sans', sans-serif",
        opacity: (phase === "sub" || phase === "out") ? 1 : 0,
        transition:"opacity 0.55s ease",
      }}>
        Welcome to Faded
      </div>
    </div>
  );
}

// ─── BARBER WELCOME SPLASH ────────────────────────────────────────────────────
const BARBER_TOP = ["B","L","A","D","E"];
const BARBER_BOT = ["R","E","A","D","Y"];

function BarberWelcomeSplash({ onDone }) {
  const [phase, setPhase] = useState("hidden");

  useEffect(() => {
    const t0 = setTimeout(() => setPhase("scissors"), 200);
    const t1 = setTimeout(() => setPhase("words"),    820);
    const t2 = setTimeout(() => setPhase("sub"),     1380);
    const t3 = setTimeout(() => setPhase("out"),     2100);
    const t4 = setTimeout(() => onDone?.(),          2800);
    return () => [t0,t1,t2,t3,t4].forEach(clearTimeout);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const wordsVisible = phase !== "hidden" && phase !== "scissors";

  return (
    <div style={{
      position:"fixed", inset:0, background:"#0A0A0A",
      display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
      zIndex:9999, overflow:"hidden",
      opacity: phase === "out" ? 0 : 1,
      transition: phase === "out" ? "opacity 0.62s cubic-bezier(0.4,0,0.2,1)" : "opacity 0.2s ease",
    }}>
      <style>{`
        @keyframes scissorLeft  { from { width:0; } to { width:44px; } }
        @keyframes scissorRight { from { width:0; } to { width:44px; } }
      `}</style>

      {/* TOP — BLADE */}
      <div style={{ display:"flex", alignItems:"baseline", justifyContent:"center", marginBottom:22 }}>
        {BARBER_TOP.map((l, i) => (
          <span key={i} style={{
            fontFamily:"'Bebas Neue', sans-serif",
            fontSize:"clamp(52px, 12vw, 80px)",
            fontWeight:900,
            letterSpacing:"0.14em",
            color:"#fff",
            display:"inline-block",
            opacity:  wordsVisible ? 1 : 0,
            transform: wordsVisible ? "translateY(0)" : "translateY(-24px)",
            transition: `opacity 0.38s ease ${i * 0.09}s, transform 0.46s cubic-bezier(0.22,1,0.36,1) ${i * 0.09}s`,
            textShadow:"0 2px 24px rgba(255,255,255,0.06)",
          }}>
            {l}
          </span>
        ))}
      </div>

      {/* Scissor X — two diagonal lines meeting from edges */}
      <div style={{ position:"relative", width:96, height:32, marginBottom:22, display:"flex", alignItems:"center", justifyContent:"center" }}>
        {/* Left arm — draws right */}
        <div style={{
          position:"absolute", left:0, top:"50%", height:1.5,
          background:"linear-gradient(90deg, transparent, #C9A84C)",
          transform:"translateY(-50%) rotate(35deg)",
          transformOrigin:"left center",
          boxShadow:"0 0 8px rgba(201,168,76,0.5)",
          animation: phase !== "hidden" ? "scissorLeft 0.44s cubic-bezier(0.22,1,0.36,1) forwards" : "none",
          width: phase === "hidden" ? 0 : undefined,
        }}/>
        {/* Right arm — draws left */}
        <div style={{
          position:"absolute", right:0, top:"50%", height:1.5,
          background:"linear-gradient(270deg, transparent, #C9A84C)",
          transform:"translateY(-50%) rotate(-35deg)",
          transformOrigin:"right center",
          boxShadow:"0 0 8px rgba(201,168,76,0.5)",
          animation: phase !== "hidden" ? "scissorRight 0.44s cubic-bezier(0.22,1,0.36,1) forwards" : "none",
          width: phase === "hidden" ? 0 : undefined,
        }}/>
        {/* Center gold dot where they meet */}
        <div style={{
          width:5, height:5, borderRadius:"50%", background:"#C9A84C",
          boxShadow:"0 0 10px rgba(201,168,76,0.8)",
          opacity: phase !== "hidden" ? 1 : 0,
          transition:"opacity 0.3s ease 0.4s",
        }}/>
      </div>

      {/* BOTTOM — READY */}
      <div style={{ display:"flex", alignItems:"baseline", justifyContent:"center" }}>
        {BARBER_BOT.map((l, i) => (
          <span key={i} style={{
            fontFamily:"'Bebas Neue', sans-serif",
            fontSize:"clamp(52px, 12vw, 80px)",
            fontWeight:900,
            letterSpacing:"0.14em",
            color:"#fff",
            display:"inline-block",
            opacity:  wordsVisible ? 1 : 0,
            transform: wordsVisible ? "translateY(0)" : "translateY(24px)",
            transition: `opacity 0.38s ease ${(i + BARBER_TOP.length) * 0.08}s, transform 0.46s cubic-bezier(0.22,1,0.36,1) ${(i + BARBER_TOP.length) * 0.08}s`,
            textShadow:"0 2px 24px rgba(255,255,255,0.06)",
          }}>
            {l}
          </span>
        ))}
      </div>

      {/* Subtext */}
      <div style={{
        marginTop:30, fontSize:11, fontWeight:400, color:"#C9A84C",
        letterSpacing:"0.42em", textTransform:"uppercase",
        fontFamily:"'DM Sans', sans-serif",
        opacity: (phase === "sub" || phase === "out") ? 1 : 0,
        transition:"opacity 0.55s ease",
      }}>
        Let&apos;s get to work
      </div>
    </div>
  );
}

// ─── ANIMATED SPLASH SCREEN ──────────────────────────────────────────────────
const SPLASH_LETTERS = [
  { char:"F", blue:false },
  { char:"A", blue:false },
  { char:"D", blue:false },
  { char:"E", blue:true  },
  { char:"D", blue:true  },
];

function SplashScreen({ onDone }) {
  const [phase, setPhase] = useState("entering");

  useEffect(() => {
    // Timeline: letters finish at ~560ms, shimmer at 900ms, exit at 1300ms, done at 1650ms
    const t1 = setTimeout(() => setPhase("holding"),  560);
    const t2 = setTimeout(() => setPhase("shimmer"),  900);
    const t3 = setTimeout(() => setPhase("exiting"),  1300);
    const t4 = setTimeout(() => onDone?.(),           1650);
    return () => [t1, t2, t3, t4].forEach(clearTimeout);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{
      position:"fixed", inset:0, background:"#0A0A0A",
      display:"flex", alignItems:"center", justifyContent:"center",
      zIndex:9999, overflow:"hidden",
      opacity: phase === "exiting" ? 0 : 1,
      transition: phase === "exiting" ? "opacity 0.65s cubic-bezier(0.4,0,0.2,1)" : "none",
    }}>
      <style>{`
        @keyframes shimmerSweep {
          from { transform: translateX(-140px) skewX(-14deg); }
          to   { transform: translateX(110vw)  skewX(-14deg); }
        }
        @keyframes taglineIn {
          from { opacity:0; letter-spacing:0.35em; }
          to   { opacity:1; letter-spacing:0.45em; }
        }
        @keyframes barExpand {
          from { transform:scaleX(0); opacity:0; }
          to   { transform:scaleX(1); opacity:1; }
        }
      `}</style>

      {/* Radial glow behind logo */}
      <div style={{
        position:"absolute", inset:0, pointerEvents:"none",
        background:"radial-gradient(ellipse 55% 40% at 50% 50%, rgba(37,99,235,0.1) 0%, transparent 70%)",
        opacity: phase === "entering" ? 0 : 1,
        transition: "opacity 0.8s ease 0.3s",
      }}/>

      {/* Gold shimmer blade — only during shimmer phase */}
      {(phase === "shimmer" || phase === "exiting") && (
        <div style={{
          position:"absolute", top:0, left:0, width:90, height:"100%",
          background:"linear-gradient(90deg, transparent 0%, rgba(212,175,55,0.05) 20%, rgba(212,175,55,0.22) 50%, rgba(255,255,255,0.18) 55%, rgba(212,175,55,0.22) 60%, rgba(212,175,55,0.05) 80%, transparent 100%)",
          animation:"shimmerSweep 0.72s cubic-bezier(0.4,0,0.2,1) forwards",
          pointerEvents:"none", zIndex:2,
        }}/>
      )}

      <div style={{ textAlign:"center", position:"relative", zIndex:1, userSelect:"none" }}>

        {/* FADED letters */}
        <div style={{ display:"flex", alignItems:"baseline", justifyContent:"center", gap:0 }}>
          {SPLASH_LETTERS.map((l, i) => (
            <span key={i} style={{
              fontFamily:"'Bebas Neue', sans-serif",
              fontSize:"clamp(80px, 18vw, 112px)",
              fontWeight:900,
              lineHeight:1,
              letterSpacing:"0.06em",
              color: l.blue ? "#2563EB" : "#ffffff",
              display:"inline-block",
              opacity:  phase === "entering" ? 0 : 1,
              transform: phase === "entering" ? "translateY(28px)" : "translateY(0)",
              transition: `opacity 0.42s ease ${i * 0.1}s, transform 0.48s cubic-bezier(0.22,1,0.36,1) ${i * 0.1}s`,
              textShadow: l.blue
                ? "0 0 48px rgba(37,99,235,0.55), 0 2px 20px rgba(37,99,235,0.3)"
                : "0 2px 24px rgba(255,255,255,0.08)",
            }}>
              {l.char}
            </span>
          ))}
        </div>

        {/* Accent bar — grows in after letters */}
        <div style={{
          height:3,
          width:64,
          background:"linear-gradient(90deg, transparent, #2563EB, #60A5FA, transparent)",
          borderRadius:2,
          margin:"14px auto 0",
          transformOrigin:"center",
          animation: phase !== "entering" ? "barExpand 0.5s cubic-bezier(0.22,1,0.36,1) forwards" : "none",
          opacity: phase === "entering" ? 0 : 1,
        }}/>

        {/* Tagline */}
        <div style={{
          marginTop:16,
          fontSize:11,
          color:"rgba(255,255,255,0.22)",
          fontFamily:"'DM Sans', sans-serif",
          fontWeight:600,
          textTransform:"uppercase",
          animation: phase !== "entering" ? "taglineIn 0.6s ease 0.55s both" : "none",
          opacity: phase === "entering" ? 0 : undefined,
        }}>
          find your barber
        </div>
      </div>
    </div>
  );
}

// ─── LEGAL MODAL ─────────────────────────────────────────────────────────────
const S = {
  h1: { fontSize:22, fontWeight:900, color:"#fff", fontFamily:"'DM Sans',sans-serif", margin:"0 0 6px" },
  h2: { fontSize:15, fontWeight:800, color:"#E8E8E8", fontFamily:"'DM Sans',sans-serif", margin:"24px 0 8px" },
  p:  { fontSize:13, color:"#aaa", lineHeight:1.7, margin:"0 0 10px" },
  li: { fontSize:13, color:"#aaa", lineHeight:1.7, margin:"0 0 6px", paddingLeft:16 },
  note: { fontSize:11, color:"#888", lineHeight:1.6 },
};
function TermsContent() {
  return (
    <div style={{ color:"#E8E8E8" }}>
      <p style={S.note}>Effective Date: June 24, 2026 · Last Updated: June 24, 2026</p>
      <p style={{ ...S.p, marginTop:12 }}>These Terms of Service ("Terms") govern your access to and use of FADED (the "Platform"), operated by FADED ("we," "us," "our"). By creating an account or using FADED, you agree to be bound by these Terms. If you do not agree, do not use the Platform.</p>

      <div style={S.h2}>1. Eligibility and Accounts</div>
      <p style={S.p}>To use FADED, you must be at least 16 years of age and provide accurate registration information. You are responsible for maintaining the security of your account credentials and may not share your account with any third party. FADED reserves the right to verify your identity at any time.</p>

      <div style={S.h2}>2. Description of Service</div>
      <p style={S.p}>FADED is a marketplace platform that connects Clients seeking grooming services with independent Barbers. FADED provides technology to facilitate discovery, booking, and communication between parties. FADED does not itself provide grooming or barber services.</p>

      <div style={S.h2}>3. The Booking System</div>
      <p style={S.p}>FADED's booking system allows Clients to discover and book appointments with Barbers in real time. Acceptance of a booking creates a direct agreement between the Client and the Barber — FADED is not a party to that agreement. Real-time availability displayed on the Platform is an approximation and may not always be accurate at the moment of booking.</p>

      <div style={S.h2}>4. Barbers Are Independent Contractors — Not Employees</div>
      <p style={S.p}>Barbers on FADED are independent contractors, not employees, agents, or representatives of FADED. FADED does not control how Barbers provide services, set their pricing, guarantee service quality, or supervise their work. Disputes arising from services rendered are between the Client and the Barber directly.</p>

      <div style={S.h2}>5. No Guarantee of Service Quality</div>
      <p style={S.p}>FADED makes no warranty regarding the skill, qualifications, or quality of any Barber on the Platform. Client reviews are provided for informational purposes only. We strongly recommend reviewing a Barber's profile and portfolio before booking, and communicating any specific requirements directly with the Barber.</p>

      <div style={S.h2}>6. Cancellations and No-Shows</div>
      <p style={S.p}>Cancellation policies are set by individual Barbers. FADED is not responsible for any losses from cancellations or no-shows by either party. Repeated no-shows or abusive cancellation behaviour may result in account suspension.</p>

      <div style={S.h2}>7. Payments and Fees</div>
      <p style={S.p}>FADED facilitates payments between Clients and Barbers via Stripe. A platform fee is charged on card transactions and disclosed at the time of payment. Barbers must complete Stripe Connect onboarding to receive card payouts. Cash and in-person payment methods are arranged directly between Client and Barber — FADED is not responsible for those transactions. All prices are in Canadian dollars (CAD). Applicable taxes are the responsibility of the individual Barber.</p>

      <div style={S.h2}>8. ⚠ Disclaimer — Third Party Services</div>
      <p style={{ ...S.p, color:"#f87171", fontWeight:600 }}>FADED IS NOT RESPONSIBLE FOR SERVICES PERFORMED BY BARBERS. ALL GROOMING SERVICES ARE PROVIDED BY INDEPENDENT BARBERS AT THEIR OWN DISCRETION. FADED EXPRESSLY DISCLAIMS ALL LIABILITY FOR PERSONAL INJURY, PROPERTY DAMAGE, DISSATISFACTION, OR ANY OTHER HARM ARISING FROM SERVICES PERFORMED BY A BARBER DISCOVERED THROUGH THE PLATFORM.</p>

      <div style={S.h2}>9. Prohibited Conduct</div>
      <p style={S.p}>You agree not to: provide false information; use the Platform for unlawful purposes; harass or abuse other users; attempt to circumvent service fees; scrape or extract Platform data; gain unauthorized system access; facilitate fraud or misrepresentation; or post fake reviews. FADED may investigate and suspend accounts for violations.</p>

      <div style={S.h2}>10. Fraud and Misuse Protection</div>
      <p style={S.p}>FADED employs automated and manual reviews to detect fraudulent activity. If FADED reasonably suspects fraud, impersonation, or misuse, we reserve the right to suspend or terminate the account immediately, withhold access to pending bookings, and report suspicious activity to relevant authorities.</p>

      <div style={S.h2}>11. Intellectual Property</div>
      <p style={S.p}>All Platform content — including the FADED name, logo, design, and software — is owned by or licensed to FADED. You retain ownership of content you submit but grant FADED a worldwide, royalty-free license to use and display such content in connection with operating the Platform.</p>

      <div style={S.h2}>12. Limitation of Liability</div>
      <p style={S.p}>TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, FADED SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE PLATFORM, SERVICES PROVIDED BY BARBERS, REAL-TIME AVAILABILITY INACCURACIES, OR UNAUTHORIZED ACCESS TO YOUR ACCOUNT. OUR TOTAL AGGREGATE LIABILITY FOR ANY CLAIMS ARISING UNDER OR RELATED TO THESE TERMS SHALL NOT EXCEED THE GREATER OF: (A) THE TOTAL FEES PAID BY YOU TO FADED IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM; OR (B) $100 CAD. NOTHING IN THESE TERMS LIMITS LIABILITY FOR GROSS NEGLIGENCE, WILFUL MISCONDUCT, DEATH, OR PERSONAL INJURY CAUSED BY OUR NEGLIGENCE, OR ANY LIABILITY THAT CANNOT BE LIMITED UNDER APPLICABLE ONTARIO OR FEDERAL CANADIAN LAW.</p>

      <div style={S.h2}>13. Indemnification</div>
      <p style={S.p}>You agree to indemnify and hold harmless FADED, its operators, and affiliates from any claims, damages, losses, or expenses (including legal fees) arising from your use of the Platform, your violation of these Terms, or your interactions with other users.</p>

      <div style={S.h2}>14. Account Suspension and Termination</div>
      <p style={S.p}>FADED reserves the right to suspend or permanently terminate your account at any time, with or without notice, for any violation of these Terms, abusive conduct, or activity determined to be harmful to other users or the Platform.</p>

      <div style={S.h2}>15. Dispute Resolution</div>
      <p style={S.p}>Disputes between Clients and Barbers are to be resolved directly between those parties. For disputes involving FADED directly, contact us first at hello@letsgetfaded.com. If resolution cannot be reached, disputes shall be subject to binding arbitration under the laws of Ontario, Canada.</p>

      <div style={S.h2}>16. Governing Law</div>
      <p style={S.p}>These Terms are governed by the laws of Ontario and the federal laws of Canada. You submit to the jurisdiction of courts located in Ontario for any disputes not resolved by arbitration.</p>

      <div style={S.h2}>17. Changes to These Terms</div>
      <p style={S.p}>FADED may update these Terms at any time. We will notify you of material changes by updating the Effective Date and, where appropriate, by email or in-app notification. Continued use after changes constitutes acceptance.</p>

      <div style={S.h2}>18. Barber Licensing Disclaimer</div>
      <p style={S.p}>Barbers on FADED self-report their licence status and licence numbers. FADED does not independently verify licence information against any provincial registry. Clients are encouraged to confirm a barber's licensing status independently if it is material to their decision to book. FADED accepts no liability for services rendered by an unlicensed individual discovered through the Platform.</p>

      <div style={S.h2}>19. Contact</div>
      <p style={S.p}>FADED · hello@letsgetfaded.com · Ontario, Canada · letsgetfaded.com</p>
      <p style={{ ...S.p, color:"#888", fontSize:11 }}>⚠ These Terms were prepared for informational purposes. They should be reviewed by a licensed Ontario lawyer before publication.</p>
    </div>
  );
}

function PrivacyContent() {
  return (
    <div style={{ color:"#E8E8E8" }}>
      <p style={S.note}>Effective Date: June 24, 2026 · Last Updated: June 24, 2026</p>
      <p style={{ ...S.p, marginTop:12 }}>This Privacy Policy explains how FADED ("we," "us," "our"), operating the FADED platform, collects, uses, discloses, and protects your personal information. It is written in compliance with Canada's Personal Information Protection and Electronic Documents Act (PIPEDA). By using FADED, you consent to the practices described in this policy.</p>

      <div style={S.h2}>1. Information We Collect</div>
      <p style={{ ...S.p, fontWeight:600, color:"#E8E8E8" }}>Information you provide:</p>
      <p style={S.p}>Full name, email address, and password when creating an account. Phone number used for booking confirmations and SMS reminders. For Barbers: handle, bio, specialties, pricing, shop address, city, neighbourhood, and province. Profile photos and portfolio images.</p>
      <p style={{ ...S.p, fontWeight:600, color:"#E8E8E8" }}>Information collected automatically:</p>
      <p style={S.p}>GPS/location data (with your permission) to show nearby barbers. Booking history and appointment data. Push notification device tokens (if you enable notifications).</p>
      <p style={{ ...S.p, fontWeight:600, color:"#E8E8E8" }}>Device storage:</p>
      <p style={S.p}>The app stores lightweight preference data on your device using local storage (e.g. last active tab, notification settings). This data never leaves your device.</p>
      <p style={{ ...S.p, fontWeight:600, color:"#E8E8E8" }}>From third parties:</p>
      <p style={S.p}>Authentication data from Google Sign-In (name, email, profile picture) and Apple Sign-In (name, email). When signing in with Apple, email may be anonymized at the user's discretion.</p>

      <div style={S.h2}>2. How We Use Your Information</div>
      <p style={S.p}>We use your information to: create and manage your account; match Clients with nearby Barbers using GPS data; send booking confirmations and updates via SMS and push notification; display barber profiles and portfolios to potential clients; operate the AI Schedule Assistant for barbers; calculate estimated taxes based on the barber's province; improve the app and fix technical issues; and comply with legal obligations. We do not sell your personal information to any third party.</p>

      <div style={S.h2}>3. Real-Time Location Data</div>
      <p style={S.p}>FADED uses your device's GPS to show you nearby barbers. Location data is only collected while the app is active, used solely for matching, and never shared with Barbers without your consent. You may disable location access in device settings at any time.</p>

      <div style={S.h2}>4. Third-Party Services</div>
      <p style={{ ...S.p, fontWeight:600, color:"#E8E8E8" }}>Supabase (supabase.com) — United States</p>
      <p style={S.p}>Database, authentication, file storage, and real-time data.</p>
      <p style={{ ...S.p, fontWeight:600, color:"#E8E8E8" }}>Stripe (stripe.com) — United States</p>
      <p style={S.p}>Payment processing when payment features are enabled. We do not store card numbers on our servers.</p>
      <p style={{ ...S.p, fontWeight:600, color:"#E8E8E8" }}>Mapbox (mapbox.com) — United States</p>
      <p style={S.p}>Address autocomplete, map display, and location search.</p>
      <p style={{ ...S.p, fontWeight:600, color:"#E8E8E8" }}>Apple APNs / Google FCM — United States</p>
      <p style={S.p}>Push notification delivery to iOS and Android devices respectively.</p>
      <p style={{ ...S.p, fontWeight:600, color:"#E8E8E8" }}>SMS provider (via Supabase) — United States</p>
      <p style={S.p}>Sending booking confirmation and reminder text messages. Phone numbers are used solely for this purpose.</p>
      <p style={{ ...S.p, fontWeight:600, color:"#E8E8E8" }}>Vercel (vercel.com) — United States</p>
      <p style={S.p}>Hosts and delivers the Platform. Vercel may log request metadata such as IP address and user agent.</p>

      <div style={S.h2}>5. Data Storage and Transfer Outside Canada</div>
      <p style={{ ...S.p, color:"#F59E0B" }}>Your data is stored in the United States. All service providers listed above operate servers in the United States, where privacy laws may differ from those in Canada. By using FADED you consent to this transfer.</p>

      <div style={S.h2}>6. Cookies and Local Storage</div>
      <p style={S.p}><strong style={{ color:"#E8E8E8" }}>Essential cookies:</strong> Required for authentication and session management. Cannot be disabled. <strong style={{ color:"#E8E8E8" }}>Local storage:</strong> Used for preferences such as your last active tab and notification state. We do not use third-party advertising or analytics cookies.</p>

      <div style={S.h2}>7. Data Sharing and Disclosure</div>
      <p style={S.p}>We do not sell or rent your personal information. We may share your data: with Barbers (your name, phone, and booking details when you make a booking); with service providers listed in Section 4; when required by law or court order; and in a business transfer (you will be notified in advance).</p>

      <div style={S.h2}>8. Data Retention and Safeguards</div>
      <p style={S.p}>We retain your information for as long as your account is active. You may request deletion at any time — we will action it within 30 days. Certain records may be retained for up to 7 years to comply with legal obligations. Data in transit is encrypted via TLS. Data at rest is encrypted by Supabase. Row-level security ensures users can only access their own data. In the event of a breach that poses real risk of harm, we will notify affected users and the Office of the Privacy Commissioner of Canada as required by PIPEDA.</p>

      <div style={S.h2}>9. Your Rights (PIPEDA)</div>
      <p style={S.p}>You have the right to: access the personal information we hold about you; request correction of inaccurate data; request deletion of your account and data; and withdraw consent for location, SMS, or push notifications at any time. To exercise these rights, email hello@letsgetfaded.com. We will respond within 30 days.</p>
      <p style={{ ...S.p, color:"#93C5FD" }}>You may also lodge a complaint with the Office of the Privacy Commissioner of Canada: www.priv.gc.ca · 1-800-282-1376</p>

      <div style={S.h2}>10. Children's Privacy</div>
      <p style={S.p}>FADED is not intended for users under 16. We do not knowingly collect personal information from individuals under 16. If we become aware that a person under 16 has provided personal information, we will promptly delete it. If you believe a minor's data has been collected, contact us at hello@letsgetfaded.com.</p>

      <div style={S.h2}>11. Accountability — Privacy Officer</div>
      <p style={S.p}>FADED is responsible for the personal information under its control. Our Privacy Officer can be contacted at: hello@letsgetfaded.com · Subject line: "Privacy Inquiry." We will acknowledge your inquiry within 5 business days and respond in full within 30 days.</p>

      <div style={S.h2}>12. Changes to This Policy</div>
      <p style={S.p}>We may update this Privacy Policy from time to time. We will notify you of material changes by updating the Effective Date above and, where appropriate, by email or in-app notification. Continued use of FADED after a material change constitutes your acceptance of the updated policy. We will not retroactively reduce your rights without obtaining fresh consent.</p>

      <div style={S.h2}>13. Contact and Complaints</div>
      <p style={S.p}>FADED · Privacy Officer · hello@letsgetfaded.com · Ontario, Canada · letsgetfaded.com</p>
      <p style={{ ...S.p, color:"#93C5FD" }}>You may also lodge a complaint with the Office of the Privacy Commissioner of Canada: www.priv.gc.ca · 1-800-282-1376</p>
    </div>
  );
}

function LegalModal({ doc, onClose }) {
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.92)", zIndex:10000, overflowY:"auto", WebkitOverflowScrolling:"touch" }}>
      <div style={{ maxWidth:720, margin:"0 auto", padding:"0 20px 80px", fontFamily:"'DM Sans',sans-serif" }}>
        <div style={{ position:"sticky", top:0, background:"#0f0f0f", borderBottom:"1px solid #1a1a1a", paddingTop:"max(16px, env(safe-area-inset-top, 16px))", paddingBottom:16, display:"flex", justifyContent:"space-between", alignItems:"center", zIndex:1 }}>
          <div>
            <div style={S.h1}>{doc==="terms" ? "Terms of Service" : "Privacy Policy"}</div>
            <div style={{ fontSize:11, color:"#555" }}>FADED · Ontario, Canada</div>
          </div>
          <button onClick={onClose} style={{ background:"#1a1a1a", border:"1px solid #2a2a2a", borderRadius:10, color:"#888", padding:"8px 16px", cursor:"pointer", fontSize:13, fontWeight:700, whiteSpace:"nowrap" }}>Close ✕</button>
        </div>
        <div style={{ paddingTop:24 }}>
          {doc==="terms" ? <TermsContent/> : <PrivacyContent/>}
        </div>
      </div>
    </div>
  );
}

// ─── STANDALONE LEGAL PAGES (/privacy and /terms) ────────────────────────────
function LegalPage({ doc }) {
  return (
    <div style={{ minHeight:"100dvh", background:"#0A0A0A", color:"#F5F5F0", fontFamily:"'DM Sans',sans-serif", overflowX:"hidden" }}>
      <style>{`*{box-sizing:border-box;margin:0;padding:0;}`}</style>
      <div style={{ maxWidth:720, margin:"0 auto", padding:"48px 24px 80px" }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:40 }}>
          <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:32, letterSpacing:3 }}>FADED</div>
          <a href="https://app.letsgetfaded.com" style={{ color:"#2563EB", fontSize:14, fontWeight:600, textDecoration:"none" }}>← Back to App</a>
        </div>
        <h1 style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:36, letterSpacing:2, marginBottom:32 }}>
          {doc==="terms" ? "Terms of Service" : "Privacy Policy"}
        </h1>
        {doc==="terms" ? <TermsContent/> : <PrivacyContent/>}
      </div>
    </div>
  );
}

// ─── LANDING PAGE (built in — no separate deployment needed) ─────────────────
function LandingPage({ onEnter, onSignIn }) {
  const [legalDoc, setLegalDoc] = useState(null);
  const [founderCount, setFounderCount] = useState(0);
  const FOUNDING_LIMIT = 20;
  useEffect(() => {
    // RPC bypasses RLS — direct table query was silently blocked by anon policy
    supabase.rpc("get_barber_waitlist_count")
      .then(({ data }) => { if (typeof data === "number") setFounderCount(data); })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div style={{ background:"#0A0A0A", color:"#F5F5F0", fontFamily:"'DM Sans',sans-serif" }}>
      {legalDoc && <LegalModal doc={legalDoc} onClose={() => setLegalDoc(null)}/>}
      <style>{`
              @keyframes pulse{0%,100%{box-shadow:0 0 0 3px rgba(34,197,94,0.3)}50%{box-shadow:0 0 0 6px rgba(34,197,94,0.1)}}
        @keyframes fadeUp{from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:translateY(0)}}
        *{box-sizing:border-box;margin:0;padding:0;} button{font-family:inherit;}
        .lp-step:hover{border-color:#2563EB44!important;transform:translateY(-4px);box-shadow:0 20px 40px rgba(37,99,235,0.1);}
        .lp-feature:hover{background:#111!important;}
        .lp-perk{background:rgba(37,99,235,0.08);border:1px solid rgba(37,99,235,0.15);border-radius:16px;padding:24px;}
      `}</style>

      {/* NAV */}
      <div style={{ position:"fixed", top:0, left:0, right:0, zIndex:100, paddingTop:"max(16px, calc(var(--sat, 0px) + 8px))", paddingBottom:16, paddingLeft:24, paddingRight:24, display:"flex", justifyContent:"space-between", alignItems:"center", background:"rgba(10,10,10,0.9)", backdropFilter:"blur(16px)", borderBottom:"1px solid #1a1a1a" }}>
        <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:26, letterSpacing:5 }}>FAD<span style={{ color:"#2563EB" }}>ED</span></div>
        <div style={{ display:"flex", gap:10, alignItems:"center" }}>
          <button onClick={onSignIn} style={{ padding:"10px 20px", background:"transparent", border:"1.5px solid #2a2a2a", borderRadius:10, color:"#888", fontSize:13, fontWeight:700, cursor:"pointer" }}>Sign In</button>
          <button onClick={onEnter} style={{ padding:"10px 20px", background:"#2563EB", border:"none", borderRadius:10, color:"#fff", fontSize:13, fontWeight:700, cursor:"pointer" }}>Get Started Free</button>
        </div>
      </div>

      {/* HERO */}
      <div style={{ minHeight:"100dvh", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", textAlign:"center", padding:"max(130px, calc(var(--sat, 0px) + 110px)) 20px 60px", position:"relative", boxSizing:"border-box" }}>
        <div style={{ position:"absolute", inset:0, background:"radial-gradient(ellipse 80% 60% at 50% 40%, rgba(37,99,235,0.12) 0%, transparent 70%)", pointerEvents:"none" }}/>

        <div style={{ display:"inline-flex", alignItems:"center", gap:8, background:"rgba(34,197,94,0.12)", border:"1px solid rgba(34,197,94,0.3)", padding:"8px 16px", borderRadius:20, marginBottom:32, animation:"fadeUp 0.6s ease both" }}>
          <div style={{ width:8, height:8, borderRadius:"50%", background:"#22C55E", animation:"pulse 2s ease infinite" }}/>
          <span style={{ fontSize:11, fontWeight:700, color:"#22C55E", letterSpacing:1 }}>LIVE NEAR YOU NOW</span>
        </div>

        <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:"clamp(72px,16vw,140px)", letterSpacing:8, lineHeight:0.9, marginBottom:24, animation:"fadeUp 0.6s ease 0.1s both" }}>
          GET<br/><span style={{ color:"#2563EB" }}>FAD</span>ED.
        </div>

        <p style={{ fontSize:"clamp(16px,3vw,20px)", color:"rgba(245,245,240,0.6)", maxWidth:480, lineHeight:1.6, marginBottom:12, animation:"fadeUp 0.6s ease 0.2s both" }}>
          Find an available barber near you right now. Book instantly. No calls, no DMs, no waiting.
        </p>
        <p style={{ fontSize:12, color:"#444", letterSpacing:1, marginBottom:48, animation:"fadeUp 0.6s ease 0.25s both" }}>
          NO CALLS · NO DMS · NO WAITING · LAUNCHING IN CANADA 🇨🇦
        </p>

        <div style={{ display:"flex", gap:12, flexWrap:"wrap", justifyContent:"center", marginBottom:48, animation:"fadeUp 0.6s ease 0.3s both" }}>
          <button onClick={onEnter} style={{ padding:"16px 36px", background:"#2563EB", border:"none", borderRadius:14, color:"#fff", fontSize:15, fontWeight:800, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", boxShadow:"0 8px 32px rgba(37,99,235,0.4)" }}>✂️ Find a Barber Now</button>
          <button onClick={onEnter} style={{ padding:"16px 28px", background:"transparent", border:"1.5px solid #2a2a2a", borderRadius:14, color:"#888", fontSize:15, fontWeight:600, cursor:"pointer" }}>💈 I'm a Barber</button>
        </div>

        {/* Stats */}
        <div style={{ display:"flex", flexWrap:"wrap", justifyContent:"center", border:"1px solid #1a1a1a", borderRadius:20, overflow:"hidden", animation:"fadeUp 0.6s ease 0.5s both", maxWidth:"100%" }}>
          {[["⚡","INSTANT BOOKING"],["$0","BOOKING FEES"],["🇨🇦","CANADA FIRST"]].map(([n,l])=>(
            <div key={l} style={{ padding:"16px 20px", textAlign:"center", borderRight:"1px solid #1a1a1a", flex:"1 1 90px" }}>
              <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:28, color:"#2563EB" }}>{n}</div>
              <div style={{ fontSize:11, color:"#444", letterSpacing:1, marginTop:4, whiteSpace:"nowrap" }}>{l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* HOW IT WORKS */}
      <div style={{ background:"#080808", borderTop:"1px solid #111", padding:"80px 24px" }}>
        <div style={{ maxWidth:900, margin:"0 auto" }}>
          <div style={{ fontSize:11, letterSpacing:4, color:"#2563EB", fontWeight:700, textAlign:"center", marginBottom:16 }}>HOW IT WORKS</div>
          <div style={{ width:80, height:2, background:"linear-gradient(90deg,transparent,#2563EB,transparent)", margin:"0 auto 24px", borderRadius:1 }}/>
          <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:"clamp(40px,8vw,72px)", letterSpacing:4, textAlign:"center", marginBottom:48 }}>THREE TAPS. FRESH CUT.</div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(250px,1fr))", gap:24 }}>
            {[["📍","Open FADED","Your GPS finds available barbers near you in real time. Closest first."],["✂️","Pick Your Barber","Browse profiles, portfolio photos, ratings. See who's ready right now."],["⚡","Book Instantly","Confirm in seconds. Apple Pay, Google Pay, Interac or cash. No calls."]].map(([icon,title,desc],i)=>(
              <div key={i} className="lp-step" style={{ background:"#111", border:"1px solid #1a1a1a", borderRadius:20, padding:"32px 28px", position:"relative", overflow:"hidden", transition:"all 0.3s" }}>
                <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:72, color:"rgba(37,99,235,0.06)", position:"absolute", top:-8, right:16 }}>{i+1}</div>
                <div style={{ fontSize:32, marginBottom:16 }}>{icon}</div>
                <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:22, fontWeight:700, marginBottom:8, letterSpacing:"0.5px" }}>
                  {i === 0 ? <>Open FAD<span style={{ color:"#2563EB" }}>ED</span></> : title}
                </div>
                <div style={{ fontSize:14, color:"#666", lineHeight:1.7 }}>{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* FEATURES */}
      <div style={{ background:"#0d0d0d", borderTop:"1px solid #1a1a1a", borderBottom:"1px solid #1a1a1a" }}>
        <div style={{ maxWidth:900, margin:"0 auto", padding:"80px 24px" }}>
          <div style={{ fontSize:11, letterSpacing:4, color:"#2563EB", fontWeight:700, textAlign:"center", marginBottom:16 }}>WHY FADED</div>
          <div style={{ width:80, height:2, background:"linear-gradient(90deg,transparent,#2563EB,transparent)", margin:"0 auto 24px", borderRadius:1 }}/>
          <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:"clamp(40px,8vw,72px)", letterSpacing:4, textAlign:"center", marginBottom:40 }}>BUILT DIFFERENT.</div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))", gap:1, background:"#1a1a1a", border:"1px solid #1a1a1a", borderRadius:20, overflow:"hidden" }}>
            {[["⚡","Real-Time Availability","See who is literally free right now — not just who has future slots."],["📍","GPS-Powered Discovery","Closest available barber shows up first. Not who paid to be featured."],["💳","All Major Payments","Apple Pay, Google Pay, Interac, Visa, Mastercard. Works across Canada."],["🔕","Zero Spam. Ever.","Booking confirmations only. No ads. No data selling. Your rules."],["🚶","Walk-In Mode","Barbers flag walk-ins welcome. Just show up — no booking needed."],["💈","Style Search","Filter by fades, braids, locs, beard, tapers and more."]].map(([icon,title,desc])=>(
              <div key={title} className="lp-feature" style={{ background:"#0d0d0d", padding:"36px", transition:"background 0.2s" }}>
                <div style={{ fontSize:28, marginBottom:14 }}>{icon}</div>
                <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:21, fontWeight:700, marginBottom:8, letterSpacing:"0.5px" }}>{title}</div>
                <div style={{ fontSize:14, color:"#555", lineHeight:1.7 }}>{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* FOR BARBERS */}
      <div style={{ background:"linear-gradient(135deg,#0f1f3d,#0a0a0a)", borderTop:"1px solid #1e3a5f", padding:"100px 24px" }}>
        <div style={{ maxWidth:900, margin:"0 auto", textAlign:"center" }}>
          <div style={{ fontSize:11, letterSpacing:4, color:"#2563EB", fontWeight:700, marginBottom:16 }}>FOR BARBERS</div>
          <div style={{ width:80, height:2, background:"linear-gradient(90deg,transparent,#2563EB,transparent)", margin:"0 auto 24px", borderRadius:1 }}/>
          <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:"clamp(40px,8vw,72px)", letterSpacing:4, marginBottom:16 }}>YOUR CHAIR.<br/>YOUR RULES.</div>
          <p style={{ color:"#4B7AC7", fontSize:16, maxWidth:480, margin:"0 auto 48px", lineHeight:1.7 }}>Go live when ready. Get booked instantly. Keep every dollar — zero commission, anywhere.</p>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(240px,1fr))", gap:16, marginBottom:48 }}>
            {[["🟢","Go Live Instantly","Toggle your status. Clients near you see you immediately."],["💰","Keep 100%","Zero commission on earnings. First 2 months free — then $30/month flat."],["📅","Full Schedule","App bookings auto-sync. Add phone/walk-in bookings manually."],["🔗","Your Booking Link","letsgetfaded.com/barber/you — share anywhere."],["📸","Portfolio Gallery","Upload your best cuts. Let your work sell itself."],["🇨🇦","Canada First","Launching in Canada first — US expansion coming soon. Get in before the rush."]].map(([icon,title,desc])=>(
              <div key={title} className="lp-perk">
                <div style={{ fontSize:24, marginBottom:12 }}>{icon}</div>
                <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:19, fontWeight:700, marginBottom:6, letterSpacing:"0.5px" }}>{title}</div>
                <div style={{ fontSize:13, color:"#4B7AC7", lineHeight:1.6 }}>{desc}</div>
              </div>
            ))}
          </div>
          {/* Founding barber spots progress bar */}
          <div style={{ maxWidth:480, margin:"0 auto 40px", background:"rgba(245,158,11,0.07)", border:"1.5px solid rgba(245,158,11,0.25)", borderRadius:20, padding:"22px 26px" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
              <div style={{ textAlign:"left" }}>
                <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, fontWeight:700, letterSpacing:"0.12em", color:"#F59E0B", marginBottom:4 }}>FOUNDING BARBER SPOTS</div>
                <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, color:"rgba(245,245,240,0.55)", lineHeight:1.5 }}>Limited to {FOUNDING_LIMIT} founding barbers. <strong style={{ color:"#F5F5F0" }}>Early members get priority placement</strong> and lifetime recognition.</div>
              </div>
              <div style={{ textAlign:"right", flexShrink:0, marginLeft:16 }}>
                <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:36, letterSpacing:"0.04em", color:"#F59E0B", lineHeight:1 }}>{founderCount}</div>
                <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:"rgba(245,245,240,0.4)", fontWeight:600 }}>OF {FOUNDING_LIMIT}</div>
              </div>
            </div>
            <div style={{ background:"rgba(245,158,11,0.12)", borderRadius:100, height:8, overflow:"hidden" }}>
              <div style={{ height:"100%", borderRadius:100, background:"linear-gradient(90deg,#F59E0B,#D97706)", width:`${Math.min(100, Math.round((founderCount/FOUNDING_LIMIT)*100))}%`, transition:"width 0.8s cubic-bezier(0.22,1,0.36,1)" }}/>
            </div>
            <div style={{ fontFamily:"'DM Sans',sans-serif", marginTop:8, fontSize:11, color:"rgba(245,158,11,0.7)", fontWeight:600, display:"flex", alignItems:"center", gap:8 }}>
              <span>{Math.max(0, FOUNDING_LIMIT - founderCount)} spots remaining</span>
              <span style={{ color:"#22C55E" }}>● Barbers joining now</span>
            </div>
          </div>

          <button onClick={onEnter} style={{ padding:"18px 48px", background:"#2563EB", border:"none", borderRadius:14, color:"#fff", fontSize:16, fontWeight:800, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", boxShadow:"0 8px 32px rgba(37,99,235,0.4)" }}>Join as a Barber — It's Free</button>
          <p style={{ marginTop:16, fontSize:12, color:"#2a3d5c" }}>No credit card. No contract. First 2 months free — then $30/month flat. Zero commission, forever.</p>
        </div>
      </div>

      {/* FINAL CTA */}
      <div style={{ textAlign:"center", padding:"100px 24px", position:"relative", overflow:"hidden" }}>
        <div style={{ position:"absolute", inset:0, background:"radial-gradient(ellipse 60% 80% at 50% 50%, rgba(37,99,235,0.08) 0%, transparent 70%)" }}/>
        <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:"clamp(48px,10vw,96px)", letterSpacing:6, lineHeight:0.9, marginBottom:24, position:"relative" }}>
          READY TO GET<br/><span style={{ color:"#2563EB" }}>FADED?</span>
        </div>
        <p style={{ fontSize:17, color:"#555", marginBottom:40, position:"relative" }}>Find your barber in under 60 seconds.</p>
        <div style={{ display:"flex", gap:12, justifyContent:"center", flexWrap:"wrap", position:"relative" }}>
          <button onClick={onEnter} style={{ padding:"18px 48px", background:"#2563EB", border:"none", borderRadius:14, color:"#fff", fontSize:16, fontWeight:800, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", boxShadow:"0 8px 32px rgba(37,99,235,0.4)" }}>✂️ Book a Barber Now</button>
          <button onClick={onEnter} style={{ padding:"18px 32px", background:"transparent", border:"1.5px solid #2a2a2a", borderRadius:14, color:"#888", fontSize:16, fontWeight:600, cursor:"pointer" }}>💈 Join as a Barber</button>
        </div>
        <p style={{ marginTop:24, fontSize:12, color:"#222", position:"relative" }}>letsgetfaded.com · Book a barber anywhere, anytime ✂️</p>
      </div>

      {/* FOOTER */}
      <div style={{ borderTop:"1px solid #1a1a1a", padding:"32px 24px", display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:16 }}>
        <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:22, letterSpacing:4 }}>FAD<span style={{ color:"#2563EB" }}>ED</span></div>
        <div style={{ display:"flex", gap:20, flexWrap:"wrap", alignItems:"center" }}>
          <button onClick={onEnter} style={{ background:"none", border:"none", color:"#444", fontSize:13, cursor:"pointer" }}>App</button>
          <button onClick={onEnter} style={{ background:"none", border:"none", color:"#444", fontSize:13, cursor:"pointer" }}>For Barbers</button>
          <a href="mailto:hello@letsgetfaded.com" style={{ color:"#444", fontSize:13, textDecoration:"none" }}>Contact</a>
          <button onClick={()=>setLegalDoc("terms")} style={{ background:"none", border:"none", color:"#333", fontSize:13, cursor:"pointer" }}>Terms</button>
          <button onClick={()=>setLegalDoc("privacy")} style={{ background:"none", border:"none", color:"#333", fontSize:13, cursor:"pointer" }}>Privacy</button>
        </div>
        <div style={{ color:"#2a2a2a", fontSize:12 }}>© 2026 FADED · hello@letsgetfaded.com</div>
      </div>
    </div>
  );
}

// ─── PASSWORD RESET SCREEN ────────────────────────────────────────────────────
function PasswordResetScreen({ onDone }) {
  const [pass, setPass]         = useState("");
  const [confirm, setConfirm]   = useState("");
  const [saving, setSaving]     = useState(false);
  const [done, setDone]         = useState(false);
  const [err, setErr]           = useState("");
  const [showPass, setShowPass]       = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const save = async () => {
    if (!pass || pass.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (pass !== confirm) { setErr("Passwords don't match."); return; }
    setSaving(true); setErr("");
    const { error } = await supabase.auth.updateUser({ password: pass });
    setSaving(false);
    if (error) { setErr(error.message); return; }
    setDone(true);
    setTimeout(() => onDone(), 2000);
  };

  return (
    <div style={{ minHeight:"100dvh", background:"#0A0A0A", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"32px 20px", fontFamily:"'DM Sans',sans-serif" }}>
      <div style={{ fontSize:32, fontWeight:900, letterSpacing:5, color:"#F5F5F0", marginBottom:4 }}>FAD<span style={{ color:"#2563EB" }}>ED</span></div>
      <div style={{ height:2, width:48, background:"linear-gradient(90deg,#2563EB,transparent)", marginBottom:32 }}/>
      <div style={{ width:"100%", maxWidth:380, background:"#111", borderRadius:20, border:"1px solid #1f1f1f", padding:"32px 28px" }}>
        {done ? (
          <div style={{ textAlign:"center" }}>
            <div style={{ fontSize:40, marginBottom:12 }}>✅</div>
            <div style={{ fontSize:18, fontWeight:800, color:"#F5F5F0", marginBottom:6 }}>Password updated!</div>
            <div style={{ fontSize:13, color:"#6B7280" }}>Taking you back to sign in…</div>
          </div>
        ) : (<>
          <div style={{ fontSize:20, fontWeight:800, color:"#F5F5F0", marginBottom:4 }}>Set new password</div>
          <div style={{ fontSize:13, color:"#6B7280", marginBottom:24, lineHeight:1.6 }}>Choose a strong password for your FADED account.</div>
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
            <div style={{ position:"relative" }}>
              <input
                type={showPass?"text":"password"} placeholder="New password" value={pass}
                onChange={e=>setPass(e.target.value)}
                style={{ width:"100%", padding:"14px 48px 14px 16px", borderRadius:14, border:"1.5px solid #222", background:"#0d0d0d", fontSize:16, color:"#E8E8E8", outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box" }}/>
              <button type="button" onClick={()=>setShowPass(v=>!v)} style={{ position:"absolute", right:14, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", padding:0, color:"#6B7280", display:"flex", alignItems:"center" }}>
                {showPass
                  ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                }
              </button>
            </div>
            <div style={{ position:"relative" }}>
              <input
                type={showConfirm?"text":"password"} placeholder="Confirm new password" value={confirm}
                onChange={e=>setConfirm(e.target.value)}
                onKeyDown={e=>e.key==="Enter"&&save()}
                style={{ width:"100%", padding:"14px 48px 14px 16px", borderRadius:14, border:"1.5px solid #222", background:"#0d0d0d", fontSize:16, color:"#E8E8E8", outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box" }}/>
              <button type="button" onClick={()=>setShowConfirm(v=>!v)} style={{ position:"absolute", right:14, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", padding:0, color:"#6B7280", display:"flex", alignItems:"center" }}>
                {showConfirm
                  ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                }
              </button>
            </div>
            {err && <div style={{ fontSize:13, color:"#EF4444", fontWeight:600 }}>{err}</div>}
            <button onClick={save} disabled={saving}
              style={{ width:"100%", padding:"14px", background:"#2563EB", border:"none", borderRadius:14, color:"#fff", fontSize:15, fontWeight:800, cursor:"pointer", opacity:saving?0.7:1, marginTop:4 }}>
              {saving ? "Saving…" : "Update Password"}
            </button>
          </div>
        </>)}
      </div>
    </div>
  );
}

// Fire-and-forget barber push, but never silent: a failed or zero-recipient send is reported
// to Sentry so a barber missing booking alerts shows up instead of vanishing.
function notifyBarberOfBooking(body) {
  return supabase.functions.invoke("notify-booking", { body }).then(({ data, error }) => {
    try {
      if (error) {
        Sentry.captureException(error, { level: "error", tags: { flow: "notify_booking" }, extra: { barber_id: body?.barber_id, booking_id: body?.booking_id } });
      } else if (data && data.sent === 0 && data.reason !== "already_sent") {
        Sentry.captureMessage("notify-booking delivered to 0 devices", { level: "warning", tags: { flow: "notify_booking", reason: data.reason || "all_sends_failed" }, extra: { barber_id: body?.barber_id, booking_id: body?.booking_id } });
      }
    } catch {}
  }).catch(() => {});
}

// ─── AUTH SCREEN ──────────────────────────────────────────────────────────────
function AuthScreen({ onAuth, onBack, initialMsg="", initialMode="landing", initialRole=null }) {
  const [mode, setMode]       = useState(initialMode || "landing");
  const [role, setRole]       = useState(initialRole);
  const [email, setEmail]     = useState("");
  const [pass, setPass]       = useState("");
  const [name, setName]       = useState("");
  const [phone, setPhone]     = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [msg, setMsg]         = useState(() => {
    if (initialMsg) return initialMsg;
    try { const n = sessionStorage.getItem("faded_auth_notice"); if (n) { sessionStorage.removeItem("faded_auth_notice"); return n; } } catch {}
    return "";
  });
  const [legalDoc, setLegalDoc]   = useState(null);
  const [tosAccepted, setTosAccepted]         = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [lockedUntil, setLockedUntil] = useState(0);
  const failCount = useRef(0);
  const [showPass, setShowPass] = useState(false);
  const [resetPhone, setResetPhone]         = useState("");
  const [resetOtp, setResetOtp]             = useState("");
  const [resetNewPass, setResetNewPass]     = useState("");
  const [resetConfirm, setResetConfirm]     = useState("");

  const isLocked = () => Date.now() < lockedUntil;
  const lockoutSecsLeft = () => Math.ceil((lockedUntil - Date.now()) / 1000);

  const loadingTimer = useRef(null);
  const setLoadingWithTimeout = (val) => {
    clearTimeout(loadingTimer.current);
    setLoading(val);
    if (val) {
      loadingTimer.current = setTimeout(() => {
        setLoading(false);
        setMsg("Connection timed out — check your network and try again.");
      }, 15000);
    }
  };

  const recordFailure = () => {
    failCount.current += 1;
    if (failCount.current >= 5) {
      failCount.current = 0;
      setLockedUntil(Date.now() + 60_000);
    }
  };
  const recordSuccess = () => { failCount.current = 0; setLockedUntil(0); };

  const inp = (val, set, type="text", ph="", required=false) => {
    const isPass = type === "password";
    // -webkit-text-security has no effect on an input with type="password" in any
    // browser (it only masks non-password input types), so toggling it here never
    // actually revealed anything. Switch the real type instead, same as PasswordResetScreen.
    const resolvedType = isPass ? (showPass ? "text" : "password") : type;
    const autoComp = isPass ? (mode==="signup" ? "new-password" : "current-password") : type==="email" ? "username" : type==="tel" ? "tel" : "name";
    const nameAttr = isPass ? "password" : type==="email" ? "email" : type==="tel" ? "tel" : "name";
    const input = (
      <input type={resolvedType} placeholder={ph} value={val} onChange={e=>set(e.target.value)}
        aria-label={ph} aria-required={required || undefined}
        name={nameAttr} autoComplete={autoComp}
        style={{ width:"100%", padding: isPass ? "14px 48px 14px 16px" : "14px 16px", borderRadius:14, border:"1.5px solid #222", background:"#0d0d0d", fontSize:16, color:"#E8E8E8", outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box" }}/>
    );
    if (!isPass) return input;
    return (
      <div style={{ position:"relative" }}>
        {input}
        <button type="button" onClick={()=>setShowPass(v=>!v)} style={{ position:"absolute", right:14, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", padding:0, color:"#6B7280", display:"flex", alignItems:"center" }}>
          {showPass
            ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
            : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
          }
        </button>
      </div>
    );
  };

  const signInWithGoogle = async () => {
    setLoadingWithTimeout(true); setMsg("");
    if (role) { try { sessionStorage.setItem('faded_oauth_role', role); } catch {} }
    if (IS_NATIVE_IOS) {
      // Native iOS: open Google OAuth in an in-app browser sheet.
      // After login Google redirects to faded://auth/callback — iOS intercepts
      // the custom scheme, CapApp.addListener fires in the main App, closes
      // the browser and exchanges the code for a Supabase session.
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: { redirectTo: "faded://auth/callback", skipBrowserRedirect: true },
      });
      if (error) { setMsg(error.message); setLoading(false); return; }
      await Browser.open({ url: data.url, windowName: "_self" });
    } else {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: { redirectTo: "https://app.letsgetfaded.com", queryParams: { access_type:"offline", prompt:"consent" } },
      });
      if (error) { setMsg(error.message); setLoading(false); }
    }
  };

  const continueAsGuest = () => { onAuth("guest"); };

  const requestResetOtp = async () => {
    if (isLocked()) { setMsg(`Too many attempts — wait ${lockoutSecsLeft()} seconds.`); return; }
    const cleaned = resetPhone.replace(/\D/g,"");
    if (cleaned.length < 10) { setMsg("Enter a valid 10-digit phone number."); return; }
    setLoadingWithTimeout(true); setMsg("");
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/request-password-reset-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "apikey": SUPABASE_KEY },
        body: JSON.stringify({ phone: cleaned }),
      });
      const data = await res.json();
      setLoadingWithTimeout(false);
      if (!res.ok) { setMsg(data.error || "Could not send code."); return; }
      setMode("forgot-otp");
      setMsg("If that number is on file, a code has been sent.");
    } catch (e) {
      setLoadingWithTimeout(false);
      setMsg("Network error. Check your connection and try again.");
    }
  };

  const submitResetOtp = async () => {
    if (isLocked()) { setMsg(`Too many attempts — wait ${lockoutSecsLeft()} seconds.`); return; }
    if (resetOtp.length !== 6) { setMsg("Enter the 6-digit code."); return; }
    if (!resetNewPass || resetNewPass.length < 8) { setMsg("Password must be at least 8 characters."); return; }
    if (resetNewPass !== resetConfirm) { setMsg("Passwords don't match."); return; }
    setLoadingWithTimeout(true); setMsg("");
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/verify-password-reset-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "apikey": SUPABASE_KEY },
        body: JSON.stringify({ phone: resetPhone.replace(/\D/g,""), code: resetOtp, newPassword: resetNewPass }),
      });
      const data = await res.json();
      setLoadingWithTimeout(false);
      if (!res.ok) { recordFailure(); setMsg(data.error || "Could not reset password."); return; }
      recordSuccess();
      setMsg("Password updated! Sign in with your new password.");
      setResetOtp(""); setResetNewPass(""); setResetConfirm(""); setResetPhone("");
      setTimeout(() => { setMode("signin"); setMsg(""); }, 1800);
    } catch (e) {
      setLoadingWithTimeout(false);
      setMsg("Network error. Check your connection and try again.");
    }
  };

  const sendPhoneOtp = async () => {
    if (isLocked()) { setMsg(`Too many attempts — wait ${lockoutSecsLeft()} seconds.`); return; }
    const cleaned = phone.replace(/\s/g,"");
    if (!cleaned) { setMsg("Enter a phone number."); return; }
    setLoadingWithTimeout(true); setMsg("");
    const { error } = await supabase.auth.signInWithOtp({ phone: cleaned });
    setLoadingWithTimeout(false);
    if (error) { recordFailure(); setMsg(error.message); return; }
    setMode("otp");
    setMsg("Code sent! Check your texts.");
  };

  const verifyOtp = async () => {
    if (isLocked()) { setMsg(`Too many attempts — wait ${lockoutSecsLeft()} seconds.`); return; }
    if (otpCode.length !== 6) { setMsg("Enter the 6-digit code."); return; }
    setLoadingWithTimeout(true); setMsg("");
    const { data, error } = await supabase.auth.verifyOtp({ phone: phone.replace(/\s/g,""), token: otpCode, type:"sms" });
    setLoadingWithTimeout(false);
    if (error) { recordFailure(); setMsg(error.message); return; }
    recordSuccess();
    try { if (document.activeElement?.blur) document.activeElement.blur(); } catch {}
    window.location.reload();
  };

  const submit = async () => {
    if (isLocked()) { setMsg(`Too many attempts — wait ${lockoutSecsLeft()} seconds.`); return; }
    if (!email||!pass) { setMsg("Please fill in all fields."); return; }
    if (mode==="signup"&&!name) { setMsg("Please enter your first and last name."); return; }
    if (mode==="signup" && name.trim().split(/\s+/).filter(p=>p.length>=2).length < 2) { setMsg("Please enter both your first and last name (e.g. John Smith)."); return; }
    if (mode==="signup"&&!role) { setMsg("Please select client or barber."); return; }
    if (mode==="signup"&&!phone) { setMsg("Phone number is required."); return; }
    if (mode==="signup"&&!email) { setMsg("Email is required."); return; }
    if (mode==="signup"&&!tosAccepted) { setMsg("Please agree to the Terms of Service to continue."); return; }
    if (mode==="signup"&&!privacyAccepted) { setMsg("Please agree to the Privacy Policy to continue."); return; }
    setLoadingWithTimeout(true); setMsg("");
    if (mode==="signin") {
      let data, error;
      try { ({ data, error } = await supabase.auth.signInWithPassword({ email, password:pass })); }
      catch (e) { error = { message: e?.message || "Network error. Check your connection." }; }
      setLoadingWithTimeout(false);
      if (error) {
        recordFailure();
        if (error.message?.toLowerCase().includes("invalid login") || error.message?.toLowerCase().includes("invalid credentials")) {
          setMsg("Incorrect email or password. Please try again.");
        } else if (error.message?.toLowerCase().includes("email not confirmed")) {
          setMsg("Please confirm your email address before signing in. Check your inbox.");
        } else {
          setMsg(error.message);
        }
        return;
      }
      recordSuccess();
      try { if (document.activeElement?.blur) document.activeElement.blur(); } catch {}
      // Fire-and-forget — do NOT await. In TestFlight/WKWebView the save-password
      // UI may never appear, causing store() to hang and block the reload below.
      try {
        if (window.PasswordCredential) {
          const cred = new window.PasswordCredential({ id: email, password: pass });
          navigator.credentials.store(cred).catch(() => {});
        }
      } catch {}
      await new Promise(r => setTimeout(r, 1000));
      window.location.reload();
    } else {
      let data, error;
      try { ({ data, error } = await supabase.auth.signUp({ email, password:pass, options:{ data:{ full_name:name, role, phone } } })); }
      catch (e) { error = { message: e?.message || "Network error. Check your connection." }; }
      setLoadingWithTimeout(false);
      if (error) {
        recordFailure();
        if (error.message?.toLowerCase().includes("already registered") || error.message?.toLowerCase().includes("user already exists") || error.message?.toLowerCase().includes("already been registered")) {
          setMsg("An account with this email already exists. Sign in instead →");
          setTimeout(() => setMode("signin"), 1800);
        } else {
          setMsg(error.message);
        }
        return;
      }
      recordSuccess();
      // Record PIPEDA consent timestamps immediately after account creation.
      // IMPORTANT: role + full_name must be included here — this is the FIRST insert
      // into profiles, which fires trg_welcome_email. Without role the trigger defaults
      // to 'client' and barbers get the wrong email.
      if (data.user?.id) {
        const now = new Date().toISOString();
        let refId = null;
        try { refId = sessionStorage.getItem('faded_ref') || null; } catch {}
        const profilePayload = {
          id: data.user.id,
          role: role || "client",
          full_name: name || "",
          terms_accepted_at: now,
          privacy_accepted_at: now,
          terms_version: "1.0",
        };
        if (refId) profilePayload.referred_by = refId;
        supabase.from("profiles").upsert(profilePayload, { onConflict: "id" }).then(() => {
          if (refId) { try { sessionStorage.removeItem('faded_ref'); } catch {} }
        });
      }
      if (data.session) window.location.reload();
      else setMsg("Account created! Check your email or sign in now.");
    }
  };

  if (mode==="landing") return (
    <div style={{ flex:1, minHeight:0, overflowY:"auto", WebkitOverflowScrolling:"touch", background:"#04050A", fontFamily:"'DM Sans',sans-serif", position:"relative" }}>
      {legalDoc && <LegalModal doc={legalDoc} onClose={() => setLegalDoc(null)}/>}
      <style>{`*{box-sizing:border-box;margin:0;padding:0;} button{font-family:inherit;} @keyframes fadedPulse{0%,100%{opacity:1}50%{opacity:0.5}}`}</style>
      {/* Fixed ambient glow — stays behind scroll */}
      <div style={{ position:"fixed", top:0, left:0, right:0, height:"70vh", background:"radial-gradient(ellipse 120% 70% at 50% -5%, rgba(37,99,235,0.2) 0%, rgba(37,99,235,0.04) 50%, transparent 75%)", pointerEvents:"none", zIndex:0 }}/>
      <div style={{ position:"relative", zIndex:1, display:"flex", flexDirection:"column", alignItems:"center", textAlign:"center", padding:"max(var(--sat, 44px),54px) 24px max(env(safe-area-inset-bottom,48px),48px)" }}>
        {/* Live badge */}
        <div style={{ display:"inline-flex", alignItems:"center", gap:8, background:"rgba(34,197,94,0.08)", border:"1px solid rgba(34,197,94,0.2)", padding:"6px 16px", borderRadius:100, marginBottom:32 }}>
          <span style={{ width:6, height:6, borderRadius:"50%", background:"#22C55E", display:"inline-block", animation:"fadedPulse 2s ease infinite" }}/>
          <span style={{ fontSize:10, fontWeight:800, color:"#22C55E", letterSpacing:"0.15em" }}>BARBERS LIVE NEAR YOU NOW</span>
        </div>
        {/* Wordmark */}
        <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:"clamp(96px,26vw,164px)", letterSpacing:"0.06em", lineHeight:0.85, color:"#F5F5F0", marginBottom:18, textShadow:"0 0 80px rgba(37,99,235,0.3)" }}>
          FAD<span style={{ color:"#2563EB" }}>ED</span>
        </div>
        <p style={{ fontSize:"clamp(15px,4vw,17px)", color:"rgba(245,245,240,0.42)", maxWidth:270, lineHeight:1.55, marginBottom:6 }}>
          Find an available barber near you.<br/>Book instantly.
        </p>
        <p style={{ fontSize:10, color:"rgba(245,245,240,0.15)", letterSpacing:"0.22em", marginBottom:44 }}>NO CALLS · NO DMS · NO WAITING</p>
        {/* Auth block */}
        <div style={{ width:"100%", maxWidth:360, display:"flex", flexDirection:"column", gap:10 }}>
          <div style={{ display:"flex", gap:10 }}>
            <button onClick={()=>setMode("signup")} style={{ flex:1, padding:"15px", background:"#2563EB", border:"none", borderRadius:14, color:"#fff", fontSize:14, fontWeight:700, cursor:"pointer", boxShadow:"0 4px 24px rgba(37,99,235,0.35)" }}>Create Account</button>
            <button onClick={()=>setMode("signin")} style={{ flex:1, padding:"15px", background:"rgba(245,245,240,0.05)", border:"1px solid rgba(245,245,240,0.1)", borderRadius:14, color:"rgba(245,245,240,0.75)", fontSize:14, fontWeight:600, cursor:"pointer" }}>Sign In</button>
          </div>
          <button onClick={continueAsGuest} style={{ padding:"13px", background:"rgba(245,245,240,0.04)", border:"1px solid rgba(245,245,240,0.14)", borderRadius:14, color:"rgba(245,245,240,0.85)", fontSize:14, fontWeight:600, cursor:"pointer" }}>See who's live near you — no account needed →</button>
        </div>
        {/* Trust row */}
        <div style={{ display:"flex", gap:18, marginTop:26, flexWrap:"wrap", justifyContent:"center" }}>
          {["Book in 30 seconds","No line, no wait","GPS-powered"].map(t=>(
            <span key={t} style={{ fontSize:11, color:"rgba(245,245,240,0.25)", display:"flex", alignItems:"center", gap:5 }}>
              <span style={{ color:"#22C55E" }}>✓</span>{t}
            </span>
          ))}
        </div>
        {/* Divider */}
        <div style={{ width:"100%", maxWidth:360, height:1, background:"linear-gradient(90deg,transparent,rgba(245,245,240,0.07),transparent)", margin:"36px 0 28px" }}/>
        {/* Feature cards */}
        <div style={{ width:"100%", maxWidth:360, display:"flex", flexDirection:"column", gap:8, textAlign:"left" }}>
          {[
            { icon:"📍", label:"GPS-powered discovery", sub:"Closest available barber, found instantly." },
            { icon:"⚡", label:"Live availability", sub:"See who's free right now — not just future slots." },
            { icon:"💳", label:"All major payments", sub:"Apple Pay, Google Pay, Interac, Visa, Mastercard." },
            { icon:"🔕", label:"Zero spam. Ever.", sub:"Booking confirmations only. No ads. No data selling." },
          ].map(f=>(
            <div key={f.label} style={{ background:"rgba(245,245,240,0.025)", border:"1px solid rgba(245,245,240,0.07)", borderRadius:14, padding:"14px 16px", display:"flex", gap:14, alignItems:"center" }}>
              <span style={{ fontSize:20, flexShrink:0, width:36, textAlign:"center" }}>{f.icon}</span>
              <div>
                <div style={{ fontSize:13, fontWeight:700, color:"rgba(245,245,240,0.8)", marginBottom:2 }}>{f.label}</div>
                <div style={{ fontSize:12, color:"rgba(245,245,240,0.32)", lineHeight:1.4 }}>{f.sub}</div>
              </div>
            </div>
          ))}
        </div>
        {/* Legal */}
        <div style={{ display:"flex", gap:16, marginTop:32 }}>
          <button onClick={()=>setLegalDoc("terms")} style={{ background:"none", border:"none", color:"rgba(245,245,240,0.18)", fontSize:11, cursor:"pointer" }}>Terms</button>
          <span style={{ color:"rgba(245,245,240,0.1)" }}>·</span>
          <button onClick={()=>setLegalDoc("privacy")} style={{ background:"none", border:"none", color:"rgba(245,245,240,0.18)", fontSize:11, cursor:"pointer" }}>Privacy</button>
        </div>
      </div>
    </div>
  );

  // Phone OTP entry screen
  if (mode === "otp") return (
    <div style={{ flex:1, minHeight:0, background:"#0A0A0A", fontFamily:"'DM Sans',sans-serif", display:"flex", alignItems:"center", justifyContent:"center" }}>
      <style>{`*{box-sizing:border-box;margin:0;padding:0;} button{font-family:inherit;} input::placeholder{color:#555!important;}`}</style>
      <div style={{ background:"#111", borderRadius:24, padding:32, border:"1px solid #1a1a1a", maxWidth:380, width:"100%", margin:"0 20px" }}>
        <div style={{ textAlign:"center", marginBottom:24 }}>
          <div style={{ fontSize:36, marginBottom:12 }}>📱</div>
          <div style={{ fontSize:20, fontWeight:900, color:"#fff", fontFamily:"'DM Sans',sans-serif" }}>Check Your Texts</div>
          <div style={{ fontSize:13, color:"#888", marginTop:8 }}>We sent a 6-digit code to<br/><span style={{ color:"#E8E8E8" }}>{phone}</span></div>
        </div>
        <form onSubmit={e=>{e.preventDefault();verifyOtp();}} autoComplete="on">
          <input type="number" placeholder="000000" value={otpCode} onChange={e=>setOtpCode(e.target.value.slice(0,6))}
            aria-label="6-digit verification code" aria-required="true" inputMode="numeric" autoComplete="one-time-code" name="one-time-code"
            style={{ width:"100%", padding:"18px 16px", borderRadius:14, border:"1.5px solid #333", background:"#0d0d0d", fontSize:28, color:"#fff", outline:"none", fontFamily:"'DM Sans',sans-serif", textAlign:"center", letterSpacing:"0.4em", boxSizing:"border-box", appearance:"textfield", marginBottom:12 }}/>
          {msg && <div style={{ fontSize:13, padding:"10px 14px", borderRadius:12, background:msg.includes("sent")?"rgba(34,197,94,0.12)":"rgba(220,38,38,0.12)", color:msg.includes("sent")?"#22C55E":"#EF4444", lineHeight:1.5, marginBottom:12 }}>{msg}</div>}
          <button type="submit" disabled={loading} style={{ width:"100%", padding:17, background:"linear-gradient(135deg,#2563EB,#1D4ED8)", border:"none", borderRadius:14, color:"#fff", fontSize:15, fontWeight:800, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", opacity:loading?0.75:1 }}>
            {loading?"Verifying...":"Verify & Sign In"}
          </button>
        </form>
        <button type="button" onClick={()=>{ setMode("phone"); setOtpCode(""); setMsg(""); }} style={{ width:"100%", padding:"12px", background:"transparent", border:"none", color:"#555", fontSize:13, cursor:"pointer", marginTop:8 }}>
          ← Back / Resend code
        </button>
      </div>
    </div>
  );

  // Phone number entry screen
  if (mode === "phone") return (
    <div style={{ flex:1, minHeight:0, background:"#0A0A0A", fontFamily:"'DM Sans',sans-serif", display:"flex", alignItems:"center", justifyContent:"center" }}>
      <style>{`*{box-sizing:border-box;margin:0;padding:0;} button{font-family:inherit;} input::placeholder{color:#555!important;}`}</style>
      <div style={{ background:"#111", borderRadius:24, padding:32, border:"1px solid #1a1a1a", maxWidth:380, width:"100%", margin:"0 20px" }}>
        <button type="button" onClick={()=>setMode("signin")} style={{ background:"transparent", border:"none", color:"#888", fontSize:13, cursor:"pointer", padding:0, marginBottom:20, display:"flex", alignItems:"center", gap:6 }}>← Back</button>
        <div style={{ textAlign:"center", marginBottom:24 }}>
          <div style={{ fontSize:36, marginBottom:12 }}>📲</div>
          <div style={{ fontSize:20, fontWeight:900, color:"#fff", fontFamily:"'DM Sans',sans-serif" }}>Sign In with Phone</div>
          <div style={{ fontSize:13, color:"#888", marginTop:8 }}>We'll text you a one-time code</div>
        </div>
        <form onSubmit={e=>{e.preventDefault();sendPhoneOtp();}} autoComplete="on">
          <div style={{ marginBottom:12 }}>
            <input type="tel" name="tel" placeholder="+1 (555) 000-0000" value={phone} onChange={e=>setPhone(e.target.value)}
              aria-label="Phone number including country code" aria-required="true" autoComplete="tel"
              style={{ width:"100%", padding:"14px 16px", borderRadius:14, border:"1.5px solid #222", background:"#0d0d0d", fontSize:16, color:"#E8E8E8", outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box" }}/>
            <div style={{ fontSize:11, color:"#555", marginTop:6 }}>Include country code, e.g. +1 for Canada/US</div>
          </div>
          {msg && <div style={{ fontSize:13, padding:"10px 14px", borderRadius:12, background:"rgba(220,38,38,0.12)", color:"#EF4444", lineHeight:1.5, marginBottom:12 }}>{msg}</div>}
          <button type="submit" disabled={loading} style={{ width:"100%", padding:17, background:"linear-gradient(135deg,#2563EB,#1D4ED8)", border:"none", borderRadius:14, color:"#fff", fontSize:15, fontWeight:800, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", opacity:loading?0.75:1 }}>
            {loading?"Sending...":"Send Code"}
          </button>
        </form>
      </div>
    </div>
  );

  // Forgot-password: redirect straight into the text-message code flow.
  // (An "email me a link" option existed here but was removed — Supabase's
  // email-link recovery is broken by corporate/carrier link-prefetch scanners
  // silently consuming the one-time token before the user clicks it.)
  if (mode === "forgot") return (
    <div style={{ flex:1, minHeight:0, background:"#0A0A0A", fontFamily:"'DM Sans',sans-serif", display:"flex", alignItems:"center", justifyContent:"center" }}>
      <style>{`*{box-sizing:border-box;margin:0;padding:0;} button{font-family:inherit;} input::placeholder{color:#555!important;}`}</style>
      <div style={{ background:"#111", borderRadius:24, padding:32, border:"1px solid #1a1a1a", maxWidth:380, width:"100%", margin:"0 20px" }}>
        <button type="button" onClick={()=>{ setMode("signin"); setMsg(""); }} style={{ background:"transparent", border:"none", color:"#888", fontSize:13, cursor:"pointer", padding:0, marginBottom:20, display:"flex", alignItems:"center", gap:6 }}>← Back</button>
        <div style={{ textAlign:"center", marginBottom:24 }}>
          <div style={{ fontSize:36, marginBottom:12 }}>📲</div>
          <div style={{ fontSize:20, fontWeight:900, color:"#fff", fontFamily:"'DM Sans',sans-serif" }}>Reset Your Password</div>
          <div style={{ fontSize:13, color:"#888", marginTop:8 }}>We'll text a 6-digit code to the phone number on your account</div>
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
          <button type="button" onClick={()=>{ setMode("forgot-phone"); setMsg(""); }} style={{ width:"100%", padding:17, background:"linear-gradient(135deg,#2563EB,#1D4ED8)", border:"none", borderRadius:14, color:"#fff", fontSize:15, fontWeight:800, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
            📱 Text me a code
          </button>
        </div>
      </div>
    </div>
  );

  // Forgot-password: phone entry for SMS code
  if (mode === "forgot-phone") return (
    <div style={{ flex:1, minHeight:0, background:"#0A0A0A", fontFamily:"'DM Sans',sans-serif", display:"flex", alignItems:"center", justifyContent:"center" }}>
      <style>{`*{box-sizing:border-box;margin:0;padding:0;} button{font-family:inherit;} input::placeholder{color:#555!important;}`}</style>
      <div style={{ background:"#111", borderRadius:24, padding:32, border:"1px solid #1a1a1a", maxWidth:380, width:"100%", margin:"0 20px" }}>
        <button type="button" onClick={()=>{ setMode("forgot"); setMsg(""); }} style={{ background:"transparent", border:"none", color:"#888", fontSize:13, cursor:"pointer", padding:0, marginBottom:20, display:"flex", alignItems:"center", gap:6 }}>← Back</button>
        <div style={{ textAlign:"center", marginBottom:24 }}>
          <div style={{ fontSize:36, marginBottom:12 }}>📲</div>
          <div style={{ fontSize:20, fontWeight:900, color:"#fff", fontFamily:"'DM Sans',sans-serif" }}>Text Me a Code</div>
          <div style={{ fontSize:13, color:"#888", marginTop:8 }}>Enter the phone number on your account</div>
        </div>
        <form onSubmit={e=>{e.preventDefault();requestResetOtp();}} autoComplete="on">
          <div style={{ marginBottom:12 }}>
            <input type="tel" name="tel" placeholder="(555) 000-0000" value={resetPhone} onChange={e=>setResetPhone(e.target.value)}
              aria-label="Phone number on file" aria-required="true" autoComplete="tel"
              style={{ width:"100%", padding:"14px 16px", borderRadius:14, border:"1.5px solid #222", background:"#0d0d0d", fontSize:16, color:"#E8E8E8", outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box" }}/>
          </div>
          {msg && <div style={{ fontSize:13, padding:"10px 14px", borderRadius:12, background:"rgba(220,38,38,0.12)", color:"#EF4444", lineHeight:1.5, marginBottom:12 }}>{msg}</div>}
          <button type="submit" disabled={loading} style={{ width:"100%", padding:17, background:"linear-gradient(135deg,#2563EB,#1D4ED8)", border:"none", borderRadius:14, color:"#fff", fontSize:15, fontWeight:800, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", opacity:loading?0.75:1 }}>
            {loading?"Sending...":"Send Code"}
          </button>
        </form>
      </div>
    </div>
  );

  // Forgot-password: enter SMS code + new password
  if (mode === "forgot-otp") return (
    <div style={{ flex:1, minHeight:0, background:"#0A0A0A", fontFamily:"'DM Sans',sans-serif", display:"flex", alignItems:"center", justifyContent:"center" }}>
      <style>{`*{box-sizing:border-box;margin:0;padding:0;} button{font-family:inherit;} input::placeholder{color:#555!important;}`}</style>
      <div style={{ background:"#111", borderRadius:24, padding:32, border:"1px solid #1a1a1a", maxWidth:380, width:"100%", margin:"0 20px" }}>
        <button type="button" onClick={()=>{ setMode("forgot-phone"); setResetOtp(""); setMsg(""); }} style={{ background:"transparent", border:"none", color:"#888", fontSize:13, cursor:"pointer", padding:0, marginBottom:20, display:"flex", alignItems:"center", gap:6 }}>← Back / Resend code</button>
        <div style={{ textAlign:"center", marginBottom:24 }}>
          <div style={{ fontSize:36, marginBottom:12 }}>📱</div>
          <div style={{ fontSize:20, fontWeight:900, color:"#fff", fontFamily:"'DM Sans',sans-serif" }}>Enter Code & New Password</div>
          <div style={{ fontSize:13, color:"#888", marginTop:8 }}>We texted a 6-digit code to<br/><span style={{ color:"#E8E8E8" }}>{resetPhone}</span></div>
        </div>
        <form onSubmit={e=>{e.preventDefault();submitResetOtp();}} autoComplete="on" style={{ display:"flex", flexDirection:"column", gap:12 }}>
          <input type="number" placeholder="000000" value={resetOtp} onChange={e=>setResetOtp(e.target.value.slice(0,6))}
            aria-label="6-digit verification code" aria-required="true" inputMode="numeric" autoComplete="one-time-code" name="one-time-code"
            style={{ width:"100%", padding:"18px 16px", borderRadius:14, border:"1.5px solid #333", background:"#0d0d0d", fontSize:28, color:"#fff", outline:"none", fontFamily:"'DM Sans',sans-serif", textAlign:"center", letterSpacing:"0.4em", boxSizing:"border-box", appearance:"textfield" }}/>
          <input type="password" placeholder="New password" value={resetNewPass} onChange={e=>setResetNewPass(e.target.value)}
            autoComplete="new-password" name="new-password"
            style={{ width:"100%", padding:"14px 16px", borderRadius:14, border:"1.5px solid #222", background:"#0d0d0d", fontSize:16, color:"#E8E8E8", outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box" }}/>
          <input type="password" placeholder="Confirm new password" value={resetConfirm} onChange={e=>setResetConfirm(e.target.value)}
            autoComplete="new-password" name="confirm-password"
            style={{ width:"100%", padding:"14px 16px", borderRadius:14, border:"1.5px solid #222", background:"#0d0d0d", fontSize:16, color:"#E8E8E8", outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box" }}/>
          {msg && <div style={{ fontSize:13, padding:"10px 14px", borderRadius:12, background:msg.includes("updated")?"rgba(34,197,94,0.12)":"rgba(220,38,38,0.12)", color:msg.includes("updated")?"#22C55E":"#EF4444", lineHeight:1.5 }}>{msg}</div>}
          <button type="submit" disabled={loading} style={{ width:"100%", padding:17, background:"linear-gradient(135deg,#2563EB,#1D4ED8)", border:"none", borderRadius:14, color:"#fff", fontSize:15, fontWeight:800, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", opacity:loading?0.75:1 }}>
            {loading?"Updating...":"Update Password"}
          </button>
        </form>
      </div>
    </div>
  );

  return (
    <div style={{ flex:1, minHeight:0, overflowY:"auto", WebkitOverflowScrolling:"touch", background:"#0A0A0A", fontFamily:"'DM Sans',sans-serif" }}>
      {legalDoc && <LegalModal doc={legalDoc} onClose={() => setLegalDoc(null)}/>}
      <style>{`*{box-sizing:border-box;margin:0;padding:0;} button{font-family:inherit;} input::placeholder{color:#555!important;}`}</style>
      <div style={{ background:"linear-gradient(135deg,#0f1f3d,#1e3a5f)", paddingTop:"max(54px, calc(var(--sat, 0px) + 16px))", paddingBottom:36, paddingLeft:24, paddingRight:24, textAlign:"center", position:"relative", borderBottom:"1px solid #1a1a1a" }}>
        <button onClick={()=>setMode("landing")} style={{ position:"absolute", top:"max(54px, calc(var(--sat, 0px) + 16px))", left:20, background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.12)", color:"rgba(255,255,255,0.7)", fontSize:13, cursor:"pointer", padding:"6px 14px", borderRadius:20, display:"flex", alignItems:"center", gap:6, fontFamily:"'DM Sans',sans-serif", fontWeight:600 }}>← Back</button>
        <div style={{ fontSize:52, letterSpacing:5, paddingLeft:5, fontFamily:"'Bebas Neue',sans-serif", color:"#fff", lineHeight:1 }}>FAD<span style={{ color:"#2563EB" }}>ED</span></div>
        <div style={{ fontSize:14, color:"rgba(255,255,255,0.5)", marginTop:10, textAlign:"center" }}>{mode==="signin"?"Welcome back — we missed you":"Join the fastest way to get faded"}</div>
        <div style={{ display:"flex", gap:16, justifyContent:"center", flexWrap:"wrap", marginTop:14 }}>
          {["Book in 30 seconds","GPS-powered","No line, no wait"].map(t=>(
            <span key={t} style={{ fontSize:11, color:"rgba(255,255,255,0.22)", display:"flex", alignItems:"center", gap:4 }}>
              <span style={{ color:"#22C55E" }}>✓</span>{t}
            </span>
          ))}
        </div>
      </div>
      <div style={{ padding:"16px 20px max(env(safe-area-inset-bottom, 24px), 24px)" }}>
        <div style={{ background:"#111", borderRadius:24, padding:"20px 24px 24px", border:"1px solid #1a1a1a", maxWidth:440, margin:"0 auto" }}>
          <div role="tablist" aria-label="Sign in or create account" style={{ display:"flex", background:"#1a1a1a", borderRadius:14, padding:3, marginBottom:24 }}>
            {[["signin","Sign In"],["signup","Create Account"]].map(([m,l])=>(
              <button key={m} onClick={()=>{ setMode(m); setMsg(""); }}
                role="tab" aria-selected={mode===m} aria-label={l}
                style={{ flex:1, padding:"11px 0", borderRadius:12, border:"none", background:mode===m?"#2a2a2a":"transparent", color:mode===m?"#fff":"#555", fontSize:14, fontWeight:700, cursor:"pointer", boxShadow:mode===m?"0 1px 8px rgba(0,0,0,0.4)":"none", transition:"all 0.18s" }}>{l}</button>
            ))}
          </div>
          <form onSubmit={e=>{e.preventDefault();submit();}} autoComplete="on" style={{ display:"flex", flexDirection:"column", gap:12 }}>
            {mode==="signup" && <>
              {inp(name, setName, "text", "First and last name")}
              {name.trim() && name.trim().split(/\s+/).filter(p=>p.length>=2).length < 2 && (
                <div style={{ fontSize:11, color:"#F59E0B", marginTop:-6, paddingLeft:4 }}>Please include your last name</div>
              )}
              <div>
                <div style={{ fontSize:13, fontWeight:700, color:"#888", marginBottom:8 }}>I am a...</div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                  {[["client","👤","Client","I want a cut"],["barber","✂️","Barber","I give cuts"]].map(([r,emoji,label,sub])=>(
                    <button type="button" key={r} onClick={()=>setRole(r)}
                      aria-pressed={role===r} aria-label={`${label} — ${sub}`}
                      style={{ padding:"16px 10px", borderRadius:14, border:`2px solid ${role===r?"#2563EB":"#222"}`, background:role===r?"rgba(37,99,235,0.12)":"#0d0d0d", cursor:"pointer", textAlign:"center", transition:"all 0.18s" }}>
                      <div aria-hidden="true" style={{ fontSize:26, marginBottom:4 }}>{emoji}</div>
                      <div style={{ fontSize:13, fontWeight:800, color:role===r?"#60A5FA":"#C4C4C4", fontFamily:"'DM Sans',sans-serif" }}>{label}</div>
                      <div style={{ fontSize:11, color:"#555", marginTop:2 }}>{sub}</div>
                    </button>
                  ))}
                </div>
              </div>
            </>}
            <div style={{ position:"relative" }}>
              {inp(email, setEmail, "email", "Email address")}
              {mode==="signup" && <span style={{ position:"absolute", top:14, right:14, color:"#EF4444", fontSize:16, fontWeight:900, pointerEvents:"none" }}>*</span>}
            </div>
            {mode==="signup" && role && (
              <div style={{ position:"relative" }}>
                <input type="tel" placeholder="Phone number" value={phone} onChange={e=>setPhone(e.target.value)}
                  style={{ width:"100%", padding:"14px 16px", borderRadius:14, border:`1.5px solid ${!phone?"#7f1d1d":"#222"}`, background:"#0d0d0d", fontSize:16, color:"#E8E8E8", outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box" }}/>
                <span style={{ position:"absolute", top:14, right:14, color:"#EF4444", fontSize:16, fontWeight:900, pointerEvents:"none" }}>*</span>
              </div>
            )}
            {inp(pass, setPass, "password", "Password")}
            {msg && <div style={{ fontSize:13, padding:"12px 14px", borderRadius:12, background:msg.includes("created")||msg.includes("Check")||msg.includes("sent")?"rgba(34,197,94,0.12)":"rgba(220,38,38,0.12)", color:msg.includes("created")||msg.includes("Check")||msg.includes("sent")?"#22C55E":"#EF4444", lineHeight:1.5 }}>{msg}</div>}
            {/* PIPEDA: separate ToS and Privacy Policy consent — each must be independently accepted */}
            {mode==="signup" && (
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                <label style={{ display:"flex", alignItems:"flex-start", gap:10, cursor:"pointer", fontSize:12, color:"#888", lineHeight:1.6 }}>
                  <input type="checkbox" checked={tosAccepted} onChange={e=>setTosAccepted(e.target.checked)} style={{ marginTop:2, width:16, height:16, flexShrink:0, cursor:"pointer", accentColor:"#2563EB" }}/>
                  <span>
                    I have read and agree to the{" "}
                    <button type="button" onClick={()=>setLegalDoc("terms")} style={{ background:"none", border:"none", color:"#60A5FA", fontSize:12, cursor:"pointer", padding:0, textDecoration:"underline" }}>Terms of Service</button>
                  </span>
                </label>
                <label style={{ display:"flex", alignItems:"flex-start", gap:10, cursor:"pointer", fontSize:12, color:"#888", lineHeight:1.6 }}>
                  <input type="checkbox" checked={privacyAccepted} onChange={e=>setPrivacyAccepted(e.target.checked)} style={{ marginTop:2, width:16, height:16, flexShrink:0, cursor:"pointer", accentColor:"#2563EB" }}/>
                  <span>
                    I have read and agree to the{" "}
                    <button type="button" onClick={()=>setLegalDoc("privacy")} style={{ background:"none", border:"none", color:"#60A5FA", fontSize:12, cursor:"pointer", padding:0, textDecoration:"underline" }}>Privacy Policy</button>
                    , including collection of my location and booking data
                  </span>
                </label>
              </div>
            )}
            <button type="submit" disabled={loading||(mode==="signup"&&(!tosAccepted||!privacyAccepted))} style={{ padding:17, background:"linear-gradient(135deg,#2563EB,#1D4ED8)", border:"none", borderRadius:14, color:"#fff", fontSize:15, fontWeight:800, cursor:(loading||(mode==="signup"&&(!tosAccepted||!privacyAccepted)))?"default":"pointer", fontFamily:"'DM Sans',sans-serif", opacity:(loading||(mode==="signup"&&(!tosAccepted||!privacyAccepted)))?0.5:1, boxShadow:"0 8px 32px rgba(37,99,235,0.4)", marginTop:4 }}>
              {loading?"...":mode==="signin"?"Sign In":"Create Account"}
            </button>

            {/* Forgot password */}
            {mode==="signin" && (
              <button type="button" onClick={()=>{ setMode("forgot"); setMsg(""); }} disabled={loading} style={{ padding:"8px", background:"transparent", border:"none", color:"#60A5FA", fontSize:13, cursor:"pointer", textAlign:"center" }}>
                Forgot your password?
              </button>
            )}

          </form>
          <div style={{ display:"flex", flexDirection:"column", gap:12, marginTop:12 }}>
            {/* Divider */}
            <div style={{ display:"flex", alignItems:"center", gap:12, margin:"4px 0" }}>
              <div style={{ flex:1, height:1, background:"#1a1a1a" }}/>
              <span style={{ fontSize:12, color:"#555", fontWeight:500 }}>or</span>
              <div style={{ flex:1, height:1, background:"#1a1a1a" }}/>
            </div>

            {/* Sign in with Apple — only shown on Apple devices */}
            {IS_APPLE && <button onClick={async()=>{
              setLoading(true); setMsg("");
              if (IS_NATIVE_IOS) {
                const { data, error } = await supabase.auth.signInWithOAuth({ provider:"apple", options:{ redirectTo:"faded://auth/callback", skipBrowserRedirect:true } });
                if (error) { setMsg(error.message); setLoading(false); return; }
                await Browser.open({ url: data.url, windowName:"_self" });
              } else {
                const { error } = await supabase.auth.signInWithOAuth({ provider:"apple", options:{ redirectTo:"https://app.letsgetfaded.com" } });
                if (error) { setMsg(error.message); setLoading(false); }
              }
            }} disabled={loading}
              style={{ padding:"14px 16px", background:"#fff", border:"1.5px solid #fff", borderRadius:14, color:"#000", fontSize:14, fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:10 }}>
              <svg aria-hidden="true" focusable="false"width="17" height="20" viewBox="0 0 814 1000" fill="#000"><path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-57.8-155.5-127.4C46 790.7 0 663 0 541.8c0-207.5 135.4-317.3 269-317.3 71 0 130.1 46.9 175 46.9 42.9 0 109.7-50.7 188.8-50.7zm-3.8-217.5c31.2-37.9 53.5-90.5 53.5-143.1 0-7.3-.6-14.6-1.9-20.7-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 137.5 0 7.9 1.3 15.9 1.9 18.5 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.1-69.3z"/></svg>
              Sign in with Apple
            </button>}

            {/* Google sign in — uses in-app browser on native iOS, web OAuth redirect on web */}
            <button onClick={signInWithGoogle} disabled={loading} style={{ padding:"14px 16px", background:"#1a1a1a", border:"1.5px solid #222", borderRadius:14, color:"#E8E8E8", fontSize:14, fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:10 }}>
              <svg aria-hidden="true" focusable="false"width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.36-8.16 2.36-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/><path fill="none" d="M0 0h48v48H0z"/></svg>
              Continue with Google
            </button>

            {/* Phone OTP sign in — only in signin mode */}
            {mode==="signin" && (
              <button onClick={()=>{ setMode("phone"); setMsg(""); }} style={{ padding:"14px 16px", background:"#1a1a1a", border:"1.5px solid #222", borderRadius:14, color:"#E8E8E8", fontSize:14, fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:10 }}>
                <svg aria-hidden="true" focusable="false" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#E8E8E8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.58 3.4 2 2 0 0 1 3.55 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.54a16 16 0 0 0 6.55 6.55l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                Sign in with Phone Number
              </button>
            )}

            {/* Guest mode */}
            {mode==="signin" && (
              <button onClick={continueAsGuest} style={{ padding:"6px 12px", background:"transparent", border:"none", color:"rgba(255,255,255,0.65)", fontSize:13, fontWeight:600, cursor:"pointer", textDecoration:"underline" }}>
                Browse as guest (no account needed)
              </button>
            )}

            {/* Legal links */}
            <div style={{ display:"flex", justifyContent:"center", gap:16, marginTop:4 }}>
              <button onClick={()=>setLegalDoc("terms")} style={{ background:"none", border:"none", color:"#333", fontSize:11, cursor:"pointer" }}>Terms of Service</button>
              <span style={{ color:"#222", fontSize:11 }}>·</span>
              <button onClick={()=>setLegalDoc("privacy")} style={{ background:"none", border:"none", color:"#333", fontSize:11, cursor:"pointer" }}>Privacy Policy</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


// ─── REGISTRATION WELCOME SPLASH ─────────────────────────────────────────────
function BarberRegistrationWelcomeSplash({ barberName, onDone }) {
  const [phase, setPhase] = useState(0);
  const [stripeLoading, setStripeLoading] = useState(false);

  useEffect(() => {
    const timers = [
      setTimeout(() => setPhase(1), 200),
      setTimeout(() => setPhase(2), 500),
      setTimeout(() => setPhase(3), 800),
      setTimeout(() => setPhase(4), 1100),
      setTimeout(() => setPhase(5), 1400),
    ];
    return () => timers.forEach(clearTimeout);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const goToStripe = async () => {
    setStripeLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("stripe-connect-onboard", {});
      if (error || !data?.url) { setStripeLoading(false); return; }
      window.location.href = data.url;
    } catch { setStripeLoading(false); }
  };

  const vis = (n) => phase >= n;

  return (
    <div style={{
      position:"fixed", inset:0, background:"#0A0A0A", display:"flex",
      flexDirection:"column", alignItems:"center", justifyContent:"center",
      zIndex:9999, padding:"0 24px",
    }}>
      <style>{`@keyframes regPulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:0.6;transform:scale(1.4)}}`}</style>

      {/* Checkmark circle */}
      <div style={{
        width:72, height:72, borderRadius:"50%", border:"3px solid #22C55E",
        display:"flex", alignItems:"center", justifyContent:"center",
        fontSize:34, color:"#22C55E", marginBottom:28,
        transform: vis(1) ? "scale(1)" : "scale(0)",
        transition:"transform 0.5s cubic-bezier(0.34,1.56,0.64,1)",
      }}>✓</div>

      {/* FADED wordmark */}
      <div style={{
        fontFamily:"'Bebas Neue',sans-serif", fontSize:56, fontWeight:900,
        letterSpacing:"0.1em", marginBottom:20,
        opacity: vis(2) ? 1 : 0,
        transform: vis(2) ? "translateY(0)" : "translateY(20px)",
        transition:"opacity 0.5s ease, transform 0.5s cubic-bezier(0.22,1,0.36,1)",
      }}>
        <span style={{ color:"#F5F5F0" }}>FAD</span>
        <span style={{ color:"#2563EB", textShadow:"0 0 40px rgba(37,99,235,0.35)" }}>ED</span>
      </div>

      {/* Welcome text */}
      <div style={{
        fontSize:22, fontWeight:800, color:"#F5F5F0", textAlign:"center",
        fontFamily:"'DM Sans',sans-serif", marginBottom:12,
        opacity: vis(3) ? 1 : 0,
        transform: vis(3) ? "translateY(0)" : "translateY(20px)",
        transition:"opacity 0.5s ease, transform 0.5s cubic-bezier(0.22,1,0.36,1)",
      }}>
        Welcome to FADED, {barberName || "barber"}!
      </div>

      {/* Subtext */}
      <div style={{
        fontSize:15, color:"#6B7280", textAlign:"center", maxWidth:280,
        fontFamily:"'DM Sans',sans-serif", lineHeight:1.65, marginBottom:28,
        opacity: vis(4) ? 1 : 0,
        transform: vis(4) ? "translateY(0)" : "translateY(20px)",
        transition:"opacity 0.5s ease, transform 0.5s cubic-bezier(0.22,1,0.36,1)",
      }}>
        Your profile is set up. Go live from your dashboard whenever you're ready to take clients.
      </div>

      {/* Pulsing dot */}
      <div style={{
        display:"flex", alignItems:"center", gap:8, marginBottom:32,
        opacity: vis(5) ? 1 : 0, transition:"opacity 0.5s ease",
      }}>
        <span style={{ width:8, height:8, borderRadius:"50%", background:"#22C55E", display:"inline-block", animation:"regPulse 1.6s ease-in-out infinite" }}/>
        <span style={{ fontSize:14, color:"#22C55E", fontWeight:700 }}>Profile Ready</span>
      </div>

      {/* Stripe CTA */}
      <div style={{
        display:"flex", flexDirection:"column", gap:10, width:"100%", maxWidth:320,
        opacity: vis(5) ? 1 : 0, transition:"opacity 0.5s ease",
      }}>
        <button onClick={goToStripe} disabled={stripeLoading} style={{
          padding:"14px 24px", background:"#2563EB", border:"none", borderRadius:14,
          color:"#fff", fontSize:15, fontWeight:800, cursor:stripeLoading?"default":"pointer",
          fontFamily:"'DM Sans',sans-serif", opacity:stripeLoading?0.7:1,
          display:"flex", alignItems:"center", justifyContent:"center", gap:8,
        }}>
          {stripeLoading ? "Opening Stripe…" : "💳 Set up card payments"}
        </button>
        <div style={{ fontSize:12, color:"#4B5563", textAlign:"center", lineHeight:1.5 }}>
          Accept debit/credit cards. Takes ~3 minutes.
        </div>
        <button onClick={onDone} style={{
          padding:"11px 24px", background:"transparent",
          border:"1px solid rgba(255,255,255,0.1)", borderRadius:12,
          color:"#6B7280", fontSize:13, fontWeight:600, cursor:"pointer",
          fontFamily:"'DM Sans',sans-serif",
        }}>
          Skip for now — go to dashboard →
        </button>
      </div>
    </div>
  );
}

// ─── SPECIALTY OPTIONS ────────────────────────────────────────────────────────
const SPECIALTY_OPTIONS = [
  "Skin Fades","Temp Fades","Taper Fades","Bald Fades","High Tops",
  "Line Ups / Edging","Beard Trims","Beard Shape Up","360 Waves",
  "Curls / Twist Outs","Locs / Dreads","Braids / Cornrows","Afro Fades",
  "Box Fade","Drop Fades","Classic Cuts","Razor Fades","Mohawks",
  "Hard Parts","Kids Cuts",
  "Caesar Cuts","Shape Ups","Coily Hair","Natural Hair","Waves & Curls",
  "Retwists","Coloring","Design / Art","Texture Cuts","Blow-Out Styles",
  "Knotless Braids","Freeform Locs","Sisterlocks","Afro Textured","Low Fade",
];
const MAX_SPECS = 15;

const LANGUAGE_OPTIONS = [
  "English","French","Spanish","Portuguese","Arabic","Persian (Farsi)","Dari",
  "Urdu","Punjabi","Hindi","Bengali","Tamil","Tagalog","Vietnamese","Mandarin",
  "Korean","Japanese","Turkish","Italian","Somali","Tigrinya","Amharic",
  "Twi","Yoruba","Igbo","Hausa","Wolof","Swahili","Patois","Creole","Pashto","Malay",
];

const HST_BY_PROVINCE = {
  ON:0.13, AB:0.05, BC:0.12, MB:0.12, NB:0.15, NL:0.15,
  NS:0.14, PE:0.15, QC:0.14975, SK:0.11, YT:0.05, NT:0.05, NU:0.05,
};

// Columns the DB grants to anon/authenticated on `barbers` — excludes phone, total_earnings,
// stripe_account_id, licence_number, and other PII/internal fields. Keep in sync with the
// GRANT SELECT (...) list in the fix_rls_gift_cards_notifications_barber_pii migration.
const BARBER_PUBLIC_COLUMNS = "id,handle,bio,specialty,price_min,price_max,rating,review_count,total_cuts,status,lat,lng,service_radius_km,accent_color,since_year,updated_at,avatar_url,portfolio_urls,instagram,city,shareable_slug,is_mobile,service_areas,next_available,address,working_hours,blocked_times,double_stamps,walkin,languages,neighbourhood,province,hst_registered,is_licensed,booking_window_days,stripe_charges_enabled,cover_photo_url,max_client_noshows,gps_updated_at,max_walkin_queue,discovery_boost_until,google_review_url,is_test,no_show_protection,cancellation_fee_type,cancellation_fee_flat_cents,cancellation_window_hours,no_show_fee_type,no_show_fee_flat_cents,country,avg_response_minutes,is_founding_barber,approved,years_cutting,bookings_paused,paused_at,accepts_cash,accepts_etransfer,etransfer_email,accepts_pay_at_shop";

const CA_PROVINCES = ["AB","BC","MB","NB","NL","NS","NT","NU","ON","PE","QC","SK","YT"];
const US_STATES    = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"];

// ─── BARBER ONBOARDING ────────────────────────────────────────────────────────
function BarberOnboarding({ session, onComplete, onBack }) {
  const ctx = useT();
  const t = ctx ? ctx.t : createTheme(false);
  const [step, setStep]   = useState(1);
  const [form, setForm]   = useState(() => {
    const raw = session?.user?.user_metadata?.full_name || "";
    const spaceIdx = raw.indexOf(" ");
    const firstName = spaceIdx > -1 ? raw.slice(0, spaceIdx) : raw;
    const lastName  = spaceIdx > -1 ? raw.slice(spaceIdx + 1) : "";
    return { first_name: firstName, last_name: lastName, handle:"", bio:"", specialty:"", price_min:25, price_max:60, city:"", address:"", phone:session?.user?.user_metadata?.phone||"", instagram:"", accent_color:"#2563EB", shareable_slug:"", is_mobile:false, years_cutting:"" };
  });
  const [avatar, setAvatar]     = useState(null);
  const [saving, setSaving]     = useState(false);
  const [err, setErr]           = useState("");

  const [showRegWelcome, setShowRegWelcome] = useState(false);
  // Address autocomplete — Mapbox
  const [addressQuery, setAddressQuery]     = useState("");
  const [addrSuggestions, setAddrSuggestions] = useState([]);
  const [showAddrDrop, setShowAddrDrop]     = useState(false);
  const [selectedAddrCoords, setSelectedAddrCoords] = useState(null);
  // Specialties multi-select
  const [selectedSpecs, setSelectedSpecs]   = useState([]);
  const [customSpecInput, setCustomSpecInput] = useState("");
  // Languages, neighbourhood, province/state, country
  const [selectedLangs, setSelectedLangs]   = useState([]);
  const [neighbourhood, setNeighbourhood]   = useState("");
  const [country, setCountry]               = useState("CA");
  const [province, setProvince]             = useState("ON");
  const fileRef    = useRef();
  const addrTimer  = useRef(null);

  const fetchAddrSuggestions = (q) => {
    clearTimeout(addrTimer.current);
    if (!q || q.length < 2) { setAddrSuggestions([]); return; }
    addrTimer.current = setTimeout(async () => {
      try {
        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json?access_token=${MAPBOX_TOKEN}&country=ca,us&limit=6&types=address,place,neighborhood,locality`;
        const res  = await fetch(url);
        const data = await res.json();
        if (!data.features?.length) { setAddrSuggestions([]); return; }
        setAddrSuggestions(data.features.map(f => ({ label: f.place_name, lat: f.center[1], lng: f.center[0] })));
        if (data.features.length > 0) setShowAddrDrop(true);
      } catch(e) { setAddrSuggestions([]); }
    }, 300);
  };

  const toggleSpec = (s) => {
    setSelectedSpecs(prev => {
      if (prev.includes(s)) return prev.filter(x=>x!==s);
      if (prev.length >= MAX_SPECS) return prev;
      return [...prev, s];
    });
  };

  const addCustomSpec = () => {
    const v = customSpecInput.trim();
    if (!v || selectedSpecs.includes(v) || selectedSpecs.length >= MAX_SPECS) return;
    setSelectedSpecs(prev => [...prev, v]);
    setCustomSpecInput("");
  };

  const toggleLang = (l) => setSelectedLangs(prev => prev.includes(l) ? prev.filter(x=>x!==l) : [...prev, l]);

  // Auto-timeout: if barber sits on onboarding > 12 min without completing, sign out
  useEffect(() => {
    const timer = setTimeout(() => { if (onBack) onBack(); }, 12 * 60 * 1000);
    return () => clearTimeout(timer);
  }, [onBack]);

  const upd = (k,v) => setForm(f=>({...f,[k]:v}));
  const inp = (key, ph, type="text", required=false) => (
    <input type={type} placeholder={ph} value={form[key]} onChange={e=>upd(key,e.target.value)} autoComplete={key==="phone"?"tel":key==="first_name"?"given-name":key==="last_name"?"family-name":"off"}
      aria-label={ph} aria-required={required || undefined}
      style={{ width:"100%", padding:"13px 14px", borderRadius:12, border:`1.5px solid ${t.border}`, background:t.section, fontSize:16, color:t.inputText, outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box", WebkitBoxShadow:`0 0 0 1000px ${t.section} inset`, WebkitTextFillColor:t.inputText }}/>
  );

  const uploadAvatar = async (file) => {
    file = await toUploadableImage(file);
    const path = `${session.user.id}/${Date.now()}.${file.name.split(".").pop()}`;
    const { error } = await supabase.storage.from("avatars").upload(path, file, { upsert:true });
    if (!error) { const { data } = supabase.storage.from("avatars").getPublicUrl(path); setAvatar(data.publicUrl); }
  };

  const save = async () => {
    if (!form.first_name.trim()) { setErr("Please enter your first name."); return; }
    if (!form.last_name.trim())  { setErr("Please enter your last name."); return; }
    const full_name = `${form.first_name.trim()} ${form.last_name.trim()}`;
    setSaving(true); setErr("");
    const slug  = form.shareable_slug||slugify(full_name);
    const specs = selectedSpecs.length > 0 ? selectedSpecs : form.specialty.split(",").map(s=>s.trim()).filter(Boolean);
    // Geocode address if barber typed manually without picking from autocomplete dropdown
    let coords = selectedAddrCoords;
    if (!coords && form.address) {
      try {
        const r = await fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(form.address)}.json?access_token=${MAPBOX_TOKEN}&country=ca,us&limit=1&types=address,place`);
        const d = await r.json();
        if (d.features?.[0]) coords = { lat: d.features[0].center[1], lng: d.features[0].center[0] };
      } catch {}
    }
    // Build handle — user-entered or auto-generated from name + ID suffix to guarantee uniqueness
    let handle = (form.handle || "").trim();
    if (!handle) {
      handle = "@" + slugify(full_name) + session.user.id.slice(0, 4);
    }
    // Check if handle is already taken by a different barber
    const { data: taken } = await supabase.from("barbers").select("id").eq("handle", handle).neq("id", session.user.id).limit(1);
    if (taken && taken.length > 0) {
      setSaving(false);
      setErr("HANDLE_TAKEN");
      return;
    }
    await supabase.from("profiles").upsert({ id:session.user.id, full_name, role:"barber", avatar_url:avatar, phone:form.phone||null, country, province });
    const barberRow = { id:session.user.id, handle, bio:form.bio, specialty:specs, price_min:+form.price_min, price_max:+form.price_max, status:"offline", avatar_url:avatar, portfolio_urls:[], accent_color:form.accent_color, city:form.city, address:form.address, phone:form.phone, instagram:form.instagram, shareable_slug:slug, since_year:new Date().getFullYear(), is_mobile:form.is_mobile, lat:coords?.lat??null, lng:coords?.lng??null, languages:selectedLangs.length>0?selectedLangs:null, neighbourhood:neighbourhood||null, province:province||(country==="CA"?"ON":"NY"), country, years_cutting:form.years_cutting!==""&&form.years_cutting!=null?+form.years_cutting:null };
    // NOTE: do NOT use .upsert() on barbers. An upsert (ON CONFLICT DO UPDATE) needs SELECT on every column
    // it writes, and authenticated has no SELECT on barbers.phone (PII) -> "permission denied for table barbers".
    // Plain INSERT / UPDATE only need write privileges, which authenticated has.
    const { data: existingBarber } = await supabase.from("barbers").select("id").eq("id", session.user.id).maybeSingle();
    const { id: _barberId, ...barberUpdate } = barberRow;
    const { error } = existingBarber
      ? await supabase.from("barbers").update(barberUpdate).eq("id", session.user.id)
      : await supabase.from("barbers").insert(barberRow);
    setSaving(false);
    if (error) {
      if (error.code === "23505" || error.message?.includes("handle")) {
        setErr("HANDLE_TAKEN");
      } else {
        setErr(error.message);
      }
      return;
    }
    setShowRegWelcome(true);
  };



  return (
    <div style={{ height:"var(--app-height,100dvh)", background:t.bg, fontFamily:"'DM Sans',sans-serif", display:"flex", flexDirection:"column" }}>
      {showRegWelcome && <BarberRegistrationWelcomeSplash barberName={`${form.first_name} ${form.last_name}`.trim()} onDone={onComplete}/>}
      <style>{`*{box-sizing:border-box;margin:0;padding:0;} button{font-family:inherit;} input::placeholder,textarea::placeholder{color:${t.t4}!important;}
.addr-suggestion{padding:10px 14px;color:${t.t1};font-size:13px;cursor:pointer;border-top:1px solid ${t.border};font-family:'DM Sans',sans-serif;}
.addr-suggestion:first-child{border-top:none;}
.addr-suggestion:hover{background:${t.blueBg};}
.spec-chip{display:inline-flex;align-items:center;gap:5px;padding:7px 13px;border-radius:20px;font-size:12px;font-weight:600;cursor:pointer;border:1.5px solid;transition:all 0.15s;font-family:'DM Sans',sans-serif;}
`}</style>
      <div style={{ background:"linear-gradient(135deg,#1e3a5f,#2563EB)", paddingTop:"max(60px,calc(var(--sat, 0px) + 6px))", paddingLeft:20, paddingRight:20, paddingBottom:16, flexShrink:0 }}>
        <div style={{ fontSize:24, letterSpacing:4, fontFamily:"'Bebas Neue',sans-serif", color:"#fff", marginBottom:6 }}>FAD<span style={{ color:"#2563EB" }}>ED</span></div>
        <div style={{ fontSize:15, fontWeight:800, color:"#fff", fontFamily:"'DM Sans',sans-serif" }}>Set Up Your Barber Profile</div>
        <div style={{ fontSize:12, color:"rgba(255,255,255,0.65)", marginTop:2 }}>Step {step} of 4</div>
        <div style={{ marginTop:10, height:4, background:"rgba(255,255,255,0.2)", borderRadius:2 }}>
          <div style={{ height:"100%", width:`${(step/4)*100}%`, background:"#fff", borderRadius:2, transition:"width 0.3s ease" }}/>
        </div>
      </div>

      <div style={{ flex:1, overflowY:"auto", WebkitOverflowScrolling:"touch" }}>
      <div style={{ padding:"16px 20px 0", maxWidth:480, width:"100%", margin:"0 auto", boxSizing:"border-box" }}>
        {/* ── STEP 1: About You ──────────────────────────────────────────── */}
        {step===1 && (
          <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
            <div>
              <div style={{ fontSize:20, fontWeight:800, color:t.t1, marginBottom:4 }}>About You</div>
              <div style={{ fontSize:13, color:t.t4 }}>Your clients will see this on your profile.</div>
            </div>
            <div style={{ display:"flex", gap:10 }}>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:12, fontWeight:700, color:t.t2, marginBottom:6 }}>First Name <span style={{ color:"#EF4444" }}>*</span></div>
                {inp("first_name","Marcus","text",true)}
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:12, fontWeight:700, color:t.t2, marginBottom:6 }}>Last Name <span style={{ color:"#EF4444" }}>*</span></div>
                {inp("last_name","Thompson","text",true)}
              </div>
            </div>
            <div>
              <div style={{ fontSize:12, fontWeight:700, color:t.t2, marginBottom:6 }}>Phone Number <span style={{ color:"#EF4444" }}>*</span></div>
              {inp("phone","(416) 555-0000","tel",true)}
              <div style={{ fontSize:11, color:t.t4, marginTop:4 }}>Used for booking confirmations — not shown publicly</div>
            </div>
            <div>
              <div style={{ fontSize:12, fontWeight:700, color:t.t2, marginBottom:6 }}>Instagram <span style={{ fontSize:11, fontWeight:500, color:t.t4 }}>(optional)</span></div>
              <div style={{ position:"relative" }}>
                <span style={{ position:"absolute", left:14, top:"50%", transform:"translateY(-50%)", color:t.t4, fontSize:14, pointerEvents:"none", fontWeight:600 }}>@</span>
                <input value={form.instagram} onChange={e=>upd("instagram",e.target.value.replace(/^@+/,""))}
                  placeholder="yourhandle"
                  style={{ width:"100%", padding:"13px 14px 13px 30px", borderRadius:12, border:`1.5px solid ${t.border}`, background:t.section, fontSize:16, color:t.inputText, outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box", WebkitBoxShadow:`0 0 0 1000px ${t.section} inset`, WebkitTextFillColor:t.inputText }}/>
              </div>
              <div style={{ fontSize:11, color:t.t4, marginTop:4 }}>Shown on your profile — clients can follow your work</div>
            </div>
            <div>
              <div style={{ fontSize:12, fontWeight:700, color:t.t2, marginBottom:6 }}>Years Cutting Hair <span style={{ color:"#EF4444" }}>*</span></div>
              <input type="number" min={0} max={60} value={form.years_cutting ?? ""} onChange={e=>upd("years_cutting", e.target.value === "" ? "" : parseInt(e.target.value,10))}
                placeholder="e.g. 7"
                style={{ width:"100%", padding:"13px 14px", borderRadius:12, border:`1.5px solid ${t.border}`, background:t.section, fontSize:16, color:t.inputText, outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box", WebkitBoxShadow:`0 0 0 1000px ${t.section} inset`, WebkitTextFillColor:t.inputText }}/>
              <div style={{ fontSize:11, color:t.t4, marginTop:4 }}>Shown on your profile — builds client trust</div>
            </div>
            <div>
              <div style={{ fontSize:12, fontWeight:700, color:t.t2, marginBottom:6 }}>Bio <span style={{ fontSize:11, fontWeight:500, color:t.t4 }}>(optional)</span></div>
              <textarea value={form.bio} onChange={e=>upd("bio",e.target.value)}
                placeholder={`e.g. "${form.years_cutting ? form.years_cutting + " years" : "7 years"} cutting hair in Toronto — specializing in skin fades and lineups. Clean cuts, sharp edges, every time."`}
                rows={3}
                style={{ width:"100%", padding:"13px 14px", borderRadius:12, border:`1.5px solid ${t.border}`, background:t.section, fontSize:16, color:t.inputText, outline:"none", fontFamily:"'DM Sans',sans-serif", resize:"vertical", boxSizing:"border-box", WebkitBoxShadow:`0 0 0 1000px ${t.section} inset`, WebkitTextFillColor:t.inputText }}/>
              <div style={{ fontSize:11, color:t.t4, marginTop:4 }}>Start with how long you've been cutting — it's the first thing clients want to know</div>
            </div>
          </div>
        )}

        {/* ── STEP 2: Where You Work ─────────────────────────────────────── */}
        {step===2 && (
          <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
            <div>
              <div style={{ fontSize:20, fontWeight:800, color:t.t1, marginBottom:4 }}>Where You Work</div>
              <div style={{ fontSize:13, color:t.t4 }}>Helps clients find you on the map.</div>
            </div>
            <div>
              <div style={{ fontSize:12, fontWeight:700, color:t.t2, marginBottom:6 }}>City <span style={{ color:"#EF4444" }}>*</span></div>
              {inp("city","Toronto, ON","text",true)}
            </div>
            <div>
              <div style={{ fontSize:12, fontWeight:700, color:t.t2, marginBottom:6 }}>Shop Address <span style={{ color:"#EF4444" }}>*</span></div>
              <div style={{ position:"relative" }}>
                <input type="text" autoComplete="off"
                  placeholder="123 Main St, Toronto ON"
                  value={addressQuery}
                  onChange={e=>{ const v=e.target.value; setAddressQuery(v); upd("address",v); setSelectedAddrCoords(null); fetchAddrSuggestions(v); }}
                  onFocus={()=>addrSuggestions.length>0&&setShowAddrDrop(true)}
                  onBlur={()=>setTimeout(()=>setShowAddrDrop(false),200)}
                  style={{ width:"100%", padding:"13px 14px", borderRadius:12, border:`1.5px solid ${t.border}`, background:t.section, fontSize:16, color:t.inputText, outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box", WebkitBoxShadow:`0 0 0 1000px ${t.section} inset`, WebkitTextFillColor:t.inputText }}/>
                {showAddrDrop && addrSuggestions.length>0 && (
                  <div role="listbox" aria-label="Address suggestions" style={{ position:"absolute", top:"calc(100% + 4px)", left:0, right:0, background:t.card, border:`1px solid #2563EB`, borderRadius:12, zIndex:99999, overflow:"hidden", boxShadow:`0 8px 24px rgba(0,0,0,0.15)` }}>
                    {addrSuggestions.map((s,i)=>(
                      <div key={i} role="option" aria-selected="false" tabIndex={0} className="addr-suggestion"
                        onMouseDown={()=>{ setAddressQuery(s.label); upd("address",s.label); setSelectedAddrCoords({lat:parseFloat(s.lat),lng:parseFloat(s.lng)}); setAddrSuggestions([]); setShowAddrDrop(false); }}
                        onKeyDown={e=>{ if(e.key==="Enter"||e.key===" "){ e.preventDefault(); setAddressQuery(s.label); upd("address",s.label); setSelectedAddrCoords({lat:parseFloat(s.lat),lng:parseFloat(s.lng)}); setAddrSuggestions([]); setShowAddrDrop(false); } }}>
                        <span style={{ color:"#2563EB", fontWeight:600 }}>{s.label.split(",")[0]}</span>
                        {s.label.includes(",")&&<span style={{ color:t.t3 }}>, {s.label.substring(s.label.indexOf(",")+1).trim()}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div>
              <div style={{ fontSize:12, fontWeight:700, color:t.t2, marginBottom:6 }}>Neighbourhood <span style={{ fontSize:11, fontWeight:500, color:t.t4 }}>(optional)</span></div>
              <input type="text" placeholder="e.g. Kensington Market, Jane-Finch"
                value={neighbourhood} onChange={e=>setNeighbourhood(e.target.value)}
                style={{ width:"100%", padding:"13px 14px", borderRadius:12, border:`1.5px solid ${t.border}`, background:t.section, fontSize:16, color:t.inputText, outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box", WebkitBoxShadow:`0 0 0 1000px ${t.section} inset`, WebkitTextFillColor:t.inputText }}/>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
              <div>
                <div style={{ fontSize:12, fontWeight:700, color:t.t2, marginBottom:6 }}>Country</div>
                <select value={country} onChange={e=>{ setCountry(e.target.value); setProvince(e.target.value==="CA"?"ON":"NY"); }}
                  style={{ width:"100%", padding:"13px 14px", borderRadius:12, border:`1.5px solid ${t.border}`, background:t.section, fontSize:16, color:t.inputText, outline:"none", fontFamily:"'DM Sans',sans-serif" }}>
                  <option value="CA">🇨🇦 Canada</option>
                  <option value="US">🇺🇸 United States</option>
                </select>
              </div>
              <div>
                <div style={{ fontSize:12, fontWeight:700, color:t.t2, marginBottom:6 }}>{country==="US"?"State":"Province"}</div>
                <select value={province} onChange={e=>setProvince(e.target.value)}
                  style={{ width:"100%", padding:"13px 14px", borderRadius:12, border:`1.5px solid ${t.border}`, background:t.section, fontSize:16, color:t.inputText, outline:"none", fontFamily:"'DM Sans',sans-serif" }}>
                  {(country==="US" ? US_STATES : CA_PROVINCES).map(p=><option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            </div>
            <button type="button" onClick={()=>upd("is_mobile",!form.is_mobile)}
              style={{ padding:"14px 16px", borderRadius:12, border:`1.5px solid ${form.is_mobile?"#2563EB":t.border}`, background:form.is_mobile?"rgba(37,99,235,0.1)":t.section, color:form.is_mobile?"#2563EB":t.t2, fontSize:14, fontWeight:700, cursor:"pointer", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <span>📍 I offer mobile cuts (I travel to you)</span>
              <span style={{ fontSize:12, background:form.is_mobile?"#2563EB":"rgba(100,100,100,0.15)", color:form.is_mobile?"#fff":t.t4, padding:"3px 12px", borderRadius:20, transition:"all 0.15s" }}>{form.is_mobile?"Yes":"No"}</span>
            </button>
          </div>
        )}

        {/* ── STEP 3: Your Craft ─────────────────────────────────────────── */}
        {step===3 && (
          <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
            <div>
              <div style={{ fontSize:20, fontWeight:800, color:t.t1, marginBottom:4 }}>Your Craft</div>
              <div style={{ fontSize:13, color:t.t4 }}>What you specialize in — pick up to {MAX_SPECS}.</div>
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div style={{ fontSize:13, fontWeight:700, color:t.t2 }}>Specialties</div>
                <div style={{ fontSize:11, color:selectedSpecs.length>=MAX_SPECS?"#EF4444":t.t4, fontWeight:600 }}>{selectedSpecs.length}/{MAX_SPECS}</div>
              </div>
              <div style={{ display:"flex", flexWrap:"wrap", gap:7 }}>
                {[...SPECIALTY_OPTIONS, ...selectedSpecs.filter(s=>!SPECIALTY_OPTIONS.includes(s))].map(s=>{
                  const sel = selectedSpecs.includes(s);
                  return (
                    <button key={s} type="button" className="spec-chip" onClick={()=>toggleSpec(s)}
                      aria-pressed={sel} aria-label={s}
                      style={{ background:sel?"rgba(37,99,235,0.12)":"transparent", borderColor:sel?"#2563EB":t.border, color:sel?"#2563EB":t.t3 }}>
                      {sel&&<span aria-hidden="true">✓ </span>}{s}
                    </button>
                  );
                })}
              </div>
              <div style={{ display:"flex", gap:8 }}>
                <input type="text" value={customSpecInput} placeholder="Add your own specialty..."
                  onChange={e=>setCustomSpecInput(e.target.value)}
                  onKeyDown={e=>e.key==="Enter"&&(e.preventDefault(),addCustomSpec())}
                  style={{ flex:1, padding:"10px 13px", borderRadius:10, border:`1.5px solid ${t.border}`, background:t.section, fontSize:16, color:t.inputText, outline:"none", fontFamily:"'DM Sans',sans-serif", WebkitBoxShadow:`0 0 0 1000px ${t.section} inset`, WebkitTextFillColor:t.inputText }}/>
                <button type="button" onClick={addCustomSpec} style={{ padding:"10px 18px", borderRadius:10, background:"#2563EB", border:"none", color:"#fff", fontSize:13, fontWeight:700, cursor:"pointer" }}>Add</button>
              </div>
            </div>
            <div>
              <div style={{ fontSize:13, fontWeight:700, color:t.t2, marginBottom:8 }}>Starting Price Range</div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                <div>
                  <div style={{ fontSize:11, color:t.t4, marginBottom:4 }}>From ($)</div>
                  {inp("price_min","25","number")}
                </div>
                <div>
                  <div style={{ fontSize:11, color:t.t4, marginBottom:4 }}>Up to ($)</div>
                  {inp("price_max","60","number")}
                </div>
              </div>
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              <div style={{ fontSize:13, fontWeight:700, color:t.t2 }}>Languages Spoken <span style={{ fontSize:11, fontWeight:500, color:t.t4 }}>(optional)</span></div>
              <div style={{ display:"flex", flexWrap:"wrap", gap:7 }}>
                {LANGUAGE_OPTIONS.map(l=>{
                  const sel=selectedLangs.includes(l);
                  return (
                    <button key={l} type="button" className="spec-chip" onClick={()=>toggleLang(l)}
                      aria-pressed={sel} aria-label={l}
                      style={{ background:sel?"rgba(37,99,235,0.12)":"transparent", borderColor:sel?"#2563EB":t.border, color:sel?"#2563EB":t.t3 }}>
                      {sel&&<span aria-hidden="true">✓ </span>}{l}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ── STEP 4: Photo & Go Live ────────────────────────────────────── */}
        {step===4 && (
          <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
            <div>
              <div style={{ fontSize:20, fontWeight:800, color:t.t1, marginBottom:4 }}>Almost there!</div>
              <div style={{ fontSize:13, color:t.t4 }}>Add your photo and pick your profile colour.</div>
            </div>
            {/* Photo */}
            <div style={{ display:"flex", gap:16, alignItems:"center" }}>
              <div onClick={()=>fileRef.current.click()}
                style={{ width:88, height:88, borderRadius:"50%", border:`2px dashed ${avatar?"transparent":"rgba(37,99,235,0.4)"}`, background:avatar?"transparent":"rgba(37,99,235,0.06)", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", overflow:"hidden", flexShrink:0 }}>
                {avatar ? <img src={avatar} alt="avatar" style={{ width:"100%", height:"100%", objectFit:"cover" }}/> : <span style={{ fontSize:32 }}>📸</span>}
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:14, fontWeight:700, color:t.t1, marginBottom:4 }}>Profile photo <span style={{ fontSize:11, fontWeight:500, color:t.t4 }}>(optional)</span></div>
                <div style={{ fontSize:12, color:t.t3, lineHeight:1.5 }}>Clients are 3× more likely to book barbers with a photo — you can add one now or later from your dashboard</div>
                <button onClick={()=>fileRef.current.click()} style={{ marginTop:10, padding:"7px 16px", background:t.blueBg, border:`1px solid ${t.blueBorder}`, borderRadius:10, color:t.blueText, fontSize:12, fontWeight:700, cursor:"pointer" }}>
                  {avatar?"Change Photo":"Upload Photo"}
                </button>
              </div>
              <input ref={fileRef} type="file" accept="image/*" style={{ display:"none" }} onChange={e=>uploadAvatar(e.target.files[0])}/>
            </div>
            {/* Colour */}
            <div>
              <div style={{ fontSize:12, fontWeight:700, color:t.t2, marginBottom:10 }}>Profile colour</div>
              <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
                {ACCENT_OPTIONS.map(c=>(
                  <button key={c} onClick={()=>upd("accent_color",c)} style={{ width:38, height:38, borderRadius:"50%", background:c, border:form.accent_color===c?`3px solid ${t.t1}`:"3px solid transparent", cursor:"pointer", transform:form.accent_color===c?"scale(1.18)":"scale(1)", transition:"all 0.15s" }}/>
                ))}
              </div>
            </div>
            {/* Booking link */}
            <div>
              <div style={{ fontSize:12, fontWeight:700, color:t.t2, marginBottom:6 }}>Your booking link</div>
              <div style={{ display:"flex", alignItems:"center", background:t.section, border:`1.5px solid ${t.border}`, borderRadius:12, overflow:"hidden" }}>
                <span style={{ padding:"13px 12px", fontSize:12, color:t.t4, fontWeight:600, whiteSpace:"nowrap", borderRight:`1px solid ${t.border}` }}>letsgetfaded.com/</span>
                <input value={form.shareable_slug||slugify(`${form.first_name} ${form.last_name}`.trim())} onChange={e=>upd("shareable_slug",e.target.value)}
                  style={{ flex:1, padding:"13px 12px", border:"none", background:"transparent", fontSize:16, color:t.inputText, outline:"none", fontFamily:"'DM Sans',sans-serif", WebkitBoxShadow:`0 0 0 1000px ${t.section} inset`, WebkitTextFillColor:t.inputText }}/>
              </div>
              <div style={{ fontSize:11, color:t.t4, marginTop:4 }}>Share this link anywhere to get direct bookings</div>
            </div>
            {/* Preview card */}
            <div style={{ background:t.card, borderRadius:18, padding:18, border:`1.5px solid ${t.border}` }}>
              <div style={{ fontSize:11, fontWeight:700, color:t.t4, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:12 }}>Your profile preview</div>
              <div style={{ display:"flex", gap:14, alignItems:"center" }}>
                <div style={{ width:56, height:56, borderRadius:"50%", background:avatar?"transparent":`linear-gradient(135deg,${form.accent_color}22,${form.accent_color}44)`, border:`2.5px solid ${form.accent_color}55`, overflow:"hidden", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, fontWeight:800, color:form.accent_color, flexShrink:0 }}>
                  {avatar?<img src={avatar} alt="p" style={{ width:"100%", height:"100%", objectFit:"cover" }}/>:initials(`${form.first_name} ${form.last_name}`)}
                </div>
                <div>
                  <div style={{ fontSize:15, fontWeight:800, color:t.t1 }}>{`${form.first_name} ${form.last_name}`.trim()||"Your Name"}</div>
                  <div style={{ fontSize:12, color:t.t4, marginTop:1 }}>{form.city}{form.is_mobile?" · Mobile":""}</div>
                  <div style={{ fontSize:12, color:"#059669", fontWeight:600, marginTop:4 }}>Starts Offline — go live anytime from your dashboard</div>
                </div>
              </div>
              {selectedSpecs.length > 0 && (
                <div style={{ display:"flex", flexWrap:"wrap", gap:5, marginTop:12 }}>
                  {selectedSpecs.slice(0,6).map(s=>(
                    <span key={s} style={{ fontSize:11, padding:"3px 10px", borderRadius:20, background:`${form.accent_color}18`, color:form.accent_color, fontWeight:600, border:`1px solid ${form.accent_color}30` }}>{s}</span>
                  ))}
                  {selectedSpecs.length > 6 && <span style={{ fontSize:11, color:t.t4 }}>+{selectedSpecs.length-6} more</span>}
                </div>
              )}
            </div>
            {err && (
              <div style={{ fontSize:13, padding:"12px 14px", borderRadius:12, background:"#FEE2E2", color:"#DC2626", lineHeight:1.5 }}>
                {err === "HANDLE_TAKEN" ? (
                  <>
                    <div style={{ fontWeight:700, marginBottom:4 }}>Account already exists.</div>
                    <div>Sign out and sign back in with the email you originally used, or contact hello@letsgetfaded.com.</div>
                  </>
                ) : err}
              </div>
            )}
            <button onClick={save} disabled={saving}
              style={{ padding:18, background:`linear-gradient(135deg,${form.accent_color},${form.accent_color}cc)`, border:"none", borderRadius:16, color:"#fff", fontSize:16, fontWeight:900, cursor:saving?"default":"pointer", opacity:saving?0.7:1, letterSpacing:"0.03em" }}>
              {saving?"Setting up your profile...":"Go Live Now 🚀"}
            </button>
          </div>
        )}

      </div>
      <div style={{ paddingBottom:16 }}/>
      </div>
      {/* ── FIXED BOTTOM ACTION BAR — always visible above home indicator ── */}
      <div style={{ flexShrink:0, padding:"12px 20px", paddingBottom:"max(44px,calc(var(--sab, 0px)+30px))", maxWidth:480, width:"100%", margin:"0 auto", boxSizing:"border-box", background:t.bg, borderTop:`1px solid ${t.border}` }}>
        {err && step<4 && <div style={{ fontSize:12, color:"#DC2626", marginBottom:8, textAlign:"center" }}>{err}</div>}
        <div style={{ display:"flex", gap:10 }}>
          {step>1
            ? <button onClick={()=>{ setErr(""); setStep(s=>s-1); }} style={{ flex:1, padding:14, background:t.card, border:`1.5px solid ${t.border}`, borderRadius:14, color:t.t2, fontSize:14, fontWeight:600, cursor:"pointer" }}>← Back</button>
            : <button onClick={onBack} style={{ flex:1, padding:14, background:t.card, border:`1.5px solid ${t.border}`, borderRadius:14, color:t.t2, fontSize:14, fontWeight:600, cursor:"pointer" }}>← Sign Out</button>
          }
          {step<4 && <button onClick={()=>{
            if (step===1) {
              if (!form.first_name.trim() || !form.last_name.trim()) { setErr("Please enter your first and last name."); return; }
              if (!form.phone.trim())     { setErr("Phone number is required."); return; }
            }
            if (step===2) {
              if (!form.city.trim())    { setErr("Please enter your city."); return; }
              if (!form.address.trim()) { setErr("Shop address is required."); return; }
            }
            setErr(""); setStep(s=>s+1);
          }} style={{ flex:1, padding:14, background:"#2563EB", border:"none", borderRadius:14, color:"#fff", fontSize:14, fontWeight:800, cursor:"pointer" }}>Continue →</button>}
        </div>
      </div>
    </div>
  );
}

// ─── BARBER CARD ──────────────────────────────────────────────────────────────
const BarberCard = React.memo(function BarberCard({ barber, onClick, userLat, userLng, favorites, onToggleFav }) {
  const { t, isDark } = useT();
  const [hov, setHov] = useState(false);
  const a = barber.accent_color||"#2563EB";
  const s = t.STATUS[barber.status]||t.STATUS.offline;
  const isFav = favorites?.includes(barber.id);
  const dist = distFmt(barber, userLat, userLng);
  const farAway = isFarAway(distKm(barber, userLat, userLng));

  return (
    <div role="button" tabIndex={0} className="faded-card-hover"
      onClick={()=>{ track("barber_profile_viewed",{barber_id:barber.id,is_mobile:!!barber.is_mobile,status:barber.status||"offline"}); onClick(barber); }}
      onKeyDown={e=>{ if(e.key==="Enter"||e.key===" "){ track("barber_profile_viewed",{barber_id:barber.id,is_mobile:!!barber.is_mobile,status:barber.status||"offline"}); onClick(barber); } }}
      onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}
      style={{ background:isDark?"linear-gradient(135deg,#1A1F2E,#141928)":t.card, borderRadius:20, padding:"14px 16px", cursor:"pointer",
        border:`1.5px solid ${barber.status==="available"?"rgba(34,197,94,0.3)":hov?a+"55":t.border2}`,
        boxShadow:barber.status==="available"?`0 0 20px rgba(34,197,94,0.08),0 4px 20px rgba(0,0,0,0.3)`:hov?`0 8px 32px ${a}20,0 2px 8px rgba(0,0,0,0.15)`:`0 2px 12px rgba(0,0,0,0.08)`,
        transition:"all 0.22s ease", transform:hov?"translateY(-2px)":"none", position:"relative", overflow:"hidden", WebkitTapHighlightColor:"transparent", userSelect:"none" }}>
      {barber.status==="available"&&<div style={{ position:"absolute", top:0, left:0, right:0, height:3, background:`linear-gradient(90deg,#22C55E,rgba(34,197,94,0.4))`, borderRadius:"20px 20px 0 0" }}/>}

      <button onClick={e=>{ e.stopPropagation(); onToggleFav&&onToggleFav(barber.id); }}
        aria-label={isFav ? `Remove ${barber.full_name||"barber"} from favourites` : `Save ${barber.full_name||"barber"} to favourites`}
        aria-pressed={isFav}
        style={{ position:"absolute", top:14, right:14, background:"none", border:"none", cursor:"pointer", fontSize:17, padding:0, opacity:isFav?1:0.55 }}>
        <span aria-hidden="true">{isFav?"❤️":"🤍"}</span>
      </button>

      <div style={{ display:"flex", gap:16, alignItems:"flex-start" }}>
        <div style={{ position:"relative", flexShrink:0 }}>
          <Avatar barber={barber} size={64}/>
          {barber.is_founding_barber&&<div style={{ position:"absolute", top:-4, left:-4 }}><FoundingBadge size={22}/></div>}
        </div>
        <div style={{ flex:1, minWidth:0, paddingRight:28 }}>
          <div style={{ fontSize:16, fontWeight:700, color:t.t1, fontFamily:"'DM Sans',sans-serif", letterSpacing:"-0.01em" }}>{barber.full_name}</div>
          <div style={{ fontSize:12, color:t.t4, marginTop:2 }}>
            {barber.neighbourhood||barber.city||barber.handle}
            {(barber.specialty||[]).length>0 && ` · ${barber.specialty.slice(0,2).join(", ")}`}
          </div>

          <div style={{ display:"flex", alignItems:"center", gap:6, marginTop:10 }}>
            <Dot status={barber.status}/>
            <span style={{ fontSize:12, fontWeight:600, color:s.color }}>{etaFmt(barber)}</span>
            {lastActiveFmt(barber) && <span style={{ fontSize:11, color:t.t4 }}>· {lastActiveFmt(barber)}</span>}
          </div>

          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:10 }}>
            <div style={{ display:"flex", alignItems:"center", gap:5 }}>
              {barber.review_count > 0 ? (
                <>
                  <Stars n={barber.rating}/>
                  <span style={{ fontSize:12, fontWeight:600, color:t.t3 }}>{barber.rating} ({barber.review_count})</span>
                </>
              ) : (
                <span style={{ fontSize:12, fontWeight:600, color:a }}>New on FADED</span>
              )}
            </div>
            <span style={{ fontSize:14, fontWeight:700, color:t.t1 }}>{priceFmt(barber)}</span>
          </div>
          <div style={{ fontSize:11, color:farAway?"#F59E0B":t.t4, marginTop:3 }}>{dist}{farAway?" · outside your area":" away"}</div>
        </div>
      </div>
    </div>
  );
});

// ─── REVIEW MODAL ─────────────────────────────────────────────────────────────
function ReviewModal({ booking, onClose, onSubmit }) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      await onSubmit({ booking_id:booking.id, barber_id:booking.barber_id, rating, comment });
      onClose();
    } catch (e) {
      console.error("[FADED] submitReview:", e);
    } finally {
      setSubmitting(false);
    }
  };

  const { t } = useT();
  const { isMd } = useR();
  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, zIndex:300, background:"rgba(17,24,39,0.5)", backdropFilter:"blur(6px)", display:"flex", alignItems:isMd?"center":"flex-end", justifyContent:"center" }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:t.card, borderRadius:isMd?24:"24px 24px 0 0", width:"100%", maxWidth:isMd?560:480, padding:"24px 24px 48px", animation:"slideUp 0.25s ease", border:`1px solid ${t.border}` }}>
        {!isMd && <div style={{ display:"flex", justifyContent:"center", marginBottom:20 }}><div style={{ width:40, height:4, background:t.surface2, borderRadius:2 }}/></div>}
        <div style={{ fontSize:20, fontWeight:900, color:t.t1, fontFamily:"'DM Sans',sans-serif", marginBottom:6 }}>Rate your cut</div>
        <div style={{ fontSize:13, color:t.t3, marginBottom:20 }}>How was your experience with {booking.barber_name}?</div>
        <div style={{ textAlign:"center", marginBottom:20 }}>
          <Stars n={rating} interactive={true} onRate={setRating}/>
          <div style={{ fontSize:13, color:t.t4, marginTop:4 }}>{["","Terrible","Bad","OK","Good","Amazing!"][rating]}</div>
        </div>
        <textarea value={comment} onChange={e=>setComment(e.target.value)} placeholder="Tell clients what you thought..." rows={3}
          style={{ width:"100%", padding:"13px 14px", borderRadius:12, border:`1.5px solid ${t.border2}`, background:t.input, fontSize:16, color:t.inputText, outline:"none", fontFamily:"'DM Sans',sans-serif", resize:"vertical", boxSizing:"border-box", marginBottom:16 }}/>
        <button onClick={submit} disabled={submitting} style={{ width:"100%", padding:16, background:"#2563EB", border:"none", borderRadius:14, color:"#fff", fontSize:15, fontWeight:800, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>
          {submitting?"Submitting...":"Submit Review"}
        </button>
      </div>
    </div>
  );
}

// ─── POST-CUT MODAL (tip + review) ────────────────────────────────────────────
function PostCutModal({ booking, onClose, onSubmit }) {
  const { t } = useT();
  const [rating, setRating]       = useState(5);
  const [comment, setComment]     = useState("");
  const [tip, setTip]             = useState(0);
  const [customTip, setCustomTip] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone]           = useState(false);

  const TIP_OPTIONS = [0, 5, 10, 15, 20];

  const submit = async () => {
    setSubmitting(true);
    try {
      const tipAmt = tip === "custom" ? (parseInt(customTip, 10) || 0) : tip;
      await onSubmit({ booking_id: booking.id, barber_id: booking.barber_id, rating, comment, tip_amount: tipAmt });
      setDone(true);
    } catch (e) {
      console.error("[FADED] postCutSubmit:", e);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) return (
    <div onClick={onClose} className="faded-sheet-wrap" style={{ position:"fixed", inset:0, zIndex:400, background:"rgba(17,24,39,0.6)", backdropFilter:"blur(6px)", display:"flex", alignItems:"flex-end", justifyContent:"center" }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:t.card, borderRadius:"28px 28px 0 0", width:"100%", maxWidth:480, padding:"40px 24px 56px", textAlign:"center", animation:"slideUp 0.25s ease" }}>
        <div style={{ fontSize:56, marginBottom:16 }}>🙌</div>
        <div style={{ fontSize:20, fontWeight:900, color:t.t1, fontFamily:"'DM Sans',sans-serif" }}>Thanks for the review!</div>
        <div style={{ fontSize:13, color:t.t3, marginTop:8, marginBottom:28 }}>Your review helps {booking.barber_name} grow their reputation on FADED.</div>
        <button onClick={onClose} style={{ padding:"14px 40px", background:"#2563EB", border:"none", borderRadius:14, color:"#fff", fontSize:15, fontWeight:800, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>Done</button>
      </div>
    </div>
  );

  return (
    <div onClick={onClose} className="faded-sheet-wrap" style={{ position:"fixed", inset:0, zIndex:400, background:"rgba(17,24,39,0.6)", backdropFilter:"blur(6px)", display:"flex", alignItems:"flex-end", justifyContent:"center" }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:t.card, borderRadius:"28px 28px 0 0", width:"100%", maxWidth:480, maxHeight:"90vh", overflowY:"auto", animation:"slideUp 0.28s cubic-bezier(0.32,0.72,0,1)", paddingBottom:40 }}>
        <div style={{ display:"flex", justifyContent:"center", paddingTop:12, paddingBottom:4 }}><div style={{ width:40, height:4, background:t.surface2, borderRadius:2 }}/></div>
        <div style={{ padding:"16px 24px 0" }}>
          <div style={{ fontSize:22, fontWeight:900, color:t.t1, fontFamily:"'DM Sans',sans-serif", marginBottom:4 }}>How was your cut? ✂️</div>
          <div style={{ fontSize:13, color:t.t3, marginBottom:20 }}>{booking.barber_name} · {booking.service_type}</div>

          {/* Stars */}
          <div style={{ textAlign:"center", marginBottom:20 }}>
            <Stars n={rating} interactive={true} onRate={setRating}/>
            <div style={{ fontSize:13, color:t.t4, marginTop:6 }}>{["","Terrible","Bad","OK","Good","Amazing!"][rating]}</div>
          </div>

          {/* Tip */}
          <div style={{ fontSize:13, fontWeight:800, color:t.t1, fontFamily:"'DM Sans',sans-serif", marginBottom:10 }}>Leave a tip?</div>
          <div style={{ display:"flex", gap:8, marginBottom:12, flexWrap:"wrap" }}>
            {TIP_OPTIONS.map(amt => (
              <button key={amt} onClick={()=>setTip(amt)}
                style={{ flex:"1 0 auto", padding:"9px 4px", background:tip===amt?"#2563EB":t.section, border:tip===amt?"none":`1px solid ${t.border}`, borderRadius:10, color:tip===amt?"#fff":t.t2, fontSize:13, fontWeight:700, cursor:"pointer", minWidth:52 }}>
                {amt === 0 ? "No tip" : `$${amt}`}
              </button>
            ))}
            <button onClick={()=>setTip("custom")}
              style={{ flex:"1 0 auto", padding:"9px 4px", background:tip==="custom"?"#2563EB":t.section, border:tip==="custom"?"none":`1px solid ${t.border}`, borderRadius:10, color:tip==="custom"?"#fff":t.t2, fontSize:13, fontWeight:700, cursor:"pointer", minWidth:52 }}>
              Other
            </button>
          </div>
          {tip === "custom" && (
            <input type="number" min="1" placeholder="Enter amount ($)" value={customTip} onChange={e=>setCustomTip(e.target.value)}
              style={{ width:"100%", padding:"11px 14px", borderRadius:12, border:`1.5px solid ${t.border2}`, background:t.input, fontSize:16, color:t.inputText, outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box", marginBottom:12 }}/>
          )}

          {/* Comment */}
          <textarea value={comment} onChange={e=>setComment(e.target.value)} placeholder="Say something about your experience... (optional)" rows={3}
            style={{ width:"100%", padding:"13px 14px", borderRadius:12, border:`1.5px solid ${t.border2}`, background:t.input, fontSize:16, color:t.inputText, outline:"none", fontFamily:"'DM Sans',sans-serif", resize:"none", boxSizing:"border-box", marginBottom:16 }}/>

          <button onClick={submit} disabled={submitting}
            style={{ width:"100%", padding:16, background:"#2563EB", border:"none", borderRadius:14, color:"#fff", fontSize:15, fontWeight:800, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", opacity:submitting?0.7:1 }}>
            {submitting ? "Submitting..." : `Submit Review${tip && tip !== 0 ? ` & Tip $${tip === "custom" ? (customTip||"0") : tip}` : ""}`}
          </button>
          <button onClick={onClose} style={{ width:"100%", padding:"12px", background:"none", border:"none", color:t.t4, fontSize:13, cursor:"pointer", marginTop:4 }}>Skip for now</button>
        </div>
      </div>
    </div>
  );
}

// ─── LIVE APPOINTMENT SCREEN ──────────────────────────────────────────────────
function LiveApptScreen({ appt, onAddService, onClose, localUpdates }) {
  const { t } = useT();
  const checkedInAt = appt.checked_in_at ? new Date(appt.checked_in_at) : new Date();
  const durationSecs = (appt.duration_minutes || 30) * 60;
  const [elapsed, setElapsed]       = useState(() => Math.floor((Date.now() - checkedInAt) / 1000));
  const [showTip, setShowTip]       = useState(false);
  const [rating, setRating]         = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [tip, setTip]               = useState(null);
  const [customTip, setCustomTip]   = useState("");
  const [showCustom, setShowCustom] = useState(false);
  const [tipDone, setTipDone]       = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const iv = setInterval(() => {
      setElapsed(prev => {
        const next = prev + 1;
        if (next >= durationSecs) setShowTip(true);
        return next;
      });
    }, 1000);
    return () => clearInterval(iv);
  }, [durationSecs]);

  const remaining   = Math.max(0, durationSecs - elapsed);
  const pct         = Math.min(elapsed / durationSecs, 1);
  const remMins     = Math.floor(remaining / 60);
  const remSecs     = remaining % 60;
  const isDone      = remaining === 0;
  const finishTime  = new Date(checkedInAt.getTime() + durationSecs * 1000)
    .toLocaleTimeString([], { hour:"2-digit", minute:"2-digit" });
  const startTime   = checkedInAt.toLocaleTimeString([], { hour:"2-digit", minute:"2-digit" });
  const service     = localUpdates[appt.id]?.service_type || appt.service_type || "Haircut";
  const price       = (localUpdates[appt.id]?.price_agreed ?? appt.price_agreed) || 0;

  const submitTip = async () => {
    setSubmitting(true);
    const tipAmt = showCustom ? (parseFloat(customTip) || 0) : (tip || 0);
    await supabase.from("bookings")
      .update({ client_rating: rating || null, client_tip: tipAmt })
      .eq("id", appt.id);
    setTipDone(true);
    setTimeout(() => onClose(), 1600);
  };

  return (
    <div style={{ position:"fixed", top:0, left:0, right:0, bottom:0, zIndex:500, background:"#0A0A0A", display:"flex", flexDirection:"column", animation:"slideUp 0.32s cubic-bezier(0.32,0.72,0,1)" }}>
      <style>{`@keyframes pulseRing { 0%,100%{opacity:0.5;transform:scale(1)} 50%{opacity:1;transform:scale(1.04)} }`}</style>

      {/* Header */}
      <div style={{ flexShrink:0, paddingTop:"max(48px, calc(var(--sat, 0px) + 6px))", paddingBottom:16, paddingLeft:20, paddingRight:20, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <div>
          <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:28, letterSpacing:"0.1em", color:"#fff", lineHeight:1 }}>
            {isDone ? "CUT COMPLETE 🎉" : "YOU'RE IN THE CHAIR ✂️"}
          </div>
          <div style={{ fontSize:12, color:"rgba(245,245,240,0.45)", marginTop:3, fontFamily:"'DM Sans',sans-serif" }}>
            {isDone ? "Rate your experience below" : `with ${appt.barber_name?.split(" ")[0] || "your barber"}`}
          </div>
        </div>
        <button onClick={onClose} style={{ width:36, height:36, borderRadius:10, background:"rgba(245,245,240,0.08)", border:"none", color:"rgba(245,245,240,0.5)", fontSize:18, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>×</button>
      </div>

      {/* Scrollable content */}
      <div style={{ flex:1, overflowY:"auto", WebkitOverflowScrolling:"touch", padding:"0 20px 24px" }}>

        {/* Expanded boarding pass card */}
        <div style={{ borderRadius:20, overflow:"hidden", boxShadow:"0 8px 40px rgba(0,0,0,0.6)", marginBottom:16 }}>
          {/* Blue gradient header */}
          <div style={{ background:"linear-gradient(135deg,#1a3a6b,#2563EB)", padding:"18px 20px", display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
            <div>
              <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:10, color:"rgba(255,255,255,0.55)", letterSpacing:"0.15em", marginBottom:4 }}>YOUR BARBER</div>
              <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:26, color:"#fff", letterSpacing:"0.05em", lineHeight:1 }}>{(appt.barber_name||"Barber").toUpperCase()}</div>
              <div style={{ fontSize:12, color:"rgba(255,255,255,0.6)", marginTop:3 }}>{service}</div>
            </div>
            <div style={{ textAlign:"right" }}>
              <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:10, color:"rgba(255,255,255,0.4)", letterSpacing:"0.1em", marginBottom:2 }}>REF</div>
              <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:12, color:"rgba(255,255,255,0.75)", letterSpacing:"0.1em" }}>
                {(() => { const n=(appt.barber_name||"XX").split(" ").map(w=>w[0]).join("").toUpperCase().slice(0,2); const num=String(appt.id||"").slice(-4).padStart(4,"0"); return `FD-${num}${n}`; })()}
              </div>
            </div>
          </div>

          {/* White body */}
          <div style={{ background:"#F8F8F6", padding:"18px 20px 20px" }}>
            {/* Start / Finish / Price row */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginBottom:18, paddingBottom:16, borderBottom:"1.5px dashed #ddd" }}>
              {[["STARTED", startTime], ["EST. DONE", finishTime], ["PRICE", `$${price}`]].map(([label, val]) => (
                <div key={label}>
                  <div style={{ fontSize:10, fontWeight:700, color:"#aaa", letterSpacing:"0.12em", marginBottom:3 }}>{label}</div>
                  <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:13, fontWeight:800, color: label==="PRICE"?"#2563EB":"#111" }}>{val}</div>
                </div>
              ))}
            </div>

            {/* Progress bar */}
            <div style={{ marginBottom:14 }}>
              <div style={{ height:6, background:"#e8e8e8", borderRadius:3, overflow:"hidden", marginBottom:8 }}>
                <div style={{ height:"100%", width:`${pct*100}%`, background: isDone?"#22C55E":"#2563EB", borderRadius:3, transition:"width 1s linear" }}/>
              </div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:11, color:"#888" }}>
                  {isDone ? "✅ Done" : `${Math.round(pct*100)}% through`}
                </div>
                <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:11, color:"#888" }}>
                  {appt.duration_minutes || 30} min total
                </div>
              </div>
            </div>

            {/* Big countdown */}
            {!isDone && (
              <div style={{ textAlign:"center", padding:"14px 0 6px" }}>
                <div style={{ fontSize:11, fontWeight:700, color:"#aaa", letterSpacing:"0.14em", marginBottom:6 }}>REMAINING</div>
                <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:48, fontWeight:800, color:"#111", lineHeight:1, letterSpacing:"-0.02em" }}>
                  {String(remMins).padStart(2,"0")}:{String(remSecs).padStart(2,"0")}
                </div>
                <div style={{ fontSize:11, color:"#aaa", marginTop:6 }}>Est. done by {finishTime}</div>
                {/* Lets the client close this screen if their cut actually finished early,
                    instead of being stuck waiting for the timer or for the barber to end it
                    on their side. UI-only dismiss (same as the header ×) -- doesn't touch
                    booking status or payment, since only the barber's End Cut should trigger
                    the Stripe capture. */}
                <button onClick={onClose} style={{ marginTop:14, padding:"8px 16px", background:"none", border:"1px solid #ddd", borderRadius:100, color:"#555", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>
                  Finished early? Close this
                </button>
              </div>
            )}
            {isDone && (
              <div style={{ textAlign:"center", padding:"14px 0 6px" }}>
                <div style={{ fontSize:32 }}>🎉</div>
                <div style={{ fontSize:14, fontWeight:800, color:"#111", marginTop:6, fontFamily:"'DM Sans',sans-serif" }}>Looking fresh!</div>
                <div style={{ fontSize:12, color:"#888", marginTop:3 }}>Leave a review below</div>
              </div>
            )}
          </div>
        </div>

        {/* Add a service */}
        <button onClick={()=>onAddService({ ...appt, service_type: service, price_agreed: price })}
          style={{ width:"100%", padding:"13px", background:"rgba(37,99,235,0.08)", border:"1.5px solid rgba(37,99,235,0.2)", borderRadius:14, color:"#2563EB", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", marginBottom:10 }}>
          ＋ Add a Service
        </button>

        {/* Rate now / tip prompt (always visible, but highlighted when done) */}
        {!tipDone && (
          <div style={{ background: isDone?"rgba(37,99,235,0.06)":"rgba(245,245,240,0.03)", border:`1.5px solid ${isDone?"rgba(37,99,235,0.25)":"rgba(245,245,240,0.06)"}`, borderRadius:16, padding:"18px 16px" }}>
            <div style={{ fontSize:14, fontWeight:800, color:"rgba(245,245,240,0.9)", fontFamily:"'DM Sans',sans-serif", marginBottom:4 }}>
              {isDone ? "Rate your cut ✂️" : "Rate when done"}
            </div>
            <div style={{ fontSize:11, color:"rgba(245,245,240,0.4)", marginBottom:14 }}>
              {isDone ? `How was your experience with ${appt.barber_name?.split(" ")[0]||"your barber"}?` : "Stars + tip unlock after the timer"}
            </div>
            {/* Stars */}
            <div style={{ display:"flex", gap:8, justifyContent:"center", marginBottom:16 }}>
              {[1,2,3,4,5].map(s => (
                <button key={s}
                  onClick={()=>isDone&&setRating(s)}
                  onMouseEnter={()=>isDone&&setHoverRating(s)}
                  onMouseLeave={()=>setHoverRating(0)}
                  style={{ background:"none", border:"none", fontSize:32, cursor:isDone?"pointer":"default", opacity:isDone?1:0.3, transition:"transform 0.1s", transform:(hoverRating||rating)>=s?"scale(1.15)":"scale(1)", filter:(hoverRating||rating)>=s?"none":"grayscale(1)" }}>
                  ⭐
                </button>
              ))}
            </div>
            {/* Tip amounts */}
            {isDone && (
              <>
                <div style={{ fontSize:11, fontWeight:700, color:"rgba(245,245,240,0.45)", letterSpacing:"0.1em", marginBottom:10, textAlign:"center" }}>ADD A TIP (CASH / IN-PERSON)</div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:8, marginBottom:14 }}>
                  {[5,10,15].map(amt => (
                    <button key={amt} onClick={()=>{ setTip(tip===amt?null:amt); setShowCustom(false); setCustomTip(""); }}
                      style={{ padding:"10px 0", borderRadius:10, border:`1.5px solid ${tip===amt?"#2563EB":"rgba(245,245,240,0.12)"}`, background:tip===amt?"rgba(37,99,235,0.15)":"transparent", color:tip===amt?"#60a5fa":"rgba(245,245,240,0.7)", fontSize:13, fontWeight:700, cursor:"pointer" }}>
                      ${amt}
                    </button>
                  ))}
                  <button onClick={()=>{ setShowCustom(v=>!v); setTip(null); }}
                    style={{ padding:"10px 0", borderRadius:10, border:`1.5px solid ${showCustom?"#2563EB":"rgba(245,245,240,0.12)"}`, background:showCustom?"rgba(37,99,235,0.15)":"transparent", color:showCustom?"#60a5fa":"rgba(245,245,240,0.7)", fontSize:13, fontWeight:700, cursor:"pointer" }}>
                    Custom
                  </button>
                </div>
                {showCustom && (
                  <div style={{ position:"relative", marginBottom:14 }}>
                    <span style={{ position:"absolute", left:14, top:"50%", transform:"translateY(-50%)", color:"rgba(245,245,240,0.5)", fontSize:15, fontWeight:700 }}>$</span>
                    <input type="number" min="0" step="0.5" value={customTip} onChange={e=>setCustomTip(e.target.value)}
                      placeholder="0.00"
                      style={{ width:"100%", padding:"12px 14px 12px 28px", background:"rgba(245,245,240,0.06)", border:"1.5px solid rgba(37,99,235,0.3)", borderRadius:10, color:"#fff", fontSize:15, fontWeight:700, fontFamily:"'JetBrains Mono',monospace", outline:"none", boxSizing:"border-box" }}/>
                  </div>
                )}
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                  <button onClick={onClose} style={{ padding:"13px", background:"transparent", border:"1px solid rgba(245,245,240,0.1)", borderRadius:12, color:"rgba(245,245,240,0.45)", fontSize:13, fontWeight:700, cursor:"pointer" }}>Skip</button>
                  <button onClick={submitTip} disabled={submitting}
                    style={{ padding:"13px", background:"#2563EB", border:"none", borderRadius:12, color:"#fff", fontSize:13, fontWeight:800, cursor:"pointer", opacity:submitting?0.6:1, fontFamily:"'DM Sans',sans-serif" }}>
                    {submitting ? "Saving…" : rating ? "Submit ✓" : "Done →"}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
        {tipDone && (
          <div style={{ textAlign:"center", padding:"20px 0" }}>
            <div style={{ fontSize:32 }}>🙌</div>
            <div style={{ fontSize:15, fontWeight:800, color:"rgba(245,245,240,0.9)", marginTop:8, fontFamily:"'DM Sans',sans-serif" }}>Thanks! See you next time.</div>
          </div>
        )}
      </div>

      {/* Safe-area spacer */}
      <div style={{ height:"env(safe-area-inset-bottom,0px)", minHeight:16, flexShrink:0 }}/>
    </div>
  );
}

// ─── BARBER LIVE SCREEN ───────────────────────────────────────────────────────
function BarberLiveScreen({ appt, onEndCut, onNoShow, onClose }) {
  const { t } = useT();
  const startedAt = appt.checked_in_at ? new Date(appt.checked_in_at) : new Date();
  const [elapsed, setElapsed] = useState(() => Math.floor((Date.now() - startedAt) / 1000));

  useEffect(() => {
    const iv = setInterval(() => setElapsed(prev => prev + 1), 1000);
    return () => clearInterval(iv);
  }, []);

  const mins = Math.floor(elapsed / 60);
  const secs = elapsed % 60;
  const service = appt.service_type || "Haircut";
  const price = appt.price_agreed || 0;
  const durationMins = appt.duration_minutes || 30;
  const pct = Math.min(elapsed / (durationMins * 60), 1);
  const isDone = elapsed >= durationMins * 60;

  return (
    <div style={{ position:"fixed", top:0, left:0, right:0, bottom:0, zIndex:500, background:"#0A0A0A", display:"flex", flexDirection:"column", animation:"slideUp 0.32s cubic-bezier(0.32,0.72,0,1)" }}>
      {/* Header */}
      <div style={{ flexShrink:0, paddingTop:"max(48px, calc(var(--sat, 0px) + 6px))", paddingBottom:16, paddingLeft:20, paddingRight:20, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <div>
          <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:28, letterSpacing:"0.1em", color:"#fff", lineHeight:1 }}>
            {isDone ? "TIME'S UP ✂️" : "CUT IN PROGRESS ✂️"}
          </div>
          <div style={{ fontSize:12, color:"rgba(245,245,240,0.45)", marginTop:3, fontFamily:"'DM Sans',sans-serif" }}>
            {appt.client_name || "Client"} · {service}
          </div>
        </div>
        <button onClick={onClose} style={{ width:36, height:36, borderRadius:10, background:"rgba(245,245,240,0.08)", border:"none", color:"rgba(245,245,240,0.5)", fontSize:20, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>×</button>
      </div>

      {/* Content */}
      <div style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"0 24px 40px" }}>
        {/* Circular timer */}
        <div style={{ position:"relative", width:200, height:200, marginBottom:32 }}>
          <svg width="200" height="200" style={{ transform:"rotate(-90deg)" }}>
            <circle cx="100" cy="100" r="88" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="8"/>
            <circle cx="100" cy="100" r="88" fill="none" stroke={isDone?"#22C55E":"#2563EB"} strokeWidth="8"
              strokeDasharray={`${2*Math.PI*88}`}
              strokeDashoffset={`${2*Math.PI*88*(1-pct)}`}
              strokeLinecap="round"
              style={{ transition:"stroke-dashoffset 1s linear, stroke 0.3s" }}/>
          </svg>
          <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}>
            <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:46, fontWeight:800, color:"#fff", lineHeight:1 }}>
              {String(mins).padStart(2,"0")}:{String(secs).padStart(2,"0")}
            </div>
            <div style={{ fontSize:11, color:"rgba(245,245,240,0.4)", marginTop:6, fontWeight:600, letterSpacing:"0.1em" }}>ELAPSED</div>
          </div>
        </div>

        {/* Service / Price / Est. time */}
        <div style={{ display:"flex", gap:0, marginBottom:36, textAlign:"center", background:"rgba(255,255,255,0.04)", borderRadius:16, border:"1px solid rgba(255,255,255,0.08)", overflow:"hidden", width:"100%", maxWidth:340 }}>
          {[["SERVICE", service], ["PRICE", `$${price}`], ["EST.", `${durationMins}min`]].map(([label, val], i) => (
            <div key={label} style={{ flex:1, padding:"14px 8px", borderRight: i<2 ? "1px solid rgba(255,255,255,0.08)" : "none" }}>
              <div style={{ fontSize:10, fontWeight:700, color:"rgba(245,245,240,0.4)", letterSpacing:"0.12em", marginBottom:5 }}>{label}</div>
              <div style={{ fontSize:14, fontWeight:800, color: label==="PRICE"?"#22C55E":"#fff" }}>{val}</div>
            </div>
          ))}
        </div>

        {/* End Cut button */}
        <button onClick={onEndCut} style={{ width:"100%", maxWidth:340, padding:"18px", background:"#22C55E", border:"none", borderRadius:16, color:"#fff", fontSize:17, fontWeight:900, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", marginBottom:10 }}>
          ✂️ END CUT — CHARGE ${price}
        </button>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, width:"100%", maxWidth:340 }}>
          <button onClick={onNoShow} style={{ padding:"13px", background:"rgba(245,158,11,0.1)", border:"1px solid rgba(245,158,11,0.3)", borderRadius:12, color:"#F59E0B", fontSize:13, fontWeight:700, cursor:"pointer" }}>
            👻 No Show
          </button>
          <button onClick={onClose} style={{ padding:"13px", background:"rgba(245,245,240,0.06)", border:"1px solid rgba(245,245,240,0.1)", borderRadius:12, color:"rgba(245,245,240,0.5)", fontSize:13, fontWeight:700, cursor:"pointer" }}>
            Minimize
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── CLIENT CALENDAR ──────────────────────────────────────────────────────────
function ClientCalendar({ bookings, barbers, onCancel, onRebook, onRefresh, session, onDiscover, compactTop }) {
  const { t } = useT();
  const { isMd } = useR();
  const [view, setView] = useState("upcoming"); // "upcoming" | "past"
  const [cancelId, setCancelId] = useState(null);
  const [cancelBarberPolicy, setCancelBarberPolicy] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [viewPassId, setViewPassId] = useState(null);
  const [checkedInIds, setCheckedInIds] = useState(() => new Set(bookings.filter(b=>b.checked_in_at).map(b=>b.id)));
  const [checkingInId, setCheckingInId] = useState(null);
  const [checkInError, setCheckInError] = useState({}); // bookingId → error string
  const [seatedConfirm, setSeatedConfirm] = useState(null); // booking pending seated confirmation
  const [liveAppt, setLiveAppt]         = useState(null);
  const [refreshing, setRefreshing]     = useState(false);
  const [addServiceAppt, setAddServiceAppt] = useState(null);
  const [clientAddonServices, setClientAddonServices] = useState([]);
  const [addingClientService, setAddingClientService] = useState(false);
  const [clientAddedMsg, setClientAddedMsg] = useState("");
  const [localUpdates, setLocalUpdates] = useState({});
  const dismissedLiveRef = useRef((() => { try { return sessionStorage.getItem("faded_client_live_dismissed") || null; } catch { return null; } })());

  // Restore live screen when app reopens or bookings load after a check-in
  useEffect(() => {
    if (liveAppt) return;
    const active = bookings.find(b =>
      b.checked_in_at &&
      (b.status === "accepted" || b.status === "confirmed") &&
      b.id !== dismissedLiveRef.current
    );
    if (active) setLiveAppt(active);
  }, [bookings]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCheckIn = async (b) => {
    // Demo / localStorage-only bookings — skip all checks
    if (b._isDemo || b._local || b.id?.startsWith("local-")) {
      setCheckedInIds(prev => new Set([...prev, b.id]));
      setLiveAppt({ ...b, checked_in_at: new Date().toISOString() });
      return;
    }
    if (!session || session === "guest") return;

    const now = new Date();
    const scheduledAt = new Date(b.scheduled_at);
    const minutesFromAppt = (now - scheduledAt) / 60000; // negative = early, positive = late

    // ── 1. Time window: 5 min before to 30 min after ─────────────────────────
    if (minutesFromAppt < -5) {
      const minsUntil = Math.ceil(-minutesFromAppt);
      const label = minsUntil >= 60
        ? `${Math.floor(minsUntil/60)}h ${minsUntil%60}m`
        : `${minsUntil} min`;
      setCheckInError(prev => ({ ...prev, [b.id]: `Check-in opens in ${label} — contact your barber if you need help` }));
      return;
    }
    if (minutesFromAppt > 30) {
      setCheckInError(prev => ({ ...prev, [b.id]: "Appointment window has passed — contact your barber" }));
      return;
    }

    // ── 2. GPS proximity: must be within 300m of barber's location ────────────
    const barber = barbers.find(bar => bar.id === b.barber_id);
    if (barber?.lat && barber?.lng) {
      setCheckingInId(b.id);
      try {
        const pos = await getPosition({ enableHighAccuracy: true, timeout: 10000, maximumAge: 0 });
        const distM = haversineKm(pos.coords.latitude, pos.coords.longitude, barber.lat, barber.lng) * 1000;
        if (distM > 300) {
          setCheckingInId(null);
          const distLabel = distM < 1000 ? `${Math.round(distM)}m` : `${(distM/1000).toFixed(1)}km`;
          setCheckInError(prev => ({ ...prev, [b.id]: `You're ${distLabel} away — get closer to check in` }));
          return;
        }
      } catch {
        setCheckingInId(null);
        setCheckInError(prev => ({ ...prev, [b.id]: "Enable location access to check in" }));
        return;
      }
    }

    setCheckInError(prev => ({ ...prev, [b.id]: null }));
    setCheckingInId(null);
    // Ask if client is actually seated before starting the timer
    setSeatedConfirm({ ...b, _minutesFromAppt: Math.round(minutesFromAppt) });
  };

  const confirmSeated = async () => {
    const b = seatedConfirm;
    if (!b) return;
    setSeatedConfirm(null);
    setCheckingInId(b.id);
    const now = new Date();
    const scheduledAt = new Date(b.scheduled_at);
    const minutesFromAppt = b._minutesFromAppt ?? 0;
    try {
      const nowISO = now.toISOString();
      const rawDelay = Math.round(minutesFromAppt);
      const delayMinutes = Math.min(rawDelay, 30); // cap push at 30 min

      const { error: checkInErr } = await supabase.from("bookings").update({ checked_in_at: nowISO }).eq("id", b.id);
      if (checkInErr) throw checkInErr;
      setCheckedInIds(prev => new Set([...prev, b.id]));
      setLiveAppt({ ...b, checked_in_at: nowISO });

      const apptTime = scheduledAt.toLocaleTimeString([], { hour:"2-digit", minute:"2-digit" });
      const isLate = rawDelay >= 10;

      // Find and shift subsequent bookings if late
      let pushedCount = 0;
      if (isLate && b.barber_id) {
        const dayEnd = new Date(b.scheduled_at);
        dayEnd.setHours(23, 59, 59, 999);

        const { data: nextBookings } = await supabase
          .from("bookings")
          .select("id, scheduled_at, client_name, client_phone, service_type")
          .eq("barber_id", b.barber_id)
          .gt("scheduled_at", b.scheduled_at)
          .lte("scheduled_at", dayEnd.toISOString())
          .in("status", ["confirmed", "accepted", "pending"])
          .order("scheduled_at");

        if (nextBookings && nextBookings.length > 0) {
          pushedCount = nextBookings.length;
          // Push all times in parallel, then SMS each client
          await Promise.all(nextBookings.map(nb => {
            const newTime = new Date(new Date(nb.scheduled_at).getTime() + delayMinutes * 60000);
            return supabase.from("bookings").update({ scheduled_at: newTime.toISOString() }).eq("id", nb.id)
              .then(() => {
                if (nb.client_phone) {
                  const oldTimeStr = new Date(nb.scheduled_at).toLocaleTimeString([], { hour:"2-digit", minute:"2-digit" });
                  const newTimeStr = newTime.toLocaleTimeString([], { hour:"2-digit", minute:"2-digit" });
                  sendSMS(nb.client_phone, `FADED: Heads up — your ${oldTimeStr} appointment has been moved to ${newTimeStr} due to a slight delay. Thanks for your patience.`);
                }
              });
          }));
        }
      }

      // SMS barber — include late/push summary if applicable
      supabase.rpc("get_barber_phone_for_booking", { p_booking_id: b.id })
        .then(({ data: phone }) => {
          if (!phone) return;
          let msg = `FADED: Your ${apptTime} client ${b.client_name || ""} has arrived.`;
          if (isLate) {
            msg += ` ${rawDelay} min late`;
            if (pushedCount > 0) msg += ` — ${pushedCount} following appointment${pushedCount > 1 ? "s" : ""} pushed ${delayMinutes} min`;
            msg += ".";
          }
          sendSMS(phone, msg);
        }).catch(() => {});
    } catch {}
    finally { setCheckingInId(null); }
  };

  const openAddService = async (b) => {
    setAddServiceAppt(b);
    setClientAddonServices([]);
    setClientAddedMsg("");
    const { data } = await supabase.from("barber_services").select("*")
      .eq("barber_id", b.barber_id).eq("is_active", true)
      .order("sort_order", { ascending: true });
    setClientAddonServices(data || []);
  };

  const addClientService = async (addon) => {
    if (!addServiceAppt) return;
    setAddingClientService(true);
    const b = addServiceAppt;
    const newService = b.service_type ? `${b.service_type} + ${addon.name}` : addon.name;
    const currentPrice = parseFloat(String(b.price_agreed || "0").replace("$","")) || 0;
    const newPrice = currentPrice + (addon.price || 0);
    await supabase.from("bookings").update({ service_type: newService, price_agreed: newPrice }).eq("id", b.id);
    setLocalUpdates(prev => ({ ...prev, [b.id]: { service_type: newService, price_agreed: newPrice } }));
    setClientAddedMsg(`${addon.name} added — new total $${newPrice}`);
    setAddingClientService(false);
    setTimeout(() => { setAddServiceAppt(null); setClientAddedMsg(""); setClientAddonServices([]); }, 1800);
  };


  const now = new Date();
  const startOfToday = new Date(); startOfToday.setHours(0,0,0,0);

  const upcoming = bookings.filter(b => {
    if (!b.scheduled_at) return false;
    if (["cancelled","declined","completed"].includes(b.status)) return false;
    return true; // Show all active bookings regardless of date — barber may be running late
  }).sort((a,b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));

  const past = bookings.filter(b =>
    b.status === "completed" || b.status === "cancelled" || b.status === "declined" ||
    (b.scheduled_at && new Date(b.scheduled_at) < startOfToday)
  ).sort((a,b) => new Date(b.scheduled_at) - new Date(a.scheduled_at)).slice(0,20);

  const doCancel = async (id) => {
    setCancelling(true);
    try {
      const bk = bookings.find(b => b.id === id);
      const w = cancelWarning(id);
      if (bk?.saved_payment_method_id && cancelBarberPolicy?.no_show_protection && (w.feePct > 0 || w.feeDollars > 0)) {
        await supabase.functions.invoke("stripe-charge-saved-card", {
          body: { booking_id: id, charge_type: "late_cancel" }
        }).catch(() => {});
      }
      if (onCancel) await onCancel(id);
      setCancelId(null);
    } catch {} finally { setCancelling(false); }
  };

  const openCancelModal = (id) => {
    setCancelId(id);
    const bk = bookings.find(b => b.id === id);
    if (bk?.barber_id) {
      supabase.from("barbers")
        .select("cancellation_window_hours,cancellation_fee_type,cancellation_fee_flat_cents,no_show_protection")
        .eq("id", bk.barber_id).single()
        .then(({ data }) => { if (data) setCancelBarberPolicy(data); });
    }
  };

  const cancelWarning = (id) => {
    const bk = bookings.find(b => b.id === id);
    const bDate = bk?.scheduled_at ? new Date(bk.scheduled_at) : null;
    const minsUntil = bDate ? (bDate - now) / 60000 : 9999;
    const price = bk?.price_agreed || 0;

    const windowHours = cancelBarberPolicy?.cancellation_window_hours ?? 24;
    const feeType = cancelBarberPolicy?.cancellation_fee_type ?? "fifty_percent";
    const feeFlatDollars = (cancelBarberPolicy?.cancellation_fee_flat_cents ?? 2500) / 100;
    const windowMins = windowHours * 60;

    const calcFee = () => {
      if (feeType === "hundred_percent") return price;
      if (feeType === "custom") return feeFlatDollars;
      return price * 0.5;
    };

    const feeAmt = calcFee();
    const feePctLabel = feeType === "fifty_percent" ? 50 : feeType === "hundred_percent" ? 100 : null;
    const feeLabel = feePctLabel ? `${feePctLabel}%` : `$${feeAmt.toFixed(2)}`;

    if (minsUntil <= 0) {
      return { emoji:"⚠️", title:"No-show fee applies", body:`This appointment has already passed. A ${feeLabel} cancellation fee ($${feeAmt.toFixed(2)}) applies.`, color:"#EF4444", feePct:feePctLabel||0, feeDollars:feeAmt };
    }
    if (minsUntil <= windowMins) {
      return { emoji: minsUntil <= 30 ? "🚨" : "⚠️", title:`${feeLabel} cancellation fee`, body:`Within ${windowHours}h of your appointment — a $${feeAmt.toFixed(2)} cancellation fee applies.`, color: minsUntil <= 30 ? "#EF4444" : "#F59E0B", feePct:feePctLabel||0, feeDollars:feeAmt };
    }
    return { emoji:"🗓️", title:"Cancel booking?", body:`Cancelling more than ${windowHours} hours ahead is free. Your barber will be notified.`, color:t.t1, feePct:0, feeDollars:0 };
  };

  const refCode = (b) => {
    const n = b.barber_name?.split(" ").map(w=>w[0]).join("").toUpperCase().slice(0,2) || "XX";
    const num = String(b.id || "").slice(-4).padStart(4,"0");
    return `FD-${num}${n}`;
  };

  const doRefresh = async () => {
    if (refreshing || !onRefresh) return;
    setRefreshing(true);
    await onRefresh();
    setRefreshing(false);
  };

  return (
    <div style={{ paddingBottom:"110px", minHeight:"var(--app-height,100dvh)", maxWidth:isMd?800:"none", margin:isMd?"0 auto":"0", padding:isMd?"0 32px 110px":"0 0 110px" }}>

      {/* Live appointment screen */}
      {liveAppt && (
        <LiveApptScreen
          appt={liveAppt}
          onAddService={openAddService}
          onClose={()=>{ const _cid = liveAppt?.id; dismissedLiveRef.current = _cid; try { if (_cid) sessionStorage.setItem("faded_client_live_dismissed", _cid); } catch {} setLiveAppt(null); }}
          localUpdates={localUpdates}
        />
      )}

      {/* Cancel warning modal */}
      {cancelId && (() => {
        const w = cancelWarning(cancelId);
        const closeCancel = () => { setCancelId(null); setCancelBarberPolicy(null); };
        return (
          <div onClick={closeCancel} style={{ position:"fixed", inset:0, zIndex:400, background:"rgba(0,0,0,0.7)", backdropFilter:"blur(8px)", display:"flex", alignItems:isMd?"center":"flex-end", justifyContent:"center", padding:isMd?0:"0 0 20px" }}>
            <div onClick={e=>e.stopPropagation()} style={{ background:t.card, borderRadius:24, padding:24, width:"100%", maxWidth:400, border:`1.5px solid rgba(220,38,38,0.3)`, margin:"0 16px" }}>
              <div style={{ fontSize:32, textAlign:"center", marginBottom:10 }}>{w.emoji}</div>
              <div style={{ fontSize:17, fontWeight:800, color:w.color, fontFamily:"'DM Sans',sans-serif", textAlign:"center", marginBottom:8 }}>{w.title}</div>
              <div style={{ fontSize:13, color:t.t3, textAlign:"center", lineHeight:1.6, marginBottom:w.feePct>0||w.feeDollars>0?12:24 }}>{w.body}</div>
              {(w.feePct > 0 || w.feeDollars > 0) && (
                <div style={{ background:"rgba(220,38,38,0.06)", border:"1px solid rgba(220,38,38,0.2)", borderRadius:12, padding:"12px 16px", marginBottom:20, textAlign:"center" }}>
                  <div style={{ fontSize:22, fontWeight:900, color:"#EF4444", fontFamily:"'Bebas Neue',sans-serif", letterSpacing:"0.04em" }}>${Number(w.feeDollars).toFixed(2)} cancellation fee</div>
                  <div style={{ fontSize:11, color:"rgba(245,245,240,0.45)", marginTop:3 }}>
                    {w.feePct > 0 ? `Charged per barber policy · ${w.feePct}% of booking price` : "Charged per barber cancellation policy"}
                  </div>
                </div>
              )}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                <button onClick={closeCancel} style={{ padding:"13px", background:t.section, border:`1px solid ${t.border}`, borderRadius:14, color:t.t1, fontSize:14, fontWeight:800, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>Keep it</button>
                <button onClick={()=>doCancel(cancelId)} disabled={cancelling} style={{ padding:"13px", background:"rgba(220,38,38,0.12)", border:"1.5px solid rgba(220,38,38,0.4)", borderRadius:14, color:"#EF4444", fontSize:14, fontWeight:800, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", opacity:cancelling?0.6:1 }}>
                  {cancelling?"Cancelling…":(w.feePct>0||w.feeDollars>0)?"Accept fee & cancel":"Yes, cancel"}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Seated confirmation modal — fires after GPS passes, before timer starts */}
      {seatedConfirm && (
        <div className="faded-sheet-wrap" style={{ position:"fixed", inset:0, zIndex:410, background:"rgba(0,0,0,0.8)", backdropFilter:"blur(10px)", display:"flex", alignItems:"flex-end", justifyContent:"center", padding:"0 0 24px" }}>
          <div style={{ background:"#111", borderRadius:24, padding:24, width:"100%", maxWidth:400, border:"1.5px solid rgba(34,197,94,0.3)", margin:"0 16px" }}>
            <div style={{ fontSize:36, textAlign:"center", marginBottom:12 }}>💈</div>
            <div style={{ fontSize:18, fontWeight:800, color:"#fff", textAlign:"center", marginBottom:8, fontFamily:"'DM Sans',sans-serif" }}>Are you seated?</div>
            <div style={{ fontSize:13, color:"#aaa", textAlign:"center", marginBottom:24, lineHeight:1.6 }}>
              The timer starts as soon as you confirm. Tap <strong style={{ color:"#fff" }}>I'm in the chair</strong> when your barber begins your cut — not when you arrive at the shop.
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
              <button onClick={() => setSeatedConfirm(null)} style={{ padding:13, background:"#1a1a1a", border:"1px solid #333", borderRadius:14, color:"#aaa", fontSize:14, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>Not yet</button>
              <button onClick={confirmSeated} style={{ padding:13, background:"rgba(34,197,94,0.15)", border:"1.5px solid rgba(34,197,94,0.5)", borderRadius:14, color:"#22C55E", fontSize:14, fontWeight:800, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>✂️ I'm in the chair</button>
            </div>
          </div>
        </div>
      )}

      {/* Boarding pass full-screen view */}
      {viewPassId && (() => {
        const b = bookings.find(bk=>bk.id===viewPassId);
        if (!b) return null;
        const bDate = new Date(b.scheduled_at);
        return (
          <div onClick={()=>setViewPassId(null)} style={{ position:"fixed", inset:0, zIndex:400, background:"rgba(0,0,0,0.85)", backdropFilter:"blur(12px)", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:20 }}>
            <div onClick={e=>e.stopPropagation()} style={{ width:"100%", maxWidth:360 }}>
              {/* Pass card */}
              <div style={{ borderRadius:20, overflow:"hidden", boxShadow:"0 20px 60px rgba(0,0,0,0.8)" }}>
                <div style={{ background:"linear-gradient(135deg,#1a3a6b,#2563EB)", padding:"16px 20px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <div style={{ fontFamily:"'Syne',sans-serif", fontSize:18, fontWeight:900, letterSpacing:2, color:"#fff" }}><span style={{ color:"rgba(255,255,255,0.45)" }}>FAD</span>ED.</div>
                  <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:11, color:"rgba(255,255,255,0.6)", letterSpacing:"0.15em" }}>BOARDING PASS</div>
                </div>
                <div style={{ background:"#F8F8F6", padding:"20px 20px 0" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:16 }}>
                    <div style={{ width:48, height:48, borderRadius:"50%", background:"linear-gradient(135deg,#1e3a5f,#2563EB)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, fontWeight:900, color:"#fff", flexShrink:0 }}>{(b.barber_name||"B")[0]}</div>
                    <div>
                      <div style={{ fontSize:11, fontWeight:700, color:"#888", letterSpacing:"0.1em", marginBottom:2 }}>YOUR BARBER</div>
                      <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:22, color:"#111", letterSpacing:"0.04em", lineHeight:1 }}>{(b.barber_name||"Barber").toUpperCase()}</div>
                    </div>
                    <div style={{ marginLeft:"auto", textAlign:"right" }}>
                      <div style={{ fontSize:11, fontWeight:700, color:"#888", letterSpacing:"0.1em", marginBottom:2 }}>CLIENT</div>
                      <div style={{ fontSize:13, fontWeight:800, color:"#111" }}>{b.client_name||"You"}</div>
                    </div>
                  </div>
                  <div style={{ borderTop:"1.5px dashed #ccc", margin:"0 -20px", padding:"16px 20px" }}>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:14 }}>
                      {[["SERVICE",b.service_type||"Haircut"],["PRICE",fmt(b.price_agreed)||"—"],["DATE",bDate.toLocaleDateString("en-CA",{weekday:"short",month:"short",day:"numeric"}).toUpperCase()],["TIME",bDate.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})],["PAYMENT",b.payment_method||"In person"],["CODE",refCode(b)]].map(([label,val])=>(
                        <div key={label}>
                          <div style={{ fontSize:11, fontWeight:700, color:"#999", letterSpacing:"0.15em", marginBottom:3 }}>{label}</div>
                          <div style={{ fontFamily:["TIME","PRICE","CODE"].includes(label)?"'JetBrains Mono',monospace":"'DM Sans',sans-serif", fontSize:label==="TIME"?16:13, fontWeight:800, color:"#111" }}>{val}</div>
                        </div>
                      ))}
                    </div>
                    {/* Barcode */}
                    <div style={{ textAlign:"center", padding:"12px 0 0", borderTop:"1px solid #e8e8e8" }}>
                      <div style={{ display:"flex", justifyContent:"center", gap:1, height:36, marginBottom:6 }}>
                        {Array.from({length:48}).map((_,i)=><div key={i} style={{ width:Math.random()>0.7?3:1.5, background:"#111", height:`${50+Math.random()*50}%`, alignSelf:"center", borderRadius:1 }}/>)}
                      </div>
                      <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:11, color:"#999", letterSpacing:"0.12em" }}>FADED · {refCode(b)}</div>
                    </div>
                  </div>
                </div>
              </div>
              <button onClick={()=>setViewPassId(null)} style={{ width:"100%", marginTop:16, padding:"14px", background:"#2563EB", border:"none", borderRadius:14, color:"#fff", fontSize:14, fontWeight:800, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>Done</button>
            </div>
          </div>
        );
      })()}

      {/* Add Service bottom sheet */}
      {addServiceAppt && (
        <div onClick={()=>{ setAddServiceAppt(null); setClientAddedMsg(""); setClientAddonServices([]); }} className="faded-sheet-wrap" style={{ position:"fixed", inset:0, zIndex:400, background:"rgba(0,0,0,0.6)", backdropFilter:"blur(6px)", display:"flex", alignItems:"flex-end", justifyContent:"center" }}>
          <div onClick={e=>e.stopPropagation()} style={{ background:t.card, borderRadius:"24px 24px 0 0", width:"100%", maxWidth:480, maxHeight:"75vh", overflowY:"auto", padding:"20px 20px 48px" }}>
            <div style={{ display:"flex", justifyContent:"center", marginBottom:16 }}>
              <div style={{ width:40, height:4, background:t.surface2||t.border, borderRadius:2 }}/>
            </div>
            <div style={{ fontSize:17, fontWeight:800, color:t.t1, marginBottom:4 }}>Add a Service</div>
            <div style={{ fontSize:12, color:t.t4, marginBottom:6 }}>Currently: {(localUpdates[addServiceAppt.id]?.service_type || addServiceAppt.service_type) || "—"}</div>
            <div style={{ fontSize:12, fontWeight:700, color:"#059669", marginBottom:16 }}>
              Current total: ${localUpdates[addServiceAppt.id]?.price_agreed ?? addServiceAppt.price_agreed ?? "0"}
            </div>
            {clientAddedMsg ? (
              <div style={{ padding:"16px", background:"rgba(34,197,94,0.1)", border:"1px solid rgba(34,197,94,0.3)", borderRadius:14, textAlign:"center" }}>
                <div style={{ fontSize:20, marginBottom:6 }}>✅</div>
                <div style={{ fontSize:14, fontWeight:700, color:"#16A34A" }}>{clientAddedMsg}</div>
              </div>
            ) : clientAddonServices.length === 0 ? (
              <div style={{ padding:"24px", textAlign:"center", color:t.t4, fontSize:13 }}>Loading services…</div>
            ) : (
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                {clientAddonServices.map(addon => (
                  <button key={addon.id} onClick={() => addClientService(addon)}
                    disabled={addingClientService}
                    style={{ padding:"14px 16px", borderRadius:14, border:`1.5px solid ${t.border}`, background:t.section, cursor:"pointer", textAlign:"left", display:"flex", justifyContent:"space-between", alignItems:"center", opacity:addingClientService?0.6:1 }}>
                    <div>
                      <div style={{ fontSize:14, fontWeight:700, color:t.t1 }}>{addon.name}</div>
                      {addon.duration_minutes && <div style={{ fontSize:11, color:t.t4, marginTop:2 }}>{addon.duration_minutes} min</div>}
                    </div>
                    <div style={{ fontSize:15, fontWeight:800, color:"#2563EB" }}>${addon.price || 0}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sticky header — no backdropFilter here: position:sticky + blur causes iOS scroll stutter.
          compactTop skips the safe-area top padding when something (e.g. the "Browsing as
          client" banner) already renders above this header, so the two don't stack.
          No transform:translateZ(0) here — that GPU-layer hack (leftover from when this
          header still had backdropFilter blur) makes WebKit render the heading-gradient
          text washed out, since forcing a compositing layer on an ancestor of
          -webkit-background-clip:text content is a known WebKit rendering quirk. */}
      <div style={{ position:"sticky", top:0, zIndex:100, background:t.bg, borderBottom:`1px solid ${t.border}`, paddingTop:compactTop ? 12 : "max(44px, calc(var(--sat, 0px) + 2px))", paddingBottom:10, paddingLeft:16, paddingRight:16 }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
          <div>
            <div style={{ fontSize:26, fontFamily:"'Bebas Neue',sans-serif", letterSpacing:"0.06em", lineHeight:1, color:t.t1 }}>BOOKINGS</div>
            <div style={{ fontSize:11, color:t.t4, marginTop:2, fontFamily:"'Syne',sans-serif", fontWeight:700, letterSpacing:"0.04em" }}>FAD<span style={{color:"#2563EB"}}>ED</span></div>
          </div>
          <button onClick={doRefresh} disabled={refreshing} style={{ background:"none", border:"none", padding:"6px 8px", cursor:"pointer", color:t.t3, display:"flex", alignItems:"center", gap:5, fontSize:12, fontWeight:600, fontFamily:"'DM Sans',sans-serif", opacity:refreshing?0.5:1 }}>
            <div style={{ width:14, height:14, border:`2px solid ${refreshing?"#2563EB":t.t3}`, borderTopColor:"#2563EB", borderRadius:"50%", animation:refreshing?"fadedSpin 0.7s linear infinite":"none", flexShrink:0 }}/>
            {refreshing ? "Updating…" : "Refresh"}
          </button>
        </div>
        <div style={{ display:"flex", gap:6, background:t.section, borderRadius:100, padding:4 }}>
          {["upcoming","past"].map(v=>(
            <button key={v} onClick={()=>setView(v)} style={{ flex:1, padding:"7px 0", borderRadius:100, border:"none", background:view===v?"#2563EB":"transparent", color:view===v?"#fff":t.t3, fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", transition:"all 0.15s" }}>
              {v==="upcoming"?"Upcoming":"Past"}
            </button>
          ))}
        </div>
      </div>

      <div style={{ padding:"16px 16px 0" }}>
        {view === "upcoming" && (<>
          {upcoming.length === 0 ? (
            <div style={{ padding:"52px 0", textAlign:"center" }}>
              <div style={{ fontSize:48, marginBottom:14 }}>💈</div>
              <div style={{ fontSize:16, fontWeight:800, color:t.t1, fontFamily:"'DM Sans',sans-serif", marginBottom:6 }}>No upcoming cuts</div>
              <div style={{ fontSize:13, color:t.t3 }}>Head to Discover and book your next one</div>
            </div>
          ) : upcoming.map((b,i) => {
            const bDate = new Date(b.scheduled_at);
            const isToday = bDate.toDateString() === now.toDateString();
            const daysUntil = Math.ceil((bDate - now) / 86400000);
            const isConfirmed = b.status === "confirmed" || b.status === "accepted";
            const isPending = b.status === "pending";
            return (
              <div key={b.id} style={{ marginBottom:10 }}>
                {/* NEXT UP label */}
                <div style={{ fontSize:11, fontWeight:800, color:t.t4, letterSpacing:"0.14em", marginBottom:8, fontFamily:"'DM Sans',sans-serif" }}>
                  {i===0 ? (isToday?"TODAY":"NEXT UP · IN "+daysUntil+" DAY"+(daysUntil!==1?"S":"")) : bDate.toLocaleDateString("en-CA",{weekday:"long",month:"long",day:"numeric"}).toUpperCase()}
                </div>

                {/* White boarding pass card */}
                <div style={{ borderRadius:18, overflow:"hidden", boxShadow:"0 4px 24px rgba(0,0,0,0.35)" }}>
                  {/* Status header */}
                  <div style={{ background: isConfirmed?"#22C55E":isPending?"#F59E0B":"#2563EB", padding:"10px 16px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:7 }}>
                      <div style={{ width:7, height:7, borderRadius:"50%", background:"rgba(255,255,255,0.8)" }}/>
                      <span style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:11, fontWeight:700, color:"#fff", letterSpacing:"0.15em" }}>{b.status?.toUpperCase()}</span>
                    </div>
                    <span style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:11, fontWeight:700, color:"rgba(255,255,255,0.8)", letterSpacing:"0.12em" }}>{refCode(b)}</span>
                  </div>

                  {/* White card body */}
                  <div style={{ background:"#F8F8F6", padding:"16px 16px 0" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:14 }}>
                      {/* Date block */}
                      <div style={{ textAlign:"center", flexShrink:0 }}>
                        <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:13, color:"#555", letterSpacing:"0.1em", lineHeight:1 }}>{bDate.toLocaleDateString("en-CA",{weekday:"short"}).toUpperCase()}</div>
                        <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:44, color:"#111", lineHeight:1, letterSpacing:"-0.02em" }}>{bDate.getDate()}</div>
                        <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:13, color:"#555", letterSpacing:"0.1em", lineHeight:1 }}>{bDate.toLocaleDateString("en-CA",{month:"short"}).toUpperCase()}</div>
                      </div>
                      {/* Divider */}
                      <div style={{ width:1, height:60, background:"#ddd" }}/>
                      {/* Barber info */}
                      <div style={{ flex:1 }}>
                        <div style={{ width:38, height:38, borderRadius:"50%", background:"linear-gradient(135deg,#1e3a5f,#2563EB)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:15, fontWeight:900, color:"#fff", marginBottom:6 }}>{(b.barber_name||"B")[0]}</div>
                        <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:18, color:"#111", letterSpacing:"0.03em", lineHeight:1 }}>{(b.barber_name||"Barber").toUpperCase()}</div>
                        <div style={{ fontSize:11, color:"#888", marginTop:1 }}>{b.barber_handle ? `@${b.barber_handle}` : ""}</div>
                      </div>
                    </div>

                    {/* SERVICE | TIME | PRICE */}
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, padding:"12px 0", borderTop:"1px dashed #ddd" }}>
                      {[["SERVICE",(localUpdates[b.id]?.service_type||b.service_type)||"Haircut"],["TIME",bDate.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})],["PRICE",fmt(localUpdates[b.id]?.price_agreed??b.price_agreed)||"—"]].map(([label,val])=>(
                        <div key={label}>
                          <div style={{ fontSize:11, fontWeight:700, color:"#999", letterSpacing:"0.15em", marginBottom:3 }}>{label}</div>
                          <div style={{ fontFamily:label!=="SERVICE"?"'JetBrains Mono',monospace":"'DM Sans',sans-serif", fontSize:label==="SERVICE"?12:14, fontWeight:800, color: label==="PRICE"?"#2563EB":"#111" }}>{val}</div>
                        </div>
                      ))}
                    </div>

                    {/* I'm Here check-in button — confirmed today bookings */}
                    {isConfirmed && isToday && (() => {
                      const alreadyCheckedIn = checkedInIds.has(b.id) || b.checked_in_at;
                      return (
                        <div style={{ padding:"10px 0 4px", borderTop:"1px dashed #ddd" }}>
                          {alreadyCheckedIn ? (
                            <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:6, padding:"10px", background:"rgba(34,197,94,0.08)", borderRadius:10 }}>
                              <span style={{ fontSize:14 }}>✅</span>
                              <span style={{ fontSize:12, fontWeight:700, color:"#16A34A", fontFamily:"'DM Sans',sans-serif" }}>Checked In — your barber knows you're here</span>
                            </div>
                          ) : (
                            <>
                              <button onClick={() => handleCheckIn(b)} disabled={checkingInId === b.id}
                                style={{ width:"100%", padding:"12px", background:"#16A34A", border:"none", borderRadius:10, color:"#fff", fontSize:13, fontWeight:800, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", opacity:checkingInId===b.id?0.7:1 }}>
                                {checkingInId===b.id ? "Checking in..." : "📍 I'm Here"}
                              </button>
                              {checkInError[b.id] && (
                                <div style={{ marginTop:7, display:"flex", alignItems:"center", gap:6, padding:"8px 12px", background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.2)", borderRadius:8 }}>
                                  <span style={{ fontSize:13 }}>🔒</span>
                                  <span style={{ fontSize:12, fontWeight:600, color:"#EF4444", fontFamily:"'DM Sans',sans-serif" }}>{checkInError[b.id]}</span>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      );
                    })()}

                    {/* Add Service — confirmed bookings */}
                    {isConfirmed && (
                      <div style={{ padding:"8px 0 4px", borderTop:"1px dashed #ddd" }}>
                        <button onClick={()=>openAddService({ ...b, service_type: localUpdates[b.id]?.service_type || b.service_type, price_agreed: localUpdates[b.id]?.price_agreed ?? b.price_agreed })}
                          style={{ width:"100%", padding:"10px", background:"rgba(37,99,235,0.07)", border:"1.5px solid rgba(37,99,235,0.2)", borderRadius:10, color:"#2563EB", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>
                          ＋ Add a Service
                        </button>
                      </div>
                    )}

                    {/* Actions */}
                    <div style={{ display:"flex", borderTop:"1px solid #e8e8e8" }}>
                      <button onClick={()=>setViewPassId(b.id)} style={{ flex:1, padding:"10px 4px", background:"transparent", border:"none", borderRight:"1px solid #e8e8e8", color:"#2563EB", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", textAlign:"center" }}>View Pass</button>
                      <button onClick={()=>{ if(onRebook) onRebook(b); }} style={{ flex:1, padding:"10px 4px", background:"transparent", border:"none", borderRight:"1px solid #e8e8e8", color:"#111", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", textAlign:"center" }}>Reschedule</button>
                      <button onClick={()=>openCancelModal(b.id)} style={{ flex:1, padding:"10px 4px", background:"transparent", border:"none", color:"#EF4444", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", textAlign:"center" }}>Cancel</button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </>)}

        {/* Book next cut CTA — shown when client has upcoming bookings */}
        {view === "upcoming" && upcoming.length > 0 && (
          <div style={{ margin:"20px 0 4px", padding:"16px 18px", background:"linear-gradient(135deg,rgba(37,99,235,0.1),rgba(37,99,235,0.04))", border:"1px solid rgba(37,99,235,0.2)", borderRadius:18, display:"flex", alignItems:"center", gap:14 }}>
            <div style={{ width:40, height:40, borderRadius:12, background:"rgba(37,99,235,0.15)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, flexShrink:0 }}>💈</div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:13, fontWeight:800, color:t.t1 }}>Want another cut?</div>
              <div style={{ fontSize:12, color:t.t3, marginTop:2 }}>Find a barber near you right now</div>
            </div>
            <button onClick={()=>{ if(onDiscover) onDiscover(); }} style={{ flexShrink:0, padding:"9px 16px", background:"#2563EB", border:"none", borderRadius:12, color:"#fff", fontSize:12, fontWeight:800, cursor:"pointer", whiteSpace:"nowrap" }}>
              Find →
            </button>
          </div>
        )}

        {view === "past" && (<>
          {past.length === 0 ? (
            <div style={{ padding:"52px 0", textAlign:"center" }}>
              <div style={{ fontSize:40, marginBottom:12 }}>✂️</div>
              <div style={{ fontSize:15, fontWeight:800, color:t.t1, fontFamily:"'DM Sans',sans-serif", marginBottom:6 }}>No past cuts yet</div>
              <div style={{ fontSize:13, color:t.t3 }}>Completed and cancelled bookings show up here.</div>
            </div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              {past.map((b) => {
                const bDate = new Date(b.scheduled_at);
                const isCompleted = b.status === "completed";
                const isCancelled = b.status === "cancelled" || b.status === "declined";
                const statusColor = isCompleted ? "#16A34A" : "#EF4444";
                const statusBg    = isCompleted ? "rgba(34,197,94,0.1)" : "rgba(220,38,38,0.08)";
                const statusLabel = isCompleted ? "Completed" : isCancelled ? "Cancelled" : b.status;
                return (
                  <div key={b.id} style={{ background:t.card, borderRadius:16, padding:"14px 16px", border:`1px solid ${t.border}`, display:"flex", alignItems:"center", gap:14 }}>
                    <div style={{ width:44, height:44, borderRadius:14, background:statusBg, display:"flex", alignItems:"center", justifyContent:"center", fontSize:17, fontWeight:900, color:statusColor, flexShrink:0 }}>{(b.barber_name||"B")[0]}</div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:14, fontWeight:700, color:t.t1, fontFamily:"'DM Sans',sans-serif", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{b.barber_name||"Barber"}</div>
                      <div style={{ fontSize:12, color:t.t3, marginTop:2, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{b.service_type||"Haircut"}</div>
                      <div style={{ fontSize:11, color:t.t4, marginTop:3 }}>{bDate.toLocaleDateString("en-CA",{weekday:"short",month:"short",day:"numeric"})} · {bDate.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})}</div>
                    </div>
                    <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:6, flexShrink:0 }}>
                      <div style={{ fontSize:11, fontWeight:700, color:statusColor, background:statusBg, padding:"3px 10px", borderRadius:20, letterSpacing:"0.04em" }}>{statusLabel}</div>
                      {b.price_agreed > 0 && <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:12, fontWeight:700, color:t.t3 }}>{fmt(b.price_agreed)}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>)}
      </div>
    </div>
  );
}

// ─── CLIENT PROFILE ───────────────────────────────────────────────────────────

function DeleteBarberAccountSection({ session, onSignOut }) {
  const [showDel, setShowDel] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const deleteBarberAccount = async () => {
    setDeleting(true);
    try {
      const uid = session?.user?.id;
      // Block deletion if active future bookings exist — clients must not be silently abandoned
      const { data: canDelData } = await supabase.rpc("can_delete_account", { p_user_id: uid });
      if (canDelData && !canDelData.can_delete) {
        const n = canDelData.active_bookings || 1;
        alert(`You have ${n} upcoming client booking${n > 1 ? "s" : ""}. Cancel ${n > 1 ? "them" : "it"} and notify clients before deleting your account.`);
        setDeleting(false);
        return;
      }
      await supabase.from("bookings").update({ barber_name:"Deleted Barber", barber_avatar:null }).eq("barber_id",uid).in("status",["completed","cancelled","declined"]);
      await supabase.from("bookings").update({ status:"cancelled", cancelled_at:new Date().toISOString(), decline_reason:"Barber account closed", barber_name:"Deleted Barber", barber_avatar:null }).eq("barber_id",uid).in("status",["pending","accepted","confirmed"]);
      await supabase.from("reviews").delete().eq("barber_id",uid);
      await supabase.from("barber_services").delete().eq("barber_id",uid);
      await supabase.from("shop_members").delete().eq("barber_id",uid);
      await supabase.from("manual_appointments").delete().eq("barber_id",uid);
      await supabase.from("push_subscriptions").delete().or(`barber_id.eq.${uid},user_id.eq.${uid}`);
      await supabase.from("push_tokens").delete().eq("user_id",uid);
      await supabase.from("notification_failures").delete().eq("user_id",uid);
      await supabase.from("barbers").delete().eq("id",uid);
      await supabase.from("profiles").delete().eq("id",uid);
      await supabase.functions.invoke("delete-account").catch(()=>{});
      await supabase.auth.signOut();
      onSignOut();
    } catch(e) { console.error("[FADED] deleteBarber:",e); setDeleting(false); }
  };
  if (showDel) return (
    <div style={{ background:"rgba(220,38,38,0.06)", border:"1.5px solid rgba(220,38,38,0.25)", borderRadius:16, padding:16 }}>
      <div style={{ fontSize:13, fontWeight:700, color:"#EF4444", marginBottom:6 }}>Delete your barber account?</div>
      <div style={{ fontSize:12, color:"rgba(245,245,240,0.55)", marginBottom:14, lineHeight:1.5 }}>This permanently deletes your profile, services, bookings, and all associated data. This cannot be undone.</div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
        <button onClick={()=>setShowDel(false)} style={{ padding:"11px", background:"transparent", border:"1px solid rgba(245,245,240,0.2)", borderRadius:12, color:"rgba(245,245,240,0.7)", fontSize:13, fontWeight:700, cursor:"pointer" }}>Cancel</button>
        <button onClick={deleteBarberAccount} disabled={deleting} style={{ padding:"11px", background:"rgba(220,38,38,0.15)", border:"1px solid rgba(220,38,38,0.4)", borderRadius:12, color:"#EF4444", fontSize:13, fontWeight:700, cursor:"pointer", opacity:deleting?0.6:1 }}>{deleting?"Deleting…":"Yes, Delete"}</button>
      </div>
    </div>
  );
  return <button onClick={()=>setShowDel(true)} style={{ width:"100%", padding:"11px", background:"transparent", border:"none", color:"rgba(220,38,38,0.5)", fontSize:12, cursor:"pointer", textDecoration:"underline" }}>Delete my account</button>;
}

function BarberSettingsTab({ session, profile, setProfile, onSignOut, onGiftCard, onBookABarber }) {
  const { t } = useT();
  const { isDesktop } = useR();
  const [editMode, setEditMode] = useState(false);
  const [name, setName] = useState(profile?.full_name || "");
  const [phone, setPhone] = useState(profile?.phone || "");
  const [saving, setSaving] = useState(false);
  const [stripeStatus, setStripeStatus] = useState(null); // null=loading, object=loaded
  const [stripeConnecting, setStripeConnecting] = useState(false);
  const [nsp, setNsp] = useState(null); // no-show protection settings (null=loading)
  const [nspSaving, setNspSaving] = useState(false);
  const [avatar, setAvatar] = useState(profile?.avatar_url || null);
  const [isShopEmployee, setIsShopEmployee] = useState(false);
  const avatarFileRef = useRef();

  useEffect(() => {
    if (!session?.user?.id) return;
    // Sequenced rather than fired via 3 simultaneous un-awaited .then() calls --
    // this component can mount in the exact same tick as the top-level post-login
    // load sequence (e.g. a barber lands here immediately if they'd already
    // selected the ME/profile tab while still browsing as a guest), and that kind
    // of concurrent native-bridge burst is what triggered a real Sentry-captured
    // WebView SIGSEGV. Same fix pattern as the App-level loadProfile sequencing.
    let cancelled = false;
    (async () => {
      const { data } = await supabase.rpc("get_my_barber_profile");
      if (cancelled) return;
      setStripeStatus(data ? { stripe_account_id: data.stripe_account_id, stripe_charges_enabled: data.stripe_charges_enabled, stripe_onboarding_complete: data.stripe_onboarding_complete } : {});
      setNsp(data ? {
        enabled: data.no_show_protection || false,
        cancelFeeType: data.cancellation_fee_type || "fifty_percent",
        cancelFlatCents: data.cancellation_fee_flat_cents ?? 2500,
        cancelWindowHours: data.cancellation_window_hours ?? 24,
        noShowFeeType: data.no_show_fee_type || "hundred_percent",
        noShowFlatCents: data.no_show_fee_flat_cents ?? 2500,
      } : { enabled: false, cancelFeeType: "fifty_percent", cancelFlatCents: 2500, cancelWindowHours: 24, noShowFeeType: "hundred_percent", noShowFlatCents: 2500 });

      const { data: memData } = await supabase.from("shop_members").select("employment_type, invite_status, shops(payment_model)").eq("barber_id", session.user.id).eq("invite_status", "active").limit(1);
      if (cancelled) return;
      const mem = memData?.[0];
      if (mem?.employment_type === "employee" && mem?.shops?.payment_model === "shop_collects") setIsShopEmployee(true);

      // Fetch latest profile values on mount so tab-switching doesn't show stale data
      const { data: profData } = await supabase.from("profiles").select("full_name,phone").eq("id", session.user.id).single();
      if (cancelled) return;
      if (profData) { setName(profData.full_name || ""); setPhone(profData.phone || ""); }
    })();
    return () => { cancelled = true; };
  }, [session?.user?.id]);

  const saveNsp = async (updates) => {
    const next = { ...nsp, ...updates };
    setNsp(next);
    setNspSaving(true);
    await supabase.from("barbers").update({
      no_show_protection: next.enabled,
      cancellation_fee_type: next.cancelFeeType,
      cancellation_fee_flat_cents: next.cancelFlatCents,
      cancellation_window_hours: next.cancelWindowHours,
      no_show_fee_type: next.noShowFeeType,
      no_show_fee_flat_cents: next.noShowFlatCents,
    }).eq("id", session.user.id);
    setNspSaving(false);
  };

  const handleStripeConnect = async () => {
    setStripeConnecting(true);
    try {
      const { data, error } = await supabase.functions.invoke("stripe-connect-onboard", {});
      setStripeConnecting(false);
      if (error || !data?.url) {
        const msg = error?.message || "Could not start Stripe setup — try again.";
        window.dispatchEvent(new CustomEvent("faded-toast", { detail: { msg, type: "error" } }));
        return;
      }
      window.location.href = data.url;
    } catch (err) {
      setStripeConnecting(false);
      window.dispatchEvent(new CustomEvent("faded-toast", { detail: { msg: "Network error — check your connection and try again.", type: "error" } }));
    }
  };

  const saveProfile = async () => {
    setSaving(true);
    const { data: { session: freshSession } } = await supabase.auth.getSession();
    if (!freshSession) {
      setSaving(false);
      window.dispatchEvent(new CustomEvent("faded-toast", { detail: { msg: "Session expired — please sign out and sign in again.", type: "error" } }));
      return;
    }
    const updates = { full_name:sanitize(name,100), phone:sanitize(phone,30) };
    const { error } = await supabase.from("profiles").update(updates).eq("id", freshSession.user.id);
    if (error) {
      setSaving(false);
      window.dispatchEvent(new CustomEvent("faded-toast", { detail: { msg: "Could not save — please try again.", type: "error" } }));
      return;
    }
    if (setProfile) setProfile(prev => ({ ...prev, ...updates }));
    setSaving(false);
    setEditMode(false);
  };

  const uploadBarberAvatar = async (file) => {
    if (!file || file.size > 5*1024*1024) return;
    const path = `${session.user.id}/${Date.now()}.${file.name.split(".").pop()}`;
    const { error } = await supabase.storage.from("avatars").upload(path, file, { upsert:true });
    if (!error) {
      const { data } = supabase.storage.from("avatars").getPublicUrl(path);
      setAvatar(data.publicUrl);
      await Promise.all([
        supabase.from("profiles").update({ avatar_url:data.publicUrl }).eq("id", session.user.id),
        supabase.from("barbers").update({ avatar_url:data.publicUrl }).eq("id", session.user.id),
      ]);
    }
  };

  const rows = [
    { label:"Email", value:session?.user?.email||"", icon:"✉️" },
    { label:"Phone", value:phone||"Not set", icon:"📱" },
    { label:"Name", value:name||"Not set", icon:"👤" },
  ];

  return (
    <div style={{ padding:isDesktop?"32px 40px 100px":"24px 16px 100px", maxWidth:isDesktop?720:"none", margin:isDesktop?"0 auto":"0" }}>
      <input ref={avatarFileRef} type="file" accept="image/*" style={{ display:"none" }} onChange={e=>uploadBarberAvatar(e.target.files[0])}/>
      {/* Account card */}
      <div style={{ background:t.card, borderRadius:20, padding:20, border:`1.5px solid ${t.border}`, marginBottom:14 }}>
        {/* Profile photo */}
        <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:18, paddingBottom:16, borderBottom:`1px solid ${t.border}` }}>
          <div onClick={()=>avatarFileRef.current?.click()} style={{ width:64, height:64, borderRadius:"50%", overflow:"hidden", flexShrink:0, cursor:"pointer", border:`2px solid ${avatar?"rgba(245,245,240,0.15)":"#2563EB"}`, position:"relative", background:"rgba(37,99,235,0.1)" }}>
            {avatar
              ? <img src={avatar} alt="profile" style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
              : <div style={{ width:"100%", height:"100%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, fontWeight:800, color:"#2563EB" }}>{(name||"B")[0].toUpperCase()}</div>}
            <div style={{ position:"absolute", bottom:0, right:0, width:20, height:20, borderRadius:"50%", background:"#2563EB", display:"flex", alignItems:"center", justifyContent:"center" }}>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
            </div>
          </div>
          <div>
            <div style={{ fontSize:13, fontWeight:800, color:t.t1, marginBottom:2 }}>Profile Photo</div>
            <div style={{ fontSize:11, color:t.t4, lineHeight:1.5 }}>Tap to change · shown to clients</div>
          </div>
        </div>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
          <div style={{ fontSize:13, fontWeight:800, color:t.t1, fontFamily:"'DM Sans',sans-serif" }}>Account</div>
          <button onClick={()=>setEditMode(!editMode)} style={{ fontSize:12, color:t.blueText, fontWeight:700, background:t.blueBg, border:"none", padding:"5px 12px", borderRadius:10, cursor:"pointer" }}>
            {editMode?"Cancel":"Edit"}
          </button>
        </div>
        {editMode ? (
          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
            <input value={name} onChange={e=>setName(e.target.value)} placeholder="Your name"
              aria-label="Your name" autoComplete="name"
              style={{ padding:"11px 13px", borderRadius:10, border:`1.5px solid ${t.border2}`, background:t.input, color:t.inputText, fontSize:16, outline:"none", fontFamily:"'DM Sans',sans-serif" }}/>
            <input value={phone} onChange={e=>setPhone(e.target.value)} placeholder="Phone number" type="tel"
              aria-label="Phone number" autoComplete="tel"
              style={{ padding:"11px 13px", borderRadius:10, border:`1.5px solid ${t.border2}`, background:t.input, color:t.inputText, fontSize:16, outline:"none", fontFamily:"'DM Sans',sans-serif" }}/>
            <div style={{ fontSize:11, color:t.t4 }}>Email: {session?.user?.email||""} (cannot be changed here)</div>
            <button onClick={saveProfile} disabled={saving} style={{ padding:"12px", background:"#2563EB", border:"none", borderRadius:12, color:"#fff", fontSize:14, fontWeight:800, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>
              {saving?"Saving...":"Save"}
            </button>
          </div>
        ) : (
          rows.map(row => (
            <div key={row.label} style={{ display:"flex", alignItems:"center", gap:12, padding:"11px 0", borderBottom:`1px solid ${t.border}` }}>
              <span style={{ fontSize:16, width:24, textAlign:"center" }}>{row.icon}</span>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:11, color:t.t4, fontWeight:600 }}>{row.label}</div>
                <div style={{ fontSize:13, color:t.t1, fontWeight:600, marginTop:1 }}>{row.value}</div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Payment methods */}
      <div style={{ background:t.card, borderRadius:20, padding:20, border:`1.5px solid ${t.border}`, marginBottom:14 }}>
        <div style={{ fontSize:13, fontWeight:800, color:t.t1, fontFamily:"'DM Sans',sans-serif", marginBottom:14 }}>Card Payouts</div>
        {isShopEmployee ? (
          <div style={{ display:"flex", alignItems:"flex-start", gap:12 }}>
            <div style={{ fontSize:22 }}>🏪</div>
            <div>
              <div style={{ fontSize:13, fontWeight:700, color:t.t1, marginBottom:4 }}>Your shop handles payments</div>
              <div style={{ fontSize:12, color:t.t3, lineHeight:1.6 }}>You're employed by the shop — your owner processes all client payments and card transactions. Contact your shop owner for payout details.</div>
            </div>
          </div>
        ) : stripeStatus === null ? (
          <div style={{ fontSize:12, color:t.t4 }}>Loading...</div>
        ) : stripeStatus.stripe_charges_enabled ? (
          <>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
              <div style={{ width:8, height:8, borderRadius:"50%", background:"#22C55E" }}/>
              <div style={{ fontSize:13, fontWeight:700, color:"#22C55E" }}>Connected — accepting card payments</div>
            </div>
            <div style={{ fontSize:11, color:t.t4, marginBottom:14 }}>Clients can now pay by Credit / Debit Card when booking you. Payouts go directly to your Stripe account.</div>
            <button onClick={handleStripeConnect} disabled={stripeConnecting} style={{ width:"100%", padding:"11px", background:"transparent", border:`1.5px solid ${t.border}`, borderRadius:12, color:t.t2, fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", display:"flex", alignItems:"center", justifyContent:"center", gap:8, opacity:stripeConnecting?0.6:1 }}>
              {stripeConnecting ? "Opening Stripe..." : "🏦 Manage Payouts & Banking"}
            </button>
          </>
        ) : (
          <>
            <div style={{ fontSize:12, color:t.t3, marginBottom:14, lineHeight:1.6 }}>
              Connect Stripe to accept card payments from clients. Setup takes ~2 minutes — you keep 100% of card earnings.
            </div>
            <button onClick={handleStripeConnect} disabled={stripeConnecting} style={{ width:"100%", padding:"13px", background:"#2563EB", border:"none", borderRadius:14, color:"#fff", fontSize:14, fontWeight:800, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", display:"flex", alignItems:"center", justifyContent:"center", gap:8, opacity:stripeConnecting?0.6:1 }}>
              {stripeConnecting ? "Opening Stripe..." : "💳 Connect Stripe"}
            </button>
            {stripeStatus.stripe_account_id && !stripeStatus.stripe_charges_enabled && (
              <div style={{ fontSize:11, color:"#F59E0B", marginTop:10, textAlign:"center" }}>Setup started but not complete — tap above to finish.</div>
            )}
          </>
        )}
      </div>

      {/* Share app */}
      <div style={{ background:t.card, borderRadius:20, padding:20, border:`1.5px solid ${t.border}`, marginBottom:14 }}>
        <div style={{ fontSize:13, fontWeight:800, color:t.t1, fontFamily:"'DM Sans',sans-serif", marginBottom:10 }}>Share FADED</div>
        <div style={{ fontSize:12, color:t.t3, marginBottom:14 }}>Spread the word — send FADED to your clients or other barbers.</div>
        <button onClick={()=>{ const txt="Book a fresh cut on FADED — the live barber booking app. https://app.letsgetfaded.com"; navigator.share?navigator.share({title:"FADED",text:txt,url:"https://app.letsgetfaded.com"}).catch(()=>{}):navigator.clipboard?.writeText(txt).then(()=>window.dispatchEvent(new CustomEvent("faded-toast",{detail:{msg:"✅ Link copied!",type:"success"}}))); }}
          style={{ width:"100%", padding:"13px", background:t.blueBg, border:`1.5px solid ${t.blueBorder}`, borderRadius:14, color:t.blueText, fontSize:14, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
          📲 Share the App
        </button>
      </div>

      {/* Book a Barber — lets the barber use the app as a client */}
      {onBookABarber && (
        <button onClick={onBookABarber} style={{ width:"100%", padding:"14px", background:"rgba(37,99,235,0.08)", border:"1.5px solid rgba(37,99,235,0.25)", borderRadius:16, color:"#3B6CF7", fontSize:14, fontWeight:800, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", marginBottom:10, display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
          💈 Book a Barber
        </button>
      )}

      {/* Sign out */}
      <button onClick={onSignOut} style={{ width:"100%", padding:"14px", background:"rgba(220,38,38,0.08)", border:"1.5px solid rgba(220,38,38,0.25)", borderRadius:16, color:"#EF4444", fontSize:14, fontWeight:800, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", marginBottom:10 }}>
        Sign Out
      </button>

      {/* Delete barber account — required by Apple */}
      <DeleteBarberAccountSection session={session} onSignOut={onSignOut}/>
    </div>
  );
}

function ClientProfile({ session, profile, setProfile, bookings, favorites, barbers, onToggleFav, onReview, onSignOut, onRebook, onGiftCard, isAdmin, onOpenAdmin }) {
  const [editMode, setEditMode]     = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingAccount, setDeletingAccount]     = useState(false);
  const [deleteBlockMsg, setDeleteBlockMsg]       = useState("");
  const [name, setName]             = useState(profile?.full_name||"");
  const [city, setCity]             = useState(profile?.city||"");
  const [phone, setPhone]           = useState(profile?.phone||"");
  const [country, setCountry]       = useState(profile?.country||"CA");
  const [province, setProvince]     = useState(profile?.province||"ON");
  const [notifBook, setNotifBook]   = useState(profile?.notif_bookings!==false);
  const [notifLive, setNotifLive]   = useState(profile?.notif_barber_live!==false);
  const [reviewBooking, setReviewBooking] = useState(null);
  const [saving, setSaving]         = useState(false);
  const [refCopied, setRefCopied]   = useState(false);
  const fileRef = useRef();
  const [avatar, setAvatar]         = useState(profile?.avatar_url||null);
  const [savedCard, setSavedCard]   = useState(null); // { pmId, brand, last4 }
  const [addingCard, setAddingCard] = useState(false);
  const [cardSaveError, setCardSaveError] = useState("");
  const [savingCard, setSavingCard] = useState(false);
  const [removingCard, setRemovingCard] = useState(false);
  const profileCardRef = useRef(null);
  const profileCardCleanupRef = useRef(null);

  useEffect(() => {
    if (!session?.user?.id) return;
    supabase.from("profiles").select("full_name,phone,city,country,province,avatar_url,notif_bookings,notif_barber_live,default_payment_method_id,default_card_brand,default_card_last4").eq("id", session.user.id).single()
      .then(({ data }) => {
        if (data) {
          setName(data.full_name || "");
          setPhone(data.phone || "");
          setCity(data.city || "");
          if (data.country) setCountry(data.country);
          if (data.province) setProvince(data.province);
          if (data.avatar_url) setAvatar(data.avatar_url);
          setNotifBook(data.notif_bookings !== false);
          setNotifLive(data.notif_barber_live !== false);
          if (data.default_payment_method_id) {
            setSavedCard({ pmId: data.default_payment_method_id, brand: data.default_card_brand, last4: data.default_card_last4 });
          }
        }
      });
  }, [session?.user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!addingCard || !STRIPE_AVAILABLE) return;
    const timer = setTimeout(() => {
      if (!profileCardRef.current) return;
      mountSetupCardElement(profileCardRef.current).then(cleanup => { profileCardCleanupRef.current = cleanup; });
    }, 80);
    return () => { clearTimeout(timer); profileCardCleanupRef.current?.(); profileCardCleanupRef.current = null; };
  }, [addingCard]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveProfile = async () => {
    setSaving(true);
    const { data: { session: freshSession } } = await supabase.auth.getSession();
    if (!freshSession) {
      setSaving(false);
      window.dispatchEvent(new CustomEvent("faded-toast", { detail: { msg: "Session expired — please sign out and sign in again.", type: "error" } }));
      return;
    }
    const updates = { full_name:sanitize(name,100), city:sanitize(city,100), phone:sanitize(phone,30), country, province, notif_bookings:notifBook, notif_barber_live:notifLive };
    const { error } = await supabase.from("profiles").update(updates).eq("id", freshSession.user.id);
    if (error) {
      setSaving(false);
      window.dispatchEvent(new CustomEvent("faded-toast", { detail: { msg: "Could not save — please try again.", type: "error" } }));
      return;
    }
    if (setProfile) setProfile(prev => ({ ...prev, ...updates }));
    setSaving(false);
    setEditMode(false);
  };

  const handleSaveProfileCard = async () => {
    setSavingCard(true);
    setCardSaveError("");
    const result = await confirmSetupIntent({ supabase, bookingId: null });
    if (!result.success) {
      setCardSaveError(result.error || "Card could not be saved — please try again.");
      setSavingCard(false);
      return;
    }
    await supabase.from("profiles").update({
      default_payment_method_id: result.paymentMethodId,
      default_card_brand: result.brand || null,
      default_card_last4: result.last4 || null,
    }).eq("id", session.user.id);
    setSavedCard({ pmId: result.paymentMethodId, brand: result.brand, last4: result.last4 });
    setAddingCard(false);
    profileCardCleanupRef.current?.();
    profileCardCleanupRef.current = null;
    window.dispatchEvent(new CustomEvent("faded-toast", { detail: { msg: "Card saved!", type: "success" } }));
    setSavingCard(false);
  };

  const handleRemoveCard = async () => {
    setRemovingCard(true);
    await supabase.from("profiles").update({ default_payment_method_id: null, default_card_brand: null, default_card_last4: null }).eq("id", session.user.id);
    setSavedCard(null);
    setRemovingCard(false);
  };

  const uploadAvatar = async (file) => {
    file = await toUploadableImage(file);
    const path = `${session.user.id}/${Date.now()}.${file.name.split(".").pop()}`;
    const { error } = await supabase.storage.from("avatars").upload(path, file, { upsert:true });
    if (!error) {
      const { data } = supabase.storage.from("avatars").getPublicUrl(path);
      setAvatar(data.publicUrl);
      await supabase.from("profiles").update({ avatar_url:data.publicUrl }).eq("id",session.user.id);
    }
  };

  const deleteAccount = async () => {
    if (!session?.user?.id) return;
    setDeleteBlockMsg("");
    setDeletingAccount(true);
    const uid = session.user.id;
    const { data: canDelete } = await supabase.rpc("can_delete_account", { p_user_id: uid });
    if (!canDelete?.can_delete) {
      const n = canDelete?.active_bookings || 1;
      setDeleteBlockMsg(`You have ${n} upcoming booking${n > 1 ? "s" : ""}. Cancel ${n > 1 ? "them" : "it"} first, then delete your account.`);
      setDeletingAccount(false);
      return;
    }
    // Anonymize bookings — barbers need the financial record for accounting.
    // client_id is nulled automatically by ON DELETE SET NULL when profile is deleted.
    // Clear PII text snapshots here so no personal data remains on the row.
    await supabase.from("bookings").update({
      client_name: "Deleted Client",
      client_phone: null,
      client_email: null,
      client_avatar: null,
    }).eq("client_id", uid);
    // Anonymize reviews — keep the rating (barber's reputation) but remove identity
    await supabase.from("reviews").update({
      comment: "[Review removed by user]",
    }).eq("client_id", uid);
    // Delete personal data — not financial records
    await supabase.from("favorites").delete().eq("client_id", uid);
    await supabase.from("instant_requests").delete().eq("client_id", uid);
    await supabase.from("push_tokens").delete().eq("user_id", uid);
    await supabase.from("notification_failures").delete().eq("user_id", uid);
    await supabase.from("profiles").delete().eq("id", uid); // FK → bookings.client_id SET NULL
    // Delete auth.users record + Stripe customer (requires service role via Edge Function)
    await supabase.functions.invoke("delete-account").catch(() => {});
    await supabase.auth.signOut();
    onSignOut();
  };

  const favBarbers = barbers.filter(b=>favorites.includes(b.id));
  const completedBookings = bookings.filter(b=>b.status==="completed");

  const { t } = useT();
  const { isDesktop: isDesktopProfile } = useR();
  return (
    <div style={{ padding:isDesktopProfile?"32px 40px 100px":"20px 16px 100px", minHeight:"var(--app-height,100dvh)", maxWidth:isDesktopProfile?720:"none", margin:isDesktopProfile?"0 auto":"0" }}>
      {reviewBooking && <ReviewModal booking={reviewBooking} onClose={()=>setReviewBooking(null)} onSubmit={onReview}/>}

      {/* Profile card — Instagram style */}
      <div style={{ background:t.card, borderRadius:20, border:`1.5px solid ${t.border}`, marginBottom:16, overflow:"hidden" }}>
        {/* Edit button top-right */}
        <div style={{ display:"flex", justifyContent:"flex-end", padding:"14px 16px 0" }}>
          <button onClick={()=>setEditMode(!editMode)} style={{ fontSize:12, color:t.blueText, fontWeight:700, background:t.blueBg, border:"none", padding:"6px 14px", borderRadius:10, cursor:"pointer" }}>
            {editMode?"Cancel":"Edit"}
          </button>
        </div>

        {/* Centered avatar + info */}
        <div style={{ display:"flex", flexDirection:"column", alignItems:"center", padding:"8px 20px 20px", gap:10 }}>
          {/* Avatar circle */}
          <div onClick={()=>editMode&&fileRef.current.click()}
            style={{ width:88, height:88, borderRadius:"50%", overflow:"hidden", border:`3px solid ${avatar?"rgba(245,245,240,0.15)":"#2563EB"}`, flexShrink:0, cursor:editMode?"pointer":"default", position:"relative" }}>
            {avatar
              ? <img src={avatar} alt="profile" style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
              : <div style={{ width:"100%", height:"100%", background:t.blueBg, display:"flex", alignItems:"center", justifyContent:"center", fontSize:28, fontWeight:800, color:t.blueText, fontFamily:"'DM Sans',sans-serif" }}>{initials(name||"U")}</div>}
            {editMode && (
              <div style={{ position:"absolute", inset:0, background:"rgba(0,0,0,0.55)", display:"flex", alignItems:"center", justifyContent:"center" }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
              </div>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/*" style={{ display:"none" }} onChange={e=>uploadAvatar(e.target.files[0])}/>

          {editMode ? (
            <div style={{ display:"flex", flexDirection:"column", gap:8, width:"100%" }}>
              <input value={name} onChange={e=>setName(e.target.value)} placeholder="Your name"
                aria-label="Your name" autoComplete="name"
                style={{ padding:"10px 12px", borderRadius:10, border:`1.5px solid ${t.border2}`, background:t.input, color:t.inputText, fontSize:14, outline:"none", fontFamily:"'DM Sans',sans-serif", width:"100%", boxSizing:"border-box" }}/>
              <input value={city} onChange={e=>setCity(e.target.value)} placeholder="Your city"
                aria-label="Your city" autoComplete="address-level2"
                style={{ padding:"10px 12px", borderRadius:10, border:`1.5px solid ${t.border2}`, background:t.input, color:t.inputText, fontSize:13, outline:"none", fontFamily:"'DM Sans',sans-serif", width:"100%", boxSizing:"border-box" }}/>
              <input value={phone} onChange={e=>setPhone(e.target.value)} placeholder="Phone (for SMS updates)" type="tel"
                aria-label="Phone number for SMS updates" autoComplete="tel"
                style={{ padding:"10px 12px", borderRadius:10, border:`1.5px solid ${t.border2}`, background:t.input, color:t.inputText, fontSize:13, outline:"none", fontFamily:"'DM Sans',sans-serif", width:"100%", boxSizing:"border-box" }}/>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                <select value={country} onChange={e=>{ setCountry(e.target.value); setProvince(e.target.value==="CA"?"ON":"NY"); }}
                  style={{ padding:"10px 12px", borderRadius:10, border:`1.5px solid ${t.border2}`, background:t.input, color:t.inputText, fontSize:13, outline:"none", fontFamily:"'DM Sans',sans-serif" }}>
                  <option value="CA">🇨🇦 Canada</option>
                  <option value="US">🇺🇸 United States</option>
                </select>
                <select value={province} onChange={e=>setProvince(e.target.value)}
                  style={{ padding:"10px 12px", borderRadius:10, border:`1.5px solid ${t.border2}`, background:t.input, color:t.inputText, fontSize:13, outline:"none", fontFamily:"'DM Sans',sans-serif" }}>
                  {(country==="US" ? US_STATES : CA_PROVINCES).map(p=><option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            </div>
          ) : (
            <>
              <div style={{ fontSize:19, fontWeight:800, color:t.t1, fontFamily:"'DM Sans',sans-serif", textAlign:"center" }}>{name||"Set your name"}</div>
              <div style={{ fontSize:12, color:t.t4, textAlign:"center", lineHeight:1.7 }}>
                {city && <span>📍 {city}</span>}
                {city && <span style={{ margin:"0 6px", opacity:0.4 }}>·</span>}
                <span>{session?.user?.email||""}</span>
              </div>
            </>
          )}

          {/* Stats row */}
          <div style={{ display:"flex", width:"100%", paddingTop:16, marginTop:4, borderTop:`1px solid ${t.border}` }}>
            {[["Cuts",completedBookings.length],["Saved",favorites.length],["Reviews",completedBookings.filter(b=>b.reviewed).length]].map(([label,val])=>(
              <div key={label} style={{ flex:1, textAlign:"center" }}>
                <div style={{ fontSize:21, fontWeight:900, color:t.t1, fontFamily:"'DM Sans',sans-serif" }}>{val}</div>
                <div style={{ fontSize:11, color:t.t4, marginTop:2 }}>{label}</div>
              </div>
            ))}
          </div>
        </div>

        {editMode && (
          <div style={{ padding:"16px 20px" }}>
            <button onClick={saveProfile} disabled={saving} style={{ width:"100%", padding:14, background:"#2563EB", border:"none", borderRadius:14, color:"#fff", fontSize:14, fontWeight:800, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", boxShadow:"0 4px 20px rgba(37,99,235,0.35)" }}>
              {saving?"Saving...":"Save Profile"}
            </button>
          </div>
        )}
      </div>

      {/* Payment Method */}
      <div style={{ background:t.card, borderRadius:20, border:`1.5px solid ${t.border}`, marginBottom:16, overflow:"hidden" }}>
        <div style={{ padding:"18px 20px" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom: addingCard || savedCard ? 14 : 0 }}>
            <div>
              <div style={{ fontSize:13, fontWeight:800, color:t.t1, fontFamily:"'DM Sans',sans-serif" }}>Payment Method</div>
              <div style={{ fontSize:12, color:t.t4, marginTop:2 }}>Saved card used automatically when a barber requires one</div>
            </div>
            {!addingCard && (
              <button onClick={()=>setAddingCard(true)} style={{ fontSize:12, color:t.blueText, fontWeight:700, background:t.blueBg, border:"none", padding:"5px 12px", borderRadius:10, cursor:"pointer", flexShrink:0, marginLeft:12 }}>
                {savedCard ? "Update" : "Add"}
              </button>
            )}
          </div>
          {!addingCard && savedCard && (
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", background:"rgba(255,255,255,0.04)", borderRadius:12, padding:"12px 14px" }}>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <div style={{ width:38, height:26, borderRadius:6, background:"rgba(37,99,235,0.15)", border:"1px solid rgba(37,99,235,0.3)", display:"flex", alignItems:"center", justifyContent:"center" }}>
                  <span style={{ fontSize:10, fontWeight:800, color:"#60A5FA", textTransform:"uppercase", letterSpacing:"0.03em" }}>{(savedCard.brand||"card").slice(0,4)}</span>
                </div>
                <div>
                  <div style={{ fontSize:13, fontWeight:700, color:t.t1 }}>•••• {savedCard.last4||"••••"}</div>
                  <div style={{ fontSize:11, color:t.t4 }}>Saved card</div>
                </div>
              </div>
              <button onClick={handleRemoveCard} disabled={removingCard} style={{ fontSize:12, color:"rgba(239,68,68,0.8)", fontWeight:700, background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.2)", padding:"5px 12px", borderRadius:10, cursor:"pointer" }}>
                {removingCard ? "Removing…" : "Remove"}
              </button>
            </div>
          )}
          {!addingCard && !savedCard && (
            <div style={{ fontSize:12, color:t.t4, lineHeight:1.6 }}>No card saved — add one to skip card entry when booking with barbers who require a card on file.</div>
          )}
          {addingCard && (
            <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
              <div ref={profileCardRef} style={{ padding:"14px 16px", background:"rgba(245,245,240,0.05)", border:`1px solid ${t.border2}`, borderRadius:12 }}/>
              {cardSaveError && <div style={{ fontSize:12, color:"#EF4444", padding:"8px 12px", background:"rgba(220,38,38,0.08)", border:"1px solid rgba(220,38,38,0.25)", borderRadius:10 }}>{cardSaveError}</div>}
              <div style={{ display:"flex", gap:8 }}>
                <button onClick={()=>{ setAddingCard(false); setCardSaveError(""); profileCardCleanupRef.current?.(); profileCardCleanupRef.current = null; }}
                  style={{ flex:1, padding:"11px", background:"transparent", border:`1px solid ${t.border2}`, borderRadius:12, color:t.t3, fontSize:13, fontWeight:700, cursor:"pointer" }}>
                  Cancel
                </button>
                <button onClick={handleSaveProfileCard} disabled={savingCard}
                  style={{ flex:2, padding:"11px", background:"#2563EB", border:"none", borderRadius:12, color:"#fff", fontSize:13, fontWeight:800, cursor:"pointer", opacity:savingCard?0.7:1 }}>
                  {savingCard ? "Saving…" : cardSaveError ? "Try Again" : "Save Card"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Loyalty stamp card */}
      {bookings.filter(b=>b.status==="completed").length > 0 && (
        <LoyaltyStampCard bookings={bookings} barbers={barbers}/>
      )}

      {/* Cut history timeline */}
      {completedBookings.length > 0 && (() => {
        const sorted = [...completedBookings].sort((a,b)=>new Date(b.scheduled_at||b.created_at)-new Date(a.scheduled_at||a.created_at));
        const totalSpent = sorted.reduce((s,b)=>s+(parseFloat(b.price_agreed)||0),0);
        return (
          <div style={{ background:t.card, borderRadius:20, border:`1.5px solid ${t.border}`, marginBottom:16, overflow:"hidden" }}>
            {/* Header */}
            <div style={{ padding:"18px 20px 16px", borderBottom:`1px solid ${t.border}` }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                <div>
                  <div style={{ fontSize:13, fontWeight:800, color:t.t1, fontFamily:"'DM Sans',sans-serif" }}>Cut History</div>
                  <div style={{ fontSize:11, color:t.t4, marginTop:2 }}>{sorted.length} cut{sorted.length!==1?"s":""} · ${totalSpent.toFixed(0)} total</div>
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:6, background:"rgba(37,99,235,0.08)", border:"1px solid rgba(37,99,235,0.2)", borderRadius:20, padding:"4px 12px" }}>
                  <span style={{ width:6, height:6, borderRadius:"50%", background:"#2563EB", display:"inline-block" }}/>
                  <span style={{ fontSize:10, fontWeight:800, color:"#60A5FA", letterSpacing:"0.08em" }}>YOUR RECORD</span>
                </div>
              </div>
            </div>
            {/* Timeline */}
            <div style={{ padding:"16px 20px 20px", position:"relative" }}>
              {/* Vertical line */}
              <div style={{ position:"absolute", left:36, top:16, bottom:20, width:1, background:`linear-gradient(180deg,${t.border} 0%,transparent 100%)` }}/>
              <div style={{ display:"flex", flexDirection:"column", gap:0 }}>
                {sorted.slice(0,8).map((b,i) => {
                  const barber = barbers.find(br=>br.id===b.barber_id);
                  const dateStr = b.scheduled_at||b.created_at;
                  const date = dateStr ? new Date(dateStr) : null;
                  const dateLabel = date ? date.toLocaleDateString("en-CA",{month:"short",day:"numeric",year:date.getFullYear()!==new Date().getFullYear()?"numeric":undefined}) : "";
                  const accent = barber?.accent_color||"#2563EB";
                  const isFirst = i === 0;
                  return (
                    <div key={b.id} style={{ display:"flex", gap:14, paddingBottom:i<Math.min(sorted.length,8)-1?20:0, position:"relative" }}>
                      {/* Avatar dot */}
                      <div style={{ flexShrink:0, width:32, height:32, borderRadius:"50%", overflow:"hidden", border:`2px solid ${isFirst?accent:t.border}`, background:barber?.avatar_url?"transparent":`linear-gradient(135deg,${accent}22,${accent}44)`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, fontWeight:800, color:accent, zIndex:1, flexShrink:0 }}>
                        {barber?.avatar_url
                          ? <img src={barber.avatar_url} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
                          : (b.barber_name||barber?.full_name||"B")[0].toUpperCase()}
                      </div>
                      {/* Content */}
                      <div style={{ flex:1, paddingTop:4 }}>
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                          <div>
                            <div style={{ fontSize:13, fontWeight:700, color:t.t1, lineHeight:1.2 }}>{b.service_type||"Haircut"}</div>
                            <div style={{ fontSize:11, color:t.t4, marginTop:2 }}>{b.barber_name||barber?.full_name||"Barber"}</div>
                          </div>
                          <div style={{ textAlign:"right", flexShrink:0, marginLeft:8 }}>
                            <div style={{ fontSize:13, fontWeight:800, color:isFirst?"#22C55E":t.t2 }}>${parseFloat(b.price_agreed||0).toFixed(0)}</div>
                            <div style={{ fontSize:10, color:t.t4, marginTop:1 }}>{dateLabel}</div>
                          </div>
                        </div>
                        {isFirst && (
                          <div style={{ display:"inline-flex", alignItems:"center", gap:4, marginTop:5, background:"rgba(34,197,94,0.08)", border:"1px solid rgba(34,197,94,0.2)", borderRadius:20, padding:"2px 8px" }}>
                            <span style={{ width:5, height:5, borderRadius:"50%", background:"#22C55E", display:"inline-block" }}/>
                            <span style={{ fontSize:10, fontWeight:700, color:"#22C55E" }}>Most recent</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                {sorted.length > 8 && (
                  <div style={{ paddingLeft:46, paddingTop:8 }}>
                    <div style={{ fontSize:12, color:t.t4 }}>+ {sorted.length - 8} more cuts in your history</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Notifications card */}
      <div style={{ background:t.card, borderRadius:20, padding:20, border:`1.5px solid ${t.border}`, marginBottom:16 }}>
        <div style={{ fontSize:13, fontWeight:800, color:t.t1, fontFamily:"'DM Sans',sans-serif", marginBottom:4 }}>Notifications</div>
        <div style={{ fontSize:12, color:t.t4, marginBottom:14 }}>Stay on top of your cuts</div>
        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
          {[
            { label:"Fresh cut reminder", sub:"Every 2 weeks — time to get faded", key:"reminder" },
            { label:"Barber goes live", sub:"When a saved barber becomes available", key:"barber_live" },
            { label:"Booking updates", sub:"Confirmations and status changes", key:"bookings" },
            { label:"Occasions and holidays", sub:"Prom, Christmas, Canada Day cuts", key:"occasions" },
          ].map(item => {
            const stored = (() => { try { return localStorage.getItem("faded_notif_"+item.key) !== "false"; } catch(e) { return true; } })();
            return (
              <div key={item.key} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 0", borderBottom:`1px solid ${t.border}` }}>
                <div>
                  <div style={{ fontSize:13, fontWeight:600, color:t.t1 }}>{item.label}</div>
                  <div style={{ fontSize:11, color:t.t4, marginTop:2 }}>{item.sub}</div>
                </div>
                <button
                  onClick={async () => {
                    const granted = await requestNotificationPermission();
                    if (!granted) { console.warn("notifications blocked"); return; }
                    const current = (() => { try { return localStorage.getItem("faded_notif_"+item.key) !== "false"; } catch(e) { return true; } })();
                    try { localStorage.setItem("faded_notif_"+item.key, (!current).toString()); } catch(e) {}
                    if (!current && item.key === "reminder") scheduleCutReminder(2);
                    window.location.reload();
                  }}
                  style={{ width:44, height:24, borderRadius:12, background:stored?"#2563EB":t.surface2, border:"none", cursor:"pointer", position:"relative", transition:"background 0.2s", flexShrink:0 }}>
                  <span style={{ position:"absolute", top:2, left:stored?22:2, width:20, height:20, borderRadius:"50%", background:"#fff", transition:"left 0.2s", display:"block", boxShadow:"0 1px 4px rgba(0,0,0,0.4)" }}/>
                </button>
              </div>
            );
          })}
        </div>
        <button
          onClick={async () => {
            const granted = await requestNotificationPermission();
            if (granted) {
              sendNotification("FADED Notifications On! ✂️", "You will get cut reminders, occasion alerts, and barber updates.");
            } else {
              console.warn("notifications pending");
            }
          }}
          style={{ marginTop:14, width:"100%", padding:"10px", background:t.blueBg, border:`1px solid ${t.blueBorder}`, borderRadius:12, color:t.blueText, fontSize:12, fontWeight:700, cursor:"pointer" }}>
          Test Notification
        </button>
      </div>

      {/* Referral card */}
      {(() => {
        const refUrl = `https://app.letsgetfaded.com?ref=${session.user.id}`;
        const shareRef = () => {
          const msg = `Hey — if you're a barber, sign up on FADED. Takes 2 minutes, free to join. Use my link and I get $5 e-transferred once you're up and running: ${refUrl}`;
          if (navigator.share) {
            navigator.share({ title:"Join FADED", text:msg, url:refUrl }).catch(()=>{});
          } else {
            navigator.clipboard?.writeText(refUrl);
            setRefCopied(true);
            setTimeout(()=>setRefCopied(false), 2000);
          }
        };
        const copyRef = () => {
          navigator.clipboard?.writeText(refUrl).then(()=>{
            setRefCopied(true);
            setTimeout(()=>setRefCopied(false), 2000);
          }).catch(()=>{});
        };
        const shortUrl = refUrl.replace("https://","");
        return (
          <div style={{ borderRadius:20, border:"1.5px solid rgba(37,99,235,0.3)", marginBottom:16, overflow:"hidden", background:"linear-gradient(160deg,rgba(37,99,235,0.08) 0%,rgba(10,10,10,0) 60%)", backdropFilter:"blur(0px)" }}>
            {/* Blue accent bar */}
            <div style={{ height:3, background:"linear-gradient(90deg,#2563EB,#60A5FA)" }}/>
            <div style={{ padding:"20px 20px 22px" }}>
              {/* Badge */}
              <div style={{ display:"inline-flex", alignItems:"center", gap:6, background:"rgba(37,99,235,0.12)", border:"1px solid rgba(37,99,235,0.3)", borderRadius:100, padding:"4px 12px", marginBottom:14 }}>
                <span style={{ width:6, height:6, borderRadius:"50%", background:"#2563EB", display:"inline-block" }}/>
                <span style={{ fontSize:10, fontWeight:800, color:"#93C5FD", letterSpacing:"0.1em" }}>REFER A BARBER</span>
              </div>
              {/* Headline */}
              <div style={{ fontSize:20, fontWeight:900, color:t.t1, fontFamily:"'DM Sans',sans-serif", lineHeight:1.25, marginBottom:8 }}>
                Get <span style={{ color:"#2563EB" }}>$5 by e-transfer</span> per barber you bring on.
              </div>
              <div style={{ fontSize:13, color:t.t3, lineHeight:1.65, marginBottom:18 }}>
                Share your personal link with any barber. Once they finish setting up their profile and complete their first booking on FADED, we e-transfer $5 straight to your account email — yours to spend however you like.
              </div>
              {/* Link display */}
              <div style={{ background:t.section||"rgba(255,255,255,0.04)", border:`1px solid ${t.border}`, borderRadius:12, padding:"11px 14px", marginBottom:14, display:"flex", alignItems:"center", justifyContent:"space-between", gap:10 }}>
                <span style={{ fontSize:11, color:t.t4, fontFamily:"'DM Mono','Courier New',monospace", wordBreak:"break-all", lineHeight:1.5, flex:1 }}>{shortUrl}</span>
                <button onClick={copyRef} aria-label="Copy referral link" style={{ flexShrink:0, background:"transparent", border:"none", cursor:"pointer", padding:"2px 4px" }}>
                  {refCopied
                    ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={t.t3} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>}
                </button>
              </div>
              {/* Share button */}
              <button onClick={shareRef} style={{ width:"100%", padding:"14px", background:"#2563EB", border:"none", borderRadius:14, color:"#fff", fontSize:14, fontWeight:800, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:9, fontFamily:"'DM Sans',sans-serif", boxShadow:"0 4px 20px rgba(37,99,235,0.35)", letterSpacing:"0.02em" }}>
                <svg aria-hidden="true" focusable="false" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
                {refCopied ? "Copied!" : "Share My Link →"}
              </button>
              <div style={{ marginTop:10, fontSize:11, color:t.t4, textAlign:"center" }}>No limit — refer as many barbers as you want</div>
            </div>
          </div>
        );
      })()}

      {/* Admin — mobile has no nav-bar space for this, so it lives here instead */}
      {isAdmin && (
        <button onClick={onOpenAdmin} style={{ width:"100%", padding:14, marginBottom:10, background:"rgba(37,99,235,0.08)", border:"1.5px solid rgba(37,99,235,0.25)", borderRadius:14, color:"#3B6CF7", fontSize:14, fontWeight:700, cursor:"pointer" }}>
          🛡️ Admin Panel
        </button>
      )}

      {/* Sign out */}
      <button onClick={onSignOut} style={{ width:"100%", padding:14, background:"rgba(220,38,38,0.08)", border:"1.5px solid rgba(220,38,38,0.2)", borderRadius:14, color:"#EF4444", fontSize:14, fontWeight:700, cursor:"pointer" }}>
        Sign Out
      </button>

      {/* FIX 35: delete account */}
      {!showDeleteConfirm ? (
        <button onClick={()=>setShowDeleteConfirm(true)} aria-label="Delete my account permanently" style={{ width:"100%", padding:12, background:"transparent", border:"none", color:"#555", fontSize:12, fontWeight:600, cursor:"pointer", marginTop:8 }}>
          Delete my account
        </button>
      ) : (
        <div style={{ marginTop:12, padding:"16px", background:"rgba(220,38,38,0.06)", border:"1.5px solid rgba(220,38,38,0.2)", borderRadius:14 }}>
          <div style={{ fontSize:14, fontWeight:800, color:"#EF4444", marginBottom:6 }}>Delete account?</div>
          <div style={{ fontSize:12, color:"#888", marginBottom:14, lineHeight:1.6 }}>This will permanently delete your bookings, reviews, favourites, and profile. This action cannot be undone.</div>
          {deleteBlockMsg && (
            <div style={{ fontSize:12, color:"#F59E0B", background:"rgba(245,158,11,0.08)", border:"1px solid rgba(245,158,11,0.25)", borderRadius:10, padding:"10px 12px", marginBottom:12, lineHeight:1.5 }}>
              {deleteBlockMsg}
            </div>
          )}
          <div style={{ display:"flex", gap:10 }}>
            <button onClick={()=>{ setShowDeleteConfirm(false); setDeleteBlockMsg(""); }} style={{ flex:1, padding:12, background:"transparent", border:"1.5px solid #333", borderRadius:12, color:"#888", fontSize:13, fontWeight:700, cursor:"pointer" }}>Cancel</button>
            <button onClick={deleteAccount} disabled={deletingAccount} aria-label={deletingAccount ? "Deleting account, please wait" : "Confirm permanent account deletion"} aria-disabled={deletingAccount} style={{ flex:1, padding:12, background:"#DC2626", border:"none", borderRadius:12, color:"#fff", fontSize:13, fontWeight:800, cursor:"pointer", opacity:deletingAccount?0.7:1 }}>
              {deletingAccount ? "Checking..." : "Yes, Delete"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── GIFT CARD MODAL ──────────────────────────────────────────────────────────
function GiftCardModal({ session, onClose }) {
  const { t } = useT();
  const [giftType, setGiftType]     = useState("amount");
  const [amount, setAmount]         = useState(30);
  const [service, setService]       = useState("Classic Cut");
  const [toName, setToName]         = useState("");
  const [toContact, setToContact]   = useState("");
  const [message, setMessage]       = useState("");
  const [step, setStep]             = useState("compose");
  const [code, setCode]             = useState("");
  const [sending, setSending]       = useState(false);

  const send = async () => {
    if (!toContact.trim() || !toName.trim()) return;
    setSending(true);
    try {
      // Cryptographically secure code — Math.random() is not suitable for gift card codes
      const newCode = Array.from(crypto.getRandomValues(new Uint8Array(8)))
        .map(b => b.toString(36).padStart(2,"0")).join("").toUpperCase().replace(/[^A-Z0-9]/g,"").slice(0,8);
      const { error: gcError } = await supabase.from("gift_cards").insert({
        code: newCode,
        from_client_id: session.user.id,
        amount_cents: giftType === "amount" ? amount * 100 : null,
        service: giftType === "service" ? service : null,
        to_name: toName,
        to_contact: toContact,
        message: message,
      });
      if (gcError) { console.error("[FADED] gift_cards insert:", gcError.message); return; }
      setCode(newCode); // Only set after confirmed DB write
      const senderName = session.user.user_metadata?.full_name || "Someone";
      const giftLine = giftType === "amount" ? `$${amount} credit` : service;
      const giftMsg = `${senderName} gifted you a haircut! 💈\n${giftLine} at any FADED barber.\nUse code ${newCode} when booking at https://app.letsgetfaded.com${message ? `\n\n"${message}"` : ""}`;
      if (toContact.includes("@")) {
        window.open(`mailto:${toContact}?subject=You got a haircut gift!&body=${encodeURIComponent(giftMsg)}`);
      } else {
        window.open(`sms:${toContact}?body=${encodeURIComponent(giftMsg)}`);
      }
      setStep("sent");
    } catch (e) {
      console.error("[FADED] giftCard send:", e);
    } finally {
      setSending(false);
    }
  };

  const { isMd } = useR();
  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, zIndex:200, background:"rgba(17,24,39,0.5)", backdropFilter:"blur(6px)", display:"flex", alignItems:isMd?"center":"flex-end", justifyContent:"center", animation:"fadeIn 0.18s ease" }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:t.card, borderRadius:isMd?24:"28px 28px 0 0", width:"100%", maxWidth:isMd?520:480, maxHeight:"90vh", overflowY:"auto", animation:"slideUp 0.28s cubic-bezier(0.32,0.72,0,1)", paddingBottom:40 }}>
        {!isMd && <div style={{ display:"flex", justifyContent:"center", paddingTop:12, paddingBottom:4 }}><div style={{ width:40, height:4, background:t.surface2, borderRadius:2 }}/></div>}

        {step === "sent" ? (
          <div style={{ padding:"36px 24px", textAlign:"center" }}>
            <div style={{ fontSize:56, marginBottom:16 }}>🎁</div>
            <div style={{ fontSize:24, fontWeight:900, color:t.t1, fontFamily:"'DM Sans',sans-serif", marginBottom:6 }}>Gift Sent!</div>
            <div style={{ fontSize:13, color:t.t3, marginBottom:24 }}>{toName} will receive their gift code.</div>
            <div style={{ background:t.section, borderRadius:16, padding:"20px 24px", border:`1.5px solid ${t.border}`, marginBottom:24 }}>
              <div style={{ fontSize:11, color:t.t4, marginBottom:8, fontWeight:600 }}>GIFT CODE</div>
              <div style={{ fontSize:32, fontWeight:900, letterSpacing:6, color:"#2563EB", fontFamily:"'DM Sans',sans-serif" }}>{code}</div>
              <div style={{ fontSize:11, color:t.t4, marginTop:8 }}>Share this code or copy it from your share sheet</div>
            </div>
            <button onClick={onClose} style={{ width:"100%", padding:16, background:"#2563EB", border:"none", borderRadius:16, color:"#fff", fontSize:15, fontWeight:800, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>Done</button>
          </div>
        ) : (
          <div style={{ padding:"20px 24px" }}>
            <div style={{ fontSize:22, fontWeight:900, color:t.t1, fontFamily:"'DM Sans',sans-serif", marginBottom:4 }}>Gift a Haircut 🎁</div>
            <div style={{ fontSize:13, color:t.t3, marginBottom:20 }}>Send a digital gift card — redeemable at checkout</div>

            <div style={{ display:"flex", gap:8, marginBottom:18 }}>
              {[{id:"amount",label:"Dollar Amount"},{id:"service",label:"Specific Service"}].map(o => (
                <button key={o.id} onClick={()=>setGiftType(o.id)}
                  style={{ flex:1, padding:"10px", borderRadius:12, border:`1.5px solid ${giftType===o.id?"#2563EB":t.border}`, background:giftType===o.id?t.blueBg:t.section, color:giftType===o.id?t.blueText:t.t3, fontSize:12, fontWeight:700, cursor:"pointer" }}>
                  {o.label}
                </button>
              ))}
            </div>

            {giftType === "amount" ? (
              <div style={{ marginBottom:18 }}>
                <div style={{ fontSize:12, fontWeight:700, color:t.t3, marginBottom:8 }}>Amount</div>
                <div style={{ display:"flex", gap:8 }}>
                  {[25, 30, 50, 75].map(a => (
                    <button key={a} onClick={()=>setAmount(a)}
                      style={{ flex:1, padding:"12px 6px", borderRadius:12, border:`1.5px solid ${amount===a?"#2563EB":t.border}`, background:amount===a?t.blueBg:t.section, color:amount===a?t.blueText:t.t3, fontSize:14, fontWeight:800, cursor:"pointer" }}>
                      ${a}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ marginBottom:18 }}>
                <div style={{ fontSize:12, fontWeight:700, color:t.t3, marginBottom:8 }}>Service</div>
                <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                  {["Classic Cut","Skin Fade","Beard Trim","Cut + Beard","Shape-Up"].map(s => (
                    <button key={s} onClick={()=>setService(s)}
                      style={{ padding:"10px 14px", borderRadius:12, border:`1.5px solid ${service===s?"#2563EB":t.border}`, background:service===s?t.blueBg:t.section, color:service===s?t.blueText:t.t2, fontSize:13, fontWeight:700, cursor:"pointer", textAlign:"left" }}>
                      {s} {service===s?"✓":""}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div style={{ marginBottom:12 }}>
              <div style={{ fontSize:12, fontWeight:700, color:t.t3, marginBottom:6 }}>Recipient's name</div>
              <input value={toName} onChange={e=>setToName(e.target.value)} placeholder="Their name"
                style={{ width:"100%", padding:"12px 14px", borderRadius:12, border:`1.5px solid ${t.border2}`, background:t.input, color:t.inputText, fontSize:14, outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box" }}/>
            </div>
            <div style={{ marginBottom:12 }}>
              <div style={{ fontSize:12, fontWeight:700, color:t.t3, marginBottom:6 }}>Send via (email or phone)</div>
              <input value={toContact} onChange={e=>setToContact(e.target.value)} placeholder="their@email.com or +1 416..."
                style={{ width:"100%", padding:"12px 14px", borderRadius:12, border:`1.5px solid ${t.border2}`, background:t.input, color:t.inputText, fontSize:14, outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box" }}/>
            </div>
            <div style={{ marginBottom:22 }}>
              <div style={{ fontSize:12, fontWeight:700, color:t.t3, marginBottom:6 }}>Personal message (optional)</div>
              <textarea value={message} onChange={e=>setMessage(e.target.value)} placeholder="Happy birthday! 🎂" rows={3}
                style={{ width:"100%", padding:"12px 14px", borderRadius:12, border:`1.5px solid ${t.border2}`, background:t.input, color:t.inputText, fontSize:13, outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box", resize:"none" }}/>
            </div>

            <button onClick={send} disabled={sending || !toName.trim() || !toContact.trim()}
              style={{ width:"100%", padding:16, background:(toName.trim()&&toContact.trim())?"#2563EB":t.surface2, border:"none", borderRadius:16, color:(toName.trim()&&toContact.trim())?"#fff":t.t4, fontSize:15, fontWeight:800, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", opacity:sending?0.7:1 }}>
              {sending ? "Sending..." : "Send Gift Card"}
            </button>
            <button onClick={onClose} style={{ width:"100%", padding:14, marginTop:10, background:"transparent", border:`1.5px solid ${t.border}`, borderRadius:16, color:t.t3, fontSize:14, cursor:"pointer" }}>Cancel</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── REBOOK WIDGET ────────────────────────────────────────────────────────────
// ─── LOYALTY STAMP CARD ───────────────────────────────────────────────────────
const RazorIcon = ({ lit }) => (
  <svg aria-hidden="true" focusable="false"width="13" height="13" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id={lit?"rbl":"rbd"} x1="8" y1="13" x2="30" y2="23" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor={lit?"#e8f0f8":"#3a3a3a"}/>
        <stop offset="100%" stopColor={lit?"#8aa0b8":"#1a1a1a"}/>
      </linearGradient>
    </defs>
    <path d="M10 18 L28 12.5 L31 18 L28 23.5 L10 18Z" fill={`url(#${lit?"rbl":"rbd"})`} opacity={lit?0.95:0.5}/>
    <line x1="10" y1="18" x2="30" y2="18" stroke={lit?"rgba(255,255,255,0.25)":"rgba(255,255,255,0.05)"} strokeWidth="0.5"/>
    <rect x="6" y="16" width="5" height="4" rx="1.5" fill={lit?"#99aabb":"#2a2a2a"} opacity={lit?0.9:0.5}/>
    <circle cx="9" cy="18" r="1.2" fill={lit?"rgba(255,255,255,0.6)":"rgba(255,255,255,0.12)"}/>
  </svg>
);

const LoyaltyStampCard = React.memo(function LoyaltyStampCard({ bookings, barbers }) {
  const { t } = useT();
  const completed = bookings.filter(b => b.status === "completed");
  if (completed.length === 0) return null;

  const byBarber = completed.reduce((acc, b) => { acc[b.barber_id] = (acc[b.barber_id] || 0) + 1; return acc; }, {});
  const topBarberId = Object.entries(byBarber).sort((a, b) => b[1] - a[1])[0]?.[0];
  const topBarber = barbers.find(b => b.id === topBarberId);
  const barberCuts = byBarber[topBarberId] || 0;
  const THRESHOLD = 10;
  const stamps = barberCuts % THRESHOLD;
  const totalRewards = Math.floor(barberCuts / THRESHOLD);
  const a = topBarber?.accent_color || "#2563EB";

  return (
    <div style={{ background:"#0e0e10", borderRadius:14, padding:"10px 12px", border:"1px solid rgba(255,255,255,0.06)", marginBottom:12, boxShadow:"0 2px 12px rgba(0,0,0,0.4)" }}>
      {/* header row */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <div style={{ fontSize:11, fontWeight:800, color:"#bbb", letterSpacing:"0.05em", textTransform:"uppercase" }}>Loyalty</div>
          {topBarber && <div style={{ fontSize:11, color:"#444" }}>· {topBarber.full_name}</div>}
        </div>
        <div style={{ display:"flex", gap:4, alignItems:"center" }}>
          {totalRewards > 0 && <span style={{ fontSize:11, fontWeight:700, color:"#22C55E" }}>🎁 {totalRewards}</span>}
          <span style={{ fontSize:11, color:"#444", fontWeight:600 }}>{stamps}/{THRESHOLD}</span>
        </div>
      </div>

      {/* stamps — fixed 28px knobs so they never blow up on desktop */}
      <div style={{ display:"flex", gap:6, alignItems:"center", marginBottom:8 }}>
        {Array.from({ length: THRESHOLD }).map((_, i) => {
          const lit = i < stamps;
          return (
            <div key={i} style={{
              width:28, height:28, borderRadius:"50%", flexShrink:0,
              background: lit
                ? "radial-gradient(circle at 32% 28%, #c8d8e8 0%, #7a9ab0 35%, #4a6a80 65%, #1e3a50 100%)"
                : "radial-gradient(circle at 32% 28%, #282830 0%, #181820 45%, #0e0e16 100%)",
              boxShadow: lit
                ? `0 0 0 1.5px ${a}, 0 0 8px 3px ${a}66, inset 0 1px 2px rgba(255,255,255,0.2)`
                : "inset 0 2px 4px rgba(0,0,0,0.6)",
              border: lit ? `1px solid ${a}` : "1px solid rgba(255,255,255,0.05)",
              display:"flex", alignItems:"center", justifyContent:"center",
              transition:"all 0.3s ease",
            }}>
              <RazorIcon lit={lit}/>
            </div>
          );
        })}
      </div>

      {/* progress bar */}
      <div style={{ background:"rgba(255,255,255,0.05)", borderRadius:2, height:2, overflow:"hidden" }}>
        <div style={{ height:"100%", width:`${(stamps/THRESHOLD)*100}%`, background:`linear-gradient(90deg,${a},${a}bb)`, borderRadius:2, transition:"width 0.6s ease" }}/>
      </div>
      <div style={{ fontSize:11, color: stamps===0&&totalRewards>0 ? "#22C55E" : "#444", fontWeight:600, marginTop:4, textAlign:"right" }}>
        {stamps===0&&totalRewards>0 ? "Free cut ready! 🎁" : `${THRESHOLD-stamps} more for a free cut`}
      </div>
    </div>
  );
});

// ─── BOOKING MODAL ─────────────────────────────────────────────────────────────
function BookingModal({ barber, session, onClose, userLat, userLng, prefillService, prefillTime, skipToConfirm, rescheduleBookingId, rescheduleFromTime }) {
  useT(); // keep context subscription
  const { isDesktop } = useR();
  const isDark = true;
  const t = createTheme(true); // booking modal is always dark
  const [step, setStep]               = useState(skipToConfirm ? "confirm" : "profile");
  const [time, setTime]               = useState(prefillTime || null);
  const stepRef = useRef(step);
  const doneRef = useRef(false);
  const detailsRef = useRef(null);
  const idempotencyKeyRef = useRef(crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);
  const [clientSavedCard, setClientSavedCard] = useState(null); // { pmId, brand, last4, customerId }
  useEffect(() => { stepRef.current = step; }, [step]);
  useEffect(() => {
    if (!session?.user?.id || barber._isDemo) return;
    supabase.from("profiles")
      .select("default_payment_method_id,default_card_brand,default_card_last4,stripe_customer_id")
      .eq("id", session.user.id).single()
      .then(({ data }) => {
        if (data?.default_payment_method_id) {
          setClientSavedCard({ pmId: data.default_payment_method_id, brand: data.default_card_brand, last4: data.default_card_last4, customerId: data.stripe_customer_id });
        }
      });
  }, [session?.user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    return () => {
      if (!doneRef.current && stepRef.current !== "profile") {
        track("booking_abandoned", { step_abandoned: stepRef.current });
      }
      destroyCardElement();
      destroySetupCardElement();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const modalScrollRef = useRef(null);
  useEffect(() => { modalScrollRef.current?.scrollTo({ top:0, behavior:"instant" }); }, [step]);

  // Fetch the barber's manually-blocked vacation/personal days so clients see those
  // dates as closed instead of just seeing an empty, unexplained time list.
  useEffect(() => {
    if (barber._isDemo) return;
    supabase.from("barber_blocked_dates")
      .select("date, reason")
      .eq("barber_id", barber.id)
      .gte("date", new Date().toISOString().slice(0, 10))
      .then(({ data }) => setBarberBlockedDates(data || []));
  }, [barber.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch already-booked slots for this barber on the selected date so we can grey them out
  useEffect(() => {
    if (step !== "time" || barber._isDemo) return;
    const selDate = time?.split("|")[0] || (() => { const d=new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; })();
    const start = new Date(selDate + "T00:00:00").toISOString();
    const end   = new Date(selDate + "T23:59:59").toISOString();
    supabase.from("bookings")
      .select("scheduled_at, duration_minutes, service_type")
      .eq("barber_id", barber.id)
      .in("status", ["pending", "accepted", "confirmed"])
      .gte("scheduled_at", start)
      .lte("scheduled_at", end)
      .then(async ({ data }) => {
        if (!data) return;
        // Fetch buffer minutes for each unique service so we can pad blocked ranges
        const svcNames = [...new Set(data.map(b => b.service_type).filter(Boolean))];
        let bufferMap = {};
        if (svcNames.length > 0) {
          const { data: svcData } = await supabase.from("barber_services")
            .select("name, buffer_minutes").eq("barber_id", barber.id).in("name", svcNames);
          (svcData || []).forEach(s => { bufferMap[s.name] = s.buffer_minutes || 0; });
        }
        const mins = [];
        data.forEach(b => {
          const bStart = new Date(b.scheduled_at);
          const dur = (b.duration_minutes || 30) + (bufferMap[b.service_type] || 0);
          for (let m = 0; m < dur; m += 15) {
            const slotDate = new Date(bStart.getTime() + m * 60000);
            mins.push(slotDate.getHours() * 60 + slotDate.getMinutes());
          }
        });
        setBookedSlotMins(mins);
      });
  }, [step, time, barber.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const [barberServices, setBarberServices] = useState([]);
  const [servicesLoaded, setServicesLoaded] = useState(false);
  const [selectedService, setSelectedService] = useState(prefillService || (barber.specialty||[])[0]||"Haircut");
  const [selectedAddons, setSelectedAddons]   = useState([]);
  const [clientName, setClientName]   = useState(session?.user?.user_metadata?.full_name||"");
  const [clientPhone, setClientPhone] = useState("");
  const [recurrence, setRecurrence]   = useState("none");
  const [myNoShows, setMyNoShows]     = useState(0);
  const [payment, setPayment]         = useState(null);

  // Stripe card element — must be declared after `payment` state
  const cardElementRef = useRef(null);
  const [cardError, setCardError]     = useState("");
  const [retryBookingId, setRetryBookingId] = useState(null); // holds pending booking_id for card retry after decline
  const cardCleanupRef = useRef(null);
  useEffect(() => {
    if (payment !== "Credit / Debit Card" || !STRIPE_AVAILABLE) return;
    const t = setTimeout(() => {
      if (!cardElementRef.current) return;
      mountCardElement(cardElementRef.current).then(cleanup => { cardCleanupRef.current = cleanup; });
    }, 80);
    return () => { clearTimeout(t); cardCleanupRef.current?.(); };
  }, [payment]); // eslint-disable-line react-hooks/exhaustive-deps

  // Payment Request Button (Apple Pay / Google Pay)
  const prButtonRef = useRef(null);
  const prCleanupRef = useRef(null);
  const prPayEvRef = useRef(null); // stores the paymentmethod event from Apple/Google Pay
  const bookingSecuredRef = useRef(false); // true once a booking row actually exists — guards against showing "join waitlist" after a later, unrelated crash
  const [prCanPay, setPrCanPay] = useState(null); // null=checking, false=unavailable, true=available
  const isWalletPayment = payment === "Apple Pay" || payment === "Google Pay";
  useEffect(() => {
    if (!isWalletPayment || !STRIPE_AVAILABLE || step !== "confirm") { setPrCanPay(null); return; }
    const t = setTimeout(() => {
      if (!prButtonRef.current) return;
      mountPaymentRequestButton(prButtonRef.current, {
        amountCents: Math.round(finalTotal * 100),
        serviceLabel: selectedService || "Haircut",
        onPaymentMethod: (ev) => { prPayEvRef.current = ev; handleConfirm(); },
      }).then(({ canPay, cleanup }) => {
        setPrCanPay(!!canPay);
        prCleanupRef.current = cleanup;
      });
    }, 80);
    return () => { clearTimeout(t); prCleanupRef.current?.(); setPrCanPay(null); };
  }, [isWalletPayment, step]); // eslint-disable-line react-hooks/exhaustive-deps

  const [booking, setBooking]         = useState(false);
  const [done, setDone]               = useState(false);
  const [doubleBookErr, setDoubleBookErr] = useState(false);
  const [doubleBookMsg, setDoubleBookMsg] = useState("");
  const [waitlistJoined, setWaitlistJoined] = useState(false);
  const [joiningWaitlist, setJoiningWaitlist] = useState(false);
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [barberReviews, setBarberReviews] = useState([]);
  const [notifAsked, setNotifAsked] = useState(false);
  const [bookedSlotMins, setBookedSlotMins] = useState([]); // minutes-since-midnight for each booked slot on selected date
  const [barberBlockedDates, setBarberBlockedDates] = useState([]); // [{date, reason}] — barber's manually-blocked vacation/personal days
  const [gcCode, setGcCode]       = useState("");
  const [gcApplied, setGcApplied] = useState(null); // { id, amount_cents, code }
  const [gcLoading, setGcLoading] = useState(false);
  const [gcError, setGcError]     = useState("");

  // No-show protection card-on-file step + future card booking card save
  const setupCardRef = useRef(null);
  const setupCleanupRef = useRef(null);
  const [nspBookingId, setNspBookingId] = useState(null);
  const [nspSavingCard, setNspSavingCard] = useState(false);
  const [nspCardError, setNspCardError] = useState("");
  // "nsp" = cash booking card-on-file for no-show protection
  // "future_card" = card booking >24h away — save card now, hold placed day before
  const [savecardType, setSavecardType] = useState("nsp");
  useEffect(() => {
    if (step !== "savecard" || !STRIPE_AVAILABLE) return;
    const t = setTimeout(() => {
      if (!setupCardRef.current) return;
      mountSetupCardElement(setupCardRef.current).then(cleanup => { setupCleanupRef.current = cleanup; });
    }, 80);
    return () => { clearTimeout(t); setupCleanupRef.current?.(); };
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  const [slotShoppingWarn, setSlotShoppingWarn] = useState(false);
  const [slotShoppingDismissed, setSlotShoppingDismissed] = useState(false);
  const [walkinQueueDepth, setWalkinQueueDepth] = useState(0);
  const [walkinJoining, setWalkinJoining] = useState(false);
  const [walkinErr, setWalkinErr] = useState("");
  const [walkinSheetOpen, setWalkinSheetOpen] = useState(false);
  const [walkinSuccess, setWalkinSuccess] = useState(false);

  const mapLinks = (() => {
    const dest = barber.lat && barber.lng
      ? `${barber.lat},${barber.lng}`
      : encodeURIComponent(barber.address || "");
    const addr = encodeURIComponent(barber.address || `${barber.lat},${barber.lng}`);
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    return [
      ...(isIOS ? [{ label: "Apple Maps", emoji: "🍎", url: `maps://?daddr=${addr}` }] : []),
      { label: "Google Maps", emoji: "🗺️", url: `https://www.google.com/maps/dir/?api=1&destination=${dest}` },
      { label: "Waze",        emoji: "🚗", url: barber.lat && barber.lng ? `waze://ul?ll=${barber.lat},${barber.lng}&navigate=yes` : `https://waze.com/ul?q=${addr}&navigate=yes` },
    ];
  })();
  const a  = barber.accent_color||"#2563EB";
  // Fresh GPS for this booking — resolved once when modal opens, doesn't affect app-level GPS
  const liveLatRef = useRef(userLat);
  const liveLngRef = useRef(userLng);
  useEffect(() => {
    getPosition({ enableHighAccuracy:true, timeout:8000, maximumAge:30000 })
      .then(pos => { liveLatRef.current = pos.coords.latitude; liveLngRef.current = pos.coords.longitude; })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    supabase.from("barber_services").select("*")
      .eq("barber_id", barber.id).eq("is_active", true)
      .order("sort_order", { ascending: true })
      .then(({ data }) => {
        if (data && data.length > 0) {
          setBarberServices(data);
          if (!prefillService) setSelectedService(data[0].name);
        }
        setServicesLoaded(true);
      }).catch(() => { setServicesLoaded(true); });
    // Fetch real reviews for this barber (only for non-demo barbers)
    if (!barber._isDemo) {
      supabase.from("reviews")
        .select("id, rating, comment, created_at, cut_photo_url, barber_reply, profiles(full_name)")
        .eq("barber_id", barber.id)
        .order("created_at", { ascending: false })
        .limit(10)
        .then(({ data }) => { if (data) setBarberReviews(data); })
        .catch(() => {});
    }
    // Check client's no-show history with this barber
    if (session?.user?.id) {
      supabase.from("bookings").select("id", { count: "exact", head: true })
        .eq("client_id", session.user.id).eq("barber_id", barber.id).eq("status", "no_show")
        .then(({ count }) => setMyNoShows(count || 0)).catch(() => {});
    }
    // Fetch live walk-in queue depth
    if (barber.walkin) {
      supabase.from("walk_in_queue").select("id", { count: "exact", head: true })
        .eq("barber_id", barber.id).eq("status", "waiting")
        .then(({ count }) => setWalkinQueueDepth(count || 0)).catch(() => {});
    }
  }, [barber.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedSvcObj   = barberServices.find(s => s.name === selectedService);
  const selectedAddonObjs = barberServices.filter(s => selectedAddons.includes(s.name));
  const selectedPrice    = (selectedSvcObj ? Number(selectedSvcObj.price) : barber.price_min || 0)
                         + selectedAddonObjs.reduce((sum, s) => sum + Number(s.price || 0), 0);
  const selectedDuration = (selectedSvcObj?.duration_minutes || 30)
                         + selectedAddonObjs.reduce((sum, s) => sum + (s.duration_minutes || 0), 0);
  const allServiceLabel  = [selectedService, ...selectedAddons].filter(Boolean).join(" + ");

  // ── Fee calculation ───────────────────────────────────────────────────────
  // Barber always receives selectedPrice in full.
  // Stripe fee passed to client on card payments.
  // $2 FADED app fee — deliberately OFF while we're still building the user base
  // (2026-08-09). Flip FADED_FEE_ENABLED back to true (and keep this in sync with
  // the server-side flag in supabase/functions/stripe-create-payment-intent) to
  // re-enable; FADED_FEE_ACTIVE_DATE is kept for when we pick a real re-enable date.
  const isCardPayment = ["Credit / Debit Card", "Interac e-Transfer", "Apple Pay", "Google Pay"].includes(payment);
  const FADED_FEE_ENABLED = false;
  const FADED_FEE_ACTIVE_DATE = new Date("2026-07-24");
  const FADED_FEE_AMOUNT = 2.00;
  const fadedFeeActive = FADED_FEE_ENABLED && new Date() >= FADED_FEE_ACTIVE_DATE;
  const fadedFeeAmt = fadedFeeActive ? FADED_FEE_AMOUNT : 0;
  // HST: applied when barber is HST-registered; rate based on their province (default ON 13%)
  const hstRate = barber.hst_registered ? (HST_BY_PROVINCE[barber.province || "ON"] || 0.13) : 0;
  const hstAmt  = barber.hst_registered ? Math.round(selectedPrice * hstRate * 100) / 100 : 0;
  const priceWithHst = selectedPrice + hstAmt;
  // Card/Interac: clientTotal = (barberPrice + hst + fadedFee + stripeFlatFee) / (1 - stripePercent)
  // Cash only: clientTotal = barberPrice + hst + fadedFee
  const clientTotal = isCardPayment
    ? Math.ceil(((priceWithHst + fadedFeeAmt + 0.30) / (1 - 0.029)) * 100) / 100
    : priceWithHst + fadedFeeAmt;
  const stripeFeeAmt = isCardPayment
    ? Math.round((clientTotal - priceWithHst - fadedFeeAmt) * 100) / 100
    : 0;
  // ── End fee calculation ───────────────────────────────────────────────────
  const gcDiscount = gcApplied ? Math.min(gcApplied.amount_cents / 100, clientTotal) : 0;
  const finalTotal = Math.max(0, clientTotal - gcDiscount);

  const bg = t.BG_FOR[a]||t.blueBg;
  const PAYMENTS = ["Interac","Apple Pay","Google Pay","Card","Cash"];
  const _nowDate  = new Date();
  const _dpad     = n => String(n).padStart(2,"0");
  const _todayLocal = `${_nowDate.getFullYear()}-${_dpad(_nowDate.getMonth()+1)}-${_dpad(_nowDate.getDate())}`;
  const DATES    = Array.from({length: Math.min(barber.booking_window_days || 14, 60)}).map((_,i) => {
    const d = new Date(); d.setDate(d.getDate()+i);
    const value = `${d.getFullYear()}-${_dpad(d.getMonth()+1)}-${_dpad(d.getDate())}`;
    return { value, label:i===0?"Today":i===1?"Tomorrow":d.toLocaleDateString("en-CA",{weekday:"short",month:"short",day:"numeric"}) };
  });
  const _selDate  = time?.split("|")[0] || DATES[0].value;
  const _isToday  = _selDate === _todayLocal;
  const _nowMins  = _nowDate.getHours()*60 + _nowDate.getMinutes() + 30;
  const _slotMins = s => { const [tp,ap]=s.split(" "); let [h,m]=tp.split(":").map(Number); if(ap==="PM"&&h!==12)h+=12; if(ap==="AM"&&h===12)h=0; return h*60+m; };
  // Respect barber's working_hours for the selected day
  const _selDow   = new Date(_selDate + "T12:00:00").getDay(); // 0=Sun,6=Sat
  const _wh       = barber.working_hours;
  const _isHoursConfigured = _wh && Object.keys(_wh).length > 0;
  const _dayHours = _wh ? (_wh[_selDow] ?? null) : null; // null = day off, undefined means no hours set
  const _parseHM  = s => { const [h,m] = (s||"00:00").split(":").map(Number); return h*60+m; };
  const _barberStart = _dayHours ? _parseHM(_dayHours.start) : 0;
  const _barberEnd   = _dayHours ? _parseHM(_dayHours.end)   : 24*60;
  const _vacationDay = barberBlockedDates.find(bd => bd.date === _selDate);
  const _isVacationDay = !!_vacationDay;
  const _isDayClosed = (_isHoursConfigured && _dayHours === null) || _isVacationDay;
  // Generate 15-min slots from 8:00 AM to 8:45 PM
  const _ALL_SLOTS = (() => {
    const slots = [];
    for (let m = 8*60; m <= 20*60+45; m += 15) {
      const h24 = Math.floor(m / 60);
      const min = m % 60;
      const ampm = h24 < 12 ? "AM" : "PM";
      const h12  = h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24;
      slots.push(`${h12}:${String(min).padStart(2,"0")} ${ampm}`);
    }
    return slots;
  })();
  const TIMES = [
    ...(_isToday && barber.status === "available" && !_isVacationDay ? ["Now — Available"] : []),
    ..._ALL_SLOTS.filter(tm => {
      if (!_isHoursConfigured || _isVacationDay) return false; // no hours configured, or barber blocked this day off
      if (_isToday && _slotMins(tm) <= _nowMins) return false;
      if (bookedSlotMins.includes(_slotMins(tm))) return false;
      if (_dayHours && (_slotMins(tm) < _barberStart || _slotMins(tm) >= _barberEnd)) return false;
      return true;
    }),
  ];
  const _isFullyBookedToday = _isToday && TIMES.length === 0;
  const GRID_COLORS = ["#EFF6FF","#ECFDF5","#FFFBEB","#F5F3FF","#FEF2F2","#F0FDFA"];
  const dist = distFmt(barber, userLat, userLng);

  const _specialtyArr = Array.isArray(barber.specialty) ? barber.specialty : typeof barber.specialty === "string" && barber.specialty ? barber.specialty.split(",").map(s => s.trim()).filter(Boolean) : [];
  const MAIN_SERVICES = barberServices.length > 0
    ? barberServices.filter(s => !s.is_addon)
    : _specialtyArr.map(n => ({ name: n, price: barber.price_min, duration_minutes: 30 }));
  const ADDON_SERVICES = barberServices.filter(s => s.is_addon && s.is_active);
  const SERVICES = MAIN_SERVICES; // kept for any remaining references

  const scheduledAt = (() => {
    if (!time || !time.includes("|")) return new Date().toISOString();
    const [dateStr, tmStr] = time.split("|");
    const [yr, mo, dy] = dateStr.split("-").map(Number);
    if (!tmStr || tmStr === "Now — Available") {
      // Use local date constructor — avoids UTC-midnight timezone shift
      return new Date(yr, mo - 1, dy, new Date().getHours(), new Date().getMinutes(), 0, 0).toISOString();
    }
    const [tPart, ampm] = tmStr.split(" ");
    let [h, m] = tPart.split(":").map(Number);
    if (ampm === "PM" && h !== 12) h += 12;
    if (ampm === "AM" && h === 12) h = 0;
    // new Date(year, month-1, day, h, m) is ALWAYS local time — no UTC shift
    return new Date(yr, mo - 1, dy, h, m, 0, 0).toISOString();
  })();

  const joinWaitlist = async () => {
    if (!session || session === "guest" || joiningWaitlist) return;
    setJoiningWaitlist(true);
    try {
      const { error: wlError } = await supabase.from("waitlist").insert({
        barber_id: barber.id,
        client_id: session.user.id,
        client_name: clientName || session.user.user_metadata?.full_name || "Client",
        client_phone: normalizePhone(clientPhone),
        service: selectedService,
        slot_time: scheduledAt,
        status: "waiting",
      });
      if (!wlError) { setWaitlistJoined(true); track("walkin_queue_joined", { barber_id: barber.id }); }
    } catch (e) {
      console.error("[FADED] joinWaitlist:", e);
    } finally {
      setJoiningWaitlist(false);
    }
  };

  const applyGiftCard = async () => {
    if (!gcCode.trim()) return;
    setGcLoading(true);
    setGcError("");
    const { data, error } = await supabase
      .rpc("lookup_gift_card", { p_code: gcCode.trim() })
      .maybeSingle();
    setGcLoading(false);
    if (error || !data) { setGcError("Invalid or already used code."); return; }
    if (data.barber_id && data.barber_id !== barber.id) { setGcError("This gift card is for a different barber."); return; }
    setGcApplied(data);
  };

  const handleConfirm = async () => {
    setDoubleBookErr(false);
    bookingSecuredRef.current = false;
    setBooking(true);
    try { await _handleConfirmInner(); } catch (err) {
      setBooking(false);
      try { localStorage.removeItem("booking_in_progress"); } catch {}
      Sentry.captureException(err, { tags: { flow:"booking", step:"confirm_inner" } });
      if (bookingSecuredRef.current) {
        // The booking (and any payment) already succeeded — a later, unrelated step
        // threw (e.g. a notification side-effect). Never tell the user to "join the
        // waitlist" here, that would be actively wrong and re-charge risk on retry.
        setDone(true);
        doneRef.current = true;
        haptic.success();
        return;
      }
      setDoubleBookErr(true);
      setDoubleBookMsg("Something went wrong — please try again.");
    }
  };
  const _handleConfirmInner = async () => {
    // Declared here (not inside the logged-in branch below) because shared code after
    // the guest/logged-in branches merge back together reads result?.booking_id --
    // for a guest checkout that branch never runs, so result was never defined and
    // threw "Can't find variable: result" on every guest booking (Sentry FADED-S).
    let result = null;
    Sentry.addBreadcrumb({ category:"booking", message:"try_book_slot called", level:"info" });
    try { localStorage.setItem("booking_in_progress", "true"); } catch {}
    if (!session || session === "guest") {
      // Guest checkout — direct insert, no account required
      if (!clientName.trim() || !clientPhone.trim()) {
        setBooking(false);
        try { localStorage.removeItem("booking_in_progress"); } catch {}
        setDoubleBookErr(true);
        setDoubleBookMsg("Please enter your name and phone number to book as a guest.");
        return;
      }
      // C-BUG-1: check pause state before guest insert (guest path bypasses the auth'd live-check)
      const { data: guestPauseCheck } = await supabase.from("barbers").select("bookings_paused").eq("id", barber.id).single();
      if (guestPauseCheck?.bookings_paused) {
        setBooking(false);
        try { localStorage.removeItem("booking_in_progress"); } catch {}
        setDoubleBookErr(true);
        setDoubleBookMsg(`${barber.full_name?.split(" ")[0] || "This barber"} is not accepting bookings right now.`);
        return;
      }
      const { data: guestResult, error: guestErr } = await supabase.rpc("try_book_slot_guest", {
        p_barber_id: barber.id,
        p_service_type: allServiceLabel,
        p_scheduled_at: scheduledAt,
        p_duration_minutes: selectedSvcObj?.duration_minutes || 30,
        p_price_agreed: selectedPrice,
        p_payment_method: payment,
        p_barber_name: barber.full_name,
        p_client_name: sanitize(clientName, 100),
        p_client_phone: normalizePhone(clientPhone),
        p_client_lat: liveLatRef.current,
        p_client_lng: liveLngRef.current,
        p_client_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      if (guestErr || !guestResult?.success) {
        setBooking(false);
        try { localStorage.removeItem("booking_in_progress"); } catch {}
        setDoubleBookErr(true);
        setDoubleBookMsg(guestResult?.message || "Booking failed. Please try again or create a free account.");
        return;
      }
      bookingSecuredRef.current = true;
      result = guestResult;
      notifyBarberOfBooking({ barber_id: barber.id, client_name: clientName, service_type: selectedService, scheduled_at: scheduledAt, booking_id: guestResult?.booking_id, is_live: !scheduledAt });
      if (clientPhone) {
        const timeLabel = time?.split("|")[1] || "Now";
        sendSMS(normalizePhone(clientPhone), `✂️ FADED: ${selectedService} with ${barber.full_name} booked for ${timeLabel}. Awaiting barber confirmation.`);
      }
    } else if (session && session !== "guest" && session.user && !barber._isDemo) {
      const duration = selectedDuration;

      // Confirm barber hasn't paused bookings or gone offline since profile was loaded
      const { data: liveCheck } = await supabase.from("barbers").select("status, bookings_paused").eq("id", barber.id).single();
      if (liveCheck?.bookings_paused) {
        setBooking(false);
        try { localStorage.removeItem("booking_in_progress"); } catch {}
        setDoubleBookErr(true);
        setDoubleBookMsg(`${barber.full_name?.split(" ")[0] || "This barber"} is not accepting bookings right now.`);
        return;
      }
      if (!scheduledAt || (new Date(scheduledAt).getTime() - Date.now()) < 2 * 60 * 60 * 1000) {
        if (liveCheck && liveCheck.status === "offline") {
          setBooking(false);
          try { localStorage.removeItem("booking_in_progress"); } catch {}
          setDoubleBookErr(true);
          setDoubleBookMsg(`${barber.full_name?.split(" ")[0] || "This barber"} just went offline. Book a future appointment or find another barber.`);
          return;
        }
      }

      // Slot shopping check — warn if client has 2+ active bookings within ±1h of this slot
      if (!slotShoppingDismissed && scheduledAt) {
        const _ssWindow = 60 * 60 * 1000;
        const _ssCenter = new Date(scheduledAt).getTime();
        const { data: _ssData } = await supabase
          .from("bookings")
          .select("id")
          .eq("client_id", session.user.id)
          .in("status", ["pending", "accepted", "confirmed"])
          .gte("scheduled_at", new Date(_ssCenter - _ssWindow).toISOString())
          .lte("scheduled_at", new Date(_ssCenter + _ssWindow).toISOString());
        if (_ssData && _ssData.length >= 2) {
          setBooking(false);
          try { localStorage.removeItem("booking_in_progress"); } catch {}
          setSlotShoppingWarn(true);
          return;
        }
      }

      // Atomic DB-level booking (or reschedule) — advisory lock prevents race conditions,
      // proper interval overlap catches back-to-back and partial overlaps,
      // and manual_appointments are checked in the same transaction.
      // reschedule_booking atomically cancels the old slot + creates the new one.
      const rpcName = rescheduleBookingId ? "reschedule_booking" : "try_book_slot";
      const rpcArgs = rescheduleBookingId
        ? {
            p_old_booking_id:   rescheduleBookingId,
            p_client_id:        session.user.id,
            p_barber_id:        barber.id,
            p_service_type:     allServiceLabel,
            p_new_scheduled_at: scheduledAt,
            p_duration_minutes: duration,
            p_price_agreed:     selectedPrice,
            p_payment_method:   payment,
            p_barber_name:      barber.full_name,
            p_client_name:      clientName,
            p_client_phone:     normalizePhone(clientPhone),
            p_client_email:     session.user.email,
            p_client_lat:       liveLatRef.current,
            p_client_lng:       liveLngRef.current,
          }
        : {
            p_client_id:        session.user.id,
            p_barber_id:        barber.id,
            p_service_type:     allServiceLabel,
            p_scheduled_at:     scheduledAt,
            p_duration_minutes: duration,
            p_price_agreed:     selectedPrice,
            p_payment_method:   payment,
            p_barber_name:      barber.full_name,
            p_client_name:      clientName,
            p_client_phone:     normalizePhone(clientPhone),
            p_client_email:     session.user.email,
            p_client_lat:       liveLatRef.current,
            p_client_lng:       liveLngRef.current,
            p_idempotency_key:  idempotencyKeyRef.current,
          };
      const { data: rpcResult, error: rpcError } = await supabase.rpc(rpcName, rpcArgs);
      result = rpcResult;
      if (rpcError || (!result?.success && !result?.duplicate)) {
        if (rpcError) Sentry.captureException(rpcError, { tags: { flow: "booking", rpc: rpcName } });
        Sentry.addBreadcrumb({ category:"booking", message:`Booking failed: ${result?.message || rpcError?.message}`, level:"error" });
        setBooking(false);
        try { localStorage.removeItem("booking_in_progress"); } catch {}
        const errCode = result?.error || "";
        const msg = result?.message || "";
        if (errCode === "service_unavailable") {
          setDoubleBookMsg(`${barber.full_name} just paused that service. Pick another — they're still open.`);
          setDoubleBookErr(true);
          setStep("service");
          return;
        }
        if (errCode === "client_blocked_noshows") {
          setDoubleBookMsg(msg || `${barber.full_name} has turned off bookings for your account after repeated no-shows. Reach out to them directly.`);
          setDoubleBookErr(true);
          return;
        }
        if (errCode === "reschedule_limit") {
          setDoubleBookMsg("You've rescheduled this booking twice already. Please cancel and book a new appointment.");
          setDoubleBookErr(true);
          return;
        }
        if (errCode === "invalid_status") {
          setDoubleBookMsg("This booking can no longer be rescheduled (it may have been cancelled or completed).");
          setDoubleBookErr(true);
          return;
        }
        const alreadyMine = msg.toLowerCase().includes("already") || msg.toLowerCase().includes("exist");
        if (alreadyMine) {
          setDoubleBookMsg("You already have a booking with this barber at this time.");
          setDoubleBookErr(true);
          return;
        }
        setDoubleBookErr(true);
        setDoubleBookMsg(msg || "That slot is unavailable — please pick a different time.");
        return;
      }
      bookingSecuredRef.current = true;
      // Reschedule success — SMS barber (old slot was already atomically cancelled by the RPC)
      if (rescheduleBookingId && result?.success && barber.phone) {
        const oldLabel = rescheduleFromTime ? formatLocalTime(rescheduleFromTime) : "previous slot";
        const newLabel = scheduledAt ? formatLocalTime(scheduledAt) : "a new slot";
        sendSMS(barber.phone, `FADED: ${clientName || "A client"} rescheduled from ${oldLabel} to ${newLabel}. ✂️`);
      }

      // Determine if appointment is same-day (<24h away) or future
      const apptMs = scheduledAt ? new Date(scheduledAt).getTime() : Date.now();
      const isFutureBooking = (apptMs - Date.now()) > 24 * 60 * 60 * 1000;

      // Stripe card payment
      if (payment === "Credit / Debit Card" && STRIPE_AVAILABLE && result?.booking_id && finalTotal > 0) {
        if (isFutureBooking) {
          // Future booking: save card now — hold placed 24h before appointment by cron (Uber-style)
          setNspBookingId(result.booking_id);
          setSavecardType("future_card");
          setBooking(false);
          setStep("savecard");
          return;
        } else {
          // Same-day: place hold immediately
          // Verify session is still valid before hitting the edge function
          const { data: { session: freshSession } } = await supabase.auth.getSession();
          if (!freshSession) {
            setBooking(false);
            try { localStorage.removeItem("booking_in_progress"); } catch {}
            setCardError("Your session expired — please sign in again before paying.");
            setStep("confirm");
            return;
          }
          const stripeResult = await confirmCardBooking({
            supabase,
            bookingId: result.booking_id,
            barberId: barber.id,
            amountCents: Math.round(finalTotal * 100),
            tipCents: 0,
            giftCardId: gcApplied?.id ?? null,
          });
          if (!stripeResult.success) {
            track("payment_failed", { service_type: selectedService, error: stripeResult.error, barber_id: barber?.id });
            setBooking(false);
            try { localStorage.removeItem("booking_in_progress"); } catch {}
            // Keep the pending booking alive for 3 min so client can retry with a different card
            setRetryBookingId(result.booking_id);
            setCardError(stripeResult.error || "Payment declined — try a different card.");
            setStep("confirm"); // return to confirm screen (card element visible)
            // Auto-cancel if not retried within 3 minutes
            setTimeout(() => {
              supabase.from("bookings").update({ status:"cancelled", cancelled_at:new Date().toISOString(), decline_reason:"Payment not completed within 3 minutes" }).eq("id", result.booking_id).eq("status","pending").then(() => {});
              setRetryBookingId(null);
            }, 3 * 60 * 1000);
            return;
          }
          setCardError("");
        }
      }

      // Apple Pay / Google Pay — charge immediately via Payment Request Button event
      if (isWalletPayment && STRIPE_AVAILABLE && result?.booking_id && finalTotal > 0 && prPayEvRef.current) {
        const walletResult = await confirmPaymentRequestBooking({
          supabase,
          bookingId: result.booking_id,
          barberId: barber.id,
          amountCents: Math.round(finalTotal * 100),
          tipCents: 0,
          giftCardId: gcApplied?.id ?? null,
          ev: prPayEvRef.current,
        });
        prPayEvRef.current = null;
        if (!walletResult.success) {
          setBooking(false);
          try { localStorage.removeItem("booking_in_progress"); } catch {}
          supabase.from("bookings").update({ status:"cancelled", cancelled_at: new Date().toISOString(), decline_reason:"Wallet payment failed" }).eq("id", result.booking_id).then(() => {});
          setDoubleBookErr(true);
          setDoubleBookMsg(walletResult.error || "Payment failed — please try again.");
          return;
        }
      }

      // Card-on-file for cash/Interac: mandatory for ALL bookings (hotel model)
      const isCashOrInterac = payment === "Cash" || payment === "Interac e-Transfer" || payment === "Pay at Shop";
      const needsNspCard = STRIPE_AVAILABLE && barber.no_show_protection && isCashOrInterac && result?.booking_id;
      // Only forces a card to secure the $2 platform fee — pointless while that fee is off.
      const needsFadedFeeCard = STRIPE_AVAILABLE && fadedFeeActive && isCashOrInterac && !barber.no_show_protection && result?.booking_id;
      if ((needsNspCard || needsFadedFeeCard) && clientSavedCard?.pmId) {
        // Client already has a saved card — attach it and skip the card entry screen
        setBooking(false);
        await supabase.from("bookings").update({
          saved_payment_method_id: clientSavedCard.pmId,
          stripe_customer_id: clientSavedCard.customerId,
        }).eq("id", result.booking_id);
        notifyBarberOfBooking({ barber_id: barber.id, client_name: clientName, service_type: selectedService, scheduled_at: scheduledAt, booking_id: result.booking_id, is_live: !scheduledAt });
        if (clientPhone) {
          const timeLabel = scheduledAt ? formatLocalTime(scheduledAt) : (time?.split("|")[1] || "Now");
          const mapsLink = barber.lat && barber.lng ? `https://maps.google.com/?q=${barber.lat},${barber.lng}` : "";
          sendSMS(normalizePhone(clientPhone), `✂️ FADED: ${selectedService} with ${barber.full_name} booked for ${timeLabel}.${mapsLink ? ` 📍 ${mapsLink}` : ""} – You'll get a confirmation once the barber accepts.`);
        }
        if (scheduledAt) scheduleApptReminder(scheduledAt, barber.full_name, selectedService, result.booking_id);
        setDone(true);
        doneRef.current = true;
        haptic.success();
        track("booking_confirmed", { service_type: selectedService, payment_method: payment || "in_person", nsp: true, card_reused: true });
        try { localStorage.removeItem("booking_in_progress"); } catch {}
        return;
      }
      if (needsNspCard || needsFadedFeeCard) {
        setNspBookingId(result.booking_id);
        setSavecardType(needsNspCard ? "nsp" : "cash_fee");
        setBooking(false);
        setStep("savecard");
        return; // notifications fire after card is saved in handleSaveCard
      }

      // Recurring bookings — fire-and-forget additional slots
      if (recurrence !== "none") {
        const recurGroupId = result?.booking_id || crypto.randomUUID?.() || Date.now().toString();
        const intervals = { weekly:7, biweekly:14, monthly:30 };
        const repeatCount = recurrence === "monthly" ? 2 : 3;
        const baseMs = new Date(scheduledAt).getTime();
        const dayMs = 86400000;
        for (let i = 1; i <= repeatCount; i++) {
          const nextAt = new Date(baseMs + i * intervals[recurrence] * dayMs).toISOString();
          supabase.rpc("try_book_slot", {
            p_client_id: session.user.id, p_barber_id: barber.id,
            p_service_type: selectedService, p_scheduled_at: nextAt,
            p_duration_minutes: duration, p_price_agreed: selectedPrice,
            p_payment_method: payment, p_barber_name: barber.full_name,
            p_client_name: clientName, p_client_phone: normalizePhone(clientPhone),
            p_client_email: session.user.email,
            p_client_lat: liveLatRef.current, p_client_lng: liveLngRef.current,
          }).then(({ data: rd }) => {
            if (rd?.success && rd?.booking_id) {
              supabase.from("bookings").update({ recurrence, recurrence_group: recurGroupId }).eq("id", rd.booking_id).then(() => {});
            }
          }).catch(() => {});
        }
      }

      // Store client timezone on the primary booking
      if (result?.booking_id) {
        supabase.from("bookings").update({ client_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone }).eq("id", result.booking_id).then(() => {});
      }

      // Notify barber via push notification
      notifyBarberOfBooking({ barber_id: barber.id, client_name: clientName, service_type: selectedService, scheduled_at: scheduledAt, booking_id: result.booking_id, is_live: !scheduledAt });

      // Booking confirmation email to client — fires immediately at submission
      if (session.user.email && result?.booking_id) {
        const bkRef = `FD-${String(result.booking_id).slice(-4).padStart(4,"0")}${(barber.full_name||"XX").split(" ").map(w=>w[0]).join("").toUpperCase().slice(0,2)}`;
        supabase.functions.invoke("send-booking-confirmation", {
          body: {
            client_email:      session.user.email,
            client_name:       clientName,
            barber_name:       barber.full_name,
            service:           allServiceLabel,
            scheduled_at:      scheduledAt,
            display_time:      scheduledAt ? formatLocalTime(scheduledAt) : "Now — walk-in",
            address:           barber.address || "",
            lat:               barber.lat,
            lng:               barber.lng,
            booking_ref:       bkRef,
            duration_minutes:  selectedDuration,
            booking_id:        result.booking_id,
          },
        }).catch(() => {});
        // Mark email sent so we don't double-send on barber accept
        supabase.from("bookings").update({ booking_email_sent: true }).eq("id", result.booking_id).then(() => {});
      }

      // SMS to client
      if (clientPhone) {
        const timeLabel = scheduledAt ? formatLocalTime(scheduledAt) : (time?.split("|")[1] || "Now");
        const mapsLink = barber.lat && barber.lng
          ? `https://maps.google.com/?q=${barber.lat},${barber.lng}`
          : barber.address ? `https://maps.google.com/?q=${encodeURIComponent(barber.address)}` : "";
        const bkRef = result?.booking_id ? `FD-${String(result.booking_id).slice(-4).padStart(4,"0")}${(barber.full_name||"XX").split(" ").map(w=>w[0]).join("").toUpperCase().slice(0,2)}` : "";
        const smsText = `✂️ FADED: ${selectedService} with ${barber.full_name} booked for ${timeLabel} (your local time).${mapsLink ? ` 📍 ${mapsLink}` : ""}${bkRef ? ` Ref: ${bkRef}` : ""} – You'll get a confirmation once the barber accepts.`;
        sendSMS(normalizePhone(clientPhone), smsText);
      }
    }
    // Save booking to localStorage for guests and demo barbers — so it shows in appointments.
    // Skipped for signed-in real bookings: those already have a real DB row (result.booking_id),
    // and this shadow copy's fabricated "local-<timestamp>" id never matches the guest-migration
    // reconciliation check below, so it would sit as a permanent frozen-"pending" phantom entry
    // stacked next to the real (and possibly since-accepted) booking.
    if (!session || session === "guest" || barber._isDemo) {
      try {
        const bkRecord = {
          id: `local-${Date.now()}`,
          barber_id: barber.id,
          barber_name: barber.full_name || barber.handle || "Barber",
          barber_handle: barber.handle || "",
          service_type: allServiceLabel,
          scheduled_at: scheduledAt || new Date().toISOString(),
          payment_method: payment || "In person",
          price_agreed: selectedPrice,
          status: barber._isDemo ? "confirmed" : "pending",
          client_name: clientName,
          created_at: new Date().toISOString(),
          _local: true,
        };
        const existing = JSON.parse(localStorage.getItem("faded_local_bookings") || "[]");
        localStorage.setItem("faded_local_bookings", JSON.stringify([bkRecord, ...existing].slice(0, 20)));
      } catch {}
    }
    // Redeem gift card if one was applied
    if (gcApplied && session && session !== "guest") {
      supabase.from("gift_cards").update({ redeemed_by: session.user.id, redeemed_at: new Date().toISOString() }).eq("id", gcApplied.id).then(() => {});
    }
    Sentry.addBreadcrumb({ category:"booking", message:"Booking confirmed", level:"info" });
    if (scheduledAt) scheduleApptReminder(scheduledAt, barber.full_name, selectedService, result?.booking_id || `local-${Date.now()}`);
    setTimeout(()=>{ setBooking(false); setDone(true); doneRef.current = true; haptic.success(); track("booking_confirmed", { service_type: selectedService, payment_method: payment || "in_person" }); try { localStorage.removeItem("booking_in_progress"); } catch {} }, 600);
  };

  const handleSaveCard = async (skip = false) => {
    // cash_fee type: if client declines card, cancel the booking instead
    if (skip && savecardType === "cash_fee") {
      if (nspBookingId) {
        supabase.from("bookings").update({ status: "cancelled", cancelled_at: new Date().toISOString(), decline_reason: "Client declined card requirement" }).eq("id", nspBookingId).then(() => {});
      }
      onClose();
      return;
    }
    setNspSavingCard(true);
    setNspCardError("");
    try {
      if (!skip && nspBookingId && STRIPE_AVAILABLE) {
        const { data: { session: freshSession } } = await supabase.auth.getSession();
        if (!freshSession) {
          setNspCardError("Your session expired — please sign in again before saving your card.");
          setNspSavingCard(false);
          return;
        }
        const result = await confirmSetupIntent({ supabase, bookingId: nspBookingId });
        if (!result.success) {
          setNspCardError(result.error || "Card could not be saved — please try again.");
          setNspSavingCard(false);
          return;
        }
        // Persist the saved payment method id on the booking
        await supabase.from("bookings").update({ saved_payment_method_id: result.paymentMethodId }).eq("id", nspBookingId);
        // Also save to profile so future bookings skip card entry
        if (session?.user?.id) {
          supabase.from("profiles").update({
            default_payment_method_id: result.paymentMethodId,
            default_card_brand: result.brand || null,
            default_card_last4: result.last4 || null,
          }).eq("id", session.user.id).then(() => {});
        }
      }
      // Fire notifications (non-blocking)
      notifyBarberOfBooking({ barber_id: barber.id, client_name: clientName, service_type: selectedService, scheduled_at: scheduledAt, booking_id: nspBookingId, is_live: !scheduledAt });
      if (clientPhone) {
        const timeLabel = scheduledAt ? formatLocalTime(scheduledAt) : (time?.split("|")[1] || "Now");
        const mapsLink = barber.lat && barber.lng ? `https://maps.google.com/?q=${barber.lat},${barber.lng}` : "";
        sendSMS(normalizePhone(clientPhone), `✂️ FADED: ${selectedService} with ${barber.full_name} booked for ${timeLabel}.${mapsLink ? ` 📍 ${mapsLink}` : ""} – You'll get a confirmation once the barber accepts.`);
      }
      setNspSavingCard(false);
      setBooking(false);
      if (scheduledAt) scheduleApptReminder(scheduledAt, barber.full_name, selectedService, nspBookingId);
      setDone(true);
      doneRef.current = true;
      haptic.success();
      track("booking_confirmed", { service_type: selectedService, payment_method: payment || "in_person", nsp: !skip });
      try { localStorage.removeItem("booking_in_progress"); } catch {}
    } catch (err) {
      setNspCardError("Something went wrong — please try again.");
      setNspSavingCard(false);
      Sentry.captureException(err, { tags: { flow:"booking", step:"savecard" } });
    }
  };

  const handleModalClose = () => {
    if (step === "savecard" && nspBookingId) {
      if (savecardType === "cash_fee") {
        // FADED-fee bookings require a card on file — closing without one cancels.
        supabase.from("bookings")
          .update({ status: "cancelled", cancelled_at: new Date().toISOString(), decline_reason: "Client closed without completing payment" })
          .eq("id", nspBookingId)
          .then(() => {});
      } else {
        // No-show-protection card is optional (same as tapping "Skip"): never lose
        // the booking if the client closes here — e.g. after a card-save error.
        // Keep it and notify the barber so it proceeds as a normal pending booking.
        notifyBarberOfBooking({ barber_id: barber.id, client_name: clientName, service_type: selectedService, scheduled_at: scheduledAt, booking_id: nspBookingId, is_live: !scheduledAt });
      }
    }
    onClose();
  };

  const MOCK_REVIEWS = [
    { client_name:"Jordan M.", comment:"Best fade in the city. Booked in under a minute.", rating:5, created_at:"Apr 21" },
    { client_name:"Alex T.",   comment:"On time, clean cuts, great energy.", rating:5, created_at:"Apr 19" },
    { client_name:"Chris B.",  comment:"30 seconds to book. Cleanest cut I have had.", rating:5, created_at:"Apr 16" },
  ];

  return (
    <div role="dialog" aria-modal="true" aria-label={`Book with ${barber.full_name || "barber"}`}
      style={{ position:"fixed", top:0, left:0, right:0, bottom:0, zIndex:200, background:"#0A0A0A", display:"flex", flexDirection:"column", animation:"slideUp 0.28s cubic-bezier(0.32,0.72,0,1)" }}>
      {/* Unified sticky header — hidden when done (pass gets full screen) */}
      {done ? (
        <button onClick={onClose} aria-label="Close" style={{ position:"absolute", top:"max(18px, calc(var(--sat, 0px) + 10px))", right:16, zIndex:10, width:36, height:36, borderRadius:"50%", background:"rgba(245,245,240,0.1)", border:"none", cursor:"pointer", fontSize:18, color:"rgba(245,245,240,0.6)", display:"flex", alignItems:"center", justifyContent:"center", backdropFilter:"blur(8px)", WebkitBackdropFilter:"blur(8px)" }}><span aria-hidden="true">×</span></button>
      ) : (
      <div style={{ flexShrink:0, background:"rgba(10,10,10,0.97)", ...(isDesktop ? {} : { backdropFilter:"blur(16px)", WebkitBackdropFilter:"blur(16px)" }), borderBottom:"1px solid rgba(245,245,240,0.07)", paddingTop:"max(48px, calc(var(--sat, 0px) + 4px))", paddingBottom:12, paddingLeft:16, paddingRight:16 }}>
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom: step!=="profile" ? 10 : 0 }}>
          {step !== "profile" ? (
            <button onClick={()=>{ if(step==="service")setStep("profile"); else if(step==="time")setStep("service"); else if(step==="payment")setStep("time"); else if(step==="confirm")setStep("payment"); else if(step==="savecard"){}/*booking already created — no back*/ }}
              aria-label="Go back to previous step"
              style={{ display:"flex", alignItems:"center", justifyContent:"center", minWidth:44, minHeight:44, width:44, height:44, background:"rgba(245,245,240,0.08)", border:"none", borderRadius:10, color:"rgba(245,245,240,0.7)", cursor:"pointer", flexShrink:0 }}>
              <svg aria-hidden="true" focusable="false" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
            </button>
          ) : <div style={{width:32}}/>}
          <div style={{ flex:1, minWidth:0 }}>
            {step==="profile" ? (
              <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:16, fontWeight:800, color:"#fff" }}>Book with {barber.full_name?.split(" ")[0] || "Barber"}</div>
            ) : (
              <>
                <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:15, fontWeight:800, color:"#fff", lineHeight:1.2 }}>
                  {step==="service"?"Choose your service":step==="time"?"Pick a time":step==="payment"?"Payment method":step==="savecard"?"Secure your booking":"Confirm booking"}
                </div>
                <div style={{ fontSize:11, color:"rgba(245,245,240,0.4)", marginTop:2 }}>
                  {step==="service"?`Step 1 of 3 · Service`:step==="time"?`Step 2 of 3 · ${selectedService}${selectedSvcObj?` · ${selectedSvcObj.duration_minutes} min`:""}`:step==="payment"?`Step 3 of 3 · How you'll pay ${barber.full_name?.split(" ")[0]||"them"}`:step==="savecard"?"Card on file — not charged now":"Review & lock it in"}
                </div>
              </>
            )}
          </div>
          <button onClick={handleModalClose} aria-label="Close booking" style={{ minWidth:44, minHeight:44, width:44, height:44, borderRadius:10, background:"rgba(245,245,240,0.07)", border:"none", cursor:"pointer", fontSize:18, color:"rgba(245,245,240,0.65)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}><span aria-hidden="true">×</span></button>
        </div>
        {/* Progress dots */}
        {step !== "profile" && step !== "savecard" && (
          <div style={{ display:"flex", gap:4, paddingLeft:42 }}>
            {["service","time","payment","confirm"].map((s,i)=>(
              <div key={s} style={{ height:3, borderRadius:2, flex:1, background: ["service","time","payment","confirm"].indexOf(step) >= i ? "#2563EB" : "rgba(245,245,240,0.12)", transition:"background 0.2s" }}/>
            ))}
          </div>
        )}
      </div>
      )}
      <div ref={modalScrollRef} style={{ flex:1, minHeight:0, overflowY:"auto", WebkitOverflowScrolling:"touch", paddingBottom:0 }}>

        {done ? (
          <motion.div variants={staggerContainer} initial="hidden" animate="visible" style={{ padding:"max(60px, calc(var(--sat, 0px) + 48px)) 20px max(env(safe-area-inset-bottom,32px),32px)" }}>
            <style>{`@keyframes popCheck { from { transform:scale(0.6); opacity:0; } to { transform:scale(1); opacity:1; } } @keyframes passSlide { from { opacity:0; transform:translateY(18px); } to { opacity:1; transform:translateY(0); } }`}</style>

            {/* Animated checkmark + particles */}
            <motion.div variants={fadeUp} style={{ display:"flex", justifyContent:"center", marginBottom:20, position:"relative" }}>
              {/* Particle burst */}
              {Array.from({length:8}).map((_,i)=>{
                const angle = (i/8)*Math.PI*2;
                return (
                  <motion.div key={i} style={{ position:"absolute", top:"50%", left:"50%", width:6, height:6, borderRadius:"50%", background:"#22C55E", marginTop:-3, marginLeft:-3 }}
                    initial={{ x:0, y:0, opacity:1, scale:1 }}
                    animate={{ x:Math.cos(angle)*44, y:Math.sin(angle)*44, opacity:0, scale:0 }}
                    transition={{ duration:0.65, delay:0.4 }}/>
                );
              })}
              <motion.svg viewBox="0 0 52 52" style={{ width:64, height:64 }}>
                <motion.circle cx="26" cy="26" r="25" fill="none" stroke="#22C55E" strokeWidth="1.5"
                  initial={{ pathLength:0 }} animate={{ pathLength:1 }}
                  transition={{ duration:0.5, ease:"easeOut" }}/>
                <motion.path d="M14 27 L22 35 L38 19" fill="none" stroke="#22C55E" strokeWidth="2.5" strokeLinecap="round"
                  initial={{ pathLength:0 }} animate={{ pathLength:1 }}
                  transition={{ duration:0.4, delay:0.45, ease:"easeOut" }}/>
              </motion.svg>
            </motion.div>

            {/* ── Boarding Pass ── */}
            <motion.div variants={fadeUp} style={{ borderRadius:20, overflow:"hidden", border:"1px solid rgba(245,245,240,0.1)", marginBottom:20, boxShadow:"0 12px 48px rgba(0,0,0,0.6)" }}>

              {/* Header strip */}
              <div style={{ background:"linear-gradient(135deg,#1a3a6b,#2563EB)", padding:"18px 20px 16px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <div>
                  <div style={{ fontFamily:"'Syne',sans-serif", fontSize:20, fontWeight:900, letterSpacing:2, color:"#fff" }}>
                    <span style={{ color:"rgba(255,255,255,0.45)" }}>FAD</span>ED
                  </div>
                  <div style={{ fontFamily:"'JetBrains Mono','Courier New',monospace", fontSize:11, letterSpacing:"0.2em", color:"rgba(255,255,255,0.5)", marginTop:2 }}>APPOINTMENT PASS</div>
                </div>
                <div style={{ animation:"popCheck 0.5s cubic-bezier(0.22,1,0.36,1) 0.15s both", background:"rgba(255,255,255,0.12)", borderRadius:20, padding:"5px 12px", display:"flex", alignItems:"center", gap:6 }}>
                  <div style={{ width:7, height:7, borderRadius:"50%", background:"#4ADE80", boxShadow:"0 0 8px #4ADE80" }}/>
                  <span style={{ fontFamily:"'JetBrains Mono','Courier New',monospace", fontSize:11, fontWeight:700, color:"#fff", letterSpacing:"0.15em" }}>BOOKED</span>
                </div>
              </div>

              {/* Barber info */}
              <div style={{ background:"#0F0F0F", padding:"18px 20px 14px", display:"flex", alignItems:"center", gap:14 }}>
                <div style={{ width:52, height:52, borderRadius:14, overflow:"hidden", flexShrink:0, background:"linear-gradient(135deg,#1e3a5f,#2563EB)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, fontWeight:900, color:"#fff", fontFamily:"'DM Sans',sans-serif" }}>
                  {barber.avatar_url
                    ? <img src={barber.avatar_url} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
                    : (barber.full_name||"B")[0].toUpperCase()
                  }
                </div>
                <div>
                  <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:22, letterSpacing:"0.04em", color:"#fff", lineHeight:1 }}>{(barber.full_name||"Barber").toUpperCase()}</div>
                  <div style={{ fontFamily:"'JetBrains Mono','Courier New',monospace", fontSize:11, letterSpacing:"0.15em", color:"#2563EB", marginTop:3 }}>{barber.handle?.toUpperCase()}</div>
                  {barber.address && <div style={{ fontSize:11, color:"rgba(255,255,255,0.4)", marginTop:4 }}>📍 {barber.address}</div>}
                </div>
              </div>

              {/* Perforated divider */}
              <div style={{ background:"#0F0F0F", padding:"0 0", display:"flex", alignItems:"center" }}>
                <div style={{ width:18, height:18, borderRadius:"50%", background:"#0A0A0A", flexShrink:0, marginLeft:-9 }}/>
                <div style={{ flex:1, borderTop:"1.5px dashed rgba(245,245,240,0.12)", margin:"0 4px" }}/>
                <div style={{ width:18, height:18, borderRadius:"50%", background:"#0A0A0A", flexShrink:0, marginRight:-9 }}/>
              </div>

              {/* Details grid */}
              <div style={{ background:"#0F0F0F", padding:"16px 20px 20px" }}>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"16px 24px" }}>
                  {[
                    { label:"SERVICE", value: selectedService || "Haircut", mono:false },
                    { label:"TIME",    value: (time?.split("|")[1] || "Now") + (time?.split("|")[1] && time.split("|")[1] !== "Now — Available" ? " ET" : ""),  mono:true  },
                    { label:"DATE",    value: time?.split("|")[0] ? new Date(time.split("|")[0]+"T12:00:00").toLocaleDateString("en-CA",{weekday:"short",month:"short",day:"numeric"}) : new Date().toLocaleDateString("en-CA",{weekday:"short",month:"short",day:"numeric"}), mono:false },
                    { label:"PRICE",   value: `$${selectedPrice}`,           mono:true  },
                    { label:"PAYMENT", value: payment || "In person",        mono:false },
                    { label:"STATUS",  value: "Pending confirmation",        mono:false, dim:true },
                  ].map(({ label, value, mono, dim }) => (
                    <div key={label}>
                      <div style={{ fontFamily:"'JetBrains Mono','Courier New',monospace", fontSize:11, letterSpacing:"0.18em", color:"rgba(245,245,240,0.3)", marginBottom:4 }}>{label}</div>
                      <div style={{ fontFamily: mono ? "'JetBrains Mono','Courier New',monospace" : "'DM Sans',sans-serif", fontSize: mono ? 15 : 13, fontWeight:700, color: dim ? "rgba(245,245,240,0.6)" : "#fff", letterSpacing: mono ? "0.05em" : 0 }}>{value}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Footer strip */}
              <div style={{ background:"#111", borderTop:"1px solid rgba(245,245,240,0.06)", padding:"10px 20px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div style={{ fontFamily:"'JetBrains Mono','Courier New',monospace", fontSize:11, letterSpacing:"0.15em", color:"rgba(245,245,240,0.2)" }}>LETSGETFADED.COM</div>
                <div style={{ fontFamily:"'JetBrains Mono','Courier New',monospace", fontSize:11, letterSpacing:"0.12em", color:"rgba(245,245,240,0.2)" }}>FADED-{Math.random().toString(36).slice(2,6).toUpperCase()}</div>
              </div>
            </motion.div>
            {/* What happens next */}
            <div style={{ margin:"16px 0 12px", padding:"14px 16px", background:"rgba(37,99,235,0.07)", border:"1px solid rgba(37,99,235,0.2)", borderRadius:14 }}>
              <div style={{ fontSize:12, fontWeight:800, color:"#93C5FD", marginBottom:6, letterSpacing:"0.05em" }}>WHAT HAPPENS NEXT</div>
              <div style={{ fontSize:13, color:"rgba(245,245,240,0.65)", lineHeight:1.7 }}>
                Your request is with <strong style={{ color:"rgba(245,245,240,0.9)" }}>{barber.full_name?.split(" ")[0]||"your barber"}</strong> — they typically confirm within minutes. You'll get an SMS once it's locked in. Show this pass at the chair.
              </div>
            </div>

            {/* Card hold disclosure */}
            {payment === "Credit / Debit Card" && (
              <div style={{ margin:"0 0 12px", padding:"12px 16px", background:"rgba(245,158,11,0.06)", border:"1px solid rgba(245,158,11,0.2)", borderRadius:14, display:"flex", gap:10, alignItems:"flex-start" }}>
                <div style={{ fontSize:16, marginTop:1 }}>🔒</div>
                <div style={{ fontSize:12, color:"rgba(245,245,240,0.6)", lineHeight:1.6 }}>
                  A <strong style={{ color:"rgba(245,245,240,0.85)" }}>{fmt(clientTotal)} hold</strong> has been placed on your card. It will not be charged until your barber accepts — if declined, the hold is released automatically.
                </div>
              </div>
            )}

            {/* Add to calendar */}
            {time && time.includes("|") && (() => {
              const [dateStr, timeStr] = time.split("|");
              if (!dateStr || !timeStr || timeStr === "Now — Available") return null;
              const parseSlot = () => {
                try {
                  const [h, rest] = timeStr.split(":");
                  const [m, ampm] = rest ? rest.split(" ") : ["00", timeStr.includes("PM") ? "PM" : "AM"];
                  let hour = parseInt(h);
                  if (ampm === "PM" && hour !== 12) hour += 12;
                  if (ampm === "AM" && hour === 12) hour = 0;
                  const start = new Date(`${dateStr}T${String(hour).padStart(2,"0")}:${(m||"00").padStart(2,"0")}:00`);
                  const end = new Date(start.getTime() + (selectedSvcObj?.duration_minutes || 45) * 60000);
                  return { start, end };
                } catch { return null; }
              };
              const slot = parseSlot();
              if (!slot) return null;
              const fmtIcs = d => d.toISOString().replace(/[-:]/g,"").replace(/\.\d{3}/,"");
              const summary = `${selectedService} with ${barber.full_name} — FADED`;
              const description = `Booked via FADED. Payment: ${payment||"In person"}. $${selectedPrice}. letsgetfaded.com`;
              const gcalUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(summary)}&dates=${fmtIcs(slot.start)}/${fmtIcs(slot.end)}&details=${encodeURIComponent(description)}${barber.address?`&location=${encodeURIComponent(barber.address)}`:""}`;
              const icsLines = [
                "BEGIN:VCALENDAR","VERSION:2.0","PRODID:-//FADED//letsgetfaded.com//EN",
                "BEGIN:VEVENT",
                `DTSTART:${fmtIcs(slot.start)}`,`DTEND:${fmtIcs(slot.end)}`,
                `SUMMARY:${summary}`,`DESCRIPTION:${description}`,
                barber.address?`LOCATION:${barber.address}`:"",
                `UID:faded-${Date.now()}@letsgetfaded.com`,
                "END:VEVENT","END:VCALENDAR",
              ].filter(Boolean).join("\r\n");
              const icsUrl = URL.createObjectURL(new Blob([icsLines],{type:"text/calendar;charset=utf-8"}));
              return (
                <div style={{ display:"flex", gap:8, marginBottom:4 }}>
                  <a href={icsUrl} download="faded-booking.ics" style={{ flex:1, display:"block", padding:"13px", background:t.section, border:`1.5px solid ${t.border}`, borderRadius:14, color:t.t1, fontSize:13, fontWeight:700, textDecoration:"none", boxSizing:"border-box", cursor:"pointer", textAlign:"center", fontFamily:"'DM Sans',sans-serif" }}>
                    📅 Apple / Device Calendar
                  </a>
                  <a href={gcalUrl} target="_blank" rel="noopener noreferrer" style={{ flex:1, display:"block", padding:"13px", background:t.section, border:`1.5px solid ${t.border}`, borderRadius:14, color:t.t1, fontSize:13, fontWeight:700, textDecoration:"none", boxSizing:"border-box", cursor:"pointer", textAlign:"center", fontFamily:"'DM Sans',sans-serif" }}>
                    📅 Google Calendar
                  </a>
                </div>
              );
            })()}
            {/* Push notification opt-in */}
            {!notifAsked && typeof Notification !== "undefined" && Notification.permission === "default" && (
              <button onClick={async () => {
                setNotifAsked(true);
                const granted = await requestNotificationPermission();
                if (granted) {
                  scheduleCutReminder(2);
                  sendNotification("FADED Notifications On ✅", "We'll remind you when your barber confirms and when it's time for your next cut.");
                }
              }} style={{ width:"100%", padding:"13px", background:t.blueBg, border:`1.5px solid ${t.blueBorder}`, borderRadius:14, color:t.blueText, fontSize:13, fontWeight:700, cursor:"pointer", marginBottom:12, fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box" }}>
                🔔 Get booking updates
              </button>
            )}
            {(() => {
              const timeLabel = scheduledAt ? formatLocalTime(scheduledAt) : (time?.split("|")[1]||"Now");
              const waMsg = `✂️ My FADED booking — ${selectedService} with ${barber.full_name} on ${timeLabel}. $${selectedPrice}. See you then!`;
              // Never expose barber's personal number — use share-only wa.me link
              const waUrl = `https://wa.me/?text=${encodeURIComponent(waMsg)}`;
              return (
                <a href={waUrl} target="_blank" rel="noopener noreferrer"
                  style={{ display:"block", width:"100%", padding:"13px", background:"rgba(37,211,102,0.1)", border:"1.5px solid rgba(37,211,102,0.35)", borderRadius:14, color:"#25D366", fontSize:13, fontWeight:700, textAlign:"center", textDecoration:"none", marginBottom:12, boxSizing:"border-box" }}>
                  💬 Share booking confirmation
                </a>
              );
            })()}
            {/* Guest sign-up prompt — localStorage bookings disappear if cache cleared */}
            {!session || session === "guest" ? (
              <div style={{ marginBottom:12, padding:"14px 16px", background:"rgba(37,99,235,0.08)", border:"1.5px solid rgba(37,99,235,0.25)", borderRadius:16, textAlign:"center" }}>
                <div style={{ fontSize:13, fontWeight:700, color:t.t1, marginBottom:4 }}>Save your booking permanently</div>
                <div style={{ fontSize:12, color:t.t3, marginBottom:10 }}>Create a free account to track this appointment and get SMS updates — no credit card needed.</div>
                <button onClick={onClose} style={{ width:"100%", padding:"11px", background:"#2563EB", border:"none", borderRadius:12, color:"#fff", fontSize:13, fontWeight:800, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>Create Free Account →</button>
              </div>
            ) : null}
            <motion.button variants={fadeUp} onClick={onClose} style={{ width:"100%", padding:14, background:"transparent", border:`1px solid ${t.border}`, borderRadius:16, color:t.t3, fontSize:14, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>Close</motion.button>
          </motion.div>

        ) : step==="service" ? (
          <motion.div key="step-service" variants={slideInRight} initial="hidden" animate="visible" style={{ padding:"20px 20px 32px" }}>
            <div style={{ fontSize:11, fontWeight:800, color:"rgba(245,245,240,0.35)", letterSpacing:"0.18em", marginBottom:14, textTransform:"uppercase" }}>Choose Your Service</div>
            {servicesLoaded && MAIN_SERVICES.length === 0 ? (
              <div style={{ padding:"32px 20px", textAlign:"center" }}>
                <div style={{ fontSize:32, marginBottom:12 }}>🔧</div>
                <div style={{ fontSize:16, fontWeight:800, color:"rgba(245,245,240,0.85)", marginBottom:8 }}>Still setting up</div>
                <div style={{ fontSize:13, color:"rgba(245,245,240,0.45)", lineHeight:1.6, marginBottom:24 }}>{barber.full_name?.split(" ")[0] || "This barber"} hasn't added their services yet. Check back soon or message them directly.</div>
                <button onClick={onClose} style={{ padding:"12px 28px", background:"rgba(245,245,240,0.08)", border:"1px solid rgba(245,245,240,0.15)", borderRadius:100, color:"rgba(245,245,240,0.7)", fontSize:14, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>Go Back</button>
              </div>
            ) : (<>
            <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:28 }}>
              {SERVICES.map((s,idx)=>{
                const sName  = s.name || s;
                const sPrice = s.price ?? barber.price_min;
                const sDur   = s.duration_minutes;
                const pct    = s.promo_pct || 0;
                const discounted = pct > 0 ? Math.round(sPrice * (1 - pct/100)) : null;
                const isSelected = selectedService === sName;
                const isMostBooked = idx === 0;
                return (
                  <motion.button key={sName}
                    whileTap={{ scale:0.98 }}
                    animate={{ borderColor:isSelected?"#3B6CF7":"rgba(245,245,240,0.1)", backgroundColor:isSelected?"rgba(59,108,247,0.12)":"rgba(245,245,240,0.04)", scale:isSelected?1.01:1 }}
                    transition={{ duration:0.15 }}
                    onClick={()=>{ haptic.light(); setSelectedService(sName); setSelectedAddons(prev => prev.filter(n => n !== sName)); track("service_selected", { service_type: sName, barber_id: barber?.id }); setTimeout(()=>detailsRef.current?.scrollIntoView({ behavior:"smooth", block:"start" }), 80); }}
                    style={{ padding:"16px 18px", borderRadius:16, border:`1.5px solid ${isSelected?"#3B6CF7":"rgba(245,245,240,0.1)"}`, background:isSelected?"rgba(59,108,247,0.12)":"rgba(245,245,240,0.04)", cursor:"pointer", textAlign:"left", display:"flex", justifyContent:"space-between", alignItems:"center", gap:12 }}>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:3 }}>
                        <span style={{ fontSize:15, fontWeight:800, color:isSelected?"#fff":"rgba(245,245,240,0.85)" }}>{sName}</span>
                        {isMostBooked && <span style={{ fontSize:11, fontWeight:700, background:"rgba(37,99,235,0.2)", color:"#93C5FD", borderRadius:6, padding:"2px 7px", letterSpacing:"0.05em" }}>most booked</span>}
                        {pct > 0 && <span style={{ fontSize:11, fontWeight:700, background:"rgba(245,158,11,0.15)", color:"#F59E0B", borderRadius:6, padding:"2px 7px" }}>{pct}% OFF</span>}
                      </div>
                      <div style={{ fontSize:12, color:"rgba(245,245,240,0.4)" }}>{sDur ? `${sDur} min` : ""}</div>
                    </div>
                    <div style={{ display:"flex", alignItems:"center", gap:12, flexShrink:0 }}>
                      <span style={{ fontSize:17, fontWeight:800, color:isSelected?"#2563EB":"rgba(245,245,240,0.7)" }}>${discounted ?? sPrice}</span>
                      <div style={{ width:24, height:24, borderRadius:"50%", border:`2px solid ${isSelected?"#2563EB":"rgba(245,245,240,0.25)"}`, background:isSelected?"#2563EB":"transparent", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                        {isSelected && <svg aria-hidden="true" focusable="false"width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
                      </div>
                    </div>
                  </motion.button>
                );
              })}
            </div>

            {/* Add-ons — barber-configured extras only, never main services */}
            {ADDON_SERVICES.length > 0 && (
              <div style={{ marginBottom:24 }}>
                <div style={{ fontSize:11, fontWeight:800, color:"rgba(245,245,240,0.35)", letterSpacing:"0.18em", marginBottom:4, textTransform:"uppercase" }}>
                  Add-ons <span style={{ fontSize:10, fontWeight:600, color:"rgba(245,245,240,0.2)", textTransform:"none", letterSpacing:0 }}>optional extras</span>
                </div>
                <div style={{ fontSize:11, color:"rgba(245,245,240,0.25)", marginBottom:10 }}>Stack on top of your selected service</div>
                <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                  {ADDON_SERVICES.map(s => {
                    const aName = s.name || s;
                    const aPrice = s.price ?? 0;
                    const aDur = s.duration_minutes;
                    const isAdded = selectedAddons.includes(aName);
                    return (
                      <button key={aName} onClick={() => setSelectedAddons(prev =>
                        isAdded ? prev.filter(n => n !== aName) : [...prev, aName]
                      )}
                        style={{ padding:"12px 16px", borderRadius:14, border:`1.5px solid ${isAdded?"rgba(37,99,235,0.6)":"rgba(245,245,240,0.08)"}`, background:isAdded?"rgba(37,99,235,0.1)":"rgba(245,245,240,0.03)", cursor:"pointer", textAlign:"left", display:"flex", alignItems:"center", gap:12, transition:"all 0.15s" }}>
                        <div style={{ width:20, height:20, borderRadius:6, border:`2px solid ${isAdded?"#2563EB":"rgba(245,245,240,0.25)"}`, background:isAdded?"#2563EB":"transparent", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, transition:"all 0.15s" }}>
                          {isAdded && <svg aria-hidden="true" focusable="false" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
                        </div>
                        <div style={{ flex:1, minWidth:0 }}>
                          <span style={{ fontSize:14, fontWeight:700, color:isAdded?"#fff":"rgba(245,245,240,0.75)" }}>{aName}</span>
                          {aDur ? <span style={{ fontSize:12, color:"rgba(245,245,240,0.35)", marginLeft:6 }}>{aDur} min</span> : null}
                        </div>
                        <span style={{ fontSize:15, fontWeight:800, color:isAdded?"#2563EB":"rgba(245,245,240,0.5)", flexShrink:0 }}>+${aPrice}</span>
                      </button>
                    );
                  })}
                </div>
                {selectedAddons.length > 0 && (
                  <div style={{ marginTop:10, padding:"10px 14px", background:"rgba(37,99,235,0.08)", borderRadius:10, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                    <span style={{ fontSize:12, color:"rgba(245,245,240,0.5)" }}>{selectedAddons.length} add-on{selectedAddons.length>1?"s":""} · {selectedDuration} min total</span>
                    <span style={{ fontSize:14, fontWeight:800, color:"#2563EB" }}>{fmt(selectedPrice)} total</span>
                  </div>
                )}
              </div>
            )}

            <div ref={detailsRef} style={{ fontSize:11, fontWeight:800, color:"rgba(245,245,240,0.35)", letterSpacing:"0.18em", marginBottom:14, textTransform:"uppercase" }}>Your Details</div>
            <div style={{ position:"relative", marginBottom:12 }}>
              <div style={{ position:"absolute", top:10, left:14, fontSize:11, fontWeight:700, color:"rgba(245,245,240,0.4)", letterSpacing:"0.12em", textTransform:"uppercase", pointerEvents:"none" }}>
                NAME <span style={{ color:"#EF4444" }}>*</span>
              </div>
              <input value={clientName} onChange={e=>setClientName(e.target.value)} placeholder="Full name"
                onFocus={e=>setTimeout(()=>e.target.scrollIntoView({behavior:"smooth",block:"center"}),300)}
                style={{ width:"100%", padding:"26px 14px 10px", borderRadius:14, border:`1.5px solid ${clientName.trim()?"rgba(37,99,235,0.5)":clientName?"rgba(220,38,38,0.4)":"rgba(245,245,240,0.12)"}`, background:"rgba(245,245,240,0.06)", color:"#fff", fontSize:16, fontWeight:600, outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box" }}/>
            </div>
            <div style={{ position:"relative" }}>
              <div style={{ position:"absolute", top:10, left:14, fontSize:11, fontWeight:700, color:"rgba(245,245,240,0.4)", letterSpacing:"0.12em", textTransform:"uppercase", pointerEvents:"none" }}>
                PHONE <span style={{ color:"#EF4444" }}>*</span>
              </div>
              <input value={clientPhone} onChange={e=>setClientPhone(e.target.value)} placeholder="(416) 555-0000" type="tel"
                onFocus={e=>setTimeout(()=>e.target.scrollIntoView({behavior:"smooth",block:"center"}),300)}
                style={{ width:"100%", padding:"26px 14px 10px", borderRadius:14, border:`1.5px solid ${clientPhone.trim()?"rgba(37,99,235,0.5)":clientPhone?"rgba(220,38,38,0.4)":"rgba(245,245,240,0.12)"}`, background:"rgba(245,245,240,0.06)", color:"#fff", fontSize:16, fontWeight:600, outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box" }}/>
              {!clientPhone.trim() && clientName.trim() && (
                <div style={{ position:"absolute", bottom:-18, left:0, fontSize:11, color:"#EF4444", fontWeight:600 }}>Phone required for SMS confirmation</div>
              )}
            </div>
            </>)}
          </motion.div>

        ) : step==="payment" ? (
          <motion.div key="step-payment" variants={slideInRight} initial="hidden" animate="visible" style={{ padding:"20px 20px 32px" }}>
            <div style={{ fontSize:11, fontWeight:800, color:"rgba(245,245,240,0.35)", letterSpacing:"0.18em", marginBottom:16, textTransform:"uppercase" }}>Select Payment</div>
            {!barber.stripe_charges_enabled && (
              <div style={{ marginBottom:12, padding:"10px 14px", background:"rgba(245,158,11,0.08)", border:"1px solid rgba(245,158,11,0.25)", borderRadius:12, fontSize:12, color:"#F59E0B", lineHeight:1.5 }}>
                This barber is cash-only right now — card payments aren't set up yet. You can still lock in your spot.
              </div>
            )}
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {[
                { id:"Interac e-Transfer", label:"Interac e-Transfer", desc:`Send to ${barber.etransfer_email||"the barber"}`, cardOnly:false, icon:<svg aria-hidden="true" focusable="false"width="32" height="20" viewBox="0 0 32 20"><rect width="32" height="20" rx="4" fill="#FFDE00"/><text x="4" y="14" fontFamily="Arial" fontSize="8" fontWeight="bold" fill="#000">interac</text></svg> },
                { id:"Apple Pay", label:"Apple Pay", desc:"Pay at the chair", cardOnly:true, icon:<svg aria-hidden="true" focusable="false"width="24" height="24" viewBox="0 0 24 24" fill="white"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg> },
                { id:"Google Pay", label:"Google Pay", desc:"Pay at the chair", cardOnly:true, icon:<svg aria-hidden="true" focusable="false"width="24" height="24" viewBox="0 0 24 24"><text x="2" y="17" fontFamily="Arial" fontSize="13" fontWeight="bold"><tspan fill="#4285F4">G</tspan><tspan fill="#EA4335">o</tspan><tspan fill="#FBBC05">o</tspan><tspan fill="#4285F4">g</tspan><tspan fill="#34A853">l</tspan><tspan fill="#EA4335">e</tspan></text></svg> },
                { id:"Credit / Debit Card", label:"Credit / Debit Card", desc:"Visa · Mastercard · Amex", cardOnly:true, icon:<svg aria-hidden="true" focusable="false"width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="rgba(245,245,240,0.7)" strokeWidth="1.8" strokeLinecap="round"><rect x="1" y="4" width="22" height="16" rx="3"/><line x1="1" y1="10" x2="23" y2="10"/></svg> },
                { id:"Cash", label:"Cash", desc:"Pay in person", cardOnly:false, icon:<svg aria-hidden="true" focusable="false"width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="rgba(245,245,240,0.7)" strokeWidth="1.8" strokeLinecap="round"><rect x="2" y="6" width="20" height="12" rx="2"/><circle cx="12" cy="12" r="3"/><line x1="2" y1="10" x2="2" y2="10"/></svg> },
                { id:"Pay at Shop", label:"Pay at Shop", desc:"Use the barber's card reader", cardOnly:false, icon:<svg aria-hidden="true" focusable="false" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="rgba(245,245,240,0.7)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="2" width="14" height="20" rx="2"/><line x1="9" y1="7" x2="15" y2="7"/><line x1="9" y1="11" x2="15" y2="11"/><rect x="8" y="15" width="8" height="4" rx="1" fill="rgba(245,245,240,0.15)" stroke="none"/></svg> },
              ].filter(opt => {
                if (opt.cardOnly && !barber.stripe_charges_enabled) return false;
                // Barber-controlled in-person methods. Cash + Pay at Shop default ON
                // (only hidden if the barber explicitly turned them off); e-Transfer is
                // opt-in and only shown once the barber has entered a destination email,
                // so a client can never pick a method that goes nowhere.
                if (opt.id === "Cash" && barber.accepts_cash === false) return false;
                if (opt.id === "Pay at Shop" && barber.accepts_pay_at_shop === false) return false;
                if (opt.id === "Interac e-Transfer" && !(barber.accepts_etransfer && barber.etransfer_email)) return false;
                // Wallet payments (Apple Pay / Google Pay) are web-only for now — the
                // native app serves its bundled JS under a capacitor:// origin, which
                // ApplePaySession refuses to run under ("insecure document" crash).
                // Card entry works identically on both, so native users just see that.
                if ((opt.id === "Apple Pay" || opt.id === "Google Pay") && isNative()) return false;
                if (opt.id === "Apple Pay") return !!window.ApplePaySession?.canMakePayments();
                return true;
              }).map(opt=>{
                const isSelected = payment === opt.id;
                return (
                  <button key={opt.id} onClick={()=>setPayment(opt.id)}
                    style={{ padding:"16px 18px", borderRadius:16, border:`1.5px solid ${isSelected?"#2563EB":"rgba(245,245,240,0.1)"}`, background:isSelected?"rgba(37,99,235,0.1)":"rgba(245,245,240,0.04)", cursor:"pointer", textAlign:"left", transition:"all 0.15s", display:"flex", alignItems:"center", gap:14 }}>
                    <div style={{ width:40, height:40, borderRadius:10, background:"rgba(245,245,240,0.08)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>{opt.icon}</div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:14, fontWeight:700, color:"rgba(245,245,240,0.9)" }}>{opt.label}</div>
                      <div style={{ fontSize:12, color:"rgba(245,245,240,0.4)", marginTop:2 }}>{opt.desc}</div>
                    </div>
                    <div style={{ width:24, height:24, borderRadius:"50%", border:`2px solid ${isSelected?"#2563EB":"rgba(245,245,240,0.25)"}`, background:isSelected?"#2563EB":"transparent", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                      {isSelected && <svg aria-hidden="true" focusable="false"width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Fee notice */}
            {payment && (
              <div style={{ marginTop:16, padding:"12px 14px", background:"rgba(37,99,235,0.06)", border:"1px solid rgba(37,99,235,0.15)", borderRadius:12 }}>
                <div style={{ fontSize:12, color:"rgba(245,245,240,0.5)", lineHeight:1.6 }}>
                  {isCardPayment
                    ? `A card processing fee is added so your barber receives their full amount.${fadedFeeActive ? ` A $${FADED_FEE_AMOUNT.toFixed(2)} platform fee also applies.` : ""}`
                    : fadedFeeActive
                      ? `A $${FADED_FEE_AMOUNT.toFixed(2)} platform fee is added to this booking — your barber still receives their full price.`
                      : "Your barber receives their full quoted price."
                  }
                </div>
              </div>
            )}

            {/* Stripe card element — only shown when Credit/Debit is selected and Stripe is configured */}
            {payment === "Credit / Debit Card" && STRIPE_AVAILABLE && (
              <div style={{ marginTop:20 }}>
                <div style={{ fontSize:11, fontWeight:800, color:"rgba(245,245,240,0.35)", letterSpacing:"0.18em", marginBottom:12, textTransform:"uppercase" }}>Card Details</div>
                <div ref={cardElementRef} style={{ background:"rgba(245,245,240,0.06)", borderRadius:14, padding:"16px 14px", border:"1.5px solid rgba(37,99,235,0.4)" }}/>
                {cardError && <div style={{ fontSize:12, color:"#EF4444", fontWeight:600, marginTop:8 }}>{cardError}</div>}
                <div style={{ fontSize:11, color:"rgba(245,245,240,0.3)", marginTop:10, display:"flex", alignItems:"center", gap:6 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                  Secured by Stripe · Your card number is never stored by FADED
                </div>
              </div>
            )}
            {payment === "Credit / Debit Card" && !STRIPE_AVAILABLE && (
              <div style={{ marginTop:16, padding:"12px 14px", background:"rgba(245,245,240,0.05)", borderRadius:12, border:"1px solid rgba(245,245,240,0.1)", fontSize:12, color:"rgba(245,245,240,0.4)" }}>
                Card payments launching soon — pay in person for now.
              </div>
            )}
          </motion.div>

        ) : step==="confirm" ? (
          <div style={{ padding:"20px 20px 140px" }}>
            {/* Barber card */}
            <div style={{ background:"rgba(245,245,240,0.05)", borderRadius:18, border:"1px solid rgba(245,245,240,0.1)", padding:"20px 18px", marginBottom:20, display:"flex", alignItems:"center", gap:16 }}>
              <div style={{ width:56, height:56, borderRadius:"50%", overflow:"hidden", flexShrink:0, background:`linear-gradient(135deg,${a}99,${a})`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, fontWeight:900, color:"#fff" }}>
                {barber.avatar_url ? <img src={barber.avatar_url} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }}/> : (barber.full_name||"B")[0].toUpperCase()}
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:22, letterSpacing:"0.04em", color:"#fff", lineHeight:1 }}>{(barber.full_name||"Barber").toUpperCase()}</div>
                <div style={{ fontSize:12, color:"rgba(245,245,240,0.5)", marginTop:3 }}>{barber.handle} · ★ {barber.rating||"4.9"}</div>
              </div>
              {barber.status==="available" && (
                <div style={{ display:"flex", alignItems:"center", gap:5, background:"rgba(34,197,94,0.12)", border:"1px solid rgba(34,197,94,0.3)", borderRadius:20, padding:"4px 10px", flexShrink:0 }}>
                  <div style={{ width:6, height:6, borderRadius:"50%", background:"#22C55E" }}/>
                  <span style={{ fontSize:11, fontWeight:700, color:"#22C55E" }}>LIVE</span>
                </div>
              )}
            </div>

            {/* Card retry banner — shown after a decline while pending booking is held */}
            {retryBookingId && cardError && (
              <div style={{ marginBottom:16, padding:"14px 16px", background:"rgba(220,38,38,0.08)", border:"1px solid rgba(220,38,38,0.25)", borderRadius:14 }}>
                <div style={{ fontSize:13, fontWeight:700, color:"#EF4444", marginBottom:4 }}>Payment declined</div>
                <div style={{ fontSize:12, color:"rgba(245,245,240,0.55)", marginBottom:10 }}>{cardError}</div>
                <div style={{ fontSize:11, color:"rgba(245,245,240,0.4)", marginBottom:10 }}>Your slot is held for 3 minutes. Enter a different card below and try again.</div>
                <div ref={cardElementRef} style={{ background:"rgba(245,245,240,0.06)", borderRadius:12, padding:"14px", border:"1.5px solid rgba(220,38,38,0.3)", marginBottom:10 }}/>
                <button onClick={async () => {
                  setBooking(true);
                  const retryResult = await confirmCardBooking({ supabase, bookingId: retryBookingId, barberId: barber.id, amountCents: Math.round(finalTotal * 100), tipCents: 0, giftCardId: gcApplied?.id ?? null });
                  if (!retryResult.success) {
                    setCardError(retryResult.error || "Still declined — try another card.");
                    setBooking(false);
                    return;
                  }
                  setRetryBookingId(null);
                  setCardError("");
                  setBooking(false);
                  setDone(true);
                  haptic.success();
                }} disabled={booking} style={{ width:"100%", padding:"12px", background:"#2563EB", border:"none", borderRadius:12, color:"#fff", fontSize:13, fontWeight:800, cursor:"pointer" }}>
                  {booking ? "Retrying…" : "Retry Payment →"}
                </button>
              </div>
            )}

            {/* Error states */}
            {doubleBookErr && !waitlistJoined && (
              <div style={{ marginBottom:16, padding:"14px 16px", background:"rgba(220,38,38,0.08)", border:"1px solid rgba(220,38,38,0.25)", borderRadius:14 }}>
                <div style={{ fontSize:13, fontWeight:700, color:"#EF4444", marginBottom:6 }}>{doubleBookMsg || "That slot is unavailable."}</div>
                {!doubleBookMsg.includes("already have") && (
                  <>
                    <div style={{ fontSize:12, color:"rgba(245,245,240,0.5)", marginBottom:12 }}>Join the waitlist — you'll be notified if it opens up.</div>
                    <button onClick={joinWaitlist} disabled={joiningWaitlist} style={{ width:"100%", padding:"11px", background:"#EF4444", border:"none", borderRadius:12, color:"#fff", fontSize:13, fontWeight:800, cursor:"pointer", opacity:joiningWaitlist?0.7:1 }}>
                      {joiningWaitlist ? "Joining..." : "Join Waitlist →"}
                    </button>
                  </>
                )}
                {doubleBookMsg.includes("already have") && (
                  <button onClick={()=>setDoubleBookErr(false)} style={{ width:"100%", padding:"11px", background:"rgba(220,38,38,0.12)", border:"1px solid rgba(220,38,38,0.25)", borderRadius:12, color:"#EF4444", fontSize:13, fontWeight:700, cursor:"pointer" }}>Pick a Different Time</button>
                )}
              </div>
            )}
            {waitlistJoined && (
              <div style={{ marginBottom:16, padding:"14px 16px", background:"rgba(34,197,94,0.08)", border:"1px solid rgba(34,197,94,0.25)", borderRadius:14, textAlign:"center" }}>
                <div style={{ fontSize:16, marginBottom:4 }}>✅</div>
                <div style={{ fontSize:13, fontWeight:700, color:"#22C55E" }}>You're on the waitlist!</div>
              </div>
            )}

            {/* Summary rows */}
            {(()=>{
              const [dStr,tStr]=(time||"").split("|");
              const datePart=dStr?new Date(dStr+"T12:00:00").toLocaleDateString("en-CA",{weekday:"short",month:"long",day:"numeric",year:"numeric"}):"";
              const timePart=tStr||"Now";
              return (
                <div style={{ background:"rgba(245,245,240,0.04)", borderRadius:18, border:"1px solid rgba(245,245,240,0.1)", overflow:"hidden", marginBottom:16 }}>
                  {/* Primary service */}
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"15px 18px", borderBottom:"1px solid rgba(245,245,240,0.07)" }}>
                    <span style={{ fontSize:14, color:"rgba(245,245,240,0.5)", fontWeight:500 }}>Service</span>
                    <span style={{ fontSize:14, fontWeight:700, color:"rgba(245,245,240,0.9)", textAlign:"right", maxWidth:"60%" }}>{selectedService}{selectedSvcObj?` · ${selectedSvcObj.duration_minutes} min`:""}</span>
                  </div>
                  {/* Add-on line items */}
                  {selectedAddonObjs.map(s => (
                    <div key={s.name} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"11px 18px", borderBottom:"1px solid rgba(245,245,240,0.07)", background:"rgba(37,99,235,0.04)" }}>
                      <span style={{ fontSize:13, color:"rgba(245,245,240,0.4)", fontWeight:500 }}>+ {s.name}{s.duration_minutes?` · ${s.duration_minutes} min`:""}</span>
                      <span style={{ fontSize:13, fontWeight:700, color:"rgba(245,245,240,0.6)" }}>+{fmt(s.price)}</span>
                    </div>
                  ))}
                  {[["Date",datePart],["Time",`${timePart}${selectedDuration>30?` · ${selectedDuration} min`:""}`,],["Payment",payment||"Pay in person"]].map(([k,v])=>(
                    <div key={k} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"15px 18px", borderBottom:"1px solid rgba(245,245,240,0.07)" }}>
                      <span style={{ fontSize:14, color:"rgba(245,245,240,0.5)", fontWeight:500 }}>{k}</span>
                      <span style={{ fontSize:14, fontWeight:700, color:"rgba(245,245,240,0.9)" }}>{v}</span>
                    </div>
                  ))}
                  {/* Service subtotal */}
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"15px 18px", borderBottom:"1px solid rgba(245,245,240,0.07)" }}>
                    <span style={{ fontSize:14, color:"rgba(245,245,240,0.5)", fontWeight:500 }}>Subtotal</span>
                    <span style={{ fontSize:14, fontWeight:700, color:"rgba(245,245,240,0.9)" }}>{fmt(selectedPrice)}</span>
                  </div>
                  {/* HST line — only for HST-registered barbers */}
                  {barber.hst_registered && hstAmt > 0 && (
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"11px 18px", borderBottom:"1px solid rgba(245,245,240,0.07)", background:"rgba(245,245,240,0.02)" }}>
                      <span style={{ fontSize:12, color:"rgba(245,245,240,0.4)", fontWeight:500 }}>HST ({Math.round(hstRate * 100)}%)</span>
                      <span style={{ fontSize:12, fontWeight:600, color:"rgba(245,245,240,0.6)" }}>+{fmt(hstAmt)}</span>
                    </div>
                  )}
                  {/* Stripe processing fee — card payments only */}
                  {isCardPayment && (
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"11px 18px", borderBottom:"1px solid rgba(245,245,240,0.07)", background:"rgba(245,245,240,0.02)" }}>
                      <span style={{ fontSize:12, color:"rgba(245,245,240,0.35)", fontWeight:500 }}>Card processing fee</span>
                      <span style={{ fontSize:12, fontWeight:600, color:"rgba(245,245,240,0.5)" }}>+{fmt(stripeFeeAmt)}</span>
                    </div>
                  )}
                  {/* FADED platform fee — only shown when active */}
                  {!isCardPayment && fadedFeeActive && (
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"11px 18px", borderBottom:"1px solid rgba(245,245,240,0.07)", background:"rgba(37,99,235,0.04)" }}>
                      <div style={{ fontSize:12, color:"rgba(245,245,240,0.4)", fontWeight:500 }}>Platform fee</div>
                      <span style={{ fontSize:12, fontWeight:600, color:"rgba(245,245,240,0.5)" }}>+{fmt(FADED_FEE_AMOUNT)}</span>
                    </div>
                  )}
                  {/* Total */}
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"15px 18px" }}>
                    <span style={{ fontSize:14, color:"rgba(245,245,240,0.5)", fontWeight:500 }}>Total</span>
                    <span style={{ fontSize:24, fontWeight:900, color:a, fontFamily:"'DM Sans',sans-serif" }}>{fmt(finalTotal)}</span>
                  </div>
                </div>
              );
            })()}

            {/* Info box */}
            <div style={{ padding:"14px 16px", background:"rgba(37,99,235,0.08)", border:"1px solid rgba(37,99,235,0.2)", borderRadius:14, display:"flex", gap:10 }}>
              <span style={{ fontSize:16, flexShrink:0 }}>ℹ️</span>
              <div style={{ fontSize:12, color:"rgba(245,245,240,0.55)", lineHeight:1.6 }}>
                {barber.full_name?.split(" ")[0]||"Your barber"} confirms within minutes. You'll get a boarding pass once accepted — show it at the chair.
              </div>
            </div>

            {/* Cancellation policy — shown upfront so there are no surprises at cancel time */}
            <div style={{ marginTop:10, padding:"12px 14px", background:"rgba(107,114,128,0.06)", border:"1px solid rgba(107,114,128,0.2)", borderRadius:12, display:"flex", gap:10 }}>
              <span style={{ fontSize:14, flexShrink:0 }}>🗓️</span>
              <div style={{ fontSize:11, color:"rgba(245,245,240,0.45)", lineHeight:1.7 }}>
                <strong style={{ color:"rgba(245,245,240,0.65)", fontWeight:700 }}>Cancellation policy:</strong>{" "}
                {barber.cancellation_window_hours
                  ? <>Free cancellation up to <strong style={{ color:"rgba(245,245,240,0.65)" }}>{barber.cancellation_window_hours}h</strong> before your appointment. A fee may apply if cancelled later.</>
                  : <>Free cancellation up to <strong style={{ color:"rgba(245,245,240,0.65)" }}>24h</strong> before your appointment.</>
                }
              </div>
            </div>

            {myNoShows >= 2 && (
              <div style={{ marginTop:12, padding:"12px 14px", background:"rgba(245,158,11,0.08)", border:"1px solid rgba(245,158,11,0.3)", borderRadius:12 }}>
                <div style={{ fontSize:11, fontWeight:700, color:"#F59E0B" }}>⚠️ Deposit may be required — {myNoShows} previous no-shows on record.</div>
              </div>
            )}

            {/* Slot shopping soft warning */}
            {slotShoppingWarn && !slotShoppingDismissed && (
              <div style={{ marginTop:12, padding:"14px 16px", background:"rgba(245,158,11,0.08)", border:"1px solid rgba(245,158,11,0.3)", borderRadius:14 }}>
                <div style={{ fontSize:13, fontWeight:700, color:"#F59E0B", marginBottom:6 }}>Heads up — you have other bookings near this time</div>
                <div style={{ fontSize:12, color:"rgba(245,245,240,0.6)", lineHeight:1.5, marginBottom:10 }}>
                  You already have 2 or more active bookings within an hour of this slot. Make sure you can make it to all of them, or cancel one first.
                </div>
                <button
                  onClick={() => { setSlotShoppingWarn(false); setSlotShoppingDismissed(true); }}
                  style={{ fontSize:12, fontWeight:700, color:"#F59E0B", background:"rgba(245,158,11,0.12)", border:"1px solid rgba(245,158,11,0.3)", borderRadius:8, padding:"7px 14px", cursor:"pointer" }}
                >
                  I understand — book anyway
                </button>
              </div>
            )}

          </div>

        ) : step==="savecard" ? (
          <div style={{ padding:"28px 20px 140px" }}>
            {/* Header */}
            <div style={{ textAlign:"center", marginBottom:24 }}>
              <div style={{ fontSize:32, marginBottom:10 }}>
                {savecardType === "future_card" ? "💳" : savecardType === "cash_fee" ? "🏨" : "🔒"}
              </div>
              <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:20, fontWeight:900, color:"#fff", marginBottom:8 }}>
                {savecardType === "future_card" ? "Add Payment Method" : "Card Required to Book"}
              </div>
              <div style={{ fontSize:13, color:"rgba(245,245,240,0.5)", lineHeight:1.6 }}>
                {savecardType === "future_card"
                  ? <>Your card <strong style={{ color:"rgba(245,245,240,0.8)" }}>will not be charged now.</strong> A hold is placed the day before your appointment and charged only when your cut is complete.</>
                  : savecardType === "cash_fee"
                    ? <>FADED requires a card on file to secure your booking.<br/>Your card <strong style={{ color:"rgba(245,245,240,0.8)" }}>will not be charged</strong> — just saved for verification.</>
                    : <>{barber.full_name?.split(" ")[0]||"This barber"} requires a card to confirm your booking.<br/>You <strong style={{ color:"rgba(245,245,240,0.8)" }}>won't be charged</strong> unless you no-show or cancel late.</>

                }
              </div>
            </div>
            {/* Policy summary */}
            <div style={{ background:"rgba(37,99,235,0.08)", border:"1px solid rgba(37,99,235,0.2)", borderRadius:14, padding:"14px 16px", marginBottom:20 }}>
              <div style={{ fontSize:12, color:"rgba(245,245,240,0.6)", lineHeight:1.7 }}>
                {savecardType === "future_card" ? <>
                  ✂️ <strong style={{ color:"#F5F5F0" }}>How it works:</strong><br/>
                  • Card saved securely today — no charge<br/>
                  • Day before your cut: <strong style={{ color:"#F5F5F0" }}>${selectedPrice} hold</strong> placed on card<br/>
                  • After your cut: hold captured — you pay<br/>
                  • No-show or late cancel: fee may apply
                </> : savecardType === "cash_fee" ? <>
                  ✂️ <strong style={{ color:"#F5F5F0" }}>Why we need this:</strong><br/>
                  • Secures your spot — like a hotel reservation<br/>
                  • Card is <strong style={{ color:"#F5F5F0" }}>not charged now</strong><br/>
                  • A small FADED platform fee will apply when activated<br/>
                  • No-show or late cancel: fee may apply
                </> : <>
                  Your card is saved securely and only charged if:<br/>
                  • You <strong style={{ color:"#F5F5F0" }}>no-show</strong><br/>
                  • You cancel within <strong style={{ color:"#F5F5F0" }}>{barber.cancellation_window_hours || 24}h</strong> of your appointment
                </>}
              </div>
            </div>
            {/* Card element */}
            <div style={{ marginBottom:16 }}>
              <div style={{ fontSize:11, fontWeight:700, color:"rgba(245,245,240,0.4)", marginBottom:8, letterSpacing:"0.5px", textTransform:"uppercase" }}>Card Details</div>
              <div ref={setupCardRef} style={{ padding:"14px 16px", background:"rgba(245,245,240,0.05)", border:"1px solid rgba(245,245,240,0.12)", borderRadius:14 }}/>
            </div>
            {/* Error with retry state */}
            {nspCardError && (
              <div style={{ marginBottom:12 }}>
                <div style={{ fontSize:12, color:"#EF4444", padding:"10px 14px", background:"rgba(220,38,38,0.08)", border:"1px solid rgba(220,38,38,0.25)", borderRadius:10, marginBottom:8 }}>{nspCardError}</div>
                <div style={{ fontSize:11, color:"rgba(245,245,240,0.4)", textAlign:"center" }}>Check your card details and try again, or use a different card.</div>
              </div>
            )}
            {/* Save button */}
            <button onClick={()=>{ setNspCardError(""); handleSaveCard(false); }} disabled={nspSavingCard}
              style={{ width:"100%", padding:"16px", background:"#2563EB", border:"none", borderRadius:100, color:"#fff", fontSize:15, fontWeight:800, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", marginBottom:10, opacity:nspSavingCard?0.7:1 }}>
              {nspSavingCard ? "Saving card…" : nspCardError ? "Try Again" : "Save Card & Confirm Booking"}
            </button>
            {/* Skip / cancel option */}
            <button onClick={()=>handleSaveCard(true)} disabled={nspSavingCard}
              style={{ width:"100%", padding:"13px", background:"transparent", border:"1px solid rgba(245,245,240,0.12)", borderRadius:100, color:"rgba(245,245,240,0.4)", fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>
              {savecardType === "future_card"
                ? "Skip — I understand my booking may not be guaranteed"
                : savecardType === "cash_fee"
                  ? "Cancel my booking instead"
                  : "Skip — I'll pay cash and understand cancellation fees may not apply"}
            </button>
          </div>

        ) : step==="time" ? (
          <motion.div key="step-time" variants={slideInRight} initial="hidden" animate="visible" style={{ padding:"20px 20px 32px" }}>
            {/* Month header */}
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
              <div style={{ fontSize:13, fontWeight:700, color:"rgba(245,245,240,0.5)", letterSpacing:"0.06em" }}>{new Date().toLocaleDateString("en-CA",{month:"long",year:"numeric"}).toUpperCase()}</div>
              <div style={{ fontSize:11, fontWeight:700, color:"#2563EB", background:"rgba(37,99,235,0.12)", border:"1px solid rgba(37,99,235,0.25)", borderRadius:20, padding:"3px 10px" }}>This week ›</div>
            </div>

            <div style={{ fontFamily:"'JetBrains Mono','Courier New',monospace", fontSize:11, letterSpacing:"0.18em", color:t.t4, marginBottom:10, fontWeight:500 }}>SELECT DAY</div>
            <div style={{ display:"flex", gap:8, overflowX:"auto", paddingBottom:8, marginBottom:20, scrollbarWidth:"none" }}>
              {DATES.map((d,i)=>{
                const isSelected = time?.split("|")[0]===d.value;
                const isToday = d.value === _todayLocal;
                const dow = new Date(d.value+"T12:00:00").toLocaleDateString("en-CA",{weekday:"short"}).toUpperCase();
                const dayNum = new Date(d.value+"T12:00:00").getDate();
                // Manually-blocked vacation/personal day — shown dimmed with an "OFF" tag
                // so clients can tell at a glance without needing to tap in first.
                const isBlocked = barberBlockedDates.some(bd => bd.date === d.value);
                return (
                  <button key={d.value}
                    onClick={()=>{
                      const prevTime = time?.split("|")[1]||"";
                      const isNowToday = d.value === _todayLocal;
                      const timeStillValid = !prevTime || prevTime === "Now — Available" || !isNowToday || _slotMins(prevTime) > _nowMins;
                      setTime(d.value+"|"+(timeStillValid ? prevTime : ""));
                    }}
                    style={{
                      flexShrink:0, width:64, padding:"10px 0 11px",
                      background:isSelected?"#F5F5F0":isDark?"rgba(245,245,240,0.04)":t.section,
                      color:isSelected?"#0A0A0A":t.t1,
                      border:isSelected?"none":`1px solid ${t.border}`,
                      borderRadius:14, cursor:"pointer", opacity:isBlocked && !isSelected?0.45:1,
                      display:"flex", flexDirection:"column", alignItems:"center", gap:4,
                    }}>
                    <div style={{ fontFamily:"'JetBrains Mono','Courier New',monospace", fontSize:11, letterSpacing:"0.14em", color:isSelected?"#0A0A0A":t.t4 }}>{dow}</div>
                    <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:24, letterSpacing:"0.02em", lineHeight:1 }}>{dayNum}</div>
                    {isBlocked ? (
                      <div style={{ fontFamily:"'JetBrains Mono','Courier New',monospace", fontSize:11, letterSpacing:"0.12em", color:isSelected?"#9CA3AF":"#6B7280" }}>OFF</div>
                    ) : isToday && barber.status === "available" && (
                      <div style={{ fontFamily:"'JetBrains Mono','Courier New',monospace", fontSize:11, letterSpacing:"0.12em", color:isSelected?"#2563EB":"#22C55E", display:"flex", alignItems:"center", gap:3 }}>
                        <span style={{ width:4, height:4, borderRadius:"50%", background:isSelected?"#2563EB":"#22C55E", display:"inline-block" }}/>
                        LIVE
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {rescheduleBookingId && (
              <div style={{ marginBottom:12, padding:"12px 14px", background:"rgba(37,99,235,0.1)", border:"1px solid rgba(37,99,235,0.3)", borderRadius:12 }}>
                <div style={{ fontSize:13, fontWeight:700, color:"#60A5FA", marginBottom:3 }}>Rescheduling</div>
                <div style={{ fontSize:12, color:"rgba(245,245,240,0.5)", lineHeight:1.5 }}>
                  Your previous appointment{rescheduleFromTime ? ` on ${formatLocalTime(rescheduleFromTime)}` : ""} will be cancelled when you confirm a new time.
                </div>
              </div>
            )}

            {/* Fully booked for selected date — show waitlist CTA.
                Same-day waitlist only makes sense if the barber is actually online —
                if they're offline today there's no live slot to open up and get notified about. */}
            {_isHoursConfigured && !_isDayClosed && TIMES.length === 0 && (!_isToday || barber.status === "available") && (
              <div style={{ marginBottom:12, padding:"16px", background:"rgba(245,158,11,0.06)", border:"1.5px solid rgba(245,158,11,0.2)", borderRadius:14 }}>
                <div style={{ fontSize:13, fontWeight:800, color:"#F59E0B", marginBottom:4 }}>{_isFullyBookedToday ? "Fully booked today" : `Fully booked ${_selDate === _todayLocal ? "today" : "on this day"}`}</div>
                <div style={{ fontSize:12, color:"rgba(245,245,240,0.5)", lineHeight:1.5, marginBottom:10 }}>
                  No open slots on this date. Try another day above, or join the waitlist to be notified if a slot opens.
                </div>
                <button onClick={joinWaitlist} disabled={joiningWaitlist || waitlistJoined} style={{ width:"100%", padding:"10px", background:waitlistJoined?"rgba(34,197,94,0.12)":"rgba(245,158,11,0.15)", border:`1px solid ${waitlistJoined?"rgba(34,197,94,0.3)":"rgba(245,158,11,0.3)"}`, borderRadius:10, color:waitlistJoined?"#22C55E":"#F59E0B", fontSize:12, fontWeight:800, cursor:"pointer" }}>
                  {waitlistJoined ? "✓ On the waitlist" : joiningWaitlist ? "Joining..." : "Join Waitlist for this date →"}
                </button>
              </div>
            )}
            {_isHoursConfigured && !_isDayClosed && TIMES.length === 0 && _isToday && barber.status !== "available" && (
              <div style={{ marginBottom:12, padding:"16px", background:"rgba(107,114,128,0.06)", border:"1.5px solid rgba(107,114,128,0.2)", borderRadius:14 }}>
                <div style={{ fontSize:13, fontWeight:800, color:t.t3, marginBottom:4 }}>Not online today</div>
                <div style={{ fontSize:12, color:"rgba(245,245,240,0.5)", lineHeight:1.5 }}>
                  {barber.full_name?.split(" ")[0] || "This barber"} isn't accepting bookings today. Try another day above.
                </div>
              </div>
            )}
            {false && barber.walkin&&barber.status==="available"&&(
              <div style={{ marginBottom:12, padding:"10px 14px", background:"rgba(34,197,94,0.08)", border:"1px solid rgba(34,197,94,0.25)", borderRadius:12 }}>
                <span style={{ fontSize:12, fontWeight:600, color:"#22C55E" }}>Walk-in welcome today — just show up!</span>
              </div>
            )}

            {/* No working hours configured banner */}
            {!_isHoursConfigured && (
              <div style={{ marginBottom:16, padding:"14px 16px", background:"rgba(107,114,128,0.08)", border:"1px solid rgba(107,114,128,0.25)", borderRadius:12, textAlign:"center" }}>
                <div style={{ fontSize:13, fontWeight:700, color:"#9CA3AF", marginBottom:3 }}>No scheduled availability</div>
                <div style={{ fontSize:12, color:"rgba(245,245,240,0.4)" }}>{barber.full_name?.split(" ")[0]||"This barber"} hasn't set their hours yet.{barber.status === "available" ? " They're available right now — tap Now above." : " Check back soon or save them to get notified."}</div>
              </div>
            )}

            {/* Day closed banner — distinct copy for a manually-blocked vacation/personal day vs a regular recurring day off */}
            {_isDayClosed && (
              <div style={{ marginBottom:16, padding:"14px 16px", background:"rgba(107,114,128,0.08)", border:"1px solid rgba(107,114,128,0.25)", borderRadius:12, textAlign:"center" }}>
                <div style={{ fontSize:13, fontWeight:700, color:"#9CA3AF", marginBottom:3 }}>{_isVacationDay ? "Unavailable this day" : "Closed this day"}</div>
                <div style={{ fontSize:12, color:"rgba(245,245,240,0.4)" }}>
                  {_isVacationDay
                    ? `${barber.full_name?.split(" ")[0]||"This barber"} is off this day${_vacationDay?.reason ? ` (${_vacationDay.reason})` : ""}. Pick another date above.`
                    : `${barber.full_name?.split(" ")[0]||"This barber"} doesn't take bookings on this day. Pick another date above.`}
                </div>
              </div>
            )}

            {/* AM slots */}
            {!_isDayClosed && TIMES.filter(tm => tm.includes("AM") || tm === "Now — Available").length > 0 && (
              <>
                <div style={{ fontSize:11, fontWeight:800, color:"rgba(245,245,240,0.35)", letterSpacing:"0.18em", marginBottom:8, textTransform:"uppercase" }}>Morning</div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8, marginBottom:16 }}>
                  {TIMES.filter(tm => tm.includes("AM") || tm === "Now — Available").map(tm=>{
                    const selTime = time?.split("|")[1];
                    const isSelected = selTime===tm;
                    return (
                      <button key={tm} onClick={()=>{ setTime((time?.split("|")[0]||DATES[0].value)+"|"+tm); track("time_slot_selected", { time_slot: tm, barber_id: barber?.id }); }}
                        style={{ padding:"10px 6px", borderRadius:12, border:`1.5px solid ${isSelected?a:t.border}`, background:isSelected?a+"18":t.section, color:isSelected?a:t.t2, fontSize:11, fontWeight:700, cursor:"pointer", transition:"all 0.15s", textAlign:"center" }}>
                        {tm}
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {/* PM slots */}
            {!_isDayClosed && TIMES.filter(tm => tm.includes("PM")).length > 0 && (
              <>
                <div style={{ fontSize:11, fontWeight:800, color:"rgba(245,245,240,0.35)", letterSpacing:"0.18em", marginBottom:8, textTransform:"uppercase" }}>Afternoon / Evening</div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8 }}>
                  {TIMES.filter(tm => tm.includes("PM")).map(tm=>{
                    const selTime = time?.split("|")[1];
                    const isSelected = selTime===tm;
                    return (
                      <button key={tm} onClick={()=>{ setTime((time?.split("|")[0]||DATES[0].value)+"|"+tm); track("time_slot_selected", { time_slot: tm, barber_id: barber?.id }); }}
                        style={{ padding:"10px 6px", borderRadius:12, border:`1.5px solid ${isSelected?a:t.border}`, background:isSelected?a+"18":t.section, color:isSelected?a:t.t2, fontSize:11, fontWeight:700, cursor:"pointer", transition:"all 0.15s", textAlign:"center" }}>
                        {tm}
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {/* Recurrence picker */}
            {!_isDayClosed && <div style={{ marginTop:16 }}>
              <div style={{ fontSize:12, fontWeight:700, color:t.t2, marginBottom:8 }}>Repeat booking?</div>
              <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                {[["none","One-time"],["weekly","Weekly"],["biweekly","Every 2 wks"],["monthly","Monthly"]].map(([v,label])=>(
                  <button key={v} onClick={()=>setRecurrence(v)}
                    style={{ padding:"7px 13px", borderRadius:20, border:`1.5px solid ${recurrence===v?a:t.border}`, background:recurrence===v?a+"18":t.section, color:recurrence===v?a:t.t3, fontSize:11, fontWeight:700, cursor:"pointer" }}>
                    {label}
                  </button>
                ))}
              </div>
              {recurrence !== "none" && (
                <div style={{ marginTop:8, padding:"8px 12px", background:"rgba(37,99,235,0.07)", border:"1px solid rgba(37,99,235,0.2)", borderRadius:10, fontSize:11, color:t.blueText, fontWeight:600 }}>
                  📅 We'll book your next {recurrence==="weekly"?"3 weekly":recurrence==="biweekly"?"3 biweekly":"2 monthly"} appointments too — barber approval required for each.
                </div>
              )}
            </div>}

          </motion.div>

        ) : (
          <div style={{ paddingBottom:32 }}>
            <div style={{ background:`linear-gradient(160deg,${bg}33,#111)`, padding:"24px 24px 24px", borderBottom:"1px solid rgba(245,245,240,0.07)" }}>
              <div style={{ display:"flex", gap:16, alignItems:"flex-start" }}>
                <Avatar barber={barber} size={72}/>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontFamily:"'JetBrains Mono','Courier New',monospace", fontSize:11, letterSpacing:"0.2em", color:"#2563EB", marginBottom:4, fontWeight:500 }}>
                    {barber.handle?.toUpperCase() || ""}
                  </div>
                  <div style={{ fontFamily:"'Bebas Neue', sans-serif", fontSize:32, letterSpacing:"0.03em", lineHeight:1, color:t.t1, marginBottom:6 }}>
                    {(barber.full_name||"Barber").toUpperCase()}
                  </div>
                  <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                    <StatusPill status={barber.status}/>
                    {dist && <span style={{ fontSize:12, color:t.t4 }}>📍 {dist}</span>}
                    {barber.is_mobile&&<span style={{ fontSize:11, background:t.blueBg, color:t.blueText, fontWeight:700, padding:"3px 8px", borderRadius:10 }}>Mobile</span>}
                    {false && barber.walkin&&barber.status==="available"&&<span style={{ fontSize:11, background:"#DCFCE7", color:"#16A34A", fontWeight:700, padding:"3px 8px", borderRadius:10 }}>Walk-in OK</span>}
                  </div>
                </div>
                {barber.is_founding_barber&&(
                  <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:2, flexShrink:0 }}>
                    <FoundingBadge size={64}/>
                  </div>
                )}
              </div>
              {barber.is_founding_barber&&(
                <div style={{ marginTop:12, padding:"8px 14px", background:"rgba(245,158,11,0.08)", border:"1px solid rgba(245,158,11,0.2)", borderRadius:12, display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ fontSize:13 }}>✦</span>
                  <span style={{ fontSize:12, fontWeight:700, color:"#F59E0B", letterSpacing:"0.05em" }}>FADED Founding Barber</span>
                  <span style={{ fontSize:12, color:"rgba(245,245,240,0.4)", marginLeft:"auto" }}>One of the first 20</span>
                </div>
              )}
              {barber.years_cutting > 0 && (
                <div style={{ display:"inline-flex", alignItems:"center", gap:6, marginTop:10, padding:"4px 12px", background:"rgba(37,99,235,0.1)", border:"1px solid rgba(37,99,235,0.25)", borderRadius:20 }}>
                  <span style={{ fontSize:13 }}>✂️</span>
                  <span style={{ fontSize:12, fontWeight:700, color:"#60A5FA" }}>{barber.years_cutting} year{barber.years_cutting!==1?"s":""} cutting hair</span>
                </div>
              )}
              {barber.bio&&<p style={{ fontSize:13, color:t.t3, marginTop:14, lineHeight:1.6 }}>{barber.bio}</p>}

              {/* Address + directions */}
              {(barber.address || (barber.lat && barber.lng)) && (
                <div style={{ marginTop:14 }}>
                  {barber.address && (
                    <div style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 12px", background:t.section, borderRadius:10, border:`1px solid ${t.border}`, marginBottom:10 }}>
                      <span style={{ fontSize:14 }}>📍</span>
                      <span style={{ fontSize:12, color:t.t2, fontWeight:600, flex:1 }}>{barber.address}</span>
                    </div>
                  )}
                  {barber.lat && barber.lng && MAPBOX_TOKEN && (
                    <div style={{ borderRadius:14, overflow:"hidden", height:150, position:"relative", border:"1px solid rgba(245,245,240,0.08)", marginBottom:10, background:"#111" }}>
                      <img
                        src={`https://api.mapbox.com/styles/v1/mapbox/dark-v11/static/pin-s+2563eb(${barber.lng},${barber.lat})/${barber.lng},${barber.lat},15,0/600x300?access_token=${MAPBOX_TOKEN}&attribution=false&logo=false`}
                        alt="Location map"
                        style={{ width:"100%", height:"100%", objectFit:"cover" }}
                        onError={e=>{ e.target.style.display="none"; }}
                      />
                      {dist && <div style={{ position:"absolute", top:8, left:8, background:"rgba(10,10,10,0.85)", backdropFilter:"blur(6px)", WebkitBackdropFilter:"blur(6px)", borderRadius:8, padding:"4px 10px", fontSize:11, fontWeight:700, color:"#F5F5F0", boxShadow:"0 2px 8px rgba(0,0,0,0.4)" }}>📍 {dist}</div>}
                    </div>
                  )}
                  <div>
                    <button
                      onClick={()=>setShowMapPicker(v=>!v)}
                      style={{ width:"100%", padding:"11px 10px", background:t.blueBg, border:`1.5px solid ${t.blueBorder}`, borderRadius:showMapPicker?"12px 12px 0 0":12, color:t.blueText, fontSize:13, fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:6 }}>
                      📍 Get Directions {showMapPicker ? "▲" : "▼"}
                    </button>
                    {showMapPicker && (
                      <div style={{ border:`1.5px solid ${t.blueBorder}`, borderTop:"none", borderRadius:"0 0 12px 12px", overflow:"hidden" }}>
                        {mapLinks.map((m, i) => (
                          <button key={m.label} onClick={()=>{ const a=document.createElement("a"); a.href=m.url; a.rel="noopener noreferrer"; if(!m.url.startsWith("maps:")&&!m.url.startsWith("waze:"))a.target="_blank"; document.body.appendChild(a); a.click(); setTimeout(()=>document.body.removeChild(a),0); setShowMapPicker(false); }}
                            style={{ width:"100%", padding:"11px 14px", background:i%2===0?t.section:t.card, border:"none", borderTop:`1px solid ${t.border}`, color:t.t1, fontSize:13, fontWeight:600, cursor:"pointer", display:"flex", alignItems:"center", gap:10, fontFamily:"'DM Sans',sans-serif" }}>
                            <span>{m.emoji}</span><span>{m.label}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginTop:16 }}>
                {[["Rating",barber.review_count>0?barber.rating:"New"],["Cuts",(barber.total_cuts||0).toLocaleString()],["Reviews",barber.review_count]].map(([label,val])=>(
                  <div key={label} style={{ background:t.section, borderRadius:14, padding:"12px 8px", textAlign:"center", border:`1px solid ${t.border}` }}>
                    <div style={{ fontSize:16, fontWeight:900, color:t.t1, fontFamily:"'DM Sans',sans-serif" }}>{val}</div>
                    <div style={{ fontSize:11, color:t.t4, marginTop:2 }}>{label}</div>
                  </div>
                ))}
              </div>
              {/* Neighbourhood + languages */}
              <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginTop:14 }}>
                {barber.neighbourhood && <span style={{ fontSize:11, fontWeight:600, color:t.t3, background:t.section, border:`1px solid ${t.border}`, borderRadius:20, padding:"3px 10px" }}>📍 {barber.neighbourhood}</span>}
                {barber.is_licensed && <span title="Licence information is self-reported by the barber and has not been independently verified by FADED." style={{ fontSize:11, fontWeight:700, color:"#059669", background:"rgba(5,150,105,0.1)", border:"1px solid rgba(5,150,105,0.3)", borderRadius:20, padding:"3px 10px", cursor:"help" }}>✓ Licensed*</span>}
                {(barber.languages||[]).slice(0,3).map(l=><span key={l} style={{ fontSize:11, fontWeight:600, color:t.t3, background:t.section, border:`1px solid ${t.border}`, borderRadius:20, padding:"3px 10px" }}>🗣 {l}</span>)}
              </div>
              {barber.is_licensed && (
                <div style={{ fontSize:10, color:t.t4, marginTop:6, lineHeight:1.5 }}>
                  * Licence information is self-reported by the barber. FADED does not independently verify licence status.
                </div>
              )}
              {barber.hst_registered && (
                <div style={{ fontSize:10, color:t.t4, marginTop:4, lineHeight:1.5 }}>
                  HST registration is self-reported. Clients are responsible for confirming HST applicability to their transaction.
                </div>
              )}
              {/* Google Review button */}
              {barber.google_review_url && (
                <a href={barber.google_review_url} target="_blank" rel="noopener noreferrer"
                  style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:8, width:"100%", marginTop:14, padding:"11px", background:"rgba(251,188,5,0.1)", border:"1.5px solid rgba(251,188,5,0.35)", borderRadius:12, color:"#FBBF24", fontSize:13, fontWeight:700, textDecoration:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box" }}>
                  ⭐ Leave a Google Review
                </a>
              )}

              {/* Report profile — required by Apple Guideline 1.2 */}
              <div style={{ marginTop:14, textAlign:"right" }}>
                <button onClick={()=>{ const sub=encodeURIComponent(`Report barber profile: ${barber.full_name} (ID: ${barber.id})`); window.open(`mailto:hello@letsgetfaded.com?subject=${sub}&body=Please describe the issue with this profile:%0A%0A`); }} style={{ background:"none", border:"none", cursor:"pointer", fontSize:11, color:t.t4, padding:"2px 4px", borderRadius:6, textDecoration:"underline" }}>Report this profile</button>
              </div>
            </div>

            <div style={{ padding:"18px 24px 0" }}>
              <div style={{ fontSize:11, fontWeight:700, color:t.t4, letterSpacing:1.5, marginBottom:10 }}>SERVICES & PRICING</div>
              {SERVICES.length > 0 ? (
                <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                  {SERVICES.slice(0,6).map(s => {
                    const sName = s.name || s;
                    const sPrice = s.price ?? barber.price_min;
                    const sDur = s.duration_minutes;
                    return (
                      <div key={sName}
                        style={{
                          display:"flex", alignItems:"center", gap:12, padding:"14px 14px",
                          background:t.section, border:`1px solid ${t.border}`,
                          borderRadius:14,
                        }}>
                        <div style={{ flex:1 }}>
                          <div style={{ fontSize:14, fontWeight:600, color:t.t1 }}>{sName}</div>
                          {sDur && <div style={{ fontFamily:"'JetBrains Mono','Courier New',monospace", fontSize:11, color:t.t4, marginTop:2, letterSpacing:"0.06em" }}>{sDur} MIN</div>}
                        </div>
                        <div style={{ fontFamily:"'Bebas Neue', sans-serif", fontSize:22, letterSpacing:"0.04em", color:t.t1 }}>${sPrice}</div>
                      </div>
                    );
                  })}
                  {SERVICES.length > 6 && (
                    <div style={{ fontSize:11, color:t.t4, textAlign:"center", padding:"6px 0" }}>+{SERVICES.length - 6} more services</div>
                  )}
                </div>
              ) : (
                <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>{(barber.specialty||[]).map(sp=><Tag key={sp} label={sp} accent={a}/>)}</div>
              )}
            </div>

            <div style={{ padding:"18px 24px 0" }}>
              <div style={{ fontSize:11, fontWeight:700, color:t.t4, letterSpacing:1.5, marginBottom:10 }}>PORTFOLIO</div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:6 }}>
                {(barber.portfolio_urls||[]).length > 0
                  ? (barber.portfolio_urls||[]).slice(0,6).map((url,i)=>(
                      <div key={i} style={{ aspectRatio:"1", borderRadius:12, overflow:"hidden" }}>
                        <img src={url} alt={`work ${i+1}`} style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
                      </div>
                    ))
                  : GRID_COLORS.map((c,i)=><div key={i} style={{ aspectRatio:"1", borderRadius:12, background:`linear-gradient(135deg,${c},${a}22)`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:20 }}>✂️</div>)
                }
              </div>
            </div>

            <div style={{ padding:"18px 24px 0" }}>
              <div style={{ fontSize:11, fontWeight:700, color:t.t4, letterSpacing:1.5, marginBottom:12 }}>REVIEWS</div>
              {(() => {
                const displayReviews = barber._isDemo
                  ? MOCK_REVIEWS
                  : barberReviews.length > 0
                    ? barberReviews.map(r => ({ ...r, client_name: r.profiles?.full_name || "Client", created_at: new Date(r.created_at).toLocaleDateString("en-CA",{month:"short",day:"numeric"}) }))
                    : null;
                if (!displayReviews) return (
                  <div style={{ textAlign:"center", padding:"24px 16px", background:t.section, borderRadius:16, border:`1px solid ${t.border}` }}>
                    <div style={{ fontSize:28, marginBottom:8 }}>⭐</div>
                    <div style={{ fontSize:13, fontWeight:700, color:t.t1, marginBottom:4 }}>No reviews yet</div>
                    <div style={{ fontSize:11, color:t.t4 }}>Be the first — book a cut and leave a review after.</div>
                  </div>
                );
                return (
                  <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                    {displayReviews.map((r,i)=>(
                      <div key={i} style={{ background:t.section, borderRadius:16, padding:16, border:`1px solid ${t.border}` }}>
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                            <div style={{ width:28, height:28, borderRadius:"50%", background:bg, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:700, color:a }}>{(r.client_name||"C")[0]}</div>
                            <span style={{ fontSize:13, fontWeight:700, color:t.t1 }}>{r.client_name}</span>
                          </div>
                          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                            <span style={{ fontSize:11, color:t.t4 }}>{r.created_at}</span>
                            {/* UGC report button — required by Apple */}
                            <button onClick={()=>{ const sub=encodeURIComponent(`Report review by ${r.client_name} on ${barber.full_name}'s profile`); window.open(`mailto:hello@letsgetfaded.com?subject=${sub}&body=Review content: ${encodeURIComponent(r.comment)}`); }} style={{ background:"none", border:"none", cursor:"pointer", fontSize:11, color:"rgba(245,245,240,0.25)", padding:"2px 4px", borderRadius:6 }} title="Report this review">⚑</button>
                          </div>
                        </div>
                        <p style={{ fontSize:13, color:t.t2, lineHeight:1.6, marginBottom:6 }}>{r.comment}</p>
                        <Stars n={r.rating}/>
                        {r.barber_reply && (
                          <div style={{ marginTop:10, padding:"8px 12px", background:a+"12", borderRadius:10, borderLeft:`2px solid ${a}` }}>
                            <div style={{ fontSize:11, fontWeight:700, color:a, marginBottom:4 }}>Barber replied</div>
                            <div style={{ fontSize:12, color:t.t2 }}>{r.barber_reply}</div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>

            <div style={{ padding:"0 24px 20px" }}>
              {false && barber.walkin && barber.status === "available" && (() => {
                const maxQ = barber.max_walkin_queue || 0;
                const queueFull = maxQ > 0 && walkinQueueDepth >= maxQ;
                if (walkinSuccess) return (
                  <div style={{ marginTop:10, padding:"20px 16px", background:"rgba(34,197,94,0.1)", border:"1px solid rgba(34,197,94,0.3)", borderRadius:16, textAlign:"center" }}>
                    <div style={{ fontSize:28, marginBottom:8 }}>✅</div>
                    <div style={{ fontSize:15, fontWeight:800, color:"#22C55E", marginBottom:4 }}>You're in the queue!</div>
                    <div style={{ fontSize:12, color:"rgba(245,245,240,0.6)", lineHeight:1.5 }}>
                      {walkinQueueDepth > 1 ? `${walkinQueueDepth - 1} ${walkinQueueDepth - 1 === 1 ? "person" : "people"} ahead of you. ` : "You're up next. "}
                      We'll text you when it's your turn.
                    </div>
                  </div>
                );
                return (
                  <div style={{ marginTop:10, padding:"14px 16px", background: queueFull ? "rgba(107,114,128,0.08)" : "rgba(34,197,94,0.08)", border:`1px solid ${queueFull ? "rgba(107,114,128,0.25)" : "rgba(34,197,94,0.3)"}`, borderRadius:16 }}>
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom: walkinQueueDepth > 0 ? 6 : 10 }}>
                      <div>
                        <div style={{ fontSize:13, fontWeight:800, color: queueFull ? "#6B7280" : "#22C55E" }}>
                          {queueFull ? "Queue Full" : "Walk-ins welcome"}
                        </div>
                        {walkinQueueDepth > 0 && (
                          <div style={{ fontSize:11, color:"rgba(245,245,240,0.5)", marginTop:2 }}>
                            {walkinQueueDepth} {walkinQueueDepth === 1 ? "person" : "people"} waiting{maxQ > 0 ? ` · max ${maxQ}` : ""}
                          </div>
                        )}
                      </div>
                      {!queueFull && (
                        <button onClick={()=>{ setWalkinErr(""); setWalkinSheetOpen(true); }}
                          style={{ padding:"10px 18px", background:"#22C55E", border:"none", borderRadius:100, color:"#fff", fontSize:13, fontWeight:800, cursor:"pointer", whiteSpace:"nowrap" }}>
                          Join Queue
                        </button>
                      )}
                    </div>
                    {queueFull && <div style={{ fontSize:11, color:"rgba(245,245,240,0.45)", textAlign:"center" }}>Check back soon or book an appointment above.</div>}
                  </div>
                );
              })()}
            </div>

            {/* Walk-in queue bottom sheet */}
            {walkinSheetOpen && (
              <div onClick={()=>setWalkinSheetOpen(false)} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:1000, display:"flex", alignItems:"flex-end" }}>
                <div onClick={e=>e.stopPropagation()} style={{ width:"100%", background:"#111", borderRadius:"20px 20px 0 0", padding:"24px 20px 40px", boxSizing:"border-box" }}>
                  <div style={{ width:36, height:4, background:"rgba(255,255,255,0.15)", borderRadius:2, margin:"0 auto 20px" }}/>
                  <div style={{ fontSize:18, fontWeight:800, color:"#fff", marginBottom:4 }}>Join Walk-in Queue</div>
                  <div style={{ fontSize:13, color:"rgba(245,245,240,0.5)", marginBottom:20 }}>
                    You'll get a text when it's your turn.
                    {walkinQueueDepth > 0 && ` ${walkinQueueDepth} ${walkinQueueDepth === 1 ? "person" : "people"} ahead of you.`}
                  </div>
                  <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:16 }}>
                    <input value={clientName} onChange={e=>setClientName(e.target.value)}
                      placeholder="Your name" autoFocus
                      style={{ padding:"14px 16px", borderRadius:12, border:"1px solid rgba(255,255,255,0.12)", background:"rgba(255,255,255,0.07)", color:"#fff", fontSize:15, fontWeight:600, outline:"none", width:"100%", boxSizing:"border-box" }}/>
                    <input value={clientPhone} onChange={e=>setClientPhone(e.target.value)}
                      placeholder="Phone number (for updates)" type="tel"
                      style={{ padding:"14px 16px", borderRadius:12, border:"1px solid rgba(255,255,255,0.12)", background:"rgba(255,255,255,0.07)", color:"#fff", fontSize:15, fontWeight:600, outline:"none", width:"100%", boxSizing:"border-box" }}/>
                  </div>
                  {walkinErr && <div style={{ fontSize:13, color:"#EF4444", marginBottom:12 }}>{walkinErr}</div>}
                  <button disabled={walkinJoining} onClick={async()=>{
                    if (!clientName.trim()) { setWalkinErr("Please enter your name."); return; }
                    setWalkinJoining(true); setWalkinErr("");
                    try {
                      const { error: qErr } = await supabase.from("walk_in_queue").insert({ barber_id: barber.id, client_name: clientName.trim(), client_phone: normalizePhone(clientPhone), service: selectedService || "Haircut", status:"waiting" });
                      if (qErr) throw qErr;
                      setWalkinQueueDepth(v => v + 1);
                      setWalkinSheetOpen(false);
                      setWalkinSuccess(true);
                    } catch { setWalkinErr("Couldn't join — try again."); }
                    finally { setWalkinJoining(false); }
                  }} style={{ width:"100%", padding:"16px", background: walkinJoining ? "rgba(34,197,94,0.5)" : "#22C55E", border:"none", borderRadius:100, color:"#fff", fontSize:16, fontWeight:800, cursor: walkinJoining ? "default":"pointer" }}>
                    {walkinJoining ? "Joining…" : "I'm on my way →"}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Sticky Book Now bar — always visible when on barber profile ── */}
      {step === "profile" && !done && (
        <div style={{ flexShrink:0, background:"rgba(10,10,10,0.96)", ...(isDesktop ? {} : { backdropFilter:"blur(20px)", WebkitBackdropFilter:"blur(20px)" }), borderTop:"1px solid rgba(245,245,240,0.08)", padding:"10px 16px max(env(safe-area-inset-bottom,20px),20px)" }}>
          {barber.bookings_paused ? (
            <div style={{ width:"100%", padding:"17px", background:"rgba(245,158,11,0.08)", border:"1.5px solid rgba(245,158,11,0.25)", borderRadius:100, color:"#F59E0B", fontFamily:"'Bebas Neue',sans-serif", fontSize:18, letterSpacing:"0.14em", display:"flex", alignItems:"center", justifyContent:"center", gap:10 }}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="4" y="3" width="2.5" height="10" rx="1" fill="#F59E0B"/><rect x="9.5" y="3" width="2.5" height="10" rx="1" fill="#F59E0B"/></svg>
              NOT ACCEPTING BOOKINGS
            </div>
          ) : (
            <button onClick={()=>setStep("service")} style={{
              width:"100%", padding:"17px", background: barber.status==="available" ? "#2563EB" : "rgba(100,100,100,0.5)",
              border:"none", borderRadius:100, color:"#fff",
              fontFamily:"'Bebas Neue',sans-serif", fontSize:20, letterSpacing:"0.14em",
              cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:10,
              boxShadow: barber.status==="available" ? "0 2px 14px rgba(37,99,235,0.45)" : "none",
            }}>
              {barber.status==="available" ? "BOOK NOW" : "SCHEDULE"}
              <svg aria-hidden="true" focusable="false" width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 7h10M8 3l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
          )}
        </div>
      )}

      {/* ── Step CTA bar — sticky bottom for all booking steps ── */}
      {!done && step !== "profile" && step !== "savecard" && (() => {
        const timeSelected = !!(time && time.includes("|") && time.split("|")[1] && time.split("|")[1].length > 0);
        const canGo =
          step === "service"  ? (!!selectedService && clientName.trim().length > 0 && clientPhone.trim().length > 0) :
          step === "time"     ? timeSelected :
          step === "payment"  ? !!payment :
          step === "confirm"  ? (!booking && !waitlistJoined && !doubleBookErr) : false;
        const label =
          step === "service"  ? "CONTINUE TO TIME →" :
          step === "time"     ? (timeSelected ? `${time.split("|")[1]} · CONTINUE →` : "SELECT A TIME TO CONTINUE") :
          step === "payment"  ? "REVIEW BOOKING →" :
          booking             ? "Booking…" : `CONFIRM BOOKING · $${selectedPrice}${selectedAddons.length > 0 ? ` (${1 + selectedAddons.length} services)` : ""}`;
        const go = () => {
          if (!canGo) return;
          if (step === "service") {
            track("booking_started", { service_type: selectedService, price: selectedPrice });
            Sentry.addBreadcrumb({ category:"booking", message:`Service selected: ${selectedService} $${selectedPrice}`, level:"info" });
            setStep("time");
          } else if (step === "time") {
            Sentry.addBreadcrumb({ category:"booking", message:`Time slot selected: ${time?.split("|")[1]||"Now"}`, level:"info" });
            setStep("payment");
          } else if (step === "payment") {
            track("payment_screen_reached", { service_type: selectedService, price: selectedPrice });
            Sentry.addBreadcrumb({ category:"booking", message:`Payment method selected: ${payment}`, level:"info" });
            // Price staleness check — re-fetch service price before client reaches confirm screen
            if (selectedSvcObj && !barber._isDemo) {
              supabase.from("barber_services").select("price,is_active").eq("barber_id", barber.id).eq("name", selectedService).single()
                .then(({ data: freshSvc }) => {
                  if (freshSvc && !freshSvc.is_active) {
                    setDoubleBookErr(true);
                    setDoubleBookMsg(`${barber.full_name?.split(" ")[0]||"This barber"} just paused that service. Pick another service to continue.`);
                    setStep("service");
                    return;
                  }
                  if (freshSvc && Number(freshSvc.price) !== Number(selectedSvcObj.price)) {
                    setDoubleBookMsg(`The price for ${selectedService} changed from ${fmt(selectedSvcObj.price)} to ${fmt(freshSvc.price)}. Returning to service selection.`);
                    setDoubleBookErr(true);
                    setStep("service");
                    return;
                  }
                }).catch(() => {});
            }
            setStep("confirm");
          } else if (step === "confirm") {
            handleConfirm().catch(err => {
              setBooking(false);
              setDoubleBookErr(true);
              setDoubleBookMsg("Something went wrong — please try again.");
              Sentry.captureException(err, { tags: { flow:"booking", step:"confirm" } });
            });
          }
        };
        return (
          <div style={{ flexShrink:0, background:"rgba(10,10,10,0.97)", ...(isDesktop ? {} : { backdropFilter:"blur(20px)", WebkitBackdropFilter:"blur(20px)" }), borderTop:"1px solid rgba(245,245,240,0.07)", padding:"10px 20px max(env(safe-area-inset-bottom,20px),20px)" }}>
            {step === "confirm" && isWalletPayment && STRIPE_AVAILABLE ? (
              <div>
                {prCanPay === null && (
                  <div style={{ width:"100%", padding:"16px", background:"rgba(255,255,255,0.06)", borderRadius:100, color:"rgba(255,255,255,0.3)", fontSize:15, fontWeight:800, textAlign:"center", fontFamily:"'DM Sans',sans-serif" }}>
                    {payment === "Apple Pay" ? "Loading Apple Pay…" : "Loading Google Pay…"}
                  </div>
                )}
                {prCanPay === false && (
                  <div style={{ width:"100%", padding:"14px 16px", background:"rgba(220,38,38,0.08)", borderRadius:14, border:"1px solid rgba(220,38,38,0.2)", fontSize:13, color:"rgba(245,245,240,0.6)", textAlign:"center" }}>
                    {payment} isn't available on this device — go back and choose a different payment method.
                  </div>
                )}
                <div ref={prButtonRef} style={{ display: prCanPay ? "block" : "none", borderRadius:14, overflow:"hidden" }}/>
              </div>
            ) : (
              <button onClick={go} disabled={!canGo}
                style={{ width:"100%", padding:"16px", background:canGo?"#2563EB":"rgba(255,255,255,0.08)", border:"none", borderRadius:100, color:canGo?"#fff":"rgba(255,255,255,0.3)", fontSize:15, fontWeight:800, cursor:canGo?"pointer":"default", fontFamily:"'DM Sans',sans-serif", letterSpacing:"0.3px" }}>
                {label}
              </button>
            )}
          </div>
        );
      })()}
    </div>
  );
}

// ─── BARBER SCHEDULE ──────────────────────────────────────────────────────────
function BarberSchedule({ session, myBarberData, targetBarberId, viewerRole, targetLabel, onClose }) {
  const barberId = targetBarberId || session.user.id;
  const isOwnSchedule = barberId === session?.user?.id;
  const [appointments, setAppointments] = useState([]);
  const [showAdd, setShowAdd]           = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [loading, setLoading]           = useState(true);
  const [form, setForm]                 = useState({ client_name:"", phone:"", service:"Haircut", time:"", price:"", notes:"", source:"manual" });
  const [saving, setSaving]             = useState(false);
  const [manualErr, setManualErr]       = useState("");

  // Mid-appointment add-on state
  const [addonServices, setAddonServices]     = useState([]);
  const [addServiceAppt, setAddServiceAppt]   = useState(null);
  const [addingService, setAddingService]     = useState(false);
  const [addedMsg, setAddedMsg]               = useState("");

  // No-show fee modal
  const [noShowModal, setNoShowModal] = useState(null); // { appt, feeCents, feeLabel }
  const [noShowCharging, setNoShowCharging] = useState(false);
  const [noShowError, setNoShowError] = useState("");

  const [historyAppt, setHistoryAppt]   = useState(null); // appt.id currently expanded
  const [clientHistory, setClientHistory] = useState({});  // { [apptId]: { visits, total, lastDate } | "loading" }

  const [scheduleServices, setScheduleServices] = useState(["Haircut","Skin Fade","Taper","Lineup","Beard Trim","Hot Towel Shave","Braids","Shape-Up","Kid's Cut"]);
  useEffect(() => {
    if (!barberId) return;
    supabase.from("barber_services").select("*").eq("barber_id", barberId).eq("is_active", true).order("sort_order")
      .then(({ data }) => {
        if (data && data.length > 0) {
          setScheduleServices(data.filter(s => !s.is_addon).map(s => s.name));
          setAddonServices(data.filter(s => s.is_addon));
        }
      }).catch(() => {});
  }, [barberId]); // eslint-disable-line react-hooks/exhaustive-deps
  const SERVICES = scheduleServices;

  const addServiceToBooking = async (appt, addon) => {
    setAddingService(true);
    const newService = appt.service ? `${appt.service} + ${addon.name}` : addon.name;
    const currentPrice = parseFloat(String(appt.price).replace("$","")) || 0;
    const newPrice = currentPrice + (addon.price || 0);

    if (appt.source === "app") {
      await supabase.from("bookings").update({
        service_type: newService,
        price_agreed: newPrice,
      }).eq("id", appt.id);
    } else {
      await supabase.from("manual_appointments").update({
        service: newService,
        price: `$${newPrice}`,
      }).eq("id", appt.id);
    }

    setAppointments(prev => prev.map(a => a.id === appt.id
      ? { ...a, service: newService, price: `$${newPrice}` }
      : a
    ));
    setAddedMsg(`${addon.name} added — new total $${newPrice}`);
    setAddingService(false);
    setTimeout(() => { setAddServiceAppt(null); setAddedMsg(""); }, 1800);
  };
  const _bsNowMins  = (() => { const n=new Date(); return n.getHours()*60+n.getMinutes(); })();
  const _bsToday    = (() => { const d=new Date(); return d.toISOString().split("T")[0]; })();
  const _bsSlotMins = s => { const [tp,ap]=s.split(" "); let [h,m]=tp.split(":").map(Number); if(ap==="PM"&&h!==12)h+=12; if(ap==="AM"&&h===12)h=0; return h*60+m; };
  const TIMES = ["8:00 AM","8:30 AM","9:00 AM","9:30 AM","10:00 AM","10:30 AM","11:00 AM","11:30 AM","12:00 PM","12:30 PM","1:00 PM","1:30 PM","2:00 PM","2:30 PM","3:00 PM","3:30 PM","4:00 PM","4:30 PM","5:00 PM","5:30 PM","6:00 PM","6:30 PM","7:00 PM","7:30 PM","8:00 PM"]
    .filter(tm => selectedDate !== _bsToday || _bsSlotMins(tm) > _bsNowMins);

  const upd = (k,v) => setForm(f=>({...f,[k]:v}));

  useEffect(() => {
    if (!session || session === "guest" || !session.user || !barberId) return;
    loadAppointments();
  }, [session, selectedDate, barberId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAppointments = async () => {
    setLoading(true);
    try {
    const startOfDay = new Date(selectedDate + "T00:00:00").toISOString();
    const endOfDay   = new Date(selectedDate + "T23:59:59").toISOString();

    // Load app bookings for this barber on this date
    const { data:appBookings } = await supabase.from("bookings")
      .select("*")
      .eq("barber_id", barberId)
      .gte("scheduled_at", startOfDay)
      .lte("scheduled_at", endOfDay)
      .neq("status", "declined");

    // Load manual appointments
    const { data:manualAppts, error:manualErr } = await supabase.from("manual_appointments")
      .select("*")
      .eq("barber_id", barberId)
      .eq("date", selectedDate)
      .order("time_slot");
    if (manualErr && manualErr.code !== "42P01") Sentry.captureException(manualErr, { tags: { operation: "fetch_manual_appointments" } });

    // Names for "added by shop owner/staff" attribution on appointments this viewer didn't create themselves
    const creatorIds = [...new Set((manualAppts||[]).filter(m => m.created_by_role !== "barber" && m.created_by).map(m => m.created_by))];
    let creatorNames = {};
    if (creatorIds.length > 0) {
      const { data: creatorProfiles } = await supabase.from("profiles").select("id, full_name").in("id", creatorIds);
      creatorNames = Object.fromEntries((creatorProfiles||[]).map(p => [p.id, p.full_name]));
    }

    const all = [
      ...(appBookings||[]).map(b => ({
        id: b.id,
        client_name: b.client_name || "App Client",
        phone: b.client_phone || "",
        service: b.service_type || "Haircut",
        time: b.scheduled_at ? new Date(b.scheduled_at).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}) : "TBD",
        time_sort: b.scheduled_at ? new Date(b.scheduled_at).getHours()*60+new Date(b.scheduled_at).getMinutes() : 0,
        price: b.price_agreed ? `$${b.price_agreed}` : "",
        price_agreed: b.price_agreed || 0,
        notes: b.notes || "",
        source: "app",
        status: b.status || "pending",
        checked_in_at: b.checked_in_at || null,
        payment_method: b.payment_method || "",
        saved_payment_method_id: b.saved_payment_method_id || null,
        stripe_payment_intent_id: b.stripe_payment_intent_id || null,
        no_show_at: b.no_show_at || null,
        scheduled_at_raw: b.scheduled_at || null,
        client_id: b.client_id || null,
      })),
      ...(manualAppts||[]).map(m => ({
        id: m.id,
        client_name: m.client_name,
        phone: m.phone || "",
        service: m.service,
        time: m.time_slot,
        time_sort: parseInt(m.time_slot.replace(":","")) || 0,
        price: m.price || "",
        notes: m.notes || "",
        source: "manual",
        status: m.status || "confirmed",
        created_by_role: m.created_by_role || "barber",
        creator_name: creatorNames[m.created_by] || null,
      }))
    ].sort((a,b) => a.time_sort - b.time_sort);

    setAppointments(all);
    } catch (e) {
      console.error("[FADED] loadAppointments:", e);
    } finally {
      setLoading(false);
    }
  };

  const saveManual = async () => {
    if (!form.client_name || !form.time) { return; return; }
    setSaving(true);
    setManualErr("");
    if (isOwnSchedule) {
      const { data: result, error } = await supabase.rpc("try_book_manual_slot", {
        p_barber_id: barberId,
        p_client_name: sanitize(form.client_name, 100),
        p_phone: sanitize(form.phone, 30),
        p_service: sanitize(form.service, 80),
        p_time_slot: form.time,
        p_price: sanitize(form.price, 20),
        p_notes: sanitize(form.notes, 500),
        p_date: selectedDate,
      });
      setSaving(false);
      if (!error && result?.success) { setShowAdd(false); setForm({ client_name:"", phone:"", service:"Haircut", time:"", price:"", notes:"", source:"manual" }); loadAppointments(); }
      else setManualErr(result?.message || "Could not save this appointment. Please try again.");
    } else {
      // Owner / trusted-employee adding to someone else's schedule — inserts directly,
      // governed by the shop-scheduling RLS policies rather than the barber-self RPC.
      const { error } = await supabase.from("manual_appointments").insert({
        barber_id: barberId,
        client_name: sanitize(form.client_name, 100),
        phone: sanitize(form.phone, 30),
        service: sanitize(form.service, 80),
        time_slot: form.time,
        price: sanitize(form.price, 20),
        notes: sanitize(form.notes, 500),
        date: selectedDate,
        created_by: session.user.id,
        created_by_role: viewerRole === "owner" ? "owner" : "trusted_employee",
      });
      setSaving(false);
      if (!error) { setShowAdd(false); setForm({ client_name:"", phone:"", service:"Haircut", time:"", price:"", notes:"", source:"manual" }); loadAppointments(); }
      else setManualErr(error.message || "Could not save this appointment. Please try again.");
    }
  };

  const updateStatus = async (appt, status) => {
    if (appt.source === "app") {
      await supabase.from("bookings").update({ status }).eq("id", appt.id);
    } else {
      await supabase.from("manual_appointments").update({ status }).eq("id", appt.id);
    }
    setAppointments(prev => prev.map(a => a.id===appt.id ? {...a, status} : a));
  };

  const deleteManual = async (id) => {
    if (!await fakedConfirm("Remove this appointment?")) return;
    await supabase.from("manual_appointments").delete().eq("id", id);
    setAppointments(prev => prev.filter(a => a.id !== id));
  };

  const loadClientHistory = async (appt) => {
    if (!appt.client_id) return;
    setClientHistory(prev => ({ ...prev, [appt.id]: "loading" }));
    const { data } = await supabase.from("bookings")
      .select("scheduled_at, service_type, price_agreed")
      .eq("barber_id", barberId)
      .eq("client_id", appt.client_id)
      .eq("status", "completed")
      .order("scheduled_at", { ascending: false })
      .limit(20);
    const visits = data || [];
    const total = visits.reduce((s, b) => s + (b.price_agreed || 0), 0);
    const lastDate = visits[0]?.scheduled_at ? new Date(visits[0].scheduled_at) : null;
    setClientHistory(prev => ({ ...prev, [appt.id]: { visits, total, lastDate } }));
  };

  const openNoShowModal = async (appt) => {
    if (appt.source !== "app") {
      updateStatus(appt, "no_show");
      return;
    }
    // Block marking a future appointment as no-show
    if (appt.scheduled_at_raw && new Date(appt.scheduled_at_raw) > new Date()) {
      setNoShowError("This appointment hasn't happened yet — mark no-show after the scheduled time.");
      return;
    }
    // Load barber no-show policy to calculate fee
    const { data: barberPolicy } = await supabase
      .from("barbers")
      .select("no_show_protection, no_show_fee_type, no_show_fee_flat_cents")
      .eq("id", barberId)
      .single();
    const hasSavedCard = !!appt.saved_payment_method_id;
    const isCardBooking = appt.payment_method === "Credit / Debit Card" && !!appt.stripe_payment_intent_id;
    const priceAgreedCents = Math.round((appt.price_agreed || 0) * 100);
    let feeCents = 0;
    let feeLabel = "No fee";
    if (barberPolicy?.no_show_protection && (hasSavedCard || isCardBooking) && priceAgreedCents > 0) {
      const feeType = barberPolicy.no_show_fee_type || "hundred_percent";
      const flatCents = barberPolicy.no_show_fee_flat_cents ?? 2500;
      feeCents = feeType === "fifty_percent" ? Math.round(priceAgreedCents * 0.5) : feeType === "hundred_percent" ? priceAgreedCents : flatCents;
      feeLabel = `$${(feeCents/100).toFixed(2)} CAD`;
    }
    setNoShowModal({ appt, feeCents, feeLabel, hasSavedCard, isCardBooking });
    setNoShowError("");
  };

  const chargeNoShow = async () => {
    if (!noShowModal) return;
    const { appt, feeCents, hasSavedCard, isCardBooking } = noShowModal;
    setNoShowCharging(true);
    setNoShowError("");
    try {
      if (feeCents > 0 && (hasSavedCard || isCardBooking)) {
        if (isCardBooking) {
          // Partial capture on authorized PaymentIntent
          const { data, error } = await supabase.functions.invoke("stripe-capture-payment", {
            body: { payment_intent_id: appt.stripe_payment_intent_id, booking_id: appt.id, amount_to_capture: feeCents, charge_type: "no_show" },
          });
          if (error || !data?.success) {
            setNoShowError(data?.error || error?.message || "Charge failed — please try again.");
            setNoShowCharging(false);
            return;
          }
        } else if (hasSavedCard) {
          // Off-session charge on saved card
          const { data, error } = await supabase.functions.invoke("stripe-charge-saved-card", {
            body: { booking_id: appt.id, charge_type: "no_show" },
          });
          if (error || !data?.success) {
            setNoShowError(data?.error || error?.message || "Charge failed — please try again.");
            setNoShowCharging(false);
            return;
          }
        }
      } else {
        // No charge — just mark as no-show
        await supabase.from("bookings").update({ status: "no_show", no_show_at: new Date().toISOString() }).eq("id", appt.id);
      }
      setAppointments(prev => prev.map(a => a.id === appt.id ? { ...a, status: "no_show", no_show_at: new Date().toISOString() } : a));
      setNoShowModal(null);
      window.dispatchEvent(new CustomEvent("faded-toast", { detail: { msg: feeCents > 0 ? `✅ No-show fee charged — ${noShowModal.feeLabel}` : "Marked as no-show", type: feeCents > 0 ? "success" : "info" } }));
    } catch (err) {
      setNoShowError("Something went wrong — please try again.");
    } finally {
      setNoShowCharging(false);
    }
  };

  // Date navigation
  const dates = Array.from({length:7}).map((_,i) => {
    const d = new Date();
    d.setDate(d.getDate() + i);
    return d.toISOString().split("T")[0];
  });
  const dayLabel = (dateStr) => { // eslint-disable-line no-unused-vars
    const d = new Date(dateStr + "T12:00:00");
    const today = new Date();
    if (d.toDateString() === today.toDateString()) return "Today";
    const tomorrow = new Date(); tomorrow.setDate(today.getDate()+1);
    if (d.toDateString() === tomorrow.toDateString()) return "Tomorrow";
    return d.toLocaleDateString("en-CA",{weekday:"short",month:"short",day:"numeric"});
  };

  const { t } = useT();
  const statusColor = (s) => t.statusColor(s === "noshow" ? "cancelled" : s);
  const statusBg    = (s) => t.statusBg(s === "noshow" ? "cancelled" : s);

  const totalRevenue = appointments.filter(a=>a.status==="completed"&&a.price).reduce((sum,a)=>sum+parseFloat(a.price.replace("$","")||0),0);

  return (
    <div style={{ padding:"20px 16px 100px" }}>

      {!isOwnSchedule && (
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16, padding:"10px 14px", background:t.blueBg, border:`1px solid ${t.blueBorder}`, borderRadius:14 }}>
          {onClose && (
            <button onClick={onClose} aria-label="Back" style={{ width:30, height:30, borderRadius:"50%", background:t.card, border:"none", cursor:"pointer", fontSize:16, color:t.t2, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>←</button>
          )}
          <div style={{ fontSize:12, fontWeight:700, color:t.blueText }}>Managing schedule for {targetLabel || "team member"}</div>
        </div>
      )}

      {/* Mid-appointment add-on sheet */}
      {addServiceAppt && (
        <div onClick={()=>{ setAddServiceAppt(null); setAddedMsg(""); }} className="faded-sheet-wrap" style={{ position:"fixed", inset:0, zIndex:400, background:"rgba(0,0,0,0.6)", backdropFilter:"blur(6px)", display:"flex", alignItems:"flex-end", justifyContent:"center" }}>
          <div onClick={e=>e.stopPropagation()} style={{ background:t.card, borderRadius:"24px 24px 0 0", width:"100%", maxWidth:480, maxHeight:"75vh", overflowY:"auto", padding:"20px 20px 48px" }}>
            <div style={{ display:"flex", justifyContent:"center", marginBottom:16 }}>
              <div style={{ width:40, height:4, background:t.surface2, borderRadius:2 }}/>
            </div>
            <div style={{ fontSize:17, fontWeight:800, color:t.t1, marginBottom:4 }}>Add Service</div>
            <div style={{ fontSize:12, color:t.t4, marginBottom:6 }}>{addServiceAppt.client_name} · currently: {addServiceAppt.service}</div>
            <div style={{ fontSize:12, fontWeight:700, color:"#059669", marginBottom:16 }}>
              Current total: {addServiceAppt.price || "$0"}
            </div>

            {addedMsg ? (
              <div style={{ padding:"16px", background:"rgba(34,197,94,0.1)", border:"1px solid rgba(34,197,94,0.3)", borderRadius:14, textAlign:"center" }}>
                <div style={{ fontSize:20, marginBottom:6 }}>✅</div>
                <div style={{ fontSize:14, fontWeight:700, color:"#22C55E" }}>{addedMsg}</div>
              </div>
            ) : addonServices.length === 0 ? (
              <div style={{ padding:"20px", textAlign:"center", color:t.t4, fontSize:13 }}>
                No add-ons set up yet. Add them in your Services tab.
              </div>
            ) : (
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                {addonServices.map(addon => (
                  <button key={addon.id} onClick={() => addServiceToBooking(addServiceAppt, addon)}
                    disabled={addingService}
                    style={{ padding:"14px 16px", borderRadius:14, border:`1.5px solid ${t.border}`, background:t.section, cursor:"pointer", textAlign:"left", display:"flex", justifyContent:"space-between", alignItems:"center", opacity:addingService?0.6:1 }}>
                    <div>
                      <div style={{ fontSize:14, fontWeight:700, color:t.t1 }}>{addon.name}</div>
                      {addon.duration_minutes && <div style={{ fontSize:11, color:t.t4, marginTop:2 }}>{addon.duration_minutes} min</div>}
                    </div>
                    <div style={{ fontSize:16, fontWeight:900, color:"#2563EB" }}>+{fmt(addon.price)}</div>
                  </button>
                ))}
                <div style={{ marginTop:8, padding:"10px 14px", background:"rgba(245,158,11,0.07)", border:"1px solid rgba(245,158,11,0.2)", borderRadius:12 }}>
                  <div style={{ fontSize:11, color:"#F59E0B", fontWeight:600 }}>
                    💳 Card payments — client pays the add-on difference at checkout. Cash/Interac — settle in person.
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add appointment modal */}
      {showAdd && (
        <div onClick={()=>setShowAdd(false)} className="faded-sheet-wrap" style={{ position:"fixed", inset:0, zIndex:300, background:"rgba(17,24,39,0.5)", backdropFilter:"blur(6px)", display:"flex", alignItems:"flex-end", justifyContent:"center" }}>
          <div onClick={e=>e.stopPropagation()} style={{ position:"relative", background:t.card, borderRadius:"24px 24px 0 0", width:"100%", maxWidth:480, maxHeight:"85vh", overflowY:"auto", padding:"20px 20px 48px" }}>
            <div style={{ display:"flex", justifyContent:"center", marginBottom:16 }}>
              <div style={{ width:40, height:4, background:t.surface2, borderRadius:2 }}/>
            </div>
            <button onClick={()=>setShowAdd(false)} aria-label="Close" style={{ position:"absolute", top:14, right:14, width:36, height:36, borderRadius:"50%", background:t.surface2, border:"none", cursor:"pointer", fontSize:18, color:t.t3, display:"flex", alignItems:"center", justifyContent:"center" }}><span aria-hidden="true">×</span></button>
            <div style={{ fontSize:18, fontWeight:800, color:t.t1, fontFamily:"'DM Sans',sans-serif", marginBottom:4 }}>Add Appointment</div>
            <div style={{ fontSize:12, color:t.t4, marginBottom:20 }}>For phone/text bookings or walk-ins</div>
            <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
              <div>
                <div style={{ fontSize:12, fontWeight:700, color:t.t2, marginBottom:6 }}>Client Name *</div>
                <input value={form.client_name} onChange={e=>upd("client_name",e.target.value)} placeholder="Jordan M."
                  style={{ width:"100%", padding:"12px 14px", borderRadius:12, border:`1.5px solid ${t.border}`, background:t.section, fontSize:16, color:t.inputText, outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box" }}/>
              </div>
              <div>
                <div style={{ fontSize:12, fontWeight:700, color:t.t2, marginBottom:6 }}>Phone Number (optional)</div>
                <input value={form.phone} onChange={e=>upd("phone",e.target.value)} placeholder="905-555-0123" type="tel"
                  style={{ width:"100%", padding:"12px 14px", borderRadius:12, border:`1.5px solid ${t.border}`, background:t.section, fontSize:16, color:t.inputText, outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box" }}/>
              </div>
              <div>
                <div style={{ fontSize:12, fontWeight:700, color:t.t2, marginBottom:6 }}>Service</div>
                <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                  {SERVICES.map(s=>(
                    <button key={s} onClick={()=>upd("service",s)} style={{ padding:"6px 12px", borderRadius:20, border:`1.5px solid ${form.service===s?"#2563EB":t.border}`, background:form.service===s?t.blueBg:t.section, color:form.service===s?t.blueText:t.t3, fontSize:11, fontWeight:600, cursor:"pointer" }}>{s}</button>
                  ))}
                </div>
              </div>
              <div>
                <div style={{ fontSize:12, fontWeight:700, color:t.t2, marginBottom:6 }}>Time *</div>
                <select value={form.time} onChange={e=>upd("time",e.target.value)}
                  style={{ width:"100%", padding:"12px 14px", borderRadius:12, border:`1.5px solid ${t.border}`, background:t.section, fontSize:16, color:form.time?t.inputText:t.t4, outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box" }}>
                  <option value="">Select a time</option>
                  {TIMES.map(tm=><option key={tm} value={tm}>{tm}</option>)}
                </select>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                <div>
                  <div style={{ fontSize:12, fontWeight:700, color:t.t2, marginBottom:6 }}>Price ($)</div>
                  <input value={form.price} onChange={e=>upd("price",e.target.value)} placeholder="35" type="number"
                    style={{ width:"100%", padding:"12px 14px", borderRadius:12, border:`1.5px solid ${t.border}`, background:t.section, fontSize:16, color:t.inputText, outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box" }}/>
                </div>
                <div>
                  <div style={{ fontSize:12, fontWeight:700, color:t.t2, marginBottom:6 }}>How booked</div>
                  <select value={form.source} onChange={e=>upd("source",e.target.value)}
                    style={{ width:"100%", padding:"12px 14px", borderRadius:12, border:`1.5px solid ${t.border}`, background:t.section, fontSize:16, color:t.inputText, outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box" }}>
                    <option value="manual">Phone/Text</option>
                    <option value="walkin">Walk-in</option>
                    <option value="social">Social Media</option>
                    <option value="referral">Referral</option>
                  </select>
                </div>
              </div>
              <div>
                <div style={{ fontSize:12, fontWeight:700, color:t.t2, marginBottom:6 }}>Notes (optional)</div>
                <input value={form.notes} onChange={e=>upd("notes",e.target.value)} placeholder="Skin fade, low taper..."
                  style={{ width:"100%", padding:"12px 14px", borderRadius:12, border:`1.5px solid ${t.border}`, background:t.section, fontSize:16, color:t.inputText, outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box" }}/>
              </div>
              {manualErr && <div style={{ fontSize:13, color:"#DC2626", textAlign:"center" }}>{manualErr}</div>}
              <button onClick={saveManual} disabled={saving} style={{ padding:16, background:"#2563EB", border:"none", borderRadius:14, color:"#fff", fontSize:15, fontWeight:800, cursor:saving?"default":"pointer", fontFamily:"'DM Sans',sans-serif", opacity:saving?0.7:1, boxShadow:"0 4px 20px rgba(37,99,235,0.3)" }}>
                {saving?"Saving...":"Add to Schedule"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Date selector */}
      <div style={{ display:"flex", gap:8, overflowX:"auto", paddingBottom:4, marginBottom:16 }}>
        {dates.map(d=>{
          const isSelected = d===selectedDate;
          const date = new Date(d+"T12:00:00");
          return (
            <button key={d} onClick={()=>setSelectedDate(d)} style={{ flexShrink:0, padding:"10px 14px", borderRadius:14, border:`1.5px solid ${isSelected?"#2563EB":t.border}`, background:isSelected?"#2563EB":t.card, color:isSelected?"#fff":t.t2, cursor:"pointer", textAlign:"center", minWidth:64, transition:"all 0.15s" }}>
              <div style={{ fontSize:11, fontWeight:600, opacity:0.8 }}>{date.toLocaleDateString("en-CA",{weekday:"short"}).toUpperCase()}</div>
              <div style={{ fontSize:18, fontWeight:900, fontFamily:"'DM Sans',sans-serif", lineHeight:1.2 }}>{date.getDate()}</div>
            </button>
          );
        })}
      </div>

      {/* Day summary */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginBottom:16 }}>
        {[
          { label:"Appointments", value:appointments.length, color:"#2563EB", emoji:"📅" },
          { label:"Completed", value:appointments.filter(a=>a.status==="completed").length, color:"#16A34A", emoji:"✅" },
          { label:"Revenue", value:totalRevenue>0?`$${totalRevenue}`:"$0", color:"#D97706", emoji:"💰" },
        ].map(s=>(
          <div key={s.label} style={{ background:t.card, borderRadius:16, padding:"14px 10px", border:`1.5px solid ${t.border}`, textAlign:"center" }}>
            <div style={{ fontSize:20, marginBottom:4 }}>{s.emoji}</div>
            <div style={{ fontSize:18, fontWeight:900, color:s.color, fontFamily:"'DM Sans',sans-serif" }}>{s.value}</div>
            <div style={{ fontSize:11, color:t.t4, marginTop:2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Add appointment button */}
      <button onClick={()=>{ setManualErr(""); setShowAdd(true); }} style={{ width:"100%", padding:"14px", background:"linear-gradient(135deg,#2563EB,#1D4ED8)", border:"none", borderRadius:16, color:"#fff", fontSize:14, fontWeight:800, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", marginBottom:16, display:"flex", alignItems:"center", justifyContent:"center", gap:8, boxShadow:"0 4px 16px rgba(37,99,235,0.3)" }}>
        <span style={{ fontSize:18 }}>➕</span> Add Appointment
      </button>

      {loading ? (
        <div style={{ textAlign:"center", padding:"40px 20px", color:t.t4 }}>
          <div style={{ fontSize:28, marginBottom:8 }}>📅</div>
          <div style={{ fontSize:13 }}>Loading schedule...</div>
        </div>
      ) : appointments.length === 0 ? (
        <div style={{ textAlign:"center", padding:"40px 20px" }}>
          <div style={{ fontSize:48, marginBottom:12 }}>📅</div>
          <div style={{ fontSize:16, fontWeight:800, color:t.t1, fontFamily:"'DM Sans',sans-serif" }}>No appointments yet</div>
          <div style={{ fontSize:13, color:t.t3, marginTop:6 }}>Tap Add Appointment for phone/walk-in bookings</div>
          <div style={{ fontSize:12, color:t.t4, marginTop:4 }}>App bookings appear here automatically</div>
        </div>
      ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {appointments.map((appt) => (
            <div key={appt.id} style={{ background:t.card, borderRadius:18, padding:16, border:`1.5px solid ${t.border}`, position:"relative", overflow:"hidden" }}>
              <div style={{ position:"absolute", left:0, top:0, bottom:0, width:4, background:appt.source==="app"?"#2563EB":"#059669", borderRadius:"18px 0 0 18px" }}/>
              <div style={{ paddingLeft:8 }}>
                {/* CLIENT HERE banner */}
                {appt.checked_in_at && ["pending","accepted","confirmed"].includes(appt.status) && (
                  <div style={{ display:"flex", alignItems:"center", gap:6, padding:"5px 10px", background:"rgba(34,197,94,0.12)", border:"1px solid rgba(34,197,94,0.35)", borderRadius:8, marginBottom:8 }}>
                    <div style={{ width:7, height:7, borderRadius:"50%", background:"#22C55E", flexShrink:0 }}/>
                    <span style={{ fontSize:11, fontWeight:800, color:"#16A34A", letterSpacing:"0.08em", fontFamily:"'DM Sans',sans-serif" }}>CLIENT HERE</span>
                  </div>
                )}
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8 }}>
                  <div>
                    <div style={{ fontSize:15, fontWeight:800, color:t.t1, fontFamily:"'DM Sans',sans-serif" }}>{appt.client_name}</div>
                    <div style={{ fontSize:12, color:t.t4, marginTop:2 }}>
                      {appt.service} · {appt.time}
                      {appt.phone && <span> · {appt.phone}</span>}
                    </div>
                    {appt.notes && <div style={{ fontSize:11, color:t.t3, marginTop:3, fontStyle:"italic" }}>{appt.notes}</div>}
                    {appt.created_by_role && appt.created_by_role !== "barber" && (
                      <div style={{ fontSize:10, color:t.t4, marginTop:3, fontWeight:600 }}>
                        Added by {appt.creator_name ? `${appt.creator_name}, shop staff` : appt.created_by_role === "owner" ? "shop owner" : "shop staff"}
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign:"right", flexShrink:0 }}>
                    {appt.price && <div style={{ fontSize:14, fontWeight:800, color:"#059669" }}>{fmt(parseFloat(String(appt.price).replace(/[^0-9.]/g,"")))}</div>}
                    <div style={{ fontSize:11, background:statusBg(appt.status), color:statusColor(appt.status), fontWeight:700, padding:"2px 8px", borderRadius:10, marginTop:4 }}>{appt.status.toUpperCase()}</div>
                    <div style={{ fontSize:11, color:appt.source==="app"?"#2563EB":"#059669", fontWeight:600, marginTop:4 }}>{appt.source==="app"?"📱 APP":appt.source==="walkin"?"🚶 WALK-IN":appt.source==="social"?"📸 SOCIAL":"📞 PHONE"}</div>
                  </div>
                </div>
                <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                  {appt.status!=="completed" && (
                    <button onClick={()=>updateStatus(appt,"completed")} style={{ padding:"5px 12px", background:"#DCFCE7", border:"1px solid #22C55E33", borderRadius:8, color:"#16A34A", fontSize:11, fontWeight:700, cursor:"pointer" }}>Done ✓</button>
                  )}
                  {/* Add service mid-appointment — only on active bookings */}
                  {["pending","accepted","confirmed"].includes(appt.status) && (
                    <button onClick={()=>setAddServiceAppt(appt)}
                      style={{ padding:"5px 12px", background:"rgba(37,99,235,0.1)", border:"1px solid rgba(37,99,235,0.3)", borderRadius:8, color:"#2563EB", fontSize:11, fontWeight:700, cursor:"pointer" }}>
                      ＋ Add Service
                    </button>
                  )}
                  {appt.status!=="cancelled" && appt.status!=="completed" && (
                    <button onClick={()=>updateStatus(appt,"cancelled")} style={{ padding:"5px 12px", background:"#FEE2E2", border:"1px solid #EF444433", borderRadius:8, color:"#DC2626", fontSize:11, fontWeight:700, cursor:"pointer" }}>Cancel</button>
                  )}
                  {appt.status!=="no_show" && appt.status!=="noshow" && appt.status!=="completed" && (
                    <button onClick={()=>openNoShowModal(appt)} style={{ padding:"5px 12px", background:t.chip, border:`1px solid ${t.border}`, borderRadius:8, color:t.t3, fontSize:11, fontWeight:700, cursor:"pointer" }}>No Show</button>
                  )}
                  {appt.source==="manual" && (
                    <button onClick={()=>deleteManual(appt.id)} style={{ padding:"5px 12px", background:"#FEE2E2", border:"1px solid #EF444433", borderRadius:8, color:"#DC2626", fontSize:11, fontWeight:700, cursor:"pointer" }}>Delete</button>
                  )}
                  {appt.phone && (
                    <a href={`tel:${appt.phone}`} style={{ padding:"5px 12px", background:t.blueBg, border:`1px solid ${t.blueBorder}`, borderRadius:8, color:t.blueText, fontSize:11, fontWeight:700, textDecoration:"none", display:"inline-block" }}>📞 Call</a>
                  )}
                  {appt.source === "app" && appt.client_id && (
                    <button onClick={() => {
                      if (historyAppt === appt.id) { setHistoryAppt(null); }
                      else { setHistoryAppt(appt.id); loadClientHistory(appt); }
                    }} style={{ padding:"5px 12px", background:historyAppt===appt.id?"rgba(37,99,235,0.15)":t.chip, border:`1px solid ${historyAppt===appt.id?t.blueBorder:t.border}`, borderRadius:8, color:historyAppt===appt.id?t.blueText:t.t3, fontSize:11, fontWeight:700, cursor:"pointer" }}>
                      {historyAppt === appt.id ? "Hide History" : "History"}
                    </button>
                  )}
                </div>
                {/* Client history panel */}
                {historyAppt === appt.id && (
                  <div style={{ marginTop:10, padding:"12px 14px", background:t.section, borderRadius:12, border:`1px solid ${t.border}` }}>
                    {clientHistory[appt.id] === "loading" ? (
                      <div style={{ fontSize:12, color:t.t4, textAlign:"center", padding:"8px 0" }}>Loading history...</div>
                    ) : !clientHistory[appt.id] || clientHistory[appt.id]?.visits?.length === 0 ? (
                      <div style={{ fontSize:12, color:t.t4, textAlign:"center", padding:"8px 0" }}>First visit with you</div>
                    ) : (
                      <>
                        <div style={{ display:"flex", gap:12, marginBottom:10 }}>
                          <div style={{ flex:1, textAlign:"center", padding:"8px", background:t.card, borderRadius:10, border:`1px solid ${t.border}` }}>
                            <div style={{ fontSize:18, fontWeight:900, color:"#2563EB" }}>{clientHistory[appt.id].visits.length}</div>
                            <div style={{ fontSize:10, color:t.t4, fontWeight:700, marginTop:2 }}>VISITS</div>
                          </div>
                          <div style={{ flex:1, textAlign:"center", padding:"8px", background:t.card, borderRadius:10, border:`1px solid ${t.border}` }}>
                            <div style={{ fontSize:18, fontWeight:900, color:"#059669" }}>{fmt(clientHistory[appt.id].total)}</div>
                            <div style={{ fontSize:10, color:t.t4, fontWeight:700, marginTop:2 }}>SPENT</div>
                          </div>
                          <div style={{ flex:1, textAlign:"center", padding:"8px", background:t.card, borderRadius:10, border:`1px solid ${t.border}` }}>
                            <div style={{ fontSize:12, fontWeight:800, color:t.t1, lineHeight:1.2 }}>
                              {clientHistory[appt.id].lastDate
                                ? clientHistory[appt.id].lastDate.toLocaleDateString("en-CA",{month:"short",day:"numeric"})
                                : "—"}
                            </div>
                            <div style={{ fontSize:10, color:t.t4, fontWeight:700, marginTop:2 }}>LAST VISIT</div>
                          </div>
                        </div>
                        <div style={{ fontSize:10, fontWeight:700, color:t.t4, letterSpacing:"0.1em", marginBottom:6, textTransform:"uppercase" }}>Recent Services</div>
                        <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
                          {clientHistory[appt.id].visits.slice(0,5).map((v, i) => (
                            <div key={i} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", fontSize:12, color:t.t2 }}>
                              <span>{v.service_type || "Haircut"}</span>
                              <span style={{ color:t.t4, fontSize:11 }}>{new Date(v.scheduled_at).toLocaleDateString("en-CA",{month:"short",day:"numeric",year:"numeric"})}</span>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop:16, padding:"14px 16px", background:t.blueBg, borderRadius:14, border:`1px solid ${t.blueBorder}` }}>
        <div style={{ fontSize:12, fontWeight:700, color:t.blueText, marginBottom:4 }}>💡 Pro tip</div>
        <div style={{ fontSize:11, color:t.t2, lineHeight:1.6 }}>Phone/text bookings show with a green stripe. App bookings show with a blue stripe. Ask your regular clients to book on FADED so everything stays in one place automatically.</div>
      </div>

      {/* No-show confirmation modal */}
      {noShowModal && (
        <div className="faded-sheet-wrap" style={{ position:"fixed", inset:0, zIndex:500, background:"rgba(0,0,0,0.7)", display:"flex", alignItems:"flex-end", justifyContent:"center" }} onClick={()=>{ if(!noShowCharging) setNoShowModal(null); }}>
          <div style={{ background:"#111", borderRadius:"24px 24px 0 0", padding:"24px 20px 40px", width:"100%", maxWidth:480 }} onClick={e=>e.stopPropagation()}>
            <div style={{ fontSize:18, fontWeight:900, color:"#fff", marginBottom:8, fontFamily:"'DM Sans',sans-serif" }}>Mark as No-Show?</div>
            <div style={{ fontSize:13, color:"rgba(245,245,240,0.6)", marginBottom:16, lineHeight:1.6 }}>
              {noShowModal.appt.client_name} · {noShowModal.appt.service}
            </div>
            {noShowModal.feeCents > 0 ? (
              <div style={{ background:"rgba(220,38,38,0.08)", border:"1px solid rgba(220,38,38,0.2)", borderRadius:14, padding:"14px 16px", marginBottom:16 }}>
                <div style={{ fontSize:13, fontWeight:700, color:"#EF4444", marginBottom:4 }}>No-show fee will be charged</div>
                <div style={{ fontSize:22, fontWeight:900, color:"#fff" }}>{noShowModal.feeLabel}</div>
                <div style={{ fontSize:11, color:"rgba(245,245,240,0.4)", marginTop:4 }}>
                  {noShowModal.isCardBooking ? "Captured from authorized card payment" : "Charged to saved card on file"}
                </div>
              </div>
            ) : (
              <div style={{ fontSize:12, color:"rgba(245,245,240,0.4)", marginBottom:16 }}>No fee — client had no card on file or protection was not enabled.</div>
            )}
            {noShowError && <div style={{ fontSize:12, color:"#EF4444", marginBottom:12, padding:"10px 14px", background:"rgba(220,38,38,0.08)", border:"1px solid rgba(220,38,38,0.25)", borderRadius:10 }}>{noShowError}</div>}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
              <button onClick={()=>setNoShowModal(null)} disabled={noShowCharging} style={{ padding:"13px", background:"transparent", border:"1px solid rgba(245,245,240,0.15)", borderRadius:14, color:"rgba(245,245,240,0.6)", fontSize:13, fontWeight:700, cursor:"pointer" }}>Cancel</button>
              <button onClick={chargeNoShow} disabled={noShowCharging} style={{ padding:"13px", background: noShowModal.feeCents > 0 ? "#EF4444" : "rgba(245,245,240,0.12)", border:"none", borderRadius:14, color:"#fff", fontSize:13, fontWeight:800, cursor:"pointer", opacity:noShowCharging?0.7:1 }}>
                {noShowCharging ? "Processing…" : noShowModal.feeCents > 0 ? `Charge ${noShowModal.feeLabel}` : "Confirm No-Show"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── AI SCHEDULE ASSISTANT ────────────────────────────────────────────────────
function detectChips(userMsg, razorMsg, ctx) {
  const u = (userMsg || "").toLowerCase();
  const r = (razorMsg || "").toLowerCase();
  const chips = [];

  // Running late — the star feature
  const lateMinMatch = u.match(/(\d+)\s*min(?:utes?)?\s*(?:late|behind)/) ||
                       u.match(/running\s*(?:about\s*)?(\d+)\s*min/);
  const halfHour = /half\s*(?:an?\s*)?hour/.test(u);
  const lateMins = lateMinMatch?.[1] ? parseInt(lateMinMatch[1]) : halfHour ? 30 : null;
  if (lateMins || u.includes("running late") || u.includes("running behind")) {
    const minutes = lateMins || 20;
    const count = ctx.remainingCount || 0;
    const clientLabel = count > 0 ? `${count} Client${count !== 1 ? "s" : ""}` : "All Clients";
    chips.push({ id:"delay", label:`Notify ${clientLabel} (+${minutes} min)`, data:{ minutes } });
  }

  // No-show (use .? to match straight or curly apostrophes and "didnt" typo)
  if (u.match(/no.?show|didn.?t show|never showed|ghosted|didn.?t come/) ||
      (r.includes("no-show") && r.includes("mark"))) {
    const timeMatch = u.match(/(\d{1,2}(?::\d{2})?\s*(?:am|pm))/i);
    chips.push({ id:"noshow", label:"Mark No-Show", data:{ timeHint: timeMatch?.[1] || null } });
  }

  // Done for the day / go offline
  if (u.match(/done\s*(?:for\s*(?:the\s*)?day|today)|going\s*offline|shutting\s*(?:down|it)|closing\s*up|wrap(?:ping)?\s*up|finished\s*(?:for\s*(?:the\s*)?day|today)/)) {
    chips.push({ id:"offline", label:"Go Offline + Close Queue", data:{} });
  }

  // Open walk-ins
  if (u.match(/open\s*(?:the\s*)?(?:walk.?ins?|queue)|taking\s*walk.?ins?|turn\s*on\s*(?:walk|queue)/)) {
    chips.push({ id:"open_walkin", label:"Open Walk-in Queue", data:{} });
  }

  // Call next in queue (only if queue has people)
  if ((u.match(/call\s*(?:up\s*)?(?:next|them|the\s*next|next\s*one)|next\s*(?:person|one|up)/) ||
       r.includes("call them up")) && ctx.queueCount > 0) {
    chips.push({ id:"call_next", label:"Call Next in Queue", data:{} });
  }

  // Cancel booking
  if (u.match(/cancel\s*(?:my|the)?\s*\d{1,2}(?::\d{2})?\s*(?:am|pm)|cancel.*(?:appointment|booking|appt)|cancel.*and\s*(?:notify|let\s*them)/)) {
    const timeMatch = u.match(/(\d{1,2}(?::\d{2})?\s*(?:am|pm))/i);
    chips.push({ id:"cancel", label:"Cancel + Notify Client", data:{ timeHint: timeMatch?.[1] || null } });
  }

  return chips.slice(0, 3);
}

function AIScheduleAssistant({ session, myBarberData, onClose, initialQuery, onOpen, todayEarnings, weekEarnings, monthEarnings, queueCount, noShowCount }) {
  const firstName = myBarberData?.full_name?.split(" ")[0] || "there";

  // PIPEDA consent: user must acknowledge AI data disclosure before first query
  const [aiConsented, setAiConsented] = useState(() => {
    try { return localStorage.getItem("faded_razor_consented") === "1"; } catch { return false; }
  });

  const [messages, setMessages] = useState(null); // null = loading initial data
  const [input, setInput]         = useState("");
  const [listening, setListening] = useState(false);
  const [loading, setLoading]     = useState(false);
  const [speaking, setSpeaking]   = useState(false);
  const [voiceReply, setVoiceReply] = useState(true);
  const [micError, setMicError]   = useState("");
  const [appointments, setAppointments] = useState([]);
  const [province, setProvince]   = useState("ON");
  const [barberServices, setBarberServices] = useState([]);
  const [incomeYtd, setIncomeYtd] = useState(null);
  const [chips, setChips] = useState([]);
  const [chipBusy, setChipBusy] = useState(null);
  const scrollRef = useRef();
  const recognitionRef = useRef();

  // Load all context data on mount (appointments, province, services, YTD income)
  useEffect(() => {
    if (!session?.user?.id) return;
    const today    = new Date().toISOString().split("T")[0];
    const tomorrow = new Date(Date.now()+86400000).toISOString().split("T")[0];
    const yearStart = new Date(new Date().getFullYear(), 0, 1).toISOString();

    Promise.all([
      supabase.from("bookings").select("*").eq("barber_id",session.user.id)
        .gte("scheduled_at", today+"T00:00:00").lte("scheduled_at", tomorrow+"T23:59:59")
        .neq("status","declined").neq("status","cancelled"),
      supabase.from("manual_appointments").select("*").eq("barber_id",session.user.id)
        .gte("date", today).lte("date", tomorrow)
        .neq("status","cancelled"),
      supabase.from("profiles").select("province").eq("id", session.user.id).single(),
      supabase.from("barber_services").select("name,price,duration_minutes").eq("barber_id", session.user.id).eq("is_active", true).order("sort_order").limit(12),
      supabase.from("bookings").select("price_agreed,tip_amount,completed_at").eq("barber_id", session.user.id).eq("status","completed").gte("completed_at", yearStart),
    ]).then(([{data:bookings},{data:manual},{data:profileData},{data:svcData},{data:ytdData}]) => {
      const parseAmt = v => parseFloat(String(v||0).replace(/[^0-9.]/g,""))||0;
      const all = [
        ...(bookings||[]).map(b=>({
          time: b.scheduled_at ? new Date(b.scheduled_at).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}) : "TBD",
          time24: b.scheduled_at ? new Date(b.scheduled_at).getHours()*60+new Date(b.scheduled_at).getMinutes() : 0,
          date: b.scheduled_at ? new Date(b.scheduled_at).toISOString().split("T")[0] : today,
          client: b.client_name||"App Client",
          service: b.service_type||"Haircut",
          source: "app",
          status: b.status,
        })),
        ...(manual||[]).map(m=>({
          time: m.time_slot,
          time24: parseInt((m.time_slot||"0:0").replace(/[^0-9:]/g,"").split(":")[0]||0)*60 + parseInt((m.time_slot||"0:0").replace(/[^0-9:]/g,"").split(":")[1]||0),
          date: m.date,
          client: m.client_name,
          service: m.service||"Haircut",
          source: "manual",
          status: m.status,
        }))
      ].sort((a,b)=>a.date.localeCompare(b.date)||a.time24-b.time24);
      setAppointments(all);

      setProvince(profileData?.province || "ON");
      setBarberServices(svcData || []);

      const ytd = (ytdData||[]).reduce((s,b) => s + parseAmt(b.price_agreed) + parseAmt(b.tip_amount), 0);
      setIncomeYtd(ytd);

      // Smart opening greeting with real data
      const todayAppts = all.filter(a => a.date === today);
      const nextAppt = todayAppts.find(a => !["completed","no_show","cancelled"].includes(a.status));
      const parts = [];
      if (todayAppts.length > 0) {
        parts.push(`${todayAppts.length} cut${todayAppts.length > 1 ? "s" : ""} today.`);
        if (nextAppt) parts.push(`Next up: ${nextAppt.client} at ${nextAppt.time} — ${nextAppt.service}.`);
      } else {
        parts.push("No cuts booked today.");
      }
      if (queueCount > 0) parts.push(`${queueCount} walk-in${queueCount > 1 ? "s" : ""} waiting.`);
      if (todayEarnings > 0) parts.push(`$${Number(todayEarnings).toFixed(0)} earned today.`);
      const greetingText = parts.join(" ") + " What do you need?";
      setMessages(prev => {
        const greeting = { role:"assistant", text:greetingText };
        if (prev && prev.length > 0) return [greeting, ...prev];
        return [greeting];
      });
    }).catch(() => {
      setMessages([{ role:"assistant", text:`Hey ${firstName}! Ask me about your schedule, income, walk-ins, or anything about running your day.` }]);
    });
  }, [session]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // On open: if wake word fired with a query, send it; otherwise auto-start mic
  useEffect(() => {
    if (!aiConsented) return;
    onOpen?.();
    if (initialQuery?.trim()) {
      sendMessage(initialQuery);
    } else if (initialQuery !== undefined) {
      // Opened via wake word with no trailing query — start listening automatically
      setTimeout(() => startListening(), 400);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Build AI context from schedule

  const speakReply = async (text) => {
    if (!voiceReply) return;
    try {
      const { TextToSpeech } = await import("@capacitor-community/text-to-speech");
      await TextToSpeech.speak({ text, lang:"en-CA", rate:1.0, pitch:1.0, volume:1.0, category:"ambient" });
      setSpeaking(false);
    } catch {
      // Web fallback (desktop browser / dev)
      if (!("speechSynthesis" in window)) return;
      window.speechSynthesis.cancel();
      const utt = new SpeechSynthesisUtterance(text);
      utt.lang = "en-CA";
      utt.rate = 1.05;
      utt.pitch = 1.0;
      utt.onstart = () => setSpeaking(true);
      utt.onend   = () => setSpeaking(false);
      utt.onerror = () => setSpeaking(false);
      window.speechSynthesis.speak(utt);
    }
  };

  const sendMessage = async (text) => {
    if (!text?.trim() || loading) return;
    const userMsg = { role:"user", text };
    const updated = [...(messages||[]), userMsg];
    setMessages(updated);
    setInput("");
    setChips([]);
    setLoading(true);
    try {
      track("razor_ai_queried");
      const { data, error } = await supabase.functions.invoke("razor-ai", {
        body: {
          messages: updated,
          appointments,
          barber_name: firstName,
          income_today: todayEarnings,
          income_week: weekEarnings,
          income_month: monthEarnings,
          income_ytd: incomeYtd,
          queue_count: queueCount || 0,
          walkin_open: !!myBarberData?.walkin,
          province,
          no_show_count: noShowCount || 0,
          barber_status: myBarberData?.status,
          services: barberServices,
        }
      });
      if (error) throw error;
      const reply = data?.text || "Something went wrong — try again.";
      setMessages(prev => [...(prev||[]), { role:"assistant", text:reply }]);
      speakReply(reply);
      const todayStr = new Date().toISOString().split("T")[0];
      const nowMins = new Date().getHours()*60 + new Date().getMinutes();
      const remainingCount = appointments.filter(a =>
        a.date === todayStr &&
        ["accepted","confirmed"].includes(a.status) &&
        a.time24 > nowMins - 30
      ).length;
      setChips(detectChips(text, reply, { queueCount: queueCount || 0, remainingCount }));
    } catch {
      setMessages(prev => [...(prev||[]), { role:"assistant", text:"Connection error. Check your internet and try again." }]);
    }
    setLoading(false);
  };

  const executeChip = async (chip) => {
    if (chipBusy) return;
    setChipBusy(chip.id);
    const barberId = session?.user?.id;
    const barberName = myBarberData?.full_name || "Your barber";
    try {
      if (chip.id === "delay") {
        const minutes = chip.data.minutes || 20;
        const now = new Date();
        const today = now.toISOString().split("T")[0];
        const { data: todayBookings } = await supabase.from("bookings")
          .select("id,client_name,client_phone,scheduled_at")
          .eq("barber_id", barberId)
          .in("status", ["accepted","confirmed"])
          .gte("scheduled_at", today + "T00:00:00")
          .lte("scheduled_at", today + "T23:59:59");
        const remaining = (todayBookings || [])
          .filter(b => new Date(b.scheduled_at) > new Date(now.getTime() - 30 * 60000))
          .sort((a,b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
        await Promise.all(remaining.map(async b => {
          const newTime = new Date(new Date(b.scheduled_at).getTime() + minutes * 60000);
          await supabase.from("bookings").update({ scheduled_at: newTime.toISOString() }).eq("id", b.id);
          if (b.client_phone) {
            const newTimeStr = newTime.toLocaleTimeString([], { hour:"2-digit", minute:"2-digit" });
            await supabase.functions.invoke("send-sms", { body: { to: b.client_phone, message: `✂️ ${barberName} is running about ${minutes} min behind — your updated time is ${newTimeStr}. Thanks for your patience! 💈` } });
          }
        }));
        const last = remaining[remaining.length - 1];
        const newEnd = last ? new Date(new Date(last.scheduled_at).getTime() + minutes * 60000).toLocaleTimeString([], { hour:"2-digit", minute:"2-digit" }) : "";
        if (remaining.length === 0) {
          setMessages(prev => [...(prev||[]), { role:"assistant", text:"No upcoming confirmed bookings found for today — nothing to push back." }]);
        } else {
          setMessages(prev => [...(prev||[]), { role:"assistant", text:`Done — ${remaining.length} client${remaining.length !== 1 ? "s" : ""} notified with updated times.${newEnd ? ` Your last cut is now at ${newEnd}.` : ""}` }]);
        }

      } else if (chip.id === "noshow") {
        const today = new Date().toISOString().split("T")[0];
        const { data: bookings } = await supabase.from("bookings")
          .select("*").eq("barber_id", barberId)
          .in("status", ["accepted","confirmed"])
          .gte("scheduled_at", today + "T00:00:00")
          .lte("scheduled_at", today + "T23:59:59")
          .order("scheduled_at");
        let booking = bookings?.[0] || null;
        if (chip.data.timeHint && bookings?.length > 1) {
          const hint = chip.data.timeHint.toLowerCase().replace(/\s/g,"");
          booking = bookings.find(b => {
            const t = new Date(b.scheduled_at).toLocaleTimeString([],{hour:"numeric",minute:"2-digit"}).toLowerCase().replace(/\s/g,"");
            return t.includes(hint) || hint.includes(t.replace(":00",""));
          }) || bookings[0];
        }
        if (booking) {
          await supabase.from("bookings").update({ status:"no_show" }).eq("id", booking.id);
          const timeStr = new Date(booking.scheduled_at).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"});
          setMessages(prev => [...(prev||[]), { role:"assistant", text:`Marked. ${booking.client_name || "Client"} at ${timeStr} logged as a no-show. No-show protection will handle the rest.` }]);
        } else {
          setMessages(prev => [...(prev||[]), { role:"assistant", text:"Couldn't find the booking — mark it manually in your schedule." }]);
        }

      } else if (chip.id === "offline") {
        await supabase.from("barbers").update({ status:"offline", walkin:false }).eq("id", barberId);
        const summary = todayEarnings > 0 ? ` $${Number(todayEarnings).toFixed(0)} earned today.` : "";
        setMessages(prev => [...(prev||[]), { role:"assistant", text:`You're offline. Walk-in queue closed.${summary} Good work today.` }]);

      } else if (chip.id === "open_walkin") {
        await supabase.from("barbers").update({ walkin:true, status:"available" }).eq("id", barberId);
        setMessages(prev => [...(prev||[]), { role:"assistant", text:"Walk-in queue is open and you're set to available — clients can see you on the map now." }]);

      } else if (chip.id === "call_next") {
        const { data: queueEntries } = await supabase.from("walk_in_queue")
          .select("*").eq("barber_id", barberId).eq("status","waiting").order("added_at").limit(1);
        const next = queueEntries?.[0];
        if (next) {
          await supabase.from("walk_in_queue").update({ status:"called" }).eq("id", next.id);
          if (next.client_phone) {
            await supabase.functions.invoke("send-sms", { body: { to: next.client_phone, message: `✂️ FADED: You're up! ${barberName} is ready for you now — head to the chair! 💈` } });
          }
          setMessages(prev => [...(prev||[]), { role:"assistant", text:`${next.client_name || "Next person"} has been called — they're on their way to your chair.` }]);
        } else {
          setMessages(prev => [...(prev||[]), { role:"assistant", text:"Queue is empty — nobody left to call up." }]);
        }

      } else if (chip.id === "cancel") {
        const today = new Date().toISOString().split("T")[0];
        const { data: bookings } = await supabase.from("bookings")
          .select("*").eq("barber_id", barberId)
          .in("status", ["accepted","confirmed","pending"])
          .gte("scheduled_at", today + "T00:00:00")
          .lte("scheduled_at", today + "T23:59:59")
          .order("scheduled_at");
        let booking = bookings?.[0] || null;
        if (chip.data.timeHint && bookings?.length > 1) {
          const hint = chip.data.timeHint.toLowerCase().replace(/\s/g,"");
          booking = bookings.find(b => {
            const t = new Date(b.scheduled_at).toLocaleTimeString([],{hour:"numeric",minute:"2-digit"}).toLowerCase().replace(/\s/g,"");
            return t.includes(hint) || hint.includes(t.replace(":00",""));
          }) || bookings[0];
        }
        if (booking) {
          await supabase.from("bookings").update({ status:"cancelled" }).eq("id", booking.id);
          if (booking.client_phone) {
            const timeStr = new Date(booking.scheduled_at).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"});
            await supabase.functions.invoke("send-sms", { body: { to: booking.client_phone, message: `✂️ FADED: Your ${timeStr} appointment with ${barberName} has been cancelled. Sorry for the inconvenience.` } });
          }
          setMessages(prev => [...(prev||[]), { role:"assistant", text:`${booking.client_name || "Client"}'s booking cancelled — they've been notified by text.` }]);
        } else {
          setMessages(prev => [...(prev||[]), { role:"assistant", text:"Couldn't find a booking to cancel — check your schedule manually." }]);
        }
      }
    } catch {
      setMessages(prev => [...(prev||[]), { role:"assistant", text:"Something went wrong — try again or handle it in the dashboard." }]);
    }
    setChipBusy(null);
    setChips([]);
  };

  // Voice input
  const startListening = async () => {
    setMicError("");

    if (!("webkitSpeechRecognition" in window) && !("SpeechRecognition" in window)) {
      setMicError("Voice not supported — try Chrome or Edge.");
      return;
    }

    // Explicitly request mic permission first so we get a clear error
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(t => t.stop()); // release immediately — SR manages its own stream
    } catch (err) {
      if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
        setMicError("Microphone access denied. Tap the lock icon in your address bar and allow microphone, then try again.");
      } else {
        setMicError("Could not access microphone: " + err.message);
      }
      return;
    }

    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SR();
    recognitionRef.current = recognition;
    recognition.lang = "en-CA";
    recognition.interimResults = true;
    recognition.continuous = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => { setListening(true); setInput(""); };
    recognition.onend   = () => { setListening(false); recognitionRef.current = null; };
    recognition.onerror = (e) => {
      setListening(false);
      recognitionRef.current = null;
      if (e.error === "not-allowed") {
        setMicError("Microphone blocked. Allow access in browser settings.");
      } else if (e.error === "no-speech") {
        setMicError("No speech detected — try speaking louder or closer.");
        setTimeout(() => setMicError(""), 3000);
      } else if (e.error !== "aborted") {
        setMicError("Voice error: " + e.error);
        setTimeout(() => setMicError(""), 3000);
      }
    };
    recognition.onresult = (e) => {
      let interim = "";
      let final   = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) final += t;
        else interim += t;
      }
      // Show live transcript in the input box while speaking
      setInput(final || interim);
      if (final) {
        recognition.stop();
        sendMessage(final);
      }
    };

    try {
      recognition.start();
    } catch (e) {
      setListening(false);
      recognitionRef.current = null;
      setMicError("Could not start voice recognition.");
    }
  };

  const stopListening = () => {
    recognitionRef.current?.stop();
    recognitionRef.current = null;
    setListening(false);
  };

  const quickQuestions = [
    "What's my schedule today?",
    "How much did I make this week?",
    "How many walk-ins are waiting?",
    "Am I close to the CRA limit?",
    "Tips for a slow day?",
  ];

  const { t } = useT();
  const a = myBarberData?.accent_color || "#2563EB";

  // PIPEDA: show consent gate on first use before any data leaves the device
  if (!aiConsented) return (
    <div style={{ display:"flex", flexDirection:"column", position:"fixed", inset:0, zIndex:200, background:t.bodyBg, alignItems:"center", justifyContent:"center", padding:"32px 24px" }}>
      <div style={{ fontSize:48, marginBottom:20 }}>🪒</div>
      <div style={{ fontSize:24, fontWeight:900, color:t.t1, fontFamily:"'Bebas Neue',sans-serif", letterSpacing:"0.06em", marginBottom:8, textAlign:"center" }}>MEET RAZOR</div>
      <div style={{ fontSize:14, color:t.t2, textAlign:"center", lineHeight:1.7, marginBottom:24, maxWidth:340 }}>
        Razor is an AI assistant that helps you manage your schedule, understand your earnings, and answer questions about running your barbershop.
      </div>
      <div style={{ background:t.section, borderRadius:16, padding:"18px 20px", marginBottom:24, maxWidth:340, width:"100%" }}>
        <div style={{ fontSize:12, fontWeight:800, color:t.t3, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:10 }}>Before you continue</div>
        <div style={{ fontSize:13, color:t.t2, lineHeight:1.7 }}>
          When you ask Razor a question, your message text and relevant booking data (appointment dates, counts, earnings totals) are sent to a third-party AI provider (Anthropic) to generate a response. No client names, phone numbers, or personal identifying information are included. Responses are not stored by Anthropic.
        </div>
      </div>
      <button onClick={() => { try { localStorage.setItem("faded_razor_consented","1"); } catch {} setAiConsented(true); }}
        style={{ width:"100%", maxWidth:340, padding:16, background:"#2563EB", border:"none", borderRadius:14, color:"#fff", fontSize:15, fontWeight:800, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>
        I Understand — Open Razor
      </button>
      {onClose && <button onClick={onClose} style={{ marginTop:12, background:"none", border:"none", color:t.t4, fontSize:13, cursor:"pointer", padding:8 }}>Not now</button>}
    </div>
  );

  return (
    <div style={{ display:"flex", flexDirection:"column", position:"fixed", inset:0, zIndex:200, background:t.bodyBg, overflow:"hidden" }}>

      {/* Header — fixed inside our fixed container, no sticky needed */}
      <div style={{ flexShrink:0, zIndex:100, background:t.headerBg, backdropFilter:"blur(20px)", WebkitBackdropFilter:"blur(20px)", borderBottom:`1px solid ${t.border}`, paddingTop:"max(48px, calc(var(--sat, 0px) + 4px))", paddingBottom:12, paddingLeft:16, paddingRight:16, display:"flex", alignItems:"center", gap:12 }}>
        {onClose && (
          <button onClick={onClose} style={{ minWidth:44, minHeight:44, width:44, height:44, borderRadius:10, background:t.section, border:`1px solid ${t.border}`, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", outline:"none", flexShrink:0 }}>
            <svg aria-hidden="true" focusable="false"width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={t.t3} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
          </button>
        )}
        <div style={{ flex:1 }}>
          <div style={{ fontSize:20, fontWeight:900, color:t.t1, fontFamily:"'Bebas Neue',sans-serif", letterSpacing:"0.06em", lineHeight:1 }}>RAZOR AI</div>
          <div style={{ fontSize:11, color:t.t4, marginTop:2 }}>AI assistant · Powered by Claude · Responses may be inaccurate</div>
        </div>
        <button onClick={()=>{ window.speechSynthesis?.cancel(); import("@capacitor-community/text-to-speech").then(({TextToSpeech})=>TextToSpeech.stop()).catch(()=>{}); setVoiceReply(v=>!v); }}
          title={voiceReply?"AI voice replies ON — tap to mute":"AI voice replies OFF — tap to unmute"}
          style={{ width:34, height:34, borderRadius:10, border:`1px solid ${t.border2}`, background:voiceReply?t.blueBg:t.section, cursor:"pointer", fontSize:16, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
          {voiceReply ? "🔊" : "🔇"}
        </button>
        {speaking ? (
          <div style={{ display:"flex", alignItems:"center", gap:4, background:"rgba(37,99,235,0.1)", padding:"4px 8px", borderRadius:12, flexShrink:0 }}>
            <div style={{ width:6, height:6, borderRadius:"50%", background:a, animation:"pulse 0.8s ease infinite" }}/>
            <span style={{ fontSize:11, color:a, fontWeight:700 }}>SPEAKING</span>
          </div>
        ) : (
          <div style={{ display:"flex", alignItems:"center", gap:4, background:"rgba(34,197,94,0.1)", padding:"4px 8px", borderRadius:12, flexShrink:0 }}>
            <div style={{ width:5, height:5, borderRadius:"50%", background:"#22C55E" }}/>
            <span style={{ fontSize:11, color:"#16A34A", fontWeight:700 }}>LIVE</span>
          </div>
        )}
      </div>

      <div style={{ flex:1, display:"flex", flexDirection:"column", padding:"16px 16px 0", overflow:"hidden" }}>

      <div style={{ display:"flex", gap:6, overflowX:"auto", paddingBottom:8, marginBottom:10 }}>
        {quickQuestions.map(q=>(
          <button key={q} onClick={()=>sendMessage(q)}
            style={{ flexShrink:0, padding:"6px 12px", borderRadius:20, border:`1px solid ${a}44`, background:`${a}10`, color:a, fontSize:11, fontWeight:600, cursor:"pointer", whiteSpace:"nowrap" }}>
            {q}
          </button>
        ))}
      </div>

      <div ref={scrollRef} style={{ flex:1, overflowY:"auto", display:"flex", flexDirection:"column", gap:10, paddingBottom:12 }}>
        {messages === null ? (
          <div style={{ display:"flex", alignItems:"center", gap:8, opacity:0.5 }}>
            <div style={{ width:28, height:28, borderRadius:8, background:`${a}22`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:14 }}>🪒</div>
            <div style={{ background:t.section, padding:"10px 16px", borderRadius:"18px 18px 18px 4px", border:`1px solid ${t.border}` }}>
              <div style={{ display:"flex", gap:4 }}>
                {[0,1,2].map(i=>(<div key={i} style={{ width:6, height:6, borderRadius:"50%", background:t.t4, animation:`bounce 1s ease infinite ${i*0.2}s` }}/>))}
              </div>
            </div>
          </div>
        ) : (messages||[]).map((msg,i)=>(
          <div key={i}>
            <div style={{ display:"flex", justifyContent:msg.role==="user"?"flex-end":"flex-start" }}>
              {msg.role==="assistant" && (
                <div style={{ width:28, height:28, borderRadius:8, background:`${a}22`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, marginRight:8, flexShrink:0, alignSelf:"flex-end" }}>🪒</div>
              )}
              <div style={{
                maxWidth:"75%",
                padding:"10px 14px",
                borderRadius:msg.role==="user"?"18px 18px 4px 18px":"18px 18px 18px 4px",
                background:msg.role==="user"?a:t.section,
                color:msg.role==="user"?"#fff":t.t1,
                fontSize:13,
                lineHeight:1.6,
                border:msg.role==="user"?"none":`1px solid ${t.border}`,
                boxShadow:"0 2px 8px rgba(0,0,0,0.06)",
              }}>
                {msg.text}
              </div>
            </div>
            {msg.role==="assistant" && i === (messages||[]).length-1 && chips.length > 0 && !loading && (
              <div style={{ display:"flex", gap:8, marginTop:8, marginLeft:36, flexWrap:"wrap" }}>
                {chips.map(chip=>(
                  <button key={chip.id} onClick={()=>executeChip(chip)} disabled={!!chipBusy}
                    style={{ padding:"8px 16px", background:chipBusy===chip.id?"#2563EB":"rgba(37,99,235,0.08)", border:"1.5px solid rgba(37,99,235,0.5)", borderRadius:20, color:chipBusy===chip.id?"#fff":"#60A5FA", fontSize:12, fontWeight:700, cursor:chipBusy?"default":"pointer", transition:"all 0.15s", opacity:chipBusy&&chipBusy!==chip.id?0.4:1, fontFamily:"'DM Sans',sans-serif" }}>
                    {chipBusy===chip.id ? "Working…" : chip.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
        {loading && (
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <div style={{ width:28, height:28, borderRadius:8, background:`${a}22`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:14 }}>🪒</div>
            <div style={{ background:t.section, padding:"10px 16px", borderRadius:"18px 18px 18px 4px", border:`1px solid ${t.border}` }}>
              <div style={{ display:"flex", gap:4 }}>
                {[0,1,2].map(i=>(
                  <div key={i} style={{ width:6, height:6, borderRadius:"50%", background:t.t4, animation:`bounce 1s ease infinite ${i*0.2}s` }}/>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <div style={{ paddingBottom:16 }}>
        <div style={{ display:"flex", gap:8, background:t.card, borderRadius:16, border:`1.5px solid ${listening?"#DC2626":t.border}`, padding:"8px 8px 8px 16px", alignItems:"center", transition:"border-color 0.2s" }}>
          <input
            value={input}
            onChange={e=>{ setInput(e.target.value); setMicError(""); }}
            onKeyDown={e=>e.key==="Enter"&&!e.shiftKey&&sendMessage(input)}
            placeholder={listening ? "Speak now — I'm listening..." : "Ask Razor anything..."}
            style={{ flex:1, border:"none", outline:"none", fontSize:16, color:listening?"#DC2626":t.inputText, fontFamily:"'DM Sans',sans-serif", background:"transparent", fontStyle:listening&&!input?"italic":"normal" }}
          />
          <button
            onClick={listening ? stopListening : startListening}
            style={{ minWidth:44, minHeight:44, width:44, height:44, borderRadius:12, background:listening?"#DC2626":`${a}15`, border:"none", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, flexShrink:0, transition:"all 0.2s", boxShadow:listening?"0 0 0 4px rgba(220,38,38,0.2)":"none" }}>
            {listening ? "⏹" : "🎤"}
          </button>
          <button onClick={()=>sendMessage(input)} disabled={!input.trim()||loading||listening}
            style={{ minWidth:44, minHeight:44, width:44, height:44, borderRadius:12, background:input.trim()&&!listening?a:t.surface2, border:"none", cursor:input.trim()&&!listening?"pointer":"default", display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, flexShrink:0, transition:"all 0.2s" }}>
            ➤
          </button>
        </div>
        {listening && (
          <div style={{ marginTop:8, fontSize:12, color:"#DC2626", fontWeight:600, display:"flex", alignItems:"center", gap:6, paddingLeft:4 }}>
            <div style={{ width:8, height:8, borderRadius:"50%", background:"#DC2626", animation:"pulse 1s ease infinite", flexShrink:0 }}/>
            <span>{input ? `"${input}"` : "Listening… speak now"}</span>
          </div>
        )}
        {micError && (
          <div style={{ marginTop:8, fontSize:12, color:"#DC2626", fontWeight:600, background:"rgba(220,38,38,0.06)", border:"1px solid rgba(220,38,38,0.2)", borderRadius:10, padding:"8px 12px" }}>
            🎤 {micError}
          </div>
        )}
      </div>

      <style>{`
        @keyframes bounce {
          0%,100%{transform:translateY(0)}
          50%{transform:translateY(-4px)}
        }
      `}</style>
    </div>
    </div>
  );
}

// ─── WAITLIST VIEWER (barber) ─────────────────────────────────────────────────
function WaitlistViewer({ barberId, t, onNotify }) {
  const [entries, setEntries] = useState([]);

  useEffect(() => {
    supabase.from("waitlist").select("*").eq("barber_id", barberId).eq("status","waiting").order("created_at")
      .then(({ data }) => setEntries(data || []));

    const channel = supabase.channel("waitlist_barber_"+barberId)
      .on("postgres_changes", { event:"*", schema:"public", table:"waitlist", filter:`barber_id=eq.${barberId}` }, () => {
        supabase.from("waitlist").select("*").eq("barber_id", barberId).eq("status","waiting").order("created_at")
          .then(({ data }) => setEntries(data || []));
      }).subscribe();
    return () => supabase.removeChannel(channel);
  }, [barberId]);

  const notifyEntry = async (entry) => {
    try {
      const { error } = await supabase.from("waitlist").update({ status:"notified", notified_at: new Date().toISOString() }).eq("id", entry.id);
      if (error) { console.error("[FADED] notifyEntry:", error.message); return; }
      setEntries(prev => prev.filter(e => e.id !== entry.id));
      if (entry.client_phone) {
        const slotTime = entry.slot_time ? new Date(entry.slot_time).toLocaleTimeString([], { hour:"2-digit", minute:"2-digit" }) : "your slot";
        const msg = `Hey ${entry.client_name}! A spot just opened up at ${slotTime}. You have 15 minutes to grab it — book now at https://app.letsgetfaded.com 💈`;
        window.open(`sms:${entry.client_phone}?body=${encodeURIComponent(msg)}`);
      }
    } catch (e) { console.error("[FADED] notifyEntry:", e); }
  };

  const removeEntry = async (id) => {
    // Optimistic update — remove from UI immediately
    setEntries(prev => prev.filter(e => e.id !== id));
    try {
      const { error, count } = await supabase.from("waitlist").delete({ count:"exact" }).eq("id", id);
      if (error || count === 0) {
        // RLS blocked it or row not found — put the entry back and log
        supabase.from("waitlist").select("*").eq("id", id).single()
          .then(({ data }) => { if (data) setEntries(prev => [...prev, data].sort((a,b) => new Date(a.created_at)-new Date(b.created_at))); });
      }
    } catch (e) { console.error("[FADED] removeEntry:", e); }
  };

  if (entries.length === 0) return null;

  return (
    <div style={{ margin:"12px 16px 0", background:t.card, borderRadius:20, padding:20, border:"1.5px solid rgba(239,68,68,0.25)" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
        <div style={{ fontSize:13, fontWeight:800, color:t.t1, fontFamily:"'DM Sans',sans-serif" }}>Waitlist</div>
        <span style={{ fontSize:11, fontWeight:700, color:"#EF4444", background:"rgba(239,68,68,0.1)", padding:"3px 10px", borderRadius:20 }}>{entries.length} waiting</span>
      </div>
      {entries.map((e, i, arr) => (
        <div key={e.id} style={{ padding:"10px 0", borderBottom:i<arr.length-1?`1px solid ${t.divider}`:"none" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
            <div>
              <div style={{ fontSize:13, fontWeight:700, color:t.t1 }}>{e.client_name}</div>
              <div style={{ fontSize:11, color:t.t3, marginTop:2 }}>
                {e.service} · {e.slot_time ? new Date(e.slot_time).toLocaleString([], { month:"short", day:"numeric", hour:"2-digit", minute:"2-digit" }) : "Flexible"}
              </div>
              {e.client_phone && <div style={{ fontSize:11, color:t.t4, marginTop:1 }}>📱 {e.client_phone}</div>}
            </div>
            <div style={{ display:"flex", gap:6, flexShrink:0 }}>
              <button onClick={()=>notifyEntry(e)} style={{ padding:"6px 10px", background:"rgba(34,197,94,0.12)", border:"1px solid rgba(34,197,94,0.3)", borderRadius:8, color:"#22C55E", fontSize:11, fontWeight:700, cursor:"pointer" }}>📲 Notify</button>
              <button onClick={()=>removeEntry(e.id)} style={{ padding:"6px 10px", background:t.section, border:`1px solid ${t.border}`, borderRadius:8, color:t.t4, fontSize:11, fontWeight:700, cursor:"pointer" }}>✕</button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── BARBER AVAILABILITY TAB ──────────────────────────────────────────────────
const DAYS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const DEFAULT_HOURS = { start:"09:00", end:"18:00" };

function BarberAvailabilityTab({ session, myBarberData, onUpdateBarberData }) {
  const { t } = useT();

  // ── Blocked dates state ──────────────────────────────────────────────────────
  const [blockedDates, setBlockedDates] = useState([]);
  const [newBlockDate, setNewBlockDate] = useState("");
  const [newBlockNote, setNewBlockNote] = useState("");
  const [addingBlock, setAddingBlock] = useState(false);
  const [removingBlock, setRemovingBlock] = useState(null);

  useEffect(() => {
    if (!session?.user?.id) return;
    supabase.from("barber_blocked_dates")
      .select("*")
      .eq("barber_id", session.user.id)
      .gte("date", new Date().toISOString().slice(0, 10))
      .order("date", { ascending: true })
      .then(({ data }) => setBlockedDates(data || []));
  }, [session]); // eslint-disable-line react-hooks/exhaustive-deps

  const addBlockedDate = async () => {
    if (!newBlockDate) return;
    setAddingBlock(true);
    const { data, error } = await supabase.from("barber_blocked_dates").insert({
      barber_id: session.user.id,
      date: newBlockDate,
      reason: newBlockNote.trim() || null,
    }).select().single();
    setAddingBlock(false);
    if (!error && data) {
      setBlockedDates(prev => [...prev, data].sort((a, b) => a.date.localeCompare(b.date)));
      setNewBlockDate("");
      setNewBlockNote("");
    }
  };

  const removeBlockedDate = async (id) => {
    setRemovingBlock(id);
    await supabase.from("barber_blocked_dates").delete().eq("id", id);
    setBlockedDates(prev => prev.filter(d => d.id !== id));
    setRemovingBlock(null);
  };

  // Build initial state from DB data
  const initHours = () => {
    const wh = myBarberData?.working_hours || {};
    const result = {};
    DAYS.forEach((_, i) => {
      result[i] = wh[i] ? { ...wh[i] } : null;
    });
    // Default Mon-Sat on, Sun off if nothing saved yet
    if (Object.keys(wh).length === 0) {
      for (let d = 1; d <= 6; d++) result[d] = { ...DEFAULT_HOURS };
      result[0] = null;
    }
    return result;
  };

  const [hours, setHours]     = useState(initHours);
  const [window_, setWindow]  = useState(myBarberData?.booking_window_days ?? 14);
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);

  const toggleDay = (d) => {
    setHours(prev => ({
      ...prev,
      [d]: prev[d] ? null : { ...DEFAULT_HOURS }
    }));
  };

  const setTime = (d, field, val) => {
    setHours(prev => ({
      ...prev,
      [d]: { ...(prev[d] || DEFAULT_HOURS), [field]: val }
    }));
  };

  const save = async () => {
    setSaving(true);
    const { error } = await supabase.from("barbers").update({
      working_hours: hours,
      booking_window_days: window_,
    }).eq("id", session.user.id);
    setSaving(false);
    if (!error) {
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      if (onUpdateBarberData) onUpdateBarberData(prev => prev ? { ...prev, working_hours: hours, booking_window_days: window_ } : prev);
    }
  };

  const activeDays = DAYS.filter((_, i) => hours[i]);
  const totalHrs = DAYS.reduce((sum, _, i) => {
    if (!hours[i]) return sum;
    const [sh, sm] = hours[i].start.split(":").map(Number);
    const [eh, em] = hours[i].end.split(":").map(Number);
    return sum + (eh + em/60) - (sh + sm/60);
  }, 0);

  return (
    <div style={{ padding:"20px 16px 100px" }}>

      {/* Summary pill */}
      <div style={{ background:t.card, borderRadius:16, padding:"14px 18px", marginBottom:16, border:`1.5px solid ${t.border}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div>
          <div style={{ fontSize:13, fontWeight:800, color:t.t1, fontFamily:"'DM Sans',sans-serif" }}>Weekly Schedule</div>
          <div style={{ fontSize:12, color:t.t3, marginTop:3 }}>{activeDays.length} days · ~{Math.round(totalHrs)}h/week</div>
        </div>
        <div style={{ fontSize:11, color:"#2563EB", fontWeight:700, background:"rgba(37,99,235,0.1)", padding:"4px 10px", borderRadius:20 }}>
          Book up to {window_}d ahead
        </div>
      </div>

      {/* Day rows */}
      <div style={{ background:t.card, borderRadius:16, border:`1.5px solid ${t.border}`, overflow:"hidden", marginBottom:16 }}>
        {DAYS.map((day, i) => {
          const on = !!hours[i];
          return (
            <div key={i} style={{ borderBottom: i < 6 ? `1px solid ${t.divider}` : "none" }}>
              {/* Day header row */}
              <div style={{ display:"flex", alignItems:"center", gap:12, padding:"14px 16px" }}>
                {/* Toggle */}
                <button onClick={() => toggleDay(i)}
                  style={{ width:44, height:26, borderRadius:13, border:"none", cursor:"pointer", padding:0, position:"relative", background: on ? "#2563EB" : t.section, transition:"background 0.2s", flexShrink:0 }}>
                  <span style={{ position:"absolute", top:3, left: on ? 21 : 3, width:20, height:20, borderRadius:10, background:"#fff", transition:"left 0.2s", boxShadow:"0 1px 3px rgba(0,0,0,0.3)" }}/>
                </button>
                <span style={{ fontSize:14, fontWeight:700, color: on ? t.t1 : t.t4, fontFamily:"'DM Sans',sans-serif", width:36 }}>{day}</span>
                {on ? (
                  <span style={{ fontSize:12, color:t.t3, marginLeft:"auto" }}>
                    {hours[i].start} – {hours[i].end}
                  </span>
                ) : (
                  <span style={{ fontSize:12, color:t.t4, marginLeft:"auto" }}>Off</span>
                )}
              </div>
              {/* Time pickers — shown when day is active */}
              {on && (
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, padding:"0 16px 14px" }}>
                  <div>
                    <div style={{ fontSize:11, fontWeight:700, color:t.t4, marginBottom:4, textTransform:"uppercase", letterSpacing:0.5 }}>Start</div>
                    <input type="time" value={hours[i].start} onChange={e => setTime(i, "start", e.target.value)}
                      style={{ width:"100%", padding:"9px 10px", borderRadius:10, border:`1.5px solid ${t.border2}`, background:t.section, color:t.inputText, fontSize:13, outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box" }}/>
                  </div>
                  <div>
                    <div style={{ fontSize:11, fontWeight:700, color:t.t4, marginBottom:4, textTransform:"uppercase", letterSpacing:0.5 }}>End</div>
                    <input type="time" value={hours[i].end} onChange={e => setTime(i, "end", e.target.value)}
                      style={{ width:"100%", padding:"9px 10px", borderRadius:10, border:`1.5px solid ${t.border2}`, background:t.section, color:t.inputText, fontSize:13, outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box" }}/>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Booking window */}
      <div style={{ background:t.card, borderRadius:16, padding:"16px 18px", border:`1.5px solid ${t.border}`, marginBottom:16 }}>
        <div style={{ fontSize:13, fontWeight:800, color:t.t1, fontFamily:"'DM Sans',sans-serif", marginBottom:4 }}>Booking Window</div>
        <div style={{ fontSize:12, color:t.t3, marginBottom:14 }}>How far ahead clients can book you</div>
        <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
          {[{label:"1 Week", val:7},{label:"2 Weeks", val:14},{label:"3 Weeks", val:21},{label:"1 Month", val:30}].map(opt => (
            <button key={opt.val} onClick={() => setWindow(opt.val)}
              style={{ padding:"8px 16px", borderRadius:20, border:`1.5px solid ${window_===opt.val?"#2563EB":t.border}`, background: window_===opt.val ? "rgba(37,99,235,0.12)" : t.section, color: window_===opt.val ? "#2563EB" : t.t3, fontSize:13, fontWeight: window_===opt.val ? 700 : 500, cursor:"pointer" }}>
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Save */}
      <button onClick={save} disabled={saving}
        style={{ width:"100%", padding:"14px", background: saved ? "#059669" : "#2563EB", border:"none", borderRadius:14, color:"#fff", fontSize:14, fontWeight:800, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", opacity: saving ? 0.7 : 1 }}>
        {saving ? "Saving…" : saved ? "✓ Saved" : "Save Availability"}
      </button>

      <div style={{ fontSize:12, color:t.t4, textAlign:"center", marginTop:12 }}>
        Clients see your open slots when booking. Service duration comes from your Services tab.
      </div>

      {/* ── Blocked / Closed Dates ─────────────────────────────────────────── */}
      <div style={{ background:t.card, borderRadius:16, padding:"16px 18px", border:`1.5px solid ${t.border}`, marginTop:20 }}>
        <div style={{ fontSize:13, fontWeight:800, color:t.t1, fontFamily:"'DM Sans',sans-serif", marginBottom:4 }}>Closed Days</div>
        <div style={{ fontSize:12, color:t.t3, marginBottom:14 }}>Block specific dates (vacation, personal days). Clients won't be able to book you on these days.</div>

        {/* Add a date */}
        <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:16 }}>
          <input
            type="date"
            value={newBlockDate}
            min={new Date().toISOString().slice(0,10)}
            onChange={e => setNewBlockDate(e.target.value)}
            style={{ padding:"9px 12px", borderRadius:10, border:`1.5px solid ${t.border2}`, background:t.section, color:t.inputText, fontSize:13, outline:"none", fontFamily:"'DM Sans',sans-serif" }}
          />
          <input
            type="text"
            value={newBlockNote}
            onChange={e => setNewBlockNote(e.target.value.slice(0, 100))}
            placeholder="Reason (optional — e.g. Vacation)"
            maxLength={100}
            style={{ padding:"9px 12px", borderRadius:10, border:`1.5px solid ${t.border2}`, background:t.section, color:t.inputText, fontSize:13, outline:"none", fontFamily:"'DM Sans',sans-serif" }}
          />
          <button
            onClick={addBlockedDate}
            disabled={!newBlockDate || addingBlock}
            style={{ padding:"10px", background: newBlockDate ? "#2563EB" : "rgba(37,99,235,0.3)", border:"none", borderRadius:10, color:"#fff", fontSize:13, fontWeight:700, cursor: newBlockDate ? "pointer" : "default", opacity: addingBlock ? 0.7 : 1 }}
          >
            {addingBlock ? "Adding…" : "+ Block This Date"}
          </button>
        </div>

        {/* List of blocked dates */}
        {blockedDates.length > 0 ? (
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {blockedDates.map(bd => (
              <div key={bd.id} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 12px", background:t.section, borderRadius:10, border:`1px solid ${t.border}` }}>
                <div>
                  <div style={{ fontSize:13, fontWeight:700, color:t.t1 }}>
                    {new Date(bd.date + "T12:00:00").toLocaleDateString("en-CA", { weekday:"short", month:"short", day:"numeric" })}
                  </div>
                  {bd.reason && <div style={{ fontSize:11, color:t.t4, marginTop:2 }}>{bd.reason}</div>}
                </div>
                <button
                  onClick={() => removeBlockedDate(bd.id)}
                  disabled={removingBlock === bd.id}
                  style={{ padding:"5px 10px", background:"rgba(220,38,38,0.1)", border:"1px solid rgba(220,38,38,0.3)", borderRadius:8, color:"#EF4444", fontSize:12, fontWeight:700, cursor:"pointer", flexShrink:0 }}
                >
                  {removingBlock === bd.id ? "…" : "Remove"}
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ fontSize:12, color:t.t4, textAlign:"center", padding:"12px 0" }}>No closed days scheduled</div>
        )}
      </div>
    </div>
  );
}

// ─── BARBER SERVICES TAB ──────────────────────────────────────────────────────
function BarberServicesTab({ session }) {
  const { t } = useT();
  const [services, setServices]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [saving, setSaving]             = useState({});
  const [seeding, setSeeding]           = useState(false);
  const [seedingAddons, setSeedingAddons] = useState(false);
  const [newAddon, setNewAddon]         = useState({ name:"", price:"", duration_minutes:"" });
  const [addingAddon, setAddingAddon]   = useState(false);
  const [showAddonForm, setShowAddonForm] = useState(false);

  const CATEGORIES = ["Haircuts","Beard & Face","Combos","Specialty"];

  useEffect(() => {
    if (!session?.user?.id) return;
    loadServices();
  }, [session]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadServices = async () => {
    setLoading(true);
    try {
      const { data } = await supabase
        .from("barber_services")
        .select("*")
        .eq("barber_id", session.user.id)
        .order("sort_order", { ascending: true });
      setServices(data || []);
    } catch (e) {
      console.error("[FADED] loadServices:", e);
    } finally {
      setLoading(false);
    }
  };

  const seedDefaultServices = async () => {
    setSeeding(true);
    const rows = DEFAULT_SERVICES.map((s, i) => ({
      ...s,
      barber_id: session.user.id,
      is_active: false,
      is_addon: false,
      sort_order: i,
    }));
    await supabase.from("barber_services").insert(rows);
    await loadServices();
    setSeeding(false);
  };

  const seedDefaultAddons = async () => {
    setSeedingAddons(true);
    const existing = services.filter(s => s.is_addon).map(s => s.name);
    const toInsert = DEFAULT_ADDONS
      .filter(a => !existing.includes(a.name))
      .map((a, i) => ({
        ...a,
        barber_id: session.user.id,
        is_active: false,
        is_addon: true,
        sort_order: 1000 + i,
      }));
    if (toInsert.length > 0) await supabase.from("barber_services").insert(toInsert);
    await loadServices();
    setSeedingAddons(false);
  };

  const addCustomAddon = async () => {
    const name = sanitize(newAddon.name, 60).trim();
    const price = parseFloat(newAddon.price);
    const duration_minutes = parseInt(newAddon.duration_minutes, 10) || 15;
    if (!name || isNaN(price) || price < 0) return;
    setAddingAddon(true);
    await supabase.from("barber_services").insert({
      barber_id: session.user.id,
      name, price, duration_minutes,
      is_active: true,
      is_addon: true,
      sort_order: 2000 + Date.now(),
    });
    setNewAddon({ name:"", price:"", duration_minutes:"" });
    setShowAddonForm(false);
    await loadServices();
    setAddingAddon(false);
  };

  const toggleActive = async (svc) => {
    const next = !svc.is_active;
    setServices(prev => prev.map(s => s.id === svc.id ? { ...s, is_active: next } : s));
    await supabase.from("barber_services").update({ is_active: next }).eq("id", svc.id);
  };

  const savePrice = async (svc, raw) => {
    const price = parseFloat(raw);
    if (isNaN(price) || price < 0) return;
    setSaving(prev => ({ ...prev, [svc.id]: true }));
    await supabase.from("barber_services").update({ price }).eq("id", svc.id);
    setServices(prev => prev.map(s => s.id === svc.id ? { ...s, price } : s));
    setSaving(prev => ({ ...prev, [svc.id]: false }));
  };

  const saveDuration = async (svc, raw) => {
    const duration_minutes = parseInt(raw, 10);
    if (isNaN(duration_minutes) || duration_minutes < 5) return;
    await supabase.from("barber_services").update({ duration_minutes }).eq("id", svc.id);
    setServices(prev => prev.map(s => s.id === svc.id ? { ...s, duration_minutes } : s));
  };

  const saveBuffer = async (svc, raw) => {
    const buffer_minutes = parseInt(raw, 10);
    if (isNaN(buffer_minutes) || buffer_minutes < 0) return;
    const clamped = Math.min(60, buffer_minutes);
    await supabase.from("barber_services").update({ buffer_minutes: clamped }).eq("id", svc.id);
    setServices(prev => prev.map(s => s.id === svc.id ? { ...s, buffer_minutes: clamped } : s));
  };

  if (loading) return <div style={{ textAlign:"center", padding:40, color:t.t4, fontSize:13 }}>Loading services...</div>;

  if (services.length === 0) return (
    <div style={{ padding:"40px 24px", textAlign:"center" }}>
      <div style={{ fontSize:36, marginBottom:16 }}>✂️</div>
      <div style={{ fontSize:17, fontWeight:800, color:t.t1, fontFamily:"'DM Sans',sans-serif", marginBottom:8 }}>Set Up Your Service Menu</div>
      <div style={{ fontSize:13, color:t.t3, maxWidth:280, margin:"0 auto 28px", lineHeight:1.6 }}>
        Add your services and prices so clients know exactly what you offer and what it costs.
      </div>
      <button onClick={seedDefaultServices} disabled={seeding}
        style={{ padding:"14px 32px", background:"#2563EB", border:"none", borderRadius:14, color:"#fff", fontSize:14, fontWeight:800, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", opacity:seeding?0.7:1 }}>
        {seeding ? "Setting up..." : "Load Service Menu →"}
      </button>
      <div style={{ marginTop:12, fontSize:11, color:t.t4 }}>22 services pre-loaded · toggle on what you offer · set your prices</div>
    </div>
  );

  const activeCount = services.filter(s => s.is_active).length;

  return (
    <div style={{ padding:"16px 16px 100px" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
        <div>
          <div style={{ fontSize:15, fontWeight:800, color:t.t1, fontFamily:"'DM Sans',sans-serif" }}>Your Services</div>
          <div style={{ fontSize:11, color:t.t4, marginTop:2 }}>{activeCount} active · visible to clients</div>
        </div>
        <div style={{ fontSize:11, color:"#2563EB", fontWeight:700, background:"rgba(37,99,235,0.1)", padding:"4px 12px", borderRadius:20 }}>
          {activeCount} live
        </div>
      </div>

      {CATEGORIES.map(cat => {
        const catServices = services.filter(s => s.category === cat);
        if (catServices.length === 0) return null;
        return (
          <div key={cat} style={{ marginBottom:20 }}>
            <div style={{ fontSize:11, fontWeight:800, color:t.t4, letterSpacing:2, marginBottom:10 }}>{cat.toUpperCase()}</div>
            <div style={{ background:t.card, borderRadius:18, border:`1.5px solid ${t.border}`, overflow:"hidden" }}>
              {catServices.map((svc, i) => (
                <div key={svc.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"14px 16px", borderBottom: i < catServices.length - 1 ? `1px solid ${t.divider}` : "none" }}>
                  {/* Toggle */}
                  <button onClick={() => toggleActive(svc)}
                    style={{ width:38, height:22, borderRadius:11, border:"none", cursor:"pointer", flexShrink:0, position:"relative",
                      background: svc.is_active ? "#2563EB" : t.surface2, transition:"background 0.2s" }}>
                    <div style={{ position:"absolute", top:3, left: svc.is_active ? 19 : 3, width:16, height:16, borderRadius:"50%", background:"#fff", transition:"left 0.2s", boxShadow:"0 1px 4px rgba(0,0,0,0.25)" }}/>
                  </button>

                  {/* Name + editable duration */}
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:13, fontWeight:700, color: svc.is_active ? t.t1 : t.t4 }}>{svc.name}</div>
                    <div style={{ display:"flex", alignItems:"center", gap:4, marginTop:3 }}>
                      <input
                        type="number"
                        defaultValue={svc.duration_minutes}
                        min={5} max={300} step={5}
                        onBlur={e => saveDuration(svc, e.target.value)}
                        style={{ width:38, padding:"2px 5px", borderRadius:7, border:`1.5px solid ${t.border2}`, background:t.input, color:t.t3, fontSize:11, fontWeight:700, textAlign:"center", outline:"none", fontFamily:"'DM Sans',sans-serif", opacity: svc.is_active ? 1 : 0.5 }}
                      />
                      <span style={{ fontSize:11, color:t.t4 }}>min</span>
                      <span style={{ fontSize:10, color:t.t4, margin:"0 2px" }}>+</span>
                      <input
                        type="number"
                        defaultValue={svc.buffer_minutes || 0}
                        min={0} max={60} step={5}
                        title="Buffer time after this service (prep/cleanup)"
                        onBlur={e => saveBuffer(svc, e.target.value)}
                        style={{ width:32, padding:"2px 4px", borderRadius:7, border:`1.5px solid ${t.border2}`, background:t.input, color:"rgba(245,158,11,0.9)", fontSize:10, fontWeight:700, textAlign:"center", outline:"none", fontFamily:"'DM Sans',sans-serif", opacity: svc.is_active ? 1 : 0.5 }}
                      />
                      <span style={{ fontSize:9, color:t.t4 }}>buf</span>
                    </div>
                  </div>

                  {/* Price input only — promo managed separately */}
                  <div style={{ display:"flex", alignItems:"center", gap:4, flexShrink:0 }}>
                    <span style={{ fontSize:13, color:t.t3, fontWeight:700 }}>$</span>
                    <input
                      type="number"
                      defaultValue={svc.price}
                      min={0}
                      onBlur={e => savePrice(svc, e.target.value)}
                      style={{ width:62, padding:"6px 8px", borderRadius:10, border:`1.5px solid ${t.border2}`, background:t.input,
                        color:t.inputText, fontSize:13, fontWeight:700, textAlign:"center", outline:"none",
                        fontFamily:"'DM Sans',sans-serif", opacity: svc.is_active ? 1 : 0.45 }}
                    />
                    {saving[svc.id] && <span style={{ fontSize:11, color:"#2563EB" }}>✓</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {/* ── Add-On Services Section ─────────────────────────────────────── */}
      <div style={{ marginTop:28, marginBottom:8 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
          <div>
            <div style={{ fontSize:15, fontWeight:800, color:t.t1 }}>Add-On Services</div>
            <div style={{ fontSize:11, color:t.t4, marginTop:2 }}>Extras clients can stack on top of any booking</div>
          </div>
          <button onClick={() => setShowAddonForm(v => !v)}
            style={{ fontSize:12, fontWeight:700, color:"#2563EB", background:"rgba(37,99,235,0.1)", border:"1px solid rgba(37,99,235,0.25)", borderRadius:20, padding:"5px 14px", cursor:"pointer" }}>
            + Custom
          </button>
        </div>

        {/* Examples */}
        <div style={{ fontSize:11, color:t.t4, marginBottom:12, lineHeight:1.6 }}>
          Examples: Hot Towel Finish · Face Mask · Eyebrow Trim · Scalp Massage · Beard Moisturiser
        </div>

        {/* Seed button if no add-ons yet */}
        {services.filter(s => s.is_addon).length === 0 && (
          <button onClick={seedDefaultAddons} disabled={seedingAddons}
            style={{ width:"100%", padding:"12px", background:t.section, border:`1.5px dashed ${t.border}`, borderRadius:14, color:t.t2, fontSize:13, fontWeight:700, cursor:"pointer", marginBottom:12, opacity:seedingAddons?0.7:1 }}>
            {seedingAddons ? "Loading..." : "Load suggested add-ons →"}
          </button>
        )}

        {/* Custom add-on form */}
        {showAddonForm && (
          <div style={{ background:t.card, border:`1.5px solid rgba(37,99,235,0.3)`, borderRadius:16, padding:"16px", marginBottom:12 }}>
            <div style={{ fontSize:12, fontWeight:700, color:t.t2, marginBottom:10 }}>New add-on</div>
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              <input placeholder="Add-on name (e.g. Face Mask)" value={newAddon.name}
                onChange={e => setNewAddon(p => ({ ...p, name: e.target.value }))}
                style={{ padding:"10px 12px", borderRadius:10, border:`1.5px solid ${t.border2}`, background:t.input, color:t.inputText, fontSize:13, outline:"none", fontFamily:"'DM Sans',sans-serif" }}/>
              <div style={{ display:"flex", gap:8 }}>
                <div style={{ flex:1, display:"flex", alignItems:"center", gap:4 }}>
                  <span style={{ fontSize:13, color:t.t3, fontWeight:700 }}>$</span>
                  <input placeholder="Price" type="number" min={0} value={newAddon.price}
                    onChange={e => setNewAddon(p => ({ ...p, price: e.target.value }))}
                    style={{ flex:1, padding:"10px 8px", borderRadius:10, border:`1.5px solid ${t.border2}`, background:t.input, color:t.inputText, fontSize:13, outline:"none", fontFamily:"'DM Sans',sans-serif" }}/>
                </div>
                <div style={{ flex:1, display:"flex", alignItems:"center", gap:4 }}>
                  <input placeholder="Mins" type="number" min={5} value={newAddon.duration_minutes}
                    onChange={e => setNewAddon(p => ({ ...p, duration_minutes: e.target.value }))}
                    style={{ flex:1, padding:"10px 8px", borderRadius:10, border:`1.5px solid ${t.border2}`, background:t.input, color:t.inputText, fontSize:13, outline:"none", fontFamily:"'DM Sans',sans-serif" }}/>
                  <span style={{ fontSize:12, color:t.t4 }}>min</span>
                </div>
              </div>
              <button onClick={addCustomAddon} disabled={addingAddon || !newAddon.name || !newAddon.price}
                style={{ padding:"11px", background:"#2563EB", border:"none", borderRadius:12, color:"#fff", fontSize:13, fontWeight:800, cursor:"pointer", opacity:(addingAddon||!newAddon.name||!newAddon.price)?0.5:1 }}>
                {addingAddon ? "Adding..." : "Add to my menu"}
              </button>
            </div>
          </div>
        )}

        {/* Add-on list */}
        {services.filter(s => s.is_addon).length > 0 && (
          <div style={{ background:t.card, borderRadius:18, border:`1.5px solid ${t.border}`, overflow:"hidden" }}>
            {services.filter(s => s.is_addon).map((svc, i, arr) => (
              <div key={svc.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"14px 16px", borderBottom: i < arr.length - 1 ? `1px solid ${t.divider}` : "none" }}>
                <button onClick={() => toggleActive(svc)}
                  style={{ width:38, height:22, borderRadius:11, border:"none", cursor:"pointer", flexShrink:0, position:"relative",
                    background: svc.is_active ? "#2563EB" : t.surface2, transition:"background 0.2s" }}>
                  <div style={{ position:"absolute", top:3, left: svc.is_active ? 19 : 3, width:16, height:16, borderRadius:"50%", background:"#fff", transition:"left 0.2s", boxShadow:"0 1px 4px rgba(0,0,0,0.25)" }}/>
                </button>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:13, fontWeight:700, color: svc.is_active ? t.t1 : t.t4 }}>{svc.name}</div>
                  <div style={{ display:"flex", alignItems:"center", gap:4, marginTop:3 }}>
                    <input type="number" defaultValue={svc.duration_minutes} min={5} max={120} step={5}
                      onBlur={e => saveDuration(svc, e.target.value)}
                      style={{ width:38, padding:"2px 5px", borderRadius:7, border:`1.5px solid ${t.border2}`, background:t.input, color:t.t3, fontSize:11, fontWeight:700, textAlign:"center", outline:"none", fontFamily:"'DM Sans',sans-serif" }}/>
                    <span style={{ fontSize:11, color:t.t4 }}>min</span>
                  </div>
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:4, flexShrink:0 }}>
                  <span style={{ fontSize:13, color:t.t3, fontWeight:700 }}>+$</span>
                  <input type="number" defaultValue={svc.price} min={0}
                    onBlur={e => savePrice(svc, e.target.value)}
                    style={{ width:56, padding:"6px 8px", borderRadius:10, border:`1.5px solid ${t.border2}`, background:t.input,
                      color:t.inputText, fontSize:13, fontWeight:700, textAlign:"center", outline:"none", fontFamily:"'DM Sans',sans-serif" }}/>
                  {saving[svc.id] && <span style={{ fontSize:11, color:"#2563EB" }}>✓</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ padding:"12px 16px", background:"rgba(37,99,235,0.06)", borderRadius:14, border:"1px solid rgba(37,99,235,0.15)", marginTop:16 }}>
        <div style={{ fontSize:11, color:t.t3, lineHeight:1.6 }}>
          💡 Toggle services <strong>on</strong> to make them bookable. Tap any price or duration to edit — changes save automatically on blur.
        </div>
      </div>
    </div>
  );
}

// ─── BARBER REVIEWS TAB ───────────────────────────────────────────────────────
function BarberReviewsTab({ session, myBarberData }) {
  const { t } = useT();
  const [reviews, setReviews]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [replyText, setReplyText]   = useState({});
  const [replyingId, setReplyingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!session?.user?.id) return;
    supabase.from("reviews")
      .select("id, rating, comment, created_at, barber_reply, booking_id, profiles(full_name)")
      .eq("barber_id", session.user.id)
      .order("created_at", { ascending: false })
      .limit(50)
      .then(({ data }) => { setReviews(data || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [session]);

  const submitReply = async (reviewId) => {
    const text = sanitize(replyText[reviewId] || "", 500);
    if (!text) return;
    setSubmitting(true);
    const { error } = await supabase.from("reviews").update({ barber_reply: text }).eq("id", reviewId).eq("barber_id", session.user.id);
    if (!error) {
      setReviews(prev => prev.map(r => r.id === reviewId ? { ...r, barber_reply: text } : r));
      setReplyingId(null);
      setReplyText(prev => ({ ...prev, [reviewId]: "" }));
    }
    setSubmitting(false);
  };

  const avgRating = reviews.length > 0 ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : null;

  if (loading) return <div style={{ textAlign:"center", padding:60, color:t.t4 }}>Loading reviews…</div>;

  return (
    <div style={{ padding:"20px 16px 100px" }}>
      {/* Rating summary */}
      <div style={{ background:t.card, borderRadius:20, padding:20, border:`1.5px solid ${t.border}`, marginBottom:20, display:"flex", alignItems:"center", gap:20 }}>
        <div style={{ textAlign:"center", minWidth:80 }}>
          <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:48, lineHeight:1, color:t.t1 }}>{avgRating || (myBarberData?.rating || "—")}</div>
          <Stars n={parseFloat(avgRating || myBarberData?.rating || 5)}/>
          <div style={{ fontSize:11, color:t.t4, marginTop:4 }}>{reviews.length} review{reviews.length !== 1 ? "s" : ""}</div>
        </div>
        <div style={{ flex:1 }}>
          {[5,4,3,2,1].map(star => {
            const count = reviews.filter(r => r.rating === star).length;
            const pct = reviews.length > 0 ? Math.round(count / reviews.length * 100) : 0;
            return (
              <div key={star} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
                <span style={{ fontSize:11, color:t.t4, minWidth:12, textAlign:"right" }}>{star}</span>
                <span style={{ fontSize:11, color:"#FBBF24" }}>★</span>
                <div style={{ flex:1, height:6, borderRadius:3, background:t.section, overflow:"hidden" }}>
                  <div style={{ width:`${pct}%`, height:"100%", background:"#FBBF24", borderRadius:3, transition:"width 0.5s ease" }}/>
                </div>
                <span style={{ fontSize:11, color:t.t4, minWidth:20 }}>{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      {reviews.length === 0 ? (
        <motion.div variants={staggerContainer} initial="hidden" animate="visible" style={{ textAlign:"center", padding:"48px 24px", background:"linear-gradient(135deg,#1A1F2E,#141928)", borderRadius:20, border:"1px solid #252B3B" }}>
          <motion.div variants={scaleIn} style={{ fontSize:40, marginBottom:12 }}>⭐</motion.div>
          <motion.div variants={fadeUp} style={{ fontSize:16, fontWeight:800, color:t.t1, fontFamily:"'DM Sans',sans-serif", marginBottom:6 }}>Your first review is coming</motion.div>
          <motion.div variants={fadeUp} style={{ fontSize:13, color:t.t3, lineHeight:1.6 }}>Complete your first booking and your client can leave a review. Keep showing up — the stars follow.</motion.div>
        </motion.div>
      ) : reviews.map(r => (
        <div key={r.id} style={{ background:t.card, borderRadius:18, border:`1.5px solid ${t.border}`, padding:16, marginBottom:12 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10 }}>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              <div style={{ width:36, height:36, borderRadius:"50%", background:t.blueBg, display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, fontWeight:700, color:t.blueText }}>
                {(r.profiles?.full_name || "C")[0]}
              </div>
              <div>
                <div style={{ fontSize:13, fontWeight:700, color:t.t1 }}>{r.profiles?.full_name || "Client"}</div>
                <div style={{ fontSize:11, color:t.t4 }}>{new Date(r.created_at).toLocaleDateString("en-CA",{month:"short",day:"numeric",year:"numeric"})}</div>
              </div>
            </div>
            <div style={{ display:"flex", gap:2 }}>
              {[1,2,3,4,5].map(s => <span key={s} style={{ color:s<=r.rating?"#FBBF24":"#374151", fontSize:14 }}>★</span>)}
            </div>
          </div>
          {r.comment && <p style={{ fontSize:13, color:t.t2, lineHeight:1.6, margin:"0 0 10px" }}>{r.comment}</p>}
          {r.barber_reply ? (
            <div style={{ padding:"10px 14px", background:t.blueBg, borderRadius:12, borderLeft:"3px solid #2563EB" }}>
              <div style={{ fontSize:11, fontWeight:700, color:"#2563EB", marginBottom:4 }}>Your reply</div>
              <div style={{ fontSize:12, color:t.t2 }}>{r.barber_reply}</div>
            </div>
          ) : replyingId === r.id ? (
            <div style={{ marginTop:8 }}>
              <textarea value={replyText[r.id]||""} onChange={e=>setReplyText(prev=>({...prev,[r.id]:e.target.value}))} placeholder="Write a reply..." rows={3}
                style={{ width:"100%", padding:"10px 12px", borderRadius:12, border:`1.5px solid #2563EB`, background:t.section, color:t.inputText, fontSize:13, outline:"none", resize:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box" }}/>
              <div style={{ display:"flex", gap:8, marginTop:8 }}>
                <button onClick={()=>setReplyingId(null)} style={{ padding:"8px 14px", background:t.section, border:`1px solid ${t.border}`, borderRadius:10, color:t.t3, fontSize:12, fontWeight:700, cursor:"pointer" }}>Cancel</button>
                <button onClick={()=>submitReply(r.id)} disabled={submitting||!(replyText[r.id]||"").trim()} style={{ flex:1, padding:"8px 14px", background:"#2563EB", border:"none", borderRadius:10, color:"#fff", fontSize:12, fontWeight:700, cursor:"pointer", opacity:submitting?0.7:1 }}>
                  {submitting ? "Posting…" : "Post Reply"}
                </button>
              </div>
            </div>
          ) : (
            <button onClick={()=>setReplyingId(r.id)} style={{ marginTop:8, padding:"6px 14px", background:t.section, border:`1px solid ${t.border}`, borderRadius:10, color:t.t3, fontSize:12, fontWeight:700, cursor:"pointer" }}>Reply</button>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── BARBER DASHBOARD ─────────────────────────────────────────────────────────
function BarberDashboard({ session, myBarberData, onStatusChange, onGoLive, onUpdateBarberData, openAI, onAIOpened, appointmentsMode, forceTab, hideHeader, onShopCreated, onSignOut }) {
  const { t, isDark } = useT();
  const [dashTab, setDashTab]   = useState(forceTab || "status");
  const [dashDropOpen, setDashDropOpen] = useState(false);
  const [dropRect, setDropRect] = useState(null);
  const sectionBtnRef = useRef(null);
  const [myStatus, setMyStatus] = useState(myBarberData?.status||"offline");
  const [walkin, setWalkin]     = useState(myBarberData?.walkin||false);
  const [bookings, setBookings] = useState([]);
  const [clientNotes, setClientNotes] = useState({}); // keyed by client_id or client_phone
  const [expandedBookingId, setExpandedBookingId] = useState(null);
  const [barberLiveAppt, setBarberLiveAppt] = useState(null);
  const dismissedBarberLiveRef = useRef((() => { try { return sessionStorage.getItem("faded_live_dismissed") || null; } catch { return null; } })());
  const [queue, setQueue]               = useState([]);
  const [showAddWalkin, setShowAddWalkin] = useState(false);
  const [walkinName, setWalkinName]     = useState("");
  const [walkinPhone, setWalkinPhone]   = useState("");
  const [walkinService, setWalkinService] = useState("Haircut");
  const [heatmapRange, setHeatmapRange] = useState(30);
  const [openSections, setOpenSections] = useState({ cuts:true, heatmap:false, noshow:false });
  const toggleSection = (k) => setOpenSections(prev => ({ ...prev, [k]:!prev[k] }));
  const [avatar, setAvatar]     = useState(myBarberData?.avatar_url||null);
  const [portfolio, setPortfolio] = useState(myBarberData?.portfolio_urls||[]);
  const [googleReviewUrl, setGoogleReviewUrl] = useState(myBarberData?.google_review_url||"");
  const [editGoogleReview, setEditGoogleReview] = useState(false);
  const [savingGoogleReview, setSavingGoogleReview] = useState(false);
  const [editAddress, setEditAddress] = useState(false);
  const [addressVal, setAddressVal]   = useState(myBarberData?.address||"");
  const [savingAddr, setSavingAddr]   = useState(false);
  const [editBio, setEditBio]               = useState(false);
  const [bioVal, setBioVal]                 = useState(myBarberData?.bio||"");
  const [yearsCuttingVal, setYearsCuttingVal] = useState(myBarberData?.years_cutting??""  );
  const [savingBio, setSavingBio]           = useState(false);
  const fileRef = useRef();
  const portRef = useRef();
  const razorRecRef = useRef(null);
  const razorTimerRef = useRef(null);
  const [razorMode, setRazorMode] = useState(() => { try { return localStorage.getItem("faded_razor_mode")==="true"; } catch { return false; } });
  const [razorToast, setRazorToast] = useState(false);
  const [razorListening, setRazorListening] = useState(false);
  const [razorInitialQuery, setRazorInitialQuery] = useState("");
  const [blastOpen, setBlastOpen] = useState(false);
  const [blastMsg,  setBlastMsg]  = useState(`Hey! 💈 Book your next cut — ${window.location.origin}`);
  const [stripeEnabled, setStripeEnabled] = useState(null);
  const [nsp, setNsp]                   = useState(null);
  const [nspDashSaving, setNspDashSaving] = useState(false);
  const [payPrefs, setPayPrefs]         = useState(null); // {cash, etransfer, etransferEmail, payAtShop} — null=loading
  const [payPrefsSaving, setPayPrefsSaving] = useState(false);
  const [stripeConnecting, setStripeConnecting] = useState(false);
  const [stripeBannerDismissed, setStripeBannerDismissed] = useState(() => {
    try { return localStorage.getItem("faded_stripe_banner_dismissed") === "1"; } catch { return false; }
  });

  // Referral modal state
  const [referralModal, setReferralModal]     = useState(null); // { bookingId, clientName, clientPhone }
  const [referralQuery, setReferralQuery]     = useState("");
  const [referralResults, setReferralResults] = useState([]);
  const [referralSearching, setReferralSearching] = useState(false);
  const [referralChosen, setReferralChosen]   = useState(null);

  const [notifInbox, setNotifInbox] = useState(() => { try { return JSON.parse(localStorage.getItem('faded_notif_inbox') || '[]'); } catch { return []; } });
  const [notifOpen, setNotifOpen] = useState(false);
  const [bookingsPaused, setBookingsPaused] = useState(myBarberData?.bookings_paused || false);
  const [pauseSaving, setPauseSaving] = useState(false);
  const [respondingIds, setRespondingIds] = useState(new Set());
  useEffect(() => {
    const handler = () => { try { setNotifInbox(JSON.parse(localStorage.getItem('faded_notif_inbox') || '[]')); } catch {} };
    window.addEventListener('faded:notif-received', handler);
    return () => window.removeEventListener('faded:notif-received', handler);
  }, []);
  const [referralSent, setReferralSent]       = useState(false);
  const referralTimer = useRef(null);

  const searchReferralBarbers = (q) => {
    clearTimeout(referralTimer.current);
    setReferralChosen(null);
    if (!q.trim()) { setReferralResults([]); return; }
    setReferralSearching(true);
    referralTimer.current = setTimeout(async () => {
      try {
        const clean = q.replace(/^@/,"").toLowerCase();
        const { data } = await supabase
          .from("barbers")
          .select("id, handle, shareable_slug, avatar_url, rating, review_count, specialty, profiles(full_name)")
          .or(`handle.ilike.%${clean}%,shareable_slug.ilike.%${clean}%`)
          .neq("id", session.user.id)
          .limit(6);
        setReferralResults(data || []);
      } catch { setReferralResults([]); }
      finally { setReferralSearching(false); }
    }, 300);
  };

  const closeReferralModal = () => {
    setReferralModal(null); setReferralQuery(""); setReferralResults([]);
    setReferralChosen(null); setReferralSent(false);
  };

  // Hey Razor wake-word — short sessions that auto-restart (more reliable on iOS than continuous mode)
  // Paused while Razor screen is open (dashTab==="ai") so the in-app mic can run without conflict
  useEffect(() => {
    if (!razorMode || dashTab === "ai") {
      if (razorTimerRef.current) { clearTimeout(razorTimerRef.current); razorTimerRef.current = null; }
      if (razorRecRef.current) { try { razorRecRef.current.abort(); } catch {} razorRecRef.current = null; }
      setRazorListening(false);
      return;
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setRazorMode(false); return; }
    let active = true;

    const start = () => {
      if (!active) return;
      try {
        const rec = new SR();
        rec.lang = "en-CA";
        rec.continuous = false;
        rec.interimResults = true;
        rec.maxAlternatives = 1;
        rec.onstart = () => { if (active) setRazorListening(true); };
        rec.onresult = (ev) => {
          for (let i = ev.resultIndex; i < ev.results.length; i++) {
            const transcript = ev.results[i][0].transcript;
            const lower = transcript.toLowerCase();
            if (lower.includes("razor") || lower.includes("razer")) {
              // Capture anything said after the wake word as the initial query
              const afterWake = transcript.replace(/hey\s*(razor|razer)/i, "").trim();
              setRazorInitialQuery(afterWake);
              setDashTab("ai");
              setRazorToast(true);
              setTimeout(() => setRazorToast(false), 2200);
            }
          }
        };
        rec.onerror = (e) => {
          setRazorListening(false);
          if (e.error === "not-allowed") {
            active = false;
            setRazorMode(false);
            try { localStorage.setItem("faded_razor_mode","false"); } catch {}
          }
        };
        rec.onend = () => {
          setRazorListening(false);
          if (active) razorTimerRef.current = setTimeout(start, 800);
        };
        razorRecRef.current = rec;
        rec.start();
      } catch {
        setRazorListening(false);
        if (active) razorTimerRef.current = setTimeout(start, 1500);
      }
    };

    start();
    return () => {
      active = false;
      if (razorTimerRef.current) { clearTimeout(razorTimerRef.current); razorTimerRef.current = null; }
      if (razorRecRef.current) { try { razorRecRef.current.abort(); } catch {} razorRecRef.current = null; }
      setRazorListening(false);
    };
  }, [razorMode, dashTab]);

  useEffect(() => {
    if (openAI) { setDashTab("ai"); setDashDropOpen(false); onAIOpened?.(); }
  }, [openAI]); // eslint-disable-line

  // Load real status from DB on mount - prevents stale state
  useEffect(() => {
    if (!session?.user?.id) return;
    supabase.from("barbers").select("status,walkin").eq("id",session.user.id).single()
      .then(({ data }) => {
        if (data) {
          setMyStatus(data.status||"offline");
          setWalkin(data.walkin||false);
        }
      });
  }, [session]);

  useEffect(() => {
    if (!session || typeof session === "string" || !session.user) return;
    supabase.from("bookings").select("*").eq("barber_id",session.user.id).order("created_at",{ascending:false}).limit(20)
      .then(({ data })=>setBookings(data||[]));
    supabase.from("barber_client_notes").select("client_id, client_phone, notes").eq("barber_id",session.user.id)
      .then(({ data }) => {
        if (!data) return;
        const map = {};
        data.forEach(n => { if (n.client_id) map[n.client_id] = n.notes; else if (n.client_phone) map[n.client_phone] = n.notes; });
        setClientNotes(map);
      });

    const ch = supabase.channel("barber-bookings")
      .on("postgres_changes",{ event:"INSERT", schema:"public", table:"bookings", filter:`barber_id=eq.${session.user.id}` },
        payload=>{
          setBookings(prev=>[payload.new,...prev]);
          const b = payload.new;
          if (Notification.permission==="granted") {
            new Notification("New Booking Request 💈", { body:`${b.client_name||"A client"} booked ${b.service_type||"a cut"} — tap to respond`, icon:"/logo192.png" });
          } else if (Notification.permission!=="denied") {
            Notification.requestPermission().then(p=>{ if(p==="granted") new Notification("New Booking Request 💈", { body:`${b.client_name||"A client"} wants to book you!` }); });
          }
        })
      .on("postgres_changes",{ event:"UPDATE", schema:"public", table:"bookings", filter:`barber_id=eq.${session.user.id}` },
        payload=>{
          const b = payload.new;
          setBookings(prev=>prev.map(bk=>bk.id===b.id?{...bk,...b}:bk));
          // Auto-show barber live screen when client checks in
          if (b.checked_in_at && !payload.old?.checked_in_at && (b.status==="accepted"||b.status==="confirmed")) {
            setBarberLiveAppt(b);
          }
        })
      .subscribe();
    return ()=>supabase.removeChannel(ch);
  },[session]);

  // Restore barber live screen when app reopens mid-cut
  useEffect(() => {
    if (barberLiveAppt) return;
    const active = bookings.find(b =>
      b.checked_in_at &&
      (b.status === "accepted" || b.status === "confirmed") &&
      b.id !== dismissedBarberLiveRef.current
    );
    if (active) setBarberLiveAppt(active);
  }, [bookings]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!session?.user?.id) return;
    loadQueue();
    const ch = supabase.channel("walkin-q-"+session.user.id)
      .on("postgres_changes",{ event:"*", schema:"public", table:"walk_in_queue", filter:`barber_id=eq.${session.user.id}` }, loadQueue)
      .subscribe();
    return ()=>supabase.removeChannel(ch);
  },[session]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!session?.user?.id) return;
    supabase.from("barbers").select("stripe_charges_enabled,no_show_protection,cancellation_fee_type,cancellation_fee_flat_cents,cancellation_window_hours,no_show_fee_type,no_show_fee_flat_cents,accepts_cash,accepts_etransfer,etransfer_email,accepts_pay_at_shop").eq("id", session.user.id).single()
      .then(({ data }) => {
        setStripeEnabled(data?.stripe_charges_enabled ?? false);
        setNsp(data ? { enabled: data.no_show_protection ?? false, cancelFeeType: data.cancellation_fee_type || "fifty_percent", cancelFlatCents: data.cancellation_fee_flat_cents || 0, cancelWindowHours: data.cancellation_window_hours || 24, noShowFeeType: data.no_show_fee_type || "hundred_percent", noShowFlatCents: data.no_show_fee_flat_cents || 0 } : { enabled:false, cancelFeeType:"fifty_percent", cancelFlatCents:0, cancelWindowHours:24, noShowFeeType:"hundred_percent", noShowFlatCents:0 });
        setPayPrefs({ cash: data?.accepts_cash !== false, etransfer: !!data?.accepts_etransfer, etransferEmail: data?.etransfer_email || "", payAtShop: data?.accepts_pay_at_shop !== false });
      });
  }, [session?.user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStripeConnect = async () => {
    setStripeConnecting(true);
    try {
      const { data, error } = await supabase.functions.invoke("stripe-connect-onboard", {});
      setStripeConnecting(false);
      if (error || !data?.url) {
        window.dispatchEvent(new CustomEvent("faded-toast", { detail: { msg:"Could not start Stripe setup — try again.", type:"error" } }));
        return;
      }
      window.location.href = data.url;
    } catch { setStripeConnecting(false); }
  };

  const saveNspDash = async (updates) => {
    const next = { ...nsp, ...updates };
    setNsp(next);
    setNspDashSaving(true);
    await supabase.from("barbers").update({
      no_show_protection: next.enabled,
      cancellation_fee_type: next.cancelFeeType,
      cancellation_fee_flat_cents: next.cancelFlatCents,
      cancellation_window_hours: next.cancelWindowHours,
      no_show_fee_type: next.noShowFeeType,
      no_show_fee_flat_cents: next.noShowFlatCents,
    }).eq("id", session.user.id);
    setNspDashSaving(false);
  };

  const savePayPrefs = async (updates) => {
    const next = { ...payPrefs, ...updates };
    // Never let a barber end up with zero ways to pay — keep at least one on.
    const cardOn = stripeEnabled === true;
    const anyOn = next.cash || next.payAtShop || (next.etransfer && next.etransferEmail.trim()) || cardOn;
    if (!anyOn) { window.dispatchEvent(new CustomEvent("faded-toast", { detail: { msg:"Keep at least one payment method on so clients can book.", type:"error" } })); return; }
    setPayPrefs(next);
    setPayPrefsSaving(true);
    await supabase.from("barbers").update({
      accepts_cash: next.cash,
      accepts_pay_at_shop: next.payAtShop,
      accepts_etransfer: next.etransfer,
      etransfer_email: next.etransfer && next.etransferEmail.trim() ? next.etransferEmail.trim() : null,
    }).eq("id", session.user.id);
    setPayPrefsSaving(false);
  };

  const updateStatus = async (status) => {
    if (status === "available" && !myBarberData?.approved) return;
    setMyStatus(status);
    const { error } = await supabase.from("barbers")
      .update({ status, updated_at:new Date().toISOString() })
      .eq("id", session.user.id);
    if (error) Sentry.captureException(error, { tags: { operation: "update_barber_status", status } });
    if (status === "available") {
      // Notify saved clients that barber is now live
      supabase.functions.invoke("notify-barber-live", {
        body: { barber_id: session.user.id, barber_name: myBarberData?.full_name || "Your saved barber" }
      }).catch(() => {});
      if (onGoLive) onGoLive();
    }
    if (onStatusChange) onStatusChange(status);
  };

  const togglePause = async () => {
    if (pauseSaving) return;
    const next = !bookingsPaused;
    setBookingsPaused(next);
    setPauseSaving(true);
    const { error } = await supabase.from("barbers").update({ bookings_paused: next, paused_at: next ? new Date().toISOString() : null }).eq("id", session.user.id);
    if (error) { setBookingsPaused(!next); Sentry.captureException(error, { tags: { operation: "toggle_bookings_paused" } }); }
    setPauseSaving(false);
  };

  const respondBooking = async (id, accept) => {
    if (respondingIds.has(id)) return;
    setRespondingIds(prev => new Set([...prev, id]));
    const update = accept ? { status:"accepted", accepted_at:new Date().toISOString() } : { status:"declined", declined_at:new Date().toISOString() };
    try {
      const { error } = await supabase.from("bookings").update(update).eq("id",id);
      if (error) { console.error("[FADED] respondBooking:", error.message); setRespondingIds(prev => { const s = new Set(prev); s.delete(id); return s; }); return; }
      const bk = bookings.find(b => b.id === id);
      track(accept ? "barber_accepted_booking" : "barber_declined_booking", { booking_id: id, service_type: bk?.service_type });
      if (accept) {
        // Email confirmation
        if (bk?.client_email) {
          supabase.functions.invoke("send-booking-confirmation", {
            body: {
              booking_id:     id,
              booking_ref:    bk.booking_ref || null,
              client_email:   bk.client_email,
              client_name:    bk.client_name,
              barber_name:    myBarberData?.full_name || bk.barber_name,
              service:        bk.service_type,
              scheduled_at:   bk.scheduled_at,
              display_time:   bk.scheduled_at
                ? new Date(bk.scheduled_at).toLocaleString("en-CA", {
                    timeZone: bk.client_timezone || "America/Toronto",
                    weekday: "long", month: "long", day: "numeric", year: "numeric",
                    hour: "numeric", minute: "2-digit",
                  })
                : null,
              address:        myBarberData?.address || null,
              lat:            myBarberData?.lat     || null,
              lng:            myBarberData?.lng     || null,
            },
          }).catch(() => {});
        }
        // SMS confirmation to client
        if (bk?.client_phone) {
          const apptTime = bk.scheduled_at
            ? formatLocalTime(bk.scheduled_at)
            : "your scheduled time";
          const mapsLink = myBarberData?.lat && myBarberData?.lng
            ? ` 📍 https://maps.google.com/?q=${myBarberData.lat},${myBarberData.lng}`
            : "";
          sendSMS(bk.client_phone, `✂️ FADED: Confirmed! ${bk.service_type} with ${myBarberData?.full_name||"your barber"} on ${apptTime} ET.${mapsLink} Show your boarding pass at the chair.`);
        }
        // Push notification to client (works even when app is closed)
        if (bk?.client_id) {
          supabase.functions.invoke("send-push-to-user", {
            body: {
              user_id: bk.client_id,
              title: "✂️ Booking Confirmed!",
              body: `${myBarberData?.full_name||"Your barber"} accepted your ${bk.service_type||"appointment"} on ${apptTime}.`,
              data: { tab: "bookings" },
            }
          }).catch(() => {});
        }
      } else {
        // Barber declined — refund card payment if one was captured
        if (bk?.stripe_payment_intent_id) {
          supabase.functions.invoke("stripe-refund", {
            body: { payment_intent_id: bk.stripe_payment_intent_id, booking_id: id, reason: "barber_declined" },
          }).catch(() => {});
        }
        // Notify client by SMS
        if (bk?.client_phone) {
          sendSMS(bk.client_phone, `FADED: Sorry, ${myBarberData?.full_name||"your barber"} couldn't accept your booking for ${bk.service_type}. Please open the app to find another available barber near you.`);
        }
        // Push notification to client on decline
        if (bk?.client_id) {
          supabase.functions.invoke("send-push-to-user", {
            body: {
              user_id: bk.client_id,
              title: "Booking Not Accepted",
              body: `Sorry, ${myBarberData?.full_name||"your barber"} couldn't take your ${bk.service_type||"appointment"}. Tap to find another barber.`,
              data: { tab: "discover" },
            }
          }).catch(() => {});
        }
      }
      setBookings(prev=>prev.map(b=>b.id===id?{...b,...update}:b));
    } catch (e) {
      console.error("[FADED] respondBooking:", e);
    } finally {
      setRespondingIds(prev => { const s = new Set(prev); s.delete(id); return s; });
    }
  };

  const cancelBooking = async (id) => {
    const bk = bookings.find(b => b.id === id);
    track("booking_cancelled", { booking_id: id, service_type: bk?.service_type, cancelled_by: "barber" });
    setBookings(prev => prev.map(b => b.id === id ? { ...b, status:"cancelled" } : b));
    setExpandedBookingId(null);
    // cancel_booking RPC handles: status update + strike issuance for barber-initiated cancels
    const { data: cancelResult } = await supabase.rpc("cancel_booking", {
      p_booking_id: id,
      p_cancelled_by: "barber",
    });
    // Update local myBarberData with new strike count so banner renders immediately
    if (cancelResult?.strike?.count_30d !== undefined && onUpdateBarberData) {
      onUpdateBarberData(prev => prev ? {
        ...prev,
        cancellation_count_30d: cancelResult.strike.count_30d,
        suspension_pending_review: cancelResult.strike.suspended || prev.suspension_pending_review,
      } : prev);
    }
    // Notify client via SMS — full refund since barber initiated
    if (bk?.client_phone) {
      sendSMS(bk.client_phone, `FADED: Your appointment with ${myBarberData?.full_name||"your barber"} has been cancelled. You will receive a full refund if payment was processed. Please rebook on the app.`);
    }
    // Push notification to client
    if (bk?.client_id) {
      supabase.functions.invoke("send-push-to-user", {
        body: {
          user_id: bk.client_id,
          title: "Appointment Cancelled",
          body: `${myBarberData?.full_name||"Your barber"} cancelled your ${bk.service_type||"appointment"}. You'll receive a full refund — please rebook.`,
          data: { tab: "discover" },
        }
      }).catch(() => {});
    }
  };

  const saveClientNote = async (b, notes) => {
    const key = b.client_id || b.client_phone;
    if (!key) return;
    setClientNotes(prev => ({ ...prev, [key]: notes }));
    const { data: existing } = await supabase.from("barber_client_notes")
      .select("id").eq("barber_id", session.user.id)
      .eq(b.client_id ? "client_id" : "client_phone", key).maybeSingle();
    if (existing) {
      await supabase.from("barber_client_notes").update({ notes, updated_at: new Date().toISOString() }).eq("id", existing.id);
    } else {
      await supabase.from("barber_client_notes").insert({ barber_id: session.user.id, client_id: b.client_id || null, client_phone: b.client_id ? null : (b.client_phone || null), notes });
    }
  };

  const completeBooking = async (id) => {
    try {
      const bk = bookings.find(b => b.id === id);

      // Card bookings hold an authorized-but-uncaptured payment — capture it
      // FIRST and only mark the booking completed if that actually succeeds.
      // Previously this fired-and-forgot the capture after already marking the
      // booking completed, so a failure (e.g. a transient 404) was completely
      // silent: the barber saw "completed" and it counted toward earnings even
      // though the client was never actually charged.
      if (bk?.stripe_payment_intent_id) {
        const { data, error: captureError } = await supabase.functions.invoke("stripe-capture-payment", {
          body: { payment_intent_id: bk.stripe_payment_intent_id, booking_id: id, charge_type: "completion" },
        });
        if (captureError || !data?.success) {
          const msg = data?.error || captureError?.message || "Payment capture failed — try End Cut again.";
          Sentry.captureException(new Error(`completeBooking capture failed: ${msg}`), { tags: { flow: "booking", step: "complete_capture" }, extra: { booking_id: id } });
          window.dispatchEvent(new CustomEvent("faded-toast", { detail: { msg: `Couldn't charge the client — ${msg}`, type: "error" } }));
          return;
        }
      }

      const { error } = await supabase.from("bookings").update({ status:"completed" }).eq("id",id);
      if (!error) {
        setBookings(prev=>prev.map(b=>b.id===id?{...b,status:"completed"}:b));
        setExpandedBookingId(null);
        setBarberLiveAppt(null);
      } else {
        console.error("[FADED] completeBooking:", error.message);
        Sentry.captureException(error, { tags: { flow: "booking", step: "complete_status_update" }, extra: { booking_id: id } });
        window.dispatchEvent(new CustomEvent("faded-toast", { detail: { msg: "Payment captured, but marking the booking complete failed — refresh and check.", type: "error" } }));
      }
    } catch (e) {
      console.error("[FADED] completeBooking:", e);
      Sentry.captureException(e, { tags: { flow: "booking", step: "complete_booking" }, extra: { booking_id: id } });
      window.dispatchEvent(new CustomEvent("faded-toast", { detail: { msg: "Something went wrong completing this booking — try again.", type: "error" } }));
    }
  };

  const markNoShow = async (id) => {
    try {
      const bk = bookings.find(b => b.id === id);
      const { error } = await supabase.from("bookings").update({ status:"no_show" }).eq("id",id);
      if (!error) {
        setBookings(prev=>prev.map(b=>b.id===id?{...b,status:"no_show"}:b));
        setExpandedBookingId(null);
        if (bk?.saved_payment_method_id) {
          const { error: chargeErr } = await supabase.functions.invoke("stripe-charge-saved-card", {
            body: { booking_id: id, charge_type: "no_show" }
          });
          const msg = chargeErr
            ? "No-show marked. Card charge failed — contact client directly."
            : "No-show marked. Client's card has been charged.";
          window.dispatchEvent(new CustomEvent("faded-toast", { detail: { msg, type: chargeErr ? "error" : "success" } }));
        }
      } else console.error("[FADED] markNoShow:", error.message);
    } catch (e) { console.error("[FADED] markNoShow:", e); }
  };

  const loadQueue = async () => {
    const { data } = await supabase.from("walk_in_queue")
      .select("*").eq("barber_id",session.user.id).eq("status","waiting").order("added_at");
    setQueue(data||[]);
  };

  const addToQueue = async () => {
    if (!walkinName.trim()) return;
    try {
      const { error } = await supabase.from("walk_in_queue").insert({ barber_id:session.user.id, client_name:walkinName.trim(), client_phone:walkinPhone.trim(), service:walkinService, status:"waiting", added_at:new Date().toISOString() });
      if (!error) { setWalkinName(""); setWalkinPhone(""); setShowAddWalkin(false); }
      else console.error("[FADED] addToQueue:", error.message);
    } catch (e) { console.error("[FADED] addToQueue:", e); }
  };

  const callNext = async (entry) => {
    await supabase.from("walk_in_queue").update({ status:"called" }).eq("id",entry.id);
    setQueue(prev=>prev.filter(e=>e.id!==entry.id));
    if (entry.client_phone) {
      sendSMS(entry.client_phone, `✂️ FADED: You're up next — ${myBarberData?.full_name||"your barber"} is ready for you now. Head to the chair! 💈`);
    }
  };

  const removeFromQueue = async (id) => {
    await supabase.from("walk_in_queue").delete().eq("id",id);
    setQueue(prev=>prev.filter(e=>e.id!==id));
  };

  // Open native SMS to notify client when a slot opens up
  const declineAndRefer = async (id) => {
    await respondBooking(id, false);
    const bk = bookings.find(b => b.id === id);
    setReferralModal({ bookingId: id, clientName: bk?.client_name || "Client", clientPhone: bk?.client_phone || "" });
    setReferralSent(false);
  };

  const notifyClientSlot = (booking) => {
    const name = myBarberData?.full_name||"Your barber";
    const msg = `Hey ${booking.client_name||"there"}, ${name} here! A slot just opened up — want to come in earlier? Reply or book at letsgetfaded.com`;
    if (booking.phone) {
      window.open(`sms:${booking.phone}?body=${encodeURIComponent(msg)}`);
    } else {
      console.warn("no phone");
    }
  };

  const allowedImageTypes = ["image/jpeg","image/jpg","image/png","image/webp","image/heic","image/heif"];
  const uploadAvatar = async (file) => {
    if (!allowedImageTypes.includes(file.type) || file.size > 5*1024*1024) return;
    file = await toUploadableImage(file);
    const path = `${session.user.id}/${Date.now()}.${file.name.split(".").pop()}`;
    const { error } = await supabase.storage.from("avatars").upload(path, file, { upsert:true });
    if (!error) { const { data } = supabase.storage.from("avatars").getPublicUrl(path); setAvatar(data.publicUrl); await supabase.from("barbers").update({ avatar_url:data.publicUrl }).eq("id",session.user.id); }
  };

  const coverFileRef = useRef();
  const [coverPhoto, setCoverPhoto] = useState(myBarberData?.cover_photo_url||null);
  const uploadCoverPhoto = async (file) => {
    if (!allowedImageTypes.includes(file.type) || file.size > 5*1024*1024) return;
    file = await toUploadableImage(file, { maxDim: 1600 });
    const path = `${session.user.id}/cover-${Date.now()}.${file.name.split(".").pop()}`;
    const { error } = await supabase.storage.from("avatars").upload(path, file, { upsert:true });
    if (!error) { const { data } = supabase.storage.from("avatars").getPublicUrl(path); setCoverPhoto(data.publicUrl); await supabase.from("barbers").update({ cover_photo_url:data.publicUrl }).eq("id",session.user.id); onUpdateBarberData(prev=>prev?{...prev,cover_photo_url:data.publicUrl}:prev); }
  };

  // Dashboard address autocomplete state
  const [addrDashSuggs, setAddrDashSuggs]   = useState([]);
  const [addrDashDrop, setAddrDashDrop]     = useState(false);
  const [addrDashCoords, setAddrDashCoords] = useState(null);
  const addrDashTimer = useRef(null);

  const fetchDashAddrSuggs = (q) => {
    clearTimeout(addrDashTimer.current);
    if (!q || q.length < 2) { setAddrDashSuggs([]); return; }
    addrDashTimer.current = setTimeout(async () => {
      try {
        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json?access_token=${MAPBOX_TOKEN}&country=ca,us&limit=6&types=address,place,neighborhood,locality`;
        const res = await fetch(url);
        const data = await res.json();
        if (!data.features?.length) { setAddrDashSuggs([]); return; }
        setAddrDashSuggs(data.features.map(f => ({ label: f.place_name, lat: f.center[1], lng: f.center[0] })));
        if (data.features.length > 0) setAddrDashDrop(true);
      } catch { setAddrDashSuggs([]); }
    }, 300);
  };

  const saveAddress = async () => {
    setSavingAddr(true);
    let lat = addrDashCoords?.lat ?? null;
    let lng = addrDashCoords?.lng ?? null;
    if ((!lat || !lng) && addressVal) {
      try {
        const r = await fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(addressVal)}.json?access_token=${MAPBOX_TOKEN}&country=ca,us&limit=1&types=address,place`);
        const d = await r.json();
        if (d.features?.[0]) { lat = d.features[0].center[1]; lng = d.features[0].center[0]; }
      } catch {}
    }
    await supabase.from("barbers").update({ address:addressVal, ...(lat && lng ? { lat, lng } : {}) }).eq("id",session.user.id);
    setSavingAddr(false);
    setEditAddress(false);
    setAddrDashCoords(null);
  };

  const [portfolioUploading, setPortfolioUploading] = useState(false);
  const [portfolioErr, setPortfolioErr] = useState("");

  const uploadPortfolio = async (file) => {
    if (!file) return;
    setPortfolioErr("");
    // FIX 26: validate file type and size before uploading
    const allowedTypes = ["image/jpeg","image/jpg","image/png","image/webp","image/heic","image/heif"];
    if (!allowedTypes.includes(file.type)) {
      setPortfolioErr("Please upload a JPG or PNG image.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setPortfolioErr("Image too large. Please use an image under 5MB.");
      return;
    }
    setPortfolioUploading(true);
    file = await toUploadableImage(file, { maxDim: 1600 });
    const path = `${session.user.id}/${Date.now()}.${file.name.split(".").pop()}`;
    const { error } = await supabase.storage.from("portfolio").upload(path, file, { upsert:true });
    setPortfolioUploading(false);
    if (error) {
      setPortfolioErr("Upload failed. Check your connection and try again.");
      return;
    }
    const { data } = supabase.storage.from("portfolio").getPublicUrl(path);
    const urls=[...portfolio,data.publicUrl];
    setPortfolio(urls);
    await supabase.from("barbers").update({ portfolio_urls:urls }).eq("id",session.user.id);
  };

  const slug = myBarberData?.shareable_slug||"";
  const shareUrl = slug ? `https://app.letsgetfaded.com/barber/${slug}` : `${window.location.origin}?open_barber=${encodeURIComponent(myBarberData?.id||"")}`;

  const pendingBookings  = bookings.filter(b=>b.status==="pending");
  const activeBookings   = bookings.filter(b=>["accepted","confirmed"].includes(b.status));
  const completedToday   = bookings.filter(b=>b.status==="completed"&&new Date(b.created_at).toDateString()===new Date().toDateString());

  // Income calculations
  const parseAmt = v => parseFloat(String(v||0).replace(/[^0-9.]/g,""))||0;
  const todayStr  = new Date().toISOString().split("T")[0];
  const weekAgo   = new Date(Date.now()-7*864e5).toISOString().split("T")[0];
  const monthAgo  = new Date(Date.now()-30*864e5).toISOString().split("T")[0];
  const completed = bookings.filter(b=>b.status==="completed");
  const totalWithTip = (b) => parseAmt(b.price_agreed) + parseAmt(b.tip_amount);
  const bDate = b => (b.completed_at || b.created_at)?.slice(0,10);
  const todayEarnings  = completed.filter(b=>bDate(b)===todayStr).reduce((s,b)=>s+totalWithTip(b),0);
  const weekEarnings   = completed.filter(b=>bDate(b)>=weekAgo).reduce((s,b)=>s+totalWithTip(b),0);
  const monthEarnings  = completed.filter(b=>bDate(b)>=monthAgo).reduce((s,b)=>s+totalWithTip(b),0);
  const recentEarnings = completed.slice(0,20);

  // Weekly earnings chart — last 7 days
  const weeklyChartData = Array.from({length:7}, (_,i) => {
    const d = new Date(Date.now() - (6-i)*864e5);
    const dateStr = d.toISOString().split("T")[0];
    const dayLabel = d.toLocaleDateString([],{weekday:"short"});
    const total = completed.filter(b=>bDate(b)===dateStr).reduce((s,b)=>s+totalWithTip(b),0);
    return { dateStr, dayLabel, total, isToday: dateStr === todayStr };
  });
  const chartMax = Math.max(...weeklyChartData.map(d=>d.total), 1);

  // Forecast — upcoming confirmed bookings within the next 7 and 30 days
  const nowIso = new Date().toISOString();
  const week7  = new Date(Date.now() + 7*864e5).toISOString();
  const week30 = new Date(Date.now() + 30*864e5).toISOString();
  const upcomingBooked = bookings.filter(b => ['confirmed','accepted'].includes(b.status) && b.scheduled_at && b.scheduled_at >= nowIso);
  const forecastWeek  = upcomingBooked.filter(b => b.scheduled_at <= week7);
  const forecastMonth = upcomingBooked.filter(b => b.scheduled_at <= week30);
  const forecastWeekTotal  = forecastWeek.reduce((s,b) => s + parseAmt(b.price_agreed), 0);
  const forecastMonthTotal = forecastMonth.reduce((s,b) => s + parseAmt(b.price_agreed), 0);

  // No-show tracking
  const noShows = bookings.filter(b=>b.status==="no_show");
  const noShowRate = bookings.length>0?Math.round(noShows.length/bookings.length*100):0;
  const strikeMap = noShows.reduce((acc,b)=>{ const k=b.client_phone||b.client_name||"?"; acc[k]=(acc[k]||0)+1; return acc; },{});
  const getStrikes = (b) => strikeMap[b.client_phone||b.client_name||""]||0;

  // Busy-hours heatmap
  const heatDays = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
  const heatHours = [8,9,10,11,12,13,14,15,16,17,18,19,20];
  const heatCutoff = new Date(Date.now()-heatmapRange*864e5);
  const heatBookings = completed.filter(b=>b.scheduled_at&&new Date(b.scheduled_at)>=heatCutoff);
  const heatGrid = {};
  heatDays.forEach((_,di)=>{ heatGrid[di]={}; heatHours.forEach(h=>{ heatGrid[di][h]=0; }); });
  heatBookings.forEach(b=>{ const d=new Date(b.scheduled_at); const di=d.getDay(); const h=d.getHours(); if(heatGrid[di]&&heatGrid[di][h]!==undefined)heatGrid[di][h]++; });
  const heatMax = Math.max(1,...heatDays.map((_,di)=>Math.max(...heatHours.map(h=>heatGrid[di][h]))));
  let peakDayIdx=0,peakHour=9,peakCount=0,slowDayIdx=1,slowHour=8;
  heatDays.forEach((_,di)=>heatHours.forEach(h=>{ const c=heatGrid[di][h]; if(c>peakCount){peakCount=c;peakDayIdx=di;peakHour=h;} }));
  const fmtHr = h=>h>12?`${h-12}pm`:h===12?"12pm":`${h}am`;


  // ── Appointments-only view (APTS bottom-nav tab) ──────────────────────────
  if (appointmentsMode) {
    return (
      <div style={{ paddingBottom:"100px" }}>
      {barberLiveAppt && (
        <BarberLiveScreen
          appt={barberLiveAppt}
          onEndCut={() => completeBooking(barberLiveAppt.id)}
          onNoShow={() => { markNoShow(barberLiveAppt.id); setBarberLiveAppt(null); }}
          onClose={() => { const _did = barberLiveAppt?.id; dismissedBarberLiveRef.current = _did; try { if (_did) sessionStorage.setItem("faded_live_dismissed", _did); } catch {} setBarberLiveAppt(null); }}
        />
      )}
        {/* Header */}
        <div style={{ background:t.headerBg, backdropFilter:"blur(16px)", WebkitBackdropFilter:"blur(16px)", borderBottom:`1px solid ${t.border}`, paddingLeft:16, paddingRight:16, paddingBottom:10, paddingTop:"max(44px, calc(var(--sat, 0px) + 2px))", position:"sticky", top:0, zIndex:100, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <div>
            <div className="heading-gradient" style={{ fontSize:26, fontFamily:"'Bebas Neue',sans-serif", letterSpacing:"0.06em", lineHeight:1 }}>SCHEDULE</div>
            <div style={{ fontSize:11, color:t.t4, marginTop:2, fontFamily:"'Syne',sans-serif", fontWeight:700, letterSpacing:"0.04em" }}>{(session?.user?.user_metadata?.full_name||myBarberData?.handle||"").toUpperCase()} · FAD<span style={{color:"#2563EB"}}>ED</span></div>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            {pendingBookings.length > 0 && <div style={{ width:8, height:8, borderRadius:"50%", background:"#F59E0B", boxShadow:"0 0 8px #F59E0B" }}/>}
            <button
              onClick={() => {
                setNotifOpen(true);
                const updated = notifInbox.map(n => ({ ...n, read: true }));
                setNotifInbox(updated);
                try { localStorage.setItem('faded_notif_inbox', JSON.stringify(updated)); } catch {}
                if (isPushReady()) {
                  import('@capacitor/push-notifications').then(({ PushNotifications }) => { PushNotifications.removeAllDeliveredNotifications?.(); }).catch(() => {});
                }
              }}
              style={{ background:"none", border:"none", cursor:"pointer", padding:4, position:"relative", display:"flex", alignItems:"center" }}
              aria-label="Notification inbox"
            >
              {notifInbox.filter(n=>!n.read).length > 0 && (
                <div style={{ position:"absolute", top:-4, right:-4, minWidth:16, height:16, borderRadius:8, background:"#EF4444", display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, fontWeight:800, color:"#fff", padding:"0 3px", lineHeight:1, fontFamily:"'DM Sans',sans-serif", pointerEvents:"none" }}>
                  {notifInbox.filter(n=>!n.read).length}
                </div>
              )}
              <svg aria-hidden="true" focusable="false" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={notifInbox.filter(n=>!n.read).length > 0 ? "#EF4444" : t.t3} strokeWidth="1.8" strokeLinecap="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
            </button>
          </div>
        </div>

        {/* Notification inbox bottom sheet */}
        {notifOpen && (
          <div className="faded-sheet-wrap" style={{ position:"fixed", inset:0, zIndex:500, background:"rgba(0,0,0,0.75)", backdropFilter:"blur(8px)", display:"flex", alignItems:"flex-end", justifyContent:"center" }} onClick={(e)=>{ if(e.target===e.currentTarget) setNotifOpen(false); }}>
            <div style={{ width:"100%", maxWidth:480, background:"#111", borderRadius:"20px 20px 0 0", maxHeight:"75vh", display:"flex", flexDirection:"column", overflow:"hidden" }}>
              {/* Sheet handle */}
              <div style={{ display:"flex", justifyContent:"center", paddingTop:10, paddingBottom:4, flexShrink:0 }}>
                <div style={{ width:36, height:4, borderRadius:2, background:"rgba(255,255,255,0.15)" }}/>
              </div>
              {/* Sheet header */}
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 18px 12px", borderBottom:"1px solid rgba(255,255,255,0.07)", flexShrink:0 }}>
                <div style={{ fontSize:15, fontWeight:800, color:"#fff", fontFamily:"'DM Sans',sans-serif", letterSpacing:"0.01em" }}>Notifications</div>
                <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                  {notifInbox.length > 0 && (
                    <button onClick={()=>{ setNotifInbox([]); try { localStorage.removeItem('faded_notif_inbox'); } catch {} }} style={{ background:"none", border:"none", cursor:"pointer", fontSize:11, color:"rgba(255,255,255,0.35)", fontFamily:"'DM Sans',sans-serif", padding:"2px 6px" }}>Clear all</button>
                  )}
                  <button onClick={()=>setNotifOpen(false)} style={{ background:"none", border:"none", cursor:"pointer", color:"rgba(255,255,255,0.5)", display:"flex", alignItems:"center", padding:4 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                </div>
              </div>
              {/* Notification list */}
              <div style={{ overflowY:"auto", flex:1 }}>
                {notifInbox.length === 0 ? (
                  <div style={{ padding:"40px 20px", textAlign:"center", color:"rgba(255,255,255,0.3)", fontSize:13, fontFamily:"'DM Sans',sans-serif" }}>No notifications yet</div>
                ) : notifInbox.map((n, i) => {
                  const ago = (() => { const s = Math.round((Date.now() - n.ts) / 1000); if (s < 60) return "just now"; if (s < 3600) return `${Math.floor(s/60)}m ago`; if (s < 86400) return `${Math.floor(s/3600)}h ago`; return `${Math.floor(s/86400)}d ago`; })();
                  const dest = n.data?.tab === "schedule" ? "appointments" : (n.data?.tab || null);
                  return (
                    <div
                      key={n.id || i}
                      onClick={() => {
                        if (dest) window.dispatchEvent(new CustomEvent('faded:push-tap', { detail: n.data }));
                        setNotifOpen(false);
                      }}
                      style={{ padding:"14px 18px", borderBottom:"1px solid rgba(255,255,255,0.05)", cursor: dest ? "pointer" : "default", display:"flex", gap:12, alignItems:"flex-start" }}
                    >
                      <div style={{ width:36, height:36, borderRadius:10, background:"rgba(37,99,235,0.15)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, fontSize:16 }}>
                        {n.data?.type === "instant_request" ? "⚡" : "✂️"}
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:13, fontWeight:700, color:"#fff", fontFamily:"'DM Sans',sans-serif", lineHeight:1.3 }}>{n.title}</div>
                        {n.body && <div style={{ fontSize:12, color:"rgba(255,255,255,0.5)", marginTop:3, lineHeight:1.4 }}>{n.body}</div>}
                        <div style={{ fontSize:11, color:"rgba(255,255,255,0.25)", marginTop:5, fontFamily:"'DM Sans',sans-serif" }}>{ago}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* NEW REQUESTS */}
        <div style={{ padding:"16px 16px 0" }}>
          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
            {pendingBookings.length > 0 && <div style={{ width:6, height:6, borderRadius:"50%", background:"#F59E0B" }}/>}
            <span style={{ fontSize:11, fontWeight:800, color:"#F59E0B", letterSpacing:"0.14em", fontFamily:"'DM Sans',sans-serif" }}>NEW REQUESTS · {pendingBookings.length}</span>
          </div>
          {pendingBookings.length === 0 ? (
            <div style={{ background:t.card, borderRadius:14, padding:"14px 16px", border:`1px solid ${t.border}`, marginBottom:12 }}>
              <div style={{ fontSize:13, color:t.t4, textAlign:"center" }}>All caught up — no pending requests</div>
            </div>
          ) : pendingBookings.map(b=>(
            <div key={b.id} style={{ background:t.card, borderRadius:16, marginBottom:12, border:"1.5px solid rgba(245,158,11,0.3)", overflow:"hidden" }}>
              {getStrikes(b)>0&&(
                <div style={{ padding:"7px 14px", background:getStrikes(b)>=3?"rgba(220,38,38,0.1)":"rgba(245,158,11,0.08)", borderBottom:`1px solid ${getStrikes(b)>=3?"rgba(220,38,38,0.2)":"rgba(245,158,11,0.15)"}` }}>
                  <span style={{ fontSize:11, fontWeight:700, color:getStrikes(b)>=3?"#EF4444":"#F59E0B" }}>{getStrikes(b)>=3?"⛔ Blocked — 3+ no-shows":getStrikes(b)>=2?"⚠️ 2 no-shows":"⚠️ 1 previous no-show"}</span>
                </div>
              )}
              <div style={{ padding:"14px 16px" }}>
                {/* Client row */}
                <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:14 }}>
                  <div style={{ width:40, height:40, borderRadius:12, background:"rgba(37,99,235,0.15)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, fontWeight:800, color:"#2563EB", flexShrink:0 }}>{(b.client_name||"C")[0].toUpperCase()}</div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:14, fontWeight:800, color:t.t1, fontFamily:"'DM Sans',sans-serif" }}>{b.client_name||"Client"}</div>
                    <div style={{ fontSize:11, color:t.t4, marginTop:1 }}>{b.client_phone ? `New client · ${b.client_phone}` : "New client"}</div>
                  </div>
                  <div style={{ background:"rgba(245,158,11,0.12)", border:"1px solid rgba(245,158,11,0.3)", borderRadius:8, padding:"3px 9px", fontSize:11, fontWeight:800, color:"#F59E0B", letterSpacing:"0.08em" }}>PENDING</div>
                </div>
                {/* SERVICE / WHEN / PRICE grid */}
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, padding:"10px 12px", background:t.section, borderRadius:12, marginBottom:12 }}>
                  <div>
                    <div style={{ fontSize:11, fontWeight:700, color:t.t4, letterSpacing:"0.12em", marginBottom:3 }}>SERVICE</div>
                    <div style={{ fontSize:13, fontWeight:800, color:t.t1 }}>{b.service_type||"Haircut"}</div>
                  </div>
                  <div>
                    <div style={{ fontSize:11, fontWeight:700, color:t.t4, letterSpacing:"0.12em", marginBottom:3 }}>WHEN</div>
                    <div style={{ fontSize:12, fontWeight:700, color:t.t1 }}>{b.scheduled_at ? new Date(b.scheduled_at).toLocaleDateString([],{weekday:"short",month:"short",day:"numeric"})+" · "+new Date(b.scheduled_at).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}) : "TBD"}</div>
                  </div>
                  <div>
                    <div style={{ fontSize:11, fontWeight:700, color:t.t4, letterSpacing:"0.12em", marginBottom:3 }}>PRICE</div>
                    <div style={{ fontSize:13, fontWeight:800, color:"#22C55E" }}>${b.price_agreed||"—"}</div>
                  </div>
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                  <button onClick={()=>declineAndRefer(b.id)} style={{ padding:"12px", background:"transparent", border:`1px solid ${t.border}`, borderRadius:12, color:t.t3, fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>Decline</button>
                  <button onClick={()=>respondBooking(b.id,true)} style={{ padding:"12px", background:"#22C55E", border:"none", borderRadius:12, color:"#fff", fontSize:13, fontWeight:800, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>Accept Booking</button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CONFIRMED */}
        {activeBookings.length > 0 && (
          <div style={{ padding:"4px 16px 0" }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
              <div style={{ width:6, height:6, borderRadius:"50%", background:"#22C55E" }}/>
              <span style={{ fontSize:11, fontWeight:800, color:"#22C55E", letterSpacing:"0.14em", fontFamily:"'DM Sans',sans-serif" }}>CONFIRMED · TODAY</span>
            </div>
            {activeBookings.map((b,i)=>(
              <div key={b.id} style={{ background:t.card, borderRadius:14, marginBottom:8, border:`1px solid rgba(34,197,94,0.2)`, overflow:"hidden" }}>
                <button onClick={()=>setExpandedBookingId(expandedBookingId===b.id?null:b.id)} style={{ width:"100%", background:"none", border:"none", cursor:"pointer", padding:"12px 14px", display:"flex", alignItems:"center", gap:12 }}>
                  {/* Time block */}
                  <div style={{ flexShrink:0, textAlign:"center", minWidth:40 }}>
                    <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:22, color:t.t1, lineHeight:1 }}>{b.scheduled_at ? new Date(b.scheduled_at).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}).split(" ")[0] : "--"}</div>
                    <div style={{ fontSize:11, color:t.t4, fontWeight:700 }}>{b.scheduled_at ? new Date(b.scheduled_at).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}).split(" ")[1]||"" : ""}</div>
                  </div>
                  {/* Avatar */}
                  <div style={{ width:36, height:36, borderRadius:10, background:"rgba(34,197,94,0.12)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, fontWeight:800, color:"#22C55E", flexShrink:0 }}>{(b.client_name||"C")[0].toUpperCase()}</div>
                  {/* Info */}
                  <div style={{ flex:1, textAlign:"left" }}>
                    <div style={{ fontSize:13, fontWeight:800, color:t.t1 }}>{b.client_name||"Client"}</div>
                    <div style={{ fontSize:11, color:t.t4, marginTop:1 }}>{b.service_type}{b.price_agreed?` · $${b.price_agreed}`:""}</div>
                  </div>
                  <div style={{ background:"rgba(34,197,94,0.12)", border:"1px solid rgba(34,197,94,0.3)", borderRadius:8, padding:"3px 9px", fontSize:11, fontWeight:800, color:"#22C55E", letterSpacing:"0.06em", flexShrink:0 }}>✓ CONFIRMED</div>
                </button>
                {expandedBookingId===b.id && (
                  <div style={{ padding:"0 14px 14px", display:"flex", flexDirection:"column", gap:8, borderTop:`1px solid ${t.divider}`, paddingTop:10 }}>
                    {b.client_phone && <div style={{ fontSize:12, color:t.t3 }}>📱 {b.client_phone}</div>}
                    {b.payment_method && <div style={{ fontSize:12, color:t.t3 }}>💳 {b.payment_method}</div>}
                    <div>
                      <div style={{ fontSize:11, fontWeight:700, color:t.t4, marginBottom:4 }}>👤 Client Notes <span style={{ fontWeight:400 }}>(saved across all visits)</span></div>
                      <textarea
                        key={`cn-${b.id}`}
                        defaultValue={clientNotes[b.client_id || b.client_phone] || ""}
                        placeholder="Style preferences, allergies, regular's go-to cut…"
                        rows={2}
                        onBlur={async e => saveClientNote(b, e.target.value)}
                        style={{ width:"100%", padding:"8px 10px", borderRadius:10, border:`1.5px solid ${t.border2}`, background:t.section, color:t.inputText, fontSize:12, resize:"none", outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box" }}
                      />
                    </div>
                    <button onClick={()=>setBarberLiveAppt(b)} style={{ width:"100%", padding:"11px", background:"#22C55E", border:"none", borderRadius:10, color:"#fff", fontSize:13, fontWeight:800, cursor:"pointer" }}>▶ Start Cut</button>
                    <button onClick={()=>completeBooking(b.id)} style={{ width:"100%", padding:"9px", background:"rgba(34,197,94,0.08)", border:"1px solid rgba(34,197,94,0.25)", borderRadius:10, color:"#22C55E", fontSize:12, fontWeight:700, cursor:"pointer" }}>✂️ End Cut (skip timer)</button>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                      <button onClick={()=>markNoShow(b.id)} style={{ padding:"9px", background:"rgba(245,158,11,0.08)", border:"1px solid rgba(245,158,11,0.25)", borderRadius:10, color:"#F59E0B", fontSize:12, fontWeight:700, cursor:"pointer" }}>👻 No Show</button>
                      <button onClick={()=>cancelBooking(b.id)} style={{ padding:"9px", background:"rgba(220,38,38,0.08)", border:"1px solid rgba(220,38,38,0.2)", borderRadius:10, color:"#EF4444", fontSize:12, fontWeight:700, cursor:"pointer" }}>✗ Cancel</button>
                    </div>
                    {b.client_phone && <button onClick={()=>notifyClientSlot(b)} style={{ width:"100%", padding:"9px", background:t.blueBg, border:`1px solid ${t.blueBorder}`, borderRadius:10, color:t.blueText, fontSize:11, fontWeight:700, cursor:"pointer" }}>📲 Notify — slot opened</button>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Empty state */}
        {pendingBookings.length === 0 && activeBookings.length === 0 && (
          <div style={{ padding:"52px 24px", textAlign:"center" }}>
            <div style={{ fontSize:48, marginBottom:14, lineHeight:1 }}>📅</div>
            <div style={{ fontSize:16, fontWeight:800, color:t.t1, marginBottom:8, fontFamily:"'DM Sans',sans-serif" }}>All clear today</div>
            <div style={{ fontSize:13, color:t.t3, lineHeight:1.6 }}>New booking requests appear here instantly.</div>
          </div>
        )}

        <WaitlistViewer barberId={session.user.id} t={t} onNotify={notifyClientSlot}/>
        <BarberSchedule session={session} myBarberData={myBarberData}/>
      </div>
    );
  }

  return (
    <div>
      <style>{`@keyframes razorPulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:0.5;transform:scale(1.4)}} @keyframes razorSlide{from{opacity:0;transform:translateY(-12px)}to{opacity:1;transform:translateY(0)}}`}</style>
      {razorToast && (
        <div style={{ position:"fixed", top:"max(20px, calc(var(--sat, 0px) + 12px))", left:"50%", transform:"translateX(-50%)", zIndex:9999, background:"linear-gradient(135deg,#1e3a5f,#2563EB)", border:"1.5px solid rgba(147,197,253,0.4)", borderRadius:20, padding:"10px 20px", display:"flex", alignItems:"center", gap:10, boxShadow:"0 8px 32px rgba(37,99,235,0.4)", animation:"razorSlide 0.25s ease-out", whiteSpace:"nowrap" }}>
          <span style={{ width:8, height:8, borderRadius:"50%", background:"#93C5FD", display:"inline-block", animation:"razorPulse 1s infinite" }}/>
          <span style={{ fontSize:13, fontWeight:800, color:"#fff", fontFamily:"'DM Sans',sans-serif", letterSpacing:1 }}>RAZOR ACTIVATED</span>
        </div>
      )}
      {/* ── Barber live cut screen ── */}
      {barberLiveAppt && (
        <BarberLiveScreen
          appt={barberLiveAppt}
          onEndCut={() => completeBooking(barberLiveAppt.id)}
          onNoShow={() => { markNoShow(barberLiveAppt.id); setBarberLiveAppt(null); }}
          onClose={() => { const _did = barberLiveAppt?.id; dismissedBarberLiveRef.current = _did; try { if (_did) sessionStorage.setItem("faded_live_dismissed", _did); } catch {} setBarberLiveAppt(null); }}
        />
      )}
      {/* ── Compact sticky header — hidden when AI is open (full screen) ── */}
      {!hideHeader && dashTab !== "ai" && <div className="sticky top-0 z-40" style={{ background:t.headerBg, backdropFilter:"blur(20px)", WebkitBackdropFilter:"blur(20px)", borderBottom:`1px solid ${t.border}` }}>
        <input ref={fileRef} type="file" accept="image/*" style={{ display:"none" }} onChange={e=>uploadAvatar(e.target.files[0])}/>
        <input ref={coverFileRef} type="file" accept="image/*" style={{ display:"none" }} onChange={e=>uploadCoverPhoto(e.target.files[0])}/>
        {/* Single compact row: avatar + page title + settings */}
        <div style={{ paddingTop:"max(10px, calc(var(--sat, 0px) + 10px))", paddingBottom:"10px", paddingLeft:"16px", paddingRight:"16px", display:"flex", alignItems:"center", gap:10 }}>
          <div onClick={()=>fileRef.current?.click()} style={{ width:32, height:32, borderRadius:"50%", overflow:"hidden", flexShrink:0, cursor:"pointer", background:"linear-gradient(135deg,#1e3a5f,#2563EB)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:900, color:"#fff", fontFamily:"'DM Sans',sans-serif", border:"1.5px solid rgba(37,99,235,0.5)" }}>
            {avatar ? <img src={avatar} alt="avatar" style={{ width:"100%", height:"100%", objectFit:"cover" }}/> : (session?.user?.user_metadata?.full_name||myBarberData?.handle||"B")[0].toUpperCase()}
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <div className="heading-gradient" style={{ fontSize:26, fontFamily:"'Bebas Neue',sans-serif", letterSpacing:"0.06em", lineHeight:1 }}>
              {dashTab === "status" ? "DASHBOARD" : dashTab === "availability" ? "HOURS" : dashTab === "services" ? "SERVICES" : dashTab === "reviews" ? "REVIEWS" : dashTab === "settings" ? "ACCOUNT" : dashTab === "earnings" ? "EARNINGS" : "DASHBOARD"}
            </div>
            <div style={{ fontSize:11, color:t.t4, marginTop:2, fontFamily:"'Syne',sans-serif", fontWeight:700, letterSpacing:"0.04em" }}>{(session?.user?.user_metadata?.full_name||myBarberData?.handle||"").toUpperCase()} · FAD<span style={{color:"#2563EB"}}>ED</span></div>
          </div>
          {/* Settings button — gear icon */}
          <button onClick={()=>setDashTab(dashTab==="settings" ? "status" : "settings")} aria-label="Settings" style={{ minWidth:36, minHeight:36, width:36, height:36, borderRadius:10, background:dashTab==="settings"?"rgba(37,99,235,0.12)":t.section, border:`1px solid ${dashTab==="settings"?"#2563EB":t.border}`, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", outline:"none", flexShrink:0 }}>
            <svg aria-hidden="true" focusable="false" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={dashTab==="settings"?"#2563EB":t.t3} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3"/>
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
            </svg>
          </button>
        </div>
        {/* Segment pills */}
        {dashTab !== "settings" && (
          <div style={{ display:"flex", gap:5, padding:"0 16px 10px" }}>
            {[{id:"status",label:"Overview"},{id:"availability",label:"Hours"},{id:"services",label:"Services"},{id:"reviews",label:"Reviews"}].map(seg=>(
              <button key={seg.id} onClick={()=>setDashTab(seg.id)}
                style={{ flex:1, padding:"7px 2px", borderRadius:10, border:`1.5px solid ${dashTab===seg.id?"#2563EB":t.border}`, background:dashTab===seg.id?"rgba(37,99,235,0.12)":t.section, color:dashTab===seg.id?"#2563EB":t.t3, fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", outline:"none", transition:"all 0.15s" }}>
                {seg.label}
              </button>
            ))}
          </div>
        )}
      </div>}

      {false && (
        <div>
          {/* dashTab=schedule removed — schedule is now appointmentsMode */}
          {pendingBookings.length > 0 && (
            <div className="mx-4 mt-4 rounded-2xl" style={{ background:t.card, padding:24, border:"1.5px solid rgba(245,158,11,0.3)" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
                <span style={{ fontSize:13, fontWeight:800, color:t.t1, fontFamily:"'DM Sans',sans-serif" }}>New Requests</span>
                <span style={{ fontSize:12, fontWeight:700, color:"#F59E0B", background:"rgba(245,158,11,0.12)", padding:"3px 10px", borderRadius:20 }}>{pendingBookings.length} pending</span>
              </div>
              {pendingBookings.map(b=>(
                <div key={b.id} style={{ background:"rgba(245,158,11,0.06)", borderRadius:14, padding:14, marginBottom:10, border:"1px solid rgba(245,158,11,0.2)" }}>
                  {getStrikes(b)>0&&(
                    <div style={{ marginBottom:8, padding:"6px 10px", background:getStrikes(b)>=3?"rgba(220,38,38,0.1)":"rgba(245,158,11,0.08)", border:`1px solid ${getStrikes(b)>=3?"rgba(220,38,38,0.3)":"rgba(245,158,11,0.25)"}`, borderRadius:8 }}>
                      <span style={{ fontSize:11, fontWeight:700, color:getStrikes(b)>=3?"#EF4444":"#F59E0B" }}>
                        {getStrikes(b)>=3?"⛔ Blocked — 3+ no-shows":getStrikes(b)>=2?"⚠️ 2 no-shows — require deposit":"⚠️ 1 previous no-show"}
                      </span>
                    </div>
                  )}
                  <div style={{ display:"flex", justifyContent:"space-between", marginBottom:10 }}>
                    <div>
                      <div style={{ fontSize:14, fontWeight:700, color:t.t1 }}>{b.client_name||"Client"}</div>
                      <div style={{ fontSize:12, color:t.t3, marginTop:2 }}>{b.service_type} · {b.payment_method}</div>
                      <div style={{ fontSize:11, color:t.t4, marginTop:2 }}>{b.scheduled_at?new Date(b.scheduled_at).toLocaleString():""}</div>
                    </div>
                    <div style={{ fontSize:14, fontWeight:800, color:"#059669" }}>${b.price_agreed}</div>
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                    <button onClick={()=>respondBooking(b.id,true)} style={{ padding:"10px", background:"#059669", border:"none", borderRadius:10, color:"#fff", fontSize:13, fontWeight:700, cursor:"pointer" }}>Accept ✓</button>
                    <button onClick={()=>respondBooking(b.id,false)} style={{ padding:"10px", background:t.card, border:`1px solid ${t.border}`, borderRadius:10, color:"#DC2626", fontSize:13, fontWeight:700, cursor:"pointer" }}>Decline</button>
                  </div>
                  {b.client_phone && (
                    <a href={`sms:${b.client_phone}`} style={{ display:"block", marginTop:8, padding:"8px", background:t.section, border:`1px solid ${t.border}`, borderRadius:10, color:t.blueText, fontSize:11, fontWeight:700, textAlign:"center", textDecoration:"none" }}>📱 Text Client</a>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Confirmed appointments — tap to expand cancel/complete */}
          {activeBookings.length > 0 && (
            <div className="mx-4 mt-3 rounded-2xl" style={{ background:t.card, padding:24, border:`1.5px solid ${t.border}` }}>
              <div style={{ fontSize:13, fontWeight:800, color:t.t1, fontFamily:"'DM Sans',sans-serif", marginBottom:14 }}>Confirmed Appointments</div>
              {activeBookings.map((b,i,arr)=>(
                <div key={b.id} style={{ borderBottom:i<arr.length-1?`1px solid ${t.divider}`:"none" }}>
                  <button onClick={()=>setExpandedBookingId(expandedBookingId===b.id?null:b.id)}
                    style={{ width:"100%", background:"none", border:"none", cursor:"pointer", padding:"12px 0", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:10, textAlign:"left" }}>
                      <div style={{ width:40, height:40, borderRadius:12, background:t.blueBg, display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, fontWeight:800, color:t.blueText, fontFamily:"'DM Sans',sans-serif", flexShrink:0 }}>
                        {(b.client_name||"C")[0].toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontSize:13, fontWeight:700, color:t.t1 }}>{b.client_name||"Client"}</div>
                        <div style={{ fontSize:11, color:t.t3, marginTop:2 }}>{b.service_type} · {b.scheduled_at?new Date(b.scheduled_at).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}):""}</div>
                      </div>
                    </div>
                    <div style={{ display:"flex", alignItems:"center", gap:8, flexShrink:0 }}>
                      <span style={{ fontSize:11, fontWeight:700, color:"#22C55E", background:"rgba(34,197,94,0.12)", padding:"3px 8px", borderRadius:8 }}>CONFIRMED</span>
                      <span style={{ fontSize:12, color:t.t4 }}>{expandedBookingId===b.id?"▲":"▼"}</span>
                    </div>
                  </button>
                  {expandedBookingId===b.id && (
                    <div style={{ paddingBottom:14, display:"flex", flexDirection:"column", gap:8 }}>
                      {b.client_phone && <div style={{ fontSize:12, color:t.t3 }}>📱 {b.client_phone}</div>}
                      {b.payment_method && <div style={{ fontSize:12, color:t.t3 }}>💳 {b.payment_method}</div>}
                      {/* Persistent client notes */}
                      <div>
                        <div style={{ fontSize:11, fontWeight:700, color:t.t4, marginBottom:4 }}>👤 Client Notes <span style={{ fontWeight:400 }}>(saved across all visits)</span></div>
                        <textarea
                          key={`cn-${b.id}`}
                          defaultValue={clientNotes[b.client_id || b.client_phone] || ""}
                          placeholder="Style preferences, allergies, regular's go-to cut…"
                          rows={2}
                          onBlur={async e => saveClientNote(b, e.target.value)}
                          style={{ width:"100%", padding:"8px 10px", borderRadius:10, border:`1.5px solid ${t.border2}`, background:t.section, color:t.inputText, fontSize:12, resize:"none", outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box" }}
                        />
                      </div>
                      {/* Per-appointment notes */}
                      <div>
                        <div style={{ fontSize:11, fontWeight:700, color:t.t4, marginBottom:4 }}>📝 Appointment Notes <span style={{ fontWeight:400 }}>(this visit only)</span></div>
                        <textarea
                          defaultValue={b.barber_notes||""}
                          placeholder="Anything specific for today's cut…"
                          rows={2}
                          onBlur={async e => {
                            const notes = e.target.value;
                            await supabase.from("bookings").update({ barber_notes: notes }).eq("id", b.id);
                            setBookings(prev => prev.map(bk => bk.id===b.id ? {...bk, barber_notes:notes} : bk));
                          }}
                          style={{ width:"100%", padding:"8px 10px", borderRadius:10, border:`1.5px solid ${t.border2}`, background:t.section, color:t.inputText, fontSize:12, resize:"none", outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box" }}
                        />
                      </div>
                      <button onClick={()=>setBarberLiveAppt(b)} style={{ width:"100%", padding:"11px", background:"#22C55E", border:"none", borderRadius:10, color:"#fff", fontSize:13, fontWeight:800, cursor:"pointer", marginTop:4 }}>▶ Start Cut</button>
                      <button onClick={()=>completeBooking(b.id)} style={{ width:"100%", padding:"9px", background:"rgba(34,197,94,0.08)", border:"1px solid rgba(34,197,94,0.25)", borderRadius:10, color:"#22C55E", fontSize:12, fontWeight:700, cursor:"pointer" }}>✂️ End Cut (skip timer)</button>
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                        <button onClick={()=>markNoShow(b.id)} style={{ padding:"9px", background:"rgba(245,158,11,0.08)", border:"1px solid rgba(245,158,11,0.25)", borderRadius:10, color:"#F59E0B", fontSize:12, fontWeight:700, cursor:"pointer" }}>👻 No Show</button>
                        <button onClick={()=>cancelBooking(b.id)} style={{ padding:"9px", background:"rgba(220,38,38,0.08)", border:"1px solid rgba(220,38,38,0.2)", borderRadius:10, color:"#EF4444", fontSize:12, fontWeight:700, cursor:"pointer" }}>✗ Cancel</button>
                      </div>
                      {b.client_phone && (
                        <button onClick={()=>notifyClientSlot(b)} style={{ width:"100%", padding:"8px", background:t.blueBg, border:`1px solid ${t.blueBorder}`, borderRadius:10, color:t.blueText, fontSize:11, fontWeight:700, cursor:"pointer" }}>📲 Notify — slot opened up</button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Waitlist viewer */}
          <WaitlistViewer barberId={session.user.id} t={t} onNotify={notifyClientSlot}/>

          <BarberSchedule session={session} myBarberData={myBarberData}/>
        </div>
      )}
      <div style={{ display: dashTab==="services" ? "block" : "none" }}><BarberServicesTab session={session}/></div>
      {dashTab==="availability" && <BarberAvailabilityTab session={session} myBarberData={myBarberData} onUpdateBarberData={onUpdateBarberData}/>}
      {dashTab==="shop"     && <BarberShopSection session={session} myBarberData={myBarberData} onShopCreated={onShopCreated}/>}
      {dashTab==="ai"       && <AIScheduleAssistant session={session} myBarberData={myBarberData} initialQuery={razorInitialQuery} onOpen={()=>setRazorInitialQuery("")} onClose={()=>setDashTab("status")} todayEarnings={todayEarnings} weekEarnings={weekEarnings} monthEarnings={monthEarnings} queueCount={queue.length} noShowCount={noShows.length}/>}
      {dashTab==="reviews"  && <BarberReviewsTab session={session} myBarberData={myBarberData}/>}
      {dashTab==="settings" && <BarberSettingsTab session={session} profile={null} setProfile={null} onGiftCard={()=>{}} onSignOut={onSignOut||(() => {})}/>}

      {dashTab==="income" && (
        <div style={{ padding:"20px 16px 100px" }}>

          {/* ── Forecast widget ── */}
          <div className="rounded-2xl mb-4" style={{ background:t.card, padding:20, border:`1.5px solid rgba(37,99,235,0.3)` }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:14 }}>
              <div>
                <div style={{ fontSize:13, fontWeight:800, color:t.t1, fontFamily:"'DM Sans',sans-serif" }}>📅 Upcoming Revenue</div>
                <div style={{ fontSize:11, color:t.t4, marginTop:2 }}>Confirmed bookings not yet completed</div>
              </div>
              <div style={{ fontSize:11, fontWeight:700, color:"#60A5FA", background:"rgba(37,99,235,0.1)", padding:"3px 10px", borderRadius:20 }}>
                {upcomingBooked.length} booked
              </div>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom: forecastWeek.length > 0 ? 14 : 0 }}>
              {[
                { label:"This Week", value:`$${forecastWeekTotal.toFixed(0)}`, sub:`${forecastWeek.length} cut${forecastWeek.length!==1?"s":""}`, color:"#60A5FA", bg:"rgba(37,99,235,0.08)" },
                { label:"Next 30 Days", value:`$${forecastMonthTotal.toFixed(0)}`, sub:`${forecastMonth.length} cut${forecastMonth.length!==1?"s":""}`, color:"#A78BFA", bg:"rgba(167,139,250,0.08)" },
              ].map(c=>(
                <div key={c.label} style={{ background:c.bg, borderRadius:14, padding:"14px 12px" }}>
                  <div style={{ fontSize:11, fontWeight:700, color:t.t4, marginBottom:4 }}>{c.label}</div>
                  <div style={{ fontSize:22, fontWeight:900, color:c.color, fontFamily:"'DM Sans',sans-serif", lineHeight:1 }}>{c.value}</div>
                  <div style={{ fontSize:11, color:t.t4, marginTop:3 }}>{c.sub}</div>
                </div>
              ))}
            </div>
            {forecastWeek.length > 0 && (
              <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                {forecastWeek.slice(0,4).map(b=>(
                  <div key={b.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 10px", background:t.section, borderRadius:10, border:`1px solid ${t.border}` }}>
                    <div>
                      <div style={{ fontSize:12, fontWeight:700, color:t.t1 }}>{b.client_name||"Client"} · {b.service_type}</div>
                      <div style={{ fontSize:11, color:t.t4 }}>{b.scheduled_at?new Date(b.scheduled_at).toLocaleDateString("en-CA",{weekday:"short",month:"short",day:"numeric"}):""} · {b.scheduled_at?new Date(b.scheduled_at).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}):""}</div>
                    </div>
                    <div style={{ fontSize:13, fontWeight:800, color:"#60A5FA" }}>${parseAmt(b.price_agreed).toFixed(0)}</div>
                  </div>
                ))}
                {forecastWeek.length > 4 && <div style={{ fontSize:11, color:t.t4, textAlign:"center", paddingTop:2 }}>+{forecastWeek.length-4} more this week</div>}
              </div>
            )}
            {upcomingBooked.length === 0 && (
              <div style={{ textAlign:"center", padding:"12px 0", color:t.t4, fontSize:12 }}>No confirmed upcoming bookings yet</div>
            )}
          </div>

          {/* ── Income summary cards ── */}
          <motion.div variants={fadeUp} className="grid grid-cols-3 gap-3 mb-4">
            {[{label:"Today",value:todayEarnings,color:"#22C55E",bg:"rgba(34,197,94,0.12)"},{label:"This Week",value:weekEarnings,color:"#60A5FA",bg:"rgba(37,99,235,0.12)"},{label:"30 Days",value:monthEarnings,color:"#F59E0B",bg:"rgba(245,158,11,0.12)"}].map(card=>(
              <motion.div key={card.label} whileHover={{ scale:1.03 }} whileTap={{ scale:0.97 }} className="rounded-2xl" style={{ background:"linear-gradient(135deg,#1A1F2E,#141928)", padding:"16px 10px", border:`1.5px solid ${t.border}`, textAlign:"center" }}>
                <div style={{ fontSize:10, fontWeight:700, color:t.t4, textTransform:"uppercase", letterSpacing:"0.12em", marginBottom:6 }}>{card.label}</div>
                <div style={{ fontSize:18, fontWeight:900, color:card.color, fontFamily:"'DM Sans',sans-serif" }}>
                  <CountUp value={card.value} prefix="$" duration={900} decimals={0}/>
                </div>
                <div style={{ fontSize:10, color:t.t4, marginTop:2 }}>earned</div>
              </motion.div>
            ))}
          </motion.div>

          {/* ── Weekly earnings chart ── */}
          <motion.div variants={fadeUp} className="rounded-2xl mb-4" style={{ background:"linear-gradient(135deg,#1A1F2E,#141928)", padding:"18px 18px 14px", border:`1.5px solid ${t.border}` }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:16 }}>
              <div style={{ fontSize:13, fontWeight:800, color:t.t1, fontFamily:"'DM Sans',sans-serif" }}>Last 7 Days</div>
              <div style={{ fontSize:12, fontWeight:700, color:"#60A5FA" }}>${weeklyChartData.reduce((s,d)=>s+d.total,0).toFixed(0)} total</div>
            </div>
            <div style={{ display:"flex", alignItems:"flex-end", gap:6, height:100 }}>
              {weeklyChartData.map((d,i) => {
                const pct = chartMax > 0 ? d.total / chartMax : 0;
                const barH = Math.max(pct * 76, d.total > 0 ? 6 : 2);
                return (
                  <div key={d.dateStr} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
                    {d.total > 0 && (
                      <div style={{ fontSize:9, fontWeight:700, color: d.isToday?"#60A5FA":"rgba(245,245,240,0.4)", lineHeight:1, marginBottom:2 }}>${d.total<100?d.total.toFixed(0):Math.round(d.total)}</div>
                    )}
                    <motion.div
                      initial={{ height:0 }} animate={{ height:barH }}
                      transition={{ duration:0.6, delay:i*0.06, ease:"easeOut" }}
                      style={{ width:"100%", borderRadius:"4px 4px 0 0",
                        background: d.isToday
                          ? "linear-gradient(180deg,#3B82F6,#2563EB)"
                          : d.total > 0
                            ? "linear-gradient(180deg,rgba(37,99,235,0.55),rgba(37,99,235,0.3))"
                            : "rgba(245,245,240,0.06)",
                        boxShadow: d.isToday && d.total > 0 ? "0 0 12px rgba(37,99,235,0.4)" : "none",
                      }}/>
                    <div style={{ height:1, width:"100%", background:"rgba(245,245,240,0.08)" }}/>
                    <div style={{ fontSize:9, fontWeight:d.isToday?800:600, color:d.isToday?"#60A5FA":"rgba(245,245,240,0.35)", textTransform:"uppercase", letterSpacing:"0.04em" }}>{d.dayLabel}</div>
                  </div>
                );
              })}
            </div>
          </motion.div>

          {/* ── HST threshold alert ── */}
          {(()=>{
            const yearStart = new Date(new Date().getFullYear(),0,1).toISOString().split("T")[0];
            const yearEarnings = completed.filter(b=>b.created_at?.slice(0,10)>=yearStart).reduce((s,b)=>s+totalWithTip(b),0);
            const pct = Math.min(100,Math.round(yearEarnings/30000*100));
            if (yearEarnings < 28000) return null;
            return (
              <div className="rounded-2xl mb-4" style={{ background:t.card, padding:20, border:"1.5px solid rgba(245,158,11,0.4)" }}>
                <div style={{ fontSize:13, fontWeight:800, color:"#F59E0B", marginBottom:6 }}>⚠️ Approaching $30K HST Threshold</div>
                <div style={{ fontSize:12, color:t.t3, marginBottom:10, lineHeight:1.5 }}>
                  You've earned CAD ${yearEarnings.toFixed(0)} this year. Once you exceed $30,000 CRA requires you to register for HST.
                </div>
                <div style={{ background:t.section, borderRadius:8, overflow:"hidden", height:8, marginBottom:8 }}>
                  <motion.div
                    initial={{ width:"0%" }}
                    animate={{ width:`${pct}%` }}
                    transition={{ duration:1.2, ease:"easeOut", delay:0.3 }}
                    style={{ height:"100%", background:pct>=90?"#EF4444":pct>=70?"#F59E0B":"#3B6CF7", borderRadius:8 }}/>
                </div>
                <div style={{ fontSize:11, color:t.t4, display:"flex", justifyContent:"space-between" }}>
                  <span><CountUp value={yearEarnings} prefix="$" duration={1000} decimals={0}/> earned this year</span>
                  <span style={{ color:pct>=90?"#EF4444":"#F59E0B", fontWeight:700 }}>{pct}% of $30K</span>
                </div>
              </div>
            );
          })()}

          {/* ── Recent Cuts — collapsible ── */}
          <div className="rounded-2xl mb-4" style={{ background:t.card, border:`1.5px solid ${t.border}`, overflow:"hidden" }}>
            <button onClick={()=>toggleSection("cuts")} style={{ width:"100%", padding:"16px 20px", background:"none", border:"none", cursor:"pointer", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div style={{ textAlign:"left" }}>
                <div style={{ fontSize:13, fontWeight:800, color:t.t1, fontFamily:"'DM Sans',sans-serif" }}>✂️ Recent Cuts</div>
                {!openSections.cuts && <div style={{ fontSize:11, color:t.t4, marginTop:2 }}>{recentEarnings.length} completed · ${recentEarnings.reduce((s,b)=>s+totalWithTip(b),0).toFixed(0)} total</div>}
              </div>
              <span style={{ fontSize:13, color:t.t4 }}>{openSections.cuts?"▲":"▼"}</span>
            </button>
            {openSections.cuts && (
              <div style={{ padding:"0 20px 16px" }}>
                <div style={{ fontSize:11, color:t.t4, marginBottom:12 }}>Your full price — client pays processing fee on top</div>
                {recentEarnings.length === 0 ? (
                  <motion.div variants={staggerContainer} initial="hidden" animate="visible" style={{ textAlign:"center", padding:"32px 0" }}>
                    <motion.div variants={scaleIn} style={{ fontSize:32, marginBottom:10 }}>✂️</motion.div>
                    <motion.div variants={fadeUp} style={{ fontSize:14, fontWeight:700, color:t.t1, fontFamily:"'DM Sans',sans-serif", marginBottom:4 }}>Your earnings will appear here</motion.div>
                    <motion.div variants={fadeUp} style={{ fontSize:12, color:t.t4, lineHeight:1.6 }}>Complete your first booking and your cut lands here.</motion.div>
                  </motion.div>
                ) : recentEarnings.map((b,i,arr)=>(
                  <div key={b.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"11px 0", borderBottom:i<arr.length-1?`1px solid ${t.divider}`:"none" }}>
                    <div>
                      <div style={{ fontSize:13, fontWeight:700, color:t.t1 }}>{b.client_name||"Client"}</div>
                      <div style={{ fontSize:11, color:t.t3, marginTop:1 }}>{b.service_type||"Haircut"} · {b.created_at?new Date(b.created_at).toLocaleDateString([],{month:"short",day:"numeric"}):""}</div>
                    </div>
                    <div style={{ textAlign:"right" }}>
                      <div style={{ fontSize:15, fontWeight:800, color:"#22C55E" }}>${totalWithTip(b).toFixed(0)}</div>
                      {parseAmt(b.tip_amount)>0 && <div style={{ fontSize:11, color:"#22C55E", opacity:0.7 }}>incl. ${parseAmt(b.tip_amount).toFixed(0)} tip</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Busy Hours — collapsible ── */}
          <div className="rounded-2xl mb-4" style={{ background:t.card, border:`1.5px solid ${t.border}`, overflow:"hidden" }}>
            <button onClick={()=>toggleSection("heatmap")} style={{ width:"100%", padding:"16px 20px", background:"none", border:"none", cursor:"pointer", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div style={{ textAlign:"left" }}>
                <div style={{ fontSize:13, fontWeight:800, color:t.t1, fontFamily:"'DM Sans',sans-serif" }}>📊 Busy Hours</div>
                {!openSections.heatmap && <div style={{ fontSize:11, color:t.t4, marginTop:2 }}>Peak: {heatDays[peakDayIdx]} at {fmtHr(peakHour)}</div>}
              </div>
              <span style={{ fontSize:13, color:t.t4 }}>{openSections.heatmap?"▲":"▼"}</span>
            </button>
            {openSections.heatmap && (
              <div style={{ padding:"0 20px 20px" }}>
                <div style={{ display:"flex", justifyContent:"flex-end", gap:6, marginBottom:12 }}>
                  {[30,90].map(r=>(
                    <button key={r} onClick={()=>setHeatmapRange(r)} style={{ padding:"4px 10px", borderRadius:8, border:`1px solid ${heatmapRange===r?"#2563EB":t.border}`, background:heatmapRange===r?"rgba(37,99,235,0.12)":t.section, color:heatmapRange===r?"#2563EB":t.t3, fontSize:11, fontWeight:700, cursor:"pointer" }}>{r}d</button>
                  ))}
                </div>
                <div style={{ overflowX:"auto" }}>
                  <div style={{ minWidth:300 }}>
                    <div style={{ display:"grid", gridTemplateColumns:"30px repeat(7,1fr)", gap:3, marginBottom:4 }}>
                      <div/>
                      {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d=><div key={d} style={{ fontSize:11, fontWeight:700, color:t.t4, textAlign:"center" }}>{d}</div>)}
                    </div>
                    {[8,9,10,11,12,13,14,15,16,17,18,19,20].map(hour=>(
                      <div key={hour} style={{ display:"grid", gridTemplateColumns:"30px repeat(7,1fr)", gap:3, marginBottom:3 }}>
                        <div style={{ fontSize:11, color:t.t4, textAlign:"right", paddingRight:4, lineHeight:"22px" }}>{hour>12?`${hour-12}p`:hour===12?"12p":`${hour}a`}</div>
                        {[0,1,2,3,4,5,6].map(day=>{
                          const count=heatGrid[day]?.[hour]||0;
                          const intensity=count/heatMax;
                          return <div key={day} title={`${count} booking${count!==1?"s":""}`} style={{ height:22, borderRadius:5, background:count===0?t.section:`rgba(37,99,235,${0.08+intensity*0.88})`, transition:"opacity 0.2s" }}/>;
                        })}
                      </div>
                    ))}
                    <div style={{ display:"flex", alignItems:"center", gap:5, marginTop:10, justifyContent:"flex-end" }}>
                      <span style={{ fontSize:11, color:t.t4 }}>Less</span>
                      {[0.08,0.3,0.55,0.75,0.96].map(o=><div key={o} style={{ width:14,height:14,borderRadius:3,background:`rgba(37,99,235,${o})` }}/>)}
                      <span style={{ fontSize:11, color:t.t4 }}>More</span>
                    </div>
                  </div>
                </div>
                {heatMax>1 ? (
                  <div style={{ marginTop:14, padding:"10px 14px", background:t.section, borderRadius:12, border:`1px solid ${t.border}` }}>
                    <div style={{ fontSize:12, fontWeight:700, color:t.t1, marginBottom:3 }}>💡 Peak: {heatDays[peakDayIdx]} at {fmtHr(peakHour)}</div>
                    <div style={{ fontSize:11, color:t.t3, marginBottom:8 }}>Consider a higher price for this slot. Promo your quieter hours to fill gaps.</div>
                    <button onClick={()=>{ const msg=`Special deal — book your cut with me and save 15%! Limited slots available. Book at letsgetfaded.com 💈`; navigator.clipboard?.writeText(msg); }} style={{ padding:"7px 14px", background:t.blueBg, border:`1px solid ${t.blueBorder}`, borderRadius:8, color:t.blueText, fontSize:11, fontWeight:700, cursor:"pointer" }}>📋 Copy Promo Deal</button>
                  </div>
                ) : (
                  <div style={{ textAlign:"center", padding:"16px 0", color:t.t4, fontSize:12 }}>Complete more appointments to see your pattern</div>
                )}
              </div>
            )}
          </div>

          {/* ── No-Show Tracker — collapsible ── */}
          <div className="rounded-2xl mb-4" style={{ background:t.card, border:`1.5px solid ${t.border}`, overflow:"hidden" }}>
            <button onClick={()=>toggleSection("noshow")} style={{ width:"100%", padding:"16px 20px", background:"none", border:"none", cursor:"pointer", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div style={{ textAlign:"left" }}>
                <div style={{ fontSize:13, fontWeight:800, color:t.t1, fontFamily:"'DM Sans',sans-serif" }}>👻 No-Show Tracker</div>
                {!openSections.noshow && <div style={{ fontSize:11, color:t.t4, marginTop:2 }}>{noShows.length} no-show{noShows.length!==1?"s":""} · {noShowRate}% rate</div>}
              </div>
              <span style={{ fontSize:13, color:t.t4 }}>{openSections.noshow?"▲":"▼"}</span>
            </button>
            {openSections.noshow && (
              <div style={{ padding:"0 20px 20px" }}>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginBottom:14 }}>
                  {[
                    { label:"Total No-Shows", value:noShows.length, color:"#EF4444", bg:"rgba(220,38,38,0.08)" },
                    { label:"No-Show Rate", value:`${noShowRate}%`, color:noShowRate>15?"#EF4444":noShowRate>5?"#F59E0B":"#22C55E", bg:"rgba(245,158,11,0.08)" },
                    { label:"Flagged Clients", value:Object.values(strikeMap).filter(c=>c>=2).length, color:"#F59E0B", bg:"rgba(245,158,11,0.08)" },
                  ].map(s=>(
                    <div key={s.label} style={{ background:s.bg, borderRadius:14, padding:"12px 8px", textAlign:"center" }}>
                      <div style={{ fontSize:20, fontWeight:900, color:s.color, fontFamily:"'DM Sans',sans-serif" }}>{s.value}</div>
                      <div style={{ fontSize:11, color:t.t3, marginTop:3, fontWeight:600, lineHeight:1.3 }}>{s.label}</div>
                    </div>
                  ))}
                </div>
                {Object.keys(strikeMap).length>0 ? (
                  <div>
                    <div style={{ fontSize:11, fontWeight:700, color:t.t3, marginBottom:8 }}>Strike History</div>
                    {Object.entries(strikeMap).sort((a,b)=>b[1]-a[1]).slice(0,8).map(([client,count])=>(
                      <div key={client} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 0", borderBottom:`1px solid ${t.divider}` }}>
                        <div style={{ fontSize:12, color:t.t1, fontWeight:600, flex:1, minWidth:0, wordBreak:"break-word" }}>{client}</div>
                        <div style={{ display:"flex", alignItems:"center", gap:8, flexShrink:0 }}>
                          <div style={{ display:"flex", gap:2 }}>{Array.from({length:Math.min(count,3)}).map((_,i)=><span key={i} style={{ fontSize:12 }}>🔴</span>)}</div>
                          <span style={{ fontSize:11, fontWeight:700, color:count>=3?"#EF4444":count>=2?"#F59E0B":"#888", padding:"2px 8px", borderRadius:8, background:count>=3?"rgba(220,38,38,0.1)":count>=2?"rgba(245,158,11,0.1)":"transparent", whiteSpace:"nowrap" }}>
                            {count>=3?"BLOCKED":count>=2?"DEPOSIT REQ":"1 strike"}
                          </span>
                        </div>
                      </div>
                    ))}
                    <div style={{ marginTop:10, padding:"10px 12px", background:"rgba(245,158,11,0.06)", borderRadius:10, border:"1px solid rgba(245,158,11,0.2)" }}>
                      <div style={{ fontSize:11, fontWeight:700, color:"#F59E0B" }}>Strike System</div>
                      <div style={{ fontSize:11, color:t.t3, marginTop:2 }}>2 no-shows → deposit required · 3+ → blocked</div>
                    </div>
                  </div>
                ) : (
                  <div style={{ textAlign:"center", padding:"16px 0", color:t.t4, fontSize:12 }}>No no-shows recorded yet 🎉</div>
                )}
              </div>
            )}
          </div>

        </div>
      )}

      {dashTab==="status" && <motion.div variants={staggerContainer} initial="hidden" animate="visible" style={{ padding:"20px 16px 120px" }}>

      {/* ── Stripe setup banner ── */}
      {stripeEnabled === false && !stripeBannerDismissed && (
        <div style={{ marginBottom:16, padding:"16px", background:"linear-gradient(135deg,rgba(37,99,235,0.14),rgba(37,99,235,0.06))", border:"1.5px solid rgba(37,99,235,0.35)", borderRadius:16, display:"flex", alignItems:"center", gap:12 }}>
          <div style={{ fontSize:26, flexShrink:0 }}>💳</div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:13, fontWeight:800, color:t.t1, marginBottom:2 }}>Accept card payments</div>
            <div style={{ fontSize:12, color:t.t3, lineHeight:1.5 }}>Connect Stripe to get paid by card. Takes ~3 min.</div>
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:6, flexShrink:0, alignItems:"flex-end" }}>
            <button onClick={handleStripeConnect} disabled={stripeConnecting}
              style={{ padding:"8px 16px", background:"#2563EB", border:"none", borderRadius:10, color:"#fff", fontSize:12, fontWeight:800, cursor:"pointer", whiteSpace:"nowrap", opacity:stripeConnecting?0.7:1 }}>
              {stripeConnecting ? "Opening…" : "Set up →"}
            </button>
            <button onClick={()=>{ setStripeBannerDismissed(true); try { localStorage.setItem("faded_stripe_banner_dismissed","1"); } catch {} }}
              style={{ background:"none", border:"none", color:t.t4, fontSize:11, cursor:"pointer", padding:"2px 4px" }}>
              Later
            </button>
          </div>
        </div>
      )}

      {/* ── CRA $30K Threshold Alert ── */}
      {myBarberData?.cra_threshold_crossed && (
        <div style={{ marginBottom:16, padding:"14px 16px", background:"rgba(245,158,11,0.08)", border:"1.5px solid rgba(245,158,11,0.35)", borderRadius:16 }}>
          <div style={{ fontSize:13, fontWeight:900, color:"#F59E0B", marginBottom:4 }}>⚠️ CRA $30,000 Threshold Crossed</div>
          <div style={{ fontSize:12, color:"rgba(245,245,240,0.65)", lineHeight:1.6 }}>
            Your lifetime earnings on FADED have passed $30,000 — you are likely now required to register for and collect HST/GST on all services. This is a heads-up only: FADED does not report this to the CRA on your behalf, and does not register you automatically. Please confirm your obligations with an accountant, then enable HST below once registered.
          </div>
          <button onClick={async () => {
            await supabase.from("barbers").update({ hst_registered: true }).eq("id", session.user.id);
            if (onUpdateBarberData) onUpdateBarberData(prev => prev ? { ...prev, hst_registered: true } : prev);
          }} style={{ marginTop:10, padding:"8px 16px", background:"rgba(245,158,11,0.15)", border:"1px solid rgba(245,158,11,0.4)", borderRadius:10, color:"#F59E0B", fontSize:12, fontWeight:700, cursor:"pointer" }}>
            ✓ Confirm HST Registration
          </button>
        </div>
      )}

      {/* ── Barber Strike / Suspension Banners ── */}
      {(myBarberData?.cancellation_count_30d >= 1 || myBarberData?.suspension_pending_review) && (() => {
        const count = myBarberData.cancellation_count_30d || 0;
        const suspended = myBarberData.suspension_pending_review;
        if (suspended) return (
          <div style={{ marginBottom:16, padding:"14px 16px", background:"rgba(220,38,38,0.08)", border:"1.5px solid rgba(220,38,38,0.4)", borderRadius:16 }}>
            <div style={{ fontSize:13, fontWeight:900, color:"#EF4444", marginBottom:4 }}>🚫 Account Flagged for Review</div>
            <div style={{ fontSize:12, color:"rgba(245,245,240,0.65)", lineHeight:1.6 }}>
              Your account has been flagged after 3 cancellations in 30 days. A FADED admin will review your account shortly.
              Existing bookings remain active. Contact hello@letsgetfaded.com if you believe this is in error.
            </div>
          </div>
        );
        if (count >= 2) return (
          <div style={{ marginBottom:16, padding:"14px 16px", background:"rgba(220,38,38,0.06)", border:"1.5px solid rgba(245,158,11,0.4)", borderRadius:16 }}>
            <div style={{ fontSize:13, fontWeight:900, color:"#F59E0B", marginBottom:4 }}>⚠️ Strike 2 of 3 — Warning</div>
            <div style={{ fontSize:12, color:"rgba(245,245,240,0.65)", lineHeight:1.6 }}>
              You have cancelled {count} bookings in the last 30 days. One more cancellation will flag your account for review.
              Clients rely on you showing up — frequent cancellations hurt their trust.
            </div>
          </div>
        );
        return (
          <div style={{ marginBottom:16, padding:"12px 16px", background:"rgba(245,158,11,0.06)", border:"1px solid rgba(245,158,11,0.25)", borderRadius:14 }}>
            <div style={{ fontSize:12, fontWeight:800, color:"#F59E0B", marginBottom:2 }}>Strike 1 of 3</div>
            <div style={{ fontSize:11, color:"rgba(245,245,240,0.5)", lineHeight:1.5 }}>
              You've cancelled a booking this month. 3 cancellations in 30 days will flag your account for review.
            </div>
          </div>
        );
      })()}

      {/* ── STATUS ── */}
      <div style={{ fontSize:10, fontWeight:800, letterSpacing:"0.16em", color:t.t4, textTransform:"uppercase", marginBottom:10, paddingLeft:2 }}>Status</div>
      <GlassCard glowColor={myStatus==="available"?"green":"blue"} style={{ padding:24, marginBottom:24, border:`1.5px solid ${myStatus==="available"?"rgba(34,197,94,0.4)":"rgba(37,43,59,0.6)"}` }} className={myStatus==="available"?"status-card-online":""}>
        <div style={{ fontSize:13, fontWeight:800, color:t.t1, fontFamily:"'DM Sans',sans-serif", marginBottom:16, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <span className="heading-gradient">Your Status</span>
          <span style={{ fontSize:11, color:t.t4, fontWeight:600 }}>Clients see this live</span>
        </div>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            {myStatus==="available"
              ? <LiveBadge label="ONLINE"/>
              : <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                  <div style={{ width:8, height:8, borderRadius:"50%", background:"#555" }}/>
                  <span style={{ fontSize:11, fontWeight:700, letterSpacing:"0.14em", color:"#6B7280" }}>OFFLINE</span>
                </div>
            }
            <div>
              <div style={{ fontSize:11, color:t.t4, marginTop:1 }}>{myStatus==="available"?"Clients can find and book you":"Hidden from clients"}</div>
            </div>
          </div>
          <motion.button
            whileTap={{ scale:0.92 }}
            onClick={()=>{ haptic.medium(); updateStatus(myStatus==="available"?"offline":"available"); }}
            disabled={!myBarberData?.approved && myStatus!=="available"}
            style={{ width:64, height:34, borderRadius:17, border:"none", cursor:myBarberData?.approved?"pointer":"not-allowed", position:"relative", background:myStatus==="available"?"#22C55E":"rgba(120,120,120,0.3)", transition:"background 0.25s", boxShadow:myStatus==="available"?"0 0 16px rgba(34,197,94,0.5)":"none", flexShrink:0, opacity:!myBarberData?.approved?0.4:1 }}>
            <div style={{ position:"absolute", top:5, left:myStatus==="available"?34:5, width:24, height:24, borderRadius:12, background:"#fff", transition:"left 0.25s", boxShadow:"0 2px 6px rgba(0,0,0,0.3)" }}/>
          </motion.button>
        </div>
        {/* Pause Bookings divider + row */}
        <div style={{ margin:"16px 0", height:1, background:"rgba(245,245,240,0.06)" }}/>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <div style={{ display:"flex", alignItems:"center", gap:6 }}>
              <div style={{ width:8, height:8, borderRadius:"50%", background: bookingsPaused ? "#F59E0B" : "#2563EB", boxShadow: bookingsPaused ? "0 0 8px rgba(245,158,11,0.7)" : "0 0 8px rgba(37,99,235,0.6)", transition:"background 0.25s, box-shadow 0.25s" }}/>
              <span style={{ fontSize:11, fontWeight:700, letterSpacing:"0.14em", color: bookingsPaused ? "#F59E0B" : "#3B82F6", transition:"color 0.25s" }}>{bookingsPaused ? "PAUSED" : "ACCEPTING"}</span>
            </div>
            <div>
              <div style={{ fontSize:13, fontWeight:700, color:t.t1 }}>Accept Bookings</div>
              <div style={{ fontSize:11, color:t.t4, marginTop:1 }}>{bookingsPaused ? "Clients cannot book you" : "Future appointments open"}</div>
            </div>
          </div>
          <motion.button
            whileTap={{ scale:0.92 }}
            onClick={togglePause}
            disabled={pauseSaving}
            style={{ width:64, height:34, borderRadius:17, border:"none", cursor:"pointer", position:"relative", background: bookingsPaused ? "rgba(245,158,11,0.25)" : "#2563EB", transition:"background 0.25s", boxShadow: bookingsPaused ? "0 0 14px rgba(245,158,11,0.3)" : "0 0 16px rgba(37,99,235,0.45)", flexShrink:0, opacity: pauseSaving ? 0.6 : 1 }}>
            <div style={{ position:"absolute", top:5, left: bookingsPaused ? 5 : 34, width:24, height:24, borderRadius:12, background:"#fff", transition:"left 0.25s", boxShadow:"0 2px 6px rgba(0,0,0,0.3)" }}/>
          </motion.button>
        </div>

        {!myBarberData?.approved && (
          <div style={{ marginTop:12, background:"rgba(245,158,11,0.08)", border:"1.5px solid rgba(245,158,11,0.3)", borderRadius:12, padding:"12px 14px", display:"flex", gap:10, alignItems:"flex-start" }}>
            <span style={{ fontSize:18 }}>⏳</span>
            <div>
              <div style={{ fontSize:13, fontWeight:800, color:"#F59E0B" }}>Profile under review</div>
              <div style={{ fontSize:12, color:"#9CA3AF", marginTop:2, lineHeight:1.5 }}>We're verifying your account — usually within 24 hours. You'll receive an email once approved and can go live.</div>
            </div>
          </div>
        )}
        {false && <button onClick={()=>{ const nv=!razorMode; setRazorMode(nv); try{localStorage.setItem("faded_razor_mode",String(nv));}catch{} }} style={{ width:"100%", marginTop:10, padding:"12px 16px", borderRadius:12, border:`1.5px solid ${razorMode?"rgba(147,197,253,0.5)":t.border2}`, background:razorMode?"rgba(37,99,235,0.1)":t.section, color:razorMode?"#93C5FD":t.t3, fontSize:13, fontWeight:700, cursor:"pointer", display:"flex", justifyContent:"space-between", alignItems:"center" }}><span>🎙️ Hey Razor — AI wake word</span><span style={{ fontSize:12 }}>{razorMode?"✓ ON":"OFF"}</span></button>}
        {myBarberData?.is_licensed ? (
          <div style={{ width:"100%", marginTop:10, padding:"12px 16px", borderRadius:12, border:"1.5px solid rgba(5,150,105,0.5)", background:"rgba(5,150,105,0.1)", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <span style={{ fontSize:13, fontWeight:700, color:"#059669" }}>✓ Ontario Licensed Barber</span>
            <span style={{ fontSize:11, fontWeight:700, color:"#059669", background:"rgba(5,150,105,0.15)", border:"1px solid rgba(5,150,105,0.3)", borderRadius:20, padding:"2px 10px" }}>VERIFIED</span>
          </div>
        ) : (
          <div style={{ width:"100%", marginTop:10, padding:"12px 16px", borderRadius:12, border:`1.5px solid ${t.border2}`, background:t.section }}>
            <div style={{ fontSize:13, fontWeight:700, color:t.t2, marginBottom:4 }}>Ontario Licensed Barber</div>
            <div style={{ fontSize:12, color:t.t4, lineHeight:1.5 }}>To display a verified licence badge on your profile, email us at <span style={{ color:"#2563EB", fontWeight:600 }}>hello@letsgetfaded.com</span> with your Ontario barbering licence number. We'll verify and enable it within 24 hrs.</div>
          </div>
        )}
      </GlassCard>

      {/* ── PAYMENT METHODS ── */}
      <div style={{ fontSize:10, fontWeight:800, letterSpacing:"0.16em", color:t.t4, textTransform:"uppercase", marginBottom:10, paddingLeft:2 }}>Payment Methods</div>
      <div style={{ background:t.card, borderRadius:20, border:`1.5px solid ${t.border}`, marginBottom:24, overflow:"hidden" }}>
        {payPrefs === null ? (
          <div style={{ padding:"20px", fontSize:12, color:t.t4 }}>Loading…</div>
        ) : (<>
          {[
            { key:"cash", label:"Cash", desc:"Client pays you in person with cash" },
            { key:"payAtShop", label:"Pay at Shop", desc:"Client taps your in-store card reader" },
          ].map(row=>(
            <div key={row.key} style={{ padding:"16px 20px", display:"flex", justifyContent:"space-between", alignItems:"center", borderBottom:`1px solid ${t.border}` }}>
              <div style={{ flex:1, marginRight:16 }}>
                <div style={{ fontSize:13, fontWeight:800, color:t.t1, fontFamily:"'DM Sans',sans-serif" }}>{row.label}</div>
                <div style={{ fontSize:12, color:t.t4, marginTop:2, lineHeight:1.45 }}>{row.desc}</div>
              </div>
              <motion.button whileTap={{ scale:0.92 }} onClick={()=>savePayPrefs({ [row.key]: !payPrefs[row.key] })}
                style={{ width:44, height:24, borderRadius:12, border:"none", cursor:"pointer", background:payPrefs[row.key]?"#2563EB":t.border2, position:"relative", transition:"background 0.2s", flexShrink:0 }}>
                <div style={{ width:18, height:18, borderRadius:"50%", background:"#fff", position:"absolute", top:3, left:payPrefs[row.key]?23:3, transition:"left 0.2s" }}/>
              </motion.button>
            </div>
          ))}

          {/* Interac e-Transfer — needs a destination email before it shows to clients */}
          <div style={{ padding:"16px 20px", borderBottom:`1px solid ${t.border}` }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div style={{ flex:1, marginRight:16 }}>
                <div style={{ fontSize:13, fontWeight:800, color:t.t1, fontFamily:"'DM Sans',sans-serif" }}>Interac e-Transfer</div>
                <div style={{ fontSize:12, color:t.t4, marginTop:2, lineHeight:1.45 }}>Clients send an e-Transfer to your email</div>
              </div>
              <motion.button whileTap={{ scale:0.92 }} onClick={()=>savePayPrefs({ etransfer: !payPrefs.etransfer })}
                style={{ width:44, height:24, borderRadius:12, border:"none", cursor:"pointer", background:payPrefs.etransfer?"#2563EB":t.border2, position:"relative", transition:"background 0.2s", flexShrink:0 }}>
                <div style={{ width:18, height:18, borderRadius:"50%", background:"#fff", position:"absolute", top:3, left:payPrefs.etransfer?23:3, transition:"left 0.2s" }}/>
              </motion.button>
            </div>
            {payPrefs.etransfer && (
              <div style={{ marginTop:14 }}>
                <div style={{ fontSize:10, fontWeight:800, color:t.t4, textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:8 }}>E-Transfer Email</div>
                <input type="email" inputMode="email" value={payPrefs.etransferEmail} placeholder="you@email.com"
                  onChange={e=>setPayPrefs(p=>({ ...p, etransferEmail:e.target.value }))}
                  onBlur={()=>savePayPrefs({})}
                  style={{ width:"100%", padding:"11px 12px", borderRadius:10, border:`1.5px solid ${payPrefs.etransferEmail.trim()?"rgba(37,99,235,0.5)":t.border2}`, background:t.input, color:t.inputText, fontSize:14, outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box" }}/>
                {!payPrefs.etransferEmail.trim() && (
                  <div style={{ fontSize:11, color:"#F59E0B", marginTop:6, lineHeight:1.4 }}>Add your email so clients know where to send — e-Transfer stays hidden to clients until you do.</div>
                )}
              </div>
            )}
          </div>

          {/* Card / Pay on App — governed by Stripe onboarding, not a free toggle */}
          <div style={{ padding:"16px 20px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <div style={{ flex:1, marginRight:16 }}>
              <div style={{ fontSize:13, fontWeight:800, color:t.t1, fontFamily:"'DM Sans',sans-serif" }}>Card / Pay on App</div>
              <div style={{ fontSize:12, color:t.t4, marginTop:2, lineHeight:1.45 }}>{stripeEnabled ? "On — clients can pay by card in the app" : "Connect Stripe to accept card payments"}</div>
            </div>
            {stripeEnabled
              ? <div style={{ fontSize:11, fontWeight:800, color:"#22C55E", display:"flex", alignItems:"center", gap:5, flexShrink:0 }}><span style={{ width:7, height:7, borderRadius:"50%", background:"#22C55E", display:"inline-block" }}/>On</div>
              : <button onClick={handleStripeConnect} disabled={stripeConnecting} style={{ flexShrink:0, padding:"9px 16px", background:"#2563EB", border:"none", borderRadius:12, color:"#fff", fontSize:12, fontWeight:800, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", opacity:stripeConnecting?0.6:1 }}>{stripeConnecting ? "…" : "Connect"}</button>}
          </div>

          {payPrefsSaving && <div style={{ fontSize:11, color:t.t4, textAlign:"right", padding:"0 20px 12px" }}>Saving…</div>}
        </>)}
      </div>

      {/* ── BOOKING POLICY ── */}
      <div style={{ fontSize:10, fontWeight:800, letterSpacing:"0.16em", color:t.t4, textTransform:"uppercase", marginBottom:10, paddingLeft:2 }}>Booking Policy</div>
      <div style={{ background:t.card, borderRadius:20, border:`1.5px solid ${t.border}`, marginBottom:24, overflow:"hidden" }}>
        {/* No-Show Protection */}
        <div style={{ padding:"20px" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <div style={{ flex:1, marginRight:16 }}>
              <div style={{ fontSize:13, fontWeight:800, color:t.t1, fontFamily:"'DM Sans',sans-serif" }}>No-Show Protection</div>
              <div style={{ fontSize:12, color:t.t4, marginTop:2, lineHeight:1.45 }}>Clients must save a card to book with you. Set your cancellation and no-show fees below.</div>
            </div>
            <motion.button whileTap={{ scale:0.92 }} onClick={()=>saveNspDash({ enabled: !nsp?.enabled })} disabled={nsp===null}
              style={{ width:44, height:24, borderRadius:12, border:"none", cursor:"pointer", background:nsp?.enabled?"#2563EB":t.border2, position:"relative", transition:"background 0.2s", flexShrink:0, opacity:nsp===null?0.4:1 }}>
              <div style={{ width:18, height:18, borderRadius:"50%", background:"#fff", position:"absolute", top:3, left:nsp?.enabled?23:3, transition:"left 0.2s" }}/>
            </motion.button>
          </div>
          {nsp?.enabled && (
            <div style={{ marginTop:18, display:"flex", flexDirection:"column", gap:16 }}>
              <div>
                <div style={{ fontSize:10, fontWeight:800, color:t.t4, textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:8 }}>Late Cancellation Fee</div>
                <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                  {[["fifty_percent","50%"],["hundred_percent","100%"],["custom","Custom"]].map(([v,label])=>(
                    <button key={v} onClick={()=>saveNspDash({ cancelFeeType:v })} style={{ padding:"7px 14px", borderRadius:10, border:`1.5px solid ${nsp.cancelFeeType===v?"#2563EB":t.border}`, background:nsp.cancelFeeType===v?"rgba(37,99,235,0.12)":"transparent", color:nsp.cancelFeeType===v?"#2563EB":t.t2, fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>{label}</button>
                  ))}
                </div>
                {nsp.cancelFeeType==="custom" && (
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:8 }}>
                    <span style={{ fontSize:13, color:t.t2 }}>$</span>
                    <input type="number" min="0" step="0.01" value={(nsp.cancelFlatCents/100).toFixed(2)}
                      onChange={e=>setNsp(p=>({...p, cancelFlatCents:Math.round(parseFloat(e.target.value||"0")*100)}))}
                      onBlur={()=>saveNspDash({})}
                      style={{ width:90, padding:"8px 10px", borderRadius:10, border:`1.5px solid ${t.border2}`, background:t.input, color:t.inputText, fontSize:13, outline:"none", fontFamily:"'DM Sans',sans-serif" }}/>
                    <span style={{ fontSize:12, color:t.t4 }}>CAD</span>
                  </div>
                )}
              </div>
              <div>
                <div style={{ fontSize:10, fontWeight:800, color:t.t4, textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:8 }}>Free Cancellation Window</div>
                <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                  {[[2,"2 hrs"],[12,"12 hrs"],[24,"24 hrs"],[48,"48 hrs"]].map(([v,label])=>(
                    <button key={v} onClick={()=>saveNspDash({ cancelWindowHours:v })} style={{ padding:"7px 14px", borderRadius:10, border:`1.5px solid ${nsp.cancelWindowHours===v?"#2563EB":t.border}`, background:nsp.cancelWindowHours===v?"rgba(37,99,235,0.12)":"transparent", color:nsp.cancelWindowHours===v?"#2563EB":t.t2, fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>{label}</button>
                  ))}
                </div>
                <div style={{ fontSize:11, color:t.t4, marginTop:6 }}>Clients cancel free up to {nsp.cancelWindowHours}h before their appointment</div>
              </div>
              <div>
                <div style={{ fontSize:10, fontWeight:800, color:t.t4, textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:8 }}>No-Show Fee</div>
                <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                  {[["fifty_percent","50%"],["hundred_percent","100%"],["custom","Custom"]].map(([v,label])=>(
                    <button key={v} onClick={()=>saveNspDash({ noShowFeeType:v })} style={{ padding:"7px 14px", borderRadius:10, border:`1.5px solid ${nsp.noShowFeeType===v?"#2563EB":t.border}`, background:nsp.noShowFeeType===v?"rgba(37,99,235,0.12)":"transparent", color:nsp.noShowFeeType===v?"#2563EB":t.t2, fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>{label}</button>
                  ))}
                </div>
                {nsp.noShowFeeType==="custom" && (
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:8 }}>
                    <span style={{ fontSize:13, color:t.t2 }}>$</span>
                    <input type="number" min="0" step="0.01" value={(nsp.noShowFlatCents/100).toFixed(2)}
                      onChange={e=>setNsp(p=>({...p, noShowFlatCents:Math.round(parseFloat(e.target.value||"0")*100)}))}
                      onBlur={()=>saveNspDash({})}
                      style={{ width:90, padding:"8px 10px", borderRadius:10, border:`1.5px solid ${t.border2}`, background:t.input, color:t.inputText, fontSize:13, outline:"none", fontFamily:"'DM Sans',sans-serif" }}/>
                    <span style={{ fontSize:12, color:t.t4 }}>CAD</span>
                  </div>
                )}
              </div>
              {nspDashSaving && <div style={{ fontSize:11, color:t.t4, textAlign:"right" }}>Saving…</div>}
              <div style={{ fontSize:11, color:t.t4, background:"rgba(255,255,255,0.04)", borderRadius:10, padding:"10px 12px", lineHeight:1.6 }}>
                Card is <strong style={{ color:t.t2 }}>never charged at booking</strong> — only if client no-shows or cancels late.
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── LOCATION ── */}
      <div style={{ fontSize:10, fontWeight:800, letterSpacing:"0.16em", color:t.t4, textTransform:"uppercase", marginBottom:10, paddingLeft:2 }}>Location</div>
      {/* Address */}
      <div className="rounded-2xl mb-4" style={{ background:t.card, padding:24, border:`1.5px solid ${t.border}` }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
          <div style={{ fontSize:13, fontWeight:800, color:t.t1, fontFamily:"'DM Sans',sans-serif" }}>Shop Address</div>
          <button onClick={()=>setEditAddress(!editAddress)} style={{ fontSize:12, color:t.blueText, fontWeight:700, background:t.blueBg, border:"none", padding:"5px 12px", borderRadius:10, cursor:"pointer" }}>
            {editAddress?"Cancel":"Edit"}
          </button>
        </div>
        {editAddress ? (
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            <div style={{ position:"relative" }}>
              <input value={addressVal}
                onChange={e=>{ setAddressVal(e.target.value); setAddrDashCoords(null); fetchDashAddrSuggs(e.target.value); }}
                onFocus={()=>addrDashSuggs.length>0&&setAddrDashDrop(true)}
                onBlur={()=>setTimeout(()=>setAddrDashDrop(false),150)}
                placeholder="123 Main St, Toronto, ON"
                style={{ width:"100%", padding:"12px 14px", borderRadius:addrDashDrop&&addrDashSuggs.length>0?"10px 10px 0 0":12, border:`1.5px solid ${addrDashDrop?"#2563EB":t.border2}`, background:t.section, color:t.inputText, fontSize:14, outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box" }}/>
              {addrDashDrop && addrDashSuggs.length > 0 && (
                <div style={{ position:"absolute", top:"100%", left:0, right:0, background:t.card, border:`1.5px solid #2563EB`, borderTop:"none", borderRadius:"0 0 10px 10px", zIndex:50, maxHeight:300, overflowY:"auto", boxShadow:"0 8px 24px rgba(0,0,0,0.18)" }}>
                  {addrDashSuggs.map((s,i)=>(
                    <button key={i} onMouseDown={()=>{ setAddressVal(s.label); setAddrDashCoords({lat:parseFloat(s.lat),lng:parseFloat(s.lng)}); setAddrDashSuggs([]); setAddrDashDrop(false); }}
                      style={{ width:"100%", padding:"11px 14px", background:"none", border:"none", borderTop:i>0?`1px solid ${t.border}`:"none", color:t.t1, fontSize:13, cursor:"pointer", textAlign:"left", fontFamily:"'DM Sans',sans-serif", display:"flex", alignItems:"center", gap:8 }}>
                      <svg aria-hidden="true" focusable="false"width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={t.t4} strokeWidth="2"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg>
                      <span style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{s.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {addrDashCoords && <div style={{ fontSize:11, color:"#22C55E", display:"flex", alignItems:"center", gap:4 }}>✓ Location pinned on map</div>}
            <div style={{ fontSize:11, color:t.t4 }}>Clients will see this on your profile with Google Maps and Waze links.</div>
            <button onClick={saveAddress} disabled={savingAddr} style={{ padding:"11px", background:"#2563EB", border:"none", borderRadius:12, color:"#fff", fontSize:13, fontWeight:800, cursor:"pointer", opacity:savingAddr?0.7:1 }}>
              {savingAddr?"Saving...":"Save Address"}
            </button>
          </div>
        ) : (
          <div style={{ fontSize:13, color:addressVal?t.t2:t.t4 }}>
            {addressVal ? <>📍 {addressVal}</> : "No address set — add one so clients can navigate to you"}
          </div>
        )}
      </div>

      {/* Bio + Experience */}
      <div className="rounded-2xl mb-4" style={{ background:t.card, padding:24, border:`1.5px solid ${t.border}` }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
          <div style={{ fontSize:13, fontWeight:800, color:t.t1, fontFamily:"'DM Sans',sans-serif" }}>Bio &amp; Experience</div>
          <button onClick={()=>setEditBio(!editBio)} style={{ fontSize:12, color:t.blueText, fontWeight:700, background:t.blueBg, border:"none", padding:"5px 12px", borderRadius:10, cursor:"pointer" }}>
            {editBio?"Cancel":"Edit"}
          </button>
        </div>
        {editBio ? (
          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
            <div>
              <div style={{ fontSize:11, fontWeight:700, color:t.t3, marginBottom:5 }}>YEARS CUTTING HAIR</div>
              <input type="number" min={0} max={60} value={yearsCuttingVal} onChange={e=>setYearsCuttingVal(e.target.value===""?"":parseInt(e.target.value,10))}
                placeholder="e.g. 7"
                style={{ width:"100%", padding:"11px 14px", borderRadius:12, border:`1.5px solid ${t.border2}`, background:t.section, color:t.inputText, fontSize:14, outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box" }}/>
            </div>
            <div>
              <div style={{ fontSize:11, fontWeight:700, color:t.t3, marginBottom:5 }}>BIO</div>
              <textarea value={bioVal} onChange={e=>setBioVal(e.target.value)} rows={4}
                placeholder={`e.g. "${yearsCuttingVal||7} years cutting hair in Toronto — specializing in skin fades and lineups. Clean cuts, sharp edges, every time."`}
                style={{ width:"100%", padding:"11px 14px", borderRadius:12, border:`1.5px solid ${t.border2}`, background:t.section, color:t.inputText, fontSize:13, outline:"none", fontFamily:"'DM Sans',sans-serif", resize:"vertical", boxSizing:"border-box" }}/>
              <div style={{ fontSize:11, color:t.t4, marginTop:4 }}>Tip: start with how long you've been cutting — it's the #1 thing clients look at</div>
            </div>
            <button onClick={async()=>{
              setSavingBio(true);
              const yc = yearsCuttingVal!==""&&yearsCuttingVal!=null?+yearsCuttingVal:null;
              await supabase.from("barbers").update({ bio:bioVal.trim()||null, years_cutting:yc }).eq("id",session.user.id);
              if(onUpdateBarberData) onUpdateBarberData(prev=>prev?{...prev,bio:bioVal.trim()||null,years_cutting:yc}:prev);
              setSavingBio(false); setEditBio(false);
            }} disabled={savingBio}
              style={{ padding:"11px", background:"#2563EB", border:"none", borderRadius:12, color:"#fff", fontSize:13, fontWeight:700, cursor:"pointer" }}>
              {savingBio?"Saving...":"Save Bio"}
            </button>
          </div>
        ) : (
          <div>
            {(myBarberData?.years_cutting > 0) && (
              <div style={{ display:"inline-flex", alignItems:"center", gap:6, marginBottom:8, padding:"3px 10px", background:"rgba(37,99,235,0.1)", border:"1px solid rgba(37,99,235,0.2)", borderRadius:20 }}>
                <span style={{ fontSize:12 }}>✂️</span>
                <span style={{ fontSize:12, fontWeight:700, color:t.blueText }}>{myBarberData.years_cutting} year{myBarberData.years_cutting!==1?"s":""} cutting hair</span>
              </div>
            )}
            <div style={{ fontSize:13, color:myBarberData?.bio?t.t2:t.t4, lineHeight:1.6 }}>
              {myBarberData?.bio || "No bio yet — tap Edit to tell clients about yourself."}
            </div>
          </div>
        )}
      </div>

      {/* Stats */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:16 }}>
        {[{label:"Today",value:`${completedToday.length} cuts`,color:"#22C55E",bg:"rgba(34,197,94,0.12)",emoji:"✂️"},{label:"Rating",value:`${myBarberData?.rating||"5.0"}`,sub:`${myBarberData?.review_count||0} reviews`,color:"#F59E0B",bg:"rgba(245,158,11,0.12)",emoji:"⭐"}].map(stat=>(
          <div key={stat.label} style={{ background:t.card, borderRadius:18, padding:18, border:`1.5px solid ${t.border}` }}>
            <div style={{ width:36, height:36, borderRadius:10, background:stat.bg, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, marginBottom:10 }}>{stat.emoji}</div>
            <div style={{ fontSize:20, fontWeight:900, color:stat.color, fontFamily:"'DM Sans',sans-serif" }}>{stat.value}</div>
            <div style={{ fontSize:11, color:t.t3, marginTop:4 }}>{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Share link */}
      {slug && (
        <div style={{ background:t.card, borderRadius:20, padding:20, border:`1.5px solid ${t.border}`, marginBottom:16 }}>
          <div style={{ fontSize:11, fontWeight:700, color:t.t4, letterSpacing:"0.09em", marginBottom:12, textTransform:"uppercase" }}>Your Booking Link</div>
          {/* URL pill */}
          <div style={{ display:"flex", alignItems:"center", background:t.section, borderRadius:14, padding:"14px 16px", border:`1px solid ${t.border}`, marginBottom:10, gap:12 }}>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:10, color:t.t4, letterSpacing:"0.05em", marginBottom:2 }}>letsgetfaded.com/</div>
              <div style={{ fontSize:16, fontWeight:800, color:t.t1, fontFamily:"'DM Sans',sans-serif", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{slug}</div>
            </div>
            <button onClick={()=>navigator.clipboard?.writeText(shareUrl).then(()=>window.dispatchEvent(new CustomEvent("faded-toast",{detail:{msg:"Link copied!",type:"success"}})))}
              style={{ flexShrink:0, padding:"9px 16px", background:"#2563EB", border:"none", borderRadius:10, color:"#fff", fontSize:13, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap" }}>
              Copy
            </button>
          </div>
          <button onClick={()=>navigator.share?navigator.share({title:`Book with me on FADED`,url:shareUrl}).catch(()=>{}):navigator.clipboard?.writeText(shareUrl).then(()=>window.dispatchEvent(new CustomEvent("faded-toast",{detail:{msg:"Link copied!",type:"success"}})))}
            style={{ width:"100%", padding:"13px", background:t.blueBg, border:`1px solid ${t.blueBorder}`, borderRadius:12, color:t.blueText, fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>
            Share link →
          </button>
        </div>
      )}

      {/* Google Review Link */}
      <div style={{ background:t.card, borderRadius:20, padding:20, border:`1.5px solid ${t.border}`, marginBottom:16 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
          <div>
            <div style={{ fontSize:13, fontWeight:800, color:t.t1, fontFamily:"'DM Sans',sans-serif" }}>⭐ Google Review Link</div>
            <div style={{ fontSize:11, color:t.t4, marginTop:2 }}>Clients see a button to leave you a Google review</div>
          </div>
          <button onClick={()=>setEditGoogleReview(v=>!v)} style={{ fontSize:12, color:t.blueText, fontWeight:700, background:t.blueBg, border:"none", padding:"5px 12px", borderRadius:10, cursor:"pointer" }}>
            {editGoogleReview?"Cancel":"Edit"}
          </button>
        </div>
        {!editGoogleReview && googleReviewUrl ? (
          <div style={{ fontSize:12, color:"#22C55E", fontWeight:600, wordBreak:"break-all" }}>✓ {googleReviewUrl}</div>
        ) : !editGoogleReview ? (
          <div style={{ fontSize:12, color:t.t4 }}>No link set — paste your Google review URL to let clients rate you.</div>
        ) : null}
        {editGoogleReview && (
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            <input
              value={googleReviewUrl}
              onChange={e=>setGoogleReviewUrl(e.target.value)}
              placeholder="https://g.page/r/YOUR_PLACE_ID/review"
              style={{ width:"100%", padding:"11px 14px", borderRadius:12, border:`1.5px solid ${t.border2}`, background:t.section, color:t.inputText, fontSize:13, outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box" }}
            />
            <div style={{ fontSize:11, color:t.t4 }}>To get your link: Google Maps → your business → Share → Copy link → paste here.</div>
            <button onClick={async()=>{ setSavingGoogleReview(true); const url=googleReviewUrl.trim(); await supabase.from("barbers").update({ google_review_url:url||null }).eq("id",session.user.id); if(onUpdateBarberData) onUpdateBarberData(prev=>prev?{...prev,google_review_url:url||null}:prev); setSavingGoogleReview(false); setEditGoogleReview(false); }}
              disabled={savingGoogleReview}
              style={{ padding:"11px", background:"#2563EB", border:"none", borderRadius:12, color:"#fff", fontSize:13, fontWeight:700, cursor:"pointer" }}>
              {savingGoogleReview?"Saving...":"Save Link"}
            </button>
          </div>
        )}
      </div>

      {/* Client Blast */}
      {(()=>{
        const clientList = [...new Map(bookings.filter(b=>b.client_name).map(b=>[b.client_phone||b.client_name,{name:b.client_name,phone:b.client_phone}])).values()];
        return (
          <div style={{ background:t.card, borderRadius:20, padding:20, border:`1.5px solid ${t.border}`, marginBottom:16 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:blastOpen?12:0 }}>
              <div>
                <div style={{ fontSize:13, fontWeight:800, color:t.t1, fontFamily:"'DM Sans',sans-serif" }}>📣 Message All Clients</div>
                <div style={{ fontSize:11, color:t.t4, marginTop:2 }}>{clientList.length} client{clientList.length!==1?"s":""} from booking history</div>
              </div>
              <button onClick={()=>setBlastOpen(v=>!v)} style={{ padding:"6px 14px", background:blastOpen?t.section:"#2563EB", border:`1px solid ${blastOpen?t.border:"#2563EB"}`, borderRadius:10, color:blastOpen?t.t3:"#fff", fontSize:12, fontWeight:700, cursor:"pointer" }}>{blastOpen?"Close":"Blast"}</button>
            </div>
            {blastOpen && (
              <div>
                <div style={{ fontSize:11, color:t.t4, marginBottom:6 }}>Compose · copy contacts to paste into SMS or WhatsApp</div>
                <textarea value={blastMsg} onChange={e=>setBlastMsg(e.target.value)} rows={3}
                  style={{ width:"100%", padding:"10px 12px", borderRadius:12, border:`1.5px solid ${t.border2}`, background:t.section, color:t.inputText, fontSize:12, resize:"none", outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box", marginBottom:8 }}/>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:12 }}>
                  <button onClick={()=>navigator.clipboard?.writeText(blastMsg).then(()=>window.dispatchEvent(new CustomEvent("faded-toast",{detail:{msg:"✅ Message copied!",type:"success"}})))} style={{ padding:"9px", background:"#2563EB", border:"none", borderRadius:10, color:"#fff", fontSize:12, fontWeight:700, cursor:"pointer" }}>📋 Copy Message</button>
                  <button onClick={()=>{ const nums=clientList.filter(c=>c.phone).map(c=>c.phone).join(", "); navigator.clipboard?.writeText(nums); }} style={{ padding:"9px", background:t.blueBg, border:`1px solid ${t.blueBorder}`, borderRadius:10, color:t.blueText, fontSize:12, fontWeight:700, cursor:"pointer" }}>📱 Copy Numbers</button>
                </div>
                {clientList.length > 0 ? (
                  <div style={{ maxHeight:220, overflowY:"auto" }}>
                    {clientList.map((c,i)=>(
                      <div key={i} style={{ display:"flex", justifyContent:"space-between", padding:"7px 0", borderBottom:`1px solid ${t.divider}`, fontSize:12 }}>
                        <span style={{ color:t.t1, fontWeight:600 }}>{c.name}</span>
                        <span style={{ color:t.t4 }}>{c.phone||"No phone"}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ textAlign:"center", padding:"12px 0", color:t.t4, fontSize:12 }}>No clients in booking history yet</div>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* Photos — status tab only */}
      <div style={{ background:t.card, borderRadius:20, padding:20, border:`1.5px solid ${t.border}`, marginBottom:16 }}>
        <div style={{ fontSize:13, fontWeight:800, color:t.t1, fontFamily:"'DM Sans',sans-serif", marginBottom:14 }}>Portfolio</div>
        <div style={{ fontSize:11, color:t.t4, marginBottom:12 }}>Tap your photo in the header to change your profile picture.</div>
        <div style={{ fontSize:12, fontWeight:700, color:t.t3, marginBottom:8 }}>Work Photos ({portfolio.length}/6)</div>
        {/* FIX 26: upload error + loading state */}
        {portfolioErr && <div style={{ fontSize:12, color:"#EF4444", marginBottom:8, padding:"8px 12px", background:"rgba(220,38,38,0.08)", borderRadius:10 }}>{portfolioErr}</div>}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:6 }}>
          {Array.from({length:6}).map((_,i)=>{
            const url = portfolio[i];
            if (url) return <div key={i} style={{ aspectRatio:"1", borderRadius:10, overflow:"hidden" }}><img src={url} alt={`p${i}`} style={{ width:"100%",height:"100%",objectFit:"cover" }}/></div>;
            if (i===portfolio.length) return (
              <div key={i} onClick={()=>!portfolioUploading&&portRef.current.click()} style={{ aspectRatio:"1", borderRadius:10, border:`2px dashed ${t.blueBorder}`, background:t.blueBg, display:"flex", alignItems:"center", justifyContent:"center", cursor:portfolioUploading?"default":"pointer", flexDirection:"column", gap:2, opacity:portfolioUploading?0.6:1 }}>
                <span style={{ fontSize:portfolioUploading?14:20 }}>{portfolioUploading?"⟳":"➕"}</span>
                <span style={{ fontSize:11, color:t.t4 }}>{portfolioUploading?"Uploading...":"Add"}</span>
              </div>
            );
            return <div key={i} style={{ aspectRatio:"1", borderRadius:10, background:t.section, border:`1px solid ${t.border}` }}/>;
          })}
        </div>
        <input ref={portRef} type="file" accept="image/*" style={{ display:"none" }} onChange={e=>{ uploadPortfolio(e.target.files[0]); e.target.value=""; }}/>
      </div>

      </motion.div>}{/* end status tab */}

      {/* ── Referral modal — shown after declining a booking ── */}
      {referralModal && (
        <div style={{ position:"fixed", inset:0, zIndex:9100, display:"flex", flexDirection:"column", justifyContent:"flex-end" }}>
          <div onClick={closeReferralModal} style={{ position:"absolute", inset:0, background:"rgba(0,0,0,0.65)", backdropFilter:"blur(4px)" }}/>
          <div style={{ position:"relative", background:"#111", borderRadius:"24px 24px 0 0", padding:"0 0 max(32px,env(safe-area-inset-bottom,20px)) 0", maxHeight:"85vh", display:"flex", flexDirection:"column" }}>
            {/* Handle */}
            <div style={{ display:"flex", justifyContent:"center", paddingTop:12, paddingBottom:4, flexShrink:0 }}>
              <div style={{ width:36, height:4, borderRadius:2, background:"rgba(245,245,240,0.15)" }}/>
            </div>

            {!referralSent ? (<>
              {/* Header */}
              <div style={{ padding:"12px 20px 16px", flexShrink:0 }}>
                <div style={{ fontSize:18, fontWeight:900, color:"#F5F5F0", fontFamily:"'DM Sans',sans-serif", marginBottom:4 }}>Refer a Barber</div>
                <div style={{ fontSize:13, color:"rgba(245,245,240,0.45)" }}>
                  Booking declined for <span style={{ color:"#F5F5F0", fontWeight:700 }}>{referralModal.clientName}</span>. Search for a barber on FADED to send them to.
                </div>
              </div>

              {/* Search input */}
              <div style={{ padding:"0 20px 12px", flexShrink:0 }}>
                <div style={{ position:"relative" }}>
                  <svg style={{ position:"absolute", left:14, top:"50%", transform:"translateY(-50%)", pointerEvents:"none" }} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(245,245,240,0.4)" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                  <input
                    autoFocus
                    value={referralQuery}
                    onChange={e=>{ setReferralQuery(e.target.value); searchReferralBarbers(e.target.value); }}
                    placeholder="Search by name or @handle…"
                    style={{ width:"100%", padding:"13px 14px 13px 40px", borderRadius:14, border:"1.5px solid rgba(245,245,240,0.12)", background:"rgba(245,245,240,0.07)", color:"#F5F5F0", fontSize:15, fontWeight:600, outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box" }}
                  />
                  {referralSearching && <div style={{ position:"absolute", right:14, top:"50%", transform:"translateY(-50%)", width:16, height:16, borderRadius:"50%", border:"2px solid #2563EB", borderTopColor:"transparent", animation:"spin 0.7s linear infinite" }}/>}
                </div>
              </div>

              {/* Results / chosen barber */}
              <div style={{ overflowY:"auto", flex:1, padding:"0 20px" }}>
                {referralChosen ? (
                  <div style={{ background:"rgba(37,99,235,0.1)", border:"1.5px solid rgba(37,99,235,0.4)", borderRadius:16, padding:"14px 16px", marginBottom:12 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                      <div style={{ width:44, height:44, borderRadius:12, background:"rgba(37,99,235,0.2)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, fontWeight:900, color:"#93C5FD", flexShrink:0, overflow:"hidden" }}>
                        {referralChosen.avatar_url ? <img src={referralChosen.avatar_url} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }}/> : (referralChosen.profiles?.full_name||"B")[0].toUpperCase()}
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:15, fontWeight:800, color:"#F5F5F0" }}>{referralChosen.profiles?.full_name||referralChosen.handle}</div>
                        <div style={{ fontSize:12, color:"rgba(245,245,240,0.45)", marginTop:1 }}>@{referralChosen.handle||referralChosen.shareable_slug} · ★ {referralChosen.rating||"5.0"}</div>
                      </div>
                      <button onClick={()=>{ setReferralChosen(null); setReferralQuery(""); setReferralResults([]); }}
                        style={{ background:"none", border:"none", color:"rgba(245,245,240,0.35)", fontSize:20, cursor:"pointer", padding:4 }}>✕</button>
                    </div>
                    <div style={{ marginTop:12, padding:"10px 12px", background:"rgba(245,245,240,0.05)", borderRadius:10, fontSize:12, color:"rgba(245,245,240,0.5)", lineHeight:1.6 }}>
                      SMS preview: <span style={{ color:"rgba(245,245,240,0.8)" }}>
                        "Hey {referralModal.clientName}! I can't take you today but {referralChosen.profiles?.full_name||"this barber"} on FADED is great — book them here 💈"
                      </span>
                    </div>
                  </div>
                ) : referralResults.length > 0 ? (
                  <div style={{ display:"flex", flexDirection:"column", gap:6, marginBottom:12 }}>
                    {referralResults.map(b => (
                      <button key={b.id} onClick={()=>{ setReferralChosen(b); setReferralResults([]); }}
                        style={{ background:"rgba(245,245,240,0.05)", border:"1.5px solid rgba(245,245,240,0.1)", borderRadius:14, padding:"12px 14px", cursor:"pointer", display:"flex", alignItems:"center", gap:12, textAlign:"left", transition:"background 0.15s" }}>
                        <div style={{ width:40, height:40, borderRadius:10, background:"rgba(37,99,235,0.15)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, fontWeight:800, color:"#93C5FD", flexShrink:0, overflow:"hidden" }}>
                          {b.avatar_url ? <img src={b.avatar_url} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }}/> : (b.profiles?.full_name||"B")[0].toUpperCase()}
                        </div>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontSize:14, fontWeight:700, color:"#F5F5F0" }}>{b.profiles?.full_name||b.handle}</div>
                          <div style={{ fontSize:12, color:"rgba(245,245,240,0.4)", marginTop:1 }}>@{b.handle||b.shareable_slug} · ★ {b.rating||"5.0"} · {b.review_count||0} reviews</div>
                        </div>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(245,245,240,0.3)" strokeWidth="2" strokeLinecap="round"><path d="M9 18l6-6-6-6"/></svg>
                      </button>
                    ))}
                  </div>
                ) : referralQuery.trim() && !referralSearching ? (
                  <div style={{ textAlign:"center", padding:"24px 0", color:"rgba(245,245,240,0.35)", fontSize:13 }}>No barbers found for "{referralQuery}"</div>
                ) : !referralQuery.trim() ? (
                  <div style={{ textAlign:"center", padding:"24px 0", color:"rgba(245,245,240,0.25)", fontSize:13 }}>Start typing to search barbers on FADED</div>
                ) : null}
              </div>

              {/* Action buttons */}
              <div style={{ padding:"12px 20px 0", flexShrink:0, display:"flex", flexDirection:"column", gap:8 }}>
                <button
                  disabled={!referralChosen}
                  onClick={()=>{
                    if (!referralChosen) return;
                    const slug = referralChosen.shareable_slug || referralChosen.handle;
                    const barberName = referralChosen.profiles?.full_name || referralChosen.handle || "this barber";
                    const link = `${window.location.origin}?open_barber=${encodeURIComponent(slug)}`;
                    const msg = `Hey ${referralModal.clientName}! I can't take you today but ${barberName} on FADED is great — book them here 💈\n${link}`;
                    if (referralModal.clientPhone) {
                      window.open(`sms:${referralModal.clientPhone}?body=${encodeURIComponent(msg)}`);
                    } else {
                      navigator.clipboard?.writeText(msg);
                    }
                    setReferralSent(true);
                  }}
                  style={{ width:"100%", padding:"15px", background:referralChosen?"#2563EB":"rgba(245,245,240,0.08)", border:"none", borderRadius:14, color:referralChosen?"#fff":"rgba(245,245,240,0.25)", fontSize:15, fontWeight:800, cursor:referralChosen?"pointer":"default", fontFamily:"'DM Sans',sans-serif", transition:"all 0.2s" }}>
                  {referralModal.clientPhone ? "Send via SMS →" : "Copy Message →"}
                </button>
                <button onClick={closeReferralModal}
                  style={{ width:"100%", padding:"13px", background:"transparent", border:"1px solid rgba(245,245,240,0.1)", borderRadius:14, color:"rgba(245,245,240,0.4)", fontSize:14, fontWeight:600, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>
                  Skip — Don't Refer
                </button>
              </div>

            </>) : (
              /* Success state */
              <div style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"40px 24px", textAlign:"center" }}>
                <div style={{ fontSize:48, marginBottom:16 }}>💈</div>
                <div style={{ fontSize:20, fontWeight:900, color:"#F5F5F0", fontFamily:"'DM Sans',sans-serif", marginBottom:8 }}>Referral Sent!</div>
                <div style={{ fontSize:14, color:"rgba(245,245,240,0.45)", marginBottom:32, lineHeight:1.6 }}>
                  {referralModal.clientName} has been sent to{" "}
                  <span style={{ color:"#F5F5F0", fontWeight:700 }}>{referralChosen?.profiles?.full_name || referralChosen?.handle}</span>'s profile.
                </div>
                <button onClick={closeReferralModal}
                  style={{ padding:"14px 40px", background:"#2563EB", border:"none", borderRadius:14, color:"#fff", fontSize:15, fontWeight:800, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>
                  Done
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Section dropdown menu — fixed above all content, backdrop closes on outside tap ── */}
      {dashDropOpen && dropRect && (
        <>
          <div onClick={()=>setDashDropOpen(false)} style={{ position:"fixed", inset:0, zIndex:8998, background:"transparent" }}/>
          <div style={{ position:"fixed", top:dropRect.bottom+6, left:dropRect.left, width:dropRect.width, background:t.card, border:`1.5px solid ${t.border}`, borderRadius:14, zIndex:8999, overflow:"hidden", boxShadow:"0 8px 40px rgba(0,0,0,0.35)" }}>
            {[{id:"status",icon:"📊",label:"Status"},{id:"availability",icon:"🗓️",label:"Availability"},{id:"services",icon:"✂️",label:"Services"},{id:"income",icon:"💰",label:"Income"},{id:"reviews",icon:"⭐",label:"Reviews"},{id:"shop",icon:"🏪",label:"Shop"},{id:"ai",icon:razorMode?"🎙️":"🤖",label:"AI Assistant"}].map((item,i,arr)=>(
              <button key={item.id} onClick={()=>{ setDashTab(item.id); setDashDropOpen(false); }}
                style={{ width:"100%", display:"flex", alignItems:"center", gap:12, padding:"13px 16px", background:dashTab===item.id?t.section:"none", border:"none", borderBottom:i<arr.length-1?`1px solid ${t.divider}`:"none", cursor:"pointer", outline:"none", textAlign:"left" }}>
                <span style={{ fontSize:17 }}>{item.icon}</span>
                <span style={{ fontSize:14, fontWeight:dashTab===item.id?700:500, color:dashTab===item.id?t.t1:t.t3, fontFamily:"'DM Sans',sans-serif" }}>{item.label}</span>
                {dashTab===item.id && <svg aria-hidden="true" focusable="false"width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft:"auto" }}><polyline points="20 6 9 17 4 12"/></svg>}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─── MAP DISCOVERY ────────────────────────────────────────────────────────────
function MapPanHandler({ onMove }) {
  useMapEvents({ move: onMove, zoom: onMove });
  return null;
}

function RecenterOnGPS({ lat, lng }) {
  const map = useMap();
  useEffect(() => {
    if (lat && lng) map.setView([lat, lng], 13, { animate: true, duration: 0.8 });
  }, [lat, lng]); // eslint-disable-line
  return null;
}

function UserDot({ lat, lng }) {
  const icon = useMemo(() => L.divIcon({
    className: "",
    html: `<div style="width:18px;height:18px;border-radius:50%;background:#2563EB;border:3px solid #fff;box-shadow:0 0 0 5px rgba(37,99,235,0.28),0 2px 8px rgba(0,0,0,0.4)"></div>`,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
  }), []);
  return <Marker position={[lat, lng]} icon={icon}/>;
}

const BarberMapMarker = React.memo(function BarberMapMarker({ barber, isSelected, onPress }) {
  const isLive = barber.status === "available";
  const isSoon = barber.status === "soon";
  // If next_available is set to tomorrow or later, barber is fully booked today
  const _naDate = barber.next_available ? new Date(barber.next_available) : null;
  const _todayEnd = new Date(); _todayEnd.setHours(23,59,59,999);
  const isFullyBookedToday = isLive && _naDate && _naDate > _todayEnd;
  const dotColor = isFullyBookedToday ? "#6B7280" : isLive ? "#22C55E" : isSoon ? "#F59E0B" : "#888";
  const firstName = (barber.full_name || "Barber").split(" ")[0].toUpperCase();
  const price = fmt(barber.price_min);

  const isOffline = barber.status === "offline";
  const pinOpacity = isOffline && !isSelected ? "0.45" : "1";
  const pinBg = isSelected ? "#2563EB" : isOffline ? "#1a1a1a" : "#0A0A0A";
  const pinBorder = isSelected ? "#2563EB" : isOffline ? "rgba(245,245,240,0.08)" : "rgba(245,245,240,0.22)";
  const icon = useMemo(() => L.divIcon({
    className: "",
    html: `<div style="display:flex;flex-direction:column;align-items:center;cursor:pointer;opacity:${pinOpacity}">
      <div style="
        background:${pinBg};
        border:1.5px solid ${pinBorder};
        border-radius:12px;padding:6px 10px 6px 8px;
        display:flex;align-items:center;gap:7px;
        box-shadow:${isSelected ? "0 8px 24px rgba(37,99,235,0.5),0 0 0 4px rgba(37,99,235,0.18)" : "0 4px 14px rgba(0,0,0,0.75)"};
        white-space:nowrap;
        transform:${isSelected ? "scale(1.08)" : "scale(1)"};
        transition:transform 220ms cubic-bezier(.2,.8,.2,1);
      ">
        <div style="position:relative;display:flex;align-items:center;flex-shrink:0">
          <div style="width:8px;height:8px;border-radius:50%;background:${dotColor};${isLive ? `box-shadow:0 0 6px ${dotColor}` : ""}"></div>
        </div>
        <div>
          <div style="font-family:'Bebas Neue',sans-serif;font-size:13px;letter-spacing:0.05em;color:${isOffline ? "rgba(245,245,240,0.5)" : "#F5F5F0"};line-height:1">${firstName}</div>
          <div style="font-size:8.5px;letter-spacing:0.04em;color:${isSelected ? "rgba(245,245,240,0.85)" : "rgba(245,245,240,0.4)"};margin-top:1px;font-family:monospace">${isOffline ? "offline" : price+"+"}</div>
        </div>
      </div>
      <div style="width:0;height:0;border-left:5px solid transparent;border-right:5px solid transparent;border-top:6px solid ${isSelected ? "#2563EB" : pinBg};margin-top:-1px"></div>
    </div>`,
    iconSize: [90, 48],
    iconAnchor: [45, 48],
  }), [barber.status, barber.price_min, barber.full_name, isSelected, pinOpacity, pinBg, pinBorder]); // eslint-disable-line

  const handlers = useMemo(() => ({ click: onPress }), [onPress]);
  return <Marker position={[barber.lat, barber.lng]} icon={icon} eventHandlers={handlers}/>;
});

// ─── GET FADED NOW — instant broadcast + barber response flow ────────────────
function InstantRequestScreen({ session, gps, onClose, t }) {
  const [phase, setPhase]         = useState("idle");   // idle | waiting | matched
  const [requestId, setRequestId] = useState(null);
  const [responses, setResponses] = useState([]);
  const [timeLeft, setTimeLeft]   = useState(180);
  const [matched, setMatched]     = useState(null);
  const [busy, setBusy]           = useState(false);
  const [broadcastErr, setBroadcastErr] = useState("");

  const clientName = session?.user?.user_metadata?.full_name || session?.user?.email?.split("@")[0] || "Client";

  // Broadcast to nearby barbers
  const broadcast = async () => {
    if (!gps?.lat || !gps?.lng) return;
    const uid = session?.user?.id;
    if (!uid) { setBroadcastErr("Sign in to use Get Faded Now."); return; }
    setBusy(true);
    try {
      console.log("[FADED] broadcast: submitting", { lat: gps.lat, lng: gps.lng, radius_km: 15 });
      const { data, error } = await supabase.from("instant_requests").insert({
        client_id:   uid,
        client_name: clientName,
        client_lat:  gps.lat,
        client_lng:  gps.lng,
        radius_km:   15,
        status:      "open",
      }).select().single();
      setBusy(false);
      if (error || !data) { console.error("[FADED] broadcast: insert failed", error); setBroadcastErr("Couldn't broadcast — check your connection and try again."); return; }
      console.log("[FADED] broadcast: inserted request", data.id);
      setBroadcastErr("");
      setRequestId(data.id);
      setPhase("waiting");
      setTimeLeft(180);
      // Push-notify barbers via web push (works for PWA/browser users; native iOS uses realtime subscription below)
      supabase.functions.invoke("notify-instant-request", {
        body: { client_lat: gps.lat, client_lng: gps.lng, client_name: clientName, radius_km: 15, request_id: data.id },
      }).then(({ data: fnData, error: fnErr }) => {
        if (fnErr) console.error("[FADED] broadcast: edge fn error", fnErr);
        else console.log("[FADED] broadcast: edge fn result", fnData);
      }).catch(e => console.error("[FADED] broadcast: edge fn threw", e));
    } catch (e) {
      setBusy(false);
      console.error("[FADED] broadcast: threw", e);
      setBroadcastErr("Couldn't broadcast — check your connection and try again.");
    }
  };

  // Realtime: listen for barber responses — enrich each with barber profile data
  useEffect(() => {
    if (!requestId) return;
    const ch = supabase.channel("instant-resp-" + requestId)
      .on("postgres_changes", { event:"INSERT", schema:"public", table:"instant_responses", filter:`request_id=eq.${requestId}` }, async payload => {
        const resp = payload.new;
        const { data: extra } = await supabase.from("barbers")
          .select("specialty, portfolio_urls, bio, price_min, price_max, accent_color")
          .eq("id", resp.barber_id).single();
        setResponses(prev => [...prev, { ...resp, _extra: extra || {} }]);
      })
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, [requestId]);

  // Countdown timer
  useEffect(() => {
    if (phase !== "waiting") return;
    const iv = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(iv);
          supabase.from("instant_requests").update({ status:"expired" }).eq("id", requestId).then(() => {});
          setPhase("idle");
          setRequestId(null);
          setResponses([]);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(iv);
  }, [phase, requestId]);

  const acceptBarber = async (resp) => {
    setBusy(true);
    await supabase.from("instant_requests").update({ status:"matched", matched_barber_id: resp.barber_id, matched_at: new Date().toISOString() }).eq("id", requestId);
    await supabase.from("instant_responses").update({ status:"accepted" }).eq("id", resp.id);
    await supabase.from("instant_responses").update({ status:"dismissed" }).eq("request_id", requestId).neq("id", resp.id);
    setMatched(resp);
    setPhase("matched");
    setBusy(false);
  };

  const cancel = async () => {
    if (requestId) await supabase.from("instant_requests").update({ status:"cancelled" }).eq("id", requestId);
    onClose();
  };

  const mins = Math.floor(timeLeft / 60);
  const secs = String(timeLeft % 60).padStart(2, "0");
  const a = "#2563EB";

  return (
    <div style={{ position:"fixed", inset:0, zIndex:300, background:t.bodyBg, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"32px 24px" }}>

      {/* Close / cancel */}
      <button onClick={cancel} style={{ position:"absolute", top:"max(52px,calc(var(--sat, 0px) + 16px))", left:20, background:t.section, border:`1px solid ${t.border}`, borderRadius:10, width:40, height:40, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={t.t3} strokeWidth="2.5" strokeLinecap="round"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
      </button>

      {phase === "idle" && (
        <>
          {/* Big FADED shield */}
          <svg width="90" height="98" viewBox="0 0 24 28" fill="none" style={{ marginBottom:24, filter:"drop-shadow(0 0 24px rgba(37,99,235,0.6))" }}>
            <path d="M3,3 L21,3 L24,10.5 L12,26 L0,10.5 Z" fill="#2563EB" stroke="rgba(255,255,255,0.22)" strokeWidth="0.7" strokeLinejoin="round"/>
            <path d="M5,4.5 L19,4.5 L21.5,10.8 L12,23.5 L2.5,10.8 Z" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="0.5" strokeLinejoin="round"/>
            <text x="12" y="15.2" textAnchor="middle" fontFamily="'DM Sans',Arial Black,sans-serif" fontWeight="900" fontSize="5.8" letterSpacing="-0.2">
              <tspan fill="white">FAD</tspan><tspan fill="#bfdbfe">ED</tspan>
            </text>
          </svg>
          <div style={{ fontSize:28, fontWeight:900, color:t.t1, fontFamily:"'Bebas Neue',sans-serif", letterSpacing:"0.06em", marginBottom:8, textAlign:"center" }}>GET FADED NOW</div>
          <div style={{ fontSize:14, color:t.t2, textAlign:"center", lineHeight:1.7, marginBottom:32, maxWidth:300 }}>
            Broadcast to available barbers near you. They respond, you pick — fresh cut in minutes.
          </div>
          {!gps?.lat && (
            <div style={{ background:"rgba(220,38,38,0.08)", border:"1px solid rgba(220,38,38,0.2)", borderRadius:12, padding:"10px 16px", marginBottom:20, fontSize:13, color:"#DC2626", textAlign:"center" }}>
              📍 Location required — allow GPS access and try again.
            </div>
          )}
          {broadcastErr && (
            <div style={{ background:"rgba(220,38,38,0.08)", border:"1px solid rgba(220,38,38,0.25)", borderRadius:10, padding:"10px 16px", marginBottom:16, fontSize:13, color:"#EF4444", textAlign:"center", maxWidth:340, width:"100%" }}>
              {broadcastErr}
            </div>
          )}
          <button onClick={broadcast} disabled={!gps?.lat || busy}
            style={{ width:"100%", maxWidth:340, padding:"17px 0", background:gps?.lat?"#2563EB":"#374151", border:"none", borderRadius:16, color:"#fff", fontSize:16, fontWeight:900, cursor:gps?.lat?"pointer":"default", fontFamily:"'DM Sans',sans-serif", letterSpacing:"0.04em", boxShadow:gps?.lat?"0 4px 24px rgba(37,99,235,0.4)":"none" }}>
            {busy ? "Broadcasting..." : "⚡ Broadcast to Nearby Barbers"}
          </button>
          <div style={{ fontSize:11, color:t.t4, marginTop:12, textAlign:"center" }}>Reaches barbers within 10 km · Request expires in 3 min</div>
        </>
      )}

      {phase === "waiting" && (
        <div style={{ width:"100%", maxWidth:420, display:"flex", flexDirection:"column", alignItems:"center" }}>
          {/* Timer ring */}
          <div style={{ position:"relative", width:100, height:100, marginBottom:24 }}>
            <svg width="100" height="100" style={{ transform:"rotate(-90deg)" }}>
              <circle cx="50" cy="50" r="44" fill="none" stroke={t.border} strokeWidth="6"/>
              <circle cx="50" cy="50" r="44" fill="none" stroke={a} strokeWidth="6"
                strokeDasharray={`${2*Math.PI*44}`}
                strokeDashoffset={`${2*Math.PI*44*(1-timeLeft/180)}`}
                strokeLinecap="round" style={{ transition:"stroke-dashoffset 1s linear" }}/>
            </svg>
            <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}>
              <div style={{ fontSize:22, fontWeight:900, color:t.t1, fontFamily:"'DM Sans',sans-serif" }}>{mins}:{secs}</div>
              <div style={{ fontSize:10, color:t.t4, fontWeight:600 }}>left</div>
            </div>
          </div>

          <div style={{ fontSize:responses.length > 0 ? 20 : 16, fontWeight:900, color:t.t1, marginBottom:4, fontFamily:"'DM Sans',sans-serif", letterSpacing:responses.length > 0 ? "0.02em" : "normal", transition:"all 0.3s" }}>
            {responses.length === 0 ? "Pinging nearby barbers..." : `${responses.length} barber${responses.length>1?"s":""} ready for you`}
          </div>
          <div style={{ fontSize:12, color:t.t4, marginBottom:responses.length > 0 ? 20 : 24 }}>
            {responses.length === 0 ? "Sit tight — available barbers near you are being notified" : "Review profiles below and choose your barber"}
          </div>

          {/* Incoming barber cards — rich profile */}
          {responses.length > 0 && (
            <div style={{ width:"100%", display:"flex", flexDirection:"column", gap:14, marginBottom:20 }}>
              {responses.map((resp, idx) => {
                const accent = resp._extra?.accent_color || a;
                const firstName = (resp.barber_name || "Barber").split(" ")[0];
                const portfolio = resp._extra?.portfolio_urls || [];
                const specs = resp._extra?.specialty || [];
                return (
                  <div key={resp.id} style={{ background:t.card, border:`1.5px solid ${accent}44`, borderRadius:22, overflow:"hidden", boxShadow:`0 4px 32px rgba(0,0,0,0.4), 0 0 0 1px ${accent}18`, animation:`slideUp 0.35s ease ${idx*0.08}s both` }}>

                    {/* Header bar with accent glow strip */}
                    <div style={{ height:3, background:`linear-gradient(90deg,${accent},${accent}88,transparent)` }}/>

                    {/* Profile row */}
                    <div style={{ display:"flex", alignItems:"center", gap:14, padding:"16px 18px 12px" }}>
                      <div style={{ position:"relative", flexShrink:0 }}>
                        {resp.barber_photo
                          ? <img src={resp.barber_photo} alt={resp.barber_name} style={{ width:62, height:62, borderRadius:16, objectFit:"cover", border:`2px solid ${accent}55` }}/>
                          : <div style={{ width:62, height:62, borderRadius:16, background:`linear-gradient(135deg,${accent}33,${accent}11)`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:28, border:`2px solid ${accent}44` }}>✂️</div>
                        }
                        <div style={{ position:"absolute", bottom:-4, right:-4, width:16, height:16, borderRadius:"50%", background:"#22C55E", border:`2px solid ${t.card}` }}/>
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:17, fontWeight:900, color:t.t1, fontFamily:"'DM Sans',sans-serif", letterSpacing:"-0.01em" }}>{resp.barber_name || "Barber"}</div>
                        <div style={{ fontSize:12, color:t.t3, marginTop:3, display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
                          {resp.est_distance_km != null && (
                            <span>📍 {resp.est_distance_km < 1 ? Math.round(resp.est_distance_km*1000)+"m" : resp.est_distance_km.toFixed(1)+"km"}</span>
                          )}
                          {resp.barber_rating && <span>⭐ {resp.barber_rating}</span>}
                          {resp._extra?.price_min && <span style={{ color:accent, fontWeight:700 }}>from ${resp._extra.price_min}</span>}
                        </div>
                        {specs.length > 0 && (
                          <div style={{ display:"flex", flexWrap:"wrap", gap:4, marginTop:7 }}>
                            {specs.slice(0,4).map(s => (
                              <span key={s} style={{ fontSize:10, padding:"3px 9px", borderRadius:20, background:`${accent}18`, color:accent, fontWeight:700, letterSpacing:"0.02em", border:`1px solid ${accent}30` }}>{s}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      {/* Live badge */}
                      <div style={{ flexShrink:0, padding:"4px 10px", borderRadius:20, background:"rgba(34,197,94,0.12)", border:"1px solid rgba(34,197,94,0.3)", fontSize:10, fontWeight:800, color:"#22C55E", letterSpacing:"0.06em" }}>LIVE</div>
                    </div>

                    {/* Bio snippet */}
                    {resp._extra?.bio && (
                      <div style={{ margin:"0 18px 10px", fontSize:12, color:t.t3, lineHeight:1.5, fontStyle:"italic" }}>"{resp._extra.bio.length > 90 ? resp._extra.bio.slice(0,90)+"…" : resp._extra.bio}"</div>
                    )}

                    {/* Portfolio strip */}
                    {portfolio.length > 0 && (
                      <div style={{ padding:"0 18px 14px" }}>
                        <div style={{ fontSize:10, fontWeight:700, color:t.t4, letterSpacing:1.4, marginBottom:7 }}>PORTFOLIO</div>
                        <div style={{ display:"flex", gap:6, overflowX:"auto", paddingBottom:2, scrollbarWidth:"none" }}>
                          {portfolio.slice(0,6).map((url,i) => (
                            <img key={i} src={url} alt={`cut ${i+1}`} style={{ width:76, height:76, borderRadius:12, objectFit:"cover", flexShrink:0, border:`1px solid ${t.border}` }}/>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Book CTA */}
                    <div style={{ padding:"0 18px 18px" }}>
                      <button onClick={() => acceptBarber(resp)} disabled={busy}
                        style={{ width:"100%", padding:"14px 0", background:`linear-gradient(135deg,${accent},${accent}cc)`, border:"none", borderRadius:14, color:"#fff", fontSize:15, fontWeight:900, cursor:busy?"default":"pointer", fontFamily:"'DM Sans',sans-serif", letterSpacing:"0.03em", boxShadow:`0 4px 20px ${accent}55` }}>
                        {busy ? "Booking…" : `Book ${firstName} ✓`}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {responses.length === 0 && (
            <div style={{ display:"flex", gap:8 }}>
              {[0,1,2].map(i => (
                <div key={i} style={{ width:10, height:10, borderRadius:"50%", background:a, animation:`bounce 1.2s ease infinite ${i*0.2}s`, opacity:0.7 }}/>
              ))}
            </div>
          )}

          <button onClick={cancel} style={{ marginTop:20, background:"none", border:`1px solid ${t.border}`, borderRadius:12, padding:"10px 24px", color:t.t3, fontSize:13, fontWeight:700, cursor:"pointer" }}>
            Cancel Request
          </button>
        </div>
      )}

      {phase === "matched" && matched && (
        <div style={{ textAlign:"center", maxWidth:360, width:"100%", animation:"slideUp 0.4s ease" }}>
          {/* Glowing checkmark */}
          <div style={{ width:88, height:88, borderRadius:"50%", background:"linear-gradient(135deg,#22C55E22,#22C55E44)", border:"2px solid #22C55E55", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 20px", boxShadow:"0 0 40px rgba(34,197,94,0.35)" }}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
          </div>
          <div style={{ fontSize:32, fontWeight:900, color:t.t1, fontFamily:"'Bebas Neue',sans-serif", letterSpacing:"0.08em", marginBottom:6 }}>YOU'RE BOOKED!</div>
          <div style={{ fontSize:13, color:t.t4, letterSpacing:2, fontWeight:700, marginBottom:24 }}>GET READY FOR YOUR CUT</div>

          {/* Matched barber card */}
          <div style={{ background:t.card, border:`1.5px solid ${(matched._extra?.accent_color||a)}44`, borderRadius:20, overflow:"hidden", marginBottom:24, textAlign:"left", boxShadow:"0 8px 40px rgba(0,0,0,0.4)" }}>
            <div style={{ height:3, background:`linear-gradient(90deg,${matched._extra?.accent_color||a},${(matched._extra?.accent_color||a)}44,transparent)` }}/>
            <div style={{ display:"flex", alignItems:"center", gap:14, padding:"16px 18px" }}>
              {matched.barber_photo
                ? <img src={matched.barber_photo} alt={matched.barber_name} style={{ width:60, height:60, borderRadius:16, objectFit:"cover", border:`2px solid ${(matched._extra?.accent_color||a)}55` }}/>
                : <div style={{ width:60, height:60, borderRadius:16, background:`${a}22`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:26 }}>✂️</div>
              }
              <div style={{ flex:1 }}>
                <div style={{ fontSize:17, fontWeight:900, color:t.t1 }}>{matched.barber_name}</div>
                <div style={{ fontSize:12, color:t.t3, marginTop:3 }}>
                  {matched.est_distance_km != null && `📍 ${matched.est_distance_km < 1 ? Math.round(matched.est_distance_km*1000)+"m" : matched.est_distance_km.toFixed(1)+"km away"}`}
                  {matched.barber_rating ? `  ⭐ ${matched.barber_rating}` : ""}
                </div>
                {(matched._extra?.specialty||[]).length > 0 && (
                  <div style={{ display:"flex", flexWrap:"wrap", gap:4, marginTop:6 }}>
                    {matched._extra.specialty.slice(0,3).map(s => (
                      <span key={s} style={{ fontSize:10, padding:"2px 8px", borderRadius:20, background:`${matched._extra?.accent_color||a}18`, color:matched._extra?.accent_color||a, fontWeight:700, border:`1px solid ${(matched._extra?.accent_color||a)}30` }}>{s}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div style={{ padding:"0 18px 18px", fontSize:13, color:t.t2, lineHeight:1.6, textAlign:"center" }}>
              Head over now — <strong style={{ color:t.t1 }}>{matched.barber_name?.split(" ")[0]}</strong> is ready for you. 💈
            </div>
          </div>

          <button onClick={onClose} style={{ width:"100%", padding:"16px 0", background:`linear-gradient(135deg,${a},${a}cc)`, border:"none", borderRadius:16, color:"#fff", fontSize:15, fontWeight:900, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", boxShadow:`0 4px 24px ${a}55`, letterSpacing:"0.04em" }}>
            Let's Go ✂️
          </button>
        </div>
      )}

      <style>{`@keyframes bounce{0%,100%{transform:translateY(0)}50%{transform:translateY(-5px)}} @keyframes slideUp{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </div>
  );
}

// ─── BARBER: incoming client ping card ───────────────────────────────────────
function ClientPingCard({ ping, myBarberData, session, onDismiss, t }) {
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed]   = useState(false);
  const a = myBarberData?.accent_color || "#2563EB";

  const claim = async () => {
    setClaiming(true);
    const { error } = await supabase.from("instant_responses").insert({
      request_id:      ping.id,
      barber_id:       session.user.id,
      barber_name:     myBarberData?.full_name || "Barber",
      barber_photo:    myBarberData?.avatar_url || null,
      barber_rating:   myBarberData?.avg_rating || null,
      est_distance_km: ping._distKm ? +ping._distKm.toFixed(1) : null,
      status:          "pending",
    });
    setClaiming(false);
    if (!error) { setClaimed(true); setTimeout(onDismiss, 3000); }
  };

  return (
    <div style={{ position:"fixed", top:"max(64px,calc(var(--sat, 0px) + 16px))", left:16, right:16, zIndex:500, background:t.card, border:`1.5px solid ${a}55`, borderRadius:22, overflow:"hidden", boxShadow:"0 12px 48px rgba(0,0,0,0.6), 0 0 0 1px rgba(37,99,235,0.15)", backdropFilter:"blur(20px)", WebkitBackdropFilter:"blur(20px)" }}>
      {/* Accent top strip */}
      <div style={{ height:3, background:`linear-gradient(90deg,${a},${a}88,transparent)` }}/>
      <div style={{ padding:"14px 16px" }}>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          {/* Pulsing icon */}
          <div style={{ position:"relative", flexShrink:0 }}>
            <div style={{ width:46, height:46, borderRadius:14, background:`${a}22`, border:`1.5px solid ${a}44`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:22 }}>⚡</div>
            <div style={{ position:"absolute", inset:-3, borderRadius:17, border:`1.5px solid ${a}33`, animation:"ping 1.5s ease infinite" }}/>
          </div>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:14, fontWeight:900, color:t.t1, letterSpacing:"-0.01em" }}>Client wants a cut NOW</div>
            <div style={{ fontSize:12, color:t.t3, marginTop:2 }}>
              {ping.client_name || "Client"} · {ping._distKm < 1 ? Math.round(ping._distKm*1000)+"m" : ping._distKm?.toFixed(1)+"km"} away
            </div>
          </div>
          <button onClick={onDismiss} style={{ background:"none", border:"none", color:t.t4, fontSize:20, cursor:"pointer", padding:4, lineHeight:1 }}>×</button>
        </div>
        {claimed ? (
          <div style={{ marginTop:12, textAlign:"center", padding:"10px 14px", background:"rgba(34,197,94,0.08)", border:"1px solid rgba(34,197,94,0.25)", borderRadius:12, color:"#22C55E", fontWeight:700, fontSize:13 }}>✓ Response sent — waiting for client to choose you</div>
        ) : (
          <div style={{ display:"flex", gap:8, marginTop:12 }}>
            <button onClick={claim} disabled={claiming}
              style={{ flex:1, padding:"13px 0", background:`linear-gradient(135deg,${a},${a}cc)`, border:"none", borderRadius:14, color:"#fff", fontSize:14, fontWeight:900, cursor:claiming?"default":"pointer", fontFamily:"'DM Sans',sans-serif", boxShadow:`0 4px 16px ${a}44`, letterSpacing:"0.02em" }}>
              {claiming ? "Sending…" : "✂️  I'll Take It"}
            </button>
            <button onClick={onDismiss}
              style={{ padding:"13px 18px", background:t.section, border:`1px solid ${t.border}`, borderRadius:14, color:t.t3, fontSize:13, fontWeight:700, cursor:"pointer" }}>
              Pass
            </button>
          </div>
        )}
      </div>
      <style>{`@keyframes ping{0%{opacity:0.6;transform:scale(1)}70%{opacity:0;transform:scale(1.25)}100%{opacity:0}}`}</style>
    </div>
  );
}

function MapDiscovery({ barbers, userLat, userLng, onBook, t, isDark, fullScreen, onListView, onLocate, gpsLoading, hideFade }) {
  const [selectedBarber, setSelectedBarber] = useState(null);
  const [mapMoved, setMapMoved] = useState(false);
  const [mapFilter, setMapFilter] = useState("all");
  const [mapSearch, setMapSearch] = useState("");

  const center = (userLat && userLng) ? [userLat, userLng] : [43.6532, -79.3832];

  const mappable = useMemo(() => {
    let result = barbers.filter(b => b.lat && b.lng);
    if (mapFilter === "live") result = result.filter(b => b.status === "available");
    if (mapFilter === "affordable") result = result.filter(b => (b.price_min || 0) < 60);
    if (mapFilter === "rated") result = result.filter(b => (b.rating || 0) >= 4.8 && (b.review_count || 0) >= 5);
    if (mapSearch) {
      const q = mapSearch.toLowerCase();
      result = result.filter(b => (b.full_name||"").toLowerCase().includes(q) || (b.handle||"").toLowerCase().includes(q));
    }
    return result;
  }, [barbers, mapFilter, mapSearch]);

  const handlePress = useCallback((b) => {
    setSelectedBarber(prev => prev?.id === b.id ? null : b);
    setMapMoved(false);
  }, []);

  const liveCount = barbers.filter(b => b.status === "available" && b.lat && b.lng).length;
  const totalOnMap = barbers.length;

  return (
    <div style={{ position:"relative", height:hideFade?"100%":fullScreen?"100dvh":"calc(100dvh - 224px)", minHeight:fullScreen?"100dvh":hideFade?"100%":440, borderRadius:fullScreen?0:16, overflow:"hidden", background:"#0A0A0A" }}>
      <style>{`
        .leaflet-container { background:#0A0A0A !important; }
        .leaflet-tile-pane { opacity:0.92; }
        .leaflet-control-attribution { display:none !important; }
        .leaflet-control-zoom { display:none !important; }
        .faded-map-preview { animation: slideUp 0.28s cubic-bezier(0.22,1,0.36,1) both; }
        @keyframes slideUp { from { transform:translateY(20px); opacity:0; } to { transform:translateY(0); opacity:1; } }
        .map-chip-scroll::-webkit-scrollbar { display:none; }
        @keyframes spin { to { transform:rotate(360deg); } }
      `}</style>

      <MapContainer
        center={center}
        zoom={13}
        style={{ height:"100%", width:"100%", zIndex:0 }}
        zoomControl={false}
        attributionControl={false}
      >
        <TileLayer url={`https://api.mapbox.com/styles/v1/mapbox/dark-v11/tiles/256/{z}/{x}/{y}@2x?access_token=${MAPBOX_TOKEN}`} tileSize={512} zoomOffset={-1} maxZoom={22}/>
        <MapPanHandler onMove={() => { setMapMoved(true); setSelectedBarber(null); }}/>
        <RecenterOnGPS lat={userLat} lng={userLng}/>
        {userLat && userLng && <UserDot lat={userLat} lng={userLng}/>}
        <MarkerClusterGroup
          chunkedLoading
          maxClusterRadius={60}
          iconCreateFunction={cluster => L.divIcon({
            html: `<div style="background:#0A0A0A;border:1.5px solid rgba(245,245,240,0.22);border-radius:14px;padding:6px 12px;box-shadow:0 4px 14px rgba(0,0,0,0.75);display:flex;align-items:center;gap:6px;white-space:nowrap">
              <div style="width:8px;height:8px;border-radius:50%;background:#2563EB;box-shadow:0 0 6px #2563EB"></div>
              <span style="font-family:'Bebas Neue',sans-serif;font-size:13px;letter-spacing:0.05em;color:#F5F5F0;line-height:1">${cluster.getChildCount()} BARBERS</span>
            </div>`,
            className: "",
            iconSize: [110, 32],
            iconAnchor: [55, 32],
          })}
        >
          {mappable.map(b => (
            <BarberMapMarker key={b.id} barber={b} isSelected={selectedBarber?.id === b.id} onPress={() => handlePress(b)}/>
          ))}
        </MarkerClusterGroup>
      </MapContainer>

      {/* Edge gradients — top fade for overlay legibility; bottom fade only on strip maps where content appears below */}
      {!hideFade && <div style={{ position:"absolute", inset:0, background:"linear-gradient(180deg, rgba(10,10,10,0.9) 0%, rgba(10,10,10,0) 18%, rgba(10,10,10,0) 60%, rgba(10,10,10,1) 100%)", pointerEvents:"none", zIndex:5 }}/>}
      {hideFade && <div style={{ position:"absolute", top:0, left:0, right:0, height:80, background:"linear-gradient(180deg, rgba(10,10,10,0.7) 0%, rgba(10,10,10,0) 100%)", pointerEvents:"none", zIndex:5 }}/>}

      {/* FADED wordmark — top left overlay */}
      {fullScreen && (
        <div style={{ position:"absolute", top:"max(20px, calc(var(--sat, 0px) + 18px))", left:16, zIndex:25, pointerEvents:"none" }}>
          <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:26, letterSpacing:5, color:"#F5F5F0", lineHeight:1 }}>
            FAD<span style={{ color:"#2563EB" }}>ED</span>
          </div>
        </div>
      )}

      {/* ── Floating glass search bar + filter chips ── */}
      <div style={{ position:"absolute", top:fullScreen?"max(60px, calc(var(--sat, 0px) + 54px))":"14px", left:14, right:14, zIndex:20 }}>
        <div style={{
          display:"flex", alignItems:"center", gap:10,
          background:"rgba(10,10,10,0.80)",
          backdropFilter:"blur(20px) saturate(140%)",
          WebkitBackdropFilter:"blur(20px) saturate(140%)",
          border:"1px solid rgba(245,245,240,0.08)",
          borderRadius:14, padding:"10px 14px",
        }}>
          <svg aria-hidden="true" focusable="false"width="15" height="15" viewBox="0 0 16 16" fill="none">
            <circle cx="7" cy="7" r="5" stroke="#F5F5F0" strokeWidth="1.6"/>
            <path d="M11 11l3 3" stroke="#F5F5F0" strokeWidth="1.6" strokeLinecap="round"/>
          </svg>
          <input
            value={mapSearch} onChange={e => setMapSearch(e.target.value)}
            placeholder="Search a fade, a name, a vibe…"
            style={{ flex:1, background:"transparent", border:"none", outline:"none", color:"#F5F5F0", fontSize:16, fontFamily:"'DM Sans', sans-serif" }}
          />
          <div style={{
            width:28, height:28, borderRadius:"50%", background:"#2563EB",
            display:"flex", alignItems:"center", justifyContent:"center",
            fontSize:11, fontWeight:800, color:"#fff", fontFamily:"'DM Sans', sans-serif", flexShrink:0,
          }}>{liveCount}</div>
        </div>
        <div className="map-chip-scroll" style={{ display:"flex", gap:8, marginTop:10 }}>
          {[
            { id:"all",        label:"All Barbers", count:totalOnMap },
            { id:"live",       label:"Live Now",    count:liveCount, live:true },
            { id:"rated",      label:"Top Rated" },
            { id:"affordable", label:"Under $60" },
          ].map(chip => (
            <button key={chip.id} onClick={() => setMapFilter(chip.id)} style={{
              flex:1, fontFamily:"'DM Sans', sans-serif", fontSize:11, fontWeight:600,
              padding:"7px 6px", borderRadius:100, minWidth:0,
              whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis",
              background:mapFilter===chip.id ? "#F5F5F0" : "rgba(10,10,10,0.76)",
              color:mapFilter===chip.id ? "#0A0A0A" : "#F5F5F0",
              border:mapFilter===chip.id ? "1px solid #F5F5F0" : "1px solid rgba(245,245,240,0.15)",
              cursor:"pointer", display:"flex", alignItems:"center", gap:6,
              backdropFilter:"blur(12px)", WebkitBackdropFilter:"blur(12px)",
            }}>
              {chip.live && <span style={{ width:6, height:6, borderRadius:"50%", background:"#22C55E", boxShadow:"0 0 6px #22C55E", display:"inline-block" }}/>}
              {chip.label}
              {chip.count !== undefined && <span style={{ opacity:0.55, marginLeft:2 }}>{chip.count}</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Search-this-area pill — after pan */}
      {mapMoved && !selectedBarber && (
        <div style={{ position:"absolute", top:"50%", left:"50%", transform:"translate(-50%,-50%)", zIndex:900 }}>
          <button onClick={() => setMapMoved(false)} style={{
            background:"rgba(10,10,10,0.88)", color:"#fff",
            border:"1px solid rgba(245,245,240,0.15)",
            backdropFilter:"blur(16px)", WebkitBackdropFilter:"blur(16px)",
            borderRadius:100, padding:"10px 20px",
            fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans', sans-serif",
            display:"flex", alignItems:"center", gap:8, whiteSpace:"nowrap",
          }}>
            <svg aria-hidden="true" focusable="false"width="13" height="13" viewBox="0 0 16 16" fill="none">
              <circle cx="7" cy="7" r="5" stroke="#F5F5F0" strokeWidth="1.6"/>
              <path d="M11 11l3 3" stroke="#F5F5F0" strokeWidth="1.6" strokeLinecap="round"/>
            </svg>
            Search this area
          </button>
        </div>
      )}

      {/* Locate me button — top-right below filter chips, never conflicts with barber card or list button */}
      {onLocate && (
        <button onClick={onLocate} aria-label="Center map on my location" style={{
          position:"absolute", right:14, top: fullScreen ? "max(184px, calc(var(--sat, 0px) + 144px))" : 110, zIndex:109,
          width:42, height:42, borderRadius:"50%",
          background:"rgba(10,10,10,0.88)", backdropFilter:"blur(14px)", WebkitBackdropFilter:"blur(14px)",
          border:"1px solid rgba(245,245,240,0.15)", display:"flex", alignItems:"center", justifyContent:"center",
          cursor:"pointer", boxShadow:"0 4px 16px rgba(0,0,0,0.4)",
        }}>
          {gpsLoading
            ? <div style={{ width:16, height:16, border:"2px solid rgba(245,245,240,0.3)", borderTopColor:"#2563EB", borderRadius:"50%", animation:"spin 0.8s linear infinite" }}/>
            : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#F5F5F0" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3"/><circle cx="12" cy="12" r="8" strokeDasharray="2 4"/>
              </svg>
          }
        </button>
      )}

      {/* List view toggle — labelled so users know what it does */}
      {fullScreen && onListView && (
        <button onClick={onListView} aria-label="Switch to list view" style={{
          position:"fixed", left:14, bottom:"calc(max(4px, env(safe-area-inset-bottom, 0px)) + 88px)", zIndex:109,
          borderRadius:20, padding:"8px 14px",
          background:"rgba(10,10,10,0.88)", backdropFilter:"blur(14px)", WebkitBackdropFilter:"blur(14px)",
          border:"1px solid rgba(245,245,240,0.15)", display:"flex", alignItems:"center", gap:7,
          cursor:"pointer", boxShadow:"0 4px 16px rgba(0,0,0,0.4)",
        }}>
          <svg aria-hidden="true" focusable="false" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#F5F5F0" strokeWidth="1.8" strokeLinecap="round">
            <line x1="3" y1="7" x2="21" y2="7"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="17" x2="21" y2="17"/>
          </svg>
          <span style={{ fontSize:12, fontWeight:700, color:"#F5F5F0", fontFamily:"'DM Sans',sans-serif" }}>List</span>
        </button>
      )}

      {/* ── Barber peek card ── */}
      {selectedBarber && (
        <div className="faded-map-preview" style={{ position:"absolute", left:12, right:12, bottom:fullScreen?"calc(max(4px, env(safe-area-inset-bottom, 0px)) + 88px)":12, zIndex:40 }}>
          <div style={{
            background:"#0A0A0A",
            border:"1px solid rgba(245,245,240,0.1)",
            borderRadius:22, padding:14,
            boxShadow:"0 -8px 40px rgba(0,0,0,0.6)",
          }}>
            {/* Status row */}
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <div style={{
                  width:7, height:7, borderRadius:"50%",
                  background:selectedBarber.status==="available"?"#22C55E":selectedBarber.status==="soon"?"#F59E0B":"#888",
                  boxShadow:selectedBarber.status==="available"?"0 0 6px #22C55E":"none",
                }}/>
                <span style={{
                  fontFamily:"'JetBrains Mono', 'Courier New', monospace",
                  fontSize:11, letterSpacing:"0.18em", fontWeight:500,
                  color:selectedBarber.status==="available"?"#22C55E":selectedBarber.status==="soon"?"#F59E0B":"#888",
                }}>
                  {selectedBarber.status==="available"?"LIVE NOW":selectedBarber.status==="soon"?"AVAILABLE SOON":"OFFLINE"}
                </span>
                {selectedBarber._distKm && (
                  <span style={{ fontFamily:"'JetBrains Mono', 'Courier New', monospace", fontSize:11, letterSpacing:"0.1em", color:isFarAway(selectedBarber._distKm)?"#F59E0B":"rgba(245,245,240,0.6)" }}>
                    · {Number(selectedBarber._distKm).toFixed(1)} km{isFarAway(selectedBarber._distKm)?" · outside your area":""}
                  </span>
                )}
              </div>
              <button onClick={() => setSelectedBarber(null)} style={{
                width:26, height:26, borderRadius:"50%",
                background:"rgba(245,245,240,0.06)", border:"none",
                color:"#F5F5F0", cursor:"pointer",
                display:"flex", alignItems:"center", justifyContent:"center", fontSize:14,
              }}>×</button>
            </div>

            {/* Body */}
            <div style={{ display:"flex", gap:12 }}>
              <div style={{
                width:68, height:68, borderRadius:14, flexShrink:0,
                background:`${selectedBarber.accent_color||"#2563EB"}22`,
                border:`1px solid ${selectedBarber.accent_color||"#2563EB"}44`,
                display:"flex", alignItems:"center", justifyContent:"center", overflow:"hidden",
              }}>
                {selectedBarber.avatar_url
                  ? <img src={selectedBarber.avatar_url} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
                  : <span style={{ fontFamily:"'Bebas Neue', sans-serif", fontSize:24, letterSpacing:"0.06em", color:selectedBarber.accent_color||"#2563EB" }}>
                      {(selectedBarber.full_name||"B").split(" ").map(w=>w[0]).join("")}
                    </span>}
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontFamily:"'Bebas Neue', sans-serif", fontSize:26, letterSpacing:"0.04em", lineHeight:1, marginBottom:4, color:"#F5F5F0" }}>
                  {(selectedBarber.full_name||"Barber").toUpperCase()}
                </div>
                <div style={{ fontSize:13, color:"rgba(245,245,240,0.65)", marginBottom:6 }}>
                  {selectedBarber.city||"Toronto"}
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:8, fontSize:12 }}>
                  <span style={{ display:"flex", alignItems:"center", gap:4 }}>
                    {selectedBarber.review_count > 0 ? (
                      <>
                        <svg aria-hidden="true" focusable="false"width="11" height="11" viewBox="0 0 11 11" fill="#F5F5F0"><path d="M5.5 0L6.7 3.7H10.5L7.4 6L8.6 9.7L5.5 7.4L2.4 9.7L3.6 6L0.5 3.7H4.3L5.5 0Z"/></svg>
                        <span style={{ fontWeight:600, color:"#F5F5F0" }}>{selectedBarber.rating}</span>
                        <span style={{ color:"rgba(245,245,240,0.5)" }}>({selectedBarber.review_count})</span>
                      </>
                    ) : (
                      <span style={{ fontWeight:700, color:selectedBarber.accent_color||"#2563EB" }}>✨ New on FADED</span>
                    )}
                  </span>
                  {(selectedBarber.price_min || selectedBarber.price_max) && <>
                  <span style={{ color:"rgba(245,245,240,0.3)" }}>·</span>
                  <span style={{ fontWeight:700, color:"#22C55E" }}>${selectedBarber.price_min ?? "?"}–${selectedBarber.price_max ?? "?"}</span>
                  </>}
                </div>
              </div>
            </div>

            {/* Specialty tags */}
            {(selectedBarber.specialty||[]).length > 0 && (
              <div style={{ display:"flex", gap:6, marginTop:12, flexWrap:"wrap" }}>
                {(selectedBarber.specialty||[]).slice(0,3).map(s => (
                  <span key={s} style={{
                    fontSize:11, fontWeight:500, padding:"4px 9px", borderRadius:100,
                    background:"rgba(245,245,240,0.05)", border:"1px solid rgba(245,245,240,0.1)",
                    color:"rgba(245,245,240,0.8)",
                  }}>{s}</span>
                ))}
              </div>
            )}

            {/* Next available + response time -- kept out of the discovery card to reduce
                clutter there, shown here on the detail view instead */}
            {(() => {
              const bits = [];
              if (selectedBarber.status !== "available" && selectedBarber.next_available) {
                const na = new Date(selectedBarber.next_available);
                if (!isNaN(na.getTime()) && na > new Date()) {
                  const now = new Date();
                  const diffH = (na - now) / 3600000;
                  const isToday = na.toDateString() === now.toDateString();
                  const isTomorrow = na.toDateString() === new Date(now.getTime()+86400000).toDateString();
                  const timeStr = na.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"});
                  bits.push(diffH < 1 ? `Available in ${Math.round(diffH*60)}m` : isToday ? `Next: Today ${timeStr}` : isTomorrow ? `Next: Tomorrow ${timeStr}` : `Next: ${na.toLocaleDateString([],{month:"short",day:"numeric"})} ${timeStr}`);
                }
              }
              if (selectedBarber.avg_response_minutes != null) {
                const m = selectedBarber.avg_response_minutes;
                bits.push(m < 10 ? "Responds in mins" : m < 60 ? `Responds ~${m}m` : m < 240 ? `Responds ~${Math.round(m/60)}h` : "Response time varies");
              }
              return bits.length > 0 ? (
                <div style={{ fontSize:12, color:"rgba(245,245,240,0.5)", marginTop:10 }}>{bits.join(" · ")}</div>
              ) : null;
            })()}

            {/* CTA */}
            {selectedBarber.status === "offline" ? (
              <div style={{ marginTop:14, width:"100%", background:"rgba(245,245,240,0.04)", border:"1px solid rgba(245,245,240,0.1)", borderRadius:14, padding:"15px 16px", textAlign:"center", color:"rgba(245,245,240,0.35)", fontFamily:"'Bebas Neue',sans-serif", fontSize:17, letterSpacing:"0.14em" }}>
                CURRENTLY OFFLINE
              </div>
            ) : selectedBarber.bookings_paused ? (
              <div style={{ marginTop:14, width:"100%", background:"rgba(245,158,11,0.08)", border:"1.5px solid rgba(245,158,11,0.25)", borderRadius:14, padding:"15px 16px", textAlign:"center", color:"#F59E0B", fontFamily:"'Bebas Neue',sans-serif", fontSize:17, letterSpacing:"0.14em", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true"><rect x="4" y="3" width="2.5" height="10" rx="1" fill="#F59E0B"/><rect x="9.5" y="3" width="2.5" height="10" rx="1" fill="#F59E0B"/></svg>
                NOT ACCEPTING BOOKINGS
              </div>
            ) : (
            <button
              onClick={() => { onBook(selectedBarber); setSelectedBarber(null); }}
              style={{
                marginTop:14, width:"100%",
                background:selectedBarber.status==="available"?"#2563EB":selectedBarber.status==="soon"?"rgba(245,158,11,0.15)":"rgba(245,245,240,0.06)",
                color:selectedBarber.status==="available"?"#F5F5F0":selectedBarber.status==="soon"?"#F59E0B":"rgba(245,245,240,0.5)",
                border:selectedBarber.status==="soon"?"1px solid rgba(245,158,11,0.3)":"none",
                borderRadius:14, padding:"15px 16px",
                fontFamily:"'Bebas Neue', sans-serif", fontSize:17, letterSpacing:"0.14em",
                cursor:"pointer",
                display:"flex", alignItems:"center", justifyContent:"center", gap:10,
                boxShadow:selectedBarber.status==="available"?"0 4px 16px rgba(37,99,235,0.35)":"none",
              }}>
              {selectedBarber.status==="available" ? "BOOK NOW" : "SCHEDULE"}
              <svg aria-hidden="true" focusable="false" width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M2 7h10M8 3l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── SHOP JOIN MODAL (deep link invite) ────────────────────────────────────────
function ShopJoinModal({ shopId, session, onAccept, onDecline }) {
  const { t } = useT();
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    supabase.from("shops").select("id,name,address,city,cover_photo_url,payment_model").eq("id", shopId).single()
      .then(({ data, error }) => { if (error || !data) setErr("Invite link is invalid or expired."); else setShop(data); setLoading(false); });
  }, [shopId]);

  const accept = async () => {
    if (!shop) return;
    setJoining(true);
    const { data: existing } = await supabase.from("shop_members").select("id,invite_status").eq("shop_id", shopId).eq("barber_id", session.user.id).maybeSingle();
    if (existing) { onAccept(existing.invite_status); return; }
    // Match the targeted-invite flow: employee-model shops (payment_model="shop_collects")
    // get their joiners set up as employees, not booth renters -- this link is meant to be
    // the smooth "brand new barber, no account yet" path for shops of either model.
    const isEmp = shop.payment_model === "shop_collects";
    const { error } = await supabase.from("shop_members").insert({ shop_id: shopId, barber_id: session.user.id, booth_rent_weekly: 0, rent_frequency: "weekly", employment_type: isEmp ? "employee" : "booth_renter", invite_status: "active" });
    if (error) { setErr("Something went wrong — please try again."); setJoining(false); return; }
    onAccept("active");
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"#04050A", zIndex:9999, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"32px 24px", fontFamily:"'DM Sans',sans-serif" }}>
      <div style={{ fontFamily:"'Bebas Neue',system-ui,sans-serif", fontSize:44, fontWeight:900, letterSpacing:"0.06em", color:"#F5F5F0", marginBottom:4, lineHeight:1 }}>FAD<span style={{ color:"#2563EB" }}>ED</span></div>
      <div style={{ fontSize:11, color:"rgba(245,245,240,0.3)", letterSpacing:"0.18em", textTransform:"uppercase", marginBottom:48 }}>Shop Invite</div>
      {loading ? (
        <div style={{ width:28, height:28, border:"3px solid rgba(255,255,255,0.1)", borderTopColor:"#2563EB", borderRadius:"50%", animation:"spin 0.8s linear infinite" }}/>
      ) : err ? (
        <div style={{ background:"rgba(239,68,68,0.08)", border:"1.5px solid rgba(239,68,68,0.3)", borderRadius:20, padding:24, maxWidth:380, width:"100%", textAlign:"center" }}>
          <div style={{ fontSize:32, marginBottom:12 }}>⚠️</div>
          <div style={{ fontSize:15, fontWeight:700, color:"#F5F5F0", marginBottom:8 }}>Invite Not Found</div>
          <div style={{ fontSize:13, color:"rgba(245,245,240,0.5)", lineHeight:1.6, marginBottom:20 }}>{err}</div>
          <button onClick={onDecline} style={{ padding:"12px 24px", background:"transparent", border:"1.5px solid rgba(255,255,255,0.12)", borderRadius:12, color:"rgba(245,245,240,0.6)", fontSize:13, fontWeight:700, cursor:"pointer" }}>Go Back</button>
        </div>
      ) : (
        <div style={{ background:"rgba(255,255,255,0.04)", border:"1.5px solid rgba(255,255,255,0.09)", borderRadius:24, padding:28, maxWidth:380, width:"100%" }}>
          {shop.cover_photo_url && <img src={shop.cover_photo_url} alt="" style={{ width:"100%", height:120, objectFit:"cover", borderRadius:16, marginBottom:20 }}/>}
          <div style={{ fontSize:10, fontWeight:800, color:"rgba(245,245,240,0.3)", letterSpacing:"0.14em", textTransform:"uppercase", marginBottom:8 }}>You've been invited to join</div>
          <div style={{ fontSize:24, fontWeight:900, color:"#F5F5F0", lineHeight:1.1, marginBottom:6 }}>{shop.name}</div>
          {shop.address && <div style={{ fontSize:13, color:"rgba(245,245,240,0.45)", marginBottom:24 }}>📍 {shop.address}{shop.city ? `, ${shop.city}` : ""}</div>}
          <div style={{ fontSize:13, color:"rgba(245,245,240,0.55)", lineHeight:1.7, marginBottom:28 }}>
            {shop.payment_model === "shop_collects"
              ? "You'll join as an employee — the shop handles your payments and payouts, and your schedule will be visible to the shop owner."
              : "You'll join as a booth renter — you keep your own payments and pay booth rent to the shop. Your schedule will be visible to the shop owner."}
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
            <button onClick={onDecline} style={{ padding:"14px", background:"transparent", border:"1.5px solid rgba(255,255,255,0.12)", borderRadius:14, color:"rgba(245,245,240,0.6)", fontSize:14, fontWeight:700, cursor:"pointer" }}>Decline</button>
            <button onClick={accept} disabled={joining} style={{ padding:"14px", background:"#2563EB", border:"none", borderRadius:14, color:"#fff", fontSize:14, fontWeight:800, cursor:"pointer", opacity:joining?0.7:1 }}>
              {joining ? "Joining…" : "Join Shop →"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── SHOP DASHBOARD ───────────────────────────────────────────────────────────
// Shop owner section — no Find a Booth toggle (owners offer booths, not look for them)
function BarberShopSection({ session, myBarberData, onShopCreated }) {
  return <ShopDashboard session={session} myBarberData={myBarberData} onShopCreated={onShopCreated}/>;
}

function ShopDashboard({ session, myBarberData, onShopCreated }) {
  const { t } = useT();
  const uid = session?.user?.id;

  // ── state ──
  const [shopTab,    setShopTab]    = useState("team");
  const [shop,       setShop]       = useState(null);        // my shop (if owner)
  const [members,    setMembers]    = useState([]);           // shop_members rows
  const [payments,   setPayments]   = useState([]);          // rent_payments rows
  const [myMembership, setMyMembership] = useState(null);    // if I'm a member (not owner)
  const [myShop,     setMyShop]     = useState(null);        // shop I'm a member of
  const [loading,    setLoading]    = useState(true);
  const [creating,   setCreating]   = useState(false);
  const [shopName,   setShopName]   = useState("");
  const [shopAddr,   setShopAddr]   = useState("");
  const [shopCity,   setShopCity]   = useState("");
  const [inviteHandle, setInviteHandle] = useState("");
  const [inviteRent,   setInviteRent]   = useState("200");
  const [inviteFreq,   setInviteFreq]   = useState("weekly");
  const [inviting,     setInviting]     = useState(false);
  const [inviteErr,    setInviteErr]    = useState("");
  const [msg,          setMsg]          = useState("");
  const [payModal,     setPayModal]     = useState(null);    // member row being paid
  const [payAmt,       setPayAmt]       = useState("");
  const [payLabel,     setPayLabel]     = useState("");
  const [payNote,      setPayNote]      = useState("");
  const [saving,       setSaving]       = useState(false);
  const [editShop,     setEditShop]     = useState(false);
  const [editName,     setEditName]     = useState("");
  const [editAddr,     setEditAddr]     = useState("");
  const [editPhone,    setEditPhone]    = useState("");
  const [editInsta,    setEditInsta]    = useState("");
  const [editDesc,     setEditDesc]     = useState("");
  const [editBooths,    setEditBooths]   = useState("");
  const [editBoothPx,   setEditBoothPx]  = useState("");
  const [editAmenities, setEditAmenities] = useState([]);
  const [editHours,     setEditHours]    = useState("");
  const [editRentOpts,  setEditRentOpts] = useState(["weekly"]);
  const [editPaymentModel, setEditPaymentModel] = useState("booth_rental");
  const shopCoverRef = useRef();
  const [shopCoverUrl,  setShopCoverUrl] = useState(null);
  const [teamSchedule, setTeamSchedule] = useState([]);
  const [scheduleFor, setScheduleFor] = useState(null); // { barber_id, name } — employee whose manual-appointments schedule is open
  const [myTeamEmployees, setMyTeamEmployees] = useState([]); // trusted-employee view: other employees at my shop
  const [myShopOwner, setMyShopOwner] = useState(null); // trusted-employee view: the shop owner's own schedule, { barber_id, name }

  // Shop address autocomplete (create flow)
  const [shopAddrSuggs, setShopAddrSuggs] = useState([]);
  const [shopAddrDrop,  setShopAddrDrop]  = useState(false);
  const [shopAddrCoords,setShopAddrCoords]= useState(null);
  const shopAddrTimer = useRef(null);

  const fetchShopAddrSuggs = (q) => {
    clearTimeout(shopAddrTimer.current);
    if (!q || q.length < 2) { setShopAddrSuggs([]); return; }
    shopAddrTimer.current = setTimeout(async () => {
      try {
        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json?access_token=${MAPBOX_TOKEN}&country=ca,us&limit=6&types=address,place,neighborhood,locality`;
        const res  = await fetch(url);
        const data = await res.json();
        if (!data.features?.length) { setShopAddrSuggs([]); return; }
        setShopAddrSuggs(data.features.map(f => ({ label: f.place_name, lat: f.center[1], lng: f.center[0] })));
        if (data.features.length > 0) setShopAddrDrop(true);
      } catch { setShopAddrSuggs([]); }
    }, 300);
  };

  // Shop address autocomplete (edit/settings flow) -- same pattern as create flow above.
  // Without this, editing the address later only ever changed the display text and never
  // updated lat/lng, so a shop that moved locations would silently keep stale map coordinates.
  const [editAddrSuggs, setEditAddrSuggs] = useState([]);
  const [editAddrDrop,  setEditAddrDrop]  = useState(false);
  const [editAddrCoords,setEditAddrCoords]= useState(null);
  const editAddrTimer = useRef(null);

  const fetchEditAddrSuggs = (q) => {
    clearTimeout(editAddrTimer.current);
    if (!q || q.length < 2) { setEditAddrSuggs([]); return; }
    editAddrTimer.current = setTimeout(async () => {
      try {
        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json?access_token=${MAPBOX_TOKEN}&country=ca,us&limit=6&types=address,place,neighborhood,locality`;
        const res  = await fetch(url);
        const data = await res.json();
        if (!data.features?.length) { setEditAddrSuggs([]); return; }
        setEditAddrSuggs(data.features.map(f => ({ label: f.place_name, lat: f.center[1], lng: f.center[0] })));
        if (data.features.length > 0) setEditAddrDrop(true);
      } catch { setEditAddrSuggs([]); }
    }, 300);
  };

  // Employee live search
  const [empQuery,     setEmpQuery]     = useState("");
  const [empResults,   setEmpResults]   = useState([]);
  const [empSearching, setEmpSearching] = useState(false);
  const [empSelected,  setEmpSelected]  = useState(null);
  const empTimer = useRef(null);

  const searchEmployees = (q) => {
    clearTimeout(empTimer.current);
    setEmpSelected(null);
    if (!q || q.length < 2) { setEmpResults([]); return; }
    empTimer.current = setTimeout(async () => {
      setEmpSearching(true);
      try {
        const clean = q.replace(/^@/, "").toLowerCase();
        const { data } = await supabase
          .from("barbers")
          .select("id, handle, shareable_slug, avatar_url, profiles(full_name)")
          .or(`handle.ilike.%${clean}%,shareable_slug.ilike.%${clean}%`)
          .neq("id", uid)
          .limit(8);
        setEmpResults(data || []);
      } catch { setEmpResults([]); }
      finally { setEmpSearching(false); }
    }, 300);
  };

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(""), 2800); };

  const load = async () => {
    setLoading(true);
    try {
      // Am I a shop owner?
      const { data: ownedShops } = await supabase.from("shops").select("*").eq("owner_id", uid).limit(1);
      if (ownedShops && ownedShops.length > 0) {
        const s = ownedShops[0];
        setShop(s);
        setEditName(s.name);
        setEditAddr(s.address || "");
        setEditPhone(s.phone || "");
        setEditInsta(s.instagram || "");
        setEditDesc(s.description || "");
        setEditBooths(s.available_booths != null ? String(s.available_booths) : "0");
        setEditBoothPx(s.booth_price_from != null ? String(s.booth_price_from) : "");
        setEditAmenities(s.amenities || []);
        setEditHours(s.open_hours || "");
        setEditRentOpts(s.rent_options?.length ? s.rent_options : ["weekly"]);
        setEditPaymentModel(s.payment_model || "booth_rental");
        setShopCoverUrl(s.cover_photo_url || null);
        const { data: mems } = await supabase.from("shop_members").select("*, barbers(id, status, rating, review_count, shareable_slug, handle, avatar_url, profiles(full_name))").eq("shop_id", s.id).order("joined_at");
        setMembers(mems || []);
        const { data: pays } = await supabase.from("rent_payments").select("*").eq("shop_id", s.id).order("paid_at", { ascending: false }).limit(100);
        setPayments(pays || []);
        const memberIds = (mems||[]).filter(m=>m.invite_status==="active").map(m=>m.barber_id);
        if (memberIds.length > 0) {
          const { data: sched } = await supabase.from("bookings").select("id,barber_id,client_name,service_type,scheduled_at,status,price_agreed").in("barber_id", memberIds).gte("scheduled_at", new Date().toISOString()).order("scheduled_at").limit(100);
          setTeamSchedule(sched || []);
        }
      } else {
        // Am I a member of a shop?
        const { data: mem } = await supabase.from("shop_members").select("*, shops(*)").eq("barber_id", uid).limit(1);
        if (mem && mem.length > 0) {
          setMyMembership(mem[0]);
          setMyShop(mem[0].shops);
          const { data: pays } = await supabase.from("rent_payments").select("*").eq("barber_id", uid).order("paid_at", { ascending: false }).limit(50);
          setPayments(pays || []);
          if (mem[0].invite_status === "active" && mem[0].employment_type === "employee" && mem[0].can_manage_shop_schedule) {
            const { data: teamEmps } = await supabase.from("shop_members")
              .select("barber_id, barbers(id, handle, profiles(full_name))")
              .eq("shop_id", mem[0].shop_id).eq("employment_type", "employee").eq("invite_status", "active");
            setMyTeamEmployees(teamEmps || []);
            if (mem[0].shops?.owner_id) {
              const { data: ownerProfile } = await supabase.from("profiles").select("full_name").eq("id", mem[0].shops.owner_id).single();
              setMyShopOwner({ barber_id: mem[0].shops.owner_id, name: ownerProfile?.full_name || "Shop Owner" });
            }
          }
        }
      }
    } catch (e) { console.error("[FADED] shop load:", e); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (uid) load(); }, [uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Real-time team updates — re-subscribe whenever member list changes
  useEffect(() => {
    const memberIds = members.filter(m=>m.invite_status==="active").map(m=>m.barber_id);
    if (!shop || memberIds.length === 0) return;
    const ch = supabase.channel(`shop-team-${shop.id}`)
      .on("postgres_changes", { event:"INSERT", schema:"public", table:"bookings" }, payload => {
        if (memberIds.includes(payload.new.barber_id)) {
          setTeamSchedule(prev => [...prev, payload.new].sort((a,b)=>new Date(a.scheduled_at)-new Date(b.scheduled_at)));
          flash(`📅 New booking — ${payload.new.client_name||"Client"} (${payload.new.service_type||"Haircut"})`);
        }
      })
      .on("postgres_changes", { event:"UPDATE", schema:"public", table:"bookings" }, payload => {
        if (memberIds.includes(payload.new.barber_id)) {
          setTeamSchedule(prev => prev.map(b => b.id===payload.new.id ? payload.new : b));
        }
      })
      .on("postgres_changes", { event:"INSERT", schema:"public", table:"walk_in_queue" }, payload => {
        if (memberIds.includes(payload.new.barber_id)) {
          flash(`🚶 Walk-in added — ${payload.new.client_name||"Walk-in"}`);
        }
      })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [shop, members]); // eslint-disable-line react-hooks/exhaustive-deps

  const createShop = async () => {
    if (!shopName.trim()) return;
    setCreating(true);
    setMsg("");
    try {
      const payload = { owner_id: uid, name: sanitize(shopName, 120) };
      if (shopAddr.trim()) payload.address = sanitize(shopAddr, 300);
      if (shopCity.trim()) payload.city = sanitize(shopCity, 100);
      let shopLat = shopAddrCoords?.lat ?? null;
      let shopLng = shopAddrCoords?.lng ?? null;
      // Owner typed an address but never picked a suggestion -- geocode it directly rather
      // than creating a shop with no map coordinates at all.
      if ((!shopLat || !shopLng) && shopAddr.trim()) {
        try {
          const r = await fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(shopAddr)}.json?access_token=${MAPBOX_TOKEN}&country=ca,us&limit=1&types=address,place`);
          const d = await r.json();
          if (d.features?.[0]) { shopLat = d.features[0].center[1]; shopLng = d.features[0].center[0]; }
        } catch {}
      }
      if (shopLat && shopLng) { payload.lat = shopLat; payload.lng = shopLng; }

      const { data, error } = await supabase.from("shops").insert(payload).select().single();

      if (error) {
        // Show error inline — msg is rendered in the create form
        setMsg("❌ " + (error.message || "Insert failed — check your connection"));
        console.error("[FADED] createShop error:", JSON.stringify(error));
        setCreating(false);
        return;
      }
      if (!data) {
        setMsg("❌ Shop was not returned — try refreshing");
        setCreating(false);
        return;
      }
      // Success — directly set shop state so view switches immediately,
      // then load() in background to hydrate members/payments/schedule
      setShop(data);
      if (onShopCreated) onShopCreated();
      setEditName(data.name);
      setEditAddr(data.address || "");
      setEditPhone(data.phone || "");
      setEditInsta(data.instagram || "");
      setEditDesc(data.description || "");
      setEditBooths(data.available_booths != null ? String(data.available_booths) : "0");
      setEditBoothPx(data.booth_price_from != null ? String(data.booth_price_from) : "");
      setEditAmenities([]); setEditHours(""); setEditRentOpts(["weekly"]);
      flash("✅ Shop created! Add your first barber below.");
      load(); // hydrate members + payments in background
    } catch (e) {
      setMsg("❌ " + (e?.message || "Something went wrong — try again"));
      console.error("[FADED] createShop exception:", e);
    } finally {
      setCreating(false);
    }
  };

  const uploadShopCover = async (file) => {
    const path = `${uid}/shop-cover-${Date.now()}.${file.name.split(".").pop()}`;
    const { error } = await supabase.storage.from("avatars").upload(path, file, { upsert:true });
    if (!error) { const { data } = supabase.storage.from("avatars").getPublicUrl(path); setShopCoverUrl(data.publicUrl); await supabase.from("shops").update({ cover_photo_url:data.publicUrl }).eq("id",shop.id); setShop(prev=>({...prev,cover_photo_url:data.publicUrl})); flash("Cover photo updated"); }
  };

  const saveShopDetails = async () => {
    setSaving(true);
    const upd = { name:editName, address:editAddr, phone:editPhone||null, instagram:editInsta||null, description:editDesc||null, available_booths:parseInt(editBooths)||0, booth_price_from:parseFloat(editBoothPx)||null, amenities:editAmenities, open_hours:editHours||null, rent_options:editRentOpts, payment_model:editPaymentModel, updated_at:new Date().toISOString() };
    if (editAddrCoords) {
      // Address was re-picked from autocomplete this edit.
      upd.lat = editAddrCoords.lat; upd.lng = editAddrCoords.lng;
    } else if (editAddr.trim() && editAddr.trim() !== (shop.address||"").trim()) {
      // Address text changed but wasn't picked from the dropdown -- geocode it directly
      // rather than silently leaving the shop's coordinates stuck at the old location.
      try {
        const r = await fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(editAddr)}.json?access_token=${MAPBOX_TOKEN}&country=ca,us&limit=1&types=address,place`);
        const d = await r.json();
        if (d.features?.[0]) { upd.lat = d.features[0].center[1]; upd.lng = d.features[0].center[0]; }
      } catch {}
    }
    // else: address untouched this edit -- leave existing lat/lng alone entirely.
    await supabase.from("shops").update(upd).eq("id", shop.id);
    setShop(prev => ({ ...prev, ...upd }));
    setEditShop(false);
    setEditAddrCoords(null);
    setSaving(false);
    flash("Shop details saved");
  };

  const inviteBarber = async () => {
    if (!inviteHandle.trim()) return;
    setInviting(true); setInviteErr("");
    try {
      const handle = inviteHandle.trim().replace(/^@/, "").toLowerCase();
      const { data: found } = await supabase.from("barbers").select("id, shareable_slug, handle, profiles(full_name)").ilike("shareable_slug", handle).limit(1);
      if (!found || found.length === 0) { setInviteErr("Barber not found — check their handle"); setInviting(false); return; }
      const barber = found[0];
      const barberName = (Array.isArray(barber.profiles)?barber.profiles[0]?.full_name:barber.profiles?.full_name) || barber.handle || "Barber";
      if (barber.id === uid) { setInviteErr("You can't invite yourself"); setInviting(false); return; }
      const isEmployee = shop.payment_model === "shop_collects";
      const { error } = await supabase.from("shop_members").insert({ shop_id: shop.id, barber_id: barber.id, booth_rent_weekly: isEmployee ? 0 : (parseFloat(inviteRent) || 0), rent_frequency: isEmployee ? "weekly" : inviteFreq, employment_type: isEmployee ? "employee" : "booth_renter", invite_status: "pending" });
      if (error) { setInviteErr(error.code === "23505" ? "This barber is already in your shop" : error.message); setInviting(false); return; }
      await load();
      setInviteHandle(""); setInviteRent("200");
      flash(`✅ ${barberName} added to your shop`);
    } catch (e) { setInviteErr("Something went wrong — try again"); }
    finally { setInviting(false); }
  };

  const removeMember = async (memberId, name) => {
    if (!await fakedConfirm(`Remove ${name} from your shop?`)) return;
    await supabase.from("shop_members").delete().eq("id", memberId);
    setMembers(prev => prev.filter(m => m.id !== memberId));
    flash(`${name} removed from shop`);
  };

  const updateRent = async (memberId, val) => {
    const amt = parseFloat(val);
    if (isNaN(amt) || amt < 0) return;
    await supabase.from("shop_members").update({ booth_rent_weekly: amt }).eq("id", memberId);
    setMembers(prev => prev.map(m => m.id === memberId ? { ...m, booth_rent_weekly: amt } : m));
  };

  const toggleTrustedEmployee = async (m) => {
    const next = !m.can_manage_shop_schedule;
    const { error } = await supabase.from("shop_members").update({ can_manage_shop_schedule: next }).eq("id", m.id);
    if (!error) {
      setMembers(prev => prev.map(x => x.id === m.id ? { ...x, can_manage_shop_schedule: next } : x));
      flash(next ? "Trusted employee access granted" : "Trusted employee access removed");
    }
  };

  const recordPayment = async () => {
    if (!payModal) return;
    setSaving(true);
    const amount = parseFloat(payAmt) || payModal.booth_rent_weekly;
    await supabase.from("rent_payments").insert({ shop_id: shop.id, barber_id: payModal.barber_id, amount, period_label: payLabel, notes: payNote });
    await load();
    setPayModal(null); setPayAmt(""); setPayLabel(""); setPayNote("");
    setSaving(false);
    flash("✅ Payment recorded");
  };

  const acceptInvite = async () => {
    await supabase.from("shop_members").update({ invite_status: "active" }).eq("id", myMembership.id);
    setMyMembership(prev => ({ ...prev, invite_status: "active" }));
    flash("You've joined the shop!");
  };

  const declineInvite = async () => {
    await supabase.from("shop_members").update({ invite_status: "declined" }).eq("id", myMembership.id);
    setMyMembership(prev => ({ ...prev, invite_status: "declined" }));
  };

  // ── Derived ──
  const weeklyOutstanding = members.reduce((s, m) => {
    if (m.invite_status !== "active") return s;
    const lastPaid = payments.filter(p => p.barber_id === m.barber_id).sort((a,b) => new Date(b.paid_at)-new Date(a.paid_at))[0];
    const weekMs = 7*24*60*60*1000;
    const overdue = lastPaid ? (Date.now() - new Date(lastPaid.paid_at).getTime()) > weekMs : true;
    return s + (overdue ? parseFloat(m.booth_rent_weekly) : 0);
  }, 0);

  const memberRentStatus = (m) => {
    const lastPaid = payments.filter(p => p.barber_id === m.barber_id).sort((a,b) => new Date(b.paid_at)-new Date(a.paid_at))[0];
    if (!lastPaid) return { label:"Never paid", color:"#EF4444", overdue:true };
    const days = Math.floor((Date.now() - new Date(lastPaid.paid_at).getTime()) / 86400000);
    const freqDays = m.rent_frequency==="biweekly"?14:m.rent_frequency==="monthly"?30:7;
    const overdue = days > freqDays;
    return overdue
      ? { label:`${days - freqDays}d overdue`, color:"#EF4444", overdue:true, lastPaid }
      : { label:`Paid ${days}d ago`, color:"#22C55E", overdue:false, lastPaid };
  };

  const SHOP_TABS = [
    { id:"team",      label:"Team" },
    { id:"schedule",  label:"Schedule" },
    { id:"analytics", label:"Analytics" },
    { id:"rent",      label:"Rent" },
    { id:"inquiries", label:"Inquiries" },
  ];

  if (loading) return <div style={{ textAlign:"center", padding:40, color:t.t4, fontSize:13 }}>Loading shop…</div>;

  // ── Member view (not owner) ──
  if (!shop && myMembership) {
    const rentStatus = payments.length > 0 ? (() => {
      const last = payments[0];
      const days = Math.floor((Date.now() - new Date(last.paid_at).getTime()) / 86400000);
      return { last, days };
    })() : null;
    return (
      <div style={{ padding:"20px 16px 100px" }}>
        {scheduleFor && (
          <div style={{ position:"fixed", inset:0, zIndex:600, background:t.bg, overflowY:"auto" }}>
            <BarberSchedule session={session} myBarberData={null} targetBarberId={scheduleFor.barber_id} viewerRole="trusted_employee" targetLabel={scheduleFor.name} onClose={()=>setScheduleFor(null)}/>
          </div>
        )}
        <div style={{ fontSize:15, fontWeight:800, color:t.t1, fontFamily:"'DM Sans',sans-serif", marginBottom:4 }}>🏪 Your Shop</div>
        <div style={{ fontSize:12, color:t.t4, marginBottom:16 }}>{myShop?.name}</div>
        {msg && <div style={{ marginBottom:12, padding:"10px 14px", background:"rgba(37,99,235,0.1)", border:"1px solid rgba(37,99,235,0.3)", borderRadius:12, fontSize:13, fontWeight:600, color:t.blueText }}>{msg}</div>}
        {myMembership.can_manage_shop_schedule && (myTeamEmployees.length > 0 || myShopOwner) && (
          <div style={{ background:t.card, borderRadius:18, border:`1.5px solid ${t.border}`, padding:16, marginBottom:16 }}>
            <div style={{ fontSize:13, fontWeight:800, color:t.t1, marginBottom:2 }}>🔑 Team Schedule</div>
            <div style={{ fontSize:11, color:t.t4, marginBottom:12 }}>You've been granted trusted-employee access — manage schedules for your team and the shop owner.</div>
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {myShopOwner && (
                <button key={myShopOwner.barber_id} onClick={()=>setScheduleFor({ barber_id:myShopOwner.barber_id, name:myShopOwner.name })}
                  style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 12px", background:t.section, border:`1px solid ${t.border}`, borderRadius:12, cursor:"pointer", textAlign:"left" }}>
                  <span style={{ fontSize:13, fontWeight:700, color:t.t1 }}>{myShopOwner.name} (owner)</span>
                  <span style={{ fontSize:11, fontWeight:700, color:"#2563EB" }}>📅 Schedule</span>
                </button>
              )}
              {myTeamEmployees.map(te => {
                const name = te.barbers?.profiles?.full_name || te.barbers?.handle || "Barber";
                return (
                  <button key={te.barber_id} onClick={()=>setScheduleFor({ barber_id:te.barber_id, name })}
                    style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 12px", background:t.section, border:`1px solid ${t.border}`, borderRadius:12, cursor:"pointer", textAlign:"left" }}>
                    <span style={{ fontSize:13, fontWeight:700, color:t.t1 }}>{name}{te.barber_id===uid?" (you)":""}</span>
                    <span style={{ fontSize:11, fontWeight:700, color:"#2563EB" }}>📅 Schedule</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {myMembership.invite_status === "pending" && (
          <div style={{ background:"rgba(245,158,11,0.08)", border:"1.5px solid rgba(245,158,11,0.35)", borderRadius:16, padding:16, marginBottom:16 }}>
            <div style={{ fontSize:14, fontWeight:800, color:"#F59E0B", marginBottom:8 }}>You've been invited to {myShop?.name}</div>
            {myMembership.employment_type === "employee" ? (
              <div style={{ fontSize:12, color:t.t3, marginBottom:12 }}>Role: Employee — your shop handles all payments and payouts.</div>
            ) : (
              <div style={{ fontSize:12, color:t.t3, marginBottom:12 }}>Booth rent: ${myMembership.booth_rent_weekly}/{myMembership.rent_frequency}</div>
            )}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
              <button onClick={acceptInvite} style={{ padding:"10px", background:"#22C55E", border:"none", borderRadius:10, color:"#fff", fontSize:13, fontWeight:700, cursor:"pointer" }}>✓ Accept</button>
              <button onClick={declineInvite} style={{ padding:"10px", background:t.section, border:`1px solid ${t.border}`, borderRadius:10, color:t.t3, fontSize:13, fontWeight:700, cursor:"pointer" }}>Decline</button>
            </div>
          </div>
        )}
        {myMembership.invite_status === "active" && myMembership.employment_type === "employee" ? (
          <div style={{ background:t.card, borderRadius:18, border:`1.5px solid ${t.border}`, padding:18 }}>
            <div style={{ fontSize:22, marginBottom:8 }}>🏪</div>
            <div style={{ fontSize:14, fontWeight:800, color:t.t1, marginBottom:6 }}>Employee of {myShop?.name}</div>
            <div style={{ fontSize:12, color:t.t3, lineHeight:1.6 }}>Your shop owner manages all client payments and card processing. Contact them directly for your pay schedule and payout details.</div>
          </div>
        ) : myMembership.invite_status === "active" && (
          <>
            <div style={{ background:t.card, borderRadius:18, border:`1.5px solid ${t.border}`, padding:18, marginBottom:12 }}>
              <div style={{ fontSize:12, color:t.t4, marginBottom:6 }}>Booth Rent</div>
              <div style={{ fontSize:26, fontWeight:900, color:t.t1, fontFamily:"'DM Sans',sans-serif" }}>${myMembership.booth_rent_weekly}<span style={{ fontSize:13, fontWeight:600, color:t.t4 }}>/{myMembership.rent_frequency}</span></div>
              {rentStatus && <div style={{ fontSize:12, color:t.t3, marginTop:6 }}>Last paid: {new Date(rentStatus.last.paid_at).toLocaleDateString([],{month:"short",day:"numeric"})} · {rentStatus.days}d ago</div>}
            </div>
            <div style={{ background:t.card, borderRadius:18, border:`1.5px solid ${t.border}`, padding:18 }}>
              <div style={{ fontSize:13, fontWeight:800, color:t.t1, marginBottom:12 }}>Payment History</div>
              {payments.length === 0 ? (
                <div style={{ color:t.t4, fontSize:12, textAlign:"center", padding:"16px 0" }}>No payments recorded yet</div>
              ) : payments.slice(0,10).map((p,i,arr) => (
                <div key={p.id} style={{ display:"flex", justifyContent:"space-between", padding:"9px 0", borderBottom:i<arr.length-1?`1px solid ${t.divider}`:"none" }}>
                  <div>
                    <div style={{ fontSize:13, fontWeight:700, color:t.t1 }}>${p.amount}</div>
                    <div style={{ fontSize:11, color:t.t4 }}>{p.period_label || new Date(p.paid_at).toLocaleDateString([],{month:"short",day:"numeric",year:"numeric"})}</div>
                  </div>
                  <span style={{ fontSize:11, fontWeight:700, color:"#22C55E", background:"rgba(34,197,94,0.12)", padding:"3px 9px", borderRadius:8, alignSelf:"center" }}>PAID</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }

  // ── No shop yet — create ──
  if (!shop) {
    return (
      <div style={{ padding:"20px 16px 100px" }}>
        <div style={{ fontSize:15, fontWeight:800, color:t.t1, fontFamily:"'DM Sans',sans-serif", marginBottom:4 }}>🏪 Shop Management</div>
        <div style={{ fontSize:12, color:t.t4, marginBottom:24 }}>Create your shop to manage barbers, track booth rent, and coordinate your team.</div>
        <div style={{ background:t.card, borderRadius:18, border:`1.5px solid ${t.border}`, padding:20 }}>
          <div style={{ fontSize:13, fontWeight:800, color:t.t1, marginBottom:16 }}>Create Your Shop</div>
          {/* Shop Name */}
          <div style={{ marginBottom:12 }}>
            <div style={{ fontSize:11, fontWeight:700, color:t.t3, marginBottom:4 }}>Shop Name *</div>
            <input value={shopName} onChange={e=>setShopName(e.target.value)} placeholder="e.g. King's Barbershop"
              style={{ width:"100%", padding:"11px 14px", borderRadius:12, border:`1.5px solid ${t.border2}`, background:t.section, color:t.inputText, fontSize:14, outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box" }}/>
          </div>
          {/* Address — LocationIQ autocomplete */}
          <div style={{ marginBottom:12 }}>
            <div style={{ fontSize:11, fontWeight:700, color:t.t3, marginBottom:4 }}>Address</div>
            <div style={{ position:"relative" }}>
              <input value={shopAddr}
                onChange={e=>{ setShopAddr(e.target.value); setShopAddrCoords(null); fetchShopAddrSuggs(e.target.value); }}
                onFocus={()=>shopAddrSuggs.length>0&&setShopAddrDrop(true)}
                onBlur={()=>setTimeout(()=>setShopAddrDrop(false),150)}
                placeholder="123 Main St, Hamilton, ON"
                autoComplete="off"
                style={{ width:"100%", padding:"11px 14px", borderRadius:shopAddrDrop&&shopAddrSuggs.length>0?"10px 10px 0 0":12, border:`1.5px solid ${shopAddrDrop?"#2563EB":t.border2}`, background:t.section, color:t.inputText, fontSize:14, outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box" }}/>
              {shopAddrDrop && shopAddrSuggs.length > 0 && (
                <div style={{ position:"absolute", top:"100%", left:0, right:0, background:t.card, border:`1.5px solid #2563EB`, borderTop:"none", borderRadius:"0 0 12px 12px", zIndex:9999, maxHeight:300, overflowY:"auto", boxShadow:"0 8px 24px rgba(0,0,0,0.2)" }}>
                  {shopAddrSuggs.map((s,i)=>(
                    <button key={i} onMouseDown={()=>{ setShopAddr(s.label); setShopAddrCoords({lat:parseFloat(s.lat),lng:parseFloat(s.lng)}); setShopAddrSuggs([]); setShopAddrDrop(false); }}
                      style={{ width:"100%", padding:"10px 14px", background:"none", border:"none", borderTop:i>0?`1px solid ${t.border}`:"none", color:t.t1, fontSize:13, cursor:"pointer", textAlign:"left", fontFamily:"'DM Sans',sans-serif", display:"flex", alignItems:"center", gap:8 }}>
                      <svg aria-hidden="true" focusable="false"width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={t.t4} strokeWidth="2"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg>
                      <span style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{s.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {shopAddrCoords && <div style={{ fontSize:11, color:"#22C55E", marginTop:4, display:"flex", alignItems:"center", gap:4 }}>✓ Location pinned on map</div>}
          </div>
          {/* City */}
          <div style={{ marginBottom:12 }}>
            <div style={{ fontSize:11, fontWeight:700, color:t.t3, marginBottom:4 }}>City</div>
            <input value={shopCity} onChange={e=>setShopCity(e.target.value)} placeholder="Hamilton, ON"
              style={{ width:"100%", padding:"11px 14px", borderRadius:12, border:`1.5px solid ${t.border2}`, background:t.section, color:t.inputText, fontSize:14, outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box" }}/>
          </div>
          {msg && (
            <div style={{ marginBottom:12, padding:"10px 14px", background: msg.startsWith("❌") ? "rgba(220,38,38,0.1)" : "rgba(34,197,94,0.1)", border:`1px solid ${msg.startsWith("❌") ? "rgba(220,38,38,0.35)" : "rgba(34,197,94,0.35)"}`, borderRadius:12, fontSize:13, fontWeight:600, color: msg.startsWith("❌") ? "#EF4444" : "#22C55E", fontFamily:"'DM Sans',sans-serif" }}>
              {msg}
            </div>
          )}
          <button onClick={createShop} disabled={creating||!shopName.trim()}
            style={{ width:"100%", padding:14, background:shopName.trim()?"#2563EB":t.surface2, border:"none", borderRadius:14, color:shopName.trim()?"#fff":t.t4, fontSize:14, fontWeight:800, cursor:shopName.trim()?"pointer":"default", fontFamily:"'DM Sans',sans-serif", opacity:creating?0.7:1 }}>
            {creating ? "Creating…" : "Create Shop →"}
          </button>
        </div>
      </div>
    );
  }

  // ── Shop owner view ──
  const activeMembers = members.filter(m => m.invite_status === "active");
  const pendingMembers = members.filter(m => m.invite_status === "pending");

  return (
    <div style={{ padding:"0 0 100px" }}>
      {/* Team Schedule overlay — owner managing an employee's manual appointments */}
      {scheduleFor && (
        <div style={{ position:"fixed", inset:0, zIndex:600, background:t.bg, overflowY:"auto" }}>
          <BarberSchedule session={session} myBarberData={null} targetBarberId={scheduleFor.barber_id} viewerRole="owner" targetLabel={scheduleFor.name} onClose={()=>setScheduleFor(null)}/>
        </div>
      )}
      {/* Header */}
      <div style={{ padding:"20px 16px 16px" }}>
        <div style={{ display:"flex", justifyContent:"flex-end", alignItems:"flex-start", marginBottom:6 }}>
          <button onClick={()=>setShopTab(shopTab==="settings"?"team":"settings")}
            style={{ width:32, height:32, borderRadius:10, background:shopTab==="settings"?t.blueBg:t.section, border:`1.5px solid ${shopTab==="settings"?t.blueBorder:t.border}`, color:shopTab==="settings"?t.blueText:t.t3, fontSize:16, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, transition:"all 0.15s" }}>
            ⚙️
          </button>
        </div>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-end" }}>
          <div>
            <div style={{ fontSize:22, fontWeight:900, color:t.t1, fontFamily:"'DM Sans',sans-serif", lineHeight:1.1 }}>{shop.name}</div>
            {shop.address && <div style={{ fontSize:11, color:t.t4, marginTop:4 }}>{shop.address}</div>}
          </div>
          <div style={{ textAlign:"right" }}>
            <div style={{ fontSize:20, fontWeight:900, color:t.t1, fontFamily:"'DM Sans',sans-serif" }}>{activeMembers.length}</div>
            <div style={{ fontSize:10, color:t.t4, textTransform:"uppercase", letterSpacing:"0.08em" }}>Barber{activeMembers.length!==1?"s":""}</div>
            {weeklyOutstanding > 0 && <div style={{ fontSize:11, fontWeight:700, color:"#EF4444", marginTop:2 }}>${weeklyOutstanding.toFixed(0)} overdue</div>}
          </div>
        </div>
      </div>

      {msg && <div style={{ margin:"0 16px 10px", padding:"10px 14px", background:"rgba(37,99,235,0.1)", border:"1px solid rgba(37,99,235,0.3)", borderRadius:12, fontSize:13, fontWeight:600, color:t.blueText }}>{msg}</div>}

      {/* Sub-tabs — underline style, hidden scrollbar */}
      <div style={{ borderBottom:`1px solid ${t.border}`, marginBottom:20, display:shopTab==="settings"?"none":"block" }}>
        <div style={{ display:"flex", overflowX:"auto", paddingLeft:16, paddingRight:16, paddingTop:8, scrollbarWidth:"none", msOverflowStyle:"none" }}>
          {SHOP_TABS.map(tb => {
            const active = shopTab === tb.id;
            return (
              <button key={tb.id} onClick={()=>setShopTab(tb.id)}
                style={{ flexShrink:0, padding:"0 4px 14px", marginRight:24, background:"none", border:"none", borderBottom:`2px solid ${active?"#2563EB":"transparent"}`, color:active?t.t1:t.t4, fontSize:13, fontWeight:active?800:600, cursor:"pointer", whiteSpace:"nowrap", letterSpacing:"0.01em", position:"relative", transition:"color 0.15s, border-color 0.15s" }}>
                {tb.label}
                {tb.id==="team" && pendingMembers.length > 0 && (
                  <span style={{ position:"absolute", top:-2, right:-10, background:"#F59E0B", color:"#fff", borderRadius:20, padding:"1px 5px", fontSize:10, fontWeight:800, lineHeight:1.4 }}>{pendingMembers.length}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── TEAM TAB ── */}
      {shopTab === "team" && (
        <div style={{ padding:"0 16px" }}>
          {/* Stats strip */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8, marginBottom:16 }}>
            {[["Barbers", activeMembers.length,"#60A5FA"],["Weekly Rent",`$${activeMembers.reduce((s,m)=>s+parseFloat(m.booth_rent_weekly||0),0).toFixed(0)}`,"#22C55E"],["Overdue",`$${weeklyOutstanding.toFixed(0)}`,weeklyOutstanding>0?"#EF4444":"#22C55E"]].map(([l,v,c])=>(
              <div key={l} style={{ background:t.card, borderRadius:14, padding:"12px 8px", border:`1.5px solid ${t.border}`, textAlign:"center" }}>
                <div style={{ fontSize:18, fontWeight:900, color:c, fontFamily:"'DM Sans',sans-serif" }}>{v}</div>
                <div style={{ fontSize:11, color:t.t4, fontWeight:600, marginTop:2 }}>{l}</div>
              </div>
            ))}
          </div>

          {/* Add barber to shop — live search */}
          <div style={{ background:t.card, borderRadius:18, border:`1.5px solid ${t.border}`, padding:16, marginBottom:16 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
              <div style={{ fontSize:13, fontWeight:800, color:t.t1 }}>+ Add Barber to Shop</div>
              <button onClick={()=>{ const link=`${window.location.origin}?join_shop=${shop.id}`; navigator.share?navigator.share({title:"Join my shop on FADED",text:"Tap to join my barber shop on FADED",url:link}).catch(()=>{}):navigator.clipboard?.writeText(link).then(()=>flash("✅ Invite link copied!")); }}
                style={{ display:"flex", alignItems:"center", gap:5, padding:"6px 12px", background:t.blueBg, border:`1px solid ${t.blueBorder}`, borderRadius:10, color:t.blueText, fontSize:11, fontWeight:700, cursor:"pointer", flexShrink:0 }}>
                🔗 Invite Link
              </button>
            </div>
            {/* Live search input */}
            <div style={{ position:"relative", marginBottom:8 }}>
              <input value={empQuery}
                onChange={e=>{ setEmpQuery(e.target.value); setEmpSelected(null); searchEmployees(e.target.value); }}
                onBlur={()=>setTimeout(()=>setEmpResults([]),200)}
                placeholder="Search by name or @handle"
                autoComplete="off"
                style={{ width:"100%", padding:"10px 12px", borderRadius:empResults.length>0&&!empSelected?10:10, border:`1.5px solid ${inviteErr?"#EF4444":empSelected?"#22C55E":t.border2}`, background:t.section, color:t.inputText, fontSize:13, outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box" }}/>
              {empSearching && <span style={{ position:"absolute", right:10, top:"50%", transform:"translateY(-50%)", fontSize:12, color:t.t4 }}>⟳</span>}
              {empResults.length > 0 && !empSelected && (
                <div style={{ position:"absolute", top:"calc(100% + 4px)", left:0, right:0, background:t.card, border:`1.5px solid ${t.border}`, borderRadius:12, zIndex:9999, overflow:"hidden", boxShadow:"0 8px 24px rgba(0,0,0,0.2)" }}>
                  {empResults.map((b,i,arr)=>{
                    const name = b.profiles?.full_name || b.handle || "Barber";
                    return (
                      <button key={b.id} onMouseDown={()=>{ setEmpSelected(b); setInviteHandle(b.shareable_slug||b.handle||""); setEmpQuery(name); setEmpResults([]); }}
                        style={{ width:"100%", display:"flex", alignItems:"center", gap:10, padding:"10px 14px", background:"none", border:"none", borderBottom:i<arr.length-1?`1px solid ${t.divider}`:"none", cursor:"pointer", outline:"none", textAlign:"left" }}>
                        <div style={{ width:32, height:32, borderRadius:10, background:t.section, overflow:"hidden", flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, fontWeight:800, color:t.t3 }}>
                          {b.avatar_url ? <img src={b.avatar_url} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }}/> : name[0].toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontSize:13, fontWeight:700, color:t.t1, fontFamily:"'DM Sans',sans-serif" }}>{name}</div>
                          <div style={{ fontSize:11, color:t.t4 }}>@{b.handle||b.shareable_slug}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            {empSelected && (
              <div style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 12px", background:"rgba(34,197,94,0.08)", border:"1px solid rgba(34,197,94,0.25)", borderRadius:10, marginBottom:10 }}>
                <div style={{ width:28, height:28, borderRadius:8, background:t.section, overflow:"hidden", flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:800, color:t.t3 }}>
                  {empSelected.avatar_url ? <img src={empSelected.avatar_url} alt="" style={{ width:"100%",height:"100%",objectFit:"cover" }}/> : (empSelected.profiles?.full_name||empSelected.handle||"B")[0].toUpperCase()}
                </div>
                <span style={{ fontSize:12, fontWeight:700, color:"#22C55E" }}>✓ {empSelected.profiles?.full_name||empSelected.handle}</span>
                <button onClick={()=>{ setEmpSelected(null); setEmpQuery(""); setInviteHandle(""); }} style={{ marginLeft:"auto", background:"none", border:"none", color:t.t4, fontSize:16, cursor:"pointer", padding:"0 4px" }}>×</button>
              </div>
            )}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:8 }}>
              <div>
                <div style={{ fontSize:11, color:t.t4, marginBottom:3 }}>Booth Rent $</div>
                {shop?.payment_model === "shop_collects" ? (
                  <div style={{ padding:"9px 10px", borderRadius:10, border:`1.5px solid ${t.border2}`, background:t.section, color:t.t4, fontSize:12 }}>N/A — shop collects (employee)</div>
                ) : (
                <input type="number" value={inviteRent} onChange={e=>setInviteRent(e.target.value)} min={0}
                  style={{ width:"100%", padding:"9px 10px", borderRadius:10, border:`1.5px solid ${t.border2}`, background:t.section, color:t.inputText, fontSize:13, outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box" }}/>
                )}
              </div>
              <div>
                <div style={{ fontSize:11, color:t.t4, marginBottom:3 }}>Frequency</div>
                <select value={inviteFreq} onChange={e=>setInviteFreq(e.target.value)}
                  style={{ width:"100%", padding:"9px 10px", borderRadius:10, border:`1.5px solid ${t.border2}`, background:t.section, color:t.inputText, fontSize:13, outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box" }}>
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Biweekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
            </div>
            {inviteErr && <div style={{ fontSize:11, color:"#EF4444", marginBottom:6 }}>{inviteErr}</div>}
            <button onClick={async ()=>{
                setInviting(true); setInviteErr("");
                try {
                  let barber = empSelected || null;
                  if (!barber) {
                    const slug = inviteHandle.trim().replace(/^@/,"").toLowerCase();
                    if (!slug) { setInviteErr("Enter a barber name or handle"); setInviting(false); return; }
                    const { data: found } = await supabase.from("barbers").select("id,shareable_slug,handle,profiles(full_name)").or(`shareable_slug.ilike.${slug},handle.ilike.${slug}`).limit(1);
                    if (!found||found.length===0){ setInviteErr("Barber not found — check their handle"); setInviting(false); return; }
                    barber = found[0];
                  }
                  if (barber.id===uid){ setInviteErr("You can't add yourself"); setInviting(false); return; }
                  const isEmp = shop.payment_model === "shop_collects";
                  const { error } = await supabase.from("shop_members").insert({ shop_id:shop.id, barber_id:barber.id, booth_rent_weekly:isEmp?0:parseFloat(inviteRent)||0, rent_frequency:isEmp?"weekly":inviteFreq, employment_type:isEmp?"employee":"booth_renter", invite_status:"pending" });
                  if (error){ setInviteErr(error.code==="23505"?"Already in your shop":error.message); setInviting(false); return; }
                  await load();
                  setInviteHandle(""); setInviteRent("200"); setEmpSelected(null); setEmpQuery("");
                  flash(`✅ Invite sent to ${barber.profiles?.full_name||barber.handle} — waiting for them to accept`);
                } catch(e) { setInviteErr("Something went wrong — try again"); }
                finally { setInviting(false); }
              }}
              disabled={inviting||(!empSelected&&!inviteHandle.trim())}
              style={{ width:"100%", padding:11, background:(empSelected||inviteHandle.trim())?"#2563EB":t.surface2, border:"none", borderRadius:10, color:(empSelected||inviteHandle.trim())?"#fff":t.t4, fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", opacity:inviting?0.7:1 }}>
              {inviting ? "Adding…" : empSelected ? `Add ${empSelected.profiles?.full_name||empSelected.handle} →` : "Add to Shop →"}
            </button>
          </div>

          {/* Pending invites */}
          {pendingMembers.length > 0 && (
            <div style={{ background:"rgba(245,158,11,0.06)", borderRadius:16, border:"1px solid rgba(245,158,11,0.3)", padding:14, marginBottom:12 }}>
              <div style={{ fontSize:12, fontWeight:700, color:"#F59E0B", marginBottom:8 }}>⏳ Pending ({pendingMembers.length})</div>
              {pendingMembers.map(m => (
                <div key={m.id} style={{ fontSize:12, color:t.t2, padding:"4px 0" }}>
                  {m.barbers?.profiles?.full_name || m.barbers?.handle || "Barber"} — waiting to accept
                </div>
              ))}
            </div>
          )}

          {/* Active members */}
          {activeMembers.length === 0 ? (
            <div style={{ textAlign:"center", padding:"32px 0", color:t.t4, fontSize:13 }}>No barbers yet — add one above</div>
          ) : activeMembers.map(m => {
            const status = memberRentStatus(m);
            return (
              <div key={m.id} style={{ background:t.card, borderRadius:16, border:`1.5px solid ${status.overdue?"rgba(220,38,38,0.3)":t.border}`, padding:14, marginBottom:10 }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10 }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:14, fontWeight:800, color:t.t1 }}>{m.barbers?.profiles?.full_name || m.barbers?.handle || "Barber"}</div>
                    <div style={{ fontSize:11, color:t.t4, marginTop:2 }}>⭐ {m.barbers?.rating||"—"} · {m.barbers?.review_count||0} reviews · @{m.barbers?.shareable_slug||m.barbers?.handle||"—"}</div>
                  </div>
                  <span style={{ fontSize:11, fontWeight:700, padding:"3px 9px", borderRadius:10, background:`${status.overdue?"rgba(220,38,38,0.1)":"rgba(34,197,94,0.1)"}`, color:status.color }}>
                    {status.label}
                  </span>
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
                  <span style={{ fontSize:12, color:t.t3, flexShrink:0 }}>Booth rent $</span>
                  <input type="number" defaultValue={m.booth_rent_weekly} min={0}
                    onBlur={e=>updateRent(m.id, e.target.value)}
                    style={{ width:80, padding:"6px 8px", borderRadius:8, border:`1.5px solid ${t.border2}`, background:t.section, color:t.inputText, fontSize:13, fontWeight:700, textAlign:"center", outline:"none", fontFamily:"'DM Sans',sans-serif" }}/>
                  <span style={{ fontSize:12, color:t.t4 }}>/{m.rent_frequency}</span>
                </div>
                {m.employment_type === "employee" && (
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:8, marginBottom:10, padding:"9px 10px", background:t.section, borderRadius:10, border:`1px solid ${t.border}` }}>
                    <span style={{ fontSize:11, fontWeight:700, color:t.t3 }}>🔑 Trusted employee — can manage other employees' schedules</span>
                    <button onClick={()=>toggleTrustedEmployee(m)} aria-label="Toggle trusted employee"
                      style={{ width:38, height:22, borderRadius:11, border:"none", cursor:"pointer", flexShrink:0, background:m.can_manage_shop_schedule?"#2563EB":t.border2, position:"relative", transition:"background 0.15s" }}>
                      <span style={{ position:"absolute", top:2, left:m.can_manage_shop_schedule?18:2, width:18, height:18, borderRadius:"50%", background:"#fff", transition:"left 0.15s" }}/>
                    </button>
                  </div>
                )}
                <div style={{ display:"grid", gridTemplateColumns: m.employment_type==="employee" ? "1fr 1fr 1fr" : "1fr 1fr", gap:8 }}>
                  {m.employment_type === "employee" && (
                    <button onClick={()=>setScheduleFor({ barber_id:m.barber_id, name:m.barbers?.profiles?.full_name||m.barbers?.handle||"Barber" })}
                      style={{ padding:"9px", background:"rgba(37,99,235,0.1)", border:"1px solid rgba(37,99,235,0.3)", borderRadius:10, color:"#2563EB", fontSize:12, fontWeight:700, cursor:"pointer" }}>📅 Schedule</button>
                  )}
                  <button onClick={()=>{ setPayModal(m); setPayAmt(String(m.booth_rent_weekly)); const now=new Date(); setPayLabel(`Week of ${now.toLocaleDateString([],{month:"short",day:"numeric"})}`); }}
                    style={{ padding:"9px", background:"rgba(34,197,94,0.12)", border:"1px solid rgba(34,197,94,0.3)", borderRadius:10, color:"#22C55E", fontSize:12, fontWeight:700, cursor:"pointer" }}>💵 Record Payment</button>
                  <button onClick={()=>removeMember(m.id, m.barbers?.profiles?.full_name||m.barbers?.handle||"Barber")}
                    style={{ padding:"9px", background:"rgba(220,38,38,0.06)", border:"1px solid rgba(220,38,38,0.2)", borderRadius:10, color:"#EF4444", fontSize:12, fontWeight:700, cursor:"pointer" }}>Remove</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── RENT TAB ── */}
      {shopTab === "rent" && (
        <div style={{ padding:"0 16px" }}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:16 }}>
            {[["Monthly Income",`$${(activeMembers.reduce((s,m)=>s+parseFloat(m.booth_rent_weekly||0),0)*4.3).toFixed(0)}`,"#22C55E","projected"],["Outstanding",`$${weeklyOutstanding.toFixed(0)}`,"#EF4444","this period"]].map(([l,v,c,sub])=>(
              <div key={l} style={{ background:t.card, borderRadius:16, padding:16, border:`1.5px solid ${t.border}`, textAlign:"center" }}>
                <div style={{ fontSize:22, fontWeight:900, color:c }}>{v}</div>
                <div style={{ fontSize:11, color:t.t4, marginTop:2 }}>{sub}</div>
                <div style={{ fontSize:11, color:t.t3, marginTop:2, fontWeight:600 }}>{l}</div>
              </div>
            ))}
          </div>

          {activeMembers.map(m => {
            const mPays = payments.filter(p => p.barber_id === m.barber_id).slice(0,5);
            const status = memberRentStatus(m);
            return (
              <div key={m.id} style={{ background:t.card, borderRadius:16, border:`1.5px solid ${t.border}`, padding:14, marginBottom:12 }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                  <div style={{ fontSize:13, fontWeight:800, color:t.t1 }}>{m.barbers?.profiles?.full_name || m.barbers?.handle || "Barber"}</div>
                  <span style={{ fontSize:11, fontWeight:700, color:status.color }}>{status.label}</span>
                </div>
                <div style={{ fontSize:11, color:t.t4, marginBottom:8 }}>Rent: ${m.booth_rent_weekly}/{m.rent_frequency}</div>
                {mPays.length === 0 ? (
                  <div style={{ fontSize:11, color:t.t4, fontStyle:"italic" }}>No payments recorded</div>
                ) : mPays.map((p,i,arr)=>(
                  <div key={p.id} style={{ display:"flex", justifyContent:"space-between", padding:"7px 0", borderBottom:i<arr.length-1?`1px solid ${t.divider}`:"none", fontSize:12 }}>
                    <div>
                      <span style={{ fontWeight:700, color:t.t1 }}>${p.amount}</span>
                      {p.period_label && <span style={{ color:t.t4, marginLeft:8 }}>{p.period_label}</span>}
                    </div>
                    <span style={{ color:t.t4 }}>{new Date(p.paid_at).toLocaleDateString([],{month:"short",day:"numeric"})}</span>
                  </div>
                ))}
                <button onClick={()=>{ setPayModal(m); setPayAmt(String(m.booth_rent_weekly)); const now=new Date(); setPayLabel(`Week of ${now.toLocaleDateString([],{month:"short",day:"numeric"})}`); setShopTab("team"); }}
                  style={{ width:"100%", marginTop:10, padding:"8px", background:"rgba(34,197,94,0.1)", border:"1px solid rgba(34,197,94,0.25)", borderRadius:10, color:"#22C55E", fontSize:12, fontWeight:700, cursor:"pointer" }}>+ Record Payment</button>
              </div>
            );
          })}

          {/* Send reminder */}
          {activeMembers.filter(m=>memberRentStatus(m).overdue).length > 0 && (
            <div style={{ background:t.card, borderRadius:16, border:`1.5px solid ${t.border}`, padding:14, marginTop:4 }}>
              <div style={{ fontSize:12, fontWeight:700, color:t.t1, marginBottom:8 }}>📲 Rent Reminder</div>
              <div style={{ fontSize:11, color:t.t3, marginBottom:10 }}>Copy this message and send to barbers with overdue rent:</div>
              <div style={{ background:t.section, borderRadius:10, padding:"10px 12px", fontSize:12, color:t.t2, lineHeight:1.6, marginBottom:10, border:`1px solid ${t.border}` }}>
                Hey! Just a reminder that your booth rent of ${activeMembers.filter(m=>memberRentStatus(m).overdue).map(m=>`$${m.booth_rent_weekly}`).join("/")} is overdue at {shop.name}. Please settle up when you get a chance. Thanks!
              </div>
              <button onClick={()=>{ const msg=`Hey! Just a reminder that your booth rent is overdue at ${shop.name}. Please settle up when you get a chance. Thanks!`; navigator.clipboard?.writeText(msg).then(()=>window.dispatchEvent(new CustomEvent("faded-toast",{detail:{msg:"✅ Reminder copied!",type:"success"}}))); }} style={{ width:"100%", padding:"9px", background:"#2563EB", border:"none", borderRadius:10, color:"#fff", fontSize:12, fontWeight:700, cursor:"pointer" }}>📋 Copy Reminder</button>
            </div>
          )}
        </div>
      )}

      {/* ── SCHEDULE TAB ── */}
      {shopTab === "schedule" && (
        <div style={{ padding:"0 16px" }}>
          <div style={{ fontSize:13, fontWeight:800, color:t.t1, marginBottom:12 }}>Team's Upcoming Appointments</div>
          {teamSchedule.length === 0 ? (
            <div style={{ textAlign:"center", padding:"40px 0", color:t.t4, fontSize:13 }}>No upcoming appointments for your team</div>
          ) : teamSchedule.map((b,i) => {
            const mem = members.find(m=>m.barber_id===b.barber_id);
            const bName = mem?.barbers?.profiles?.full_name || mem?.barbers?.handle || "Barber";
            const dt = new Date(b.scheduled_at);
            return (
              <div key={b.id} style={{ background:t.card, borderRadius:14, border:`1.5px solid ${t.border}`, padding:12, marginBottom:8 }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13, fontWeight:800, color:t.t1 }}>{b.client_name||"Client"} · {b.service_type||"Service"}</div>
                    <div style={{ fontSize:11, color:t.t4, marginTop:2 }}>with {bName}</div>
                    <div style={{ fontSize:11, color:t.t3, marginTop:3 }}>{dt.toLocaleDateString([],{weekday:"short",month:"short",day:"numeric"})} at {dt.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})}</div>
                  </div>
                  <div style={{ textAlign:"right" }}>
                    <span style={{ fontSize:11, fontWeight:700, padding:"3px 9px", borderRadius:10, background:b.status==="confirmed"?"rgba(34,197,94,0.12)":b.status==="pending"?"rgba(245,158,11,0.1)":"rgba(37,99,235,0.1)", color:b.status==="confirmed"?"#22C55E":b.status==="pending"?"#F59E0B":"#60A5FA" }}>{(b.status||"pending").toUpperCase()}</span>
                    {b.price_agreed && <div style={{ fontSize:12, fontWeight:700, color:t.t1, marginTop:4 }}>${b.price_agreed}</div>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── ANALYTICS TAB ── */}
      {shopTab === "analytics" && (() => {
        const activeMs = members.filter(m=>m.invite_status==="active");
        const totalRevenue = payments.reduce((s,p)=>s+parseFloat(p.amount||0),0);
        const weeklyRent = activeMs.reduce((s,m)=>s+parseFloat(m.booth_rent_weekly||0),0);
        return (
          <div style={{ padding:"0 16px" }}>
            {/* Shop-level stats */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:16 }}>
              {[["Total Rent Collected",`$${totalRevenue.toFixed(0)}`,"#22C55E"],["Weekly Projected",`$${weeklyRent.toFixed(0)}`,"#60A5FA"],["Active Barbers",activeMs.length,"#A78BFA"],["Upcoming Apts",teamSchedule.length,"#F59E0B"]].map(([l,v,c])=>(
                <div key={l} style={{ background:t.card, borderRadius:16, padding:16, border:`1.5px solid ${t.border}`, textAlign:"center" }}>
                  <div style={{ fontSize:22, fontWeight:900, color:c, fontFamily:"'DM Sans',sans-serif" }}>{v}</div>
                  <div style={{ fontSize:11, color:t.t4, marginTop:3, fontWeight:600 }}>{l}</div>
                </div>
              ))}
            </div>

            {/* Per-barber breakdown */}
            <div style={{ fontSize:12, fontWeight:800, color:t.t3, marginBottom:8, textTransform:"uppercase", letterSpacing:"0.06em" }}>Per Barber</div>
            {activeMs.map(m => {
              const bName = m.barbers?.profiles?.full_name || m.barbers?.handle || "Barber";
              const mPays = payments.filter(p=>p.barber_id===m.barber_id);
              const mRev = mPays.reduce((s,p)=>s+parseFloat(p.amount||0),0);
              const mApts = teamSchedule.filter(b=>b.barber_id===m.barber_id);
              const status = memberRentStatus(m);
              return (
                <div key={m.id} style={{ background:t.card, borderRadius:16, border:`1.5px solid ${status.overdue?"rgba(220,38,38,0.25)":t.border}`, padding:14, marginBottom:10 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                    <div style={{ fontSize:13, fontWeight:800, color:t.t1 }}>{bName}</div>
                    <span style={{ fontSize:11, fontWeight:700, color:status.color }}>{status.label}</span>
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:6 }}>
                    {[["Rent Paid",`$${mRev.toFixed(0)}`,"#22C55E"],["Payments",mPays.length,"#60A5FA"],["Upcoming",mApts.length,"#A78BFA"]].map(([l,v,c])=>(
                      <div key={l} style={{ background:t.section, borderRadius:10, padding:"8px 6px", textAlign:"center" }}>
                        <div style={{ fontSize:16, fontWeight:800, color:c }}>{v}</div>
                        <div style={{ fontSize:11, color:t.t4, marginTop:1 }}>{l}</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* ── INQUIRIES TAB ── */}
      {shopTab === "inquiries" && shop && (
        <InquiriesTab shopId={shop.id} t={t}/>
      )}

      {/* ── SETTINGS TAB ── */}
      {shopTab === "settings" && (
        <div style={{ padding:"0 16px" }}>
          {/* Cover photo */}
          <div style={{ marginBottom:12, position:"relative", height:120, borderRadius:16, overflow:"hidden", background:(shopCoverUrl||shop?.cover_photo_url)?`url(${shopCoverUrl||shop.cover_photo_url}) center/cover no-repeat`:"linear-gradient(135deg,#0D1117,#0f1e3d)", border:`1.5px solid ${t.border}`, cursor:"pointer" }} onClick={()=>shopCoverRef.current?.click()}>
            <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", background:"rgba(0,0,0,0.35)" }}>
              <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
                <span style={{ fontSize:22 }}>📷</span>
                <span style={{ fontSize:11, fontWeight:700, color:"rgba(255,255,255,0.8)" }}>{shopCoverUrl||shop?.cover_photo_url?"Change Cover Photo":"Add Cover Photo"}</span>
              </div>
            </div>
            <input ref={shopCoverRef} type="file" accept="image/*" style={{ display:"none" }} onChange={e=>uploadShopCover(e.target.files[0])}/>
          </div>

          <div style={{ background:t.card, borderRadius:18, border:`1.5px solid ${t.border}`, padding:18, marginBottom:12 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
              <div style={{ fontSize:13, fontWeight:800, color:t.t1 }}>Shop Profile</div>
              <button onClick={()=>setEditShop(!editShop)} style={{ fontSize:12, color:t.blueText, fontWeight:700, background:t.blueBg, border:"none", padding:"5px 12px", borderRadius:10, cursor:"pointer" }}>{editShop?"Cancel":"Edit"}</button>
            </div>
            {editShop ? (
              <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                {[{label:"Shop Name *",val:editName,set:setEditName,ph:"King's Barbershop",type:"text"}].map(f=>(
                  <div key={f.label}>
                    <div style={{ fontSize:11, fontWeight:700, color:t.t3, marginBottom:3 }}>{f.label}</div>
                    <input type={f.type} value={f.val} onChange={e=>f.set(e.target.value)} placeholder={f.ph}
                      style={{ width:"100%", padding:"11px 14px", borderRadius:12, border:`1.5px solid ${t.border2}`, background:t.section, color:t.inputText, fontSize:13, outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box" }}/>
                  </div>
                ))}
                {/* Address — same Mapbox autocomplete as the create-shop flow, so editing later
                    actually updates lat/lng instead of only the display text */}
                <div>
                  <div style={{ fontSize:11, fontWeight:700, color:t.t3, marginBottom:3 }}>Address</div>
                  <div style={{ position:"relative" }}>
                    <input value={editAddr}
                      onChange={e=>{ setEditAddr(e.target.value); setEditAddrCoords(null); fetchEditAddrSuggs(e.target.value); }}
                      onFocus={()=>editAddrSuggs.length>0&&setEditAddrDrop(true)}
                      onBlur={()=>setTimeout(()=>setEditAddrDrop(false),150)}
                      placeholder="123 Main St, Hamilton, ON"
                      autoComplete="off"
                      style={{ width:"100%", padding:"11px 14px", borderRadius:editAddrDrop&&editAddrSuggs.length>0?"10px 10px 0 0":12, border:`1.5px solid ${editAddrDrop?"#2563EB":t.border2}`, background:t.section, color:t.inputText, fontSize:13, outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box" }}/>
                    {editAddrDrop && editAddrSuggs.length > 0 && (
                      <div style={{ position:"absolute", top:"100%", left:0, right:0, background:t.card, border:"1.5px solid #2563EB", borderTop:"none", borderRadius:"0 0 12px 12px", zIndex:9999, maxHeight:300, overflowY:"auto", boxShadow:"0 8px 24px rgba(0,0,0,0.2)" }}>
                        {editAddrSuggs.map((s,i)=>(
                          <button key={i} onMouseDown={()=>{ setEditAddr(s.label); setEditAddrCoords({lat:parseFloat(s.lat),lng:parseFloat(s.lng)}); setEditAddrSuggs([]); setEditAddrDrop(false); }}
                            style={{ width:"100%", padding:"10px 14px", background:"none", border:"none", borderTop:i>0?`1px solid ${t.border}`:"none", color:t.t1, fontSize:13, cursor:"pointer", textAlign:"left", fontFamily:"'DM Sans',sans-serif", display:"flex", alignItems:"center", gap:8 }}>
                            <svg aria-hidden="true" focusable="false" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={t.t4} strokeWidth="2"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg>
                            <span style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{s.label}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {editAddrCoords && <div style={{ fontSize:11, color:"#22C55E", marginTop:4, display:"flex", alignItems:"center", gap:4 }}>✓ Location pinned on map</div>}
                </div>
                {[{label:"Phone",val:editPhone,set:setEditPhone,ph:"+1 (905) 555-0100",type:"tel"},{label:"Instagram",val:editInsta,set:setEditInsta,ph:"@yourshop",type:"text"}].map(f=>(
                  <div key={f.label}>
                    <div style={{ fontSize:11, fontWeight:700, color:t.t3, marginBottom:3 }}>{f.label}</div>
                    <input type={f.type} value={f.val} onChange={e=>f.set(e.target.value)} placeholder={f.ph}
                      style={{ width:"100%", padding:"11px 14px", borderRadius:12, border:`1.5px solid ${t.border2}`, background:t.section, color:t.inputText, fontSize:13, outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box" }}/>
                  </div>
                ))}
                <div>
                  <div style={{ fontSize:11, fontWeight:700, color:t.t3, marginBottom:3 }}>About Your Shop</div>
                  <textarea value={editDesc} onChange={e=>setEditDesc(e.target.value)} placeholder="Tell barbers what makes your shop special — vibe, clientele, perks…" rows={3}
                    style={{ width:"100%", padding:"11px 14px", borderRadius:12, border:`1.5px solid ${t.border2}`, background:t.section, color:t.inputText, fontSize:13, outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box", resize:"none" }}/>
                </div>
                <div>
                  <div style={{ fontSize:11, fontWeight:700, color:t.t3, marginBottom:3 }}>Hours (e.g. Mon–Sat 9am–7pm)</div>
                  <input value={editHours} onChange={e=>setEditHours(e.target.value)} placeholder="Mon–Sat 9am–7pm, Sun closed"
                    style={{ width:"100%", padding:"11px 14px", borderRadius:12, border:`1.5px solid ${t.border2}`, background:t.section, color:t.inputText, fontSize:13, outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box" }}/>
                </div>
                {/* Payment model */}
                <div>
                  <div style={{ fontSize:11, fontWeight:700, color:t.t3, marginBottom:6 }}>How do your barbers get paid?</div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                    {[{ val:"booth_rental", label:"Booth Rental", sub:"Barbers collect their own payments" },{ val:"shop_collects", label:"Shop Collects", sub:"Barbers are hourly/salary employees" }].map(opt=>(
                      <button key={opt.val} onClick={()=>setEditPaymentModel(opt.val)}
                        style={{ padding:"12px 10px", borderRadius:12, border:`1.5px solid ${editPaymentModel===opt.val?"#2563EB":t.border}`, background:editPaymentModel===opt.val?"rgba(37,99,235,0.1)":t.section, cursor:"pointer", textAlign:"left" }}>
                        <div style={{ fontSize:12, fontWeight:800, color:editPaymentModel===opt.val?t.blueText:t.t1, marginBottom:3 }}>{opt.label}</div>
                        <div style={{ fontSize:10, color:t.t4, lineHeight:1.4 }}>{opt.sub}</div>
                      </button>
                    ))}
                  </div>
                </div>
                {editPaymentModel === "booth_rental" && (<>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                  <div>
                    <div style={{ fontSize:11, fontWeight:700, color:t.t3, marginBottom:3 }}>Available Booths</div>
                    <input type="number" min={0} value={editBooths} onChange={e=>setEditBooths(e.target.value)}
                      style={{ width:"100%", padding:"11px 14px", borderRadius:12, border:`1.5px solid ${t.border2}`, background:t.section, color:t.inputText, fontSize:13, outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box" }}/>
                  </div>
                  <div>
                    <div style={{ fontSize:11, fontWeight:700, color:t.t3, marginBottom:3 }}>Starting Rent $/wk</div>
                    <input type="number" min={0} value={editBoothPx} onChange={e=>setEditBoothPx(e.target.value)} placeholder="150"
                      style={{ width:"100%", padding:"11px 14px", borderRadius:12, border:`1.5px solid ${t.border2}`, background:t.section, color:t.inputText, fontSize:13, outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box" }}/>
                  </div>
                </div>
                {/* Rent frequency options */}
                <div>
                  <div style={{ fontSize:11, fontWeight:700, color:t.t3, marginBottom:6 }}>Rent Frequency Options</div>
                  <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                    {["daily","weekly","biweekly","monthly"].map(opt=>(
                      <button key={opt} onClick={()=>setEditRentOpts(prev=>prev.includes(opt)?prev.filter(x=>x!==opt):[...prev,opt])}
                        style={{ padding:"6px 12px", borderRadius:20, border:`1.5px solid ${editRentOpts.includes(opt)?"#2563EB":t.border}`, background:editRentOpts.includes(opt)?"rgba(37,99,235,0.1)":t.section, color:editRentOpts.includes(opt)?t.blueText:t.t3, fontSize:11, fontWeight:700, cursor:"pointer", textTransform:"capitalize" }}>
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
                </>)}
                {/* Amenities */}
                <div>
                  <div style={{ fontSize:11, fontWeight:700, color:t.t3, marginBottom:6 }}>Amenities</div>
                  <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                    {["WiFi","Parking","Wash Station","Supplies Provided","Reception","Private Suite","A/C","Late Hours","Street Parking","Wheelchair Accessible"].map(a=>(
                      <button key={a} onClick={()=>setEditAmenities(prev=>prev.includes(a)?prev.filter(x=>x!==a):[...prev,a])}
                        style={{ padding:"6px 12px", borderRadius:20, border:`1.5px solid ${editAmenities.includes(a)?"#22C55E":t.border}`, background:editAmenities.includes(a)?"rgba(34,197,94,0.1)":t.section, color:editAmenities.includes(a)?"#22C55E":t.t3, fontSize:11, fontWeight:700, cursor:"pointer" }}>
                        {a}
                      </button>
                    ))}
                  </div>
                </div>
                <button onClick={saveShopDetails} disabled={saving}
                  style={{ padding:12, background:"#2563EB", border:"none", borderRadius:12, color:"#fff", fontSize:13, fontWeight:800, cursor:"pointer", opacity:saving?0.7:1 }}>
                  {saving ? "Saving…" : "Save Changes"}
                </button>
              </div>
            ) : (
              <>
                <div style={{ fontSize:14, fontWeight:700, color:t.t1, marginBottom:4 }}>{shop.name}</div>
                {shop.address && <div style={{ fontSize:12, color:t.t4, marginBottom:2 }}>📍 {shop.address}</div>}
                {shop.open_hours && <div style={{ fontSize:12, color:t.t4, marginBottom:2 }}>🕐 {shop.open_hours}</div>}
                {shop.phone && <div style={{ fontSize:12, color:t.t4, marginBottom:2 }}>📞 {shop.phone}</div>}
                {shop.instagram && <div style={{ fontSize:12, color:"#7C3AED", marginBottom:2 }}>{shop.instagram}</div>}
                {shop.description && <div style={{ fontSize:12, color:t.t3, marginTop:6, lineHeight:1.6 }}>{shop.description}</div>}
                {(shop.available_booths > 0) && <div style={{ marginTop:8, fontSize:12, fontWeight:700, color:"#22C55E" }}>🪑 {shop.available_booths} booth{shop.available_booths!==1?"s":""} available{shop.booth_price_from?` · from $${shop.booth_price_from}/wk`:""}</div>}
                {(shop.amenities||[]).length > 0 && <div style={{ display:"flex", gap:4, flexWrap:"wrap", marginTop:8 }}>{(shop.amenities||[]).map(a=><span key={a} style={{ padding:"3px 8px", borderRadius:20, background:"rgba(34,197,94,0.1)", color:"#22C55E", fontSize:11, fontWeight:700 }}>{a}</span>)}</div>}
                <div style={{ fontSize:11, color:t.t4, marginTop:6 }}>Created {new Date(shop.created_at).toLocaleDateString([],{month:"long",year:"numeric"})}</div>
              </>
            )}
          </div>
          <div style={{ background:"rgba(37,99,235,0.06)", borderRadius:14, border:"1px solid rgba(37,99,235,0.15)", padding:"12px 14px" }}>
            <div style={{ fontSize:11, color:t.t3, lineHeight:1.7 }}>
              💡 Fill in amenities and hours to attract the right barbers faster. Shops with complete profiles get 3× more booth inquiries.
            </div>
          </div>

          {/* Delete shop */}
          <div style={{ marginTop:24, background:"rgba(239,68,68,0.05)", borderRadius:14, border:"1px solid rgba(239,68,68,0.2)", padding:"14px 16px" }}>
            <div style={{ fontSize:12, fontWeight:800, color:"#EF4444", marginBottom:4 }}>Danger Zone</div>
            <div style={{ fontSize:12, color:t.t4, marginBottom:12 }}>Permanently delete this shop. All members will be removed and booth data will be lost.</div>
            <button onClick={async () => {
              if (!window.confirm(`Delete "${shop?.name}" permanently? This cannot be undone.`)) return;
              await supabase.from("shop_members").delete().eq("shop_id", shop.id);
              await supabase.from("shops").delete().eq("id", shop.id);
              window.location.reload();
            }} style={{ padding:"10px 18px", background:"rgba(239,68,68,0.1)", border:"1px solid rgba(239,68,68,0.4)", borderRadius:10, color:"#EF4444", fontSize:13, fontWeight:800, cursor:"pointer" }}>
              Delete Shop
            </button>
          </div>
        </div>
      )}

      {/* ── Pay modal ── */}
      {payModal && (
        <div className="faded-sheet-wrap" style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:2000, display:"flex", alignItems:"flex-end", justifyContent:"center" }} onClick={()=>setPayModal(null)}>
          <div style={{ background:t.card, borderRadius:"24px 24px 0 0", padding:"24px 20px 36px", width:"100%", maxWidth:480 }} onClick={e=>e.stopPropagation()}>
            <div style={{ fontSize:16, fontWeight:800, color:t.t1, marginBottom:16 }}>Record Payment — {payModal.barbers?.profiles?.full_name || payModal.barbers?.handle || "Barber"}</div>
            {[{label:"Amount ($)",val:payAmt,set:setPayAmt,type:"number",ph:`${payModal.booth_rent_weekly}`},{label:"Period",val:payLabel,set:setPayLabel,type:"text",ph:"Week of May 19"},{label:"Notes (optional)",val:payNote,set:setPayNote,type:"text",ph:"Cash / Interac / etc."}].map(f=>(
              <div key={f.label} style={{ marginBottom:12 }}>
                <div style={{ fontSize:11, fontWeight:700, color:t.t3, marginBottom:4 }}>{f.label}</div>
                <input type={f.type} value={f.val} onChange={e=>f.set(e.target.value)} placeholder={f.ph}
                  style={{ width:"100%", padding:"11px 14px", borderRadius:12, border:`1.5px solid ${t.border2}`, background:t.section, color:t.inputText, fontSize:14, outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box" }}/>
              </div>
            ))}
            <button onClick={recordPayment} disabled={saving}
              style={{ width:"100%", padding:14, background:"#22C55E", border:"none", borderRadius:14, color:"#fff", fontSize:15, fontWeight:800, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", opacity:saving?0.7:1 }}>
              {saving ? "Saving…" : "✓ Record Payment"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── INQUIRIES TAB ────────────────────────────────────────────────────────────
function InquiriesTab({ shopId, t }) {
  const [inquiries, setInquiries] = useState([]);
  const [loading,   setLoading]   = useState(true);

  useEffect(() => {
    supabase.from("shop_inquiries")
      .select("*")
      .eq("shop_id", shopId)
      .order("created_at", { ascending: false })
      .then(({ data }) => { setInquiries(data||[]); setLoading(false); });
  }, [shopId]);

  const markRead = async (id) => {
    await supabase.from("shop_inquiries").update({ status:"read" }).eq("id", id);
    setInquiries(prev => prev.map(i => i.id === id ? { ...i, status:"read" } : i));
  };

  if (loading) return <div style={{ padding:24, textAlign:"center", color:t.t4, fontSize:13 }}>Loading inquiries…</div>;

  return (
    <div style={{ padding:"0 16px" }}>
      {inquiries.length === 0 ? (
        <div style={{ textAlign:"center", padding:"40px 0" }}>
          <div style={{ fontSize:36, marginBottom:12 }}>💬</div>
          <div style={{ fontSize:15, fontWeight:800, color:t.t1, fontFamily:"'DM Sans',sans-serif", marginBottom:6 }}>No inquiries yet</div>
          <div style={{ fontSize:12, color:t.t4, lineHeight:1.6 }}>When barbers inquire about booth space at your shop, their messages will appear here.</div>
        </div>
      ) : inquiries.map(inq => (
        <div key={inq.id} onClick={()=>{ if(inq.status==="new") markRead(inq.id); }}
          style={{ background:t.card, borderRadius:16, border:`1.5px solid ${inq.status==="new"?"#2563EB44":t.border}`, padding:"14px 16px", marginBottom:10, cursor:"pointer", position:"relative" }}>
          {inq.status === "new" && (
            <div style={{ position:"absolute", top:14, right:14, width:8, height:8, borderRadius:"50%", background:"#2563EB" }}/>
          )}
          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
            <div style={{ width:32, height:32, borderRadius:"50%", background:"rgba(37,99,235,0.12)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, flexShrink:0 }}>✂️</div>
            <div>
              <div style={{ fontSize:13, fontWeight:800, color:t.t1 }}>{inq.barber_name || "A barber"}</div>
              <div style={{ fontSize:11, color:t.t4 }}>{new Date(inq.created_at).toLocaleDateString([],{month:"short",day:"numeric",year:"numeric"})}</div>
            </div>
            <div style={{ marginLeft:"auto", fontSize:11, fontWeight:700, padding:"3px 8px", borderRadius:20, background:inq.status==="new"?"rgba(37,99,235,0.12)":"rgba(107,114,128,0.1)", color:inq.status==="new"?"#60A5FA":"#9CA3AF" }}>
              {inq.status === "new" ? "NEW" : "READ"}
            </div>
          </div>
          <div style={{ fontSize:13, color:t.t2, lineHeight:1.6, background:t.section, borderRadius:10, padding:"10px 12px" }}>{inq.message}</div>
        </div>
      ))}
    </div>
  );
}

// ─── BOOTH BROWSE (barbers find available shops) ───────────────────────────────
const AMENITY_ICONS = { "WiFi":"📶","Parking":"🅿️","Wash Station":"🚿","Supplies Provided":"🧴","Reception":"💁","Private Suite":"🚪","A/C":"❄️","Late Hours":"🌙","Street Parking":"🛣️","Wheelchair Accessible":"♿" };

function BoothBrowse({ session, isTestBarber }) {
  const { t } = useT();
  const { isMd } = useR();
  const [shops,    setShops]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [selected, setSelected] = useState(null);
  const [search,   setSearch]   = useState("");
  const [sortBy,   setSortBy]   = useState("recent"); // recent | price | booths
  const [filterAmenity, setFilterAmenity] = useState(null);

  useEffect(() => {
    let q = supabase.from("shops")
      .select("*, shop_members(barber_id,invite_status)")
      .gt("available_booths", 0)
      .order("updated_at", { ascending: false })
      .limit(80);
    if (!isTestBarber) q = q.or("is_demo.is.null,is_demo.eq.false");
    q.then(({ data }) => { setShops(data||[]); setLoading(false); });
  }, []);

  const filtered = shops
    .filter(s => {
      const q = search.toLowerCase();
      const matchSearch = !search || s.name?.toLowerCase().includes(q) || s.city?.toLowerCase().includes(q) || s.address?.toLowerCase().includes(q);
      const matchAmenity = !filterAmenity || (s.amenities||[]).includes(filterAmenity);
      return matchSearch && matchAmenity;
    })
    .sort((a,b) => {
      if (sortBy==="price") return (a.booth_price_from||9999) - (b.booth_price_from||9999);
      if (sortBy==="booths") return (b.available_booths||0) - (a.available_booths||0);
      return 0; // recent — already ordered by DB
    });

  // Collect all amenities across shops for filter chips
  const allAmenities = [...new Set(shops.flatMap(s=>s.amenities||[]))].slice(0,8);

  return (
    <div style={{ maxWidth: isMd ? 960 : "100%", margin:"0 auto", padding: isMd ? "28px 32px 60px" : "16px 16px 20px" }}>

      {/* Search */}
      <div style={{ position:"relative", marginBottom:10 }}>
        <span style={{ position:"absolute", left:12, top:"50%", transform:"translateY(-50%)", fontSize:14, pointerEvents:"none" }}>🔍</span>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search by shop name or city…"
          style={{ width:"100%", padding:"11px 12px 11px 36px", borderRadius:12, border:`1.5px solid ${t.border}`, background:t.section, fontSize:16, color:t.inputText, outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box" }}/>
      </div>

      {/* Sort + amenity filters — wrap so chips never scroll horizontally */}
      <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:20 }}>
        {/* Sort group */}
        {[["recent","🕐 Recent"],["price","💰 Price ↑"],["booths","🪑 Most Booths"]].map(([val,label])=>(
          <button key={val} onClick={()=>setSortBy(val)}
            style={{ padding:"6px 14px", borderRadius:20, border:`1.5px solid ${sortBy===val?"#2563EB":t.border}`, background:sortBy===val?"rgba(37,99,235,0.1)":t.section, color:sortBy===val?t.blueText:t.t3, fontSize:12, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap" }}>
            {label}
          </button>
        ))}
        {/* Amenity filter chips — shown inline, wrap naturally */}
        {allAmenities.slice(0,6).map(a=>(
          <button key={a} onClick={()=>setFilterAmenity(filterAmenity===a?null:a)}
            style={{ padding:"6px 14px", borderRadius:20, border:`1.5px solid ${filterAmenity===a?"#22C55E":t.border}`, background:filterAmenity===a?"rgba(34,197,94,0.1)":t.section, color:filterAmenity===a?"#22C55E":t.t3, fontSize:12, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap" }}>
            {AMENITY_ICONS[a]||"✓"} {a}
          </button>
        ))}
        {allAmenities.length > 6 && (
          <button onClick={()=>setFilterAmenity(null)}
            style={{ padding:"6px 12px", borderRadius:20, border:`1.5px solid ${t.border}`, background:"transparent", color:t.t4, fontSize:12, fontWeight:600, cursor:"pointer" }}>
            +{allAmenities.length - 6} more
          </button>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign:"center", padding:60, color:t.t4, fontSize:13 }}>Loading available booths…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign:"center", padding:"80px 0" }}>
          <div style={{ fontSize:48, marginBottom:16 }}>🏪</div>
          <div style={{ fontSize:16, fontWeight:800, color:t.t1, marginBottom:8 }}>{search||filterAmenity ? "No shops match" : "No available booths yet"}</div>
          <div style={{ fontSize:13, color:t.t4 }}>{search||filterAmenity ? "Try adjusting your search or filters." : "Check back soon — shops will list available space here."}</div>
        </div>
      ) : (
        <div style={{ display:"grid", gridTemplateColumns: isMd ? "repeat(2,1fr)" : "1fr", gap:16 }}>
          {filtered.map(s => {
            const activeCount = (s.shop_members||[]).filter(m=>m.invite_status==="active").length + (s.owner_id ? 1 : 0);
            const hasCover = !!(s.cover_photo_url);
            const rentLabel = s.rent_options?.length ? s.rent_options.map(r=>r.charAt(0).toUpperCase()+r.slice(1)).join(" · ") : "Weekly";
            return (
              <div key={s.id} onClick={()=>setSelected(s)} style={{ background:t.card, borderRadius:20, border:`1.5px solid ${t.border}`, overflow:"hidden", cursor:"pointer" }}>
                {/* Cover photo or gradient banner */}
                <div style={{ height:110, background:hasCover?`url(${s.cover_photo_url}) center/cover no-repeat`:"linear-gradient(135deg,#0D1117 0%,#0f1e3d 60%,#0D1117 100%)", position:"relative" }}>
                  <div style={{ position:"absolute", inset:0, background:"linear-gradient(180deg,rgba(0,0,0,0.1) 0%,rgba(0,0,0,0.6) 100%)" }}/>
                  {/* Available badge */}
                  <div style={{ position:"absolute", top:10, right:10, background:"rgba(34,197,94,0.9)", borderRadius:20, padding:"4px 10px", fontSize:11, fontWeight:800, color:"#fff", backdropFilter:"blur(6px)" }}>
                    🪑 {s.available_booths} open
                  </div>
                  {/* Price badge */}
                  {s.booth_price_from && (
                    <div style={{ position:"absolute", bottom:10, left:12, background:"rgba(0,0,0,0.65)", borderRadius:20, padding:"5px 12px", fontSize:12, fontWeight:800, color:"#fff", backdropFilter:"blur(6px)" }}>
                      from <span style={{ color:"#22C55E" }}>${s.booth_price_from}</span>/wk
                    </div>
                  )}
                  {!hasCover && (
                    <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center" }}>
                      <div style={{ fontSize:11, fontWeight:800, color:"rgba(255,255,255,0.25)", letterSpacing:"0.15em" }}>{s.name?.toUpperCase()}</div>
                    </div>
                  )}
                </div>

                {/* Card body */}
                <div style={{ padding:"14px 16px 16px" }}>
                  <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:8 }}>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:15, fontWeight:900, color:t.t1, fontFamily:"'DM Sans',sans-serif" }}>{s.name}</div>
                      {(s.address||s.city) && <div style={{ fontSize:11, color:t.t4, marginTop:2 }}>📍 {s.address||s.city}</div>}
                      {s.open_hours && <div style={{ fontSize:11, color:t.t4, marginTop:1 }}>🕐 {s.open_hours}</div>}
                    </div>
                    {activeCount > 0 && (
                      <div style={{ flexShrink:0, background:t.section, border:`1px solid ${t.border}`, borderRadius:20, padding:"4px 10px", fontSize:11, color:t.t4, marginLeft:8 }}>
                        ✂️ {activeCount} barber{activeCount!==1?"s":""}
                      </div>
                    )}
                  </div>

                  {/* Rent options */}
                  <div style={{ fontSize:11, color:t.t3, marginBottom:8 }}>
                    <span style={{ fontWeight:700, color:t.t2 }}>Rent: </span>{rentLabel}
                  </div>

                  {/* Amenities chips */}
                  {(s.amenities||[]).length > 0 && (
                    <div style={{ display:"flex", gap:5, flexWrap:"wrap", marginBottom:12 }}>
                      {(s.amenities||[]).slice(0,5).map(a=>(
                        <div key={a} style={{ padding:"3px 9px", borderRadius:20, background:"rgba(34,197,94,0.08)", border:"1px solid rgba(34,197,94,0.2)", fontSize:11, fontWeight:700, color:"#22C55E" }}>
                          {AMENITY_ICONS[a]||"✓"} {a}
                        </div>
                      ))}
                      {(s.amenities||[]).length > 5 && <div style={{ padding:"3px 9px", borderRadius:20, background:t.section, border:`1px solid ${t.border}`, fontSize:11, color:t.t4 }}>+{s.amenities.length-5} more</div>}
                    </div>
                  )}

                  {/* Description */}
                  {s.description && <div style={{ fontSize:12, color:t.t3, lineHeight:1.6, marginBottom:12 }}>{s.description.slice(0,110)}{s.description.length>110?"…":""}</div>}

                  <button onClick={e=>{ e.stopPropagation(); setSelected(s); }}
                    style={{ width:"100%", padding:"11px", background:"#2563EB", border:"none", borderRadius:12, color:"#fff", fontSize:13, fontWeight:800, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>
                    Inquire About This Booth →
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selected && <BoothInquiryModal shop={selected} session={session} onClose={()=>setSelected(null)}/>}
    </div>
  );
}

// ─── SHOP PROFILE MODAL ───────────────────────────────────────────────────────
function ShopProfileModal({ shop, barbers, onClose, onBookBarber, session }) {
  const { t } = useT();
  const { isMd } = useR();
  const [showInquiry, setShowInquiry] = useState(false);
  // Owners aren't in shop_members (that's for renters/employees who joined someone else's
  // shop) — include the owner directly so a barber who owns and also cuts at their own shop
  // still shows up in their own roster.
  const activeBarbers = barbers.filter(b => b.id === shop.owner_id || shop.shop_members?.some(m => m.barber_id === b.id && m.invite_status === "active"));
  const accent = "#2563EB";
  const avgRating = activeBarbers.length > 0
    ? (activeBarbers.reduce((s,b) => s + (b.rating||0), 0) / activeBarbers.filter(b=>b.rating).length).toFixed(1)
    : null;

  return (
    <>
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.72)", zIndex:3000, display:"flex", alignItems:isMd?"center":"flex-end", justifyContent:"center" }} onClick={onClose}>
      <div style={{ background:t.card, borderRadius:isMd?24:"28px 28px 0 0", width:"100%", maxWidth:isMd?640:520, maxHeight:isMd?"88vh":"92vh", overflowY:"auto", display:"flex", flexDirection:"column" }} onClick={e=>e.stopPropagation()}>

        {/* ── Hero cover ── */}
        <div style={{ position:"relative", background:`linear-gradient(135deg,${accent}dd,${accent}99,#0f0f0f)`, borderRadius:isMd?"24px 24px 0 0":"28px 28px 0 0", padding:"28px 20px 20px", flexShrink:0 }}>
          <button onClick={onClose} aria-label="Close" style={{ position:"absolute", top:14, right:14, minWidth:44, minHeight:44, width:44, height:44, borderRadius:"50%", background:"rgba(255,255,255,0.12)", border:"none", color:"#fff", fontSize:16, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", backdropFilter:"blur(6px)" }}><span aria-hidden="true">✕</span></button>
          <div style={{ width:56, height:56, borderRadius:18, background:"rgba(255,255,255,0.15)", display:"flex", alignItems:"center", justifyContent:"center", marginBottom:12, backdropFilter:"blur(4px)" }}>
            <svg aria-hidden="true" focusable="false" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9l1.4-5.4A1 1 0 0 1 5.36 3h13.28a1 1 0 0 1 .96 1.6L21 9"/>
              <path d="M3 9a2.2 2.2 0 0 0 4.4 0 2.2 2.2 0 0 0 4.4 0 2.2 2.2 0 0 0 4.4 0 2.2 2.2 0 0 0 4.4 0"/>
              <path d="M4.5 9.5V19a1 1 0 0 0 1 1h13a1 1 0 0 0 1-1V9.5"/>
              <path d="M9.5 20v-6a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v6"/>
            </svg>
          </div>
          <div style={{ fontSize:isMd?26:22, fontWeight:900, color:"#fff", fontFamily:"'DM Sans',sans-serif", letterSpacing:"-0.02em", marginBottom:4 }}>{shop.name}</div>
          {shop.address && <div style={{ fontSize:12, color:"rgba(255,255,255,0.7)", marginBottom:2 }}>📍 {shop.address}</div>}
          {shop.city && !shop.address && <div style={{ fontSize:12, color:"rgba(255,255,255,0.7)", marginBottom:2 }}>📍 {shop.city}</div>}

          {/* ── Stats row ── */}
          <div style={{ display:"flex", gap:8, marginTop:14, flexWrap:"wrap" }}>
            {activeBarbers.length > 0 && (
              <div style={{ background:"rgba(255,255,255,0.12)", borderRadius:20, padding:"5px 12px", fontSize:11, fontWeight:700, color:"#fff", backdropFilter:"blur(4px)" }}>
                ✂️ {activeBarbers.length} barber{activeBarbers.length!==1?"s":""}
              </div>
            )}
            {avgRating && (
              <div style={{ background:"rgba(255,255,255,0.12)", borderRadius:20, padding:"5px 12px", fontSize:11, fontWeight:700, color:"#fff", backdropFilter:"blur(4px)" }}>
                ⭐ {avgRating} avg
              </div>
            )}
            {shop.available_booths > 0 && (
              <div style={{ background:"rgba(34,197,94,0.3)", borderRadius:20, padding:"5px 12px", fontSize:11, fontWeight:700, color:"#86efac", backdropFilter:"blur(4px)" }}>
                🪑 {shop.available_booths} booth{shop.available_booths!==1?"s":""} available
              </div>
            )}
            {shop.instagram && (
              <div style={{ background:"rgba(124,58,237,0.3)", borderRadius:20, padding:"5px 12px", fontSize:11, fontWeight:700, color:"#c4b5fd", backdropFilter:"blur(4px)" }}>
                {shop.instagram}
              </div>
            )}
          </div>
        </div>

        {/* ── Body ── */}
        <div style={{ padding:"18px 20px 36px", flex:1, overflowY:"auto" }}>

          {/* Description */}
          {shop.description && (
            <div style={{ background:t.section, borderRadius:14, padding:"14px 16px", marginBottom:16, fontSize:13, color:t.t2, lineHeight:1.7, border:`1px solid ${t.border}` }}>{shop.description}</div>
          )}

          {/* Booth rental CTA — only shown to barbers */}
          {shop.available_booths > 0 && session && session !== "guest" && session?.user?.user_metadata?.role === "barber" && (
            <div style={{ background:"rgba(34,197,94,0.07)", border:"1.5px solid rgba(34,197,94,0.3)", borderRadius:16, padding:"14px 16px", marginBottom:16, display:"flex", alignItems:"center", justifyContent:"space-between", gap:12 }}>
              <div>
                <div style={{ fontSize:13, fontWeight:800, color:"#22C55E", marginBottom:2 }}>🪑 Booth space available</div>
                <div style={{ fontSize:11, color:t.t3 }}>{shop.available_booths} seat{shop.available_booths!==1?"s":""}{shop.booth_price_from ? ` · from $${shop.booth_price_from}/wk` : ""}</div>
              </div>
              <button onClick={e=>{ e.stopPropagation(); setShowInquiry(true); }} style={{ padding:"9px 16px", background:"#22C55E", border:"none", borderRadius:12, color:"#fff", fontSize:12, fontWeight:800, cursor:"pointer", whiteSpace:"nowrap", flexShrink:0 }}>
                Inquire →
              </button>
            </div>
          )}

          {/* Contact row */}
          {(shop.phone || shop.instagram) && (
            <div style={{ display:"flex", gap:8, marginBottom:16, flexWrap:"wrap" }}>
              {shop.phone && (
                <a href={`tel:${shop.phone}`} style={{ padding:"8px 16px", background:t.section, border:`1px solid ${t.border}`, borderRadius:20, fontSize:12, fontWeight:700, color:t.t2, textDecoration:"none", display:"flex", alignItems:"center", gap:6 }}>
                  📞 {shop.phone}
                </a>
              )}
              {shop.instagram && (
                <a href={`https://instagram.com/${shop.instagram.replace(/^@/,"")}`} target="_blank" rel="noopener noreferrer" style={{ padding:"8px 16px", background:t.section, border:`1px solid ${t.border}`, borderRadius:20, fontSize:12, fontWeight:700, color:"#7C3AED", textDecoration:"none", display:"flex", alignItems:"center", gap:6 }}>
                  Instagram ↗
                </a>
              )}
            </div>
          )}

          {/* Barbers section */}
          <div style={{ fontSize:12, fontWeight:800, color:t.t3, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:12 }}>
            Barbers at this shop
          </div>
          {activeBarbers.length === 0 ? (
            <div style={{ textAlign:"center", padding:"32px 0", color:t.t4, fontSize:13 }}>No barbers listed yet</div>
          ) : (
            <div style={{ display:"grid", gridTemplateColumns:isMd?"1fr 1fr":"1fr", gap:10 }}>
              {activeBarbers.map(b => {
                const a = b.accent_color || "#2563EB";
                const isAvail = b.status === "available";
                return (
                  <div key={b.id} onClick={()=>{ onClose(); onBookBarber(b); }} style={{ background:t.section, borderRadius:18, padding:"14px 14px 12px", border:`1.5px solid ${isAvail ? a+"44" : t.border}`, position:"relative", overflow:"hidden", transition:"box-shadow 0.2s", cursor:"pointer" }}>
                    {isAvail && <div style={{ position:"absolute", top:0, left:0, right:0, height:3, background:`linear-gradient(90deg,${a},${a}66)` }}/>}
                    <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
                      <Avatar barber={b} size={40}/>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:13, fontWeight:800, color:t.t1, wordBreak:"break-word" }}>{b.full_name}</div>
                        <div style={{ fontSize:11, color:t.t4 }}>⭐ {b.rating||"—"} · {b.review_count||0} reviews</div>
                      </div>
                    </div>
                    {(b.specialty||[]).length > 0 && (
                      <div style={{ fontSize:11, color:t.t3, marginBottom:8, lineHeight:1.5 }}>{b.specialty.slice(0,3).join(" · ")}</div>
                    )}
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:4, background:t.STATUS?.[b.status]?.bg||"rgba(107,114,128,0.12)", padding:"3px 8px", borderRadius:20 }}>
                        <Dot status={b.status}/>
                        <span style={{ fontSize:11, fontWeight:700, color:t.STATUS?.[b.status]?.color||t.t3 }}>{isAvail?"Available":"Offline"}</span>
                      </div>
                      <button onClick={e=>{ e.stopPropagation(); onClose(); onBookBarber(b); }} style={{ padding:"7px 14px", background:isAvail?a:"#374151", border:"none", borderRadius:10, color:"#fff", fontSize:11, fontWeight:700, cursor:"pointer", opacity:isAvail?1:0.6 }}>
                        {isAvail?"Book Now":"Book"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
    {showInquiry && <BoothInquiryModal shop={shop} session={session} onClose={()=>setShowInquiry(false)}/>}
    </>
  );
}

// ─── BOOTH INQUIRY MODAL ──────────────────────────────────────────────────────
function BoothInquiryModal({ shop, session, onClose }) {
  const { t } = useT();
  const { isMd } = useR();
  const [startDate,  setStartDate]  = useState("");
  const [rentPref,   setRentPref]   = useState((shop.rent_options||["weekly"])[0]);
  const [daysWanted, setDaysWanted] = useState([]);
  const [message,    setMessage]    = useState("");
  const [sending,    setSending]    = useState(false);
  const [sent,       setSent]       = useState(false);
  const [err,        setErr]        = useState("");

  const days = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
  const toggleDay = d => setDaysWanted(prev => prev.includes(d) ? prev.filter(x=>x!==d) : [...prev,d]);

  const send = async () => {
    if (!message.trim()) return;
    setSending(true); setErr("");
    try {
      const name = session?.user?.user_metadata?.full_name || session?.user?.email || "A barber";
      const fullMsg = [
        startDate    ? `Start date: ${startDate}` : "",
        daysWanted.length ? `Preferred days: ${daysWanted.join(", ")}` : "",
        rentPref     ? `Rent preference: ${rentPref}` : "",
        message.trim(),
      ].filter(Boolean).join("\n");
      const { error } = await supabase.from("shop_inquiries").insert({
        shop_id: shop.id, barber_id: session.user.id, barber_name: name, message: fullMsg,
      });
      if (error) throw error;
      setSent(true);
    } catch { setErr("Couldn't send — please try again."); }
    finally { setSending(false); }
  };

  const canSend = message.trim().length > 0;

  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.82)", zIndex:4000, display:"flex", alignItems:isMd?"center":"flex-end", justifyContent:"center" }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:t.card, borderRadius:isMd?24:"24px 24px 0 0", width:"100%", maxWidth:isMd?520:520, maxHeight:isMd?"90vh":"94vh", overflowY:"auto", padding:"24px 22px 36px", animation:"slideUp 0.25s ease", border:`1px solid ${t.border}` }}>
        {!isMd && <div style={{ display:"flex", justifyContent:"center", marginBottom:14 }}><div style={{ width:40, height:4, background:t.surface2, borderRadius:2 }}/></div>}
        {sent ? (
          <div style={{ textAlign:"center", padding:"24px 0" }}>
            <div style={{ fontSize:52, marginBottom:14 }}>✅</div>
            <div style={{ fontSize:19, fontWeight:900, color:t.t1, fontFamily:"'DM Sans',sans-serif", marginBottom:8 }}>Inquiry Sent!</div>
            <div style={{ fontSize:13, color:t.t3, lineHeight:1.7, marginBottom:24 }}>
              The owner at <strong style={{ color:t.t1 }}>{shop.name}</strong> has been notified and will reach out through FADED.
            </div>
            <button onClick={onClose} style={{ width:"100%", padding:14, background:"#2563EB", border:"none", borderRadius:14, color:"#fff", fontSize:14, fontWeight:800, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>Done</button>
          </div>
        ) : (
          <>
            {/* Header */}
            <div style={{ marginBottom:18 }}>
              <div style={{ fontSize:18, fontWeight:900, color:t.t1, fontFamily:"'DM Sans',sans-serif" }}>🪑 Booth Inquiry</div>
              <div style={{ fontSize:12, color:t.t4, marginTop:3 }}>{shop.name}{shop.booth_price_from ? ` · from $${shop.booth_price_from}/wk` : ""}{shop.address ? ` · ${shop.address}` : ""}</div>
            </div>

            {/* Desired start date */}
            <div style={{ marginBottom:14 }}>
              <div style={{ fontSize:11, fontWeight:700, color:t.t3, marginBottom:5 }}>When do you want to start?</div>
              <input type="date" value={startDate} onChange={e=>setStartDate(e.target.value)} min={new Date().toISOString().split("T")[0]}
                style={{ width:"100%", padding:"11px 14px", borderRadius:12, border:`1.5px solid ${t.border}`, background:t.section, color:t.inputText, fontSize:13, outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box" }}/>
            </div>

            {/* Preferred days */}
            <div style={{ marginBottom:14 }}>
              <div style={{ fontSize:11, fontWeight:700, color:t.t3, marginBottom:6 }}>Which days? <span style={{ fontWeight:400, color:t.t4 }}>(select all that apply)</span></div>
              <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
                {days.map(d=>(
                  <button key={d} onClick={()=>toggleDay(d)}
                    style={{ padding:"7px 12px", borderRadius:20, border:`1.5px solid ${daysWanted.includes(d)?"#2563EB":t.border}`, background:daysWanted.includes(d)?"rgba(37,99,235,0.1)":t.section, color:daysWanted.includes(d)?t.blueText:t.t3, fontSize:12, fontWeight:700, cursor:"pointer" }}>
                    {d}
                  </button>
                ))}
              </div>
            </div>

            {/* Rent preference */}
            {(shop.rent_options||[]).length > 1 && (
              <div style={{ marginBottom:14 }}>
                <div style={{ fontSize:11, fontWeight:700, color:t.t3, marginBottom:6 }}>Rent frequency preference</div>
                <div style={{ display:"flex", gap:6 }}>
                  {(shop.rent_options||["weekly"]).map(opt=>(
                    <button key={opt} onClick={()=>setRentPref(opt)}
                      style={{ flex:1, padding:"8px", borderRadius:10, border:`1.5px solid ${rentPref===opt?"#2563EB":t.border}`, background:rentPref===opt?"rgba(37,99,235,0.1)":t.section, color:rentPref===opt?t.blueText:t.t3, fontSize:12, fontWeight:700, cursor:"pointer", textTransform:"capitalize" }}>
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Message */}
            <div style={{ marginBottom:14 }}>
              <div style={{ fontSize:11, fontWeight:700, color:t.t3, marginBottom:5 }}>Introduce yourself *</div>
              <textarea value={message} onChange={e=>setMessage(e.target.value)} rows={4}
                placeholder={`Hi! I'm a barber specialising in fades and tapers. I'm looking for a booth to grow my clientele and I'd love to know more about the space.`}
                style={{ width:"100%", padding:"12px 14px", borderRadius:12, border:`1.5px solid ${t.border}`, background:t.section, fontSize:16, color:t.inputText, outline:"none", fontFamily:"'DM Sans',sans-serif", resize:"none", boxSizing:"border-box", lineHeight:1.6 }}/>
            </div>

            {err && <div style={{ fontSize:12, color:"#EF4444", marginBottom:8 }}>{err}</div>}
            <button onClick={send} disabled={sending||!canSend}
              style={{ width:"100%", padding:14, background:canSend?"#2563EB":"#374151", border:"none", borderRadius:14, color:"#fff", fontSize:14, fontWeight:800, cursor:canSend?"pointer":"default", fontFamily:"'DM Sans',sans-serif", opacity:sending?0.7:1, transition:"background 0.2s" }}>
              {sending ? "Sending…" : "Send Inquiry →"}
            </button>
            <button onClick={onClose} style={{ width:"100%", marginTop:8, padding:"11px", background:"transparent", border:"none", color:t.t4, fontSize:13, cursor:"pointer" }}>Cancel</button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── ADMIN PANEL ──────────────────────────────────────────────────────────────
function AdminPanel({ session }) {
  const { t } = useT();
  const { isNavDesktop } = useR();
  const [adminTab, setAdminTab]     = useState("overview");
  const [stats, setStats]           = useState(null);
  const [allBarbers, setAllBarbers] = useState([]);
  const [allReviews, setAllReviews] = useState([]);
  const [allBookings, setAllBookings] = useState([]);
  const [recentSignups, setRecentSignups] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [actionMsg, setActionMsg]   = useState("");

  const flash = (msg) => { setActionMsg(msg); setTimeout(() => setActionMsg(""), 2500); };

  const load = async () => {
    setLoading(true);
    try {
      const [statsRes, barbersRes, reviewsRes, bookingsRes, signupsRes] = await Promise.all([
        supabase.rpc("admin_get_stats"),
        supabase.from("barbers").select("id, status, approved, specialty, city, rating, review_count, updated_at, handle, shareable_slug, profiles(full_name)").order("updated_at",{ascending:false}).limit(200),
        supabase.from("reviews").select("*").order("created_at",{ascending:false}).limit(200),
        supabase.rpc("admin_get_bookings", { lim: 200 }),
        supabase.from("profiles").select("id, full_name, role, created_at").neq("role","admin").order("created_at",{ascending:false}).limit(50),
      ]);
      if (statsRes.data) setStats(statsRes.data);
      setAllBarbers(barbersRes.data || []);
      setAllReviews(reviewsRes.data || []);
      setAllBookings(bookingsRes.data || []);
      setRecentSignups(signupsRes.data || []);
    } catch (e) { console.error("[FADED] admin load:", e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const approveBarber = async (id) => {
    await supabase.rpc("admin_approve_barber", { p_barber_id: id });
    setAllBarbers(prev => prev.map(b => b.id === id ? { ...b, approved: true, status: "offline" } : b));
    flash("✅ Barber approved — they can now go live");
  };

  const suspendBarber = async (id) => {
    if (!await fakedConfirm("Suspend this barber? They will be hidden from clients.")) return;
    await supabase.rpc("admin_set_barber_status", { p_barber_id: id, p_status: "suspended" });
    setAllBarbers(prev => prev.map(b => b.id === id ? { ...b, status: "suspended" } : b));
    flash("⛔ Barber suspended");
  };

  const deleteReview = async (id) => {
    if (!await fakedConfirm("Delete this review permanently?")) return;
    await supabase.rpc("admin_delete_review", { p_review_id: id });
    setAllReviews(prev => prev.filter(r => r.id !== id));
    flash("🗑️ Review deleted");
  };

  const cancelBookingAdmin = async (id) => {
    if (!await fakedConfirm("Cancel this booking?")) return;
    await supabase.from("bookings").update({ status: "cancelled" }).eq("id", id);
    setAllBookings(prev => prev.map(b => b.id === id ? { ...b, status: "cancelled" } : b));
    flash("Booking cancelled");
  };

  const pendingBarbers  = allBarbers.filter(b => !b.approved && !b.is_test);
  const activeBarbers   = allBarbers.filter(b => b.status === "available" && !b.is_test);
  const suspendedBarbers = allBarbers.filter(b => b.status === "suspended" && !b.is_test);

  const ADMIN_TABS = [
    { id:"overview",  label:"Overview" },
    { id:"barbers",   label:"Barbers" },
    { id:"reviews",   label:"Reviews" },
    { id:"bookings",  label:"Bookings" },
    { id:"payments",  label:"Payments" },
    { id:"blast",     label:"Blast" },
    { id:"referrals", label:"Referrals" },
  ];

  // ── Blast state ──────────────────────────────────────────────────────────────
  const [blastCounts, setBlastCounts]   = useState(null);
  const [blasting, setBlasting]         = useState(null); // 'barbers' | 'clients' | 'all'
  const [blastResult, setBlastResult]   = useState(null);

  const loadBlastCounts = async () => {
    const { data } = await supabase
      .from("waitlist_signups")
      .select("role, blast_sent_at");
    if (!data) return;
    const barberUnsent  = data.filter(r => r.role === "barber"  && !r.blast_sent_at).length;
    const clientUnsent  = data.filter(r => r.role === "client"  && !r.blast_sent_at).length;
    const barberTotal   = data.filter(r => r.role === "barber").length;
    const clientTotal   = data.filter(r => r.role === "client").length;
    setBlastCounts({ barberUnsent, clientUnsent, barberTotal, clientTotal });
  };

  useEffect(() => { if (adminTab === "blast") loadBlastCounts(); }, [adminTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Referral payouts ────────────────────────────────────────────────────────
  const [referralPayouts, setReferralPayouts] = useState([]);
  const [loadingReferrals, setLoadingReferrals] = useState(false);
  const [markingPaidId, setMarkingPaidId] = useState(null);

  const [barberFreeMonths, setBarberFreeMonths] = useState([]);
  const [campaignCodes, setCampaignCodes] = useState([]);
  const [expandedCampaignId, setExpandedCampaignId] = useState(null);
  const [campaignDetails, setCampaignDetails] = useState({}); // campaign_id -> rows
  const [newCampaignLabel, setNewCampaignLabel] = useState("");
  const [newCampaignAmount, setNewCampaignAmount] = useState("15");
  const [creatingCampaign, setCreatingCampaign] = useState(false);

  const loadReferralPayouts = async () => {
    setLoadingReferrals(true);
    const [payoutsRes, monthsRes, campaignsRes] = await Promise.all([
      supabase.rpc("get_referral_payouts_admin"),
      supabase.rpc("get_barber_free_months_admin"),
      supabase.rpc("get_campaign_payouts_admin"),
    ]);
    if (payoutsRes.error) console.error("[FADED] loadReferralPayouts:", payoutsRes.error.message);
    if (monthsRes.error) console.error("[FADED] loadBarberFreeMonths:", monthsRes.error.message);
    if (campaignsRes.error) console.error("[FADED] loadCampaigns:", campaignsRes.error.message);
    setReferralPayouts(payoutsRes.data || []);
    setBarberFreeMonths(monthsRes.data || []);
    setCampaignCodes(campaignsRes.data || []);
    setLoadingReferrals(false);
  };

  useEffect(() => { if (adminTab === "referrals") loadReferralPayouts(); }, [adminTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const markReferralPaid = async (id) => {
    setMarkingPaidId(id);
    const { error } = await supabase.from("referral_payouts").update({ paid_at: new Date().toISOString() }).eq("id", id);
    if (!error) {
      setReferralPayouts(prev => prev.map(r => r.id === id ? { ...r, paid_at: new Date().toISOString() } : r));
      flash("💸 Marked as paid");
    }
    setMarkingPaidId(null);
  };

  const createCampaignCode = async () => {
    if (!newCampaignLabel.trim()) return;
    setCreatingCampaign(true);
    const code = newCampaignLabel.trim().toUpperCase().replace(/[^A-Z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 24) + "-" + Math.random().toString(36).slice(2, 6).toUpperCase();
    const amountCents = Math.round((parseFloat(newCampaignAmount) || 15) * 100);
    const { error } = await supabase.from("campaign_codes").insert({ code, label: newCampaignLabel.trim(), reward_amount_cents: amountCents });
    if (!error) {
      setNewCampaignLabel("");
      flash("🔗 Campaign code created");
      loadReferralPayouts();
    }
    setCreatingCampaign(false);
  };

  const toggleCampaignDetails = async (campaignId) => {
    if (expandedCampaignId === campaignId) { setExpandedCampaignId(null); return; }
    setExpandedCampaignId(campaignId);
    if (!campaignDetails[campaignId]) {
      const { data } = await supabase.rpc("get_campaign_payout_details_admin", { p_campaign_id: campaignId });
      setCampaignDetails(prev => ({ ...prev, [campaignId]: data || [] }));
    }
  };

  const markCampaignPaid = async (id, campaignId) => {
    setMarkingPaidId(id);
    const { error } = await supabase.from("campaign_payouts").update({ paid_at: new Date().toISOString() }).eq("id", id);
    if (!error) {
      setCampaignDetails(prev => ({ ...prev, [campaignId]: prev[campaignId].map(r => r.id === id ? { ...r, paid_at: new Date().toISOString() } : r) }));
      flash("💸 Marked as paid");
      loadReferralPayouts();
    }
    setMarkingPaidId(null);
  };

  const sendBlast = async (audience) => {
    if (!await fakedConfirm(`Send "${audience}" blast now? This emails everyone who hasn't been blasted yet.`)) return;
    setBlasting(audience);
    setBlastResult(null);
    try {
      const res = await supabase.functions.invoke("send-launch-blast", { body: { audience } });
      setBlastResult(res.data || { error: res.error?.message });
      loadBlastCounts();
      flash(`🚀 Blast sent — ${res.data?.sent ?? 0} emails delivered`);
    } catch (e) {
      setBlastResult({ error: e.message });
    } finally {
      setBlasting(null);
    }
  };

  const statCard = (label, value, color, sub) => (
    <div key={label} style={{ background:t.card, borderRadius:18, padding:18, border:`1.5px solid ${t.border}`, textAlign:"center" }}>
      <div style={{ fontSize:22, fontWeight:900, color, fontFamily:"'DM Sans',sans-serif" }}>{value ?? "—"}</div>
      {sub && <div style={{ fontSize:11, color:t.t4, fontWeight:600, marginTop:2 }}>{sub}</div>}
      <div style={{ fontSize:11, color:t.t3, marginTop:4, fontWeight:600 }}>{label}</div>
    </div>
  );

  const statusColor = (s) => ({ available:"#22C55E", offline:"#888", pending:"#F59E0B", suspended:"#EF4444" })[s] || "#888";

  return (
    <div style={{ padding:"0 0 100px", maxWidth:isNavDesktop?960:"100%", margin:isNavDesktop?"0 auto":undefined }}>
      {actionMsg && (
        <div style={{ margin:"20px 16px 12px", padding:"11px 16px", background:"rgba(37,99,235,0.1)", border:"1px solid rgba(37,99,235,0.3)", borderRadius:12, fontSize:13, fontWeight:600, color:t.blueText }}>
          {actionMsg}
        </div>
      )}

      {/* Tab bar */}
      <div style={{ padding:`${actionMsg?12:20}px 16px 16px`, overflowX:"auto" }}>
        <div style={{ display:"inline-flex", gap:2, minWidth:"max-content", background:t.section, borderRadius:14, padding:4, border:`1px solid ${t.border}` }}>
          {ADMIN_TABS.map(tb => (
            <button key={tb.id} onClick={()=>setAdminTab(tb.id)}
              style={{ position:"relative", padding:"9px 16px", borderRadius:11, border:"none", background:adminTab===tb.id?t.card:"transparent", color:adminTab===tb.id?t.t1:t.t3, fontSize:12.5, fontWeight:adminTab===tb.id?700:600, cursor:"pointer", whiteSpace:"nowrap", boxShadow:adminTab===tb.id?"0 1px 4px rgba(0,0,0,0.12)":"none", transition:"all 0.15s" }}>
              {tb.label}
              {tb.id==="barbers" && pendingBarbers.length > 0 && <span style={{ marginLeft:6, background:"#EF4444", color:"#fff", borderRadius:20, padding:"1px 7px", fontSize:11 }}>{pendingBarbers.length}</span>}
              {tb.id==="overview" && pendingBarbers.length > 0 && <span style={{ marginLeft:6, background:"#F59E0B", color:"#fff", borderRadius:20, padding:"1px 7px", fontSize:11 }}>{pendingBarbers.length}</span>}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign:"center", padding:40, color:t.t4, fontSize:13 }}>Loading admin data…</div>
      ) : (
        <>
          {/* ── OVERVIEW ── */}
          {adminTab === "overview" && (
            <div style={{ padding:"0 16px" }}>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:16 }}>
                {statCard("Total Barbers", allBarbers.filter(b=>!b.is_test).length, t.t1)}
                {statCard("Live Right Now", stats?.active_barbers ?? activeBarbers.length, "#22C55E")}
                {statCard("Total Bookings", stats?.total_bookings ?? allBookings.length, t.t1)}
                {statCard("Completed", stats?.completed_bookings ?? allBookings.filter(b=>b.status==="completed").length, t.t1)}
                {statCard("Platform Revenue", stats?.total_revenue != null ? `$${Number(stats.total_revenue).toFixed(0)}` : "$—", "#F59E0B")}
                {statCard("Total Reviews", stats?.total_reviews ?? allReviews.length, t.t1)}
              </div>

              {/* Pending approvals banner */}
              {pendingBarbers.length > 0 && (
                <div style={{ background:"rgba(245,158,11,0.08)", border:"1.5px solid rgba(245,158,11,0.35)", borderRadius:16, padding:16, marginBottom:12 }}>
                  <div style={{ fontSize:13, fontWeight:800, color:"#F59E0B", marginBottom:8 }}>⚠️ {pendingBarbers.length} Barber{pendingBarbers.length!==1?"s":""} Awaiting Approval</div>
                  {pendingBarbers.slice(0,3).map(b => (
                    <div key={b.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 0", borderBottom:`1px solid ${t.divider}` }}>
                      <div>
                        <div style={{ fontSize:13, fontWeight:700, color:t.t1 }}>{b.profiles?.full_name || b.handle || "Unnamed"}</div>
                        <div style={{ fontSize:11, color:t.t4 }}>{b.city || "No city"} · {(b.specialty||[]).slice(0,2).join(", ")}</div>
                      </div>
                      <div style={{ display:"flex", gap:6 }}>
                        <button onClick={()=>approveBarber(b.id)} style={{ padding:"6px 12px", background:"rgba(34,197,94,0.12)", border:"1px solid rgba(34,197,94,0.3)", borderRadius:8, color:"#22C55E", fontSize:11, fontWeight:700, cursor:"pointer" }}>Approve</button>
                        <button onClick={()=>suspendBarber(b.id)} style={{ padding:"6px 12px", background:"rgba(220,38,38,0.08)", border:"1px solid rgba(220,38,38,0.2)", borderRadius:8, color:"#EF4444", fontSize:11, fontWeight:700, cursor:"pointer" }}>Reject</button>
                      </div>
                    </div>
                  ))}
                  {pendingBarbers.length > 3 && <button onClick={()=>setAdminTab("barbers")} style={{ marginTop:8, fontSize:12, color:t.blueText, fontWeight:700, background:"none", border:"none", cursor:"pointer" }}>See all {pendingBarbers.length} pending →</button>}
                </div>
              )}

              {/* Recent signups feed */}
              <div style={{ background:t.card, borderRadius:18, border:`1.5px solid ${t.border}`, padding:16, marginBottom:12 }}>
                <div style={{ fontSize:13, fontWeight:800, color:t.t1, marginBottom:12 }}>Recent Signups</div>
                {recentSignups.length === 0 && <div style={{ fontSize:12, color:t.t4 }}>No signups yet</div>}
                {recentSignups.slice(0,12).map((u,i,arr) => {
                  const isBarberRow = u.role === "barber";
                  const barberRec = isBarberRow ? allBarbers.find(b => b.id === u.id) : null;
                  const needsApproval = isBarberRow && barberRec && !barberRec.approved;
                  return (
                    <div key={u.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"9px 0", borderBottom:i<arr.length-1?`1px solid ${t.divider}`:"none" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                        <div style={{ width:28, height:28, borderRadius:14, background:isBarberRow?"rgba(37,99,235,0.15)":"rgba(34,197,94,0.12)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, flexShrink:0 }}>
                          {isBarberRow ? "💈" : "✂️"}
                        </div>
                        <div>
                          <div style={{ fontSize:13, fontWeight:700, color:t.t1 }}>{u.full_name || "—"}</div>
                          <div style={{ fontSize:11, color:t.t4 }}>{u.created_at ? new Date(u.created_at).toLocaleString([],{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"}) : "—"}</div>
                        </div>
                      </div>
                      <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                        {needsApproval && (
                          <button onClick={()=>approveBarber(u.id)} style={{ padding:"5px 11px", background:"rgba(34,197,94,0.12)", border:"1px solid rgba(34,197,94,0.3)", borderRadius:8, color:"#22C55E", fontSize:11, fontWeight:700, cursor:"pointer" }}>Approve</button>
                        )}
                        <span style={{ fontSize:10, fontWeight:700, padding:"3px 9px", borderRadius:10, background:isBarberRow?"rgba(37,99,235,0.12)":"rgba(34,197,94,0.1)", color:isBarberRow?"#93C5FD":"#4ADE80" }}>
                          {isBarberRow ? "BARBER" : "CLIENT"}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Recent activity */}
              <div style={{ background:t.card, borderRadius:18, border:`1.5px solid ${t.border}`, padding:16 }}>
                <div style={{ fontSize:13, fontWeight:800, color:t.t1, marginBottom:12 }}>Recent Bookings</div>
                {allBookings.slice(0,8).map((b,i,arr) => (
                  <div key={b.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 0", borderBottom:i<arr.length-1?`1px solid ${t.divider}`:"none" }}>
                    <div>
                      <div style={{ fontSize:12, fontWeight:700, color:t.t1 }}>{b.client_name||"Client"} → {b.service_type||"Haircut"}</div>
                      <div style={{ fontSize:11, color:t.t4 }}>{b.scheduled_at ? new Date(b.scheduled_at).toLocaleString([],{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"}) : "—"}</div>
                    </div>
                    <span style={{ fontSize:11, fontWeight:700, padding:"3px 9px", borderRadius:10, background:b.status==="completed"?"rgba(34,197,94,0.12)":b.status==="cancelled"?"rgba(220,38,38,0.08)":"rgba(245,158,11,0.1)", color:b.status==="completed"?"#22C55E":b.status==="cancelled"?"#EF4444":"#F59E0B" }}>
                      {b.status?.toUpperCase()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── BARBERS ── */}
          {adminTab === "barbers" && (
            <div style={{ padding:"0 16px" }}>
              {/* Status breakdown */}
              <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8, marginBottom:16 }}>
                {[["Pending",pendingBarbers.length,"#F59E0B"],["Active",allBarbers.filter(b=>b.status&&b.status!=="suspended"&&b.status!=="pending").length,"#22C55E"],["Suspended",suspendedBarbers.length,"#EF4444"]].map(([l,v,c])=>(
                  <div key={l} style={{ background:t.card, borderRadius:14, padding:"12px 8px", border:`1.5px solid ${t.border}`, textAlign:"center" }}>
                    <div style={{ fontSize:20, fontWeight:900, color:c }}>{v}</div>
                    <div style={{ fontSize:11, color:t.t4, fontWeight:600, marginTop:2 }}>{l}</div>
                  </div>
                ))}
              </div>

              {allBarbers.map(b => (
                <div key={b.id} style={{ background:t.card, borderRadius:16, border:`1.5px solid ${t.border}`, padding:14, marginBottom:10 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8 }}>
                    <div>
                      <div style={{ fontSize:14, fontWeight:800, color:t.t1 }}>{b.profiles?.full_name || b.handle || "Unnamed Barber"}</div>
                      <div style={{ fontSize:11, color:t.t4 }}>{b.city || "No city"} · ⭐ {b.rating||"—"} ({b.review_count||0} reviews)</div>
                      <div style={{ fontSize:11, color:t.t4, marginTop:2 }}>{(b.specialty||[]).slice(0,3).join(" · ")}</div>
                    </div>
                    <span style={{ fontSize:11, fontWeight:700, padding:"3px 9px", borderRadius:10, background:`${statusColor(b.status)}18`, color:statusColor(b.status) }}>
                      {b.status?.toUpperCase() || "PENDING"}
                    </span>
                  </div>
                  <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                    {!b.approved && (
                      <button onClick={()=>approveBarber(b.id)} style={{ padding:"6px 12px", background:"rgba(34,197,94,0.12)", border:"1px solid rgba(34,197,94,0.3)", borderRadius:8, color:"#22C55E", fontSize:11, fontWeight:700, cursor:"pointer" }}>✓ Approve</button>
                    )}
                    {b.status !== "suspended" && (
                      <button onClick={()=>suspendBarber(b.id)} style={{ padding:"6px 12px", background:"rgba(220,38,38,0.08)", border:"1px solid rgba(220,38,38,0.2)", borderRadius:8, color:"#EF4444", fontSize:11, fontWeight:700, cursor:"pointer" }}>⛔ Suspend</button>
                    )}
                    {b.status === "suspended" && (
                      <button onClick={()=>approveBarber(b.id)} style={{ padding:"6px 12px", background:"rgba(34,197,94,0.1)", border:"1px solid rgba(34,197,94,0.25)", borderRadius:8, color:"#22C55E", fontSize:11, fontWeight:700, cursor:"pointer" }}>↩ Reinstate</button>
                    )}
                    <div style={{ fontSize:11, color:t.t4, alignSelf:"center" }}>
                      Joined {b.created_at ? new Date(b.created_at).toLocaleDateString([],{month:"short",year:"numeric"}) : "—"}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── REVIEWS ── */}
          {adminTab === "reviews" && (
            <div style={{ padding:"0 16px" }}>
              <div style={{ fontSize:12, color:t.t4, marginBottom:12 }}>{allReviews.length} total reviews on platform</div>
              {allReviews.length === 0 && (
                <div style={{ textAlign:"center", padding:40, color:t.t4, fontSize:13 }}>No reviews yet</div>
              )}
              {allReviews.map((r,i,arr) => (
                <div key={r.id} style={{ background:t.card, borderRadius:16, border:`1.5px solid ${t.border}`, padding:14, marginBottom:10 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:6 }}>
                    <div>
                      <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4 }}>
                        {"⭐".repeat(r.rating||5)}
                        <span style={{ fontSize:12, fontWeight:700, color:t.t2 }}>{r.rating}/5</span>
                      </div>
                      <div style={{ fontSize:12, color:t.t1, lineHeight:1.5, marginBottom:4 }}>{r.comment || <em style={{ color:t.t4 }}>No comment</em>}</div>
                      <div style={{ fontSize:11, color:t.t4 }}>
                        {r.created_at ? new Date(r.created_at).toLocaleDateString([],{month:"short",day:"numeric",year:"numeric"}) : "—"}
                      </div>
                    </div>
                    <button onClick={()=>deleteReview(r.id)} style={{ padding:"6px 12px", background:"rgba(220,38,38,0.08)", border:"1px solid rgba(220,38,38,0.2)", borderRadius:8, color:"#EF4444", fontSize:11, fontWeight:700, cursor:"pointer", flexShrink:0 }}>🗑️ Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── BOOKINGS ── */}
          {adminTab === "bookings" && (
            <div style={{ padding:"0 16px" }}>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8, marginBottom:16 }}>
                {[["Pending",allBookings.filter(b=>b.status==="pending").length,"#F59E0B"],["Confirmed",allBookings.filter(b=>b.status==="accepted"||b.status==="confirmed").length,"#22C55E"],["Completed",allBookings.filter(b=>b.status==="completed").length,"#60A5FA"]].map(([l,v,c])=>(
                  <div key={l} style={{ background:t.card, borderRadius:14, padding:"12px 8px", border:`1.5px solid ${t.border}`, textAlign:"center" }}>
                    <div style={{ fontSize:20, fontWeight:900, color:c }}>{v}</div>
                    <div style={{ fontSize:11, color:t.t4, fontWeight:600, marginTop:2 }}>{l}</div>
                  </div>
                ))}
              </div>
              {allBookings.length === 0 && (
                <div style={{ textAlign:"center", padding:40, color:t.t4, fontSize:13 }}>No bookings found — admin RPC may need setup</div>
              )}
              {allBookings.map(b => (
                <div key={b.id} style={{ background:t.card, borderRadius:16, border:`1.5px solid ${t.border}`, padding:14, marginBottom:10 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, fontWeight:700, color:t.t1 }}>{b.client_name||"Client"} · {b.service_type||"Haircut"}</div>
                      <div style={{ fontSize:11, color:t.t4, marginTop:2 }}>
                        {b.scheduled_at ? new Date(b.scheduled_at).toLocaleString([],{weekday:"short",month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"}) : "—"}
                      </div>
                      <div style={{ fontSize:11, color:t.t3, marginTop:2 }}>💰 ${b.price_agreed||0}{b.tip_amount&&Number(b.tip_amount)>0?` + $${b.tip_amount} tip`:""}</div>
                    </div>
                    <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:6 }}>
                      <span style={{ fontSize:11, fontWeight:700, padding:"3px 9px", borderRadius:10, background:b.status==="completed"?"rgba(34,197,94,0.12)":b.status==="cancelled"?"rgba(220,38,38,0.08)":"rgba(245,158,11,0.1)", color:b.status==="completed"?"#22C55E":b.status==="cancelled"?"#EF4444":"#F59E0B" }}>
                        {b.status?.toUpperCase()}
                      </span>
                      {b.status !== "cancelled" && b.status !== "completed" && (
                        <button onClick={()=>cancelBookingAdmin(b.id)} style={{ padding:"5px 10px", background:"rgba(220,38,38,0.08)", border:"1px solid rgba(220,38,38,0.2)", borderRadius:8, color:"#EF4444", fontSize:11, fontWeight:700, cursor:"pointer" }}>Cancel</button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── PAYMENTS ── */}
          {adminTab === "payments" && (
            <div style={{ padding:"0 16px" }}>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:16 }}>
                {statCard("Total Revenue", stats?.total_revenue != null ? `$${Number(stats.total_revenue).toFixed(2)}` : "$—", "#22C55E", "all-time completed")}
                {statCard("Avg Per Booking", allBookings.filter(b=>b.status==="completed").length > 0 ? `$${(allBookings.filter(b=>b.status==="completed").reduce((s,b)=>s+parseFloat(b.price_agreed||0),0) / allBookings.filter(b=>b.status==="completed").length).toFixed(0)}` : "$—", t.t1, "completed bookings")}
                {statCard("Tips Collected", `$${allBookings.reduce((s,b)=>s+parseFloat(b.tip_amount||0),0).toFixed(0)}`, "#22C55E", "all-time")}
                {statCard("Cancelled", allBookings.filter(b=>b.status==="cancelled").length, "#EF4444", "bookings cancelled")}
              </div>

              <div style={{ background:t.card, borderRadius:18, border:`1.5px solid ${t.border}`, padding:16, marginBottom:12 }}>
                <div style={{ fontSize:13, fontWeight:800, color:t.t1, marginBottom:12 }}>Payment Methods Used</div>
                {["Interac","Apple Pay","Google Pay","Card","Cash"].map(method => {
                  const count = allBookings.filter(b=>b.payment_method===method).length;
                  const pct = allBookings.length > 0 ? Math.round(count/allBookings.length*100) : 0;
                  return (
                    <div key={method} style={{ marginBottom:10 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                        <span style={{ fontSize:12, fontWeight:600, color:t.t2 }}>{method}</span>
                        <span style={{ fontSize:12, fontWeight:700, color:t.t1 }}>{count} ({pct}%)</span>
                      </div>
                      <div style={{ height:6, borderRadius:3, background:t.section, overflow:"hidden" }}>
                        <div style={{ height:"100%", width:`${pct}%`, background:"#2563EB", borderRadius:3, transition:"width 0.5s" }}/>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div style={{ background:"rgba(37,99,235,0.06)", borderRadius:14, border:"1px solid rgba(37,99,235,0.15)", padding:"14px 16px" }}>
                <div style={{ fontSize:12, fontWeight:700, color:t.blueText, marginBottom:6 }}>💳 Stripe Integration</div>
                <div style={{ fontSize:11, color:t.t3, lineHeight:1.6 }}>
                  Live Stripe payouts, refunds, and dispute management will appear here once Stripe keys are configured. Currently all payment data is self-reported by barbers.
                </div>
              </div>
            </div>
          )}

          {/* ── BLAST ── */}
          {adminTab === "blast" && (
            <div style={{ padding:"0 16px" }}>
              {/* Waitlist counts */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:16 }}>
                {statCard("Barbers to Blast", blastCounts?.barberUnsent ?? "—", "#2563EB", `${blastCounts?.barberTotal ?? 0} total signed up`)}
                {statCard("Clients to Blast", blastCounts?.clientUnsent ?? "—", "#22C55E", `${blastCounts?.clientTotal ?? 0} total signed up`)}
              </div>

              {/* Barber blast — pre-access link */}
              <div style={{ background:t.card, borderRadius:18, border:"1.5px solid rgba(37,99,235,0.3)", padding:20, marginBottom:12 }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12 }}>
                  <div>
                    <div style={{ fontSize:14, fontWeight:800, color:t.t1 }}>💈 Barber Pre-Access</div>
                    <div style={{ fontSize:11, color:t.t4, marginTop:3, lineHeight:1.5 }}>
                      Sends each barber their beta link + step-by-step setup instructions.<br/>
                      Only reaches barbers who haven't been blasted yet.
                    </div>
                  </div>
                  <span style={{ fontSize:11, fontWeight:700, background:"rgba(37,99,235,0.12)", color:"#60A5FA", padding:"3px 10px", borderRadius:20, flexShrink:0, marginLeft:10 }}>
                    {blastCounts?.barberUnsent ?? "—"} unsent
                  </span>
                </div>
                <button
                  onClick={() => sendBlast("barbers")}
                  disabled={!!blasting || blastCounts?.barberUnsent === 0}
                  style={{ width:"100%", padding:"12px", background: blastCounts?.barberUnsent === 0 ? t.section : "#2563EB", border:"none", borderRadius:12, color: blastCounts?.barberUnsent === 0 ? t.t4 : "#fff", fontSize:13, fontWeight:800, cursor: blastCounts?.barberUnsent === 0 ? "default" : "pointer", opacity: blasting === "barbers" ? 0.7 : 1 }}>
                  {blasting === "barbers" ? "Sending…" : blastCounts?.barberUnsent === 0 ? "All barbers already blasted ✓" : `Send Pre-Access to ${blastCounts?.barberUnsent ?? "…"} Barbers →`}
                </button>
              </div>

              {/* Client blast — launch tomorrow notice */}
              <div style={{ background:t.card, borderRadius:18, border:"1.5px solid rgba(34,197,94,0.3)", padding:20, marginBottom:12 }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12 }}>
                  <div>
                    <div style={{ fontSize:14, fontWeight:800, color:t.t1 }}>✂️ Client Launch Alert</div>
                    <div style={{ fontSize:11, color:t.t4, marginTop:3, lineHeight:1.5 }}>
                      Tells clients "FADED launches tomorrow" with early access hype.<br/>
                      Only reaches clients who haven't been blasted yet.
                    </div>
                  </div>
                  <span style={{ fontSize:11, fontWeight:700, background:"rgba(34,197,94,0.12)", color:"#4ADE80", padding:"3px 10px", borderRadius:20, flexShrink:0, marginLeft:10 }}>
                    {blastCounts?.clientUnsent ?? "—"} unsent
                  </span>
                </div>
                <button
                  onClick={() => sendBlast("clients")}
                  disabled={!!blasting || blastCounts?.clientUnsent === 0}
                  style={{ width:"100%", padding:"12px", background: blastCounts?.clientUnsent === 0 ? t.section : "#059669", border:"none", borderRadius:12, color: blastCounts?.clientUnsent === 0 ? t.t4 : "#fff", fontSize:13, fontWeight:800, cursor: blastCounts?.clientUnsent === 0 ? "default" : "pointer", opacity: blasting === "clients" ? 0.7 : 1 }}>
                  {blasting === "clients" ? "Sending…" : blastCounts?.clientUnsent === 0 ? "All clients already blasted ✓" : `Send Launch Alert to ${blastCounts?.clientUnsent ?? "…"} Clients →`}
                </button>
              </div>

              {/* Send both at once */}
              <button
                onClick={() => sendBlast("all")}
                disabled={!!blasting || (blastCounts?.barberUnsent === 0 && blastCounts?.clientUnsent === 0)}
                style={{ width:"100%", padding:"13px", background:"transparent", border:`1.5px solid ${t.border}`, borderRadius:12, color:t.t2, fontSize:13, fontWeight:700, cursor:"pointer", marginBottom:16 }}>
                {blasting === "all" ? "Sending…" : "🚀 Blast Everyone (barbers + clients)"}
              </button>

              {/* Result card */}
              {blastResult && (
                <div style={{ background: blastResult.error ? "rgba(220,38,38,0.08)" : "rgba(34,197,94,0.08)", border:`1px solid ${blastResult.error ? "rgba(220,38,38,0.25)" : "rgba(34,197,94,0.25)"}`, borderRadius:14, padding:16 }}>
                  {blastResult.error ? (
                    <div style={{ fontSize:13, color:"#EF4444", fontWeight:700 }}>⚠️ {blastResult.error}</div>
                  ) : (
                    <>
                      <div style={{ fontSize:13, fontWeight:800, color:"#22C55E", marginBottom:8 }}>🚀 Blast complete</div>
                      <div style={{ fontSize:12, color:t.t3 }}>✅ Sent: <strong style={{ color:t.t1 }}>{blastResult.sent}</strong></div>
                      {blastResult.failed > 0 && <div style={{ fontSize:12, color:"#EF4444", marginTop:4 }}>❌ Failed: {blastResult.failed}</div>}
                    </>
                  )}
                </div>
              )}

              <div style={{ marginTop:16, padding:"12px 14px", background:t.section, borderRadius:12, border:`1px solid ${t.border}` }}>
                <div style={{ fontSize:11, fontWeight:700, color:t.t3, marginBottom:4 }}>How it works</div>
                <div style={{ fontSize:11, color:t.t4, lineHeight:1.7 }}>
                  Each blast only emails people who haven't been blasted yet — safe to press multiple times.
                  You'll also receive a summary email at hello@letsgetfaded.com after each send.
                </div>
              </div>
            </div>
          )}

          {adminTab === "referrals" && (
            <div style={{ padding:"0 16px" }}>
              <div style={{ marginBottom:16, padding:"12px 14px", background:t.section, borderRadius:12, border:`1px solid ${t.border}` }}>
                <div style={{ fontSize:11, color:t.t4, lineHeight:1.7 }}>
                  A client gets $5 for referring a barber once that barber completes onboarding
                  (approved + profile set up) <strong style={{ color:t.t2 }}>and</strong> gets their first
                  completed booking. This list is who currently qualifies — send the e-transfer to the
                  email shown, then mark it paid.
                </div>
              </div>

              {loadingReferrals ? (
                <div style={{ textAlign:"center", padding:"40px 0", color:t.t4, fontSize:13 }}>Loading…</div>
              ) : referralPayouts.length === 0 ? (
                <div style={{ textAlign:"center", padding:"40px 0", color:t.t4, fontSize:13 }}>No qualifying referrals yet</div>
              ) : (
                <>
                  {referralPayouts.filter(r => !r.paid_at).length > 0 && (
                    <>
                      <div style={{ fontSize:12, fontWeight:800, color:t.t3, marginBottom:8 }}>UNPAID — {referralPayouts.filter(r => !r.paid_at).length}</div>
                      {referralPayouts.filter(r => !r.paid_at).map(r => (
                        <div key={r.id} style={{ background:t.card, borderRadius:16, border:"1.5px solid rgba(245,158,11,0.3)", padding:16, marginBottom:10 }}>
                          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10 }}>
                            <div>
                              <div style={{ fontSize:14, fontWeight:800, color:t.t1 }}>${(r.amount_cents/100).toFixed(2)} owed</div>
                              <div style={{ fontSize:12, color:t.t3, marginTop:2 }}>To: {r.referrer_name || "—"} · <span style={{ color:t.t2 }}>{r.referrer_email}</span></div>
                              <div style={{ fontSize:11, color:t.t4, marginTop:2 }}>For referring: {r.barber_name || r.barber_handle || "—"}</div>
                              <div style={{ fontSize:11, color:t.t4, marginTop:2 }}>Qualified: {new Date(r.qualified_at).toLocaleDateString()}</div>
                            </div>
                          </div>
                          <button
                            onClick={() => markReferralPaid(r.id)}
                            disabled={markingPaidId === r.id}
                            style={{ width:"100%", padding:"11px", background:"#2563EB", border:"none", borderRadius:12, color:"#fff", fontSize:13, fontWeight:800, cursor:"pointer", opacity:markingPaidId===r.id?0.6:1 }}>
                            {markingPaidId === r.id ? "Marking…" : "✅ Mark as Paid (e-transfer sent)"}
                          </button>
                        </div>
                      ))}
                    </>
                  )}

                  {referralPayouts.filter(r => r.paid_at).length > 0 && (
                    <>
                      <div style={{ fontSize:12, fontWeight:800, color:t.t3, marginTop:20, marginBottom:8 }}>PAID — {referralPayouts.filter(r => r.paid_at).length}</div>
                      {referralPayouts.filter(r => r.paid_at).map(r => (
                        <div key={r.id} style={{ background:t.card, borderRadius:16, border:`1.5px solid ${t.border}`, padding:14, marginBottom:8, opacity:0.6 }}>
                          <div style={{ fontSize:13, fontWeight:700, color:t.t2 }}>${(r.amount_cents/100).toFixed(2)} — {r.referrer_name || r.referrer_email}</div>
                          <div style={{ fontSize:11, color:t.t4, marginTop:2 }}>For {r.barber_name || r.barber_handle} · Paid {new Date(r.paid_at).toLocaleDateString()}</div>
                        </div>
                      ))}
                    </>
                  )}
                </>
              )}

              {/* Barber-refers-barber: free months, not cash. Founding barbers get a base of 2,
                  everyone since gets 1, plus 1 more per barber they successfully refer. Nothing
                  to "pay" here yet since the $30/month fee isn't being charged yet -- this is a
                  ledger to apply once billing turns on. */}
              <div style={{ fontSize:12, fontWeight:800, color:t.t3, marginTop:28, marginBottom:8 }}>BARBER FREE-MONTH BALANCES</div>
              <div style={{ marginBottom:12, padding:"12px 14px", background:t.section, borderRadius:12, border:`1px solid ${t.border}` }}>
                <div style={{ fontSize:11, color:t.t4, lineHeight:1.7 }}>
                  Founding barbers start with 2 free months, everyone after gets 1. Each barber earns
                  +1 more month per barber they refer who completes onboarding and their first
                  booking. Not billed against yet — this is a running balance to apply once the
                  $30/month fee goes live.
                </div>
              </div>
              {barberFreeMonths.map(bm => (
                <div key={bm.barber_id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", background:t.card, borderRadius:14, border:`1px solid ${t.border}`, padding:"12px 14px", marginBottom:6 }}>
                  <div>
                    <div style={{ fontSize:13, fontWeight:700, color:t.t1 }}>{bm.barber_name || bm.barber_handle}</div>
                    <div style={{ fontSize:11, color:t.t4, marginTop:1 }}>{bm.base_free_months} base{bm.bonus_months > 0 ? ` + ${bm.bonus_months} referral bonus` : ""}</div>
                  </div>
                  <div style={{ fontSize:16, fontWeight:900, color:"#2563EB" }}>{bm.total_months}mo</div>
                </div>
              ))}

              {/* Campaign codes — trackable per-shop/per-partner links (e.g. a growth partner's
                  QR codes at individual shops), each with its own payout rate. Qualification uses
                  the exact same bar as everything else: onboarded + first completed booking. */}
              <div style={{ fontSize:12, fontWeight:800, color:t.t3, marginTop:28, marginBottom:8 }}>CAMPAIGN CODES</div>
              <div style={{ background:t.card, borderRadius:14, border:`1px solid ${t.border}`, padding:14, marginBottom:12 }}>
                <div style={{ fontSize:12, fontWeight:700, color:t.t2, marginBottom:8 }}>New code</div>
                <input value={newCampaignLabel} onChange={e=>setNewCampaignLabel(e.target.value)} placeholder="Label, e.g. Joe's Barbershop QR"
                  style={{ width:"100%", padding:"10px 12px", borderRadius:10, border:`1.5px solid ${t.border}`, background:t.section, color:t.t1, fontSize:13, marginBottom:8, boxSizing:"border-box" }}/>
                <div style={{ display:"flex", gap:8 }}>
                  <div style={{ position:"relative", flex:1 }}>
                    <span style={{ position:"absolute", left:12, top:"50%", transform:"translateY(-50%)", color:t.t4, fontSize:13 }}>$</span>
                    <input value={newCampaignAmount} onChange={e=>setNewCampaignAmount(e.target.value)} type="number" step="0.01" placeholder="15.00"
                      style={{ width:"100%", padding:"10px 12px 10px 22px", borderRadius:10, border:`1.5px solid ${t.border}`, background:t.section, color:t.t1, fontSize:13, boxSizing:"border-box" }}/>
                  </div>
                  <button onClick={createCampaignCode} disabled={creatingCampaign || !newCampaignLabel.trim()}
                    style={{ padding:"10px 18px", background:"#2563EB", border:"none", borderRadius:10, color:"#fff", fontSize:13, fontWeight:800, cursor:"pointer", opacity:creatingCampaign?0.6:1, whiteSpace:"nowrap" }}>
                    {creatingCampaign ? "…" : "+ Create"}
                  </button>
                </div>
              </div>

              {campaignCodes.length === 0 ? (
                <div style={{ textAlign:"center", padding:"20px 0", color:t.t4, fontSize:13 }}>No campaign codes yet</div>
              ) : campaignCodes.map(cc => {
                const url = `https://app.letsgetfaded.com?campaign=${cc.code}`;
                const isOpen = expandedCampaignId === cc.campaign_id;
                return (
                  <div key={cc.campaign_id} style={{ background:t.card, borderRadius:14, border:`1px solid ${t.border}`, padding:14, marginBottom:8 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:13, fontWeight:800, color:t.t1 }}>{cc.label || cc.code}</div>
                        <div style={{ fontSize:11, color:t.t4, marginTop:2, fontFamily:"monospace", wordBreak:"break-all" }}>{url}</div>
                        <div style={{ fontSize:11, color:t.t3, marginTop:4 }}>
                          ${(cc.reward_amount_cents/100).toFixed(2)}/activation · {cc.total_signups} signups · {cc.qualified_count} qualified
                          {cc.unpaid_count > 0 && <span style={{ color:"#F59E0B", fontWeight:700 }}> · ${(cc.unpaid_amount_cents/100).toFixed(2)} unpaid</span>}
                        </div>
                      </div>
                      <button onClick={()=>{ navigator.clipboard?.writeText(url); flash("📋 Link copied"); }}
                        style={{ background:"none", border:"none", cursor:"pointer", fontSize:16, padding:4 }} aria-label="Copy link">🔗</button>
                    </div>
                    {cc.qualified_count > 0 && (
                      <button onClick={()=>toggleCampaignDetails(cc.campaign_id)} style={{ marginTop:8, background:"none", border:"none", color:"#60A5FA", fontSize:12, fontWeight:700, cursor:"pointer", padding:0 }}>
                        {isOpen ? "Hide details ▲" : "View activations ▼"}
                      </button>
                    )}
                    {isOpen && (campaignDetails[cc.campaign_id]||[]).map(d => (
                      <div key={d.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:8, paddingTop:8, borderTop:`1px solid ${t.border}` }}>
                        <div>
                          <div style={{ fontSize:12, fontWeight:700, color:t.t2 }}>{d.barber_name || d.barber_handle}</div>
                          <div style={{ fontSize:10, color:t.t4 }}>${(d.amount_cents/100).toFixed(2)} · Qualified {new Date(d.qualified_at).toLocaleDateString()}</div>
                        </div>
                        {d.paid_at ? (
                          <span style={{ fontSize:11, color:t.t4 }}>Paid ✓</span>
                        ) : (
                          <button onClick={()=>markCampaignPaid(d.id, cc.campaign_id)} disabled={markingPaidId===d.id}
                            style={{ padding:"6px 12px", background:"#2563EB", border:"none", borderRadius:8, color:"#fff", fontSize:11, fontWeight:700, cursor:"pointer" }}>
                            {markingPaidId===d.id ? "…" : "Mark Paid"}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── ERROR BOUNDARY ───────────────────────────────────────────────────────────
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(err, info) {
    console.error("[FADED] Component crash:", err?.message || err, info?.componentStack?.slice(0, 300));
    if (process.env.REACT_APP_SENTRY_DSN) { Sentry.captureException(err, { extra: info }); }
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ position:"fixed", inset:0, zIndex:300, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", background:"rgba(10,10,10,0.92)", padding:32, textAlign:"center", fontFamily:"'DM Sans',sans-serif" }}>
          {this.props.onDismiss && (
            <button onClick={this.props.onDismiss} aria-label="Dismiss" style={{ position:"absolute", top:20, right:20, background:"rgba(245,245,240,0.1)", border:"none", color:"rgba(245,245,240,0.7)", fontSize:22, cursor:"pointer", width:44, height:44, borderRadius:10, display:"flex", alignItems:"center", justifyContent:"center" }}>×</button>
          )}
          <div style={{ fontSize:40, marginBottom:16 }}>✂️</div>
          <div style={{ fontSize:18, fontWeight:800, color:"#F0F0F0", marginBottom:8 }}>{this.props.title||"Something went wrong"}</div>
          <div style={{ fontSize:13, color:"#888", marginBottom:24 }}>{this.props.message||"Tap below to reload and try again."}</div>
          <button onClick={()=>window.location.reload()} style={{ padding:"12px 28px", background:"#2563EB", border:"none", borderRadius:14, color:"#fff", fontSize:14, fontWeight:700, cursor:"pointer" }}>Reload App</button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ─── AI FAB ───────────────────────────────────────────────────────────────────
function AiFab({ onOpen }) {
  let isNew = false;
  try { isNew = !localStorage.getItem("faded_fab_seen"); } catch {}
  return (
    <div style={{ position:"fixed", bottom:"calc(max(4px, env(safe-area-inset-bottom, 0px)) + 84px)", right:18, zIndex:109 }}>
      {isNew && (
        <div style={{ position:"absolute", inset:-6, borderRadius:"50%", border:"1.5px solid rgba(147,197,253,0.5)", animation:"ripple 1.8s ease-out infinite", pointerEvents:"none" }}/>
      )}
      <button onClick={()=>{ try { localStorage.setItem("faded_fab_seen","1"); } catch {} onOpen(); }}
        style={{ width:48, height:48, borderRadius:"50%", background:"linear-gradient(135deg,#1e3a5f 0%,#2563EB 100%)", border:"1.5px solid rgba(147,197,253,0.35)", boxShadow:"0 4px 20px rgba(37,99,235,0.55)", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", outline:"none" }}>
        <svg aria-hidden="true" focusable="false"width="21" height="21" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="2.5" fill="#fff"/>
          <path d="M12 3v2.5M12 18.5V21M3 12h2.5M18.5 12H21M5.64 5.64l1.77 1.77M16.59 16.59l1.77 1.77M5.64 18.36l1.77-1.77M16.59 7.41l1.77-1.77" stroke="#fff" strokeWidth="1.8" strokeLinecap="round"/>
        </svg>
      </button>
    </div>
  );
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
export default function App() {
  const [session, setSession]         = useState(undefined);
  const [versionCheck, setVersionCheck] = useState(null); // {currentVersion, minVersion, force, message, platform} once a stale install is detected
  const [guestMode, setGuestMode]     = useState(() => { try { return new URLSearchParams(window.location.search).has("open_barber") || window.location.pathname.startsWith("/barber/"); } catch { return false; } });
  const [viewAsClient, setViewAsClient] = useState(false);
  const [oauthErr, setOauthErr]       = useState("");
  // The "Signing you in..." spinner (below) renders purely off "is there a
  // ?code= in the URL and no session yet" with no upper bound — if
  // exchangeCodeForSession ever hangs or fails silently (e.g. the PKCE
  // code_verifier isn't found because the reset was requested in a
  // different storage context, common on mobile Safari), nothing ever
  // clears the code param or sets a session, so that screen spins forever.
  // This flag gives it an escape hatch.
  const [codeExchangeTimedOut, setCodeExchangeTimedOut] = useState(false);
  // If the URL contains an OAuth callback (code= or access_token=), skip landing
  // so the user isn't bounced back to the start after Google sign-in redirects.
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [isIOSInstall, setIsIOSInstall]           = useState(false);
  const installPromptRef                          = useRef(null);
  const [showLanding, setShowLanding]     = useState(() => {
    try {
      const hash = window.location.hash;
      const search = window.location.search;
      if (hash.includes("access_token=") || search.includes("code=") || search.includes("error=") || search.includes("join=")) return false;
    } catch {}
    return false;
  });
  const [splash, setSplash]               = useState({ show:true, fading:false });
  const [landingFading, setLandingFading] = useState(false);
  const [authInitialMode, setAuthInitialMode] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('join') ? "signup" : "landing"; } catch { return "landing"; }
  });
  const [passwordResetMode, setPasswordResetMode] = useState(false);
  const [adminToast, setAdminToast] = useState(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const ok = p.get('admin_ok'), err = p.get('admin_err'), name = p.get('name') || 'Barber';
      if (ok === 'approved') return { msg: `✅ ${decodeURIComponent(name)} is approved and can now go live.`, color: '#22C55E' };
      if (ok === 'already')  return { msg: `ℹ️ ${decodeURIComponent(name)} was already approved.`, color: '#2563EB' };
      if (err) return { msg: '❌ Approval link is invalid or expired.', color: '#EF4444' };
    } catch {}
    return null;
  });
  const [roleSplashDone, setRoleSplashDone] = useState(() => {
    try { return sessionStorage.getItem("faded_client_welcomed") === "1"; } catch { return false; }
  });
  const [barberLoginSplash, setBarberLoginSplash] = useState(false);
  const freshLoginRef = useRef(false); // true only when session transitions null → truthy
  const [showBarberWelcome, setShowBarberWelcome] = useState(false);
  const [barberAIDeepLink, setBarberAIDeepLink] = useState(false);
  // Push notification deep link — set when app opens via ?booking=<id> or ?tab=schedule
  const [bookingDeepLink] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('booking') || null; } catch { return null; }
  });
  const [scheduleDeepLink] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('tab') === 'schedule'; } catch { return false; }
  });
  const [joinRoleDeepLink] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('join') || null; } catch { return null; }
  });
  const [pendingShopJoin, setPendingShopJoin] = useState(() => {
    try {
      const p = new URLSearchParams(window.location.search).get('join_shop');
      if (p) { try { sessionStorage.setItem('faded_pending_shop_join', p); } catch {} return p; }
      return sessionStorage.getItem('faded_pending_shop_join') || null;
    } catch { return null; }
  });
  // Referral tracking — persist ?ref= through OAuth redirects via sessionStorage
  const [pendingRef] = useState(() => {
    try {
      const r = new URLSearchParams(window.location.search).get('ref');
      if (r) { try { sessionStorage.setItem('faded_ref', r); } catch {} return r; }
      return sessionStorage.getItem('faded_ref') || null;
    } catch { return null; }
  });
  // Campaign code tracking (shop QR codes etc.) — same persistence pattern as ?ref=, separate
  // storage key since a signup can carry a personal referrer and a campaign code at once.
  useEffect(() => {
    try {
      const c = new URLSearchParams(window.location.search).get('campaign');
      if (c) { sessionStorage.setItem('faded_campaign', c); localStorage.setItem('faded_campaign', c); }
    } catch {}
  }, []);
  const [profile, setProfile]         = useState(null);
  const [referralCount, setReferralCount] = useState(0);
  const [myBarberData, setMyBarberData] = useState(null);
  const [barberHasShop, setBarberHasShop] = useState(false);
  const [needsOnboard, setNeedsOnboard] = useState(false);
  const [needsRolePicker, setNeedsRolePicker] = useState(false);
  const [barbers, setBarbers]         = useState([]);
  const [nearbyShops, setNearbyShops] = useState([]);
  const [selectedShop, setSelectedShop] = useState(null);
  const [selected, setSelected]       = useState(null);
  const [tab, setTab]                 = useState(() => { try { return localStorage.getItem("faded_tab") || "discover"; } catch { return "discover"; } });
  // Nav SAB ready gate — hides nav on first-ever cold launch until --sab is confirmed.
  // navSabReady ref is the guard; navVisible state drives the opacity.
  // Both start true when localStorage has a cached SAB (every launch after the first).
  const navSabReady = useRef(false);
  // Tracks whether session was absent before the last sign-in so we can distinguish
  // an auth transition (needs WKWebView scroll reset) from a cold launch (already correct).
  const prevSessionRef = useRef(session);
  const [navVisible, setNavVisible] = useState(() => {
    try {
      const has = localStorage.getItem('faded_sab') !== null;
      navSabReady.current = has; // sync ref so set() RAF skips setNavVisible if already ready
      return has;
    } catch { return false; }
  });
  const discoverTracked = useRef(false);
  useEffect(() => { if (tab === "discover" && !discoverTracked.current) { discoverTracked.current = true; track("discover_viewed"); } }, [tab]);
  const [urgent, setUrgent]           = useState(false);
  const [a11yAnnouncement, setA11yAnnouncement] = useState("");
  const [sort, setSort]               = useState("availability");
  const [styleFilter, setStyleFilter] = useState("All");
  const [priceFilter, setPriceFilter] = useState("Any Price");
  const [showFilters, setShowFilters] = useState(false);
  const [search, setSearch]           = useState("");
  const [livePulse, setLivePulse]     = useState(true);
  const [loading, setLoading]         = useState(true);
  const [toast, setToast]             = useState(null);
  const [refreshKey, setRefreshKey]   = useState(0);
  const [mapView, setMapView]         = useState(true); // always true for discover — Uber style
  // Instant "Get Faded Now" request state
  const [instantOpen, setInstantOpen]           = useState(false);
  const [incomingClientPing, setIncomingClientPing] = useState(null); // barber side
  const [pullY, setPullY]             = useState(0);
  const [refreshing, setRefreshing]   = useState(false);
  const touchStartY                   = useRef(0);
  const scrollContainerRef            = useRef(null);
  const [favorites, setFavorites]     = useState([]);
  const [bookings, setBookings]       = useState([]);
  const [rebookPrefill, setRebookPrefill] = useState(null);
  const [rescheduleBooking, setRescheduleBooking] = useState(null);
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [postCutBooking, setPostCutBooking] = useState(null);
  const [isDark, setIsDark]           = useState(() => { try { const s = localStorage.getItem("faded_theme"); return s === null ? true : s === "dark"; } catch(e) { return true; } });
  const [fontsLoaded, setFontsLoaded] = useState(false);
  const [isOnline, setIsOnline]       = useState(() => navigator.onLine !== false);
  useEffect(() => {
    const go = () => setIsOnline(true);
    const stop = () => setIsOnline(false);
    window.addEventListener("online", go);
    window.addEventListener("offline", stop);
    return () => { window.removeEventListener("online", go); window.removeEventListener("offline", stop); };
  }, []);
  const toggleTheme = () => { const next = !isDark; setIsDark(next); try { localStorage.setItem("faded_theme", next?"dark":"light"); } catch(e) {} };
  const t = createTheme(isDark);
  useEffect(() => {
    // Landing page and auth screen are always dark regardless of user theme —
    // using the light bg there causes a white strip on iOS (home indicator area
    // inherits html background) since those screens hardcode #0A0A0A themselves.
    const onAuthScreen = showLanding || !session;
    const bgColor = (onAuthScreen || isDark) ? '#0A0A0A' : '#F7F8FA';
    document.documentElement.classList.toggle('dark', isDark && !onAuthScreen);
    document.body.style.background = bgColor;
    document.documentElement.style.background = bgColor;
  }, [isDark, showLanding, session]);
  const gps = useGPS();
  const realtimeHealth = useRealtimeHealth();
  const winWidth = useWindowWidth();
  const isMd        = winWidth >= 640;
  const isDesktop   = winWidth >= 1024; // layout/grid decisions (3-col grid etc.)
  const isNavDesktop = winWidth >= 768; // nav visibility threshold

  // Detect incomplete booking from a previous session; verify against DB before alarming user
  const [incompleteBookingMsg, setIncompleteBookingMsg] = useState(false);
  useEffect(() => {
    let localFlag = false;
    try { localFlag = localStorage.getItem("booking_in_progress") === "true"; } catch {}
    if (!localFlag) return;
    if (!session || session === "guest") {
      // Guest — can't verify against DB; show banner
      setIncompleteBookingMsg(true);
      return;
    }
    // Check if a booking actually landed in the last 10 minutes
    supabase.from("bookings")
      .select("id")
      .eq("client_id", session.user.id)
      .gte("created_at", new Date(Date.now() - 10 * 60 * 1000).toISOString())
      .order("created_at", { ascending: false })
      .limit(1)
      .then(({ data }) => {
        if (data?.length) {
          // Booking landed — clean up the flag silently
          try { localStorage.removeItem("booking_in_progress"); } catch {}
        } else {
          setIncompleteBookingMsg(true);
        }
      });
  }, [session]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist active tab across cold launches and page refreshes
  useEffect(() => { try { localStorage.setItem("faded_tab", tab); } catch {} }, [tab]);
  useEffect(() => { scrollContainerRef.current?.scrollTo({ top:0, behavior:"instant" }); }, [tab]);

  // Guard: barber-only tab stored from a previous session should not land a client on a blank screen
  useEffect(() => {
    if (!profile) return;
    const isBar = (profile.role || session?.user?.user_metadata?.role) === "barber";
    const barberOnlyTabs = ["dashboard","appointments","shop","booth","earnings"];
    if (!isBar && barberOnlyTabs.includes(tab)) setTab("discover");
  }, [profile?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Wait for fonts before showing splash — prevents Bebas Neue flash
  useEffect(() => {
    document.fonts.ready.then(() => setFontsLoaded(true));
    const fallback = setTimeout(() => setFontsLoaded(true), 1500);
    return () => clearTimeout(fallback);
  }, []);

  // Browser / phone back button — navigate within the app, never exit to OS.
  useEffect(() => {
    window.history.replaceState({ faded: true }, "", window.location.href);
    const handlePop = (e) => {
      // Always re-push so the next back is intercepted too
      window.history.pushState({ faded:true }, "", window.location.href);
      // Close modals / overlays first
      if (selected) { setSelected(null); return; }
      if (showGiftModal) { setShowGiftModal(false); return; }
      // Navigate up one level within the app
      if (tab !== "discover") { setTab("discover"); setMapView(true); return; }
      // Already on discover — do nothing (prevent exit)
    };
    window.addEventListener("popstate", handlePop);
    // Capacitor Android hardware back button
    const handleAndroidBack = (e) => {
      e.preventDefault();
      handlePop(e);
    };
    document.addEventListener("backbutton", handleAndroidBack, false);
    return () => {
      window.removeEventListener("popstate", handlePop);
      document.removeEventListener("backbutton", handleAndroidBack, false);
    };
  }, [selected, showGiftModal, tab]); // eslint-disable-line react-hooks/exhaustive-deps

  // One-time history buffer, run once on load. Without this, the FIRST back-press
  // after arriving here (e.g. via a link from letsgetfaded.com) always escaped the
  // app entirely — popstate only fires for same-document history transitions, so
  // the handler above can never intercept a back-press that lands on a different
  // document (it's already unloaded by the time any of our JS could react). Pushing
  // one extra same-document entry here means that first back-press pops into an
  // entry this app owns, which the handler above can catch and re-push forever.
  useEffect(() => {
    window.history.pushState({ faded: true }, "", window.location.href);
  }, []);

  // Escape key closes any open modal/drawer — desktop UX expectation
  useEffect(() => {
    const onEsc = (e) => {
      if (e.key !== "Escape") return;
      if (selected) { setSelected(null); return; }
      if (selectedShop) { setSelectedShop(null); return; }
      if (showGiftModal) { setShowGiftModal(false); return; }
    };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [selected, selectedShop, showGiftModal]); // eslint-disable-line react-hooks/exhaustive-deps

  // Push notification deep link — when barber data loads and URL has ?booking= or ?tab=schedule,
  // route straight to the appointments tab and clean up the URL
  useEffect(() => {
    if (!myBarberData) return;
    if (bookingDeepLink || scheduleDeepLink) {
      setTab("appointments");
      try {
        const clean = new URL(window.location.href);
        clean.searchParams.delete("booking");
        clean.searchParams.delete("tab");
        window.history.replaceState({}, "", clean.toString());
      } catch {}
    }
  }, [myBarberData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Native iOS deep link handler — catches faded://auth/callback from Google OAuth
  useEffect(() => {
    if (!IS_NATIVE_IOS) return;
    const handler = CapApp.addListener('appUrlOpen', async ({ url }) => {
      if (url.startsWith('faded://auth/callback')) {
        // Only an OAuth sign-in actually has an in-app browser window open at this
        // point (via Browser.open() below) — a password-reset link tapped from the
        // Mail app never opened one, so Browser.close() throws "No active window to
        // close!" here. Left uncaught, that exception aborted the rest of this
        // async handler, meaning the code param was never even extracted and
        // exchangeCodeForSession never ran for any Mail-app-triggered deep link.
        try { await Browser.close(); } catch {}
        const params = new URLSearchParams(url.split('?')[1] || '');
        const code = params.get('code');
        const isRecovery = params.get('type') === 'recovery';
        console.log("[FADED][deeplink] appUrlOpen faded://auth/callback", { hasCode: !!code, isRecovery, rawUrl: url });
        Sentry.addBreadcrumb({ category: "auth", message: "deep-link callback received", level: "info", data: { hasCode: !!code, isRecovery } });
        if (!code) {
          console.warn("[FADED][deeplink] no code param found in callback URL — nothing to exchange");
          Sentry.captureMessage("Password/OAuth deep link had no code param", { level: "warning", extra: { url } });
          return;
        }
        const { data, error } = await supabase.auth.exchangeCodeForSession(code);
        if (error) {
          // DIAGNOSTIC: this used to fail completely silently — no log, no user
          // feedback, the user just ended up wherever they already were with no
          // explanation. Capturing this is the only way to see the real cause
          // (expired/reused code, PKCE code_verifier missing because the reset
          // was requested on a different device/browser than this one, etc.)
          console.error("[FADED][deeplink] exchangeCodeForSession failed:", error.message);
          Sentry.captureException(error, { tags: { flow: "auth-deep-link", isRecovery: String(isRecovery) } });
          return;
        }
        if (data.session) {
          console.log("[FADED][deeplink] exchangeCodeForSession succeeded", { isRecovery, userId: data.session.user?.id });
          if (isRecovery) {
            // Don't reload for password recovery — exchangeCodeForSession already
            // fired onAuthStateChange with PASSWORD_RECOVERY, which sets
            // passwordResetMode and shows the reset screen. Reloading here would
            // wipe that state before React ever renders it, dropping the user
            // back at a plain signed-in/signed-out screen instead.
            return;
          }
          // Reload instead of setSession — WKWebView cold-starts with correct
          // contentOffset and headers. Session persists in localStorage via Supabase.
          window.location.reload();
        }
      }
    });
    return () => { handler.then(h => h.remove()); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Version gate — checks the installed native app version against app_versions
  // in Supabase. force_update:true blocks the whole app until updated (reserved
  // for releases fixing crashes/payment bugs); otherwise shows a dismissible nudge.
  useEffect(() => {
    if (!isNative()) return;
    (async () => {
      try {
        const platform = Capacitor.getPlatform(); // "ios" | "android"
        if (platform !== "ios" && platform !== "android") return;
        const [{ version: currentVersion }, { data: row }] = await Promise.all([
          CapApp.getInfo(),
          supabase.from("app_versions").select("min_version, force_update, message").eq("platform", platform).single().then(r => r, () => ({ data: null })),
        ]);
        if (!row) return;
        if (compareVersions(currentVersion, row.min_version) < 0) {
          setVersionCheck({ currentVersion, minVersion: row.min_version, force: !!row.force_update, message: row.message, platform });
        }
      } catch (e) { console.error("[FADED] version check failed:", e); }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auth — detectSessionInUrl:true handles implicit flow hash tokens automatically
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get("ref");
    if (refCode) {
      try { localStorage.setItem("faded_ref", refCode.toUpperCase()); } catch {}
      const cleanRef = new URL(window.location.href);
      cleanRef.searchParams.delete("ref");
      window.history.replaceState({}, "", cleanRef.toString());
    }

    const errCode = params.get("error_code") || params.get("error");

    if (errCode) {
      const desc = params.get("error_description") || "Sign-in failed. Please try again.";
      setOauthErr(decodeURIComponent(desc.replace(/\+/g," ")));
      setShowLanding(false);
      setSession(null);
      window.history.replaceState({}, "", window.location.pathname);
    }

    // Catch a PASSWORD_RECOVERY (or any auth event) that fired before this component
    // mounted — see the module-level onAuthStateChange registration near createClient.
    let done = false;
    console.log("[FADED][auth] mount check — earlyAuthEvent:", _earlyAuthEvent, "hasEarlySession:", !!_earlyAuthSession);
    if (_earlyAuthEvent === "PASSWORD_RECOVERY") {
      console.log("[FADED][auth] passwordResetMode set via EARLY capture (event fired before mount)");
      done = true; setSession(_earlyAuthSession); setShowLanding(false); setPasswordResetMode(true);
    } else if (_earlyAuthSession?.user?.id) {
      done = true; setSession(_earlyAuthSession); setShowLanding(false); Sentry.setUser({ id: _earlyAuthSession.user.id }); posthog.identify(_earlyAuthSession.user.id);
    }

    // Immediate check — usually resolves from localStorage in <1ms, unblocks the splash
    // screen. getSession() can still trigger a network token-refresh under the hood
    // though, so it isn't guaranteed instant — a fallback timeout (mirrors fontsLoaded's)
    // guarantees the splash screen can never hang forever if that network call stalls.
    supabase.auth.getSession().then(({ data:{ session:s } }) => {
      if (s?.user?.id) { done = true; setSession(s); setShowLanding(false); Sentry.setUser({ id: s.user.id }); posthog.identify(s.user.id); }
      else if (!done) { done = true; setSession(null); }
    }).catch(() => { if (!done) { done = true; setSession(null); } });
    const sessionFallback = setTimeout(() => { if (!done) { done = true; setSession(null); } }, 5000);

    // Background poll — catches OAuth hash tokens processed asynchronously after load
    const poll = async () => {
      for (let i = 0; i < 8; i++) {
        await new Promise(r => setTimeout(r, 400));
        const { data:{ session:s } } = await supabase.auth.getSession();
        if (s?.user?.id) { done = true; setSession(s); setShowLanding(false); Sentry.setUser({ id: s.user.id }); posthog.identify(s.user.id); return; }
      }
    };
    poll();

    const { data:{ subscription } } = supabase.auth.onAuthStateChange((event, s) => {
      if (event === "SIGNED_OUT") { setSession(null); Sentry.setUser(null); posthog.reset(); return; }
      if (event === "PASSWORD_RECOVERY") {
        console.log("[FADED][auth] passwordResetMode set via LIVE subscription (event fired after mount)");
        done = true; setSession(s); setShowLanding(false); setPasswordResetMode(true); return;
      }
      if (s?.user?.id) { done = true; setSession(s); setShowLanding(false); Sentry.setUser({ id: s.user.id }); posthog.identify(s.user.id); }
    });
    return () => { clearTimeout(sessionFallback); subscription.unsubscribe(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Watchdog for the "Signing you in..." code-exchange spinner (rendered below,
  // gated on window.location.search containing 'code=' with no session yet).
  // That condition has no natural upper bound — if exchangeCodeForSession hangs
  // or fails silently (PKCE code_verifier not found — common on mobile Safari
  // if the reset was requested in a different storage context/tab), nothing
  // ever clears the code param or sets a session, so the spinner never resolves.
  useEffect(() => {
    if (session || guestMode) return;
    if (!window.location.search.includes('code=')) return;
    setCodeExchangeTimedOut(false);
    console.log("[FADED][auth] code-exchange spinner shown — starting 9s watchdog", { url: window.location.href });
    Sentry.addBreadcrumb({ category: "auth", message: "code-exchange spinner shown", level: "info" });
    const timer = setTimeout(() => {
      console.error("[FADED][auth] code-exchange watchdog fired — exchange never resolved within 9s");
      Sentry.captureMessage("Password/OAuth code exchange timed out (spinner hang)", { level: "error", extra: { url: window.location.href } });
      setCodeExchangeTimedOut(true);
    }, 9000);
    return () => clearTimeout(timer);
  }, [session, guestMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Whenever a real session is detected (any method — OAuth, email, phone), skip landing page
  useEffect(() => {
    if (session && session !== "guest" && typeof session !== "string") {
      setShowLanding(false);
      initCapacitorPush(supabase, session.user.id).catch(() => {});
    }
  }, [session]);

  // Track fresh login: session goes null → truthy (not a cached cold-start resume)
  useEffect(() => {
    if (session === null) { freshLoginRef.current = true; }
  }, [session]);

  // Once profile + role confirmed after a fresh login, show FADED splash before barber dashboard
  useEffect(() => {
    const isBarberRole = (profile?.role || session?.user?.user_metadata?.role) === "barber";
    if (session && session !== "guest" && profile && isBarberRole && freshLoginRef.current) {
      freshLoginRef.current = false;
      setBarberLoginSplash(true);
    }
  }, [session, profile]); // eslint-disable-line react-hooks/exhaustive-deps

  // Guest booking migration: silently import localStorage bookings on first sign-in
  useEffect(() => {
    if (!session || session === "guest" || typeof session === "string") return;
    try {
      const local = JSON.parse(localStorage.getItem("faded_local_bookings") || "[]");
      const matchable = local.filter(b => !b._imported && b.id && !b.id.startsWith("local-demo"));
      if (matchable.length === 0) return;
      (async () => {
        for (const bk of matchable) {
          if (bk.id && !bk.id.startsWith("local-")) {
            await supabase.from("bookings").update({ client_id: session.user.id }).eq("id", bk.id).is("client_id", null);
          }
        }
        const updated = matchable.map(b => ({ ...b, _imported: true }));
        const remaining = local.filter(b => !matchable.find(m => m.id === b.id));
        localStorage.setItem("faded_local_bookings", JSON.stringify([...updated, ...remaining].slice(0, 20)));
        const { data } = await supabase.from("bookings").select("*").eq("client_id", session.user.id).order("created_at",{ascending:false}).limit(50);
        if (data) setBookings([...data, ...remaining.filter(b => b._local)]);
      })().catch(() => {});
    } catch {}
  }, [session?.user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Push notification tap → navigate to correct tab
  useEffect(() => {
    const handler = (e) => {
      const { tab: targetTab, booking_id, tab, type, request_id } = e.detail || {};
      // Barber tapped a broadcast notification — fetch the request and show the accept card
      if (type === "instant_request" && request_id) {
        setTab("dashboard");
        supabase.from("instant_requests").select("*").eq("id", request_id).eq("status", "open").single()
          .then(({ data: req }) => { if (req) setIncomingClientPing(req); });
        return;
      }
      const raw = targetTab || tab;
      // "schedule" was the old barber tab name; the correct ID is "appointments"
      const dest = raw === "schedule" ? "appointments" : raw;
      if (dest) { setTab(dest); return; }
      if (booking_id) setTab("appointments");
    };
    window.addEventListener("faded:push-tap", handler);
    return () => window.removeEventListener("faded:push-tap", handler);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // iOS PWA: lock --app-height to window.innerHeight so position:fixed nav
  // doesn't drift when the browser restores the viewport on foreground/re-open.
  // Also measures and caches --sab (safe-area-inset-bottom) so the nav pill
  // doesn't jump on hard-close + reopen (env() starts at 0, resolves post-paint).
  useEffect(() => {
    const cachedSab = localStorage.getItem('faded_sab');
    if (cachedSab !== null) document.documentElement.style.setProperty('--sab', parseFloat(cachedSab) + 'px');

    // Full recalc — updates BOTH --app-height and --sab.
    // Only called on init, resize, and orientationchange (real viewport changes).
    // NEVER called on visibilitychange — window.innerHeight fluctuates briefly on
    // iOS Capacitor resume and updating --app-height triggers a layout reflow that
    // visually shifts position:fixed elements.
    const set = () => {
      const h = window.innerHeight;
      document.documentElement.style.setProperty("--app-height", `${h}px`);
      requestAnimationFrame(() => {
        const computed = getComputedStyle(document.documentElement);
        const sab = parseFloat(computed.getPropertyValue('--sab-env')) || 0;
        document.documentElement.style.setProperty('--sab', sab + 'px');
        const keyboardOpen = (window.screen.height - h) > 200;
        if (!keyboardOpen) {
          try { localStorage.setItem('faded_sab', sab); } catch {}
          // --sat (safe-area-inset-top) is a physical device constant that never changes
          // with keyboard state. Only update it when keyboard is absent — iOS WKWebView
          // can transiently report wrong env() values during keyboard animation, and the
          // main app CSS that defines --sat-env only mounts at the moment of sign-in
          // (while keyboard may still be dismissing), making that window especially risky.
          const sat = parseFloat(computed.getPropertyValue('--sat-env')) || 0;
          if (sat > 0) {
            document.documentElement.style.setProperty('--sat', sat + 'px');
            try { localStorage.setItem('faded_sat', sat); } catch {}
          }
        }
        if (!navSabReady.current) {
          navSabReady.current = true;
          setNavVisible(true);
        }
      });
    };

    // Resume-only recalc — updates ONLY --sab, never --app-height.
    // Prevents layout reflow (and nav pill jump) on background→foreground.
    const onResume = () => {
      requestAnimationFrame(() => {
        const sab = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sab-env')) || 0;
        const cur = parseFloat(document.documentElement.style.getPropertyValue('--sab') || '0');
        // Only apply if env() resolved to a real value — ignore transient 0s
        if (sab > 0 || cur === 0) {
          document.documentElement.style.setProperty('--sab', sab + 'px');
          try { localStorage.setItem('faded_sab', sab); } catch {}
        }
      });
    };

    set();
    window.addEventListener("resize", set);
    window.addEventListener("orientationchange", set);
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      onResume();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("resize", set);
      window.removeEventListener("orientationchange", set);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // After sign-in the iOS keyboard animates away over ~300ms while React has
  // already rendered the main view. Recalculate --app-height and --sab once the
  // keyboard is fully gone. At 900ms we also re-read --sat: by then the keyboard
  // is definitely dismissed and --sat-env (injected by the main app CSS) is stable,
  // giving us a reliable anchor read that catches any sign-in path (email, Google,
  // Apple OAuth via SFSafariViewController) where the viewport was in flux earlier.
  useEffect(() => {
    const wasSignedOut = !prevSessionRef.current || prevSessionRef.current === "guest";
    prevSessionRef.current = session;
    if (!session || session === "guest") return;
    const setHeightAndSab = () => {
      document.documentElement.style.setProperty("--app-height", `${window.innerHeight}px`);
      requestAnimationFrame(() => {
        const sab = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sab-env')) || 0;
        const cur = parseFloat(document.documentElement.style.getPropertyValue('--sab') || '0');
        if (sab > 0 || cur === 0) {
          document.documentElement.style.setProperty('--sab', sab + 'px');
          try { localStorage.setItem('faded_sab', sab); } catch {}
        }
      });
    };
    const confirmSat = () => {
      requestAnimationFrame(() => {
        const sat = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sat-env')) || 0;
        if (sat > 0) {
          document.documentElement.style.setProperty('--sat', sat + 'px');
          try { localStorage.setItem('faded_sat', sat); } catch {}
        }
      });
    };
    const t1 = setTimeout(setHeightAndSab, 200);
    const t2 = setTimeout(setHeightAndSab, 500);
    const t3 = setTimeout(() => { setHeightAndSab(); confirmSat(); }, 900);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [session]); // eslint-disable-line react-hooks/exhaustive-deps

  // Deep link: ?open_barber=slug — opens a specific barber's profile directly
  // Used by the referral SMS flow so the client lands straight on the barber's card
  useEffect(() => {
    if (!barbers.length) return;
    try {
      const params = new URLSearchParams(window.location.search);
      const slug = params.get("open_barber");
      if (!slug) return;
      // Remove param from URL without reload
      const clean = new URL(window.location.href);
      clean.searchParams.delete("open_barber");
      window.history.replaceState({}, "", clean.toString());
      // Find the barber in the already-loaded list first
      const found = barbers.find(b => b.shareable_slug === slug || b.handle === slug || b.id === slug);
      if (found) { setSelected(found); setTab("discover"); return; }
      // Not in list yet — fetch from DB (match slug, handle, or raw UUID from post-cut-review links)
      supabase.from("barbers")
        .select(`${BARBER_PUBLIC_COLUMNS}, profiles(full_name)`)
        .or(`shareable_slug.eq.${slug},handle.eq.${slug},id.eq.${slug}`)
        .eq("approved", true)
        .or("is_test.is.null,is_test.eq.false")
        .limit(1)
        .then(({ data }) => { if (data?.[0]) { setSelected(data[0]); setTab("discover"); } });
    } catch {}
  }, [barbers]); // eslint-disable-line react-hooks/exhaustive-deps

  // PWA install prompt — Android native + iOS manual
  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) return;
    try { if (localStorage.getItem("faded_install_dismissed")) return; } catch {}
    const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.navigator.standalone;
    const isSafari = /safari/i.test(navigator.userAgent) && !/chrome/i.test(navigator.userAgent);
    const handler = (e) => { e.preventDefault(); installPromptRef.current = e; setTimeout(() => setShowInstallPrompt(true), 8000); };
    window.addEventListener('beforeinstallprompt', handler);
    if (isIOS && isSafari) setTimeout(() => { setIsIOSInstall(true); setShowInstallPrompt(true); }, 8000);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Realtime: watch client bookings for completion → trigger post-cut modal
  useEffect(() => {
    if (!session?.user?.id) return;
    const userRole = session.user.user_metadata?.role;
    if (userRole === "barber") return;
    const ch = supabase.channel("client-bookings-" + session.user.id)
      .on("postgres_changes", { event:"INSERT", schema:"public", table:"bookings", filter:`client_id=eq.${session.user.id}` }, payload => {
        setBookings(prev => prev.some(b => b.id === payload.new.id) ? prev : [payload.new, ...prev]);
      })
      .on("postgres_changes", { event:"UPDATE", schema:"public", table:"bookings", filter:`client_id=eq.${session.user.id}` }, payload => {
        const bk = payload.new;
        setBookings(prev => prev.map(b => b.id === bk.id ? { ...b, ...bk } : b));
        if (bk.status === "completed" && !bk.reviewed) setPostCutBooking(bk);
        // Announce booking status change to VoiceOver
        const statusLabels = { accepted:"Your booking has been accepted", confirmed:"Your booking is confirmed", completed:"Your cut is complete", cancelled:"Your booking was cancelled", declined:"Your booking was declined" };
        if (statusLabels[bk.status]) { setA11yAnnouncement(statusLabels[bk.status]); setTimeout(()=>setA11yAnnouncement(""), 1500); }
      }).subscribe();
    return () => supabase.removeChannel(ch);
  }, [session?.user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load profile
  useEffect(() => { loadProfile(); }, [session?.user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const loadProfile = async () => {
    // Guard against null, undefined, AND the "guest" string
    if (!session || session === "guest" || typeof session === "string") {
      setProfile(null); setMyBarberData(null);
      setNeedsOnboard(false); setBookings([]);
      setFavorites((() => { try { return JSON.parse(localStorage.getItem("faded_demo_favs")||"[]"); } catch(e) { return []; } })());
      return;
    }
    // Only runs for real Supabase session objects with a user property
    if (!session.user) return;

    // OAuth role picker: Google and Apple don't set user_metadata.role, so new OAuth users
    // always defaulted to "client". Show a one-time role picker for these users.
    const isOAuthUser = ['google', 'apple'].includes(session.user.app_metadata?.provider);
    const hasExplicitRole = !!session.user.user_metadata?.role;
    const rolePickedKey = `faded_role_picked_${session.user.id}`;
    const alreadyPicked = (() => { try { return !!localStorage.getItem(rolePickedKey); } catch { return false; } })();
    if (isOAuthUser && !hasExplicitRole && !alreadyPicked) {
      // Check DB before showing picker — existing barbers/admins already have a real role
      const { data: existingForPicker } = await supabase.from("profiles").select("role").eq("id", session.user.id).single();
      if (existingForPicker?.role) {
        // Existing user — stamp localStorage so they never see the picker again
        try { localStorage.setItem(rolePickedKey, "1"); } catch {}
        // fall through using DB role below
      } else {
        // Check if user pre-selected a role before launching Google OAuth
        const pendingOAuthRole = (() => { try { return sessionStorage.getItem('faded_oauth_role'); } catch { return null; } })();
        if (pendingOAuthRole) {
          try { sessionStorage.removeItem('faded_oauth_role'); } catch {}
          try { localStorage.setItem(rolePickedKey, "1"); } catch {}
          const metaNameEarly = session.user.user_metadata?.full_name || session.user.user_metadata?.name || "";
          await supabase.from("profiles").upsert(
            { id: session.user.id, role: pendingOAuthRole, full_name: metaNameEarly },
            { onConflict: "id" }
          );
          // fall through — DB now has the role, upsertProfile below will see it
        } else {
          setNeedsRolePicker(true);
          return;
        }
      }
    }
    setNeedsRolePicker(false);

    // Prefer DB role for OAuth users who have no role in user_metadata
    let metaRole = session.user.user_metadata?.role || "client";
    if (isOAuthUser && !hasExplicitRole) {
      const { data: dbProfile } = await supabase.from("profiles").select("role").eq("id", session.user.id).single();
      if (dbProfile?.role) metaRole = dbProfile.role;
    }
    const metaName = session.user.user_metadata?.full_name || "";
    const metaPhone = session.user.user_metadata?.phone || null;

    // Set profile immediately so isBarber works right away
    setProfile({ id:session.user.id, role:metaRole, full_name:metaName });

    // If barber - check if they have completed onboarding
    // Sequenced with await below rather than fired via un-awaited .then() --
    // a burst of simultaneous native fetch calls right after login correlated
    // with a real SIGSEGV inside Android System WebView's native code (Sentry-
    // captured on a OnePlus 9 Pro/Android 11 in production). This doesn't fix
    // the WebView-level bug itself, but reduces the concurrent JNI-bridge
    // pressure that appears to trigger it.
    let barberOnboarded = false;
    if (metaRole === "barber") {
      const { data:b } = await supabase.rpc("get_my_barber_profile");
      if (b && b.handle) {
        setMyBarberData(b);
        setNeedsOnboard(false);
        barberOnboarded = true;
        // Check if barber owns or belongs to a shop
        const { count } = await supabase.from("shops").select("id", { count:"exact", head:true }).eq("owner_id", session.user.id);
        if (count > 0) {
          setBarberHasShop(true);
        } else {
          const { count: c } = await supabase.from("shop_members").select("id", { count:"exact", head:true }).eq("barber_id", session.user.id).eq("invite_status","active");
          if (c > 0) setBarberHasShop(true);
        }
      } else {
        setNeedsOnboard(true);
      }
    }

    // FIX 18: Upsert profile — never overwrite an existing barber/admin role, but metaRole wins over auto-created "client" rows
    const upsertProfile = async (attempt = 1) => {
      // Fetch existing profile first so we never downgrade an established barber/admin
      const { data: existing } = await supabase.from("profiles").select("*").eq("id",session.user.id).single();
      // metaRole is the role the user chose at sign-up (from auth options.data.role).
      // Only trust existing.role if it's barber or admin — a Supabase trigger may auto-create
      // a "client" profile row which must NOT override an explicit barber sign-up.
      const resolvedRole = (existing?.role === "barber" || existing?.role === "admin")
        ? existing.role
        : metaRole;
      // Do NOT call setProfile(existing) here — existing.role may be "client" from a DB trigger,
      // which would flip isBarber → false and hide the onboarding screen mid-render.
      // The re-fetch at the bottom of this function loads the final corrected profile.
      // Derive referral code from user ID (first 8 hex chars)
      const myRefCode = existing?.referral_code || session.user.id.replace(/-/g,"").substring(0,8).toUpperCase();
      const { error } = await supabase.from("profiles").upsert(
        { id:session.user.id, role:resolvedRole, referral_code:myRefCode, ...(metaName && !existing?.full_name ? { full_name:metaName } : {}), ...(metaPhone && !existing?.phone ? { phone:metaPhone } : {}) },
        { onConflict:"id" }
      );
      if (error && attempt < 2) {
        await new Promise(r => setTimeout(r, 1200));
        return upsertProfile(2);
      }
      if (error) {
        console.error("[FADED] Profile upsert failed after retry:", error.message);
        return;
      }
      // Credit referrer if this user was invited and hasn't been credited yet
      if (!existing?.referred_by) {
        const pendingRef = (() => { try { return localStorage.getItem("faded_ref"); } catch { return null; } })();
        if (pendingRef && pendingRef !== myRefCode) {
          const { data: referrer } = await supabase.from("profiles").select("id").eq("referral_code", pendingRef).maybeSingle();
          if (referrer) {
            await supabase.from("profiles").update({ referred_by: referrer.id }).eq("id", session.user.id);
            try { localStorage.removeItem("faded_ref"); } catch {}
          }
        }
      }
      // Attribute campaign code (shop QR codes etc.) if this user arrived through one and
      // hasn't been attributed yet — independent of referred_by, both can be set at once.
      if (!existing?.campaign_code_id) {
        const pendingCampaign = (() => { try { return localStorage.getItem("faded_campaign"); } catch { return null; } })();
        if (pendingCampaign) {
          const { data: campaign } = await supabase.from("campaign_codes").select("id").eq("code", pendingCampaign).eq("active", true).maybeSingle();
          if (campaign) {
            await supabase.from("profiles").update({ campaign_code_id: campaign.id }).eq("id", session.user.id);
            try { localStorage.removeItem("faded_campaign"); } catch {}
          }
        }
      }
      const { data } = await supabase.from("profiles").select("*").eq("id",session.user.id).single();
      if (data) setProfile(data);
      // Load how many friends this user has referred
      supabase.from("profiles").select("id", { count:"exact", head:true }).eq("referred_by", session.user.id)
        .then(({ count }) => setReferralCount(count || 0));
    };
    await upsertProfile();

    // Load favorites and bookings
    const demoFavs = (() => { try { return JSON.parse(localStorage.getItem("faded_demo_favs")||"[]"); } catch(e) { return []; } })();
    const { data: favData } = await supabase.from("favorites").select("barber_id").eq("client_id",session.user.id);
    setFavorites([...(favData||[]).map(f=>f.barber_id), ...demoFavs]);
    const localBks = (() => { try { return JSON.parse(localStorage.getItem("faded_local_bookings")||"[]"); } catch { return []; } })();
    const { data: bookingsData } = await supabase.from("bookings").select("*").eq("client_id",session.user.id)
      .order("created_at",{ascending:false}).limit(50);
    const freshLocalBks = dropStaleLocalBookings(localBks, bookingsData);
    setBookings([...(bookingsData||[]), ...freshLocalBks]);

    // Subscribe to push last, after everything else in this chain has settled --
    // it's still fire-and-forget (never awaited) since Notification.requestPermission()
    // shows a native OS dialog that could sit unanswered indefinitely, but it no
    // longer overlaps with the shop-check/upsert/favorites/bookings sequence above.
    // Previously fired mid-chain right after the barber-profile check, which was one
    // of the concurrent native calls behind a real Sentry-captured WebView SIGSEGV.
    if (metaRole === "client" || barberOnboarded) subscribeToPush(session.user.id);
  };

  // Load barbers with GPS distance
  useEffect(() => {
    const load = async () => {
      // Demo barbers only visible to showcase/reviewer accounts — hidden from public after launch
      const showcaseEmails = ["barber-test@letsgetfaded.com","client-test@letsgetfaded.com","apple.reviewer@letsgetfaded.com","google.reviewer@letsgetfaded.com","kunal.kumar@live.com"];
      const isShowcase = session && session !== "guest" && showcaseEmails.includes(session?.user?.email);

      const { data, error } = await supabase.from("barbers").select(`${BARBER_PUBLIC_COLUMNS}, profiles(full_name,avatar_url)`).eq("approved",true).not("handle","is",null).or("is_test.is.null,is_test.eq.false").limit(200);
      const real = (error||!data||data.length===0) ? [] : data.map(b=>({ ...b, full_name:(Array.isArray(b.profiles)?b.profiles[0]?.full_name:b.profiles?.full_name)||"Barber", _distKm:gps.lat&&gps.lng&&b.lat&&b.lng?haversineKm(gps.lat,gps.lng,b.lat,b.lng):99 }));

      if (isShowcase) {
        // Test/reviewer accounts see ONLY demo barbers — never mixed with real live barbers
        const demos = DEMO_BARBERS.map(b => ({ ...b, _distKm: gps.lat&&gps.lng ? haversineKm(gps.lat,gps.lng,b.lat,b.lng) : b._distKm }));
        const { data: dbDemoData } = await supabase.from("demo_barbers").select("*").limit(250);
        const dbDemos = (dbDemoData||[]).map(b => ({ ...b, _isDemo:true, _distKm:gps.lat&&gps.lng&&b.lat&&b.lng?haversineKm(gps.lat,gps.lng,b.lat,b.lng):99 }));
        setBarbers([...demos, ...dbDemos]);
      } else {
        setBarbers(real);
      }
      // Load shops for discovery — exclude demo/test shops for real clients
      const shopsQuery = supabase.from("shops").select("*, shop_members(barber_id, invite_status)").limit(50);
      const { data: shopData } = isShowcase ? await shopsQuery : await shopsQuery.or("is_demo.is.null,is_demo.eq.false");
      if (shopData) setNearbyShops(shopData);
      setLoading(false);
    };
    if (!gps.loading) load();
  }, [gps.lat, gps.lng, gps.loading, refreshKey, session]);

  // Barber: subscribe to instant "Get Faded Now" client pings within radius
  // NOTE: isBarber is declared below so we derive it here from profile/session
  const _isBarberForPing = !!(profile?.role === "barber" || session?.user?.user_metadata?.role === "barber");
  useEffect(() => {
    if (!_isBarberForPing || !myBarberData) return;
    const ch = supabase.channel("instant-client-pings")
      .on("postgres_changes", { event:"INSERT", schema:"public", table:"instant_requests" }, payload => {
        const req = payload.new;
        console.log("[FADED] barber ping received", { requestId: req.id, status: req.status, clientLat: req.client_lat, clientLng: req.client_lng });
        if (req.status !== "open") { console.log("[FADED] barber ping ignored — status:", req.status); return; }
        const barberLat = myBarberData.lat;
        const barberLng = myBarberData.lng;
        if (!barberLat || !barberLng) { console.warn("[FADED] barber ping ignored — barber has no location"); return; }
        const dist = haversineKm(barberLat, barberLng, req.client_lat, req.client_lng);
        console.log("[FADED] barber ping distance:", dist.toFixed(2), "km, radius:", req.radius_km, "available:", myBarberData.is_available);
        if (dist <= (req.radius_km || 10) && myBarberData.status === "available" && myBarberData.approved === true) {
          console.log("[FADED] barber ping accepted — showing card");
          setIncomingClientPing({ ...req, _distKm: dist });
          setTimeout(() => setIncomingClientPing(p => p?.id === req.id ? null : p), 90000);
        } else {
          console.log("[FADED] barber ping rejected — out of range or unavailable");
        }
      })
      .subscribe(status => {
        console.log("[FADED] instant-client-pings channel:", status);
      });
    return () => supabase.removeChannel(ch);
  }, [_isBarberForPing, myBarberData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Real-time barber status
  useEffect(() => {
    const ch = supabase.channel("barbers-live")
      .on("postgres_changes",{ event:"UPDATE", schema:"public", table:"barbers" }, payload => {
        setBarbers(prev=>prev.map(b=>b.id===payload.new.id?{...b,...payload.new}:b));
        setMyBarberData(prev=>prev && prev.id===payload.new.id ? {...prev,...payload.new} : prev);
      }).subscribe();
    return ()=>supabase.removeChannel(ch);
  }, []);

  useEffect(()=>{ const iv=setInterval(()=>setLivePulse(p=>!p),1800); return()=>clearInterval(iv); },[]);

  // Request notification permission and start reminders
  useEffect(() => {
    if (!session || session === "guest") return;
    // Ask for notification permission after 3 seconds (not immediately)
    const timer = setTimeout(async () => {
      const granted = await requestNotificationPermission();
      if (granted) {
        scheduleCutReminder(2); // every 2 weeks
        checkCutReminder();
        checkUpcomingOccasions();
      }
    }, 3000);
    return () => clearTimeout(timer);
  }, [session]);

  // Check reminders every time app opens
  useEffect(() => {
    checkApptReminders(); // check on mount
    const interval = setInterval(() => {
      checkCutReminder();
      checkApptReminders();
      checkUpcomingOccasions();
    }, 60 * 60 * 1000); // check every hour
    return () => clearInterval(interval);
  }, []);

  const toggleFavorite = useCallback(async (barberId) => {
    const isDemo = String(barberId).startsWith("demo_");
    const isLoggedIn = session && session !== "guest";

    if (!isLoggedIn && !isDemo) {
      setToast({ msg:"Sign up to save barbers ❤️", type:"info" });
      setTimeout(()=>setToast(null), 2500);
      return;
    }

    const isFav = favorites.includes(barberId);
    if (isFav) {
      if (!isDemo && isLoggedIn) await supabase.from("favorites").delete().eq("client_id",session.user.id).eq("barber_id",barberId);
      setFavorites(prev => {
        const next = prev.filter(id=>id!==barberId);
        try { localStorage.setItem("faded_demo_favs", JSON.stringify(next.filter(id=>String(id).startsWith("demo_")))); } catch(e) {}
        return next;
      });
    } else {
      if (!isDemo && isLoggedIn) await supabase.from("favorites").insert({ client_id:session.user.id, barber_id:barberId });
      haptic.medium();
      setFavorites(prev => {
        const next = [...prev, barberId];
        try { localStorage.setItem("faded_demo_favs", JSON.stringify(next.filter(id=>String(id).startsWith("demo_")))); } catch(e) {}
        return next;
      });
    }
  }, [session, favorites]);

  const submitReview = useCallback(async ({ booking_id, barber_id, rating, comment, tip_amount }) => {
    if (!session || session === "guest") return;
    // Review fraud gate — account must be ≥24h old and have a completed booking with this barber.
    // Param names must match the DB function's actual signature (p_reviewer_id, p_barber_id) --
    // they'd drifted out of sync, which made this RPC call fail every time and silently no-op
    // the gate (canReviewData came back null, so the "not eligible" check never triggered).
    const { data: canReviewData } = await supabase.rpc("can_submit_review", {
      p_reviewer_id: session.user.id,
      p_barber_id: barber_id,
    });
    if (canReviewData && !canReviewData.can_review) {
      setToast({ msg: canReviewData.reason || "Not eligible to review this barber yet", type:"error" });
      setTimeout(() => setToast(null), 4000);
      return;
    }
    // Upsert on booking_id — one review per booking, clients can edit theirs but not spam.
    // Errors must be checked here: this previously went unchecked, so a broken DB constraint
    // (fixed separately) let every review fail silently while the booking still got marked
    // reviewed:true and the barber still got a real "New Review!" push for a review that was
    // never actually saved.
    const { error: reviewError } = await supabase.from("reviews").upsert(
      { booking_id, barber_id, client_id:session.user.id, rating, comment:sanitize(comment||"",1000) },
      { onConflict: "booking_id" }
    );
    if (reviewError) {
      setToast({ msg: "Couldn't submit your review — please try again", type:"error" });
      setTimeout(() => setToast(null), 4000);
      throw reviewError;
    }
    const tipUpdate = tip_amount ? { reviewed:true, tip_amount } : { reviewed:true };
    await supabase.from("bookings").update(tipUpdate).eq("id",booking_id);
    setBookings(prev=>prev.map(b=>b.id===booking_id?{...b,reviewed:true,...tipUpdate}:b));
    track("review_submitted", { rating, has_photo: false });
    // Push notification to barber
    supabase.functions.invoke("send-push-to-user", {
      body: {
        user_id: barber_id,
        title: "⭐ New Review!",
        body: `You got a ${rating}-star review${comment ? `: "${comment.slice(0, 60)}"` : ""}`,
        data: { tab: "dashboard" },
      }
    }).catch(() => {});
  }, [session]);

  const cancelBooking = useCallback(async (id) => {
    const booking = bookings.find(b => b.id === id);
    track("booking_cancelled", { booking_id: id, service_type: booking?.service_type, cancelled_by: "client" });
    // Optimistic update — React state + localStorage so it survives app restart
    setBookings(prev => prev.map(b => b.id === id ? { ...b, status:"cancelled" } : b));
    try {
      const localBks = JSON.parse(localStorage.getItem("faded_local_bookings") || "[]");
      localStorage.setItem("faded_local_bookings", JSON.stringify(
        localBks.map(b => b.id === id ? { ...b, status:"cancelled" } : b)
      ));
    } catch {}
    if (session && session !== "guest") {
      // Use cancel_booking RPC — enforces fee policy and logs cancelled_by
      const { data: cancelResult } = await supabase.rpc("cancel_booking", {
        p_booking_id: id,
        p_cancelled_by: "client",
      });
      if (cancelResult?.fee_cents > 0 && booking) {
        const feeCents = cancelResult.fee_cents;
        const hasSavedCard = !!booking.saved_payment_method_id;
        const isCardBooking = booking.payment_method === "Credit / Debit Card" && !!booking.stripe_payment_intent_id;
        if (isCardBooking) {
          supabase.functions.invoke("stripe-capture-payment", {
            body: { payment_intent_id: booking.stripe_payment_intent_id, booking_id: id, amount_to_capture: feeCents, charge_type: "late_cancel" },
          }).catch(() => {});
        } else if (hasSavedCard) {
          supabase.functions.invoke("stripe-charge-saved-card", {
            body: { booking_id: id, charge_type: "late_cancel" },
          }).catch(() => {});
        }
      }
      // Notify barber via SMS and push
      if (booking?.barber_id) {
        supabase.rpc("get_barber_phone_for_booking", { p_booking_id: id })
          .then(({ data: phone }) => {
            if (phone) {
              const apptTime = booking.scheduled_at ? formatLocalTime(booking.scheduled_at) : "your scheduled time";
              sendSMS(phone, `FADED: ${booking.client_name || "A client"} cancelled their ${booking.service_type || "appointment"} on ${apptTime}. Your slot is now open.`);
            }
          }).catch(() => {});
        supabase.functions.invoke("send-push-to-user", {
          body: {
            user_id: booking.barber_id,
            title: "Booking Cancelled",
            body: `${booking.client_name||"A client"} cancelled their ${booking.service_type||"appointment"}. Your slot is open.`,
            data: { tab: "appointments" },
          }
        }).catch(() => {});
      }
    }
  }, [session, bookings]);

  const refreshClientBookings = useCallback(async () => {
    if (!session || session === "guest" || typeof session === "string") return;
    const localBks = (() => { try { return JSON.parse(localStorage.getItem("faded_local_bookings") || "[]"); } catch { return []; } })();
    const { data } = await supabase.from("bookings").select("*").eq("client_id", session.user.id)
      .order("created_at", { ascending: false }).limit(50);
    const freshLocalBks = dropStaleLocalBookings(localBks, data);
    setBookings([...(data || []), ...freshLocalBks]);
  }, [session]);

  const rebookBarber = useCallback(async (booking) => {
    const barber = barbers.find(b => b.id === booking.barber_id);
    if (!barber) {
      // Barber not in local list — check if they still exist in DB
      const { data } = await supabase.from("barbers").select("id,status").eq("id", booking.barber_id).is("deleted_at", null).limit(1);
      if (!data?.length) {
        setToast({ msg: `${booking.barber_name || "This barber"} is no longer on FADED — find another on the map`, type:"info" });
        setTimeout(() => setToast(null), 4000);
        setTab("discover");
        return;
      }
    }
    if (!barber) return;

    // Check availability before opening the booking sheet
    const wh = barber.working_hours;
    const hasWorkingHours = wh && Object.keys(wh).some(dow => wh[dow] !== null);
    const isOnline = barber.status === "open";
    const windowDays = barber.booking_window_days || 14;
    let hasFutureDay = false;
    if (hasWorkingHours) {
      const now = new Date();
      for (let i = 0; i <= windowDays; i++) {
        const dow = new Date(now.getTime() + i * 86400000).getDay();
        if (wh[dow] !== null && wh[dow] !== undefined) { hasFutureDay = true; break; }
      }
    }
    if (!isOnline && !hasFutureDay) {
      setToast({ msg: `${barber.full_name?.split(" ")[0] || "This barber"} has no available times right now — try again later`, type:"info" });
      setTimeout(() => setToast(null), 4000);
      return;
    }

    setRescheduleBooking(booking);
    setSelected(barber);
    setRebookPrefill(null);
    setTab("discover");
  }, [barbers]);

  // Universal refresh — reloads barbers + bookings depending on context
  const doRefresh = useCallback((activeTab) => {
    setRefreshing(true);
    setRefreshKey(k => k + 1);
    if (session?.user?.id && session !== "guest") {
      supabase.from("bookings").select("*").eq("client_id", session.user.id)
        .order("created_at", { ascending:false }).limit(50)
        .then(({ data }) => { if (data) setBookings(data); });
    }
    setTimeout(() => setRefreshing(false), 1200);
  }, [session]); // eslint-disable-line react-hooks/exhaustive-deps

  // Barber inactivity auto-logout after 18 minutes
  useEffect(() => {
    const userRole = session?.user?.user_metadata?.role;
    if (!session?.user?.id || userRole !== "barber") return;
    const TIMEOUT = 18 * 60 * 1000;
    let timer;
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        supabase.auth.signOut();
        setSession(null);
      }, TIMEOUT);
    };
    const events = ["mousedown", "mousemove", "keypress", "touchstart", "scroll", "click"];
    events.forEach(ev => window.addEventListener(ev, reset, { passive:true }));
    reset();
    return () => { clearTimeout(timer); events.forEach(ev => window.removeEventListener(ev, reset)); };
  }, [session?.user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const lat = gps.lat, lng = gps.lng;
    // Pre-compute distance once per barber — avoids O(n log n) haversine calls during sort
    const withDist = barbers.map(b => ({ b, d: distKm(b, lat, lng) }));
    const statusOrder = { available:0, soon:1, busy:2, offline:3 };
    return withDist
      .filter(({ b }) => urgent ? b.status === "available" : true)
      .filter(({ b }) => styleMatch(b, styleFilter))
      .filter(({ b }) => priceMatch(b, priceFilter))
      .filter(({ b }) => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (b.full_name||"").toLowerCase().includes(q) || (b.handle||"").toLowerCase().includes(q) || (b.bio||"").toLowerCase().includes(q) || (b.city||"").toLowerCase().includes(q) || (b.neighbourhood||"").toLowerCase().includes(q) || (b.specialty||[]).some(s=>s.toLowerCase().includes(q)) || (b.service_areas||[]).some(s=>(s||"").toLowerCase().includes(q));
      })
      .sort((x, y) => {
        if (sort === "availability") return (statusOrder[x.b.status] - statusOrder[y.b.status]) || (x.d - y.d);
        if (sort === "distance") return x.d - y.d;
        if (sort === "rating") {
          // Weight rating by review count: barbers with <5 reviews rank below established ones
          const scoreA = (x.b.review_count || 0) >= 5 ? (x.b.rating || 0) : (x.b.rating || 0) * 0.5;
          const scoreB = (y.b.review_count || 0) >= 5 ? (y.b.rating || 0) : (y.b.rating || 0) * 0.5;
          return scoreB - scoreA;
        }
        return 0;
      })
      .map(({ b }) => b);
  }, [barbers, gps.lat, gps.lng, urgent, styleFilter, priceFilter, search, sort]); // eslint-disable-line react-hooks/exhaustive-deps

  const availableNow = filtered.filter(b=>b.status==="available").length;
  // Guest mode — MUST be before isBarber
  const isGuest  = session === "guest" || (guestMode && !session);
  const isBarber = !isGuest && !viewAsClient && (profile?.role||session?.user?.user_metadata?.role)==="barber";
  const isAdmin  = !isGuest && (profile?.role === "admin" || session?.user?.user_metadata?.role === "admin" || session?.user?.email === "fadednow.app@gmail.com" || session?.user?.id === "04445aba-838b-4fc3-9186-d96ac9ed8f5e");

  // Standalone public pages — no auth required
  const currentPath = window.location.pathname;
  if (currentPath === "/privacy") return <LegalPage doc="privacy"/>;
  if (currentPath === "/terms")   return <LegalPage doc="terms"/>;
  // /barber/:slug deep links — rewrite to ?open_barber= so the existing handler picks it up
  if (currentPath.startsWith("/barber/")) {
    const barberSlug = currentPath.replace("/barber/", "").split("/")[0];
    if (barberSlug) window.history.replaceState({}, "", `/?open_barber=${encodeURIComponent(barberSlug)}`);
  }

  if (session===undefined || !fontsLoaded) return (
    <div style={{ minHeight:"100dvh", background:"#0A0A0A", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:20 }}>
      <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:40, letterSpacing:6, color:"#fff" }}>FAD<span style={{ color:"#2563EB" }}>ED</span></div>
      <div style={{ width:28, height:28, border:"3px solid rgba(255,255,255,0.12)", borderTopColor:"#2563EB", borderRadius:"50%", animation:"spin 0.8s linear infinite" }}/>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  const enterApp = (initialMode) => {
    const mode = typeof initialMode === "string" ? initialMode : "landing";
    setAuthInitialMode(mode);
    setLandingFading(true);
    setTimeout(() => { setShowLanding(false); setLandingFading(false); }, 520);
  };

  if (!isOnline) return (
    <div style={{ minHeight:"100dvh", background:"#0A0A0A", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:32, fontFamily:"'DM Sans',sans-serif", textAlign:"center" }}>
      <div style={{ fontSize:48, fontWeight:900, letterSpacing:4, color:"#F0F0F0", marginBottom:8, fontFamily:"'Syne',sans-serif" }}>FAD<span style={{ color:"#2563EB" }}>ED</span></div>
      <div style={{ fontSize:36, marginBottom:20 }}>📵</div>
      <div style={{ fontSize:20, fontWeight:800, color:"#F0F0F0", marginBottom:8 }}>You're offline</div>
      <div style={{ fontSize:14, color:"#888", lineHeight:1.6, maxWidth:280, marginBottom:32 }}>Check your connection — FADED needs internet to show barbers near you.</div>
      <button onClick={()=>window.location.reload()} style={{ padding:"14px 32px", background:"#2563EB", border:"none", borderRadius:16, color:"#fff", fontSize:15, fontWeight:800, cursor:"pointer" }}>Try Again</button>
    </div>
  );

  if (splash.show) return <SplashScreen onDone={() => setSplash({ show:false, fading:false })}/>;
  if (barberLoginSplash) return <SplashScreen onDone={() => setBarberLoginSplash(false)}/>;

  // Forced update gate — blocks the whole app until the user updates. Reserved for
  // releases where app_versions.force_update is flipped on (crash/payment fixes).
  if (versionCheck?.force) {
    const storeUrl = versionCheck.platform === "ios"
      ? "https://apps.apple.com/ca/app/get-faded/id6782978371"
      : "https://play.google.com/store/apps/details?id=com.letsgetfaded.app";
    return (
      <div style={{ position:"fixed", inset:0, background:"#0A0A0A", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:16, padding:24, textAlign:"center", zIndex:999999 }}>
        <div style={{ fontSize:32, fontWeight:900, letterSpacing:6, color:"#fff", fontFamily:"'Bebas Neue',sans-serif" }}>FAD<span style={{color:"#2563EB"}}>ED</span></div>
        <div style={{ fontSize:15, fontWeight:700, color:"#F5F5F0", fontFamily:"'DM Sans',sans-serif", maxWidth:320 }}>Update Required</div>
        <div style={{ fontSize:13, color:"rgba(255,255,255,0.5)", fontFamily:"'DM Sans',sans-serif", maxWidth:300, lineHeight:1.5 }}>
          {versionCheck.message || "Please update FADED to keep using the app."}
        </div>
        <a href={storeUrl} target="_blank" rel="noopener noreferrer"
          style={{ marginTop:8, padding:"12px 24px", background:"#2563EB", border:"none", borderRadius:14, color:"#fff", fontSize:14, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", textDecoration:"none" }}>
          Update Now
        </a>
      </div>
    );
  }

  // Admin toast — global, shows on any page when ?admin_ok or ?admin_err is in the URL
  const AdminToastEl = adminToast ? (
    <div onClick={() => setAdminToast(null)} style={{ position:"fixed", top:24, left:"50%", transform:"translateX(-50%)", zIndex:99999, background:"#111", border:`1.5px solid ${adminToast.color}`, borderRadius:14, padding:"14px 22px", fontSize:15, fontWeight:700, color:adminToast.color, boxShadow:"0 8px 32px rgba(0,0,0,0.6)", whiteSpace:"nowrap", cursor:"pointer", letterSpacing:"0.01em" }}>
      {adminToast.msg}
    </div>
  ) : null;


  // Pre-launch gate — bypass with ?beta=1 or existing session
  const LAUNCH_DATE = new Date("2026-06-24T09:00:00");
  const isBeta = new URLSearchParams(window.location.search).has("beta") || (() => { try { return localStorage.getItem("faded_beta") === "1"; } catch(e) { return false; } })();
  if (isBeta) { try { localStorage.setItem("faded_beta", "1"); } catch(e) {} }
  const hasBarberLink = new URLSearchParams(window.location.search).has("open_barber");
  const isPreLaunch = new Date() < LAUNCH_DATE && !isBeta && !session && !hasBarberLink;
  if (isPreLaunch) {
    window.location.replace("https://letsgetfaded.com");
    return null;
  }

  if (passwordResetMode) return <PasswordResetScreen onDone={() => { setPasswordResetMode(false); setSession(null); setShowLanding(true); }}/>;

  // OAuth PKCE callback — URL has ?code= so Supabase is exchanging it.
  // Show a spinner so users can't click Google again and generate a new
  // PKCE verifier that would invalidate the in-flight code exchange.
  // codeExchangeTimedOut (set by the watchdog effect below) gives this an
  // escape hatch if the exchange hangs or fails silently instead of
  // spinning forever with no way out.
  if (!session && !guestMode && window.location.search.includes('code=')) return (
    <div style={{ position:"fixed", inset:0, background:"#0A0A0A", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:16, padding:24, textAlign:"center" }}>
      <div style={{ fontSize:32, fontWeight:900, letterSpacing:6, color:"#fff", fontFamily:"'Bebas Neue',sans-serif" }}>FAD<span style={{color:"#2563EB"}}>ED</span></div>
      {codeExchangeTimedOut ? (
        <>
          <div style={{ fontSize:15, fontWeight:700, color:"#F5F5F0", fontFamily:"'DM Sans',sans-serif", maxWidth:320 }}>This link may have expired or been opened in an unsupported browser.</div>
          <div style={{ fontSize:13, color:"rgba(255,255,255,0.4)", fontFamily:"'DM Sans',sans-serif", maxWidth:300 }}>Please request a new one from the sign-in screen.</div>
          <button onClick={() => { const clean = new URL(window.location.href); clean.search = ""; window.history.replaceState({}, "", clean.toString()); window.location.reload(); }}
            style={{ marginTop:8, padding:"12px 24px", background:"#2563EB", border:"none", borderRadius:14, color:"#fff", fontSize:14, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>
            Back to Sign In
          </button>
        </>
      ) : (
        <>
          <div style={{ width:36, height:36, border:"3px solid rgba(37,99,235,0.25)", borderTopColor:"#2563EB", borderRadius:"50%", animation:"fadedSpin 0.8s linear infinite" }}/>
          <div style={{ fontSize:13, color:"rgba(255,255,255,0.4)", fontFamily:"'DM Sans',sans-serif" }}>Signing you in…</div>
        </>
      )}
    </div>
  );
  if (!session && !guestMode) return <>{AdminToastEl}<AuthScreen onBack={()=>setShowLanding(true)} onAuth={s=>{ if(s==="guest"){ setGuestMode(true); setTab("discover"); } else setSession(s); }} initialMsg={oauthErr} initialMode={authInitialMode} initialRole={joinRoleDeepLink}/></>;
  if (needsRolePicker && session && session !== "guest" && !isAdmin) {
    const handleOAuthRolePick = async (chosenRole) => {
      const metaName = session.user.user_metadata?.full_name || session.user.user_metadata?.name || "";
      await supabase.from("profiles").upsert(
        { id: session.user.id, role: chosenRole, full_name: metaName },
        { onConflict: "id" }
      );
      try { localStorage.setItem(`faded_role_picked_${session.user.id}`, "1"); } catch {}
      setProfile({ id: session.user.id, role: chosenRole, full_name: metaName });
      setNeedsRolePicker(false);
      if (chosenRole === "barber") {
        setNeedsOnboard(true);
        subscribeToPush(session.user.id);
      } else {
        supabase.from("favorites").select("barber_id").eq("client_id", session.user.id)
          .then(({ data }) => setFavorites((data||[]).map(f => f.barber_id)));
        supabase.from("bookings").select("*").eq("client_id", session.user.id)
          .order("created_at",{ascending:false}).limit(50)
          .then(({ data }) => setBookings(data||[]));
        subscribeToPush(session.user.id);
      }
    };
    const dismissRolePicker = () => {
      supabase.auth.signOut();
      setSession(null);
      setNeedsRolePicker(false);
      setShowLanding(false);
    };
    return (
      <div style={{height:"100%",background:"#0A0A0A",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"32px 24px",boxSizing:"border-box",position:"relative"}}>
        <button onClick={dismissRolePicker} style={{position:"absolute",top:"max(20px,var(--sat, 20px))",right:20,background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.12)",borderRadius:"50%",width:36,height:36,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",color:"rgba(255,255,255,0.5)",fontSize:18,lineHeight:1}}>×</button>
        <div style={{fontSize:28,fontWeight:900,letterSpacing:"0.06em",color:"#fff",marginBottom:6}}>
          FAD<span style={{color:"#2563EB"}}>ED</span>
        </div>
        <p style={{fontSize:14,color:"#6B7280",marginBottom:48,textAlign:"center",margin:"6px 0 48px"}}>One quick question before we continue</p>
        <h2 style={{fontSize:22,fontWeight:800,color:"#F5F5F0",textAlign:"center",margin:"0 0 8px"}}>How are you using FADED?</h2>
        <p style={{fontSize:14,color:"#9CA3AF",textAlign:"center",margin:"0 0 36px",maxWidth:300,lineHeight:1.5}}>This helps us set up the right experience for you.</p>
        <div style={{width:"100%",maxWidth:340,display:"flex",flexDirection:"column",gap:12}}>
          <button onClick={() => handleOAuthRolePick("client")} style={{display:"flex",alignItems:"center",gap:16,width:"100%",background:"#111",border:"1.5px solid #1F2937",borderRadius:14,padding:"18px 20px",cursor:"pointer",textAlign:"left",transition:"border-color 0.15s"}}>
            <span style={{fontSize:28,flexShrink:0}}>✂️</span>
            <div>
              <div style={{fontWeight:800,color:"#F5F5F0",fontSize:16}}>I'm looking for a barber</div>
              <div style={{fontSize:13,color:"#6B7280",marginTop:3}}>Discover and book barbers near you</div>
            </div>
          </button>
          <button onClick={() => handleOAuthRolePick("barber")} style={{display:"flex",alignItems:"center",gap:16,width:"100%",background:"#111",border:"1.5px solid #1F2937",borderRadius:14,padding:"18px 20px",cursor:"pointer",textAlign:"left",transition:"border-color 0.15s"}}>
            <span style={{fontSize:28,flexShrink:0}}>💈</span>
            <div>
              <div style={{fontWeight:800,color:"#F5F5F0",fontSize:16}}>I'm a barber</div>
              <div style={{fontSize:13,color:"#6B7280",marginTop:3}}>List my services and take bookings</div>
            </div>
          </button>
        </div>
      </div>
    );
  }
  if (isBarber&&needsOnboard&&!isGuest) return (
    <ThemeCtx.Provider value={{ t, isDark, toggleTheme }}>
      <ErrorBoundary title="We hit a snag" message="Sorry — an update is in progress. Tap below to reload and try again. Your account is safe.">
        <BarberOnboarding session={session} onComplete={()=>{ setNeedsOnboard(false); supabase.rpc("get_my_barber_profile").then(({data})=>setMyBarberData(data)); }} onBack={()=>{ supabase.auth.signOut(); setSession(null); setShowLanding(true); }}/>
      </ErrorBoundary>
    </ThemeCtx.Provider>
  );
  if (pendingShopJoin && session && session !== "guest" && isBarber) return (
    <ThemeCtx.Provider value={{ t, isDark, toggleTheme }}>
      <ShopJoinModal
        shopId={pendingShopJoin}
        session={session}
        onAccept={(status) => {
          try { sessionStorage.removeItem('faded_pending_shop_join'); } catch {}
          setPendingShopJoin(null);
          window.history.replaceState({}, "", window.location.pathname);
          if (status === "active") window.dispatchEvent(new CustomEvent("faded-toast", { detail: { msg: "✅ You've joined the shop!", type: "success" } }));
          else window.dispatchEvent(new CustomEvent("faded-toast", { detail: { msg: "✅ You're already a member of this shop.", type: "success" } }));
        }}
        onDecline={() => {
          try { sessionStorage.removeItem('faded_pending_shop_join'); } catch {}
          setPendingShopJoin(null);
          window.history.replaceState({}, "", window.location.pathname);
        }}
      />
    </ThemeCtx.Provider>
  );

  // Client welcome splash — once per session login (not for admin)
  // profile must be loaded first so isBarber is accurate; without this guard, the splash
  // briefly flashes for barbers while loadProfile() is still fetching from DB
  if (session && session !== "guest" && profile && !isBarber && !isAdmin && !roleSplashDone) {
    return <ClientWelcomeSplash onDone={() => { setRoleSplashDone(true); try { sessionStorage.setItem("faded_client_welcomed","1"); } catch {} }}/>;
  }

  const adminTab   = isAdmin ? [{id:"admin",label:"Admin"}] : [];
  const clientTabs = [
    {id:"discover",  label:"MAP",      icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg>},
    // Desktop (≥768px): separate BARBERS list tab; MAP tab shows split pane
    ...(isNavDesktop ? [{id:"list", label:"BARBERS", icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>}] : []),
    {id:"saved",     label:"SAVED",    icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>},
    {id:"bookings",  label:"CUTS",     icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><polyline points="9 16 11 18 15 14"/></svg>},
    {id:"profile",   label:"ACCOUNT",  icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>},
    ...adminTab
  ];
  const barberTabs = [
    {id:"appointments",label:"SCHEDULE", icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><polyline points="9 16 11 18 15 14"/></svg>},
    {id:"dashboard",  label:"MANAGE",   icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>},
    ...(barberHasShop
      ? [{id:"shop",  label:"MY SHOP", icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>}]
      : [{id:"booth", label:"BOOTH",   icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/><line x1="12" y1="12" x2="12" y2="16"/><line x1="10" y1="14" x2="14" y2="14"/></svg>}]
    ),
    {id:"earnings",   label:"EARNINGS", icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>},
    {id:"profile",    label:"ACCOUNT",  icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>},
    ...adminTab
  ];
  const tabs = isBarber ? barberTabs : clientTabs;

  return (
    <ThemeCtx.Provider value={{ t, isDark, toggleTheme }}>
    <ResponsiveCtx.Provider value={{ isMd, isDesktop }}>
    <div style={{ display:"flex", minHeight:"100dvh", background:t.bg, fontFamily:"'DM Sans',sans-serif", color:t.t1 }}>
      <style>{`
        @keyframes ripple{0%{transform:scale(1);opacity:.35}100%{transform:scale(2.8);opacity:0}}
        @keyframes fadeIn{from{opacity:0}to{opacity:1}}
        @keyframes slideUp{from{transform:translateY(60px);opacity:0}to{transform:translateY(0);opacity:1}}
        @keyframes popIn{0%{transform:scale(.95);opacity:0}100%{transform:scale(1);opacity:1}}
        @keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
            scroll-behavior: auto !important;
          }
        }
        *{box-sizing:border-box;margin:0;padding:0;}
        ::-webkit-scrollbar{display:none;}
        button{font-family:inherit;}
        input::placeholder,textarea::placeholder{color:${t.t4}!important;}
        body{background:#0A0A0A;min-height:100svh;min-height:100dvh;overscroll-behavior:none;overscroll-behavior-y:none;overflow-x:hidden;max-width:100vw;-webkit-overflow-scrolling:auto;}
        html{overscroll-behavior:none;overscroll-behavior-y:none;overflow-x:hidden;overflow-y:hidden;height:100%;background:#0A0A0A;}
        #root{height:var(--app-height,100dvh);overflow-y:auto;overscroll-behavior-y:none;-webkit-overflow-scrolling:auto;
          background:${(tab==="discover"&&mapView&&!isNavDesktop)?"#0A0A0A":t.bodyBg};
          background-clip:content-box;
          box-sizing:border-box;}
        #root>div:first-child{overflow-x:clip;}
        *{max-width:100%;}
        /* ── Responsive nav switching — CSS-first to avoid JS flicker ── */
        /* Desktop (≥768px): show top nav for clients, hide mobile pill + mobile header */
        @media(min-width:768px){
          .faded-mobile-nav{display:none!important;}
          .faded-mobile-header{display:none!important;}
          .faded-topnav{display:flex!important;}
          .faded-client-scroll{padding-top:60px;}
        }
        /* Mobile (<768px): hide top nav, show pill nav */
        @media(max-width:767px){
          .faded-topnav{display:none!important;}
          .faded-mobile-nav{display:flex!important;}
        }
        /* Desktop hover states — true pointer devices only (no touch) */
        @media(min-width:768px) and (hover:hover){
          .faded-card-hover{transition:box-shadow 0.2s,border-color 0.2s,transform 0.2s;}
          .faded-card-hover:hover{box-shadow:0 8px 32px rgba(59,108,247,0.22),0 2px 8px rgba(0,0,0,0.15)!important;border-color:rgba(59,108,247,0.55)!important;transform:translateY(-2px)!important;}
          /* Button hover — darken 10%; skip elements that handle hover themselves */
          button:not([disabled]):not(.faded-no-hover):hover{filter:brightness(0.88);transition:filter 0.15s;}
          button[disabled]{opacity:0.45;cursor:not-allowed!important;}
        }
        /* ── Desktop polish — min-width:768px ── */
        @media(min-width:768px){
          /* Cursor enforcement */
          button,a,[role="button"],[role="tab"],[role="checkbox"],[role="switch"],
          input[type="checkbox"],input[type="radio"],select,label[for],
          [tabindex]:not([tabindex="-1"]){cursor:pointer;}
          /* Focus visible ring — #3B6CF7 outline, no ugly default */
          :focus-visible{outline:2px solid #3B6CF7!important;outline-offset:3px;border-radius:6px;}
          button:focus-visible,a:focus-visible,[role="button"]:focus-visible{outline:2px solid #3B6CF7!important;outline-offset:3px;border-radius:8px;}
          /* Sheet modals → center-aligned on desktop */
          .faded-sheet-wrap{align-items:center!important;}
          .faded-sheet-wrap>div:first-of-type{border-radius:20px!important;max-width:520px;}
          /* Nav link slide-underline */
          .faded-topnav button{position:relative;overflow:hidden;}
          .faded-topnav button::after{content:'';position:absolute;bottom:0;left:50%;right:50%;height:2px;background:#3B6CF7;transition:left 0.18s ease,right 0.18s ease;}
          .faded-topnav button:hover::after{left:12px;right:12px;}
          /* Styled scrollbars — desktop only */
          ::-webkit-scrollbar{width:6px;height:6px;}
          ::-webkit-scrollbar-track{background:rgba(255,255,255,0.04);border-radius:4px;}
          ::-webkit-scrollbar-thumb{background:#3B6CF7;border-radius:4px;}
          ::-webkit-scrollbar-thumb:hover{background:#2563EB;}
          /* Override hidden scrollbars set for mobile */
          *{scrollbar-width:thin;scrollbar-color:#3B6CF7 rgba(255,255,255,0.04);}
        }
        /* Split-pane map — right panel scrollbar */
        .faded-split-list::-webkit-scrollbar{display:none;}
        .faded-split-list{scrollbar-width:none;}
      `}</style>

      {/* CLIENT TOP NAV — desktop only (≥768px via CSS). Always in DOM; CSS hides on mobile. */}
      {!isBarber && (
        <nav className="faded-topnav" role="navigation" aria-label="Main navigation"
          style={{ display:"none", position:"fixed", top:0, left:0, right:0, height:60, zIndex:200,
            background:"#0C0C10", borderBottom:"1px solid #3B6CF7",
            alignItems:"center", padding:"0 28px", gap:0 }}>
          {/* Logo */}
          <div style={{ fontSize:22, fontWeight:900, letterSpacing:"0.18em", fontFamily:"'Syne',sans-serif", color:"#F5F5F0", marginRight:36, flexShrink:0 }}>
            FAD<span style={{ color:"#3B6CF7" }}>ED</span>
          </div>
          {/* Nav links */}
          <div style={{ display:"flex", alignItems:"stretch", gap:0, flex:1, height:"100%" }}>
            {[
              {id:"discover", label:"MAP",     onClick:()=>{ startTransition(()=>{ setTab("discover"); setMapView(true); }); }},
              {id:"list",     label:"BARBERS", onClick:()=>startTransition(()=>setTab("list"))},
              {id:"saved",    label:"SAVED",   onClick:()=>startTransition(()=>setTab("saved"))},
              {id:"bookings", label:"CUTS",    onClick:()=>startTransition(()=>setTab("bookings"))},
              {id:"profile",  label:"ACCOUNT", onClick:()=>startTransition(()=>setTab("profile"))},
              ...(isAdmin ? [{id:"admin", label:"ADMIN", onClick:()=>startTransition(()=>setTab("admin"))}] : []),
            ].map(item => {
              const active = tab === item.id;
              return (
                <button key={item.id} onClick={item.onClick}
                  style={{ background:"none", border:"none", cursor:"pointer",
                    padding:"0 16px", fontSize:12, fontWeight:active?800:600,
                    color:active?"#3B6CF7":"rgba(245,245,240,0.5)",
                    letterSpacing:"0.07em", fontFamily:"'DM Sans',sans-serif",
                    borderBottom:active?"2px solid #3B6CF7":"2px solid transparent",
                    transition:"color 0.15s, border-color 0.15s", flexShrink:0 }}>
                  {item.label}
                </button>
              );
            })}
          </div>
          {/* Right: live badge + theme toggle + CTA */}
          <div style={{ display:"flex", alignItems:"center", gap:10, flexShrink:0 }}>
            <div style={{ display:"flex", alignItems:"center", gap:5, background:"rgba(34,197,94,0.1)", border:"1px solid rgba(34,197,94,0.25)", padding:"4px 10px", borderRadius:20 }}>
              <span style={{ width:6, height:6, borderRadius:"50%", background:"#22C55E", display:"inline-block", boxShadow:livePulse?"0 0 0 3px rgba(34,197,94,0.3)":"none", transition:"box-shadow 0.8s" }}/>
              <span style={{ fontSize:11, fontWeight:700, color:"#22C55E", letterSpacing:"0.04em" }}>LIVE</span>
            </div>
            <button onClick={toggleTheme} aria-label={isDark?"Switch to light mode":"Switch to dark mode"}
              style={{ width:32, height:32, borderRadius:8, border:"1px solid rgba(59,108,247,0.3)", background:"transparent", cursor:"pointer", fontSize:14, display:"flex", alignItems:"center", justifyContent:"center" }}>
              {isDark?"☀️":"🌙"}
            </button>
            {isGuest ? (
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <button onClick={()=>{ setGuestMode(false); setShowLanding(true); }}
                  style={{ padding:"7px 14px", background:"transparent", border:"1px solid rgba(245,245,240,0.18)", borderRadius:10, color:"rgba(245,245,240,0.6)", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", letterSpacing:"0.04em" }}>
                  ← Exit
                </button>
                <button onClick={()=>{ setSession(null); setGuestMode(false); setAuthInitialMode("signin"); }}
                  style={{ padding:"8px 20px", background:"#3B6CF7", border:"none", borderRadius:10, color:"#fff", fontSize:12, fontWeight:800, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", letterSpacing:"0.05em" }}>
                  SIGN IN
                </button>
              </div>
            ) : (
              <button onClick={()=>startTransition(()=>setTab("profile"))}
                style={{ display:"flex", alignItems:"center", gap:8, padding:"5px 14px 5px 5px", background:"rgba(59,108,247,0.12)", border:"1px solid rgba(59,108,247,0.25)", borderRadius:20, cursor:"pointer" }}>
                <Avatar barber={{ avatar_url:profile?.avatar_url, full_name:profile?.full_name, status:"available" }} size={28}/>
                <span style={{ fontSize:12, fontWeight:700, color:"rgba(245,245,240,0.85)", maxWidth:100, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", fontFamily:"'DM Sans',sans-serif" }}>
                  {profile?.full_name?.split(" ")[0] || "Account"}
                </span>
              </button>
            )}
          </div>
        </nav>
      )}

      {/* DESKTOP SIDEBAR — barbers only, ≥768px */}
      {isNavDesktop && isBarber && (
        <div style={{ width:240, flexShrink:0, background:t.card, borderRight:`1px solid ${t.border}`, display:"flex", flexDirection:"column", padding:"32px 0", position:"sticky", top:0, height:"100dvh", overflowY:"auto" }}>
          <div style={{ padding:"0 24px 32px" }}>
            <div style={{ fontSize:28, fontWeight:900, letterSpacing:3, fontFamily:"'Syne',sans-serif", color:t.t1, marginBottom:4 }}>FAD<span style={{ color:"#2563EB" }}>ED</span></div>
            <div style={{ fontSize:11, color:t.t4 }}>
              {gps.loading?"Finding location...":gps.error?"Enable location":(`${availableNow} live near you`)}
            </div>
          </div>
          <nav style={{ flex:1, padding:"0 12px" }}>
            {tabs.map(tb=>{
              const active = tab === tb.id;
              return (
                <button key={tb.id} onClick={()=>startTransition(()=>setTab(tb.id))} style={{ width:"100%", display:"flex", alignItems:"center", gap:10, padding:"11px 14px", borderRadius:12, border:"none", background:active?"#2563EB18":"transparent", color:active?"#2563EB":t.t2, fontSize:14, fontWeight:active?800:500, cursor:"pointer", marginBottom:2, textAlign:"left", transition:"all 0.15s", position:"relative" }}>
                  {active && <span style={{ position:"absolute", left:0, top:"20%", bottom:"20%", width:3, borderRadius:"0 3px 3px 0", background:"#2563EB" }}/>}
                  <span style={{ opacity:active?1:0.6, flexShrink:0, display:"flex" }}>{tb.icon}</span>
                  {tb.label}
                </button>
              );
            })}
          </nav>
          <div style={{ padding:"0 12px 24px", borderTop:`1px solid ${t.border}`, paddingTop:16, marginTop:16 }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, padding:"0 16px", marginBottom:12 }}>
              <div style={{ display:"flex", alignItems:"center", gap:6, background:"rgba(34,197,94,0.1)", border:"1px solid rgba(34,197,94,0.25)", padding:"5px 10px", borderRadius:20, flex:1 }}>
                <span style={{ width:6, height:6, borderRadius:"50%", background:"#22C55E", display:"inline-block", boxShadow:livePulse?"0 0 0 3px rgba(34,197,94,0.3)":"none", transition:"box-shadow 0.8s" }}/>
                <span style={{ fontSize:11, fontWeight:700, color:"#22C55E" }}>LIVE</span>
              </div>
              <button onClick={toggleTheme} aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"} style={{ minWidth:44, minHeight:44, width:44, height:44, borderRadius:10, border:`1px solid ${t.border2}`, background:t.chip, cursor:"pointer", fontSize:14, display:"flex", alignItems:"center", justifyContent:"center" }}>
                {isDark?"☀️":"🌙"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MAIN CONTENT AREA */}
      <div ref={scrollContainerRef} className={!isBarber?"faded-client-scroll":undefined} style={{ flex:1, maxWidth:isDesktop?"none":isMd?680:480, margin:isDesktop?"0":"0 auto", position:"relative", minWidth:0, overflowY:"auto", height:"var(--app-height,100dvh)" }}>

      {/* MOBILE HEADER — hidden on full-screen map and tabs with own headers; CSS hides on desktop */}
      {!isNavDesktop && !(tab === "discover" && mapView) &&
       !(tab === "bookings" && !isBarber) &&
       !(tab === "appointments" && isBarber) &&
       !(tab === "dashboard" && isBarber) &&
       !(tab === "booth" && isBarber) && (
      <div className="faded-mobile-header" style={{ background:t.headerBg, backdropFilter:"blur(16px)", borderBottom:`1px solid ${t.border}`, paddingLeft:20, paddingRight:20, paddingBottom:12, paddingTop:"max(44px, calc(var(--sat, 0px) + 2px))", position:"sticky", top:0, zIndex:100 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div>
            <div style={{ fontSize:26, color:t.t1, fontFamily:"'Bebas Neue',sans-serif", letterSpacing:"0.06em", lineHeight:1 }}>
              {tab === "discover" ? "DISCOVER" : tab === "saved" ? "SAVED" : tab === "bookings" ? "BOOKINGS" : tab === "profile" ? "ACCOUNT" : tab === "shop" ? "MY SHOP" : tab === "earnings" ? "EARNINGS" : tab === "admin" ? "ADMIN" : "FADED"}
            </div>
            <div style={{ fontSize:11, color:t.t4, marginTop:2, fontFamily:"'Syne',sans-serif", fontWeight:700, letterSpacing:"0.04em" }}>
              FAD<span style={{color:"#2563EB"}}>ED</span> · {gps.loading ? "Finding location…" : gps.error ? "Enable location" : `${availableNow} barber${availableNow!==1?"s":""} nearby`}
            </div>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <button onClick={toggleTheme} style={{ width:34, height:34, borderRadius:10, border:`1px solid ${t.border2}`, background:t.chip, cursor:"pointer", fontSize:16, display:"flex", alignItems:"center", justifyContent:"center" }}>
              {isDark ? "☀️" : "🌙"}
            </button>
            <div style={{ display:"flex", alignItems:"center", gap:6, background:"rgba(34,197,94,0.1)", border:"1px solid rgba(34,197,94,0.25)", padding:"6px 12px", borderRadius:20 }}>
              <span style={{ width:7, height:7, borderRadius:"50%", background:"#22C55E", display:"inline-block", boxShadow:livePulse?"0 0 0 4px rgba(34,197,94,0.3)":"none", transition:"box-shadow 0.8s ease" }}/>
              <span style={{ fontSize:11, fontWeight:700, color:"#22C55E" }}>LIVE</span>
            </div>
          </div>
        </div>

        {/* GPS required gate — FIX 12 & 13: platform-specific instructions */}
        {gps.error && tab === "discover" && (
          <div style={{ margin:"16px 0 0", padding:"20px 18px", background:t.card, borderRadius:18, border:`1.5px solid ${t.border}`, textAlign:"center" }}>
            <div style={{ fontSize:32, marginBottom:10 }}>📍</div>
            <div style={{ fontSize:16, fontWeight:800, color:t.t1, marginBottom:6 }}>Location required</div>
            <div style={{ fontSize:13, color:t.t3, lineHeight:1.6, marginBottom:12 }}>
              FADED only shows barbers near you. Enable location access and tap Retry.
            </div>
            {/* iOS instructions */}
            <div style={{ padding:"10px 12px", background:t.section, borderRadius:10, textAlign:"left", marginBottom:8, border:`1px solid ${t.border}` }}>
              <div style={{ fontSize:11, fontWeight:800, color:t.t2, marginBottom:4 }}>iPhone / Safari</div>
              <div style={{ fontSize:11, color:t.t3, lineHeight:1.7 }}>Settings → Safari → Location → Allow</div>
            </div>
            {/* Android instructions */}
            <div style={{ padding:"10px 12px", background:t.section, borderRadius:10, textAlign:"left", marginBottom:14, border:`1px solid ${t.border}` }}>
              <div style={{ fontSize:11, fontWeight:800, color:t.t2, marginBottom:4 }}>Android / Chrome</div>
              <div style={{ fontSize:11, color:t.t3, lineHeight:1.7 }}>Tap the 🔒 lock icon in the address bar → Site settings → Location → Allow</div>
            </div>
            <button onClick={()=>window.location.reload()} style={{ width:"100%", padding:"13px", background:"#2563EB", border:"none", borderRadius:14, color:"#fff", fontSize:14, fontWeight:800, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", marginBottom:10 }}>
              Retry with Location →
            </button>
            <button onClick={()=>setUrgent(false)} style={{ width:"100%", padding:"11px", background:"transparent", border:`1.5px solid ${t.border}`, borderRadius:14, color:t.t3, fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>
              Browse all barbers without location
            </button>
          </div>
        )}

      </div>
      )}

      {/* DISCOVER — full-screen map (mobile / narrow tablet <768px) */}
      {tab==="discover" && mapView && !isNavDesktop && (
        <div style={{ position:"fixed", top:0, left:0, right:0, bottom:0, zIndex:50, background:"#0A0A0A" }}>
          <MapDiscovery
            barbers={filtered}
            userLat={gps.lat}
            userLng={gps.lng}
            onBook={setSelected}
            t={t}
            isDark={isDark}
            fullScreen={true}
            onListView={isBarber ? null : () => setMapView(false)}
            onLocate={gps.refresh}
            gpsLoading={gps.loading}
          />
        </div>
      )}

      {/* DISCOVER — map for barbers on desktop */}
      {tab==="discover" && isDesktop && isBarber && (
        <div style={{ position:"relative", height:"100dvh", overflow:"hidden" }}>
          <MapDiscovery
            barbers={filtered}
            userLat={gps.lat}
            userLng={gps.lng}
            onBook={setSelected}
            t={t}
            isDark={isDark}
            fullScreen={false}
            onListView={null}
            onLocate={gps.refresh}
            gpsLoading={gps.loading}
            hideFade={true}
          />
        </div>
      )}

      {/* DISCOVER — split pane for clients on desktop (≥768px): 60% map · 40% barber list */}
      {tab==="discover" && isNavDesktop && !isBarber && (
        <div style={{ position:"fixed", top:60, left:0, right:0, bottom:0, zIndex:10, display:"flex" }}>
          {/* Map — 60% */}
          <div style={{ flex:"0 0 60%", position:"relative", height:"100%", borderRight:`1px solid ${t.border}` }}>
            <MapDiscovery barbers={filtered} userLat={gps.lat} userLng={gps.lng} onBook={setSelected} t={t} isDark={isDark} fullScreen={false} onListView={null} onLocate={gps.refresh} gpsLoading={gps.loading} hideFade={true}/>
          </div>
          {/* Barber list panel — 40% */}
          <div className="faded-split-list" style={{ flex:"0 0 40%", overflowY:"auto", background:t.bg, height:"100%", display:"flex", flexDirection:"column" }}>
            {/* Sticky header: count + search + sort chips */}
            <div style={{ padding:"16px 20px 12px", borderBottom:`1px solid ${t.border}`, position:"sticky", top:0, background:t.bg, zIndex:5 }}>
              <div style={{ fontSize:11, fontWeight:800, color:t.t3, letterSpacing:"0.07em", marginBottom:10 }}>
                {filtered.length} BARBER{filtered.length!==1?"S":""} NEARBY
              </div>
              <div style={{ position:"relative", marginBottom:10 }}>
                <span style={{ position:"absolute", left:12, top:"50%", transform:"translateY(-50%)", fontSize:14, pointerEvents:"none" }}>🔍</span>
                <input value={search} onChange={e=>{ const v=e.target.value; startTransition(()=>setSearch(v)); }}
                  placeholder="Search barbers, styles..."
                  style={{ width:"100%", padding:"10px 12px 10px 36px", borderRadius:12, border:`1.5px solid ${t.border2}`, background:t.input, fontSize:13, color:t.inputText, outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box" }}/>
                {search && <button onClick={()=>setSearch("")} style={{ position:"absolute", right:10, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", fontSize:16, cursor:"pointer", color:t.t4 }}>×</button>}
              </div>
              <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                <button onClick={()=>setUrgent(!urgent)} style={{ padding:"5px 12px", borderRadius:20, border:`1.5px solid ${urgent?"#2563EB":t.border2}`, background:urgent?"#2563EB":t.card, color:urgent?"#fff":t.t3, fontSize:11, fontWeight:700, cursor:"pointer" }}>⚡ Now</button>
                {[{id:"availability",label:"🟢 Available"},{id:"distance",label:"📍 Nearest"},{id:"rating",label:"⭐ Top Rated"}].map(s=>(
                  <button key={s.id} onClick={()=>setSort(s.id)} style={{ padding:"5px 12px", borderRadius:20, border:`1.5px solid ${sort===s.id?"#2563EB":t.border2}`, background:sort===s.id?t.blueBg:t.card, color:sort===s.id?t.blueText:t.t3, fontSize:11, fontWeight:700, cursor:"pointer" }}>{s.label}</button>
                ))}
              </div>
            </div>
            {/* Scrollable barber cards */}
            <div style={{ padding:"12px 16px 32px", display:"flex", flexDirection:"column", gap:10, flex:1 }}>
              {loading ? (
                [1,2,3,4].map(i=>(
                  <div key={i} style={{ background:t.card, borderRadius:20, padding:18, border:`1.5px solid ${t.border}`, display:"flex", gap:14, alignItems:"flex-start" }}>
                    <div style={{ width:52, height:52, borderRadius:"50%", background:t.surface2, flexShrink:0, animation:"pulse 1.4s ease-in-out infinite" }}/>
                    <div style={{ flex:1 }}>
                      <div style={{ height:14, borderRadius:8, background:t.surface2, marginBottom:8, width:"60%", animation:"pulse 1.4s ease-in-out infinite" }}/>
                      <div style={{ height:11, borderRadius:8, background:t.surface2, width:"40%", animation:"pulse 1.4s ease-in-out infinite" }}/>
                    </div>
                  </div>
                ))
              ) : filtered.map(b=>(
                <div key={b.id} style={{ animation:"popIn 0.3s ease both" }}>
                  <BarberCard barber={b} onClick={setSelected} userLat={gps.lat} userLng={gps.lng} favorites={favorites} onToggleFav={toggleFavorite}/>
                </div>
              ))}
              {!loading && filtered.length===0 && (
                <div style={{ textAlign:"center", padding:"40px 20px" }}>
                  <div style={{ fontSize:32, marginBottom:8 }}>✂️</div>
                  <div style={{ fontSize:14, fontWeight:800, color:t.t1 }}>No barbers match</div>
                  <div style={{ fontSize:12, color:t.t3, marginTop:4, marginBottom:16 }}>Try adjusting your search or filters</div>
                  {(search||urgent) && <button onClick={()=>{ setSearch(""); setUrgent(false); }} style={{ padding:"8px 20px", background:"#2563EB", border:"none", borderRadius:12, color:"#fff", fontSize:12, fontWeight:700, cursor:"pointer" }}>Clear filters</button>}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* DISCOVER — list view: mobile/tablet combined map+list, OR desktop BARBERS tab (list only) */}
      {((tab==="discover" && !isNavDesktop) || (tab==="list" && isNavDesktop)) && (!mapView || isMd) && !isBarber && (
        <div style={isMd
          ? { display:"flex", flexDirection:"column", height:"100dvh", overflow:"hidden" }
          : { padding:"20px 16px 80px" }}>

          {/* Tablet map strip — only at 640–767px (not ≥768px which has split pane) */}
          {isMd && !isNavDesktop && (
            <div style={{ height:240, flexShrink:0, position:"relative", borderBottom:`1px solid ${t.border}` }}>
              <MapDiscovery barbers={filtered} userLat={gps.lat} userLng={gps.lng} onBook={setSelected} t={t} isDark={isDark} fullScreen={false} onListView={null} onLocate={gps.refresh} gpsLoading={gps.loading}/>
            </div>
          )}

          {/* Scrollable content: search, filters, list */}
          <div style={isMd ? { flex:1, overflowY:"auto", padding:isNavDesktop?"24px 40px 40px":"16px 20px 32px" } : {}}>
          <div style={isMd ? { maxWidth:1280, margin:"0 auto" } : {}}>

          {/* Barber browsing as client — back-to-dashboard banner */}
          {viewAsClient && (
            <div style={{ background:"rgba(37,99,235,0.08)", border:"1px solid rgba(37,99,235,0.25)", borderRadius:14, padding:"12px 16px", marginBottom:14, display:"flex", justifyContent:"space-between", alignItems:"center", gap:12 }}>
              <div style={{ fontSize:13, fontWeight:700, color:"#3B6CF7", fontFamily:"'DM Sans',sans-serif" }}>Browsing as client</div>
              <button onClick={()=>{ setViewAsClient(false); setTab("dashboard"); }} style={{ padding:"8px 14px", background:"#2563EB", border:"none", borderRadius:10, color:"#fff", fontSize:12, fontWeight:800, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", whiteSpace:"nowrap" }}>← My Dashboard</button>
            </div>
          )}

          {/* Guest mode banner */}
          {isGuest && (
            <div style={{ background:"linear-gradient(135deg,#1e3a8a,#1D4ED8)", borderRadius:16, padding:"14px 18px", marginBottom:14, display:"flex", justifyContent:"space-between", alignItems:"center", gap:12, border:"1px solid rgba(255,255,255,0.1)" }}>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:13, fontWeight:800, color:"#fff", fontFamily:"'DM Sans',sans-serif" }}>Browsing as Guest</div>
                <div style={{ fontSize:11, color:"rgba(255,255,255,0.65)", marginTop:2, lineHeight:1.4 }}>Create a free account to save barbers, track bookings & get notified when confirmed</div>
              </div>
              <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:6, flexShrink:0 }}>
                <button onClick={()=>{ setGuestMode(false); setShowLanding(true); }} style={{ padding:"9px 16px", background:"#fff", border:"none", borderRadius:10, color:"#1D4ED8", fontSize:12, fontWeight:800, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", whiteSpace:"nowrap" }}>Join Free →</button>
                <button onClick={()=>{ setGuestMode(false); setShowLanding(true); }} style={{ background:"none", border:"none", color:"rgba(255,255,255,0.5)", fontSize:11, fontWeight:600, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", padding:"0 2px", whiteSpace:"nowrap" }}>← Back</button>
              </div>
            </div>
          )}


          {/* Invite Friends */}
          {!isGuest && profile?.referral_code && (
            <div style={{ background:t.card, border:`1.5px solid ${t.border}`, borderRadius:18, padding:"14px 16px", marginBottom:12, display:"flex", alignItems:"center", gap:14 }}>
              <div style={{ width:44, height:44, borderRadius:14, background:"rgba(37,99,235,0.12)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, flexShrink:0 }}>💈</div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:14, fontWeight:800, color:t.t1, fontFamily:"'DM Sans',sans-serif", letterSpacing:"-0.01em" }}>Invite Friends</div>
                <div style={{ fontSize:12, color:t.t3, marginTop:1 }}>{referralCount > 0 ? `${referralCount} friend${referralCount!==1?"s":""} joined via your link` : "Share FADED — get your crew on the app"}</div>
              </div>
              <button onClick={()=>{
                const url = `${window.location.origin}?ref=${profile.referral_code}`;
                if (navigator.share) {
                  navigator.share({ title:"Book barbers on FADED", text:"I use FADED to book my barber — check it out! 💈", url }).catch(()=>{});
                } else {
                  navigator.clipboard?.writeText(url).then(()=>flash("✅ Invite link copied!"));
                }
              }} style={{ flexShrink:0, padding:"8px 16px", background:"#2563EB", border:"none", borderRadius:12, color:"#fff", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", whiteSpace:"nowrap" }}>
                Share Link
              </button>
            </div>
          )}

          {/* Search bar */}
          <div style={{ position:"relative", marginBottom:12 }}>
            <span style={{ position:"absolute", left:14, top:"50%", transform:"translateY(-50%)", fontSize:16, pointerEvents:"none" }}>🔍</span>
            <input value={search} onChange={e=>{ const v=e.target.value; startTransition(()=>setSearch(v)); }} placeholder="Search barbers, styles..."
              style={{ width:"100%", padding:"13px 14px 13px 42px", borderRadius:14, border:`1.5px solid ${t.border2}`, background:t.input, fontSize:16, color:t.inputText, outline:"none", fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box" }}/>
            {search && <button onClick={()=>setSearch("")} style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", fontSize:18, cursor:"pointer", color:t.t4 }}>×</button>}
          </div>

          {/* Sort + Filter — wrapping chip row so all chips visible without scrolling */}
          <div style={{ display:"flex", gap:8, marginBottom:12, flexWrap:"wrap" }}>
            <button onClick={()=>setUrgent(!urgent)} style={{ padding:"8px 14px", borderRadius:20, border:`1.5px solid ${urgent?"#2563EB":t.border2}`, background:urgent?"#2563EB":t.card, color:urgent?"#fff":t.t3, fontSize:12, fontWeight:700, cursor:"pointer", transition:"all 0.15s", whiteSpace:"nowrap" }}>⚡ Now</button>
            {[{id:"availability",label:"🟢 Available"},{id:"distance",label:"📍 Nearest"},{id:"rating",label:"⭐ Top Rated"}].map(s=>(
              <button key={s.id} onClick={()=>setSort(s.id)} style={{ padding:"8px 14px", borderRadius:20, border:`1.5px solid ${sort===s.id?"#2563EB":t.border2}`, background:sort===s.id?t.blueBg:t.card, color:sort===s.id?t.blueText:t.t3, fontSize:12, fontWeight:700, cursor:"pointer", transition:"all 0.15s", whiteSpace:"nowrap" }}>{s.label}</button>
            ))}
            <button onClick={()=>setShowFilters(!showFilters)} style={{ padding:"8px 14px", borderRadius:20, border:`1.5px solid ${showFilters||styleFilter!=="All"||priceFilter!=="Any Price"?"#2563EB":t.border2}`, background:showFilters||styleFilter!=="All"||priceFilter!=="Any Price"?t.blueBg:t.card, color:showFilters||styleFilter!=="All"||priceFilter!=="Any Price"?t.blueText:t.t3, fontSize:12, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap" }}>
              {styleFilter!=="All"||priceFilter!=="Any Price"?"Filters ✓":"Filters"}
            </button>
          </div>

          {showFilters && (
            <div style={{ background:t.section, borderRadius:18, padding:16, border:`1.5px solid ${t.border}`, marginBottom:14, animation:"popIn 0.2s ease" }}>
              <div style={{ fontSize:12, fontWeight:700, color:t.t3, marginBottom:8 }}>Style</div>
              <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:14 }}>
                {STYLES.map(s=>(<button key={s} onClick={()=>setStyleFilter(s)} style={{ padding:"6px 12px", borderRadius:20, border:`1.5px solid ${styleFilter===s?"#2563EB":t.border2}`, background:styleFilter===s?t.blueBg:t.chip, color:styleFilter===s?t.blueText:t.t3, fontSize:11, fontWeight:600, cursor:"pointer" }}>{s}</button>))}
              </div>
              <div style={{ fontSize:12, fontWeight:700, color:t.t3, marginBottom:8 }}>Price</div>
              <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                {PRICES.map(p=>(<button key={p} onClick={()=>setPriceFilter(p)} style={{ padding:"6px 12px", borderRadius:20, border:`1.5px solid ${priceFilter===p?"#2563EB":t.border2}`, background:priceFilter===p?t.blueBg:t.chip, color:priceFilter===p?t.blueText:t.t3, fontSize:11, fontWeight:600, cursor:"pointer" }}>{p}</button>))}
              </div>
              {(styleFilter!=="All"||priceFilter!=="Any Price") && (
                <button onClick={()=>{ setStyleFilter("All"); setPriceFilter("Any Price"); }} style={{ marginTop:12, padding:"6px 14px", background:"rgba(220,38,38,0.12)", border:"none", borderRadius:10, color:"#EF4444", fontSize:11, fontWeight:700, cursor:"pointer" }}>Clear filters</button>
              )}
            </div>
          )}

          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <span style={{ fontSize:13, fontWeight:800, color:t.t1, fontFamily:"'DM Sans',sans-serif" }}>
                {barbers.length > 0 && barbers.length <= LOW_DENSITY_BARBER_THRESHOLD && !search && styleFilter==="All" && priceFilter==="Any Price" && !urgent ? "Founding Barbers" : "Barbers Near You"}
              </span>
              {barbers[0]?._isDemo && <span style={{ fontSize:11, background:"rgba(217,119,6,0.12)", color:"#F59E0B", fontWeight:700, padding:"2px 8px", borderRadius:10, border:"1px solid rgba(217,119,6,0.25)" }}>Demo</span>}
              <span style={{ fontSize:12, color:t.t4 }}>{filtered.length}</span>
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:6 }}>
              {!isDesktop && (
                <button onClick={() => setMapView(true)} title="Map view"
                  style={{ width:30, height:30, borderRadius:"50%", border:`1.5px solid ${t.border2}`, background:t.card, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", fontSize:15 }}>
                  🗺️
                </button>
              )}
              <button
                onClick={() => { setRefreshing(true); setRefreshKey(k => k + 1); setTimeout(() => setRefreshing(false), 1200); }}
                style={{ width:30, height:30, borderRadius:"50%", border:`1.5px solid ${t.border2}`, background:t.card, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", fontSize:16, color:t.t3 }}
                title="Refresh"
              >
                <span style={{ display:"inline-block", animation:refreshing?"spin 0.7s linear infinite":"none" }}>↻</span>
              </button>
            </div>
          </div>

          {(!isMd && mapView) ? (
            <MapDiscovery
              barbers={filtered}
              userLat={gps.lat}
              userLng={gps.lng}
              onBook={setSelected}
              t={t}
              isDark={isDark}
              onLocate={gps.refresh}
              gpsLoading={gps.loading}
            />
          ) : loading && !gps.loading ? (
            /* FIX 36 State 3: skeleton loading cards */
            <div style={{ display:"grid", gridTemplateColumns:isDesktop?"repeat(3,1fr)":isMd?"1fr 1fr":"1fr", gap:12 }}>
              {[1,2,3,4,5,6].map(i=>(
                <div key={i} style={{ background:t.card, borderRadius:20, padding:18, border:`1.5px solid ${t.border}`, display:"flex", gap:14, alignItems:"flex-start" }}>
                  <div style={{ width:52, height:52, borderRadius:"50%", background:t.surface2, flexShrink:0, animation:"pulse 1.4s ease-in-out infinite" }}/>
                  <div style={{ flex:1 }}>
                    <div style={{ height:14, borderRadius:8, background:t.surface2, marginBottom:8, width:"60%", animation:"pulse 1.4s ease-in-out infinite" }}/>
                    <div style={{ height:11, borderRadius:8, background:t.surface2, marginBottom:6, width:"40%", animation:"pulse 1.4s ease-in-out infinite" }}/>
                    <div style={{ height:11, borderRadius:8, background:t.surface2, width:"75%", animation:"pulse 1.4s ease-in-out infinite" }}/>
                  </div>
                </div>
              ))}
              <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.45}}`}</style>
            </div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:isMd?16:12 }}>
              {/* Shops section */}
              {nearbyShops.length > 0 && !search && styleFilter==="All" && priceFilter==="Any Price" && (
                <div style={{ marginBottom:16 }}>
                  <div style={{ display:"flex", alignItems:"baseline", gap:8, marginBottom:12 }}>
                    <div style={{ fontSize:11, fontWeight:800, color:t.t3, textTransform:"uppercase", letterSpacing:"0.1em" }}>Barbershops</div>
                    <div style={{ fontSize:11, color:t.t4 }}>{nearbyShops.length} near you</div>
                  </div>
                  <div style={isMd
                    ? { display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))", gap:14 }
                    : { display:"flex", gap:12, overflowX:"auto", paddingBottom:6 }}>
                    {nearbyShops.map(s => {
                      // Owners aren't in shop_members (that table is for renters/employees who joined
                      // someone else's shop), but an owner who also cuts hair still counts as a barber here.
                      const memberCount = (s.shop_members||[]).filter(m=>m.invite_status==="active").length + (s.owner_id ? 1 : 0);
                      const initial = (s.name||"S")[0].toUpperCase();
                      return (
                        <div key={s.id} onClick={()=>setSelectedShop(s)}
                          style={{ minWidth:isMd?0:260, borderRadius:20, border:`1px solid ${t.border}`, cursor:"pointer", flexShrink:isMd?1:0, overflow:"hidden", background:t.card, boxShadow:"0 4px 24px rgba(0,0,0,0.18)", transition:"transform 0.18s, box-shadow 0.18s" }}
                          onMouseEnter={e=>{ e.currentTarget.style.transform="translateY(-2px)"; e.currentTarget.style.boxShadow="0 8px 32px rgba(0,0,0,0.28)"; }}
                          onMouseLeave={e=>{ e.currentTarget.style.transform="translateY(0)"; e.currentTarget.style.boxShadow="0 4px 24px rgba(0,0,0,0.18)"; }}>

                          {/* ── Cover photo hero ── */}
                          <div style={{ height:140, position:"relative", background:s.cover_photo_url?`url(${s.cover_photo_url}) center/cover no-repeat`:"linear-gradient(135deg,#0a1628 0%,#0f2044 50%,#1a3566 100%)", display:"flex", alignItems:"flex-end" }}>
                            {/* Lettermark for shops without a cover photo */}
                            {!s.cover_photo_url && (
                              <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", fontSize:72, fontWeight:900, color:"rgba(37,99,235,0.18)", fontFamily:"'Bebas Neue','DM Sans',sans-serif", letterSpacing:4, userSelect:"none" }}>
                                {initial}
                              </div>
                            )}
                            {/* Gradient overlay so text is always readable */}
                            <div style={{ position:"absolute", inset:0, background:"linear-gradient(to bottom, rgba(0,0,0,0) 20%, rgba(0,0,0,0.72) 100%)" }}/>
                            {/* Shop name + city on photo */}
                            <div style={{ position:"relative", zIndex:1, padding:"10px 14px", width:"100%" }}>
                              <div style={{ fontSize:15, fontWeight:900, color:"#fff", lineHeight:1.15, fontFamily:"'DM Sans',sans-serif", letterSpacing:"0.01em" }}>{s.name}</div>
                              {(s.city||s.address) && <div style={{ fontSize:11, color:"rgba(255,255,255,0.6)", marginTop:2 }}>{s.city||s.address?.split(",")[0]}</div>}
                            </div>
                          </div>

                          {/* ── Card body ── */}
                          <div style={{ padding:"12px 14px 14px" }}>
                            {s.open_hours && (
                              <div style={{ fontSize:11, color:t.t4, marginBottom:8, display:"flex", alignItems:"center", gap:4 }}>
                                <span style={{ fontSize:10 }}>🕐</span>{s.open_hours}
                              </div>
                            )}
                            {(s.amenities||[]).length > 0 && (
                              <div style={{ display:"flex", gap:5, flexWrap:"wrap", marginBottom:10 }}>
                                {(s.amenities||[]).slice(0,4).map(a=>(
                                  <span key={a} style={{ fontSize:10, fontWeight:700, color:t.t3, background:t.section, border:`1px solid ${t.border}`, borderRadius:6, padding:"3px 7px", letterSpacing:"0.02em" }}>{a}</span>
                                ))}
                              </div>
                            )}
                            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                              <div style={{ fontSize:12, color:t.t4 }}>
                                <span style={{ fontWeight:700, color:t.t2 }}>{memberCount}</span> barber{memberCount!==1?"s":""}
                              </div>
                              <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                                {s.booth_price_from && (
                                  <div style={{ fontSize:11, color:"#93C5FD", fontWeight:700 }}>from ${Number(s.booth_price_from).toFixed(0)}/wk</div>
                                )}
                                {s.available_booths > 0 && (
                                  <div style={{ fontSize:11, fontWeight:700, color:"#22C55E", background:"rgba(34,197,94,0.1)", border:"1px solid rgba(34,197,94,0.2)", borderRadius:8, padding:"3px 8px" }}>
                                    {s.available_booths} open
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <div style={{ display:"grid", gridTemplateColumns:isDesktop?"repeat(3,1fr)":isMd?"1fr 1fr":"1fr", gap:12 }}>
              {filtered.map((b)=>(
                <div key={b.id} style={{ animation:"popIn 0.3s ease both" }}>
                  <BarberCard barber={b} onClick={setSelected} userLat={gps.lat} userLng={gps.lng} favorites={favorites} onToggleFav={toggleFavorite}/>
                </div>
              ))}
              </div>
              {filtered.length===0&&(
                /* FIX 36: distinct empty states */
                search || styleFilter!=="All" || priceFilter!=="Any Price" || urgent ? (
                  /* State 1: filters active, no match */
                  <div style={{ textAlign:"center", padding:"60px 20px" }}>
                    <div style={{ fontSize:40, marginBottom:12 }}>✂️</div>
                    <div style={{ fontSize:17, fontWeight:800, color:t.t1, fontFamily:"'DM Sans',sans-serif" }}>No barbers match</div>
                    <div style={{ fontSize:13, color:t.t3, marginTop:6, marginBottom:20 }}>Try adjusting your search or filters</div>
                    <button onClick={()=>{ setSearch(""); setStyleFilter("All"); setPriceFilter("Any Price"); setUrgent(false); }} style={{ padding:"12px 24px", background:"#2563EB", border:"none", borderRadius:14, color:"#fff", fontSize:13, fontWeight:800, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>Browse all barbers</button>
                  </div>
                ) : gps.lat ? (
                  /* State 2: GPS works but no barbers in range */
                  <div style={{ textAlign:"center", padding:"60px 20px" }}>
                    <div style={{ fontSize:40, marginBottom:12 }}>📍</div>
                    <div style={{ fontSize:17, fontWeight:800, color:t.t1, fontFamily:"'DM Sans',sans-serif" }}>No barbers in your area yet</div>
                    <div style={{ fontSize:13, color:t.t3, marginTop:6, marginBottom:20, lineHeight:1.6 }}>FADED is growing. Check back soon or share with your barber.</div>
                    <button onClick={()=>{ const txt=`Book me on FADED — the live barber booking app! letsgetfaded.com`; navigator.share?navigator.share({text:txt}).catch(()=>{}):window.open(`sms:?body=${encodeURIComponent(txt)}`); }} style={{ padding:"12px 24px", background:"#2563EB", border:"none", borderRadius:14, color:"#fff", fontSize:13, fontWeight:800, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>Share FADED with your barber →</button>
                  </div>
                ) : (
                  /* State 3: no GPS + no barbers */
                  <div style={{ textAlign:"center", padding:"60px 20px" }}>
                    <div style={{ fontSize:40, marginBottom:12 }}>💈</div>
                    <div style={{ fontSize:17, fontWeight:800, color:t.t1, fontFamily:"'DM Sans',sans-serif" }}>No barbers available right now</div>
                    <div style={{ fontSize:13, color:t.t3, marginTop:6, marginBottom:20 }}>Check back soon — barbers are joining daily.</div>
                    <button onClick={()=>{ setRefreshing(true); setRefreshKey(k=>k+1); setTimeout(()=>setRefreshing(false),1200); }} style={{ padding:"12px 24px", background:"#2563EB", border:"none", borderRadius:14, color:"#fff", fontSize:13, fontWeight:800, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>Refresh</button>
                  </div>
                )
              )}
            </div>
          )}
          </div>{/* end maxWidth centering */}
          </div>{/* end scrollable content */}
        </div>
      )}

      {/* BARBER APPOINTMENTS — direct 1-tap access to today's schedule */}
      {tab==="appointments" && isBarber && myBarberData && (
        <BarberDashboard session={session} myBarberData={myBarberData}
          appointmentsMode={true}
          openAI={barberAIDeepLink}
          onAIOpened={() => setBarberAIDeepLink(false)}
          onGoLive={() => setShowBarberWelcome(true)}
          onUpdateBarberData={setMyBarberData}
          onStatusChange={(status)=>{
            setMyBarberData(prev=>prev?{...prev,status}:prev);
            supabase.from("barbers").select(`${BARBER_PUBLIC_COLUMNS}, profiles(full_name,avatar_url)`).neq("status","offline").eq("approved",true).limit(200)
              .then(({data})=>{
                if(data&&data.length>0){
                  const mapped=data.map(b=>({...b,full_name:(Array.isArray(b.profiles)?b.profiles[0]?.full_name:b.profiles?.full_name)||"Barber",_distKm:gps.lat&&gps.lng&&b.lat&&b.lng?haversineKm(gps.lat,gps.lng,b.lat,b.lng):99}));
                  setBarbers(mapped);
                }
              });
          }}/>
      )}

      {/* BARBER BOOTH TAB — only for barbers without a shop */}
      {tab==="booth" && isBarber && !barberHasShop && (
        <div style={{ paddingBottom:"100px" }}>
          <div style={{ background:t.headerBg, backdropFilter:"blur(20px)", borderBottom:`1px solid ${t.border}`, paddingTop:"max(44px, calc(var(--sat, 0px) + 2px))", paddingBottom:14, position:"sticky", top:0, zIndex:100 }}>
            <div style={{ maxWidth: isDesktop ? 960 : "100%", margin:"0 auto", paddingLeft: isDesktop ? 32 : 16, paddingRight: isDesktop ? 32 : 16 }}>
              <div style={{ fontSize:28, fontFamily:"'Bebas Neue',sans-serif", color:t.t1, letterSpacing:"0.06em", lineHeight:1 }}>FIND A BOOTH</div>
              <div style={{ fontSize:11, color:t.t3, marginTop:3, fontFamily:"'DM Sans',sans-serif", fontWeight:700, letterSpacing:"0.05em" }}>Browse shops with available chair space</div>
            </div>
          </div>
          {/* Own a shop? Create it here */}
          <div style={{ margin:"16px 16px 0", background:t.blueBg, border:`1.5px solid ${t.blueBorder}`, borderRadius:18, padding:20, display:"flex", alignItems:"center", justifyContent:"space-between", gap:12 }}>
            <div>
              <div style={{ fontSize:13, fontWeight:800, color:t.t1, fontFamily:"'DM Sans',sans-serif", marginBottom:4 }}>Own a shop?</div>
              <div style={{ fontSize:12, color:t.t3, lineHeight:1.5 }}>Create your shop to list booths, manage your team, and track rent.</div>
            </div>
            <button onClick={()=>setTab("shop")} style={{ flexShrink:0, padding:"10px 16px", background:"#2563EB", border:"none", borderRadius:12, color:"#fff", fontSize:12, fontWeight:800, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", whiteSpace:"nowrap" }}>Create Shop</button>
          </div>
          <BoothBrowse session={session} isTestBarber={!!myBarberData?.is_test}/>
        </div>
      )}

      {/* BARBER SHOP TAB */}
      {tab==="shop" && isBarber && myBarberData && (
        <BarberDashboard session={session} myBarberData={myBarberData}
          forceTab="shop" hideHeader={true}
          openAI={false} onAIOpened={()=>{}} onGoLive={()=>setShowBarberWelcome(true)}
          onUpdateBarberData={setMyBarberData} onStatusChange={()=>{}}
          onShopCreated={()=>setBarberHasShop(true)}/>
      )}

      {/* BARBER EARNINGS TAB */}
      {tab==="earnings" && isBarber && myBarberData && (
        <BarberDashboard session={session} myBarberData={myBarberData}
          forceTab="income" hideHeader={true}
          openAI={false} onAIOpened={()=>{}} onGoLive={()=>setShowBarberWelcome(true)}
          onUpdateBarberData={setMyBarberData} onStatusChange={()=>{}}/>
      )}

      {/* BARBER DASHBOARD */}
      {tab==="dashboard" && isBarber && myBarberData && (
        <BarberDashboard session={session} myBarberData={myBarberData}
          openAI={barberAIDeepLink}
          onAIOpened={() => setBarberAIDeepLink(false)}
          onGoLive={() => setShowBarberWelcome(true)}
          onUpdateBarberData={setMyBarberData}
          onShopCreated={()=>setBarberHasShop(true)}
          onSignOut={()=>{ setSession(null); setGuestMode(false); setShowLanding(false); supabase.auth.signOut(); }}
          onStatusChange={(status)=>{
            setMyBarberData(prev=>prev?{...prev,status}:prev);
            supabase.from("barbers").select(`${BARBER_PUBLIC_COLUMNS}, profiles(full_name,avatar_url)`).neq("status","offline").eq("approved",true).limit(200)
              .then(({data})=>{
                if(data&&data.length>0){
                  const mapped=data.map(b=>({...b,full_name:(Array.isArray(b.profiles)?b.profiles[0]?.full_name:b.profiles?.full_name)||"Barber",_distKm:gps.lat&&gps.lng&&b.lat&&b.lng?haversineKm(gps.lat,gps.lng,b.lat,b.lng):99}));
                  setBarbers(mapped);
                }
              });
          }}/>
      )}

      {/* BARBER GO LIVE SPLASH OVERLAY */}
      {showBarberWelcome && (
        <BarberWelcomeSplash onDone={() => setShowBarberWelcome(false)}/>
      )}

      {/* CLIENT MAP TAB */}
      {tab==="map" && !isBarber && (
        <div style={{ position:"fixed", inset:0, zIndex:50 }}>
          <MapDiscovery barbers={barbers} userLat={gps.lat} userLng={gps.lng} onBook={b=>setSelected(b)} t={t} isDark={isDark} fullScreen={true} onListView={()=>setTab("discover")} onLocate={gps.refresh} gpsLoading={gps.loading}/>
        </div>
      )}

      {/* CLIENT BOOKINGS */}
      {tab==="bookings" && !isGuest && !isBarber && (
        <div>
          <ClientCalendar bookings={bookings} barbers={barbers} onCancel={cancelBooking} onRefresh={refreshClientBookings} session={session} onRebook={rebookBarber} onDiscover={()=>{ startTransition(()=>{ setTab("discover"); setMapView(true); }); }}/>
        </div>
      )}
      {tab==="bookings" && isGuest && (() => {
        const localBks = (() => { try { return JSON.parse(localStorage.getItem("faded_local_bookings")||"[]"); } catch { return []; } })();
        return (
          <>
            {localBks.length === 0 ? (
              <motion.div variants={staggerContainer} initial="hidden" animate="visible" style={{ minHeight:"70vh", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:isNavDesktop?"60px 24px 60px":"24px 24px 120px", textAlign:"center" }}>
                <motion.div variants={fadeUp} style={{ width:64, height:64, borderRadius:20, background:"linear-gradient(135deg,#3B6CF7,#5B8AFF)", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 20px", boxShadow:"0 0 32px rgba(59,108,247,0.25)" }}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><polyline points="9 16 11 18 15 14"/></svg>
                </motion.div>
                <motion.div variants={fadeUp} style={{ fontSize:20, fontWeight:800, color:t.t1, fontFamily:"'DM Sans',sans-serif", marginBottom:8 }}>Your first cut awaits</motion.div>
                <motion.div variants={fadeUp} style={{ fontSize:13, color:t.t3, marginBottom:24, lineHeight:1.6 }}>Find a barber near you and book your next fresh fade.</motion.div>
                <motion.button variants={fadeUp} onClick={()=>{ setGuestMode(false); setShowLanding(false); }} className="btn-primary" style={{ padding:"14px 32px", border:"none", borderRadius:14, color:"#fff", fontSize:15, fontWeight:800, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>Sign up to save bookings</motion.button>
              </motion.div>
            ) : (
              <ClientCalendar bookings={localBks} barbers={barbers} onCancel={(id)=>{ try { const cur=JSON.parse(localStorage.getItem("faded_local_bookings")||"[]"); localStorage.setItem("faded_local_bookings",JSON.stringify(cur.map(b=>b.id===id?{...b,status:"cancelled"}:b))); setBookings(bks=>[...bks]); } catch {} }} session="guest" onRebook={b=>{ const barber = barbers.find(br=>br.id===b.barber_id); if(barber) setSelected(barber); }} onRefresh={async ()=>{ setBookings(bks=>[...bks]); }}/>
            )}
          </>
        );
      })()}

      {/* PROFILE */}
      {tab==="profile" && !isGuest && isBarber && (
        <BarberSettingsTab session={session} profile={profile} setProfile={setProfile} onGiftCard={()=>{}} onSignOut={()=>{ setSession(null); setGuestMode(false); setShowLanding(false); setViewAsClient(false); supabase.auth.signOut(); }} onBookABarber={()=>{ setViewAsClient(true); setTab("discover"); setMapView(false); }}/>
      )}
      {tab==="profile" && !isGuest && !isBarber && (
        <div
          onTouchStart={e => { touchStartY.current = e.touches[0].clientY; }}
          onTouchMove={e => { const dy = e.touches[0].clientY - touchStartY.current; const atTop = (scrollContainerRef.current?.scrollTop ?? window.scrollY) === 0; if (dy > 0 && atTop) setPullY(Math.min(dy, 80)); }}
          onTouchEnd={() => { if (pullY >= 60) doRefresh(); setPullY(0); }}
        >
          {pullY > 0 && <div style={{ textAlign:"center", height:pullY, display:"flex", alignItems:"center", justifyContent:"center", overflow:"hidden" }}><span style={{ fontSize:pullY>=60?22:16, opacity:pullY/80 }}>{pullY>=60?"🔄":"↓"}</span></div>}
          {refreshing && <div style={{ textAlign:"center", padding:"6px 0" }}><span style={{ fontSize:13, color:"#2563EB", fontWeight:700 }}>Refreshing...</span></div>}
          {viewAsClient && (
            <div style={{ margin:"12px 16px 0", background:"rgba(37,99,235,0.08)", border:"1px solid rgba(37,99,235,0.25)", borderRadius:14, padding:"12px 16px", display:"flex", justifyContent:"space-between", alignItems:"center", gap:12 }}>
              <div style={{ fontSize:13, fontWeight:700, color:"#3B6CF7", fontFamily:"'DM Sans',sans-serif" }}>Browsing as client</div>
              <button onClick={()=>{ setViewAsClient(false); setTab("dashboard"); }} style={{ padding:"8px 14px", background:"#2563EB", border:"none", borderRadius:10, color:"#fff", fontSize:12, fontWeight:800, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", whiteSpace:"nowrap" }}>← My Dashboard</button>
            </div>
          )}
          <div style={{ display:"flex", justifyContent:"flex-end", padding:"12px 16px 0" }}>
            <button onClick={doRefresh} style={{ padding:"6px 14px", background:t.section, border:`1px solid ${t.border}`, borderRadius:10, color:t.t3, fontSize:12, fontWeight:700, cursor:"pointer" }}>⟳ Refresh</button>
          </div>
          <ClientProfile session={session} profile={profile} setProfile={setProfile} bookings={bookings} favorites={favorites} barbers={barbers} onToggleFav={toggleFavorite} onReview={submitReview} onRebook={rebookBarber} onGiftCard={()=>{}} onSignOut={()=>{ setSession(null); setGuestMode(false); setShowLanding(false); setViewAsClient(false); supabase.auth.signOut(); }} isAdmin={isAdmin} onOpenAdmin={()=>startTransition(()=>setTab("admin"))}/>
        </div>
      )}
      {tab==="profile" && isGuest && (
        <div style={{ minHeight:"70vh", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:isNavDesktop?"60px 24px 60px":"24px 24px 120px", textAlign:"center" }}>
          <div style={{ width:64, height:64, borderRadius:20, background:"linear-gradient(135deg,#3B6CF7,#5B8AFF)", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 20px", boxShadow:"0 0 32px rgba(59,108,247,0.25)" }}><svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg></div>
          <div style={{ fontSize:20, fontWeight:800, color:t.t1, fontFamily:"'DM Sans',sans-serif", marginBottom:8 }}>Create a free account</div>
          <div style={{ fontSize:13, color:t.t3, marginBottom:24 }}>Sign up to book barbers, save favourites, and track your cuts.</div>
          <button onClick={()=>{ setGuestMode(false); setShowLanding(false); }} style={{ padding:"14px 32px", background:"#2563EB", border:"none", borderRadius:14, color:"#fff", fontSize:15, fontWeight:800, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>Join FADED Free</button>
          <div style={{ marginTop:12, fontSize:12, color:t.t4 }}>Already have an account? <button onClick={()=>{ setGuestMode(false); setShowLanding(false); }} style={{ background:"none", border:"none", color:t.blueText, fontSize:12, fontWeight:700, cursor:"pointer" }}>Sign in</button></div>
        </div>
      )}

      {/* ADMIN */}
      {tab==="admin" && isAdmin && <AdminPanel session={session}/>}

      {/* SAVED BARBERS */}
      {tab==="saved" && (
        <div style={{ maxWidth:isNavDesktop?960:"100%", margin:"0 auto", padding:isNavDesktop?"24px 32px 60px":`20px 16px 100px`, minHeight:"var(--app-height,100dvh)", background:t.bodyBg }}>
          {viewAsClient && (
            <div style={{ marginBottom:14, background:"rgba(37,99,235,0.08)", border:"1px solid rgba(37,99,235,0.25)", borderRadius:14, padding:"12px 16px", display:"flex", justifyContent:"space-between", alignItems:"center", gap:12 }}>
              <div style={{ fontSize:13, fontWeight:700, color:"#3B6CF7", fontFamily:"'DM Sans',sans-serif" }}>Browsing as client</div>
              <button onClick={()=>{ setViewAsClient(false); setTab("dashboard"); }} style={{ padding:"8px 14px", background:"#2563EB", border:"none", borderRadius:10, color:"#fff", fontSize:12, fontWeight:800, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", whiteSpace:"nowrap" }}>← My Dashboard</button>
            </div>
          )}
          {/* My Barbers — from booking history (logged-in clients only) */}
          {!isGuest && (() => {
            const completedBks = bookings.filter(b=>b.status==="completed");
            const myBarbers = Object.values(completedBks.reduce((acc, b) => {
              const key = b.barber_id || b.barber_name || "unknown";
              if (!acc[key]) acc[key] = { barber_id:b.barber_id, barber_name:b.barber_name||"Barber", cuts:0, lastService:"", lastBooking:b };
              acc[key].cuts++;
              if (!acc[key].lastDate || new Date(b.created_at) > new Date(acc[key].lastDate)) {
                acc[key].lastService = b.service_type;
                acc[key].lastDate = b.created_at;
                acc[key].lastBooking = b;
              }
              return acc;
            }, {}));
            if (myBarbers.length === 0) return null;
            return (
              <div style={{ marginBottom:28 }}>
                <div style={{ fontSize:11, fontWeight:800, color:t.t3, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:14 }}>My Barbers</div>
                <div style={{ background:t.card, borderRadius:18, border:`1.5px solid ${t.border}`, overflow:"hidden" }}>
                  {myBarbers.map((mb,i)=>{
                    const liveBarber = barbers.find(b=>b.id===mb.barber_id);
                    const alreadyReviewed = mb.lastBooking?.reviewed;
                    return (
                      <div key={mb.barber_id||i} style={{ padding:"14px 16px", borderBottom:i<myBarbers.length-1?`1px solid ${t.divider}`:"none" }}>
                        <div style={{ display:"flex", gap:12, alignItems:"center", marginBottom:10 }}>
                          {liveBarber ? <Avatar barber={liveBarber} size={44}/> : (
                            <div style={{ width:44, height:44, borderRadius:"50%", background:t.blueBg, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, fontWeight:800, color:t.blueText, flexShrink:0 }}>{(mb.barber_name||"B")[0].toUpperCase()}</div>
                          )}
                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ fontSize:13, fontWeight:700, color:t.t1 }}>{mb.barber_name}</div>
                            <div style={{ fontSize:11, color:t.t4, marginTop:2 }}>{mb.cuts} cut{mb.cuts!==1?"s":""} · Last: {mb.lastService}</div>
                          </div>
                        </div>
                        <div style={{ display:"flex", gap:8 }}>
                          {liveBarber && (
                            <button onClick={()=>setSelected(liveBarber)} style={{ flex:1, padding:"8px 0", background:t.blueBg, border:`1px solid ${t.blueBorder}`, borderRadius:10, color:t.blueText, fontSize:12, fontWeight:700, cursor:"pointer" }}>↩ Rebook</button>
                          )}
                          {alreadyReviewed ? (
                            <div style={{ flex:1, padding:"8px 0", textAlign:"center", fontSize:12, fontWeight:700, color:"#22C55E" }}>✓ Reviewed</div>
                          ) : (
                            <button onClick={()=>setPostCutBooking(mb.lastBooking)} style={{ flex:1, padding:"8px 0", background:t.section, border:`1px solid ${t.border}`, borderRadius:10, color:t.t2, fontSize:12, fontWeight:700, cursor:"pointer" }}>⭐ Review</button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Saved / Favourite barbers */}
          <div style={{ fontSize:11, fontWeight:800, color:t.t3, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:14 }}>Saved Barbers</div>
          {favorites.length === 0 ? (
            <motion.div variants={staggerContainer} initial="hidden" animate="visible" style={{ textAlign:"center", padding:"40px 20px" }}>
              <motion.div variants={fadeUp} style={{ display:"inline-flex", width:64, height:64, borderRadius:20, background:"linear-gradient(135deg,rgba(59,108,247,0.15),rgba(59,108,247,0.05))", border:"1px solid rgba(59,108,247,0.2)", alignItems:"center", justifyContent:"center", marginBottom:16 }}>
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#3B6CF7" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>
              </motion.div>
              <motion.div variants={fadeUp} style={{ fontSize:17, fontWeight:800, color:t.t1, fontFamily:"'DM Sans',sans-serif", marginBottom:6 }}>No saved barbers yet</motion.div>
              <motion.div variants={fadeUp} style={{ fontSize:13, color:t.t3, marginBottom:20, lineHeight:1.6 }}>Tap the bookmark on any barber profile to save them here.</motion.div>
              <motion.button variants={fadeUp} onClick={()=>{ startTransition(()=>{ setTab("discover"); setMapView(true); }); }} className="btn-primary" style={{ padding:"12px 24px", border:"none", borderRadius:14, color:"#fff", fontSize:13, fontWeight:800, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>Find a Barber →</motion.button>
            </motion.div>
          ) : (
            <div style={{ display:"grid", gridTemplateColumns:isNavDesktop?"repeat(2,1fr)":"1fr", gap:16 }}>
              {barbers.filter(b=>favorites.includes(b.id)).map((b,i)=>{
                const lastCompletedBk = bookings.filter(bk=>bk.barber_id===b.id && bk.status==="completed").sort((a,c)=>new Date(c.created_at)-new Date(a.created_at))[0];
                const alreadyReviewed = lastCompletedBk?.reviewed;
                return (
                  <div key={b.id} style={{ animation:`popIn 0.3s ease ${i*0.04}s both` }}>
                    <BarberCard barber={b} onClick={setSelected} userLat={gps.lat} userLng={gps.lng} favorites={favorites} onToggleFav={toggleFavorite}/>
                    <div style={{ display:"flex", gap:8, marginTop:8 }}>
                      <button onClick={()=>setSelected(b)} style={{ flex:1, padding:"9px 0", background:t.blueBg, border:`1px solid ${t.blueBorder}`, borderRadius:12, color:t.blueText, fontSize:12, fontWeight:700, cursor:"pointer" }}>↩ Rebook</button>
                      {lastCompletedBk ? (
                        alreadyReviewed ? (
                          <div style={{ flex:1, padding:"9px 0", textAlign:"center", fontSize:12, fontWeight:700, color:"#22C55E" }}>✓ Reviewed</div>
                        ) : (
                          <button onClick={()=>setPostCutBooking(lastCompletedBk)} style={{ flex:1, padding:"9px 0", background:t.section, border:`1px solid ${t.border}`, borderRadius:12, color:t.t2, fontSize:12, fontWeight:700, cursor:"pointer" }}>⭐ Review</button>
                        )
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* BARBER MAP FLOATING BUTTON — stacked above AI fab */}
      {!isNavDesktop && isBarber && session && session !== "guest" && tab !== "discover" && (
        <button onClick={()=>{ startTransition(()=>{ setTab("discover"); setMapView(true); }); }} aria-label="Live map"
          style={{ position:"fixed", bottom:"calc(max(4px, env(safe-area-inset-bottom, 0px)) + 142px)", right:18, zIndex:109, width:40, height:40, borderRadius:"50%", background:"rgba(10,10,10,0.85)", border:"1.5px solid rgba(245,245,240,0.15)", boxShadow:"0 2px 12px rgba(0,0,0,0.4)", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", backdropFilter:"blur(8px)", WebkitBackdropFilter:"blur(8px)", outline:"none" }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgba(245,245,240,0.7)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21"/><line x1="9" y1="3" x2="9" y2="18"/><line x1="15" y1="6" x2="15" y2="21"/></svg>
        </button>
      )}
      {/* BARBER AI FLOATING BUTTON — visible on all pages for barbers */}
      {!isNavDesktop && isBarber && session && session !== "guest" && (
        <AiFab onOpen={()=>{ setBarberAIDeepLink(true); setTab("dashboard"); }}/>
      )}

      {/* MOBILE BOTTOM NAV — placeholder removed, rendered outside scroll container below */}
      {false && (
        <div>
          <div>

            {isBarber ? (<>
              {/* BARBER NAV: Dashboard | Schedule | Clients | Earnings */}
              {[
                { id:"dashboard", label:"DASHBOARD", icon:<svg aria-hidden="true" focusable="false"width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg> },
                { id:"appointments", label:"SCHEDULE", icon:<svg aria-hidden="true" focusable="false"width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><polyline points="9 16 11 18 15 14"/></svg> },
                ...(barberHasShop
                  ? [{ id:"shop",  label:"MY SHOP", icon:<svg aria-hidden="true" focusable="false"width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg> }]
                  : [{ id:"booth", label:"BOOTH",   icon:<svg aria-hidden="true" focusable="false"width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/><line x1="12" y1="12" x2="12" y2="16"/><line x1="10" y1="14" x2="14" y2="14"/></svg> }]
                ),
                { id:"earnings",     label:"EARNINGS", icon:<svg aria-hidden="true" focusable="false"width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg> },
                { id:"discover",     label:"MAP",      icon:<svg aria-hidden="true" focusable="false"width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21"/><line x1="9" y1="3" x2="9" y2="18"/><line x1="15" y1="6" x2="15" y2="21"/></svg> },
              ].map(item => {
                const active = tab === item.id || (item.id==="dashboard" && tab==="profile" && isBarber);
                return (
                  <button key={item.id} onClick={()=>{ startTransition(()=>{ setTab(item.id); if(item.id==="discover") setMapView(true); }); }} style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:3, padding:"8px 14px", borderRadius:26, border:"none", background:active?"rgba(37,99,235,0.18)":"transparent", cursor:"pointer", color:active?t.blueText:"rgba(245,245,240,0.6)" }}>
                    {item.icon}
                    <span style={{ fontSize:11, fontWeight:700, letterSpacing:"0.06em", color:"currentColor", fontFamily:"'DM Sans',sans-serif" }}>{item.label}</span>
                  </button>
                );
              })}
            </>) : (<>
              {/* CLIENT NAV: Discover | Saved | Booked | Me */}
              {/* CLIENT NAV: Discover | Map | Bookings | Profile */}
              {[
                { id:"discover", label:"DISCOVER", icon:<svg aria-hidden="true" focusable="false"width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg> },
                { id:"saved",    label:"SAVED",    icon:<svg aria-hidden="true" focusable="false"width="20" height="20" viewBox="0 0 24 24" fill={tab==="saved"?"currentColor":"none"} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg> },
                { id:"bookings", label:"BOOKINGS", icon:<svg aria-hidden="true" focusable="false"width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><polyline points="9 16 11 18 15 14"/></svg> },
                { id:"profile",  label:"PROFILE",  icon:<svg aria-hidden="true" focusable="false"width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg> },
              ].map(item => {
                const active = tab === item.id;
                return (
                  <button key={item.id} onClick={()=>{ startTransition(()=>{ setTab(item.id); if(item.id==="discover") setMapView(true); }); }} style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:3, padding:"8px 16px", borderRadius:26, border:"none", background:active?"rgba(37,99,235,0.18)":"transparent", cursor:"pointer", color:active?"#2563EB":"rgba(245,245,240,0.6)" }}>
                    {item.icon}
                    <span style={{ fontSize:11, fontWeight:700, letterSpacing:"0.06em", fontFamily:"'DM Sans',sans-serif" }}>{item.label}</span>
                  </button>
                );
              })}
            </>)}
          </div>
        </div>
      )}


      </div>{/* end main content area */}

      {/* ALL MODALS — outside overflow:auto container so position:fixed works on iOS/Android */}
      {selected && <ErrorBoundary key={selected.id} onDismiss={()=>{ setSelected(null); setRebookPrefill(null); setRescheduleBooking(null); }}><BookingModal barber={selected} session={isGuest ? null : session} onClose={()=>{ setSelected(null); setRebookPrefill(null); setRescheduleBooking(null); }} userLat={gps.lat} userLng={gps.lng} prefillService={rebookPrefill?.service} prefillTime={rebookPrefill?.time} skipToConfirm={rebookPrefill?.skipToConfirm} rescheduleBookingId={rescheduleBooking?.id} rescheduleFromTime={rescheduleBooking?.scheduled_at}/></ErrorBoundary>}
      {false && showGiftModal && session && session !== "guest" && <ErrorBoundary onDismiss={()=>setShowGiftModal(false)}><GiftCardModal session={session} onClose={()=>setShowGiftModal(false)}/></ErrorBoundary>}
      {postCutBooking && <PostCutModal booking={postCutBooking} onClose={()=>setPostCutBooking(null)} onSubmit={async (args)=>{ await submitReview(args); setPostCutBooking(null); }}/>}
      {selectedShop && <ErrorBoundary onDismiss={()=>setSelectedShop(null)}><ShopProfileModal shop={selectedShop} barbers={barbers} session={session} onClose={()=>setSelectedShop(null)} onBookBarber={(b)=>{ setSelected(b); }}/></ErrorBoundary>}
      <Toast toast={toast}/>
      <ConfirmDialog/>
      {(realtimeHealth === "reconnecting" || realtimeHealth === "back") && (
        <div role="status" aria-live="polite" aria-atomic="true"
          style={{ position:"fixed", bottom:"calc(max(4px, env(safe-area-inset-bottom, 0px)) + 88px)", left:"50%", transform:"translateX(-50%)", zIndex:500, padding:"9px 18px", borderRadius:12, fontSize:12, fontWeight:700, fontFamily:"'DM Sans',sans-serif", color:"#fff", background: realtimeHealth==="back" ? "#16A34A" : "#D97706", boxShadow:"0 4px 16px rgba(0,0,0,0.3)", whiteSpace:"nowrap", animation:"popIn 0.2s ease", pointerEvents:"none" }}>
          {realtimeHealth === "back" ? "✓ Back online" : "⟳ Reconnecting..."}
        </div>
      )}
      {incompleteBookingMsg && (
        <div role="alert" aria-live="assertive"
          style={{ position:"fixed", top:0, left:"50%", transform:"translateX(-50%)", width:"100%", maxWidth:480, zIndex:600, background:"#D97706", paddingTop:"calc(var(--sat, 0px) + 12px)", paddingBottom:"12px", paddingLeft:"20px", paddingRight:"20px", display:"flex", alignItems:"center", justifyContent:"space-between", gap:12 }}>
          <span style={{ fontSize:13, fontWeight:700, color:"#fff", fontFamily:"'DM Sans',sans-serif" }}>You have an incomplete booking. Check your email for confirmation or try again.</span>
          <button onClick={()=>{ setIncompleteBookingMsg(false); try{localStorage.removeItem("booking_in_progress");}catch{} }}
            aria-label="Dismiss incomplete booking warning"
            style={{ background:"rgba(255,255,255,0.2)", border:"none", color:"#fff", fontSize:18, cursor:"pointer", borderRadius:8, padding:"2px 8px", flexShrink:0 }}><span aria-hidden="true">×</span></button>
        </div>
      )}


      {/* MOBILE BOTTOM NAV — always in DOM; CSS hides at ≥768px, shows below 768px.
           Uses var(--sab) so init.js localStorage pre-fetch takes effect on frame 0.
           opacity:0 on first-ever cold launch until set() RAF confirms --sab. */}
      <div className="faded-mobile-nav" style={{ position:"fixed", bottom:"max(4px, env(safe-area-inset-bottom, 0px))", left:16, right:16, zIndex:110, display:"none", justifyContent:"center", alignItems:"center", pointerEvents:"none", overflow:"visible", opacity:navVisible?1:0 }}>
          <nav aria-label="Main navigation">
        <div role="tablist" aria-label="Main navigation" style={{ display:"flex", alignItems:"center", background:isDark?"rgba(10,10,10,0.60)":"rgba(8,8,8,0.82)", backdropFilter:"blur(28px)", WebkitBackdropFilter:"blur(28px)", border:"1px solid rgba(245,245,240,0.12)", borderRadius:32, padding:"6px 8px", gap:0, pointerEvents:"all", boxShadow:"0 8px 40px rgba(0,0,0,0.45)", position:"relative", zIndex:1, width:"calc(100vw - 32px)", maxWidth:440 }}>

            {isBarber ? (<>
              {/* BARBER NAV: Shop/Booth | Schedule | Manage (FADED shield) | Earnings | Account */}
              {[
                ...(barberHasShop
                  ? [{ id:"shop",  label:"My Shop", staticIcon:<svg aria-hidden="true" focusable="false" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg> }]
                  : [{ id:"booth", label:"Find Booth", staticIcon:<svg aria-hidden="true" focusable="false" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/><line x1="12" y1="12" x2="12" y2="16"/><line x1="10" y1="14" x2="14" y2="14"/></svg> }]
                ),
                { id:"appointments", label:"Schedule", staticIcon:<svg aria-hidden="true" focusable="false" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><polyline points="9 16 11 18 15 14"/></svg> },
                { id:"dashboard", label:"Manage", staticIcon:null, shield:true },
                { id:"earnings", label:"Earnings", staticIcon:<svg aria-hidden="true" focusable="false" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg> },
                { id:"profile",  label:"Account",  staticIcon:<svg aria-hidden="true" focusable="false" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg> },
              ].map(item => {
                const active = tab === item.id;
                if (item.shield) {
                  return (
                    <button key={item.id} onClick={()=>startTransition(()=>setTab(item.id))}
                      role="tab" aria-selected={active} aria-label="Manage"
                      style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:0, padding:0, border:"none", background:"transparent", cursor:"pointer", marginTop:"-34px", filter:active?"drop-shadow(0 0 14px rgba(37,99,235,0.95))":"drop-shadow(0 4px 12px rgba(0,0,0,0.6))", transition:"filter 0.2s", flexShrink:0 }}>
                      <svg aria-hidden="true" focusable="false" width="54" height="58" viewBox="0 0 24 28" fill="none">
                        {/* Superman-style shield: flat top, angled shoulders, sharp bottom point */}
                        <path d="M3,3 L21,3 L24,10.5 L12,26 L0,10.5 Z" fill={active?"#3b82f6":"#2563EB"} stroke="rgba(255,255,255,0.22)" strokeWidth="0.7" strokeLinejoin="round"/>
                        {/* Inner bevel for depth */}
                        <path d="M5,4.5 L19,4.5 L21.5,10.8 L12,23.5 L2.5,10.8 Z" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="0.5" strokeLinejoin="round"/>
                        <text x="12" y="11.5" textAnchor="middle" fontFamily="'DM Sans',Arial Black,sans-serif" fontWeight="900" fontSize="6.4" letterSpacing="-0.2">
                          <tspan fill="white">FAD</tspan><tspan fill={active?"#93c5fd":"#bfdbfe"}>ED</tspan>
                        </text>
                      </svg>
                    </button>
                  );
                }
                return (
                  <button key={item.id} onClick={()=>{ haptic.light(); startTransition(()=>{ setTab(item.id); if(item.id==="discover") setMapView(true); }); }}
                    role="tab" aria-selected={active} aria-label={item.label}
                    style={{ flex:1, minWidth:0, display:"flex", flexDirection:"column", alignItems:"center", gap:2, padding:"6px 8px", borderRadius:26, border:"none", background:"transparent", cursor:"pointer", color:active?"#3B6CF7":"rgba(245,245,240,0.55)", position:"relative" }}>
                    {active && <motion.div layoutId="barberTabIndicator" style={{ position:"absolute", top:0, left:0, right:0, margin:"0 auto", width:28, height:2, borderRadius:2, background:"#3B6CF7" }} transition={{ type:"spring", stiffness:400, damping:30 }}/>}
                    {item.staticIcon}
                    <span aria-hidden="true" style={{ fontSize:10, fontWeight:700, letterSpacing:"0.06em", color:"currentColor", fontFamily:"'DM Sans',sans-serif" }}>{item.label.toUpperCase()}</span>
                  </button>
                );
              })}
            </>) : (<>
              {/* CLIENT NAV — two flex:1 halves flank the shield so each side is
                  exactly half the non-shield space, and buttons inside each half
                  split evenly. This is immune to label-length skew (ACCOUNT vs MAP). */}
              <div style={{ flex:1, display:"flex" }}>
                {[
                  { id:"discover", label:"MAP",   onClick:()=>{ startTransition(()=>{ setTab("discover"); setMapView(true); }); }, icon:<svg aria-hidden="true" focusable="false" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg> },
                  { id:"saved",    label:"SAVED",  onClick:()=>startTransition(()=>setTab("saved")),    icon:<svg aria-hidden="true" focusable="false" width="20" height="20" viewBox="0 0 24 24" fill={tab==="saved"?"currentColor":"none"} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg> },
                ].map(item => {
                  const active = tab === item.id;
                  return (
                    <button key={item.id} onClick={()=>{ haptic.light(); item.onClick(); }}
                      role="tab" aria-selected={active} aria-label={item.label}
                      style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:2, padding:"6px 8px", borderRadius:26, border:"none", background:"transparent", cursor:"pointer", color:active?"#3B6CF7":"rgba(245,245,240,0.55)", minWidth:0, position:"relative" }}>
                      {active && <motion.div layoutId="clientTabIndicator" style={{ position:"absolute", top:0, left:0, right:0, margin:"0 auto", width:28, height:2, borderRadius:2, background:"#3B6CF7" }} transition={{ type:"spring", stiffness:400, damping:30 }}/>}
                      {item.icon}
                      <span aria-hidden="true" style={{ fontSize:10, fontWeight:700, letterSpacing:"0.06em", fontFamily:"'DM Sans',sans-serif" }}>{item.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* FADED shield — dead center, floats above the pill */}
              <button onClick={() => setInstantOpen(true)} aria-label="Get Faded Now"
                style={{ flexShrink:0, display:"flex", flexDirection:"column", alignItems:"center", padding:"0 4px", border:"none", background:"transparent", cursor:"pointer", marginTop:"-34px", filter:instantOpen?"drop-shadow(0 0 14px rgba(37,99,235,0.95))":"drop-shadow(0 4px 12px rgba(0,0,0,0.6))", transition:"filter 0.2s" }}>
                <svg aria-hidden="true" focusable="false" width="54" height="58" viewBox="0 0 24 28" fill="none">
                  <path d="M3,3 L21,3 L24,10.5 L12,26 L0,10.5 Z" fill={instantOpen?"#3b82f6":"#2563EB"} stroke="rgba(255,255,255,0.22)" strokeWidth="0.7" strokeLinejoin="round"/>
                  <path d="M5,4.5 L19,4.5 L21.5,10.8 L12,23.5 L2.5,10.8 Z" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="0.5" strokeLinejoin="round"/>
                  <text x="12" y="11.5" textAnchor="middle" fontFamily="'DM Sans',Arial Black,sans-serif" fontWeight="900" fontSize="6.4" letterSpacing="-0.2">
                    <tspan fill="white">FAD</tspan><tspan fill={instantOpen?"#93c5fd":"#bfdbfe"}>ED</tspan>
                  </text>
                </svg>
              </button>

              <div style={{ flex:1, display:"flex" }}>
                {[
                  { id:"bookings", label:"CUTS",    onClick:()=>startTransition(()=>setTab("bookings")), icon:<svg aria-hidden="true" focusable="false" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><polyline points="9 16 11 18 15 14"/></svg> },
                  { id:"profile",  label:"ME",       onClick:()=>startTransition(()=>setTab("profile")),  icon:<svg aria-hidden="true" focusable="false" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg> },
                ].map(item => {
                  const active = tab === item.id;
                  return (
                    <button key={item.id} onClick={()=>{ haptic.light(); item.onClick(); }}
                      role="tab" aria-selected={active} aria-label={item.label}
                      style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:2, padding:"6px 8px", borderRadius:26, border:"none", background:"transparent", cursor:"pointer", color:active?"#3B6CF7":"rgba(245,245,240,0.55)", minWidth:0, position:"relative" }}>
                      {active && <motion.div layoutId="clientTabIndicator" style={{ position:"absolute", top:0, left:0, right:0, margin:"0 auto", width:28, height:2, borderRadius:2, background:"#3B6CF7" }} transition={{ type:"spring", stiffness:400, damping:30 }}/>}
                      {item.icon}
                      <span aria-hidden="true" style={{ fontSize:10, fontWeight:700, letterSpacing:"0.06em", fontFamily:"'DM Sans',sans-serif" }}>{item.label}</span>
                    </button>
                  );
                })}
              </div>
            </>)}
          </div>
        </nav>
        </div>
    </div>{/* end flex wrapper */}
    {/* Global VoiceOver announcement region — visually hidden, announced by screen readers */}
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      style={{ position:"absolute", width:1, height:1, padding:0, margin:-1, overflow:"hidden", clip:"rect(0,0,0,0)", whiteSpace:"nowrap", border:0 }}>
      {a11yAnnouncement}
    </div>
    {/* Soft update nudge — dismissible, re-shown only if min_version bumps again */}
    {versionCheck && !versionCheck.force && (() => {
      const dismissKey = `faded_update_dismissed_${versionCheck.minVersion}`;
      let dismissed = false;
      try { dismissed = localStorage.getItem(dismissKey) === "1"; } catch {}
      if (dismissed) return null;
      const storeUrl = versionCheck.platform === "ios"
        ? "https://apps.apple.com/ca/app/get-faded/id6782978371"
        : "https://play.google.com/store/apps/details?id=com.letsgetfaded.app";
      return (
        <div style={{ position:"fixed", bottom:"calc(88px + env(safe-area-inset-bottom))", left:16, right:16, zIndex:9000, background:"#111", border:"1.5px solid #2563EB", borderRadius:14, padding:"12px 14px", display:"flex", alignItems:"center", gap:12, boxShadow:"0 8px 32px rgba(0,0,0,0.6)" }}>
          <div style={{ flex:1, fontSize:13, color:"#F5F5F0", fontFamily:"'DM Sans',sans-serif", lineHeight:1.4 }}>
            {versionCheck.message || "A new version of FADED is available."}
          </div>
          <a href={storeUrl} target="_blank" rel="noopener noreferrer"
            style={{ padding:"8px 14px", background:"#2563EB", borderRadius:10, color:"#fff", fontSize:12, fontWeight:700, textDecoration:"none", whiteSpace:"nowrap" }}>
            Update
          </a>
          <button onClick={() => { try { localStorage.setItem(dismissKey, "1"); } catch {} setVersionCheck(v => ({ ...v })); }}
            style={{ background:"transparent", border:"none", color:"rgba(255,255,255,0.4)", fontSize:18, cursor:"pointer", padding:4, lineHeight:1 }}>
            ×
          </button>
        </div>
      );
    })()}
    <Analytics />
    <SpeedInsights />

    {/* Desktop FAB — Get Faded Now (clients only, desktop only) */}
    {!isBarber && isDesktop && !instantOpen && (
      <button
        onClick={() => setInstantOpen(true)}
        aria-label="Get Faded Now"
        title="Get Faded Now — broadcast to nearby barbers"
        style={{
          position:"fixed", bottom:32, right:32, zIndex:120,
          display:"flex", flexDirection:"column", alignItems:"center", gap:6,
          border:"none", background:"none", cursor:"pointer", padding:0,
          filter: isDark
            ? "drop-shadow(0 6px 24px rgba(37,99,235,0.5)) drop-shadow(0 2px 8px rgba(0,0,0,0.7))"
            : "drop-shadow(0 4px 20px rgba(37,99,235,0.4)) drop-shadow(0 1px 6px rgba(37,99,235,0.25))",
          transition:"filter 0.2s",
        }}
        onMouseEnter={e => e.currentTarget.style.filter = isDark
          ? "drop-shadow(0 8px 32px rgba(37,99,235,0.85)) drop-shadow(0 2px 12px rgba(0,0,0,0.8))"
          : "drop-shadow(0 6px 28px rgba(37,99,235,0.65)) drop-shadow(0 2px 10px rgba(37,99,235,0.35))"}
        onMouseLeave={e => e.currentTarget.style.filter = isDark
          ? "drop-shadow(0 6px 24px rgba(37,99,235,0.5)) drop-shadow(0 2px 8px rgba(0,0,0,0.7))"
          : "drop-shadow(0 4px 20px rgba(37,99,235,0.4)) drop-shadow(0 1px 6px rgba(37,99,235,0.25))"}
      >
        <svg aria-hidden="true" focusable="false" width="72" height="78" viewBox="0 0 24 28" fill="none">
          <path d="M3,3 L21,3 L24,10.5 L12,26 L0,10.5 Z" fill="#2563EB" stroke={isDark?"rgba(255,255,255,0.22)":"rgba(255,255,255,0.5)"} strokeWidth="0.7" strokeLinejoin="round"/>
          <path d="M5,4.5 L19,4.5 L21.5,10.8 L12,23.5 L2.5,10.8 Z" fill="none" stroke={isDark?"rgba(255,255,255,0.1)":"rgba(255,255,255,0.25)"} strokeWidth="0.5" strokeLinejoin="round"/>
          <text x="12" y="15.2" textAnchor="middle" fontFamily="'DM Sans',Arial Black,sans-serif" fontWeight="900" fontSize="5.8" letterSpacing="-0.2">
            <tspan fill="white">FAD</tspan><tspan fill={isDark?"#bfdbfe":"#dbeafe"}>ED</tspan>
          </text>
        </svg>
        <span style={{ fontSize:11, fontWeight:700, letterSpacing:"0.08em", fontFamily:"'DM Sans',sans-serif", textTransform:"uppercase", color: isDark ? "rgba(245,245,240,0.75)" : "#1D4ED8" }}>Get Faded Now</span>
      </button>
    )}

    {/* Get Faded Now — client broadcast screen */}
    {!isBarber && instantOpen && (
      <InstantRequestScreen session={session} gps={gps} onClose={() => setInstantOpen(false)} t={t}/>
    )}

    {/* Barber: incoming client ping card */}
    {isBarber && incomingClientPing && (
      <ClientPingCard ping={incomingClientPing} myBarberData={myBarberData} session={session} onDismiss={() => setIncomingClientPing(null)} t={t}/>
    )}

    {/* PWA install prompt */}
    {showInstallPrompt && (
      <div style={{ position:"fixed", bottom:`calc(var(--sab, 0px) + 76px)`, left:16, right:16, zIndex:1200,
        background:"rgba(12,12,16,0.97)", backdropFilter:"blur(20px)", WebkitBackdropFilter:"blur(20px)",
        border:"1px solid rgba(37,99,235,0.4)", borderRadius:18, padding:"14px 16px",
        display:"flex", alignItems:"center", gap:12,
        boxShadow:"0 8px 40px rgba(0,0,0,0.5)", animation:"slideUp 0.3s cubic-bezier(0.32,0.72,0,1)" }}>
        <div style={{ fontSize:26, flexShrink:0 }}>💈</div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:13, fontWeight:800, color:"#F5F5F0", fontFamily:"'DM Sans',sans-serif" }}>Add FADED to Home Screen</div>
          <div style={{ fontSize:12, color:"rgba(245,245,240,0.5)", marginTop:2, lineHeight:1.4 }}>
            {isIOSInstall ? "Tap the Share icon below, then \"Add to Home Screen\"" : "One-tap booking — no App Store needed"}
          </div>
        </div>
        {!isIOSInstall && (
          <button onClick={async () => {
            if (installPromptRef.current) {
              installPromptRef.current.prompt();
              await installPromptRef.current.userChoice;
              installPromptRef.current = null;
            }
            setShowInstallPrompt(false);
            try { localStorage.setItem("faded_install_dismissed", "1"); } catch {}
          }} style={{ background:"#2563EB", border:"none", borderRadius:10, color:"#fff", fontSize:12, fontWeight:800, padding:"8px 14px", cursor:"pointer", whiteSpace:"nowrap", flexShrink:0 }}>
            Install
          </button>
        )}
        <button onClick={() => { setShowInstallPrompt(false); try { localStorage.setItem("faded_install_dismissed", "1"); } catch {}; }}
          style={{ background:"rgba(245,245,240,0.08)", border:"none", borderRadius:8, color:"rgba(245,245,240,0.45)", fontSize:18, width:30, height:30, cursor:"pointer", flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center", lineHeight:1 }}>
          ×
        </button>
      </div>
    )}

    </ResponsiveCtx.Provider>
    </ThemeCtx.Provider>
  );
}

